/* ============================================================
 * 武者之路 —— 2D 横版修仙路遇战斗
 *
 * 玩法流程：
 *   按住左下角【移动】→ 角色向前走
 *   → 路上遇到拦路者 → 进入战斗
 *   → 使用【攻击 / 法术】击败敌人
 *   → 移动按钮恢复，才能继续前进
 *
 * 素材：Kenney.nl (Creative Commons CC0)
 * 运行：npm run dev（开发热更新） / npm run build（产物在 dist/，交给 nginx 托管）
 * ============================================================ */

import {
  SLOT_DEFS,
  WEAPON_KINDS,
  RARITY_DEFS,
  SHOP_LOCK_COST,
  SAVE_KEY,
  GEM_OPTIONS,
  SKILL_POOL,
  ACTIVE_SKILL_POOL,
  REALM_LIST,
  CULTIVATION_STAGES,
  MAX_STAGE,
  TRIBULATION_STAGE,
  ATTR_DEFS,
  ATTR_POINTS_PER_LEVEL,
  CAVE_DEFS,
  CAVE_TOKEN_COST,
  CAVE_JOIN_CHANCE,
  CAVE_JOIN_MAX,
  ACTIVITY_DUNGEONS,
  ACTIVITY_PERIOD_HOURS,
  ACTIVITY_LIFETIME_MIN,
  ACTIVITY_TEST_OPEN,
  INVASION_MATERIALS,
  ABYSS_GIFT_ITEMS,
  DUNGEON_LOOT,
  CHAT_STICKERS,
  RECYCLE_TIERS,
  RECYCLE_MATERIALS,
  RECYCLE_MATERIAL_CHANCE,
  SHIELD_AFFIXES,
  GENERIC_AFFIXES,
  WEAPON_AFFIXES,
  ARMOUR_AFFIXES,
  CLOTHES_AFFIXES,
  GLOVES_AFFIXES,
  SHOES_AFFIXES,
  BASE_DEFENSE_BANDS,
  JEWELRY_AFFIXES,
  TREASURE_SPECIALS,
  UNIQUE_DEFS,
  SECTS,
  SECT_SKILLS,
  AFFIX_BAND_SIZE,
  CODEX_ITEMS,
  CODEX_TIERS,
  CODEX_LEGACY_IDS,
  STAT_LABELS,
  GACHA_SINGLE_COST,
  GACHA_TEN_COST,
  GACHA_PITY_MAX,
  GACHA_TEN_MIN_TIER,
  GACHA_TEN_CAPS,
  WORLD_BOSSES,
  WEAPON_BASE_MODS,
  JEWELRY_BASE_POOL,
  DUNGEONS,
  DUNGEON_TEST_OPEN,
  COSTUMES,
  COSTUME_SLOTS,
  FACTIONS,
  SPIRIT_ROOTS,
  SPIRIT_ROOT_REROLL_COST,
  SPIRIT_ROOT_REROLL_RELIC,
  FACTION_INTRO,
  CG_SLIDES,
  CG_ART_DIR
} from './data/defs.js';
// 网络层：服务器地址解析 + 账号/存档 API + 聊天 WebSocket（服务器不可用时自动回退单机）
import { createApiClient, createChatClient, normalizeServerUrl, wsUrlFrom, ApiError } from './net.js';
// 资源基址（挂第三方平台 iframe 时素材仍从自有域名取，见 assetBase.js）
import {
  ASSET_BASE, assetUrl, fixStaticAssetSrcs, guardDrawImage,
  defaultServerOrigin, defaultServerInput
} from './assetBase.js';

(function () {
  'use strict';

  /* ============================================================
   * 素材地址修正（必须先跑，越早越好）
   *   把游戏挂到 VibeHub 之类的平台 iframe 里时，页面文档地址会变成平台域名，
   *   index.html / JS 里的 "assets/img/..." 会被拼到平台站点上 → 全部 404。
   *   这里统一补成绝对地址（自有域名或正式域名），并给 drawImage 加一层兜底。
   * ============================================================ */
  fixStaticAssetSrcs();
  guardDrawImage();
  window.__assetBase = ASSET_BASE;      // 调试用：确认素材实际从哪个基址取（本地/线上排查 404 用）
  window.__assetUrl = assetUrl;         // 调试用：传入任意素材路径，看最终会请求成什么地址
  window.__serverUrl = normalizeServerUrl; // 调试用：传入登录框里填的地址，看最终会连到哪

  // ---------- 画布 ----------
  const W = 1280;
  const H = 720;
  const canvas = document.getElementById('game');
  const ctx = canvas.getContext('2d');

  // ---------- 游戏常量 ----------
  const GROUND_Y = 650;
  const START_X = 360;
  const SUN_X = 995;
  const SUN_Y = 128;
  const KM_UNIT = 2400;
  const WALK_SPEED = 250;
  const FIRST_ENEMY_DIST = 1700;   // 起点到第一个敌人
  const ENEMY_GAP = 3000;          // 敌人之间的间距
  const ENEMY_GAP_PX = 170;        // 玩家停在敌人面前的距离
  const ENEMY_LEVEL_KM = 10;       // 每 10km 敌人等级 +1

  // ---------- 测试环境便利设置 ----------
  // 本地 / 内网 / file:// 访问时，每次进入游戏自动发放测试资源：
  //   10000 金币（买装备 / 抽卡测试）+ 100 活力（自动冒险 / 活力兑换测试）
  // 加 ?debug=0 关闭，加 ?debug=1 可在任意环境强制开启。
  const TEST_GOLD = 10000;
  const TEST_VITALITY = 100;
  let testGoldGranted = false;
  /* 删档之后、新档案落定之前：禁止任何落盘（详见 resetAllSaves / flushOnPageHide） */
  let savesDisabled = false;

  function isTestEnvironment() {
    if (/[?&](debug|test)=0/.test(location.search)) return false;
    if (/[?&](debug|test)=1/.test(location.search) || location.hash === '#debug') return true;
    const host = location.hostname;
    return location.protocol === 'file:' ||
      host === 'localhost' || host === '127.0.0.1' || host === '::1' ||
      /^192\.168\./.test(host) || /^10\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host);
  }

  // “劈砍”特效节奏：刀光从敌人上方劈下，落到敌人身上时结算伤害
  const SLASH_DUR = 0.78;
  const SLASH_IMPACT = 0.42;
  // “劈砍”双倍伤害几率（%，触发时战斗场景飘一条「双倍效果」）
  const SLASH_DOUBLE_PCT = 30;
  const HURT_TIME = 0.32; // 受击震动 / 血色划痕持续时长

  // “防御”护盾特效：pipo-btleffect208（192×192，20 帧，见 drawDefendFx）
  // 表现：护盾从角色身上撑开 → 防御期间把角色整个包裹住（护盾维持）→ 防御结束收盾
  const DEFEND_FX_FRAMES = 20;
  // ① 撑开：1→12 帧，0.42 秒
  const DEFEND_FX_IN_DUR = 0.42;
  const DEFEND_FX_IN_FROM = 1, DEFEND_FX_IN_TO = 12;
  // ② 维持：12~17 帧循环（护盾裹住角色，轻微呼吸感）
  const DEFEND_FX_HOLD_FROM = 12, DEFEND_FX_HOLD_TO = 17;
  const DEFEND_FX_HOLD_FPS = 10;
  // ③ 收盾：17→20 帧，0.28 秒
  const DEFEND_FX_OUT_DUR = 0.28;
  const DEFEND_FX_OUT_FROM = 17, DEFEND_FX_OUT_TO = 20;
  const DEFEND_FX_SIZE = 252;                        // 屏幕上的绘制尺寸：正好罩住整个角色
  // 贴图里护盾是从左侧一点往右撑开的：这样会看成"从背后往前糊"，方向反了。
  // 水平镜像之后，护盾改为从角色前方（敌人在的那一侧）撑开、再回裹到身上。
  const DEFEND_FX_FLIP = true;
  // 源图里"盾面"的重心（192×192 画布约 86,98）：把这一点钉在角色身上，护盾才会包裹角色，
  // 而不是从角色身前一路铺到敌人脚下。
  // 镜像后重心同步翻到 1-86/192，保证翻面以后护盾依旧罩在角色身上。
  const DEFEND_FX_ORIGIN = { x: (DEFEND_FX_FLIP ? 1 - 86 / 192 : 86 / 192), y: 98 / 192 };
  const DEFEND_FX_LIFT = 94;    // 角色身体中心离地高度（护盾圆心）

  // 「破空斩」剑气：用户素材 HolySlash_B（64×64，4 帧，红色弧形剑气）
  // 表现：连放 2 道，从角色身前向前扩散，一直飞到屏幕最右侧边界才消失
  const AIRSLASH_FX_FRAMES = 4;
  const AIRSLASH_FX_SIZE = 220;        // 屏幕上的绘制尺寸（像素风放大）
  const AIRSLASH_FX_LIFT = 104;        // 剑气中心离地高度（横过敌人胸腹）
  const AIRSLASH_FX_SPEED = 1500;      // px/秒：约 0.7 秒穿过整屏
  const AIRSLASH_FX_FPS = 16;          // 4 帧循环
  const AIRSLASH_FX_WAVES = 2;         // 连续 2 道
  const AIRSLASH_FX_GAP = 0.26;        // 第二道落后的时间
  const AIRSLASH_FX_START = 52;        // 出招位置：角色身前

  /* 「盾墙 · 碎石崩裂」碎石特效：Foozle Rock（64×64，10 帧）
   * 触发时机：盾墙激活时每次成功格挡 → 从角色身前朝前方敌人喷出一串碎石 */
  const ROCK_FX_FRAMES = 10;
  const ROCK_FX_FPS = 22;              // 10 帧约 0.45 秒播完
  const ROCK_FX_SIZE = 92;             // 每块碎石在屏幕上的尺寸（像素风放大）
  const ROCK_FX_SPEED = 1050;          // px/秒
  const ROCK_FX_START = 46;            // 出发点：角色身前
  const ROCK_FX_COUNT = 5;             // 每次崩裂喷几块碎石

  /* 独特「武者的刑具」·熔岩骤雨：Fire Arrow（craftpix，320×170，8 帧，箭头朝右）
   *   释放形态：从玩家头顶上方、沿 45° 斜线向右下坠落，砸在敌人身上。
   *   素材的透明重心在 (228, 84)，绘制时把这一点对准箭矢坐标再旋转 45°。 */
  const FIRE_ARROW_FRAMES = 8;
  const FIRE_ARROW_W = 320;
  const FIRE_ARROW_H = 170;
  const FIRE_ARROW_CX = 228;
  const FIRE_ARROW_CY = 84;
  const FIRE_ARROW_SCALE = 0.44;       // 绘制缩放（原始素材偏大）
  const FIRE_ARROW_DEG = Math.PI / 4;  // 统一 45°：向右下
  const FIRE_ARROW_SPEED = 980;        // 沿 45° 的速度（px/秒，每轴分量同值）

  /* 「强力射击」法术特效：Wispstrike（256×256）
   *   flight：18 帧，从玩家身前射出 → 命中敌人后消失
   *   impact：19 帧，只有"完美时机"那一箭命中后才播放的新形态 */
  const WISP_FLIGHT_FRAMES = 18;
  const WISP_IMPACT_FRAMES = 19;
  const WISP_IMPACT_SIZE = 320;        // 命中形态绘制尺寸（完美时机更夸张）
  const POWER_FLY_DUR = 0.42;          // 飞行时长（正好放完 18 帧）
  const POWER_IMPACT_DUR = 0.62;       // 完美时机的命中形态时长（放完 19 帧）
  const POWER_PUFF_DUR = 0.42;         // 非完美：只剩一小圈气浪
  /* ---- 「强力射击」的飞行形态 ----
   *   · 素材用 Wispstrike 的 flight_1~18（不再用手绘箭矢）
   *   · 但飞行轨迹保持"从角色胸前平射出去、命中敌人同一 Y 轴"（水平直线，没有上抛弧线）。
   *     玩家和敌人的立绘同尺寸、同脚底基线（GROUND_Y），所以"玩家胸前"就等于
   *     "敌人胸前"，同 Y 轴平飞即可。
   *   · 高度依据实测：两个立绘都占画面 y≈460~660，其中
   *       头部 ≈ 490~530，肩/胸（最宽处）≈ 545~560，腰腹（身体中点）≈ 570
   *     取离地 100px（画面 y=550）正好落在"胸前"那一段。
   *   · 命中形态（impact）位置保持原样，没有调整。 */
  const WISP_BOLT_SIZE = 190;          // 飞行形态绘制尺寸
  const POWER_ARROW_LIFT = 100;        // 飞行高度：离地 100px ＝ 角色/敌人"胸前"
  const POWER_ARROW_LEN = 78;          // 手绘箭矢兜底时的长度（素材没加载出来才会用到）
  /* flight 素材的"视觉重心"比贴图中心偏高（尾巴在左边、亮部偏上）
   *   实测 18 帧平均：α 质心 y=120.9、α² 质心 y=119.1（画布中心 128），
   *   按 190px 绘制换算 ≈ 偏高 5.3~6.6px。
   *  → 贴图中心比飞行线再低 6px，视觉重心才正好压在"胸前"那条线上（和之前你认可的位置一致）。 */
  const POWER_FLIGHT_BIAS = 6;
  const POWER_SHOT_BASE_PROJ = 1;      // 基础 1 支箭（辅助「多重投射物」再 +2）
  const POWER_VOLLEY_GAP = 0.26;       // 多重投射物：每一支跟前一支的出发间隔（尾随飞出去）
  const POWER_HIT_OFFSET_X = 40;       // 命中点相对敌人锚点的横向偏移

  // ---------- 玩家角色素材（生成素材 → 统一帧） ----------
  // tools/make_player_assets 把 434×724 的原始帧按"脚底基线 + 腿部重心"对齐，
  // 输出成尺寸一致、脚底基线固定在画布底部上方 basePad 的小画布，游戏里整帧贴即可。
  const PLAYER_FRAME = { w: 199, h: 207, basePad: 12 };
  const PLAYER_SPRITE_FLIP = false; // 素材方向反了就改成 true（一行切换）
  const PLAYER_IDLE_FPS = 6;        // 待机呼吸
  const PLAYER_WALK_FPS = 7;        // 赶路（复用这一套帧 + 轻微上下起伏）
  const PLAYER_ATK_FPS = 16;        // 出手动作
  const PLAYER_ATK_FRAMES = 5;
  const PLAYER_ATK_DUR = PLAYER_ATK_FRAMES / PLAYER_ATK_FPS; // ≈0.31s
  // 休息动作（路上停着不动、按钮显示「休息」时）：4 张 idle 帧随机挑一张当当前休息状态
  const PLAYER_REST_FRAMES = 4;
  const PLAYER_REST_FPS = 6;          // 休息帧按动画连续播放（约 0.67 秒一轮）
  const PLAYER_REST_REROLL_MIN = 6;   // 秒：随机换下一个休息姿态的最短间隔
  const PLAYER_REST_REROLL_MAX = 11;  // 秒：最长间隔
  // 门派矿山 · 挖矿动作（用户提供的 5 帧 333×481 素材）
  const PLAYER_MINE_FRAME = { w: 434, h: 724, basePad: 104 };
  const PLAYER_MINE_FRAMES = 5;
  const PLAYER_MINE_FPS = 7;
  const PLAYER_MINE_LOOPS = 2;        // 挖矿动作循环两轮，约 1.4 秒
  const PLAYER_MINE_DRAW_SCALE = 0.367; // 相对玩家正常缩放：让角色本体大小接近普通立绘
  const PLAYER_MINE_GROUND_DROP = 12;   // 挖矿素材整体再下沉一点，避免脚底悬空
  const MINE_ANIM_SEC = PLAYER_MINE_FRAMES / PLAYER_MINE_FPS * PLAYER_MINE_LOOPS;
  const MINE_FLIGHT_SEC = 0.92;       // 矿石从抛起到回落的基准时长
  /* 两轮挖矿之间的间隔：调大 → 出矿更慢（原来是 280ms） */
  const MINE_CYCLE_GAP_MS = 1000;

  // ---------- 敌人美术（9 种，各只有移动帧；见 tools/make_enemy_assets.cjs）----------
  // 统一画布 204×194、脚底基线距底 14px、锚点 x=93 —— 和玩家素材同一套对齐思路
  // 新素材（用户自己抠好的透明底立绘）：每种是一整套连贯的跑步帧，帧数见下表
  // 说明：素材默认朝右（ENEMY_ART_FACES_RIGHT）。若某个模型的原图天生朝左，
  // 只需给那一项加 faceRight: false，游戏会单独把它翻过来。
  const ENEMY_SPRITES = [
    { key: 'bingfei', name: '兵匪', frames: 6 },
    { key: 'qigai', name: '乞丐', frames: 3 },
    { key: 'qiangdao', name: '强盗', frames: 2 },
    { key: 'shanzhu', name: '山猪', frames: 3 },
    { key: 'shuifei', name: '水匪', frames: 2 },
    { key: 'yaoshi', name: '药士', frames: 5 },
    { key: 'youmin', name: '游民', frames: 3 },
    { key: 'nongfu', name: '农夫', frames: 8 },
    { key: 'kuaiji', name: '会计', frames: 8 },
    /* 副本专属敌人：只在自己那张副本地图里刷（dungeonOnly = 不进道路 / 洞府 / 其它副本的随机池）
     * 素材导入：tools/import_evil_mage.cjs（8 张跑步帧 → 统一 204×194 画布） */
    { key: 'evilMage', name: '邪恶法师', frames: 8, dungeonOnly: true },
    /* 深渊入侵（亡灵）其余 5 种：素材导入 tools/import_undead_enemies.cjs
     *   普通 / 魔法杂兵 4 种 + 骸骨精英 1 种；BOSS 是上面的邪恶法师 */
    { key: 'ghoul', name: '腐胀食尸者', frames: 12, dungeonOnly: true },
    { key: 'graveWorm', name: '墓地蛆虫兽', frames: 5, dungeonOnly: true },
    { key: 'boneInfantry', name: '朽骨步兵', frames: 4, dungeonOnly: true },
    { key: 'wraith', name: '怨魂游荡者', frames: 16, dungeonOnly: true },
    { key: 'graveKnight', name: '破败守墓骑士', frames: 6, dungeonOnly: true },
    /* 迷雾副本（僵尸）：素材导入 tools/import_zombie_enemies.cjs
     *   杂兵 3 种 + 精英「缢魂女鬼」+ 关卡BOSS「僵尸王」（21 帧） */
    { key: 'zombie', name: '腐衣行尸', frames: 12, dungeonOnly: true },
    { key: 'graveGhost', name: '坟头怨鬼', frames: 11, dungeonOnly: true },
    { key: 'paperGuard', name: '纸俑卫兵', frames: 7, dungeonOnly: true },
    { key: 'hangedGhost', name: '缢魂女鬼', frames: 8, dungeonOnly: true },
    { key: 'zombieBoss', name: '僵尸王', frames: 21, dungeonOnly: true }
  ];
  /* 随机抽敌人模型的地方（道路 / 洞府 / 活动副本 / 虚影 / 召唤物）统一用这一份：
   *   把"只属于某张副本地图"的敌人排除掉，免得它们在马路上乱跑。 */
  function enemySpritePool() {
    return ENEMY_SPRITES.filter(function (s) { return !s.dungeonOnly; });
  }
  function randEnemySprite() {
    const list = enemySpritePool();
    return list[randInt(0, list.length - 1)];
  }
  function enemySpriteByKey(key) {
    for (let i = 0; i < ENEMY_SPRITES.length; i++) {
      if (ENEMY_SPRITES[i].key === key) return ENEMY_SPRITES[i];
    }
    return null;
  }
  const ENEMY_FRAME = { w: 204, h: 194, basePad: 14, anchorX: 93 };
  // 世界BOSS 专用立绘（森特欧斯萨满）：见 tools/make_boss_asset.cjs
  // 导出图是「游戏内显示尺寸的 2 倍」，所以绘制时用 scaleAdj 折半（超采样更清晰）
  // 每个世界BOSS 一张立绘（defs.js 里的 art 字段）；资源 key 统一是 'wbArt_' + id
  function wbBossArtKey(defOrId) {
    const id = typeof defOrId === 'string' ? defOrId : (defOrId && defOrId.id);
    return id ? 'wbArt_' + id : null;
  }
  // scaleAdj 0.68：显示尺寸 ≈ 665×608（比敌人立绘大得多，衬托"庞然大物"）
  const WB_BOSS_ART = { w: 376, h: 344, basePad: 6, scaleAdj: 0.68 };
  const ENEMY_ART_FACES_RIGHT = true; // 素材朝向：和玩家素材一样默认朝右（反了就改这里）
  /* 动画节奏按"跑完一整轮"的时长来定（不是固定帧率）：
   *   帧数多的模型（兵匪 6 帧 / 农夫 8 帧）不会因为帧率高而变成鬼畜，
   *   帧数少的（强盗 2 帧 / 水匪 2 帧）也不会慢得看不出在跑。 */
  const ENEMY_WALK_CYCLE = 0.72;      // 秒：赶路时跑完一轮跑步帧
  const ENEMY_STAND_CYCLE = 2.6;      // 秒：战斗站定时慢慢循环一轮
  function enemyWalkFps(def) { return Math.max(2, (def && def.frames ? def.frames : 1) / ENEMY_WALK_CYCLE); }
  function enemyStandFps(def) { return Math.max(1.2, (def && def.frames ? def.frames : 1) / ENEMY_STAND_CYCLE); }

  // ---------- 武器美术图标（用户提供的 2 组 ×30 张 32×32 像素图）----------
  const WEAPON_ICON_COUNT = 60; // 每件武器随机分一个，存在物品上，渲染时不会变
  // ---------- 防具美术图标（用户提供的 服装 素材：头盔 / 衣服 / 鞋子 / 手套 各 10 张）----------
  // 生成脚本：tools/make_gear_icons.cjs（512×512 源图缩到 128×128）
  const GEAR_ICON_COUNT = 10;
  // 装备槽 → 图标集合（集合名同时也是文件名前缀：helmet_1.png / clothes_3.png …）
  const GEAR_ICON_SET_BY_SLOT = {
    helmet: 'helmet',
    clothes: 'clothes',
    shoes: 'shoes',
    gloves: 'gloves',
    jewelry: 'jewelry'   // 首饰（戒指素材）
  };

  function gearIconUrl(setName, idx) {
    if (!setName || !idx) return null;
    if (setName === 'weapon') return assetUrl('assets/img/weapon/wicon_' + idx + '.png');
    return assetUrl('assets/img/gear/' + setName + '_' + idx + '.png');
  }

  // 给装备随机分配一张美术图标（存在物品上，之后渲染都用它，不会变）
  // 老存档里没有 iconSet 的装备也会在这里补齐
  function ensureItemIcon(item) {
    if (!item || item.type !== 'equipment') return item;
    let set = item.iconSet;
    if (!set) {
      set = item.slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[item.slot] || null);
      if (set) item.iconSet = set;
    }
    if (!item.iconImg && set) {
      item.iconImg = set === 'weapon' ? randInt(1, WEAPON_ICON_COUNT) : randInt(1, GEAR_ICON_COUNT);
    }
    return item;
  }

  const ASSETS = [
    // 玩家角色：生成素材处理后的统一帧（待机 4 帧 / 攻击 5 帧）
    ['player_idle_1', 'assets/img/player/player_idle_1.png'],
    ['player_idle_2', 'assets/img/player/player_idle_2.png'],
    ['player_idle_3', 'assets/img/player/player_idle_3.png'],
    ['player_idle_4', 'assets/img/player/player_idle_4.png'],
    ['player_atk_1', 'assets/img/player/player_atk_1.png'],
    ['player_atk_2', 'assets/img/player/player_atk_2.png'],
    ['player_atk_3', 'assets/img/player/player_atk_3.png'],
    ['player_atk_4', 'assets/img/player/player_atk_4.png'],
    ['player_atk_5', 'assets/img/player/player_atk_5.png'],
    // 门派矿山挖矿动作（5 帧）
    ['player_mine_1', 'assets/img/player/mining_v2_1.png'],
    ['player_mine_2', 'assets/img/player/mining_v2_2.png'],
    ['player_mine_3', 'assets/img/player/mining_v2_3.png'],
    ['player_mine_4', 'assets/img/player/mining_v2_4.png'],
    ['player_mine_5', 'assets/img/player/mining_v2_5.png'],
    // 矿石抛起/落回用的 crafting_materials 图标（7 张 20×20）
    ['ore_1', 'assets/img/ore/ore_v2_1.png'],
    ['ore_2', 'assets/img/ore/ore_v2_2.png'],
    ['ore_3', 'assets/img/ore/ore_v2_3.png'],
    ['ore_4', 'assets/img/ore/ore_v2_4.png'],
    ['ore_5', 'assets/img/ore/ore_v2_5.png'],
    ['ore_6', 'assets/img/ore/ore_v2_6.png'],
    ['ore_7', 'assets/img/ore/ore_v2_7.png'],
    ['oreProcessLight', 'assets/img/ore/process_light_v1.png'],
    ['oreProcessFile', 'assets/img/ore/process_file_v1.png'],
    ['oreProcessHammer', 'assets/img/ore/process_hammer_v1.png'],
    ['cactus',    'assets/img/cactus.png'],
    ['rock',      'assets/img/rock.png'],
    ['enemyIdle', 'assets/img/enemy_idle.png'],
    ['enemyWalkA', 'assets/img/enemy_walk_a.png'],
    ['enemyWalkB', 'assets/img/enemy_walk_b.png'],
    // 洞府入口美术（用户提供的最新 Build1.png，处理脚本 tools/make_cave_asset.cjs）
    // 注意：nginx 对 /assets/ 做了 immutable 长缓存，换图必须换文件名，否则浏览器会一直用旧图
    ['caveEntrance', 'assets/img/cave/cave_entrance3.png'],
    // 门派山门建筑：五座殿宇各自一套美术
    ['sectBuildHall', 'assets/img/sect/build_hall_v1.png'],
    ['sectBuildAlchemy', 'assets/img/sect/build_alchemy_v1.png'],
    ['sectBuildReception', 'assets/img/sect/build_reception_v1.png'],
    ['sectBuildMine', 'assets/img/sect/build_mine_v1.png'],
    ['sectBuildCultivation', 'assets/img/sect/build_cultivation_v1.png'],
    // 门派建筑标题图片（替换原来的文字牌）
    ['sectTitleHall', 'assets/img/sect/title_hall_v1.png'],
    ['sectTitleAlchemy', 'assets/img/sect/title_alchemy_v1.png'],
    ['sectTitleReception', 'assets/img/sect/title_reception_v1.png'],
    ['sectTitleMine', 'assets/img/sect/title_mine_v1.png'],
    ['sectTitleCultivation', 'assets/img/sect/title_cultivation_v1.png'],
    // 世界BOSS：战场宝箱（击杀后走到屏幕中央亲手开启）+ 奖励宝箱外观 + 掉在地上的战利品展示
    ['wbChestBig', 'assets/img/boss/wb_chest_big.png'],
    ['wbChestBox', 'assets/img/boss/wb_chest_box.png'],
    ['wbGift1', 'assets/img/boss/wb_gift1.png'],
    ['wbGift2', 'assets/img/boss/wb_gift2.png'],
    ['wbGift3', 'assets/img/boss/wb_gift3.png'],
    ['wbGift4', 'assets/img/boss/wb_gift4.png'],
    // 「气血反弹」血气虚影：不规则椭圆冲击波 + 拖尾（透明 PNG）
    ['bloodReboundFx', 'assets/img/fx/blood_rebound_v2.png'],
    // 「劈砍」刀光：itch.io「Pixel Art Animations - Slashes」64/128 像素刀光（9 帧，红色）
    ['slash1_1', 'assets/img/slash1/slash1_1.png'],
    ['slash1_2', 'assets/img/slash1/slash1_2.png'],
    ['slash1_3', 'assets/img/slash1/slash1_3.png'],
    ['slash1_4', 'assets/img/slash1/slash1_4.png'],
    ['slash1_5', 'assets/img/slash1/slash1_5.png'],
    ['slash1_6', 'assets/img/slash1/slash1_6.png'],
    ['slash1_7', 'assets/img/slash1/slash1_7.png'],
    ['slash1_8', 'assets/img/slash1/slash1_8.png'],
    ['slash1_9', 'assets/img/slash1/slash1_9.png'],
    // 普通攻击刀光：同一资源包的 Slash 3（64×64，9 帧）
    ['slash3_1', 'assets/img/slash3/slash3_1.png'],
    ['slash3_2', 'assets/img/slash3/slash3_2.png'],
    ['slash3_3', 'assets/img/slash3/slash3_3.png'],
    ['slash3_4', 'assets/img/slash3/slash3_4.png'],
    ['slash3_5', 'assets/img/slash3/slash3_5.png'],
    ['slash3_6', 'assets/img/slash3/slash3_6.png'],
    ['slash3_7', 'assets/img/slash3/slash3_7.png'],
    ['slash3_8', 'assets/img/slash3/slash3_8.png'],
    ['slash3_9', 'assets/img/slash3/slash3_9.png'],
    // 召唤宠物素材：沙漠敌人包（48×48/帧，idle / walk / attack / hurt 四种动作）
    ['pet_scorpio_idle', 'assets/img/pets/scorpio_idle.png'],
    ['pet_scorpio_walk', 'assets/img/pets/scorpio_walk.png'],
    ['pet_scorpio_attack', 'assets/img/pets/scorpio_attack.png'],
    ['pet_scorpio_hurt', 'assets/img/pets/scorpio_hurt.png'],
    ['pet_hyena_idle', 'assets/img/pets/hyena_idle.png'],
    ['pet_hyena_walk', 'assets/img/pets/hyena_walk.png'],
    ['pet_hyena_attack', 'assets/img/pets/hyena_attack.png'],
    ['pet_hyena_hurt', 'assets/img/pets/hyena_hurt.png'],
    ['pet_snake_idle', 'assets/img/pets/snake_idle.png'],
    ['pet_snake_walk', 'assets/img/pets/snake_walk.png'],
    ['pet_snake_attack', 'assets/img/pets/snake_attack.png'],
    ['pet_snake_hurt', 'assets/img/pets/snake_hurt.png'],
    ['pet_vulture_idle', 'assets/img/pets/vulture_idle.png'],
    ['pet_vulture_walk', 'assets/img/pets/vulture_walk.png'],
    ['pet_vulture_attack', 'assets/img/pets/vulture_attack.png'],
    ['pet_vulture_hurt', 'assets/img/pets/vulture_hurt.png']
  ];

  // 敌人的 32 张帧按类型批量挂进来（加载进度条会一起算进去）
  ENEMY_SPRITES.forEach(function (s) {
    for (let i = 1; i <= s.frames; i++) {
      ASSETS.push([s.key + '_' + i, 'assets/img/enemy/' + s.key + '_' + i + '.png']);
    }
  });

  // 「防御」护盾特效：pipo-btleffect208（192×192，20 帧，淡蓝能量冲击）
  // 导入脚本：tools/make_defend_fx.cjs
  for (let dfi = 1; dfi <= DEFEND_FX_FRAMES; dfi++) {
    ASSETS.push(['defend_' + dfi, 'assets/img/defend/defend_' + dfi + '.png']);
  }

  // 「破空斩」剑气：HolySlash_B（64×64，4 帧）
  // 导入脚本：tools/make_holyslash_fx.cjs
  for (let hsi = 1; hsi <= AIRSLASH_FX_FRAMES; hsi++) {
    ASSETS.push(['holyslash_' + hsi, 'assets/img/holyslash/holyslash_' + hsi + '.png']);
  }

  // 「盾墙 · 碎石崩裂」碎石：Foozle Rock（64×64，10 帧）
  // 导入脚本：tools/import_rock_fx.cjs
  for (let ri = 1; ri <= ROCK_FX_FRAMES; ri++) {
    ASSETS.push(['rock_' + ri, 'assets/img/fx/rock_' + ri + '.png']);
  }

  // 「强力射击」法术：Wispstrike（256×256，flight 18 帧 / impact 19 帧）
  //   flight = 飞行形态（从角色胸前平射）；impact = 完美时机命中后的二次形态
  // 导入脚本：tools/import_wispstrike_fx.cjs
  for (let wf = 1; wf <= WISP_FLIGHT_FRAMES; wf++) {
    ASSETS.push(['wisp_flight_' + wf, 'assets/img/wispstrike/flight_' + wf + '.png']);
  }
  for (let wi2 = 1; wi2 <= WISP_IMPACT_FRAMES; wi2++) {
    ASSETS.push(['wisp_impact_' + wi2, 'assets/img/wispstrike/impact_' + wi2 + '.png']);
  }

  // 独特「武者的刑具」·熔岩骤雨：Fire Arrow 8 帧（从玩家头顶 45° 斜落）
  // 导入脚本：tools/import_fire_arrow_fx.cjs
  for (let fa = 1; fa <= FIRE_ARROW_FRAMES; fa++) {
    ASSETS.push(['fireArrow_' + fa, 'assets/img/firearrow/fireArrow_' + fa + '.png']);
  }

  // 武器图标（60 张，很小，一起进加载进度条）
  for (let wi = 1; wi <= WEAPON_ICON_COUNT; wi++) {
    ASSETS.push(['wicon_' + wi, 'assets/img/weapon/wicon_' + wi + '.png']);
  }

  // 防具图标（头盔 / 衣服 / 鞋子 / 手套 各 10 张，共 40 张，也会进加载进度条）
  Object.keys(GEAR_ICON_SET_BY_SLOT).forEach(function (slotKey) {
    const setName = GEAR_ICON_SET_BY_SLOT[slotKey];
    for (let gi = 1; gi <= GEAR_ICON_COUNT; gi++) {
      ASSETS.push(['gear_' + setName + '_' + gi, 'assets/img/gear/' + setName + '_' + gi + '.png']);
    }
  });

  /* 飞虹（练气期后远处天空划过的飞虹）——程序生成的透明底精灵图，
   * 生成脚本：tools/make_sky_streak.cjs。
   * 每一道飞虹 = 两条极细的丝线（a / b 各一张），游戏里第二条上下镜像 + 反向摆动，
   * 两条线一路穿插交错，就是"穿梭"的效果。三种配色：六色飞虹 / 青白剑气 / 金赤仙光。 */
  const SKY_FLIGHT_ART_VARIANTS = 3;
  for (let sf = 1; sf <= SKY_FLIGHT_ART_VARIANTS; sf++) {
    ASSETS.push(['skyFlight_' + sf + 'a', 'assets/img/sky/flight_' + sf + 'a.png']);
    ASSETS.push(['skyFlight_' + sf + 'b', 'assets/img/sky/flight_' + sf + 'b.png']);
  }
  /* 两条丝线"触碰"时炸开的烟尘（10 帧，导入脚本：tools/import_sky_smoke.cjs） */
  const SKY_SMOKE_FRAMES = 10;
  for (let sm = 1; sm <= SKY_SMOKE_FRAMES; sm++) {
    ASSETS.push(['skySmoke_' + sm, 'assets/img/sky/smoke/smoke_' + sm + '.png']);
  }

  // 清单里的路径统一补成绝对地址：这样 loadAssets() / 软加载 / 装备图标
  // 都自动带上资源基址，挂到第三方平台也不会再去请求平台域名。
  ASSETS.forEach(function (a) { a[1] = assetUrl(a[1]); });

  // ---------- DOM ----------
  const ui = {
    title: document.getElementById('titleScreen'),
    start: document.getElementById('btnStart'),
    loginScreen: document.getElementById('loginScreen'),
    loginHelpBtn: document.getElementById('loginHelpBtn'),
    loginHelp: document.getElementById('loginHelp'),
    loginUser: document.getElementById('loginUser'),
    loginPwd: document.getElementById('loginPwd'),
    loginIp: document.getElementById('loginIp'),
    loginError: document.getElementById('loginError'),
    btnLogin: document.getElementById('btnLogin'),
    btnWipe: document.getElementById('btnWipe'),
    introDialog: document.getElementById('introDialog'),
    introDialogBtn: document.getElementById('introDialogBtn'),
    questPopup: document.getElementById('questPopup'),
    questNowStage: document.getElementById('questNowStage'),
    questAccept: document.getElementById('questAccept'),
    wipePopup: document.getElementById('wipePopup'),
    wipeConfirm: document.getElementById('wipeConfirm'),
    wipeCancel: document.getElementById('wipeCancel'),
    titleUser: document.getElementById('titleUser'),
    hud: document.getElementById('hud'),
    wrap: document.getElementById('wrap'),
    menuGrow: document.getElementById('menuGrow'),
    menuTrade: document.getElementById('menuTrade'),
    subGrow: document.getElementById('subGrow'),
    subTrade: document.getElementById('subTrade'),
    dist: document.getElementById('distDisplay'),
    flaskWrap: document.getElementById('flaskWrap'),
    btnFlask: document.getElementById('btnFlask'),
    flaskCount: document.getElementById('flaskCount'),
    goldValue: document.getElementById('goldValue'),
    vitalityValue: document.getElementById('vitalityValue'),
    playerLv: document.getElementById('playerLv'),
    xpFill: document.getElementById('xpFill'),
    xpText: document.getElementById('xpText'),
    onlineTimer: document.getElementById('onlineTimer'),
    btnBreak: document.getElementById('btnBreak'),
    btnOnlineClaim: document.getElementById('btnOnlineClaim'),
    btnGear: document.getElementById('btnGear'),
    btnShop: document.getElementById('btnShop'),
    btnSign: document.getElementById('btnSign'),
    btnAuto: document.getElementById('btnAuto'),
    btnMineStop: document.getElementById('btnMineStop'),
    btnExchange: document.getElementById('btnExchange'),
    legendBar: document.getElementById('legendBar'),
    legendWave: document.getElementById('legendWave'),
    legendKm: document.getElementById('legendKm'),

    controls: document.getElementById('controls'),
    btnMove: document.getElementById('btnMove'),
    btnAttack: document.getElementById('btnAttack'),
    btnSpell: document.getElementById('btnSpell'),
    btnDefend: document.getElementById('btnDefend'),

    battleTint: document.getElementById('battleTint'),
    battleHud: document.getElementById('battleHud'),
    playerHpBar: document.getElementById('playerHpBar'),
    playerMpBar: document.getElementById('playerMpBar'),
    playerMpReserve: document.getElementById('playerMpReserve'),
    playerHpText: document.getElementById('playerHpText'),
    mpText: document.getElementById('mpText'),
    battleLog: document.getElementById('battleLog'),

    spellPopup: document.getElementById('spellPopup'),
    spellList: document.getElementById('spellList'),
    spellClose: document.getElementById('spellClose'),
    spellHint: document.getElementById('spellHint'),
    spellActiveHint: document.getElementById('spellActiveHint'),
    skillTip: document.getElementById('skillTip'),
    itemTip: document.getElementById('itemTip'),
    cmpTip: document.getElementById('cmpTip'),
    wbBossTip: document.getElementById('wbBossTip'),
    wbTipName: document.getElementById('wbTipName'),
    wbTipPlace: document.getElementById('wbTipPlace'),
    wbTipImg: document.getElementById('wbTipImg'),
    wbTipDetail: document.getElementById('wbTipDetail'),
    btnDonate: document.getElementById('btnDonate'),
    musicControl: document.getElementById('musicControl'),
    btnMusic: document.getElementById('btnMusic'),
    musicVolume: document.getElementById('musicVolume'),
    musicVolumeText: document.getElementById('musicVolumeText'),
    sfxVolume: document.getElementById('sfxVolume'),
    sfxVolumeText: document.getElementById('sfxVolumeText'),
    beianBar: document.getElementById('beianBar'),
    donatePopup: document.getElementById('donatePopup'),
    donateClose: document.getElementById('donateClose'),
    donateAlipay: document.getElementById('donateAlipay'),
    donateWechat: document.getElementById('donateWechat'),
    btnWorldBoss: document.getElementById('btnWorldBoss'),
    wbPopup: document.getElementById('worldBossPopup'),
    wbList: document.getElementById('wbList'),
    wbNote: document.getElementById('wbNote'),
    wbClose: document.getElementById('wbClose'),
    wbClockNow: document.getElementById('wbClockNow'),
    wbClockSrc: document.getElementById('wbClockSrc'),
    wbRewardState: document.getElementById('wbRewardState'),
    wbSchedule: document.getElementById('wbSchedule'),
    wbHelpBtn: document.getElementById('wbHelpBtn'),
    wbHelp: document.getElementById('wbHelp'),
    wbHelpClose: document.getElementById('wbHelpClose'),
    wbRewardPopup: document.getElementById('wbRewardPopup'),
    wbRewardChests: document.getElementById('wbRewardChests'),
    wbRewardNote: document.getElementById('wbRewardNote'),
    wbRewardProgressText: document.getElementById('wbRewardProgressText'),
    wbRewardDots: document.getElementById('wbRewardDots'),
    wbRewardClose: document.getElementById('wbRewardClose'),
    wbBar: document.getElementById('wbBar'),
    wbBarName: document.getElementById('wbBarName'),
    wbBarLv: document.getElementById('wbBarLv'),
    wbBarReq: document.getElementById('wbBarReq'),
    wbHpFill: document.getElementById('wbHpFill'),
    wbHpText: document.getElementById('wbHpText'),
    wbBarTime: document.getElementById('wbBarTime'),
    wbLeave: document.getElementById('wbLeave'),

    lootPopup: document.getElementById('lootPopup'),
    lootList: document.getElementById('lootList'),
    lootClose: document.getElementById('lootClose'),

    gearPopup: document.getElementById('gearPopup'),
    equipSlots: document.getElementById('equipSlots'),
    bagList: document.getElementById('bagList'),
    bagMenu: document.getElementById('bagMenu'),
    gearClose: document.getElementById('gearClose'),
    // 装备回收窗口（白/蓝/金 一键回收 + 托管自动回收）
    btnRecycle: document.getElementById('btnRecycle'),
    recyclePopup: document.getElementById('recyclePopup'),
    recycleTiers: document.getElementById('recycleTiers'),
    recyclePreview: document.getElementById('recyclePreview'),
    recycleRun: document.getElementById('recycleRun'),
    recycleClose: document.getElementById('recycleClose'),
    reforgePopup: document.getElementById('reforgePopup'),
    reforgeIcon: document.getElementById('reforgeIcon'),
    reforgeModelName: document.getElementById('reforgeModelName'),
    reforgeModelMeta: document.getElementById('reforgeModelMeta'),
    reforgeList: document.getElementById('reforgeList'),
    // 装备镶嵌窗口
    inlayPopup: document.getElementById('inlayPopup'),
    inlayIcon: document.getElementById('inlayIcon'),
    inlayModelName: document.getElementById('inlayModelName'),
    inlayModelMeta: document.getElementById('inlayModelMeta'),
    inlayList: document.getElementById('inlayList'),
    inlayPreview: document.getElementById('inlayPreview'),
    btnInlay: document.getElementById('btnInlay'),
    inlayClose: document.getElementById('inlayClose'),
    oreProcessPopup: document.getElementById('oreProcessPopup'),
    oreProcessTitle: document.getElementById('oreProcessTitle'),
    oreProcessCanvas: document.getElementById('oreProcessCanvas'),
    oreProcessStatus: document.getElementById('oreProcessStatus'),
    oreProcessSteps: document.getElementById('oreProcessSteps'),
    oreProcessFinal: document.getElementById('oreProcessFinal'),
    oreProcessClose: document.getElementById('oreProcessClose'),
    reforgeHint: document.getElementById('reforgeHint'),
    btnReforge: document.getElementById('btnReforge'),
    reforgeClose: document.getElementById('reforgeClose'),
    derivedText: document.getElementById('derivedText'),

    btnChar: document.getElementById('btnChar'),
    btnSkill: document.getElementById('btnSkill'),
    btnPet: document.getElementById('btnPet'),
    petPopup: document.getElementById('petPopup'),
    petList: document.getElementById('petList'),
    petNote: document.getElementById('petNote'),
    petClose: document.getElementById('petClose'),
    skillPopup: document.getElementById('skillPopup'),
    skillList: document.getElementById('skillList'),
    skillNote: document.getElementById('skillNote'),
    skillClose: document.getElementById('skillClose'),
    charPopup: document.getElementById('charPopup'),
    charClose: document.getElementById('charClose'),
    charStage: document.getElementById('charStage'),
    charPoints: document.getElementById('charPoints'),
    charPointNote: document.getElementById('charPointNote'),
    charAttrs: document.getElementById('charAttrs'),
    charStats: document.getElementById('charStats'),
    charSlots: document.getElementById('charSlots'),
    charFigureImg: document.getElementById('charFigureImg'),
    armorHelp: document.getElementById('armorHelp'),
    armorHelpClose: document.getElementById('armorHelpClose'),
    armorHelpFormula: document.getElementById('armorHelpFormula'),
    armorHelpTable: document.getElementById('armorHelpTable'),

    shopPopup: document.getElementById('shopPopup'),
    shopList: document.getElementById('shopList'),
    shopGold: document.getElementById('shopGold'),
    shopClose: document.getElementById('shopClose'),

    chestPopup: document.getElementById('chestPopup'),
    chestWeapons: document.getElementById('chestWeapons'),
    chestGem: document.getElementById('chestGem'),
    chestStone: document.getElementById('chestStone'),
    chestClose: document.getElementById('chestClose'),
    deathPopup: document.getElementById('deathPopup'),
    deathInfo: document.getElementById('deathInfo'),
    deathNote: document.getElementById('deathNote'),
    deathLoss: document.getElementById('deathLoss'),
    duelPopup: document.getElementById('duelPopup'),
    duelInfo: document.getElementById('duelInfo'),
    duelCountdown: document.getElementById('duelCountdown'),
    duelAccept: document.getElementById('duelAccept'),
    duelFlee: document.getElementById('duelFlee'),
    pvpTurnRow: document.getElementById('pvpTurnRow'),
    reviveBtn: document.getElementById('reviveBtn'),

    gemPopup: document.getElementById('gemPopup'),
    gemOptions: document.getElementById('gemOptions'),
    gemClose: document.getElementById('gemClose'),

    socketPopup: document.getElementById('socketPopup'),
    socketOptions: document.getElementById('socketOptions'),
    socketClose: document.getElementById('socketClose'),

    exchangePopup: document.getElementById('exchangePopup'),
    exchangeList: document.getElementById('exchangeList'),
    exchangeNote: document.getElementById('exchangeNote'),
    exchangeClose: document.getElementById('exchangeClose'),

    btnLog: document.getElementById('btnLog'),
    logPopup: document.getElementById('logPopup'),
    logList: document.getElementById('logList'),
    logClose: document.getElementById('logClose'),

    chatPanel: document.getElementById('chatPanel'),
    loadPanel: document.getElementById('loadPanel'),
    loadFill: document.getElementById('loadFill'),
    loadPct: document.getElementById('loadPct'),
    loadTip: document.getElementById('loadTip'),
    // 开场 CG
    cgScreen: document.getElementById('cgScreen'),
    cgCanvas: document.getElementById('cgCanvas'),
    cgIndex: document.getElementById('cgIndex'),
    cgTitle: document.getElementById('cgTitle'),
    cgDots: document.getElementById('cgDots'),
    cgSkip: document.getElementById('cgSkip'),
    chatList: document.getElementById('chatList'),
    chatForm: document.getElementById('chatForm'),
    chatStatus: document.getElementById('chatStatus'),
    identityPopup: document.getElementById('identityPopup'),
    idRealName: document.getElementById('idRealName'),
    idCardNo: document.getElementById('idCardNo'),
    identityError: document.getElementById('identityError'),
    identitySubmit: document.getElementById('identitySubmit'),
    identityCancel: document.getElementById('identityCancel'),
    nickPopup: document.getElementById('nickPopup'),
    nickInput: document.getElementById('nickInput'),
    nickError: document.getElementById('nickError'),
    nickSubmit: document.getElementById('nickSubmit'),
    nickCancel: document.getElementById('nickCancel'),
    nickNow: document.getElementById('nickNow'),
    btnEditNick: document.getElementById('btnEditNick'),
    rootRerollBtn: document.getElementById('rootRerollBtn'),
    chatInput: document.getElementById('chatInput'),
    chatToggle: document.getElementById('chatToggle'),
    chatEmoji: document.getElementById('chatEmoji'),
    chatEmojiPanel: document.getElementById('chatEmojiPanel'),
    btnSellOre: document.getElementById('btnSellOre'),
    chatDot: document.getElementById('chatDot'),
    chatStatus: document.getElementById('chatStatus'),

    btnCodex: document.getElementById('btnCodex'),
    btnGacha: document.getElementById('btnGacha'),
    btnDungeon: document.getElementById('btnDungeon'),
    dungeonPopup: document.getElementById('dungeonPopup'),
    btnActivity: document.getElementById('btnActivity'),
    btnQuest: document.getElementById('btnQuest'),
    // 阵营抉择弹窗（武者大圆满 · 命运分叉点）
    factionPopup: document.getElementById('factionPopup'),
    factionIntro: document.getElementById('factionIntro'),
    factionOptions: document.getElementById('factionOptions'),
    factionLater: document.getElementById('factionLater'),
    // 灵脉：异常灵气聚集地提示
    lingPrompt: document.getElementById('lingPrompt'),
    lingPromptText: document.getElementById('lingPromptText'),
    lingPromptGo: document.getElementById('lingGo'),
    lingPromptSkip: document.getElementById('lingSkip'),
    lingBoom: document.getElementById('lingBoom'),
    bottleneckPopup: document.getElementById('bottleneckPopup'),
    bottleneckList: document.getElementById('bottleneckList'),
    bottleneckClose: document.getElementById('bottleneckClose'),
    // 灵根：突破弹窗 + 角色窗口里的重新随机按钮
    rootPopup: document.getElementById('rootPopup'),
    rootName: document.getElementById('rootName'),
    rootGrade: document.getElementById('rootGrade'),
    rootDesc: document.getElementById('rootDesc'),
    rootCurrent: document.getElementById('rootCurrent'),
    rootAccept: document.getElementById('rootAccept'),
    rootReroll: document.getElementById('rootReroll'),
    rootClose: document.getElementById('rootClose'),
    rootDisc: document.getElementById('rootDisc'),
    rootGlyph: document.getElementById('rootGlyph'),
    rootStars: document.getElementById('rootStars'),
    rootEffects: document.getElementById('rootEffects'),
    rootRerollCost: document.getElementById('rootRerollCost'),
    rootCostHint: document.getElementById('rootCostHint'),
    // 重测灵根确认窗（材料消耗说明）
    rootRerollPopup: document.getElementById('rootRerollPopup'),
    rootRerollPopupCost: document.getElementById('rootRerollPopupCost'),
    rootRerollPopupOwn: document.getElementById('rootRerollPopupOwn'),
    rootRerollConfirm: document.getElementById('rootRerollConfirm'),
    rootRerollCancel: document.getElementById('rootRerollCancel'),
    // 每日任务窗口（注意：不能复用 questPopup —— 那个是引导任务弹窗在用）
    dailyPopup: document.getElementById('dailyQuestPopup'),
    dailyHead: document.getElementById('dailyQuestHead'),
    dailyBar: document.getElementById('dailyQuestBar'),
    dailyBarFill: document.getElementById('dailyQuestFill'),
    dailyList: document.getElementById('dailyQuestList'),
    dailyFoot: document.getElementById('dailyQuestFoot'),
    dailyClose: document.getElementById('dailyQuestClose'),
    // 门派窗口
    btnSect: document.getElementById('btnSect'),
    sectPopup: document.getElementById('sectPopup'),
    sectBody: document.getElementById('sectBody'),
    sectClose: document.getElementById('sectClose'),
    sectRoomOverlay: document.getElementById('sectRoomOverlay'),
    sectRoomTitle: document.getElementById('sectRoomTitle'),
    sectRoomBody: document.getElementById('sectRoomBody'),
    sectRoomBack: document.getElementById('sectRoomBack'),
    sectRoomClose: document.getElementById('sectRoomClose'),
    activityPopup: document.getElementById('activityPopup'),
    activityList: document.getElementById('activityList'),
    activityWindow: document.getElementById('activityWindow'),
    activityNext: document.getElementById('activityNext'),
    activityCount: document.getElementById('activityCount'),
    activityClose: document.getElementById('activityClose'),
    activityHelpBtn: document.getElementById('activityHelpBtn'),
    activityHelp: document.getElementById('activityHelp'),
    activityHelpClose: document.getElementById('activityHelpClose'),
    dungeonToday: document.getElementById('dungeonToday'),
    dungeonTestOpen: document.getElementById('dungeonTestOpen'),
    dungeonWeek: document.getElementById('dungeonWeek'),
    dungeonList: document.getElementById('dungeonList'),
    dungeonCountdown: document.getElementById('dungeonCountdown'),
    dungeonClose: document.getElementById('dungeonClose'),
    codexPopup: document.getElementById('codexPopup'),
    codexTiers: document.getElementById('codexTiers'),
    codexGrid: document.getElementById('codexGrid'),
    codexTip: document.getElementById('codexTip'),
    codexProgress: document.getElementById('codexProgress'),
    codexBonusText: document.getElementById('codexBonusText'),
    gachaPopup: document.getElementById('gachaPopup'),
    gachaGold: document.getElementById('gachaGold'),
    gachaPity: document.getElementById('gachaPity'),
    gachaCount: document.getElementById('gachaCount'),
    gachaResults: document.getElementById('gachaResults'),
    gachaRates: document.getElementById('gachaRates'),
    gachaStage: document.getElementById('gachaStage'),
    gachaStageCards: document.getElementById('gachaStageCards'),
    gachaStageCard: document.getElementById('gachaStageCard'),
    gachaStageTip: document.getElementById('gachaStageTip'),
    gachaHalo: document.getElementById('gachaHalo'),
    gachaOne: document.getElementById('gachaOne'),
    gachaTen: document.getElementById('gachaTen'),
    gachaCostNote: document.getElementById('gachaCostNote'),
    codexClose: document.getElementById('codexClose'),
    gachaClose: document.getElementById('gachaClose'),

    btnElite: document.getElementById('btnElite'),
    eliteCount: document.getElementById('eliteCount'),

    caveBar: document.getElementById('caveBar'),
    caveName: document.getElementById('caveName'),
    caveLeft: document.getElementById('caveLeft'),
    caveKilled: document.getElementById('caveKilled'),
    caveLeave: document.getElementById('caveLeave')
  };

  // ---------- 状态 ----------
  let gameState = 'loading'; // loading | title | walk | battle
  let images = {};
  let loaded = 0;
  let lastTime = performance.now();
  let hudTimer = 0;
  let slashFx = null; // 当前“劈砍”动画：{ t, enemyWx, hit, particles, ... }
  let damagePopups = []; // 飘出的伤害数字
  let pendingVictory = 0; // 击败演出的小延迟，让受击特效可见
  let battleTurn = 'player'; // player | enemy（回合制先后手）
  let enemyTurnTimer = 0;
  let playerIdleTimer = 0; // 玩家先手但 10 秒不行动时敌人抢攻

  /* ---------- 洞府敌人增幅作用在玩家身上的状态 ---------- */
  let playerStunTurns = 0;    // 被「震慑」击晕：跳过 N 个玩家回合
  let playerFrozenTurns = 0;  // 被「寒冰」冰冻：跳过 N 个玩家回合
  let playerBurnStacks = 0;   // 被「点燃」：每层每回合掉 1% 最大生命
  let caveAuraClock = 0;      // 「烈焰光环」每秒结算的计时器
  let corpse = null; // 可搜索的尸体 { worldX, level, searched }
  // 出战宠物列表（可以有多只；按等级从高到低排在玩家身后）
  const pets = [];
  let petSeq = 0;
  let petAttackCount = 0; // 宠物出手次数（调试 / 统计用）
  let gold = 0;
  let flaskCharges = 5; // 左下角血瓶充能（默认 5 层，靠击杀进度补充）
  // 血瓶充能进度：击败 10 个普通敌人 + 1 个精英/传奇敌人 → 充能 +1
  const FLASK_COMMON_NEED = 10;
  const FLASK_ELITE_NEED = 1;
  let flaskKillCommon = 0;
  let flaskKillElite = 0;
  let inventory = [];
  let shopItems = [];
  let lockedShopItems = []; // 被锁定、下次开店仍保留的商品
  let chest = null;         // 首次游戏的宝箱
  let newbieChestUsed = false;   // 新手宝箱每个档案只出一次（随存档保存）
  /* 装备回收：勾选的品级（白 / 蓝 / 金）。手动「一键回收」按它来，
   * 开启托管后也按它自动回收 —— 全是 false 时托管不会碰任何装备。 */
  let recycleFilter = { common: false, magic: false, rare: false };

  /* ---------- 时装（收藏品，不加属性；只在角色窗口的时装展示槽显示）----------
   * 目前只有头部槽；每件时装绑定一个世界BOSS，只从它的宝箱里掉落。 */
  let costumes = { head: null };   // { head: { id, name, detail, icon } | null }

  function costumeDef(id) {
    return COSTUMES.filter(function (c) { return c.id === id; })[0] || null;
  }
  function makeCostumeItem(def) {
    if (!def) return null;
    return {
      id: 'cs' + (++itemSeq),
      type: 'costume',
      costumeId: def.id,
      name: def.name,
      icon: def.icon || '衣',
      art: def.art,
      detail: def.desc || '',
      price: 0
    };
  }
  function equippedCostume(slotId) {
    return costumes[slotId] || null;
  }
  /* 时装槽 ↔ 装备槽的对应关系：
   *   头部时装不单独占格子，而是"覆盖"在头盔槽上展示（角色窗口里那一格）。 */
  const COSTUME_GEAR_SLOT = { head: 'helmet' };
  function costumeOnSlot(gearSlot) {
    let worn = null;
    Object.keys(COSTUME_GEAR_SLOT).forEach(function (csId) {
      if (!worn && COSTUME_GEAR_SLOT[csId] === gearSlot) worn = equippedCostume(csId);
    });
    return worn;
  }
  // 穿上时装（同槽位的旧时装退回背包）
  function equipCostume(item) {
    if (!item || item.type !== 'costume') return false;
    const def = costumeDef(item.costumeId);
    if (!def) return false;
    const old = costumes[def.slot];
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    costumes[def.slot] = { id: def.id, name: def.name, detail: item.detail || def.desc || '', icon: item.icon || def.icon };
    if (old && old.id !== def.id) {
      const back = makeCostumeItem(costumeDef(old.id));
      if (back) inventory.push(back);
    }
    showToast('已收藏时装「' + def.name + '」' + (old && old.id !== def.id ? '，原来那件已放回背包。' : '。（可在角色窗口的时装展示槽查看）'));
    pushBattleLog('收藏时装「' + def.name + '」（收藏品，只在角色窗口的时装展示槽显示）', 'reward');
    recalcPlayerStats();
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }
  // 脱下时装 → 放回背包
  function unequipCostume(slotId) {
    const worn = costumes[slotId];
    if (!worn) return false;
    const def = costumeDef(worn.id);
    costumes[slotId] = null;
    const back = makeCostumeItem(def);
    if (back) inventory.push(back);
    showToast('已取下时装「' + worn.name + '」（放回背包）。');
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }

  /* ---------- 任务系统（目前只有"第一个任务"） ----------
   * first：提升至武者大圆满（level 达到 TRIBULATION_STAGE = 武者十境·后期/大圆满） */
  let quests = { first: { accepted: false, done: false } };
  const QUEST_FIRST_STAGE = TRIBULATION_STAGE;
  let identifyingGem = null; // 正在选择效果的空白技能石
  let socketingGem = null;   // 正在镶嵌的技能石
  let usingStone = null;     // 正在使用的主动技能石
  let vitality = 0;          // 活力值：签到获得，自动冒险消耗
  let lastSignDate = '';
  let autoAdventure = false; // 自动冒险开关
  let autoKmLeft = 0;        // 当前 1 点活力剩余的可冒险公里数
  let autoLastX = 0;
  let autoSkillId = null;    // 法术窗口里「激活」的技能：自动冒险时优先使用它
  let restHealTimer = 0;     // 连续休息计时（满 60 秒回一次血）
  let cans = [];             // 马路上滚动的易拉罐
  let canTimer = 0;
  let playerDirtyT = 0;      // 被易拉罐脏水溅到的持续时间
  let lastCanStrike = null;  // 调试/测试：最近一次"易拉罐击中敌人并击晕"的结果
  let playerAtkT = 0;        // >0 表示正在播放玩家出手动作
  let miningFx = null;       // 门派矿山：挖矿动作 + 矿石抛起/落回/入包表现
  let miningContinuous = false; // 持续挖矿：开凿一次后自动循环，直到点「停止挖矿」
  let miningAnimClock = 0;   // 持续挖矿动画时钟：不受单轮矿石飞行阶段影响
  let mineOreDrawn = 0;      // 调试/测试：本轮流星矿石实际绘制次数
  let lastMineFrameKey = null; // 调试/测试：最近一次挖矿动作帧
  let lastPlayerFrameKey = null; // 最近一次真正画出去的玩家帧（调试/测试用）
  let debugHidePlayer = false;   // 调试用：临时隐藏玩家，便于量出角色在屏幕上的位置
  let debugHideEnemies = false;  // 调试用：临时隐藏敌人
  let debugForceFace = 0;        // 调试用：强制角色朝向（-1 朝左 / 1 朝右 / 0 不强制）
  // ---------- 玩家休息姿态（软加载：图不存在就自动回落待机呼吸） ----------
  const playerRestFrames = [];
  let playerResting = false;  // 当前是否处于"休息"状态
  let restPoseIdx = -1;       // 当前随到的休息姿态（0..PLAYER_REST_FRAMES-1）
  let restPoseForce = -1;     // 调试/预览：强制固定某一帧；-1 = 正常播放休息动画
  let restPoseTimer = 0;      // 距离下次随机换姿态还有多久
  let enemyAnimClock = 0;     // 敌人动画用的自由时钟（每秒 += dt）
  // ---------- 路上的人影：海市蜃楼错觉 ----------
  let mirage = null;         // 当前的虚影 { t, life, worldX, mode, enemyWx, walkT, phase }
  let mirageTimer = rand(8, 16); // 下一次"可能"出现的时间
  const dirtyDrops = [];     // 脏水飞溅粒子
  // 赶路扬尘：跑步时身后扬起的沙尘粒子
  const runDust = [];
  let runDustTimer = 0;
  let wasRunning = false;
  let dustSprite = null;
  let dustDrawn = 0;        // 已绘制过的尘粒数（调试用：确认真的画出来了）
  let eliteCharges = 0;      // 精英挑战次数
  let ally = null;           // “召唤普通敌人”的临时召唤物
  let enemyMinionTurns = 0;  // 精英“召唤普通敌人”的协助攻击次数
  let bloodRebound = false;  // 气血反弹：持续生效的开关状态
  let shieldWall = false;    // 盾墙：持续生效的开关状态（占用 25% 生命、+5% 格挡、格挡触发碎石崩裂）
  /* 「盾墙」：占用 25% 生命上限，常驻 +5% 格挡；每次成功格挡触发「碎石崩裂」 */
  const SHIELD_WALL_LIFE_PCT = 25;
  const SHIELD_WALL_BLOCK_PCT = 5;
  const SHIELD_WALL_PROC_LIFE_PCT = 25;   // 碎石崩裂的"25% 生命"真实伤害
  // 气血反弹的血色波纹：{ t, life, maxR, strength }
  const bloodWaves = [];
  let bloodWaveTimer = 0;
  /* 「气血反弹」冲向敌人的血气冲击波：
   *   { t, life, startX, endX, laneY, scale, flip, strength, burst } */
  const bloodBolts = [];
  const BLOOD_BOLT_MIN_HIT_T = 1.0;  // 发射后至少飞 1 秒才到敌人位置
  const BLOOD_BOLT_POST_T = 1.15;    // 命中后减速继续飞行的时长
  let bloodBoltDrawn = 0;     // 调试/测试：实际绘制帧数
  let bloodBoltSpawnCount = 0; // 调试/测试：累计触发的血气冲击波次数
  let enemyQuake = 0;        // 传奇BOSS落步/出手的地面余震
  let legendMap = null;      // 传奇BOSS地图：{ wave, startX, km }
  // ---------- 遗迹洞府 ----------
  const caveEntrances = [];  // 马路上的洞府入口：{ def, worldX, used }
  let cave = null;           // 当前所在的洞府：{ def, remaining, total, killed }
  let caveNextX = 0;         // 下一个洞府入口生成的位置
  let caveToken = 0;         // 金色洞府令牌数量（活力兑换获得）
  const battleGroup = [];    // 一场战斗里的敌人（[0] 为当前目标 activeEnemy）
  let pendingEliteGroup = []; // 系统赠送的精英挑战：1~3 个精英同时出现，进战时整组入队
  let onlineSeconds = 0;     // 在线时长（每满 1 小时获得 1 点活力）
  let onlineUiTimer = 0;
  const battleLogHistory = []; // 战斗日志（本次会话）
  let battleLogClock = 0;
  let equipment = {
    helmet: null,
    weapon: null,
    clothes: null,
    shoes: null,
    gloves: null,
    jewelry: null,
    treasure: null   // 灵宝：金色洞府终点掉落
  };
  let itemSeq = 0;
  let weatherTime = 0;
  const sandFar = [];
  const sandNear = [];
  const leaves = [];
  const derived = {
    maxHp: 100,
    maxMp: 30,
    armor: 0,
    flatAtk: 0,
    speedMult: 1,
    res: 0,
    thorns: 0,
    blockPct: 0,
    strength: 0,
    chaosRes: 0,
    critReduce: 0,
    armorPct: 0,
    lifeRegen: 0,
    atkSpeed: 0,
    critChance: 0,
    critDamage: 0,
    // 自由分配的属性点（角色窗口）
    attrStr: 0,
    attrAgi: 0,
    attrInt: 0,
    strengthTotal: 0,
    spellDmgPct: 0,
    // 持续 / 召唤类技能占用的灵力上限
    mpReservePct: 0,
    mpUsable: 30,
    mpReserved: 0
  };

  const input = { moving: false };

 const player = {
   worldX: START_X,
   vx: 0,
   walkT: 0,
   animT: 0,                  // 待机呼吸用的累计时间
   hurtT: 0,
    level: 1,
    xp: 0,
    bottleneck: false,
    attrs: { str: 0, agi: 0, int: 0 }, // 玩家自由分配的三维
    attrPoints: 0,                     // 未分配的属性点
    hpMaxBonus: 0,                     // 门派丹药等来源的永久生命上限加成
    mpMaxBonus: 0                      // 门派丹药等来源的永久灵力上限加成
  };

  // 玩家在战斗中的属性
  const combat = {
    hp: 100,
    maxHp: 100,
    mp: 30,
    maxMp: 30        // maxMp / mp 就是「灵力」（= 原来的法力槽，已合并）
  };

  // ---------- 装备槽 / 品级 ----------
  // 活力兑换（vitality 兑换物品）
  const EXCHANGE_ITEMS = [
    { cost: 30, label: '传奇BOSS挑战卷', note: '开启 10km 传奇BOSS地图', make: function () { return makeLegendTicket(); } },
    { cost: 30, label: '主动技能石', note: '升级/学习主动技能', make: function () { return makeActiveSkillStone(); } },
    { cost: 20, label: '随机辅助技能石', note: '镶嵌到主动技能', make: function () { return gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]); } },
    { cost: 30, label: '宠物技能书', note: '有几率提升宠物品级', make: function () { return makePetSkillBook(); } },
    {
      cost: CAVE_TOKEN_COST,
      label: '金色洞府令牌',
      note: '进入金色洞府所需（每次消耗 1 枚）',
      grant: function () { caveToken += 1; },
      owned: function () { return '已持有 ' + caveToken + ' 枚'; }
    }
  ];

  // 玩家已学会的技能/法术。技能书只能通过击败 BOSS 掉落获得。
 const knownSkills = [
   {
     id: 'slash',
       name: '劈砍',
       desc: '挥动武器劈向敌人，造成武器攻击力 75% 的伤害；30% 几率造成双倍伤害。',
       dmg: [34, 48],
       cost: 10, // 施放需要消耗灵力（打空时可以用不计灵力的普通攻击）
       type: 'melee',
       level: 1,
     sockets: []
   }
 ];

  // 初始技能（新档案 / 读档时没有技能数据时用）
  function starterSkill() {
    return {
      id: 'slash', name: '劈砍', desc: '挥动武器劈向敌人，造成武器攻击力 75% 的伤害；30% 几率造成双倍伤害。',
      dmg: [34, 48], cost: 10, type: 'melee', level: 1, sockets: []
    };
  }
  // 已经移除的技能（召唤宠物 / 横扫 / 强攻）：老存档里如果留着，读档时直接丢掉
  // 注：法杖灵宠本身还在（装备法杖宠物依旧自动现身），只是不再占用一个技能格
  const REMOVED_SKILL_IDS = ['summonPet', 'sweep', 'powerStrike'];
  function isRemovedSkillId(id) { return REMOVED_SKILL_IDS.indexOf(id) >= 0; }

  // 读档续玩的起点：玩家上次的里程（除非阵亡 / 新建档案，否则接着往前走）
  let resumeWorldX = null;

  let defeatedEnemies = 0;
  let killsTotal = 0;      // 累计击杀人数（不随阵亡 / 复活清零，存档里保存）
  /* 冒险道路的品级轮换：
   *   每击杀 3 个普通敌人 → 下一个刷蓝色敌人；
   *   每击杀 3 个蓝色敌人 → 下一个刷金色稀有（精英）敌人。
   *   只统计普通冒险道路上的击杀，洞府 / 传奇地图 / 临时副本 / 世界BOSS 不计入。 */
  const ROAD_BLUE_AFTER = 3;
  const ROAD_GOLD_AFTER = 3;
  let roadCommonKills = 0;   // 普通敌人击杀进度
  let roadMagicKills = 0;    // 蓝色敌人击杀进度
  let activeEnemy = null; // 当前路上的敌人（击败后生成下一个）

  // ---------- 小工具 ----------
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function randInt(a, b) { return Math.floor(rand(a, b + 1)); }
  function wrapInt(v, n) { return ((v % n) + n) % n; }
  function wrapX(v, span) { return ((v % span) + span) % span; }
  function hash01(v) {
    const s = Math.sin(v * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  }
  function roundRect(x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  // 在角色身侧弹出伤害数字（屏幕坐标，随画面自然缩放）
  // delay：延迟多少秒才出现（多重投射物的箭矢是尾随命中的，飘字跟着那一支的抵达时间）
  function addDamagePopup(sx, sy, dmg, color, positive, delay) {
    damagePopups.push({
      x: sx + rand(-6, 8),
      y: sy + rand(-8, 6),
      t: 0,
      life: 0.9,
      delay: Math.max(0, delay || 0),
      dmg: Math.max(1, Math.ceil(dmg)),
      color: color || '#ff5940',
      positive: !!positive
    });
  }

  // 纯文字飘字（例如“格挡”）
  function addTextPopup(sx, sy, text, color) {
    damagePopups.push({
      x: sx + rand(-6, 8),
      y: sy + rand(-8, 6),
      t: 0,
      life: 0.9,
      text: text,
      color: color || '#ffd9a8'
    });
  }

  function rollRarity() {
    const r = Math.random();
    if (r < 0.2) return 'rare';   // 黄色：3~6 条
    if (r < 0.55) return 'magic'; // 蓝色：1~2 条
    return 'common';
  }

  // 掉落品级与敌人品级绑定：普通敌人绝不掉黄色
  // 装备掉落品级：永远不超过敌人自己的品级
  //   tier     敌人品级        允许掉出的最高品级
  //   common   普通            白色
  //   magic    蓝色            蓝色
  //   elite    精英            黄色
  //   legend   传奇            黄色（暗金/灵宝只能从洞府终点等特殊途径拿）
  // force=true 时"必掉装备"（洞府内的怪走这条路），此时按品级加权抽一件，而不是有概率空手
  function rollGearForTier(tier, force) {
    const r = Math.random();
    if (tier === 'magic') {
      if (r < 0.3) return 'common';
      if (r < 0.4) return 'magic';
      return force ? (Math.random() < 0.68 ? 'common' : 'magic') : null;
    }
    if (tier === 'elite') {
      if (r < 0.3) return 'common';
      if (r < 0.4) return 'magic';
      if (r < 0.45) return 'rare';
      return force ? (Math.random() < 0.5 ? 'common' : (Math.random() < 0.6 ? 'magic' : 'rare')) : null;
    }
    if (tier === 'legend') {
      if (r < 0.3) return 'common';
      if (r < 0.5) return 'magic';
      if (r < 0.65) return 'rare';
      return force ? (Math.random() < 0.4 ? 'common' : (Math.random() < 0.58 ? 'magic' : 'rare')) : null;
    }
    // 普通敌人：只掉白色装备
    if (r < 0.3) return 'common';
    return force ? 'common' : null;   // 洞府里必掉，且只能是白色
  }

  // 一次击杀掉几件装备、各是什么品级（返回数组，可能为空）
  //   野外：按 rollGearForTier 概率掉 0~1 件
  //   洞府：必掉；其中精英怪是「黄色必掉 + 额外再掉一件（白/蓝）」
  function rollGearRarities(tier, force) {
    const t = tier || 'common';
    if (!force) {
      const r = rollGearForTier(t, false);
      return r ? [r] : [];
    }
    if (t === 'elite') {
      // 黄色装备必然掉落，且不受概率影响
      return ['rare', Math.random() < 0.68 ? 'common' : 'magic'];
    }
    return [rollGearForTier(t, true)];
  }

  // 装备词缀涉及的所有属性字段（洗练前先清零，避免旧值累加）
  /* 两项上限的基准值（「上限 +X%」词缀只抬上限，不直接给抗性 / 格挡本身） */
  const RES_CAP_BASE = 75;      // 全元素抗性默认上限 75%
  const BLOCK_CAP_BASE = 75;    // 格挡几率默认上限 75%
  /* 防御上限（按需求统一）：三种防御数值各自封顶，只有「上限 +X%」类词缀能抬 */
  const DODGE_CAP_BASE = 75;    // 闪避几率默认上限 75%
  const TOTAL_REDUCE_CAP = 0.90; // 常驻减伤（护甲/抗性 → 盾牌减伤 → 物理减免）合计上限 90%
  const CAP_RAISE_MAX = 30;     // 「XX上限 +X%」单项词缀最多 +30%（例：75% → 105%）
  /* 护甲词缀提供的减伤：50% 是**软上限**（拐点）。
   *   50% 以内按正常递减曲线走（约 120 点护甲到 50%）；
   *   超过之后继续堆护甲仍然会涨，但每点护甲只按 1/4 的收益递增（收益递减），
   *   所以护甲这一层的渐近上限约 62.5% —— 永远不会"堆了完全没用"。
   *   （元素 / 混沌抗性在这一层之外另算，整层仍然受总减伤上限约束） */
  const ARMOR_SOFT_CAP = 0.5;
  const ARMOR_POST_KNEE_MULT = 0.25;
  /* 武者境（未练气）冒险敌人的经验倍率：下调 30% */
  const WUZHE_XP_MULT = 0.7;
  /* 防具基底闪避点数换算成「闪避几率」的常数：闪避率 = 闪避 ÷（闪避 + 这个数）
   * 给 900 是为了让闪避（完全免伤）比同数值的护甲收益慢得多：
   *   4 件 × 14（1 级）= 56 → 5.9% ／ 4 件 × 100（20~30 级）= 400 → 30.8%
   *   4 件 × 280（70 级以上）= 1120 → 55.4%（再叠敏捷与「闪避几率 +X%」才容易摸到 75% 上限） */
  const EVASION_RATING_CONST = 900;

  const GEAR_STAT_KEYS = [
    'hp', 'armor', 'atk', 'speedPct', 'mp', 'res', 'thorns', 'blockPct', 'strength',
    'agility', 'intelligence',
    'chaosRes', 'critReduce', 'armorPct', 'lifeRegen', 'mpRegen', 'atkSpeed', 'critChance', 'critDamage',
    'extraArrowPct',
    // 手套专属：击杀 / 命中回复与异常状态几率（双属性在 applyOneItemMod 里拆成三维）
    'lifePerKill', 'mpPerKill', 'lifePerHit', 'poisonChance', 'bleedChance', 'igniteChance',
    // 盾牌专属：额外减伤与吸纳为生命
    'dmgReducePct', 'recoupPct',
    // 新增：物理伤害减免（盾牌 / 衣服）、抗性上限（盾牌 / 衣服）、格挡上限（盾牌）
    'physReducePct', 'resCapPct', 'blockCapPct',
    // 新增：闪避几率 / 闪避上限 / 总减伤上限（目前只有特殊装备会产出）
    'dodgePct', 'dodgeCapPct', 'reduceCapPct',
    // 新增：防具基底「闪避」点数（敏捷性质防具）与「闪避提高 X%」
    'evasion', 'evasionPct'
  ];

  const lastAffixRolls = [];   // 调试用：最近一次词缀随机的明细（key / 档位 / 数值）

  // 装备价格：只按品级在区间内浮动（不再按词缀强度算，避免首饰因为词缀强而普遍更贵）
  const GEAR_PRICE_BANDS = {
    common: [100, 300],   // 白色
    magic: [300, 500],    // 蓝色
    rare: [500, 1000]     // 黄色稀有
  };

  function rollGearPrice(rarity) {
    const band = GEAR_PRICE_BANDS[rarity] || GEAR_PRICE_BANDS.common;
    return randInt(band[0], band[1]);
  }

  // 词缀库分配：盾牌 / 头盔衣服 / 手套 / 首饰 / 其它（生成与洗练共用）
  /* 武器（长弓 / 短剑 / 爪 / 法杖）不该出现的词缀：
   *   荆棘伤害   —— 反伤属于盾牌 / 防具
   *   全元素抗性 —— 抗性属于防具 / 首饰，武器上不该有
   *   移动速度   —— 武器不提供移速（移速属于鞋子 / 首饰基底）
   * 注意：盾牌也是 slot=weapon，但它有自己的属性库（含抗性 / 荆棘），不受这条限制。 */
  const WEAPON_FORBIDDEN_MODS = ['thorns', 'res', 'speedPct'];
  function isPlainWeaponItem(item) {
    return !!item && item.slot === 'weapon' && item.weaponType !== '盾牌';
  }
  function affixPoolFor(slot, weaponType) {
    if (weaponType === '盾牌') return SHIELD_AFFIXES;
    /* 头盔 / 衣服同池，但衣服多两条专属（物理伤害减免 / 最大元素抗性上限）——
     * 头盔、手套、鞋子都不会滚出这两条。 */
    if (slot === 'clothes') return CLOTHES_AFFIXES;
    if (slot === 'helmet') return ARMOUR_AFFIXES;
    if (slot === 'gloves') return GLOVES_AFFIXES;
    if (slot === 'shoes') return SHOES_AFFIXES;   // 鞋子专用词缀库：分档位、不含攻击伤害
    if (slot === 'jewelry') return JEWELRY_AFFIXES;
    if (slot === 'treasure') return JEWELRY_AFFIXES; // 灵宝的常规词缀沿用首饰（特殊属性另算）
    /* 武器（长弓 / 短剑 / 爪 / 法杖）走专用词缀库：
     *   生命 / 护甲 / 攻击伤害 / 最大灵力 + 三维 + 攻击速度 / 暴击率 / 暴击伤害
     * 不含抗性、移动速度、荆棘。 */
    if (slot === 'weapon') return WEAPON_AFFIXES;
    return GENERIC_AFFIXES;
  }

  // 按装备自己的等级 + 品级重新随机全部词缀。
  // 「生成装备」与「洗练」共用这一套规则：等级只决定"能抽到哪一档"，
  // 所以 1 级装备洗练多少次也只会落在 1-10 级的档位区间里。
// 词缀 → 中文描述
  const AFFIX_TEXT = {
    hp: ['最大生命 +', ''],
    armor: ['护甲 +', ''],
    atk: ['攻击伤害 +', ''],
    speedPct: ['移动速度提高 ', '%'],
    mp: ['最大灵力 +', ''],
    res: ['全元素抗性 +', '%'],
    thorns: ['荆棘伤害 +', ''],
    blockPct: ['格挡几率 +', '%'],
    strength: ['力量 +', ''],
    agility: ['敏捷 +', ''],
    intelligence: ['智力 +', ''],
    chaosRes: ['混沌抗性 +', '%'],
    critReduce: ['暴击伤害降低 ', '%'],
    lifeRegen: ['生命每回合再生 +', ''],
    mpRegen: ['灵力每回合恢复 +', ''],
    armorPct: ['防御力提高 ', '%'],
    allAttr: ['全能力 +', ''],
    extraArrowPct: ['攻击时 ', '% 几率额外射出一支箭矢'],
    atkSpeed: ['攻击速度提高 ', '%'],
    critChance: ['暴击率 +', '%'],
    critDamage: ['暴击伤害 +', '%'],
    // 手套专属
    lifePerKill: ['每击败一名敌人获得 ', ' 生命'],
    mpPerKill: ['每击败一名敌人获得 ', ' 灵力'],
    lifePerHit: ['攻击每击中一名敌人获得 ', ' 生命'],
    strInt: ['力量与智慧 +', ''],
    strDex: ['力量与敏捷 +', ''],
    intDex: ['智慧与敏捷 +', ''],
    poisonChance: ['攻击时有 ', '% 几率造成中毒'],
    bleedChance: ['攻击时有 ', '% 几率造成流血'],
    igniteChance: ['攻击时有 ', '% 几率造成点燃'],
    // 盾牌专属
    dmgReducePct: ['受到的伤害降低 ', '%'],
    recoupPct: ['将所受伤害的 ', '% 吸纳为生命'],
    // 新增三条（盾牌 / 衣服）
    physReducePct: ['物理伤害减免 ', '%'],
    resCapPct: ['最大元素抗性上限 +', '%'],
    blockCapPct: ['格挡几率上限 +', '%'],
    // 闪避 / 总减伤上限（特殊装备才产出，池子里暂时没有）
    dodgePct: ['闪避几率 +', '%'],
    dodgeCapPct: ['闪避几率上限 +', '%'],
    reduceCapPct: ['总减伤上限 +', '%'],
    // 防具基底：闪避点数 / 闪避提高
    evasion: ['闪避 +', ''],
    evasionPct: ['闪避提高 ', '%']
  };

  function modToText(mod) {
    if (mod.label) return mod.label;      // 基底词缀自带说法（首饰基底池）
    if (mod.key === 'hybridArmor') {
      return '护甲提高 ' + mod.value + '%，生命上限 +' + (mod.value2 || 0);
    }
    const t = AFFIX_TEXT[mod.key];
    return t ? t[0] + mod.value + t[1] : (mod.key + ' +' + mod.value);
  }

  // 反向解析：老存档里只有 detail 文本、没有逐条词缀数据时，按文本还原成词缀（数值不变）
  function affixTextToMod(text) {
    const hy = text.match(/^护甲提高 (\d+)%，生命上限 \+(\d+)$/);
    if (hy) return { key: 'hybridArmor', value: Number(hy[1]), value2: Number(hy[2]), bandIdx: 0, locked: false };
    for (const key in AFFIX_TEXT) {
      if (!Object.prototype.hasOwnProperty.call(AFFIX_TEXT, key)) continue;
      const pre = AFFIX_TEXT[key][0];
      const suf = AFFIX_TEXT[key][1];
      if (text.indexOf(pre) !== 0) continue;
      if (suf && text.slice(-suf.length) !== suf) continue;
      const num = parseInt(text.slice(pre.length).replace('%', ''), 10);
      if (!isNaN(num)) return { key: key, value: num, value2: 0, bandIdx: 0, locked: false };
    }
    return null;
  }

  // 随机一条词缀。等级只决定"最高能到第几档"：1 级装备永远只落在第 1 档（1-10 级区间）
  /* ---------- 武器攻击伤害（词缀「攻击伤害 +X」）----------
   *   atkBase 只跟「武器种类 + 装备等级」线性相关，品级只做小幅加成：
   *     长弓 1.5 + 等级×1.00 ／ 短剑 1.2 + 等级×0.85 ／ 爪 1.0 + 等级×0.72 ／ 法杖 1.0 + 等级×0.60
   *   同一条词缀的实际取值 = atkBase × (0.8 ~ 1.2) × (1 + 10% × (品级 - 1))
   *   （白 1 档 / 蓝 2 档 / 金 3 档 → 金色比白色高 20%）
   *   调平衡只需要动上面这几个系数，`__debugWeaponAtkTable()` 会按同一套公式出表。 */
  function weaponAtkBase(weaponType, level) {
    if (weaponType === '长弓') return 1.5 + level * 1.0;
    if (weaponType === '短剑') return 1.2 + level * 0.85;
    if (weaponType === '爪') return 1.0 + level * 0.72;
    if (weaponType === '法杖') return 1.0 + level * 0.6;
    return 1 + level * 0.5;                 // 非武器槽（防具上的攻击伤害）走这条
  }
  function weaponAtkBounds(weaponType, level, tier) {
    const base = weaponAtkBase(weaponType, Math.max(1, level)) * (1 + ((tier || 1) - 1) * 0.10);
    return { min: Math.max(1, Math.round(base * 0.8)), max: Math.max(1, Math.round(base * 1.2)) };
  }
  function rollWeaponAtk(weaponType, level, tier) {
    const base = weaponAtkBase(weaponType, Math.max(1, level)) * (1 + ((tier || 1) - 1) * 0.10);
    return Math.max(1, Math.round(base * (0.8 + rand(0, 0.4))));
  }

  function rollOneMod(slot, weaponType, level, rarity, pool) {
    const rar = RARITY_DEFS[rarity] || RARITY_DEFS.common;
    const tier = rar.tier;
    const statMult = 1 + (level - 1) * 0.08 + (tier - 1) * 0.22;
    const mod = pool[randInt(0, pool.length - 1)];
    let value = 1;
    let value2 = 0;
    let usedBand = -1;
    if (mod.bands) {
      const maxBand = Math.min(mod.bands.length - 1, Math.floor((level - 1) / AFFIX_BAND_SIZE));
      const bandIdx = randInt(0, maxBand);
      usedBand = bandIdx;
      const range = mod.bands[bandIdx];
      value = randInt(range[0], range[1]);
      if (mod.bands2) {
        const range2 = mod.bands2[Math.min(bandIdx, mod.bands2.length - 1)];
        value2 = randInt(range2[0], range2[1]);
      }
    } else {
      let base;
      if (mod.key === 'atk') {
        // 见上面 weaponAtkBase()：只按等级线性给，品级小幅加成（22 级长弓约 +19~28）
        value = slot !== 'weapon'
          ? Math.max(1, Math.round((1 + level * 0.5) * (0.8 + rand(0, 0.4)) * (1 + (tier - 1) * 0.10)))
          : rollWeaponAtk(weaponType, level, tier);
        return { key: mod.key, value: value, value2: 0, bandIdx: -1, locked: false };
      } else if (mod.key === 'hp') {
        base = (slot === 'helmet' || slot === 'clothes' ? 5 : 3) + level * 0.5;
      } else if (mod.key === 'armor') {
        base = (slot === 'clothes' ? 6 : slot === 'helmet' ? 4 : 3) + level * 0.45;
      } else if (mod.key === 'speedPct') {
        base = (slot === 'shoes' ? 4 : weaponType === '爪' ? 3 : 2) + level * 0.18;
      } else if (mod.key === 'mp') {
        base = (weaponType === '法杖' ? 5 : 4) + level * 0.55;
      } else if (mod.key === 'thorns') {
        base = 2 + level * 0.6;
      } else {
        base = (weaponType === '法杖' ? 3 : 2) + level * 0.2;
      }
      value = Math.max(1, Math.round((base + rand(0, base * 0.9)) * statMult));
    }
    return { key: mod.key, value: value, value2: value2, bandIdx: usedBand, locked: false };
  }

  // 把逐条词缀汇总成：属性数值 / 中文描述 / 价格（生成、洗练、老存档迁移都走这里）
  /* 基底词缀（implicit）：盾牌自带「格挡几率 +15%」。
   * 它不算随机词缀条数，洗练也不会动它，只跟着武器种类走。
   * 老存档里没有 baseMods 的盾牌会在这里自动补齐。 */
  function baseModsFor(item) {
    if (!item) return [];
    if (Array.isArray(item.baseMods) && item.baseMods.length) return item.baseMods;
    let def = null;
    if (item.slot === 'weapon') {
      def = WEAPON_BASE_MODS[item.weaponType] || null;
    } else if (item.slot === 'jewelry') {
      // 首饰：生成时从基底池里随机抽一条（抽到就固定在物品上）
      const pick = JEWELRY_BASE_POOL[randInt(0, JEWELRY_BASE_POOL.length - 1)];
      def = pick ? pick.mods : null;
    }
    if (!def || !def.length) return [];
    item.baseMods = def.map(function (m) {
      return { key: m.key, value: m.value, value2: m.value2 || 0, base: true };
    });
    // 首饰基底用词缀池里写好的说法（生命恢复 +5 / 全能力 +5 …），比通用文案更直观
    if (item.slot === 'jewelry' && item.baseMods.length === 1) {
      const pick = JEWELRY_BASE_POOL.filter(function (p) { return p.mods === def; })[0];
      if (pick && pick.label) item.baseMods[0].label = pick.label;
    }
    return item.baseMods;
  }

  /* ---------- 防具基底：护甲 / 闪避 / 灵力 ----------
   * 头盔 / 衣服 / 手套 / 鞋子 默认都带一条基底防御，性质由「三维词缀」决定：
   *   仅力量 → 护甲 ； 仅敏捷 → 闪避 ； 仅智慧 → 灵力
   *   （混搭按 力量 > 敏捷 > 智慧 取先；一件三维都没有的防具按护甲处理）
   * 数值按装备等级取档、在区间内随机，roll 一次就固定在装备上（洗练换掉三维时重 roll）。
   * 之后「防御力提高 X%」/「闪避提高 X%」还会在角色总属性上再乘一遍。 */
  const BASE_DEF_SLOTS = ['helmet', 'clothes', 'gloves', 'shoes'];
  const BASE_DEF_KIND_TEXT = { armor: '护甲', evasion: '闪避', spirit: '灵力' };
  function baseDefenseRange(level) {
    const lv = Math.max(1, Math.round(level || 1));
    let pick = BASE_DEFENSE_BANDS[0];
    BASE_DEFENSE_BANDS.forEach(function (b) { if (lv >= b.minLevel) pick = b; });
    return pick;
  }
  function itemAttrFlags(item) {
    const has = { str: false, agi: false, int: false };
    const scan = function (m) {
      if (!m || !m.key) return;
      const k = m.key;
      if (k === 'strength' || k === 'strInt' || k === 'strDex' || k === 'allAttr') has.str = true;
      if (k === 'agility' || k === 'strDex' || k === 'intDex' || k === 'allAttr') has.agi = true;
      if (k === 'intelligence' || k === 'strInt' || k === 'intDex' || k === 'allAttr') has.int = true;
    };
    (item.baseMods || []).forEach(scan);
    (item.mods || []).forEach(scan);
    // 独特装备的固定数值也算「装备自带的三维」（例：漂泊者护甲 +20~30 力量）
    if (item.uniqueStats) {
      if (item.uniqueStats.strength || item.uniqueStats.allAttr) has.str = true;
      if (item.uniqueStats.agility || item.uniqueStats.allAttr) has.agi = true;
      if (item.uniqueStats.intelligence || item.uniqueStats.allAttr) has.int = true;
    }
    return has;
  }
  function baseDefKindOf(item) {
    if (!item || BASE_DEF_SLOTS.indexOf(item.slot) < 0) return null;
    const a = itemAttrFlags(item);
    if (a.str) return 'armor';
    if (a.agi) return 'evasion';
    if (a.int) return 'spirit';
    return 'armor';           // 一件三维都没有的防具：按护甲性质
  }
  function baseDefenseFor(item) {
    const kind = baseDefKindOf(item);
    if (!kind) return null;
    const band = baseDefenseRange(item.level);
    // 性质变了（洗练换掉三维）或第一次算：重新 roll 一次并记在装备上
    if (item.baseDefKind !== kind || !item.baseDefValue) {
      item.baseDefKind = kind;
      item.baseDefValue = randInt(band.min, band.max);
    }
    return { kind: kind, value: item.baseDefValue, band: band };
  }

  // 把一条词缀加到装备的属性上（基底词缀 / 随机词缀共用同一条路径）
  function applyOneItemMod(item, mod) {
    const v = mod.value || 0;
    if (mod.key === 'speedPct') {
      item.speedPct = Math.min(15, v);              // 移动速度词缀封顶 15%
    } else if (mod.key === 'hybridArmor') {
      item.armorPct = (item.armorPct || 0) + v;
      item.hp = (item.hp || 0) + (mod.value2 || 0);
    } else if (mod.key === 'allAttr') {
      // 「全能力 +X」：力量 / 敏捷 / 智力 各 +X，但只显示一行
      item.strength = (item.strength || 0) + v;
      item.agility = (item.agility || 0) + v;
      item.intelligence = (item.intelligence || 0) + v;
    } else if (mod.key === 'strInt') {
      // 「力量与智慧 +X」：两项各 +X，只显示一行（同全能力的处理方式）
      item.strength = (item.strength || 0) + v;
      item.intelligence = (item.intelligence || 0) + v;
    } else if (mod.key === 'strDex') {
      item.strength = (item.strength || 0) + v;
      item.agility = (item.agility || 0) + v;
    } else if (mod.key === 'intDex') {
      item.intelligence = (item.intelligence || 0) + v;
      item.agility = (item.agility || 0) + v;
    } else {
      item[mod.key] = (item[mod.key] || 0) + v;
    }
  }

  function applyItemMods(item) {
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    const rarity = RARITY_DEFS[item.rarity] ? item.rarity : 'common';
    GEAR_STAT_KEYS.forEach(function (k) { item[k] = 0; });
    /* 老存档清理：以前武器上滚出过抗性 / 移速（甚至荆棘），
     * 这里在重算属性时直接把这几条摘掉，避免旧装备一直带着不该有的词缀。 */
    if (isPlainWeaponItem(item) && (item.mods || []).length) {
      const kept = item.mods.filter(function (mod) {
        return WEAPON_FORBIDDEN_MODS.indexOf(mod.key) < 0;
      });
      if (kept.length !== item.mods.length) {
        item.mods = kept;
        item.modCount = kept.length;
        item.weaponModsCleaned = true;
      }
    }
    const texts = [];
    // 先算基底词缀（盾牌 +15% 格挡 / 长弓额外箭矢 / 首饰随机一条），再叠随机词缀
    const baseTexts = [];
    baseModsFor(item).forEach(function (mod) {
      applyOneItemMod(item, mod);
      baseTexts.push(modToText(mod));
    });
    item.baseDetail = baseTexts.join('　');
    (item.mods || []).forEach(function (mod) {
      applyOneItemMod(item, mod);
      texts.push(modToText(mod));
    });
    item.detail = texts.join('　');
    /* 独特装备：机制说明行（不是词缀，不参与洗练 / 反解），单独拼在详情后面 */
    if (Array.isArray(item.uniqueLines) && item.uniqueLines.length) {
      item.detail = [item.detail].concat(item.uniqueLines).filter(Boolean).join('　');
    }
    /* 独特装备的固定数值：生成时 roll 好存在 uniqueStats 上，
     * 每次重算都在这里重新叠加（上面刚清零过，所以是幂等的）。 */
    if (item.uniqueStats) {
      Object.keys(item.uniqueStats).forEach(function (k) {
        const v = item.uniqueStats[k];
        if (typeof v !== 'number') return;
        if (k === 'speedPct') {
          // 独特鞋子允许 10~20% 移速（普通词缀仍然封顶 15%）
          item.speedPct = Math.min(30, (item.speedPct || 0) + v);
        } else if (k === 'allAttr') {
          /* 「均衡之符」的全属性：一次 roll 出来的同一个数值加到力量 / 敏捷 / 智慧 */
          item.strength = (item.strength || 0) + v;
          item.agility = (item.agility || 0) + v;
          item.intelligence = (item.intelligence || 0) + v;
        } else {
          item[k] = (item[k] || 0) + v;
        }
      });
    }
    /* 防具基底：按三维词缀定性质，把这条基底防御加到装备上
     * （护甲 → armor ／ 闪避 → evasion ／ 灵力 → mp，最后都在角色总属性里汇总） */
    const baseDef = baseDefenseFor(item);
    if (baseDef) {
      if (baseDef.kind === 'armor') item.armor = (item.armor || 0) + baseDef.value;
      else if (baseDef.kind === 'evasion') item.evasion = (item.evasion || 0) + baseDef.value;
      else item.mp = (item.mp || 0) + baseDef.value;
      // 让这条基底在装备卡片上看得见（和盾牌「〔基底〕格挡几率 +15%」一个写法）
      const baseLine = BASE_DEF_KIND_TEXT[baseDef.kind] + ' +' + baseDef.value;
      item.baseDetail = item.baseDetail ? (item.baseDetail + '　' + baseLine) : baseLine;
    }
    item.rarityName = rar.name;
    item.rarityColor = rar.color;
  // 价格只在生成时随品级 roll 一次，洗练不会改价（稀有度没变）
    if (!item.price) item.price = rollGearPrice(rarity);
    return item;
  }

  // ---------- 灵宝（金色洞府终点掉落的传奇装备）----------
  // 除了常规词缀，还会带一条"特殊属性"：改机制 / 给特殊收益
  function makeTreasureItem(level) {
    const it = makeEquipment(Math.max(1, Math.round(level || 1)), 'legend', 'treasure');
    return attachTreasureSpecial(it, pickTreasureSpecial());
  }

  // 灵宝特殊属性的抽取：普通灵宝等概率，带 rare 标记的（破境符诏）出现率极低
  const TREASURE_RARE_WEIGHT = 0.14;   // ≈ 7 件普通灵宝里才见 1 件的量级（约 2%）
  function pickTreasureSpecial() {
    const total = TREASURE_SPECIALS.reduce(function (s, sp) {
      return s + (sp.rare ? TREASURE_RARE_WEIGHT : 1);
    }, 0);
    let r = Math.random() * total;
    for (let i = 0; i < TREASURE_SPECIALS.length; i++) {
      const w = TREASURE_SPECIALS[i].rare ? TREASURE_RARE_WEIGHT : 1;
      r -= w;
      if (r <= 0) return TREASURE_SPECIALS[i];
    }
    return TREASURE_SPECIALS[0];
  }

  // 给一件灵宝挂上特殊属性（掉落与调试共用同一套逻辑）
  function attachTreasureSpecial(it, sp) {
    it.special = { key: sp.key, name: sp.name, kind: sp.kind, desc: sp.desc };
    // 「蓝宝灵韵」：增幅比例 50%~150%，每件灵宝随机 roll 一次
    if (sp.key === 'magicAmp') {
      it.special.roll = randInt(50, 150);
      it.special.desc = '已装备的蓝色（魔法）装备属性增幅 +' + it.special.roll + '%';
    }
    if (sp.kind === 'skill') {
      const pool = ACTIVE_SKILL_POOL;
      const def = pool[randInt(0, pool.length - 1)];
      it.special.skillId = def.id;
      it.special.skillName = def.name;
      it.special.desc = '附带主动技能「' + def.name + '」（装备后自动学会）';
    }
    it.detail = it.detail + '　【灵宝·' + it.special.name + '】' + it.special.desc;
    it.name = '灵宝 · ' + it.special.name;
    return it;
  }

  // 当前穿戴的灵宝是否带某条特殊属性
  function treasureSpecial(key) {
    const it = equipment.treasure;
    return !!(it && it.special && it.special.key === key);
  }

  /* ============================================================
   * 独特装备（暗金）
   *   和灵宝一样属于"固定名字 + 固定词缀"的特殊装备，区别是：
   *     · 灵宝走 treasure 槽，独特装备占正常部位（武器 / 衣服 / 鞋子）
   *     · 独特装备的数值在生成时 roll 一次就固化（洗练不会动，见 rollItemAffixes 的 locked）
   *     · 专属机制放在 item.mech 里，recalcPlayerStats 汇总进 derived.unique
   * ============================================================ */
  const UNIQUE_BY_ID = {};
  UNIQUE_DEFS.forEach(function (d) { UNIQUE_BY_ID[d.id] = d; });

  function uniqueDefOf(item) {
    return (item && item.rarity === 'unique' && item.unique)
      ? (UNIQUE_BY_ID[item.unique] || null) : null;
  }

  // defs 里的取值：数组＝区间（这里 roll 成定值），对象＝继续往下 roll，数字／布尔＝原样
  function rollUniqueSpec(v) {
    if (Array.isArray(v)) return randInt(v[0], v[1]);
    if (v && typeof v === 'object') {
      const out = {};
      Object.keys(v).forEach(function (k) { out[k] = rollUniqueSpec(v[k]); });
      return out;
    }
    return v;
  }

  // 说明行里的 {key} 用 roll 出来的数值替换
  function uniqueLineText(text, bag) {
    return String(text).replace(/\{(\w+)\}/g, function (m, k) {
      return (bag[k] === undefined) ? m : bag[k];
    });
  }

  function makeUniqueItem(defId, level) {
    const def = UNIQUE_BY_ID[defId];
    if (!def) return null;
    const item = {
      id: 'uq' + (++itemSeq),
      type: 'equipment',
      unique: def.id,
      slot: def.slot,
      rarity: 'unique',
      name: def.name,
      baseName: def.name,
      icon: def.icon,
      iconSet: def.iconSet || null,
      iconImg: def.iconImg || (def.iconSet === 'weapon' ? randInt(1, WEAPON_ICON_COUNT)
        : def.iconSet ? randInt(1, GEAR_ICON_COUNT) : 0),
      weaponType: def.weaponType || null,
      level: Math.max(1, Math.round(level || 1)),
      price: def.price || 9000,
      mods: [],
      modCount: 0
    };
    GEAR_STAT_KEYS.forEach(function (k) { item[k] = 0; });
    item.uniqueStats = rollUniqueSpec(def.stats || {});
    item.mech = rollUniqueSpec(def.mech || {});
    const bag = {};
    Object.keys(item.uniqueStats).forEach(function (k) { bag[k] = item.uniqueStats[k]; });
    Object.keys(item.mech).forEach(function (k) { bag[k] = item.mech[k]; });
    item.uniqueLines = (def.lines || []).map(function (t) { return uniqueLineText(t, bag); });
    applyItemMods(item);
    return item;
  }

  // 随机挑一件独特装备（掉落用）
  function rollUniqueDrop(level) {
    const def = UNIQUE_DEFS[randInt(0, UNIQUE_DEFS.length - 1)];
    return makeUniqueItem(def.id, level);
  }

  /* ============================================================
   * 【测试用赠送 · 用完删掉】账号 AAA 进游戏时补一把独特武器「武者的刑具」
   *   删除方法：删掉这个函数 + startScene 里那一行 maybeGrantTestUnique();
   *   （测试钩子 __debugTestGrant 也可以一起删）
   * ============================================================ */
  const TEST_GRANT_ACCOUNT = 'aaa';              // 归一化后比较（忽略大小写 / 全角）
  const TEST_GRANT_UNIQUE = 'wuzheInstrument';   // 武者的刑具
  function maybeGrantTestUnique() {
    const name = normalizeAccountName(account.name || '').toLowerCase();
    if (!name || name !== TEST_GRANT_ACCOUNT) return null;
    const owned = inventory.some(function (it) {
      return it && it.unique === TEST_GRANT_UNIQUE;
    }) || Object.keys(equipment).some(function (k) {
      return equipment[k] && equipment[k].unique === TEST_GRANT_UNIQUE;
    });
    if (owned) return null;      // 已经有（在背包或身上）就不再补
    const it = makeUniqueItem(TEST_GRANT_UNIQUE, playerLevel() + 2);
    if (!it) return null;
    inventory.push(it);
    saveGame();
    showToast('测试赠送：已把独特武器「' + it.name + '」放进背包（装备窗口可见）');
    pushBattleLog('【测试赠送】独特武器「' + it.name + '」已进入背包（测试用，正式版会删除）', 'reward');
    return it;
  }

  /* 当前身上所有独特装备的机制合并（数字相加，对象／布尔取后者） */
  function equippedUniqueMech() {
    const out = {};
    Object.keys(equipment).forEach(function (slot) {
      const g = equipment[slot];
      if (!uniqueDefOf(g)) return;
      const m = g.mech || {};
      Object.keys(m).forEach(function (k) {
        const v = m[k];
        if (typeof v === 'number') out[k] = (out[k] || 0) + v;
        else out[k] = v;
      });
    });
    return out;
  }

  // 老存档兼容：把只有文本的装备反解成逐条词缀（数值保持一致）
  function ensureItemMods(item) {
    if (!item || item.type !== 'equipment') return item;
    ensureItemIcon(item);   // 老存档的装备顺便补一张美术图标
    /* 独特装备没有随机词缀，detail 里是机制说明（不是词缀文本），
     * 不能走下面的"按文本反解词缀"，否则会把这些说明当成词缀再叠一遍数值。 */
    if (item.rarity === 'unique') {
      applyItemMods(item);
      return item;
    }
    if (!Array.isArray(item.mods) || !item.mods.length) {
      const mods = [];
      String(item.detail || '').split('　').filter(Boolean).forEach(function (t) {
        const m = affixTextToMod(t);
        if (m) mods.push(m);
      });
      if (mods.length) {
        item.mods = mods;
        item.modCount = mods.length;
      }
    }
    // 统一重算一遍：既补齐老存档的逐条词缀，也给盾牌补上基底词缀（+15% 格挡）
    applyItemMods(item);
    return item;
  }

  /* 新装备的词缀条数（金色稀有的稀缺度就靠这张表）
   *   白色 1 条 / 蓝色 2~3 条 / 金色稀有：3 条 75% · 4 条 15% · 5 条 7% · 6 条 3%
   *   （只有"新生成"的装备会抽这张表；洗练沿用原有条数，不会越洗越多） */
  const RARE_AFFIX_ODDS = [[3, 75], [4, 15], [5, 7], [6, 3]];
  function rollAffixCountForRarity(rarity) {
    if (rarity === 'common') return 1;
    if (rarity === 'magic') return randInt(2, 3);
    let roll = Math.random() * 100;
    for (let i = 0; i < RARE_AFFIX_ODDS.length; i++) {
      roll -= RARE_AFFIX_ODDS[i][1];
      if (roll < 0) return RARE_AFFIX_ODDS[i][0];
    }
    return 3;
  }

  /* 重新随机装备词缀。
   * 规则：
   *   · 词缀数量不变（原有 3 条就还是 3 条，不会洗成 6 条）
   *   · 被锁定的词缀整条保留（key / 数值 / 档位都不动）
   *   · 其余词缀按「装备等级」对应的档位重掷 —— 1 级装备永远洗不出高等级档位
   */
  function rollItemAffixes(item, opts) {
    const options = opts || {};
    const slot = item.slot;
    const level = Math.max(1, Math.round(item.level || 1));
    const rarity = RARITY_DEFS[item.rarity] ? item.rarity : 'common';
    const weaponType = item.weaponType || null;
    const pool = affixPoolFor(slot, weaponType);
    const old = Array.isArray(item.mods) ? item.mods : [];

    let modCount = old.length;
    if (!modCount) {
      modCount = rollAffixCountForRarity(rarity);
    }
    item.modCount = modCount;

    lastAffixRolls.length = 0;
    const mods = [];
    for (let i = 0; i < modCount; i++) {
      const prev = old[i];
      /* 锁定的原样保留；镶嵌带进去的那条词缀也一并保留
       * （镶嵌是"额外加一条"，洗练不该把它洗掉） */
      if (prev && (prev.locked || prev.inlay) && options.ignoreLocks !== true) {
        mods.push(prev);
        continue;
      }
      const fresh = rollOneMod(slot, weaponType, level, rarity, pool);
      mods.push(fresh);
      lastAffixRolls.push({ key: fresh.key, level: level, bandIdx: fresh.bandIdx, value: fresh.value, value2: fresh.value2 });
    }
    item.mods = mods;
    applyItemMods(item);
    return item;
  }

  // 随机生成一件装备，词缀方向参考流放之路：防御、生命、攻击、移速等
  function makeEquipment(level, forcedRarity, forcedSlot, forcedWeaponKind) {
    // 灵宝槽不参与随机：只能由金色洞府终点掉落（makeTreasureItem）
    const slotNames = Object.keys(SLOT_DEFS).filter(function (s) { return s !== 'treasure'; });
    const slot = forcedSlot && SLOT_DEFS[forcedSlot]
      ? forcedSlot
      : slotNames[randInt(0, slotNames.length - 1)];
    const def = SLOT_DEFS[slot];
    const rarity = forcedRarity && RARITY_DEFS[forcedRarity]
      ? forcedRarity
      : rollRarity();
    const rar = RARITY_DEFS[rarity];
    const tier = rar.tier;
    level = Math.max(1, Math.round(level));
    let baseName;
    let icon = def.icon;
    let weaponType = null;
    if (slot === 'weapon') {
      let kind = null;
      if (forcedWeaponKind) {
        for (let i = 0; i < WEAPON_KINDS.length; i++) {
          if (WEAPON_KINDS[i].name === forcedWeaponKind) kind = WEAPON_KINDS[i];
        }
      }
      if (!kind) kind = WEAPON_KINDS[randInt(0, WEAPON_KINDS.length - 1)];
      baseName = kind.name;
      icon = kind.icon;
      weaponType = kind.name;
    } else {
      baseName = def.base[Math.min(def.base.length - 1, Math.floor(level / 2))];
    }
    const item = {
      id: 'eq' + (++itemSeq),
      type: 'equipment',
      slot: slot,
      rarity: rarity,
      name: rar.name + ' ' + baseName,
      baseName: baseName,        // 镶嵌时要在名字前插「前缀*」，所以留一份不带前缀的底名
      icon: icon,
      // 随机分一张美术图标：武器用武器图，头盔/衣服/鞋子/手套用服装图
      iconSet: slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[slot] || null),
      iconImg: slot === 'weapon' ? randInt(1, WEAPON_ICON_COUNT)
        : (GEAR_ICON_SET_BY_SLOT[slot] ? randInt(1, GEAR_ICON_COUNT) : 0),
      weaponType: weaponType,
      level: level,
      hp: 0,
      armor: 0,
      atk: 0,
      speedPct: 0,
      mp: 0,
      res: 0,
      thorns: 0,
      blockPct: 0,
      strength: 0,
      agility: 0,
      intelligence: 0,
      chaosRes: 0,
      critReduce: 0,
      armorPct: 0,
      lifeRegen: 0,
      atkSpeed: 0,
      critChance: 0,
      critDamage: 0
    };

    // 词缀按等级档位随机（生成装备 / 洗练共用同一套规则）
    rollItemAffixes(item);
    return item;
  }

  // ---------- 物品图标渲染 ----------
  // 武器 / 防具用美术图标（<img>），其它物品/素材没图就退回原来的文字图标
  function itemIconUrl(item) {
    if (!item) return null;
    // 天地灵物 / 金币：用用户提供的美术图标（见 ICON_ART）
    if (item.type === 'spiritItem') return assetUrl(ICON_ART.spirit);
    if (item.kind === 'gold' || item.type === 'gold') return assetUrl(ICON_ART.gold);
    // 回血药瓶 / 主动技能石 / 辅助技能石：用现成美术（见 ICON_ART）
    if (item.type === 'potion') return assetUrl(ICON_ART.flask);
    if (item.type === 'skillStone') return assetUrl(ICON_ART.stoneActive);
    /* 辅助技能石 / 空白被动技能石（还没鉴定）/ 商店里那颗自选辅助石：
     *   一律用辅助石的美术图标 —— 以前只有"鉴定过的辅助石"才有图，
     *   空白被动技能石和商店货架上的那格只能显示文字「石/辅」。 */
    if (item.type === 'gem' || item.type === 'shopGem' || item.type === 'gemShop') {
      return assetUrl(ICON_ART.stoneSupport);
    }
    if (item.iconArt) return assetUrl(item.iconArt);
    // 时装：直接用它的美术（面具这类是小图，塞进格子里正好）
    if (item.type === 'costume') {
      const def = costumeDef(item.costumeId);
      return assetUrl(item.art || (def && def.art) || '');
    }
    ensureItemIcon(item);
    if (!item.iconImg) return null;
    return gearIconUrl(item.iconSet, item.iconImg);
  }

  /* 图标用不用平滑缩放：武器是 32×32 像素图（要锐利），
   * 服装 / 技能石这些是高清插画（要平滑），药瓶沿用像素风素材。 */
  function itemIconSmooth(item) {
    if (!item) return false;
    if (item.iconArtSmooth) return true;      // 高清插画类的掉落物图标
    if (item.iconSet && item.iconSet !== 'weapon') return true;
    if (item.type === 'skillStone') return true;
    if (item.type === 'gem' || item.type === 'shopGem' || item.type === 'gemShop') return true;
    return false;
  }

  function fillItemIcon(el, item, glyph) {
    if (!el) return;
    const url = itemIconUrl(item);
    el.textContent = '';
    if (url) {
      const img = document.createElement('img');
      // 武器是 32×32 像素图（锐利），服装素材是高清插画（平滑缩放）
      img.className = 'itemIconImg' + (itemIconSmooth(item) ? ' gearArt' : '');
      img.alt = '';
      img.src = url;
      el.appendChild(img);
      el.classList.add('hasImg');
    } else {
      el.classList.remove('hasImg');
      el.textContent = glyph || (item && item.icon) || '?';
    }
    return el;
  }

  // ---------- 世界BOSS 独特圣物（森特欧斯）----------
  // 只要带在背包里就生效（不需要装备），是打世界BOSS的极小概率掉落
  const SANTOS_RELIC_CHANCE = 0.001;    // 0.001%（十万分之一），每次领取世界BOSS奖励时判定
  const SANTOS_RELICS = {
    skull: {
      key: 'skull', name: '森特欧斯的头骨', icon: '颅', intBonus: 200,
      detail: '免疫中毒 · 智力 +200 · 附带主动技能「森特欧斯的囚笼」'
    },
    totem: {
      key: 'totem', name: '森特欧斯的图腾', icon: '柱', intBonus: 50,
      detail: '免疫控制 · 智力 +50 · 每回合随机召唤陨石 / 树木 / 有毒的孢子攻击敌人'
    }
  };

  function makeSantosRelic(key) {
    const def = SANTOS_RELICS[key] || SANTOS_RELICS.skull;
    return {
      id: 'relic' + (++itemSeq),
      type: 'santosRelic',
      relic: def.key,
      name: def.name,
      icon: def.icon,
      detail: def.detail,
      rarityName: '圣物',
      rarityColor: '#ff8ad4'
    };
  }

  function hasSantosRelic(key) {
    return inventory.some(function (it) {
      return it && it.type === 'santosRelic' && it.relic === key;
    });
  }

  // 圣物提供的智力总和（头骨 +200 / 图腾 +50）
  function santosRelicIntBonus() {
    let n = 0;
    Object.keys(SANTOS_RELICS).forEach(function (k) {
      if (hasSantosRelic(k)) n += SANTOS_RELICS[k].intBonus;
    });
    return n;
  }

  function makeMaterial(level) {
    const names = ['粗布', '皮革', '铁矿石', '精木料', '魔化尘'];
    return {
      id: 'mat' + (++itemSeq),
      type: 'material',
      name: names[randInt(0, names.length - 1)],
      icon: '料',
      detail: '制作装备的材料',
      rarityName: '材料',
      rarityColor: '#a8d1a8'
    };
  }

  function makePotion(level) {
    const heal = Math.round(24 + level * 2 + rand(0, 8));
    return {
      id: 'pot' + (++itemSeq),
      type: 'potion',
      name: '回血药瓶',
      icon: '药',
      detail: '恢复 ' + heal + ' 点生命',
      heal: heal,
      rarityName: '道具',
      rarityColor: '#ff9d86'
    };
  }

  /* ---------- 门派用到的两种材料：药材（丹殿任务）/ 矿石（矿山） ---------- */
  function makeSectMaterial(kind, n) {
    const count = Math.max(1, Math.floor(n || 1));
    if (kind === 'herb') {
      return {
        id: 'herb' + (++itemSeq), type: 'material', name: '药材', icon: '药',
        detail: '丹殿炼丹用的药材（可在洞府中采集）',
        rarityName: '门派材料', rarityColor: '#9adf8c', sectMaterial: 'herb', stack: count
      };
    }
    return {
      id: 'ore' + (++itemSeq), type: 'material', name: '矿石', icon: '矿',
      detail: '门派矿山开采的矿石（可在门派矿山挖取）',
      rarityName: '门派材料', rarityColor: '#c9c2b4', sectMaterial: 'ore', stack: count,
      oreArtIdx: randInt(1, 7)
    };
  }

  /* ---------- 丹殿丹方：消耗门派贡献 + 材料炼成丹药 ----------
   *   丹药仍走 type:'potion'，但带 pill 标记，背包里显示为「丹药」；
   *   使用入口沿用背包的统一「使用」按钮，效果在 usePotion 里分流。 */
  const SECT_PILL_RECIPES = [
    {
      id: 'qingling', name: '清灵丹', icon: '丹', color: '#8fd0ff',
      contribution: 10, herb: 4, ore: 0,
      detail: '服用后立即恢复 80 点灵力', mpGain: 80
    },
    {
      id: 'ningling', name: '凝灵丹', icon: '丹', color: '#b89cff',
      contribution: 30, herb: 12, ore: 4,
      detail: '服用后永久提高灵力上限 60 点', mpMaxBonus: 60
    },
    {
      id: 'peiyuan', name: '培元丹', icon: '丹', color: '#9adf8c',
      contribution: 30, herb: 12, ore: 4,
      detail: '服用后永久提高生命上限 150 点', hpMaxBonus: 150
    },
    {
      id: 'xisui', name: '洗髓丹', icon: '丹', color: '#ffd76a',
      contribution: 60, herb: 20, ore: 8,
      detail: '服用后力量、敏捷、智力各永久提高 1 点',
      attrBonus: { str: 1, agi: 1, int: 1 }
    }
  ];

  function sectPillRecipe(id) {
    return SECT_PILL_RECIPES.filter(function (r) { return r.id === id; })[0] || null;
  }

  function sectMaterialCount(kind) {
    return inventory.reduce(function (sum, it) {
      if (!it || it.type !== 'material' || it.sectMaterial !== kind) return sum;
      return sum + Math.max(1, Math.floor(Number(it.stack) || 1));
    }, 0);
  }

  function consumeSectMaterial(kind, need) {
    const want = Math.max(0, Math.floor(need || 0));
    if (!want) return true;
    if (sectMaterialCount(kind) < want) return false;
    let left = want;
    for (let i = inventory.length - 1; i >= 0 && left > 0; i--) {
      const it = inventory[i];
      if (!it || it.type !== 'material' || it.sectMaterial !== kind) continue;
      const have = Math.max(1, Math.floor(Number(it.stack) || 1));
      if (have <= left) {
        left -= have;
        inventory.splice(i, 1);
      } else {
        it.stack = have - left;
        left = 0;
      }
    }
    return left === 0;
  }

  function makeSectPill(recipe) {
    const r = recipe || {};
    const pill = {
      id: 'pill' + (++itemSeq),
      type: 'potion',
      pill: true,
      recipeId: r.id || '',
      name: r.name || '丹药',
      icon: r.icon || '丹',
      detail: r.detail || '',
      rarityName: '门派丹药',
      rarityColor: r.color || '#9adf8c'
    };
    if (r.mpGain) pill.mpGain = r.mpGain;
    if (r.mpMaxBonus) pill.mpMaxBonus = r.mpMaxBonus;
    if (r.hpMaxBonus) pill.hpMaxBonus = r.hpMaxBonus;
    if (r.attrBonus) pill.attrBonus = Object.assign({}, r.attrBonus);
    return pill;
  }

  /* ---------- 矿石鉴定 / 加工 ----------
   *   原始矿石：出售直接得金币，也可上缴宗门。
   *   鉴定（100 金币）后进入三步加工：观微 → 去皮 → 塑形。
   *   每一步费用与动画不同，最终按评分落成：废矿石 / 正常矿石 / 材料矿石。 */
  const ORE_IDENTIFY_COST = 100;
  const ORE_STEPS = [
    { id: 'observe', name: '观微', cost: 200, dur: 1.45 },
    { id: 'peel',    name: '去皮', cost: 300, dur: 1.35 },
    { id: 'shape',   name: '塑形', cost: 500, dur: 1.55 }
  ];
  const ORE_RAW_SELL = 5;        // 未鉴定矿石：5 金币/块（一键卖矿也是这个价）
  const ORE_RAW_CONTRIB = 2;
  /* 鉴定 / 加工之后售卖矿石的金币收益整体下调 70%（原矿的 30 金币不变）。
   * 挖矿产出现在主要用来镶嵌 / 上缴宗门，不再是一条卖钱流水线。 */
  const ORE_SELL_MULT = 0.3;

  function oreIsRaw(item) {
    return !!(item && item.sectMaterial === 'ore' && !item.oreIdentified);
  }
  function oreStepIndex(item) {
    return item && Array.isArray(item.oreSteps) ? item.oreSteps.length : 0;
  }
  function oreStepDef(item) {
    return ORE_STEPS[oreStepIndex(item)] || null;
  }
  function oreFinalDef(score) {
    if (score < 35) return { id: 'waste', name: '废矿石', color: '#9c9488', value: 25, contribution: 1, detail: '加工失败的废矿石，只能低价处理。' };
    if (score < 70) return { id: 'normal', name: '正常矿石', color: '#c9c2b4', value: 120 + Math.round(score * 4), contribution: 5, detail: '品质正常的矿石，可出售或上缴宗门。' };
    return { id: 'material', name: '材料矿石', color: '#ffd76a', value: 260 + Math.round(score * 6), contribution: 12, detail: '高品质矿石，可作为装备镶嵌材料。' };
  }
  const ORE_ATTR_DEFS = {
    str: { name: '力量', color: '#ff8f72' },
    agi: { name: '敏捷', color: '#9adf8c' },
    int: { name: '智慧', color: '#8fd0ff' }
  };
  function oreAttrValue(item) {
    const values = [5, 10, 15, 20, 30];
    return values[clamp(Math.floor(item && item.oreAttrLevel ? item.oreAttrLevel : 1), 1, 5) - 1];
  }
  function oreAttrValueText(item) {
    return '+' + oreAttrValue(item) + ' ' + (ORE_ATTR_DEFS[item.oreAttr] || ORE_ATTR_DEFS.str).name;
  }
  /* 属性石等级：评分越高越容易出高等级，同时保证 4 级真的会出、5 级很稀罕。
   * （旧版是 1 + floor((评分-70)/6)：因为评分经常顶到 100，5 级占了快一半、4 级反而少见） */
  function oreAttrStoneLevel(score) {
    const q = clamp(((score | 0) - 70) / 30, 0, 1);
    const weights = [
      34 - 24 * q,     // 1 级
      30 - 10 * q,     // 2 级
      22 + 6 * q,      // 3 级
      12 + 20 * q,     // 4 级
      2 + 8 * q        // 5 级
    ];
    let roll = Math.random() * weights.reduce(function (a, b) { return a + b; }, 0);
    for (let i = 0; i < weights.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return i + 1;
    }
    return 1;
  }
  function oreMaterialKind(item) {
    if (!item || item.oreFinal !== 'material') return null;
    return item.oreMaterialKind || 'affix';
  }
  function makeAttributeOre(attr, level) {
    const a = ORE_ATTR_DEFS[attr] ? attr : 'str';
    const lv = clamp(Math.floor(level || 1), 1, 5);
    let name = ORE_ATTR_DEFS[a].name + '矿石·' + lv + '级';
    if (lv === 5) name = '极品' + ORE_ATTR_DEFS[a].name + '矿石';
    return {
      id: 'ore' + (++itemSeq),
      type: 'material',
      sectMaterial: 'ore',
      oreIdentified: true,
      oreFinished: true,
      oreFinal: 'material',
      oreMaterialKind: 'attribute',
      oreAttr: a,
      oreAttrLevel: lv,
      oreScore: 70 + lv * 5,
      oreSteps: ['observe', 'peel', 'shape'].slice(0, 3),
      name: name,
      icon: '矿',
      rarityName: '属性矿石',
      rarityColor: ORE_ATTR_DEFS[a].color,
      detail: '属性矿石：' + oreAttrValueText({ oreAttr: a, oreAttrLevel: lv }) + '；三颗同级同属性矿石可合成为高一级。'
    };
  }
  function oreContributionValue(item) {
    if (oreMaterialKind(item) === 'attribute') return 2 * oreAttrValue(item);
    const def = item && item.oreFinished ? oreFinalDef(item.oreScore | 0) : null;
    return def ? def.contribution : ORE_RAW_CONTRIB;
  }
  function oreValue(item) {
    if (!item) return 0;
    if (oreIsRaw(item)) return ORE_RAW_SELL;
    let base;
    if (oreMaterialKind(item) === 'attribute') base = 120 * oreAttrValue(item);
    else if (item.oreFinished) base = oreFinalDef(item.oreScore | 0).value;
    else base = 60 + Math.round((item.oreScore | 0) * 3);
    return Math.max(1, Math.round(base * ORE_SELL_MULT));
  }
  function removeOneInventoryItem(item) {
    if (!item) return false;
    const idx = inventory.indexOf(item);
    if (idx < 0) return false;
    if (item.stack && item.stack > 1) {
      item.stack -= 1;
      return true;
    }
    inventory.splice(idx, 1);
    return true;
  }
  function refreshInventoryUi() {
    if (ui.gearPopup && !ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (ui.oreProcessPopup && !ui.oreProcessPopup.classList.contains('hidden')) renderOreProcessPanel();
  }
  function sellOreItem(item) {
    if (!item || item.sectMaterial !== 'ore') return false;
    const value = oreValue(item);
    if (!removeOneInventoryItem(item)) return false;
    gold += value;
    updateGold();
    saveGame();
    refreshInventoryUi();
    showToast('售出「' + item.name + '」，获得 ' + value + ' 金币。');
    return true;
  }
  /* 一键出售背包里**所有未鉴定的矿石**（5 金币/块，按堆叠数量结算） */
  function sellAllRawOres() {
    const list = inventory.filter(function (it) { return oreIsRaw(it); });
    if (!list.length) { showToast('背包里没有未鉴定的矿石。'); return false; }
    let count = 0;
    list.forEach(function (it) { count += Math.max(1, it.stack || 1); });
    const total = count * ORE_RAW_SELL;
    inventory = inventory.filter(function (it) { return !oreIsRaw(it); });
    gold += total;
    updateGold();
    saveGame();
    refreshInventoryUi();
    showToast('一键出售 ' + count + ' 块未鉴定矿石（' + ORE_RAW_SELL + ' 金币/块），获得 ' + total + ' 金币。');
    pushBattleLog('【背包】一键出售未鉴定矿石 ×' + count + '，获得 ' + total + ' 金币', 'reward');
    return { count: count, gold: total };
  }
  function contributeOreItem(item) {
    if (!item || item.sectMaterial !== 'ore') return false;
    if (!hasSect()) { showToast('还没有加入门派，无法上缴宗门。'); return false; }
    const gain = oreContributionValue(item);
    if (!removeOneInventoryItem(item)) return false;
    sect.contribution = Math.max(0, (sect.contribution | 0) + gain);
    saveGame();
    refreshInventoryUi();
    if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    showToast('已上缴「' + item.name + '」，门派贡献 +' + gain + '。');
    return true;
  }
  function identifyOreItem(item) {
    if (!item || item.sectMaterial !== 'ore') return false;
    if (!oreIsRaw(item)) { openOreProcessPanel(item); return true; }
    if (gold < ORE_IDENTIFY_COST) {
      showToast('鉴定需要 ' + ORE_IDENTIFY_COST + ' 金币，当前金币不足。');
      return false;
    }
    gold -= ORE_IDENTIFY_COST;
    removeOneInventoryItem(item);
    const identified = {
      id: 'ore' + (++itemSeq),
      type: 'material',
      sectMaterial: 'ore',
      oreIdentified: true,
      oreScore: randInt(20, 68),
      oreSteps: [],
      oreFinished: false,
      oreArtIdx: randInt(1, 7),
      name: '已鉴定矿石',
      icon: '矿',
      rarityName: '矿石',
      rarityColor: '#8fd0ff',
      detail: '已鉴定矿石：可进行观微、去皮、塑形加工。'
    };
    inventory.push(identified);
    updateGold();
    saveGame();
    refreshInventoryUi();
    showToast('花费 ' + ORE_IDENTIFY_COST + ' 金币，获得「已鉴定矿石」。');
    openOreProcessPanel(identified);
    return true;
  }
  /* 直接"使用"属性矿石的老逻辑：界面入口已全部改成镶嵌，这里只留给调试钩子用 */
  function useAttributeOre(item) {
    if (!item || oreMaterialKind(item) !== 'attribute') return false;
    const attr = item.oreAttr || 'str';
    const value = oreAttrValue(item);
    if (!removeOneInventoryItem(item)) return false;
    player.attrs[attr] = Math.max(0, (player.attrs[attr] | 0) + value);
    recalcPlayerStats();
    saveGame();
    refreshInventoryUi();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    showToast('使用「' + item.name + '」：' + (ORE_ATTR_DEFS[attr] || ORE_ATTR_DEFS.str).name + ' +' + value + '。');
    return true;
  }
  function combineAttributeOres(item) {
    if (!item || oreMaterialKind(item) !== 'attribute') return false;
    const attr = item.oreAttr || 'str';
    const lv = item.oreAttrLevel || 1;
    if (lv >= 5) { showToast('5级属性矿石已是最高等级，无法继续合成。'); return false; }
    const same = inventory.filter(function (it) {
      return it && oreMaterialKind(it) === 'attribute' && it.oreAttr === attr && (it.oreAttrLevel || 1) === lv;
    });
    if (same.length < 3) {
      showToast('合成需要 3 颗同属性同等级矿石（当前 ' + same.length + '/' + 3 + '）。');
      return false;
    }
    const useIds = same.slice(0, 3).map(function (it) { return it.id; });
    inventory = inventory.filter(function (it) { return useIds.indexOf(it.id) < 0; });
    const upgraded = makeAttributeOre(attr, lv + 1);
    inventory.push(upgraded);
    saveGame();
    refreshInventoryUi();
    showToast('合成成功：3 颗 ' + lv + ' 级' + (ORE_ATTR_DEFS[attr] || ORE_ATTR_DEFS.str).name +
      '矿石 → 1 颗 ' + (lv + 1) + ' 级。');
    return true;
  }

  /* ---------- 矿石加工窗口 ---------- */
  let oreProcess = null;   // { item, anim: { kind, t, dur, delta } | null }

  function openOreProcessPanel(item) {
    if (!item || item.sectMaterial !== 'ore' || !ui.oreProcessPopup) return false;
    oreProcess = { item: item, anim: null };
    renderOreProcessPanel();
    ui.oreProcessPopup.classList.remove('hidden');
    drawOreProcessVisual();
    return true;
  }
  function closeOreProcessPanel() {
    if (ui.oreProcessPopup) ui.oreProcessPopup.classList.add('hidden');
    oreProcess = null;
  }
  function oreStepDelta(step, score) {
    const quality = clamp((score || 0) / 100, 0, 1);
    if (step.id === 'observe') return randInt(-12, 22) + Math.round(quality * 14);
    if (step.id === 'peel') return randInt(-16, 24) + Math.round(quality * 12);
    return randInt(-22, 28) + Math.round(quality * 16);
  }
  function finishOreStep(step) {
    if (!oreProcess || !oreProcess.item || !oreProcess.anim) return false;
    const item = oreProcess.item;
    const delta = oreProcess.anim.delta;
    item.oreScore = clamp((item.oreScore | 0) + delta, 0, 100);
    item.oreSteps = item.oreSteps || [];
    item.oreSteps.push(step.id);
    if (step.id === 'peel') {
      item.oreRevealed = true;
      item.oreRevealText = item.oreScore < 35 ? '去皮后灵光黯淡，疑似废矿石'
        : item.oreScore < 70 ? '去皮后泛起稳定灵光，品相正常'
        : '去皮后灵光大盛，疑似材料矿石';
    }
    if (item.oreSteps.length >= ORE_STEPS.length) {
      const finalDef = oreFinalDef(item.oreScore);
      item.oreFinished = true;
      item.oreFinal = finalDef.id;
      if (finalDef.id === 'material' && Math.random() < 0.5) {
        const keys = Object.keys(ORE_ATTR_DEFS);
        const attr = keys[randInt(0, keys.length - 1)];
        const level = oreAttrStoneLevel(item.oreScore);
        const attrItem = makeAttributeOre(attr, level);
        item.oreMaterialKind = 'attribute';
        item.oreAttr = attrItem.oreAttr;
        item.oreAttrLevel = attrItem.oreAttrLevel;
        item.name = attrItem.name;
        item.rarityName = attrItem.rarityName;
        item.rarityColor = attrItem.rarityColor;
        item.detail = attrItem.detail + '　鉴定评分：' + item.oreScore;
      } else {
        item.oreMaterialKind = 'affix';
        item.name = finalDef.name;
        item.rarityName = '矿石';
        item.rarityColor = finalDef.color;
        item.detail = finalDef.detail + '　鉴定评分：' + item.oreScore;
      }
      pushBattleLog('【矿石鉴定】' + item.name + '（评分 ' + item.oreScore + '）', 'reward');
    } else {
      item.detail = '已鉴定矿石（评分 ' + item.oreScore + '）：继续下一步加工。';
    }
    oreProcess.anim = null;
    saveGame();
    renderOreProcessPanel();
    refreshInventoryUi();
    showToast('「' + step.name + '」完成：' + (delta >= 0 ? '+' : '') + delta + ' 矿石品级。');
    return true;
  }
  function startOreStep(stepId) {
    if (!oreProcess || !oreProcess.item || oreProcess.anim) return false;
    const item = oreProcess.item;
    if (item.oreFinished) { showToast('这块矿石已经加工完成。'); return false; }
    const step = ORE_STEPS[oreStepIndex(item)];
    if (!step || step.id !== stepId) { showToast('请按顺序进行：观微 → 去皮 → 塑形。'); return false; }
    if (gold < step.cost) { showToast('金币不足：' + step.name + '需要 ' + step.cost + ' 金币。'); return false; }
    gold -= step.cost;
    updateGold();
    oreProcess.anim = {
      kind: step.id,
      t: 0,
      dur: step.dur,
      delta: oreStepDelta(step, item.oreScore)
    };
    saveGame();
    renderOreProcessPanel();
    return true;
  }
  function renderOreProcessPanel() {
    if (!oreProcess || !ui.oreProcessSteps) return;
    const item = oreProcess.item;
    if (ui.oreProcessTitle) ui.oreProcessTitle.textContent = item.name || '矿石加工';
    const finalDef = item.oreFinished ? oreFinalDef(item.oreScore | 0) : null;
    if (ui.oreProcessStatus) {
      ui.oreProcessStatus.textContent = item.oreFinished
        ? ('加工完成：' + item.name + '　评分 ' + item.oreScore + '　估值 ' + oreValue(item) + ' 金币')
        : ('当前评分：' + item.oreScore + '　已完成 ' + oreStepIndex(item) + ' / ' + ORE_STEPS.length + ' 步　当前估值 ' + oreValue(item) + ' 金币' +
          (item.oreRevealed ? ('　·　' + (item.oreRevealText || '')) : ''));
    }
    ui.oreProcessSteps.textContent = '';
    ORE_STEPS.forEach(function (step, idx) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'oreStepBtn';
      btn.setAttribute('data-ore-step', step.id);
      btn.innerHTML = '<b>' + step.name + '</b><span>' + step.cost + ' 金币</span>';
      const done = oreStepIndex(item) > idx;
      const active = !item.oreFinished && !oreProcess.anim && oreStepIndex(item) === idx;
      btn.disabled = !active;
      btn.classList.toggle('done', done);
      btn.classList.toggle('active', active);
      if (active) btn.addEventListener('click', function () { startOreStep(step.id); });
      ui.oreProcessSteps.appendChild(btn);
    });
    if (ui.oreProcessFinal) {
      ui.oreProcessFinal.textContent = '';
      ui.oreProcessFinal.classList.toggle('hidden', !item.oreFinished);
      if (item.oreFinished) {
        const sell = document.createElement('button');
        sell.type = 'button'; sell.className = 'primary oreFinalBtn';
        sell.textContent = '出售（' + oreValue(item) + ' 金币）';
        sell.addEventListener('click', function () { sellOreItem(item); closeOreProcessPanel(); });
        const give = document.createElement('button');
        give.type = 'button'; give.className = 'primary oreFinalBtn';
        give.textContent = '上缴宗门（贡献 +' + oreContributionValue(item) + '）';
        give.disabled = !hasSect();
        give.addEventListener('click', function () { contributeOreItem(item); closeOreProcessPanel(); });
        let socketBtn = null;
        if (oreMaterialKind(item) === 'attribute') {
          // 属性矿石：同样走"镶嵌"，不再直接使用
          const socket = document.createElement('button');
          socket.type = 'button'; socket.className = 'primary oreFinalBtn socket';
          socket.textContent = '镶嵌装备';
          socket.title = '把这颗属性矿石镶到装备上（附加 ' + oreAttrValueText(item) + '）';
          socket.addEventListener('click', function () {
            const id = oreAttrInlayId(item);
            const label = item.name;
            closeOreProcessPanel();
            startInlayFromMaterial(id, label);
          });
          socketBtn = socket;
        } else if (oreMaterialKind(item) === 'affix') {
          const socket = document.createElement('button');
          socket.type = 'button'; socket.className = 'primary oreFinalBtn socket';
          socket.textContent = '镶嵌装备';
          socket.title = '打开装备窗口，选择装备点击「镶嵌」，即可用材料矿石追加 1 条随机属性';
          socket.addEventListener('click', function () {
            closeOreProcessPanel();
            openGearPanel();
            showToast('请在装备窗口选择装备，点击「镶嵌」使用材料矿石。');
          });
          socketBtn = socket;
        }
        const keep = document.createElement('button');
        keep.type = 'button'; keep.className = 'primary ghost oreFinalBtn';
        keep.textContent = '收藏';
        keep.addEventListener('click', function () {
          item.oreFavorite = true;
          saveGame();
          showToast('已收藏「' + item.name + '」，保留在背包中。');
          closeOreProcessPanel();
        });
        ui.oreProcessFinal.appendChild(sell);
        ui.oreProcessFinal.appendChild(give);
        if (socketBtn) ui.oreProcessFinal.appendChild(socketBtn);
        ui.oreProcessFinal.appendChild(keep);
      }
    }
  }
  function updateOreProcess(dt) {
    if (!oreProcess || !oreProcess.anim) { if (oreProcess) drawOreProcessVisual(); return; }
    oreProcess.anim.t += dt;
    if (oreProcess.anim.t >= oreProcess.anim.dur) {
      const step = ORE_STEPS.filter(function (s) { return s.id === oreProcess.anim.kind; })[0];
      finishOreStep(step || ORE_STEPS[0]);
    }
    drawOreProcessVisual();
  }
  function drawOreProcessVisual() {
    if (!oreProcess || !ui.oreProcessCanvas) return;
    const cv = ui.oreProcessCanvas;
    const g = cv.getContext('2d');
    const Wc = cv.width, Hc = cv.height;
    const t = oreProcess.anim ? oreProcess.anim.t : 0;
    const dur = oreProcess.anim ? oreProcess.anim.dur : 1;
    const p = clamp(t / dur, 0, 1);
    const cx = Wc * 0.52, cy = Hc * 0.58, r = 74;
    g.clearRect(0, 0, Wc, Hc);
    const bg = g.createLinearGradient(0, 0, 0, Hc);
    bg.addColorStop(0, '#171017'); bg.addColorStop(1, '#0b0808');
    g.fillStyle = bg; g.fillRect(0, 0, Wc, Hc);
    g.fillStyle = 'rgba(70,52,38,.65)';
    g.fillRect(0, Hc - 46, Wc, 46);
    // 矿石：使用此前导入的 crafting_materials 矿石模型
    const score = clamp(oreProcess.item.oreScore | 0, 0, 100);
    const oreColor = score >= 70 ? '#ffd76a' : score >= 35 ? '#9ba9b5' : '#7e7468';
    g.save();
    if (oreProcess.anim && oreProcess.anim.kind === 'shape' && p > 0.58) {
      g.translate(Math.sin(p * 80) * 4, 0);
    }
    const oreImg = images['ore_' + ((oreProcess.item.oreArtIdx || 1) % 7 + 1)];
    if (oreImg && oreImg.complete && oreImg.naturalWidth > 0) {
      const glow = g.createRadialGradient(cx, cy, 8, cx, cy, 92);
      glow.addColorStop(0, oreColor + 'aa');
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = glow;
      g.beginPath(); g.arc(cx, cy, 92, 0, Math.PI * 2); g.fill();
      g.imageSmoothingEnabled = false;
      g.drawImage(oreImg, cx - 58, cy - 58, 116, 116);
    } else {
      g.fillStyle = oreColor;
      g.beginPath();
      for (let i = 0; i <= 16; i++) {
        const a = Math.PI * 2 * i / 16;
        const rr = r * (0.82 + 0.18 * Math.sin(i * 2.7 + 1.3));
        const x = cx + Math.cos(a) * rr, y = cy + Math.sin(a) * rr * 0.72;
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
      g.closePath(); g.fill();
      g.fillStyle = 'rgba(255,255,255,.22)';
      g.beginPath(); g.ellipse(cx - 20, cy - 18, 24, 14, -0.4, 0, Math.PI * 2); g.fill();
    }
    g.restore();
    if (oreProcess.item.oreRevealed || oreProcess.item.oreFinished) {
      const revealScore = clamp(oreProcess.item.oreScore | 0, 0, 100);
      const revealColor = revealScore >= 70 ? '255,215,106' : revealScore >= 35 ? '180,220,255' : '135,125,112';
      const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 240);
      g.save();
      g.globalCompositeOperation = 'lighter';
      const halo = g.createRadialGradient(cx, cy, 12, cx, cy, 104 + pulse * 20);
      halo.addColorStop(0, 'rgba(' + revealColor + ',' + (0.28 + pulse * 0.14).toFixed(3) + ')');
      halo.addColorStop(1, 'rgba(' + revealColor + ',0)');
      g.fillStyle = halo;
      g.beginPath(); g.arc(cx, cy, 110 + pulse * 20, 0, Math.PI * 2); g.fill();
      for (let i = 0; i < 6; i++) {
        const a = Date.now() / 900 + i * Math.PI / 3;
        g.strokeStyle = 'rgba(' + revealColor + ',' + (0.18 + pulse * 0.18).toFixed(3) + ')';
        g.lineWidth = 2;
        g.beginPath();
        g.moveTo(cx + Math.cos(a) * 58, cy + Math.sin(a) * 42);
        g.lineTo(cx + Math.cos(a) * 108, cy + Math.sin(a) * 78);
        g.stroke();
      }
      g.restore();
    }
    if (!oreProcess.anim) return;
    const kind = oreProcess.anim.kind;
    if (kind === 'observe') {
      const lightImg = images.oreProcessLight;
      const anchors = [
        { x: cx - 190, y: cy - 4 },   // 左侧
        { x: cx, y: cy - 152 },       // 顶部
        { x: cx + 190, y: cy - 4 }    // 右侧
      ];
      anchors.forEach(function (a, i) {
        const sway = Math.sin(p * 4.2 + i * 1.7) * 3.5;
        const lx = a.x + sway, ly = a.y + Math.cos(p * 3.4 + i) * 2.5;
        if (lightImg && lightImg.complete && lightImg.naturalWidth > 0) {
          const angle = Math.atan2(cy - ly, cx - lx);
          // 光束
          g.save();
          g.globalCompositeOperation = 'lighter';
          const beam = g.createLinearGradient(lx, ly, cx, cy);
          beam.addColorStop(0, 'rgba(180,230,255,.34)');
          beam.addColorStop(1, 'rgba(180,230,255,0)');
          g.fillStyle = beam;
          g.beginPath();
          g.moveTo(lx, ly);
          g.lineTo(cx + Math.cos(angle + 1.25) * 74, cy + Math.sin(angle + 1.25) * 74);
          g.lineTo(cx + Math.cos(angle - 1.25) * 74, cy + Math.sin(angle - 1.25) * 74);
          g.closePath();
          g.fill();
          g.restore();
          g.save();
          g.globalCompositeOperation = 'lighter';
          g.globalAlpha = 0.96;
          g.translate(lx, ly);
          g.rotate(angle - Math.PI / 2 + Math.sin(p * 5 + i) * 0.035);
          g.drawImage(lightImg, -82, -48, 164, 96);
          g.restore();
        } else {
          const grad = g.createLinearGradient(lx, ly, cx, cy);
          grad.addColorStop(0, 'rgba(160,220,255,.32)');
          grad.addColorStop(1, 'rgba(160,220,255,0)');
          g.strokeStyle = grad; g.lineWidth = 8;
          g.beginPath(); g.moveTo(lx, ly); g.lineTo(cx, cy); g.stroke();
        }
      });
    } else if (kind === 'peel') {
      const fileImg = images.oreProcessFile;
      const fx = cx - 130 + p * 260;
      if (fileImg && fileImg.complete && fileImg.naturalWidth > 0) {
        g.save();
        g.translate(fx, cy - 48 + Math.sin(p * Math.PI) * -18);
        g.rotate(-0.48 + p * 0.7);
        g.globalAlpha = 0.96;
        g.drawImage(fileImg, -82, -55, 164, 110);
        g.restore();
      } else {
        g.save(); g.translate(fx, cy - 58); g.rotate(-0.35);
        g.fillStyle = '#d9d2c5'; g.fillRect(-54, -7, 108, 14);
        g.fillStyle = '#8b5a32'; g.fillRect(42, -9, 38, 18);
        g.restore();
      }
      for (let i = 0; i < 8; i++) {
        g.fillStyle = i % 2 ? '#ffd777' : '#fff0b0';
        g.fillRect(cx + rand(-42, 42), cy + rand(-20, 30), 3, 3);
      }
    } else if (kind === 'shape') {
      const swing = Math.sin(p * Math.PI);
      const hammerImg = images.oreProcessHammer;
      const hx = cx + 118 - swing * 150, hy = cy - 138 + swing * 146;
      if (hammerImg && hammerImg.complete && hammerImg.naturalWidth > 0) {
        g.save();
        g.translate(hx, hy);
        g.rotate(-0.72 + swing * 1.28);
        g.drawImage(hammerImg, -104, -52, 208, 104);
        g.restore();
      } else {
        g.save(); g.translate(hx, hy); g.rotate(-0.72 + swing * 1.28);
        g.fillStyle = '#9fadb4'; g.fillRect(-16, -10, 118, 20);
        g.fillStyle = '#6b4930'; g.fillRect(88, -7, 72, 14);
        g.restore();
      }
      if (p > 0.62) {
        g.fillStyle = 'rgba(255,235,170,' + (1 - p) * 0.8 + ')';
        g.beginPath(); g.arc(cx, cy, 30 + p * 42, 0, Math.PI * 2); g.fill();
      }
    }
  }

  // ---------- 被动技能石 ----------
  function makeEmptyGem() {
    return {
      id: 'gem' + (++itemSeq),
      type: 'gem',
      identified: false,
      name: '空白被动技能石',
      icon: '石',
      detail: '右键点击挑选被动效果',
      rarityColor: '#d9d3c4'
    };
  }

  function gemOptionToItem(opt) {
    const value = opt.support ? 0 : randInt(opt.min, opt.max);
    return {
      id: 'gem' + (++itemSeq),
      type: 'gem',
      identified: true,
      effect: opt.id,
      value: value,
      onlySkill: opt.onlySkill || null,
      projectileOnly: !!opt.projectileOnly,
      minionOnly: !!opt.minionOnly,
      support: !!opt.support,
      name: (opt.support ? '辅助技能石：' : '被动技能石：') + opt.name,
      icon: '石',
      detail: opt.support ? (opt.short || opt.desc || opt.name) : (opt.name + ' +' + value + opt.unit),
      desc: opt.desc || '',
      rarityColor: '#8fd7ff'
    };
  }

  function socketedGems() {
    const list = [];
    knownSkills.forEach(function (skill) {
      (skill.sockets || []).forEach(function (gem) { list.push(gem); });
    });
    return list;
  }

  // ---------- 技能的镶嵌槽：默认 3 个，最多 6 个，每开一个槽 500 金币 ----------
  const SOCKET_SLOTS_DEFAULT = 3;
  const SOCKET_SLOTS_MAX = 6;
  const SOCKET_SLOT_COST = 500;

  function socketSlotsOf(skill) {
    const n = Math.floor(Number(skill && skill.socketSlots) || SOCKET_SLOTS_DEFAULT);
    return clamp(n, SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
  }

  // 花 500 金币开一个槽（存到技能上，随存档保留）
  function buySocketSlot(skill) {
    if (!skill) return false;
    const slots = socketSlotsOf(skill);
    if (slots >= SOCKET_SLOTS_MAX) {
      showToast('「' + skill.name + '」的镶嵌槽已经开到上限（' + SOCKET_SLOTS_MAX + ' 个）。');
      return false;
    }
    if (gold < SOCKET_SLOT_COST) {
      showToast('金币不足：开一个镶嵌槽需要 ' + SOCKET_SLOT_COST + ' 金币（当前 ' + gold + '）。');
      return false;
    }
    gold -= SOCKET_SLOT_COST;
    skill.socketSlots = slots + 1;
    saveGame();
    updateGold();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (!ui.socketPopup.classList.contains('hidden')) renderSocketOptions();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('「' + skill.name + '」镶嵌槽 +1（' + (slots + 1) + ' / ' + SOCKET_SLOTS_MAX +
      '），花费 ' + SOCKET_SLOT_COST + ' 金币。');
    return true;
  }

  // 汇总某几类被动技能石的百分比 / 数值
  function gemSum(effectIds) {
    let sum = 0;
    socketedGems().forEach(function (gem) {
      if (effectIds.indexOf(gem.effect) >= 0) sum += gem.value || 0;
    });
    return sum;
  }

  function gemDamageMult(kind) {
    if (kind === 'pet') {
      return 1 + skillGemSum('summonPet', ['summonDamage']) / 100;
    }
    // 普通攻击与劈砍共用「劈砍」技能上的辅助宝石
    return 1 + skillGemSum('slash', ['weaponDamage', 'meleeDamage', 'spellDamage']) / 100;
  }

  function skillGemSum(skillId, effectIds) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return 0;
    let sum = 0;
    (skill.sockets || []).forEach(function (gem) {
      if (effectIds.indexOf(gem.effect) >= 0) sum += gem.value || 0;
    });
    return sum;
  }

  /* ============================================================
   * 投射物 / 通用辅助技能石（参考流放之路的辅助宝石）
   *   多重投射物：伤害总降 25%、技能速度总降 20%、多发射 2 个投射物
   *   投射物加速：投射物速度总增 40%
   *   处决：你低血 或 敌人低血（≤ 最大生命 50%）时，击中伤害总增 30%
   * ------------------------------------------------------------
   * 投射物辅助**只认技能标签**，不绑定任何具体技能：
   *   技能定义里写了 tags: ['projectile'] 的，就是投射物技能 ——
   *     破空斩（剑气）、强力射击（箭矢）、盾墙（格挡触发的碎石崩裂）。
   *   以后新做带投射物的技能（火球术 / 闪电箭……得到飞行物之后），
   *   只要在 defs.js 的技能定义里补一句 tags: ['projectile']，
   *   多重投射物 / 投射物加速 就自动能镶、自动增幅，不用回来改这里。
   *   （旧档案里没有 tags 的技能，用 LEGACY_PROJECTILE_SKILLS 兜底）
   * ============================================================ */
  const LEGACY_PROJECTILE_SKILLS = ['airSlash', 'powerShot', 'shieldWall'];
  const MULTI_PROJ_EXTRA = 2;        // 额外投射物数量
  const MULTI_PROJ_DMG_PCT = -25;    // 被辅助技能伤害总降
  const MULTI_PROJ_SPEED_PCT = -20;  // 被辅助技能速度总降
  const PROJ_SPEED_PCT = 40;         // 投射物速度总增
  const LOW_LIFE_PCT = 50;           // 低血门槛：≤ 最大生命 50%
  const EXECUTE_DMG_PCT = 30;        // 处决：每一条 +30%

  function skillById(id) {
    return knownSkills.filter(function (s) { return s.id === id; })[0] || null;
  }

  function skillHasGem(skillId, effect) {
    const skill = skillById(skillId);
    return !!(skill && (skill.sockets || []).some(function (g) { return g.effect === effect; }));
  }

  // 技能定义（主动技能池 + 精英奖励技能池）
  function skillDefOf(id) {
    return ACTIVE_SKILL_POOL.concat(SKILL_POOL).concat(SECT_SKILLS)
      .filter(function (d) { return d.id === id; })[0] || null;
  }

  // 技能标签：优先看定义里的 tags，旧档案 / 没写 tags 的按兜底名单
  function skillTagsOf(skillId) {
    const def = skillDefOf(skillId);
    const tags = (def && def.tags) ? def.tags.slice() : [];
    if (!tags.length && LEGACY_PROJECTILE_SKILLS.indexOf(skillId) >= 0) tags.push('projectile');
    return tags;
  }

  // 是不是投射物技能（辅助宝石能否镶嵌、能否被增幅，全看这一个判断）
  function isProjectileSkill(skillId) {
    return skillTagsOf(skillId).indexOf('projectile') >= 0;
  }

  // 所有投射物技能 + 它们的投射物是什么（镶嵌提示里直接列出来，别再写死"破空斩的剑气"）
  function projectileSkillHint() {
    const names = [];
    ACTIVE_SKILL_POOL.concat(SKILL_POOL).forEach(function (d) {
      if (!isProjectileSkill(d.id)) return;
      names.push(d.name + (d.projectileNote ? '（' + d.projectileNote + '）' : ''));
    });
    return names.join(' / ');
  }

  /* 投射物的"独立结算次数"：基础次数 + 多重投射物的额外投射物。
   *   和「强力射击」的规则一致 —— 多一支箭 = 多结算一次伤害；
   *   剑气 / 碎石同理（视觉上多出来的投射物，各自单独结算、单独显示）。 */
  function projectileHitsOf(skillId, base) {
    const b = (base === undefined) ? 1 : base;
    return b + (skillHasGem(skillId, 'multipleProjectiles') ? MULTI_PROJ_EXTRA : 0);
  }

  // 旧档案里存下来的技能石可能没有 projectileOnly 字段，按 effect 回查一遍
  function isProjectileOnlyGem(gem) {
    if (!gem) return false;
    if (gem.projectileOnly) return true;
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === gem.effect; })[0];
    return !!(opt && opt.projectileOnly);
  }

  // 低血：≤ 最大生命 50%（玩家血量见战斗状态，敌人血量见 maxHp）
  function playerLowLife() {
    return combat.maxHp > 0 && combat.hp <= combat.maxHp * LOW_LIFE_PCT / 100;
  }

  function enemyLowLife(e) {
    return !!(e && e.maxHp > 0 && e.hp <= e.maxHp * LOW_LIFE_PCT / 100);
  }

  // 被辅助技能的伤害总增/总降（同一层相加，不做叠乘）
  function supportDamagePct(skillId, enemy) {
    let pct = 0;
    if (skillHasGem(skillId, 'multipleProjectiles')) pct += MULTI_PROJ_DMG_PCT;
    if (skillHasGem(skillId, 'execute')) {
      if (playerLowLife()) pct += EXECUTE_DMG_PCT;
      if (enemyLowLife(enemy)) pct += EXECUTE_DMG_PCT;
    }
    return pct;
  }

  function supportDamageMult(skillId, enemy) {
    return 1 + supportDamagePct(skillId, enemy) / 100;
  }

  /* 技能最终伤害倍率 = 辅助宝石倍率 × 世界BOSS 时装充能倍率
   *   —— 充能只作用于"被充能的那个技能"，并且只吃一次（见 costumeChargeMult） */
  function skillDamageMult(skillId, enemy) {
    /* 漂泊者护甲：每 2 点力量 +1% 投射物伤害（只作用于带「投射物」标签的技能） */
    const projMult = (derived.projDmgPct && isProjectileSkill(skillId))
      ? 1 + derived.projDmgPct / 100 : 1;
    /* 灵力催动（练气期及以上）：技能伤害 +40% */
    return supportDamageMult(skillId, enemy) * costumeChargeMult(skillId) * projMult *
      spiritSkillMult();
  }

  // 投射物数量：基础数量 + 多重投射物的额外数量
  function projectileCountOf(skillId, base) {
    return base + (skillHasGem(skillId, 'multipleProjectiles') ? MULTI_PROJ_EXTRA : 0);
  }

  // 投射物速度倍率（投射物加速）
  function projectileSpeedMult(skillId) {
    return 1 + (skillHasGem(skillId, 'projectileSpeed') ? PROJ_SPEED_PCT : 0) / 100;
  }

  // 技能速度倍率（多重投射物：总降 20% → 出手间隔 ×1.25）
  function skillSpeedMult(skillId) {
    return Math.max(0.2, 1 + (skillHasGem(skillId, 'multipleProjectiles') ? MULTI_PROJ_SPEED_PCT : 0) / 100);
  }

  /* ============================================================
   * 召唤生物辅助技能石（肉盾 / 狂噬 / 召唤生物专精 / 仆从 / 忠诚）
   *   只对"被辅助召唤技能召唤出来的"宠物生效：
   *   法杖灵宠、宠物蛋孵出来的宠物不是技能召唤的，不吃这一套。
   *   数值都按"同层相加"的习惯处理（和多重投射物 / 处决一致）。
   * ============================================================ */
  const MINION_MEAT_SHIELD_DMG_PCT = -35;    // 肉盾：召唤物造成的伤害总降
  const MINION_MEAT_SHIELD_TAKEN_PCT = -35;  // 肉盾：召唤物受到的伤害总降
  const MINION_FRENZY_DMG_PCT = 30;          // 狂噬：召唤物造成的伤害总增
  const MINION_FRENZY_TAKEN_PCT = 20;        // 狂噬：召唤物受到的伤害总增
  /* 召唤生物专精：技能 +1 级 —— 走"召唤物等级 +1"（见 summonLevelOf），
   * 于是和其它 +1 级一样：伤害 +15%、生命 +20%（数值在 PET_* 常量里）。 */
  const MINION_SERVANT_HP_MULT = 2.2;        // 仆从「庞然大物」：生命
  const MINION_SERVANT_ATK_MULT = 1.6;       // 仆从「庞然大物」：伤害
  const MINION_SERVANT_SCALE = 1.55;         // 仆从「庞然大物」：立绘放大
  const MINION_CAP_WITH_SERVANT = 1;         // 仆从：只能拥有一个召唤物
  /* 调试/测试用：敌人这一回合打谁
   *   null = 按原本的 50% 概率随机；true = 一定打宠物；false = 一定打玩家（忠诚就能稳定验证） */
  let debugForcePetHit = null;
  window.__debugForcePetHit = function (on) {
    debugForcePetHit = (on === null || on === undefined) ? null : !!on;
    return debugForcePetHit;
  };
  window.__debugHealPets = function () {
    alivePets().forEach(function (p) { p.hp = p.maxHp; });
    if (ui.petPopup && !ui.petPopup.classList.contains('hidden')) renderPetPanel();
    return alivePets().map(function (p) { return { name: p.name, hp: p.hp, maxHp: p.maxHp }; });
  };
  window.__debugSummonScale = function () {
    return {
      damageScale: SUMMON_DAMAGE_SCALE, hyenaDoublePct: HYENA_DOUBLE_PCT,
      base: { dmgMin: PET_BASE_DMG_MIN, dmgMax: PET_BASE_DMG_MAX, hp: PET_BASE_HP,
        dmgPerLevel: PET_DMG_PER_LEVEL, hpPerLevel: PET_HP_PER_LEVEL }
    };
  };
  /* 调试/测试用：召唤物按当前等级应有的基础伤害区间 / 生命上限 */
  window.__debugPetStats = function (sprite, skillId) {
    const p = { sprite: sprite || 'scorpio', atkMult: 1, skillId: skillId || null, hpMult: 1 };
    return {
      level: summonLevelOf(p),
      dmg: petDamageRangeOf(p),
      maxHp: petMaxHpOf(p),
      alive: alivePets().map(function (x) {
        return { name: x.name, level: summonLevelOf(x), hp: Math.round(x.hp), maxHp: x.maxHp };
      })
    };
  };
  window.__debugPetAttackValue = function (sprite) {
    return petAttackValue({ sprite: sprite || 'scorpio', atkMult: 1, skillId: null, name: sprite || 'scorpio' });
  };
  window.__debugPetEffects = function (sprite) {
    if (!activeEnemy) return null;
    const labels = petApplyOnHitEffects({ sprite: sprite || 'scorpio' }, activeEnemy, 50);
    return {
      labels: labels,
      burn: activeEnemy.burnStacks || 0,
      poison: activeEnemy.poison ? { dmg: activeEnemy.poison.dmg, turns: activeEnemy.poison.turns, stacks: activeEnemy.poison.stacks || 1 } : null,
      bleed: activeEnemy.bleed ? { dmg: activeEnemy.bleed.dmg, turns: activeEnemy.bleed.turns, stacks: activeEnemy.bleed.stacks || 1 } : null
    };
  };

  // 旧存档里存下来的技能石可能没有 minionOnly 字段，按 effect 回查
  function isMinionOnlyGem(gem) {
    if (!gem) return false;
    if (gem.minionOnly) return true;
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === gem.effect; })[0];
    return !!(opt && opt.minionOnly);
  }
  function hasMinionSupport(skillId, effect) {
    return !!skillId && skillHasGem(skillId, effect);
  }
  /* 一只宠物吃到的召唤辅助加成（按它来自哪个召唤技能实时结算：
   * 换宝石立刻生效，拔掉也立刻失效） */
  function minionGemMods(p) {
    const sid = (p && p.skillId) || null;
    const mods = { dmgMult: 1, takenMult: 1, hpMult: 1, atkMult: 1, scale: 1, notes: [] };
    if (!sid) return mods;                       // 法杖灵宠 / 宠物蛋：不吃召唤辅助
    if (hasMinionSupport(sid, 'minionMastery')) {
      // 技能 +1 级：加成统一走 summonLevelOf()（伤害 +15%、生命 +20%），这里只留备注
      mods.notes.push('专精 +1级');
    }
    let dmgPct = 0, takenPct = 0;
    if (hasMinionSupport(sid, 'meatShield')) {
      dmgPct += MINION_MEAT_SHIELD_DMG_PCT;
      takenPct += MINION_MEAT_SHIELD_TAKEN_PCT;
      mods.notes.push('肉盾');
    }
    if (hasMinionSupport(sid, 'feedingFrenzy')) {
      dmgPct += MINION_FRENZY_DMG_PCT;
      takenPct += MINION_FRENZY_TAKEN_PCT;
      mods.notes.push('狂噬');
    }
    mods.dmgMult *= Math.max(0.05, 1 + dmgPct / 100);
    mods.takenMult *= Math.max(0.05, 1 + takenPct / 100);
    if (hasMinionSupport(sid, 'servant')) {
      mods.hpMult *= MINION_SERVANT_HP_MULT;
      mods.atkMult *= MINION_SERVANT_ATK_MULT;
      mods.scale = MINION_SERVANT_SCALE;
      mods.notes.push('庞然大物');
    }
    mods.dmgPct = Math.round(dmgPct);
    mods.takenPct = Math.round(takenPct);
    return mods;
  }
  // 出战召唤物上限：「仆从」在场时只能有 1 只
  function hasServantGem() {
    return knownSkills.some(function (s) { return skillHasGem(s.id, 'servant'); });
  }
  function petCap() {
    return hasServantGem() ? MINION_CAP_WITH_SERVANT : PET_MAX;
  }
  // 「忠诚」指定的伙伴：由带忠诚的召唤技能召唤出来、还活着的宠物
  function loyaltyPet() {
    return alivePets().filter(function (p) {
      return !!(p.skillId && skillHasGem(p.skillId, 'loyalty'));
    })[0] || null;
  }

  // 主动技能每提升 1 级，效果提高 12%
  /* 「阳爪」= 6 次连击，每次造成「攻击力的 15%」（逐次独立结算 / 显示）。
   * 以前每段是固定的 6~10 点基础伤害 + 敌人等级/3，低级时虚高、高级时又完全跟不上，
   * 所以改成按玩家攻击力百分比结算：攻击力 = 普攻区间中值（28.5 + 攻击加成），
   * 和宠物估伤（玩家攻击的 35%）用的是同一个口径。 */
  const YANG_CLAW_HITS = 6;
  const YANG_CLAW_ATK_PCT = 0.15;
 function skillLevelMult(skill) {
   const lv = (skill && skill.level) || 1;
   return 1 + 0.12 * (lv - 1);
 }

  // 主动伤害技能（劈砍 / 阳爪）：每提升 1 级，基础伤害提高 10%
  const SKILL_DAMAGE_LV_STEP = 0.10;
  function skillDamageLevelMult(skill) {
    const lv = (skill && skill.level) || 1;
    const step = (skill && (skill.id === 'slash' || skill.id === 'yangClaw'))
      ? SKILL_DAMAGE_LV_STEP
      : 0.12;
    return 1 + step * (lv - 1);
  }

  // 技能格 / 悬停提示里显示的「等级加成 +N%」（伤害技能按伤害加成算）
  function levelBonusPct(skill) {
    const mult = (skill && skill.dmg) ? skillDamageLevelMult(skill) : skillLevelMult(skill);
    return Math.round((mult - 1) * 100);
  }

  // 玩家暴击：暴击率来自手套等装备，暴击伤害 = 1.5 倍 + 暴击伤害加成
  let lastBloodInfo = null;   // 调试用：最近一次「炼血刀诀」的结算结果
  /* 玩家打出的伤害：先按"境界压制"折算，再判暴击
   *   target 省略时按当前交战目标（activeEnemy）算；多目标技能可以把具体敌人传进来 */
  function rollPlayerDamage(base, target) {
    const tgt = (target && typeof target.level === 'number') ? target : activeEnemy;
    const realmMult = realmDamageMult(playerLevel(), tgt ? tgt.level : playerLevel());
    if (realmMult < 1) noticeRealmGap('player', realmMult);   // 战斗内提示"境界压制"
    let raw = Math.max(1, Math.round(base * realmMult));
    /* 门派法诀「炼血刀诀」（近战武器）：按双方生命百分比差额增伤 / 吸血 */
    let blood = null;
    if (lianxueActive() && tgt && tgt.hp > 0) {
      const myPct = combat.maxHp > 0 ? combat.hp / combat.maxHp : 1;
      const foePct = tgt.maxHp > 0 ? Math.max(0, tgt.hp) / tgt.maxHp : 1;
      const diff = myPct - foePct;
      if (diff > 0.02) {
        const extra = Math.max(1, Math.round(raw * Math.min(1, diff)));
        raw += extra;
        blood = { extra: extra, diff: diff };
      } else if (diff < -0.02) {
        const heal = Math.max(1, Math.round(raw * Math.min(1, -diff)));
        combat.hp = Math.min(combat.maxHp, combat.hp + heal);
        blood = { heal: heal, diff: diff };
      }
      if (blood && typeof tgt.x === 'number') {
        addTextPopup(tgt.x - cameraX() + 88, GROUND_Y - 268,
          blood.extra ? ('炼血 +' + blood.extra) : ('炼血吸 +' + blood.heal),
          '#ff8fb0');
      }
    }
    lastBloodInfo = blood;   // 调试用：最近一次"炼血"结算
    const isCrit = derived.critChance > 0 && Math.random() * 100 < derived.critChance;
    const dmg = isCrit
      ? Math.max(1, Math.round(raw * (1.5 + derived.critDamage / 100)))
      : Math.max(1, Math.round(raw));
    return { dmg: dmg, crit: isCrit, blood: blood };
  }

  // 长弓基底「攻击时 X% 几率额外射出一支箭矢」：命中判定，额外一击单独结算
  const bowStat = { attacks: 0, procs: 0 };   // 调试统计：命中率
  function rollExtraArrow() {
    return derived.extraArrowPct > 0 && Math.random() * 100 < derived.extraArrowPct;
  }

  // ---------- 休息回血：站在路上不动（休息）满 1 分钟，恢复 3% 最大生命 ----------
  const REST_HEAL_INTERVAL = 60;   // 秒
  const REST_HEAL_PCT = 0.03;      // 3% 最大生命

  function restHealOnce() {
    const heal = Math.max(1, Math.round(combat.maxHp * REST_HEAL_PCT));
    /* 路上原地休息只回血，**不再补灵力**：
     *   灵力要在灵脉（仙人掌）旁打坐修炼才回（见 updateXiuLian 里的吐纳回灵）。 */
    if (combat.hp >= combat.maxHp) return 0;
    const before = combat.hp;
    combat.hp = Math.min(combat.maxHp, combat.hp + heal);
    const real = Math.round(combat.hp - before);
    addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 172, real, '#6dff8d', true);
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return real;
  }

  function updateRestHeal(dt) {
    // 只在路上、没有移动、也没有出手动作时算「休息」
    const resting = gameState === 'walk' && !deathActive && !input.moving &&
      player.vx <= 0 && playerAtkT <= 0;
    if (!resting) {
      restHealTimer = 0;
      return;
    }
    restHealTimer += dt;
    while (restHealTimer >= REST_HEAL_INTERVAL) {
      restHealTimer -= REST_HEAL_INTERVAL;
      const healed = restHealOnce();
      if (healed > 0) {
        showToast('休息恢复：生命 +' + healed + '（' + Math.round(REST_HEAL_PCT * 100) + '% 最大生命）');
        pushBattleLog('休息 1 分钟：恢复 ' + healed + ' 点生命', 'reward');
      }
    }
  }

  // 生命每回合再生
  let regenUiTimer = 0;
  function applyLifeRegen(dt) {
    // 「生命再生」现在是按回合结算的，不再按秒持续回血（见 applyTurnRegen）
    return;
  }

  // 生命再生：每个战斗回合开始时结算一次
  function applyTurnRegen() {
    // 灵力恢复（首饰基底「灵力恢复 +5」）：每回合恢复，上限是"可用灵力上限"（扣掉占用）
    if (derived.mpRegen > 0) {
      const mpCap = Math.min(combat.maxMp, derived.mpUsable);
      const back = Math.min(derived.mpRegen, mpCap - combat.mp);
      if (back > 0.01) {
        combat.mp = Math.min(mpCap, combat.mp + back);
        addDamagePopup(
          player.worldX - cameraX() - 82,
          GROUND_Y - 196,
          Math.round(back),
          '#7fb6ff',
          true
        );
        pushBattleLog('灵力恢复：本回合恢复 ' + Math.round(back) + ' 点灵力', 'reward');
      }
    }
    if (derived.lifeRegen > 0 && combat.hp < combat.maxHp) {
      const heal = Math.min(derived.lifeRegen, combat.maxHp - combat.hp);
      if (heal > 0) {
        combat.hp = Math.min(combat.maxHp, combat.hp + heal);
        addDamagePopup(
          player.worldX - cameraX() - 82,
          GROUND_Y - 168,
          Math.round(heal),
          '#6dff8d',
          true
        );
        pushBattleLog('生命再生：本回合恢复 ' + Math.round(heal) + ' 点生命', 'reward');
      }
    }
    updateBattleHud();
  }

  // ---------- 气血反弹（持续生效） ----------
  // 必须真的学过「气血反弹」才允许生效（特效与反噬都以此为前提）
  function hasBloodReboundSkill() {
    return knownSkills.some(function (s) { return s.id === 'bloodRebound'; });
  }

  function bloodReboundValue() {
    const sk = knownSkills.filter(function (s) { return s.id === 'bloodRebound'; })[0];
    return Math.max(1, Math.round(
      (combat.maxHp * 0.10 + derived.strengthTotal) * skillLevelMult(sk)
    ));
  }

  // 每次被敌人攻击时反噬敌人；返回本次反噬伤害（未激活返回 0）
  function applyBloodRebound() {
    if (!bloodRebound || !hasBloodReboundSkill() || !activeEnemy) return 0;
    const dmg = bloodReboundValue();
    // 反噬成功的瞬间：血气冲向敌人，并在玩家身周炸开两圈浓血色波纹
    playSfx('bloodRebound');
    spawnBloodBolt();
    spawnBloodWave(1.4, 0);
    spawnBloodWave(1.0, 0.12);
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 218,
      dmg,
      '#e05cff'
    );
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「气血反弹」反噬 ' + dmg + ' 点，敌人被反噬击败！');
      updateBattleHud();
      if (!resolveEnemyDown()) return dmg;
      pendingVictory = 0.5;
      battleTurn = 'player';
      resetPlayerIdleTimer();
      setControls('battle');
      return dmg;
    }
    return dmg;
  }

  // 尸体必定掉 5~10 金币；道具 / 材料 / 装备各自独立 5% 掉落
  // 结算尸体战利品。
  // commit=false 时只掷结果、先摆在原地（敌人一倒下就要能看出"这堆东西值不值得搜"），
  // 等玩家搜索尸体时再真正进背包 / 加金币。
  function rollCorpseLoot(level, tier, commit, forceGear) {
    const take = commit !== false;
    const drops = [];
    /* 每日副本（入侵）的"双倍场"：金币翻倍，材料 / 道具 / 装备的判定各掷两次（掉落 ×2） */
    const lootMul = dungeonRewardMul();
    const invasionDef = currentInvasionDef();
    // 金币随敌人等级提高：Lv1 约 5~10，之后每级上下限都会提升
    const coins = randInt(4 + level, 8 + level * 2) * lootMul;
    if (take) gold += coins;
    drops.push({
      kind: 'gold',
      name: '金币',
      icon: '金',
      detail: '+' + coins,
      coins: coins,
      rarityColor: '#ffd970'
    });
    for (let roll = 0; roll < lootMul; roll++) {
      if (Math.random() < 0.05) {
        const mat = makeMaterial(level);
        if (take) inventory.push(mat);
        drops.push(mat);
      }
      if (Math.random() < 0.05) {
        const pot = makePotion(level);
        if (take) inventory.push(pot);
        drops.push(pot);
      }
    }
    /* 入侵副本：击杀敌人有几率掉这一天的专属入侵材料（双倍场就是两次判定） */
    const invasionMat = invasionMaterialOf(invasionDef);
    if (invasionMat) {
      for (let roll = 0; roll < lootMul; roll++) {
        if (Math.random() < INVASION_DROP_CHANCE) {
          const inv = makeInvasionMaterial(invasionMat);
          if (take) inventory.push(inv);
          drops.push(inv);
        }
      }
    }
    /* 深渊入侵：邪恶法师还掉「深渊战利品」（可售卖，图标是用户给的 gift 素材） */
    if (invasionHasLoot(invasionDef)) {
      for (let roll = 0; roll < lootMul; roll++) {
        if (Math.random() < ABYSS_GIFT_DROP_CHANCE) {
          const gift = makeDungeonLootItem(invasionDef);
          if (!gift) break;
          if (take) inventory.push(gift);
          drops.push(gift);
        }
      }
    }
    // 装备掉落品级受敌人品级限制；forceGear=true（洞府内）时必掉（精英怪还会多掉一件）
    for (let roll = 0; roll < lootMul; roll++) {
      rollGearRarities(tier || 'common', !!forceGear).forEach(function (gearRarity) {
        const gear = makeEquipment(level, gearRarity);
        gear.fromCave = !!forceGear;
        if (take) inventory.push(gear);
        drops.push(gear);
      });
    }
    if (take) updateGold();
    return drops;
  }

  // 把"掉在原地"的战利品收进背包（搜索尸体时调用）
  //   markFromActivity=true 时给物品打上"副本内所得"标记：副本里阵亡会全部掉回去
  function collectCorpseDrops(drops, markFromActivity) {
    (drops || []).forEach(function (d) {
      if (!d) return;
      if (d.kind === 'gold') gold += d.coins || 0;
      else {
        if (markFromActivity) d.fromActivity = true;
        inventory.push(d);
      }
    });
    updateGold();
  }

  function recalcPlayerStats() {
    syncStaffPet();
    syncSantosSkill();
    let hp = 100 + (player.level - 1) * 10 + Math.max(0, player.hpMaxBonus | 0);
    /* 灵力（= 法力槽）：境界基准 = 当前境界升级所需修为 × 2；再叠装备/宝石/智力加成 */
    let mp = spiritBaseOf(playerLevel()) + Math.max(0, player.mpMaxBonus | 0);
    let armor = 0;
    let flatAtk = (player.level - 1) * 2;
    let speedPct = 0;
    let res = 0;
    let thorns = 0;
    let blockPct = 0;
    let strength = 0;
    let agility = 0;         // 装备/图鉴提供的敏捷（三维之一）
    let intelligence = 0;    // 装备/图鉴提供的智力（三维之一）
    let chaosRes = 0;
    let critReduce = 0;
    let armorPct = 0;
    let lifeRegen = 0;
    let mpRegen = 0;
    let extraArrowPct = 0;
    let atkSpeed = 0;
    let critChance = 0;
    let critDamage = 0;
    let spellDmgPctBonus = 0;    // 法术伤害提高（目前只有灵根会给，见 spiritRootBonus）
    // 手套专属：击杀 / 命中回复与异常状态几率
    let lifePerKill = 0;
    let mpPerKill = 0;
    let lifePerHit = 0;
    let poisonChance = 0;
    let bleedChance = 0;
    let igniteChance = 0;
    // 盾牌新增：受到的伤害降低 / 吸纳为生命
    let dmgReducePct = 0;
    // 新增：物理伤害减免（盾牌/衣服）、两项上限加成（盾牌/衣服）
    let physReducePct = 0;
    let resCapPct = 0;
    let blockCapPct = 0;
    // 新增：闪避几率 / 闪避上限 / 总减伤上限
    let dodgePct = 0;
    let dodgeCapPct = 0;
    let reduceCapPct = 0;
    // 防具基底：闪避点数（敏捷性质防具）与「闪避提高 X%」
    let evasion = 0;
    let evasionPct = 0;
    let recoupPct = 0;
    // 灵宝「蓝宝灵韵」：已装备的蓝色（魔法）装备属性按比例增幅
    const magicAmp = treasureSpecial('magicAmp')
      ? 1 + ((equipment.treasure.special && equipment.treasure.special.roll) || 100) / 100
      : 1;
    Object.keys(equipment).forEach(function (slot) {
      const g = equipment[slot];
      if (!g) return;
      const m = (magicAmp !== 1 && g.rarity === 'magic') ? magicAmp : 1;
      /* 门派法诀「封极法」：该部位参悟过、且这件装备词缀同源统一时，整件装备数值被单向增幅 */
      const fj = fengjiSlotMult(slot);
      const mp_ = function (v) { return Math.round((v || 0) * m * fj); };
      hp += mp_(g.hp);
      armor += mp_(g.armor);
      flatAtk += mp_(g.atk);
      speedPct += mp_(g.speedPct);
      mp += mp_(g.mp);
      res += mp_(g.res);
      thorns += mp_(g.thorns);
      blockPct += mp_(g.blockPct);
      strength += mp_(g.strength);
      agility += mp_(g.agility);
      intelligence += mp_(g.intelligence);
      chaosRes += mp_(g.chaosRes);
      critReduce += mp_(g.critReduce);
      armorPct += mp_(g.armorPct);
      lifeRegen += mp_(g.lifeRegen);
      mpRegen += mp_(g.mpRegen);
      extraArrowPct += mp_(g.extraArrowPct);
      atkSpeed += mp_(g.atkSpeed);
      critChance += mp_(g.critChance);
      critDamage += mp_(g.critDamage);
      lifePerKill += mp_(g.lifePerKill);
      mpPerKill += mp_(g.mpPerKill);
      lifePerHit += mp_(g.lifePerHit);
      poisonChance += mp_(g.poisonChance);
      bleedChance += mp_(g.bleedChance);
      igniteChance += mp_(g.igniteChance);
      dmgReducePct += mp_(g.dmgReducePct);
      physReducePct += mp_(g.physReducePct);
      resCapPct += mp_(g.resCapPct);
      blockCapPct += mp_(g.blockCapPct);
      dodgePct += mp_(g.dodgePct);
      dodgeCapPct += mp_(g.dodgeCapPct);
      reduceCapPct += mp_(g.reduceCapPct);
      evasion += mp_(g.evasion);
      evasionPct += mp_(g.evasionPct);
      recoupPct += mp_(g.recoupPct);
    });
    // 图鉴增幅：已激活的图鉴提供永久属性
    const codexBonus = codexTotalBonus();
    hp += codexBonus.hp || 0;
    armor += codexBonus.armor || 0;
    flatAtk += codexBonus.atk || 0;
    speedPct += codexBonus.speedPct || 0;
    mp += codexBonus.mp || 0;
    res += codexBonus.res || 0;
    thorns += codexBonus.thorns || 0;
    blockPct += codexBonus.blockPct || 0;
    strength += codexBonus.strength || 0;
    agility += codexBonus.agility || 0;
    intelligence += codexBonus.intelligence || 0;
    chaosRes += codexBonus.chaosRes || 0;
    critReduce += codexBonus.critReduce || 0;
    armorPct += codexBonus.armorPct || 0;
    dodgePct += codexBonus.dodgePct || 0;
    evasion += codexBonus.evasion || 0;
    evasionPct += codexBonus.evasionPct || 0;
    lifeRegen += codexBonus.lifeRegen || 0;
    atkSpeed += codexBonus.atkSpeed || 0;
    critChance += codexBonus.critChance || 0;
    critDamage += codexBonus.critDamage || 0;
    /* 灵根增幅：随机灵根给的永久属性（字段和图鉴/词缀同一套） */
    const rootBonus = spiritRootBonus();
    hp += rootBonus.hp || 0;
    armor += rootBonus.armor || 0;
    flatAtk += rootBonus.atk || 0;
    speedPct += rootBonus.speedPct || 0;
    mp += rootBonus.mp || 0;
    res += rootBonus.res || 0;
    thorns += rootBonus.thorns || 0;
    blockPct += rootBonus.blockPct || 0;
    strength += rootBonus.strength || 0;
    agility += rootBonus.agility || 0;
    intelligence += rootBonus.intelligence || 0;
    chaosRes += rootBonus.chaosRes || 0;
    critReduce += rootBonus.critReduce || 0;
    armorPct += rootBonus.armorPct || 0;
    dodgePct += rootBonus.dodgePct || 0;
    evasion += rootBonus.evasion || 0;
    evasionPct += rootBonus.evasionPct || 0;
    lifeRegen += rootBonus.lifeRegen || 0;
    atkSpeed += rootBonus.atkSpeed || 0;
    critChance += rootBonus.critChance || 0;
    critDamage += rootBonus.critDamage || 0;
    spellDmgPctBonus += rootBonus.spellDmgPct || 0;   // 法术伤害：单独累计，见下面 derived.spellDmgPct
    // 力量：每点 +5 生命上限（装备/图鉴提供的力量同样算），
    // 灵宝「蛮力之心」会把这 5 点提升到 10 点；力量不再提供攻击伤害
    const strHpPer = treasureSpecial('strToHpDouble') ? 10 : 5;
    hp += strength * strHpPer;
    // 自由分配的属性点：力量 / 敏捷 / 智力
    const attrStr = Math.max(0, player.attrs.str | 0);
    const attrAgi = Math.max(0, player.attrs.agi | 0);
    const attrInt = Math.max(0, player.attrs.int | 0);
    // 加点力量与装备力量同一套换算
    hp += attrStr * strHpPer;
    // 记录"力量换算出来的生命"（面板/调试用）
    derived.strengthHp = (strength + attrStr) * strHpPer;
    // 敏捷：每点 +1% 攻击速度、+0.6% 移动速度
    atkSpeed += attrAgi;
    speedPct += attrAgi * 0.6;
    // 装备/图鉴提供的敏捷同样按「每点 +1% 攻速、+0.6% 移速」换算
    atkSpeed += agility;
    speedPct += agility * 0.6;
  // 智力：每点 +2 灵力上限（法术消耗灵力；灵力同时提高宠物伤害）
    // 圣物（森特欧斯的头骨 / 图腾）也提供智力，同样按 +2 灵力换算
    const relicInt = santosRelicIntBonus();
    // 装备/图鉴提供的智力同样按 +2 灵力换算
    mp += (attrInt + relicInt + intelligence) * 2;
    // 护甲提高%：只作用于装备/力量提供的护甲总量
    armor = Math.round(armor * (1 + armorPct / 100));
    /* 闪避提高%：和护甲提高%同一个口径 —— 先把所有来源（防具基底 + 词缀 + 图鉴 …）
     * 加起来，再整体乘一遍「闪避提高 X%」 */
    evasion = Math.round(evasion * (1 + evasionPct / 100));
    derived.evasion = evasion;
    derived.evasionPct = evasionPct;
    hp += gemSum(['life']);
    mp += gemSum(['energy']);
    armor += gemSum(['armorUp']);
    speedPct += gemSum(['moveSpeed']);
    // 灵宝「不坏金身」：生命上限额外 +1000
    if (treasureSpecial('hpFlat1000')) hp += 1000;
    /* 盾墙：占用 25% 生命上限（和灵力占用一个思路：上限按比例被"留着"） */
    const wallOn = shieldWall && knownSkills.some(function (s) { return s.id === 'shieldWall'; });
    if (wallOn) hp = Math.max(1, Math.round(hp * (1 - SHIELD_WALL_LIFE_PCT / 100)));
    derived.lifeReservePct = wallOn ? SHIELD_WALL_LIFE_PCT : 0;
    /* ---------- 独特装备（暗金）机制（要参与灵力量算，所以在 maxMp 之前先算）---------- */
    const umech = equippedUniqueMech();
    /* 「智行之手」：每 20 敏捷使灵力提高 1%（在汇总完所有灵力来源之后再乘） */
    const agiForMp = agility + attrAgi;
    derived.mpPer20AgiPct = Math.floor(agiForMp / 20) * (umech.mpPctPer20Agi || 0);
    if (derived.mpPer20AgiPct > 0) {
      mp = Math.round(mp * (1 + derived.mpPer20AgiPct / 100));
    }
    derived.maxHp = Math.max(1, hp);
    derived.maxMp = Math.max(1, mp);
    // 持续类 / 召唤类技能会占用（保留）一部分灵力上限：
    //   持续类（气血反弹等）= 35%，召唤类第一只 = 50%，每多一只 +20%（封顶 85%）
    let mpReserve = 0;
    if (bloodRebound && knownSkills.some(function (s) { return s.type === 'toggle'; })) mpReserve += 35;
    const alivePetCount = alivePets().length;
    if (alivePetCount > 0) mpReserve += 50 + (alivePetCount - 1) * 20;
    mpReserve = Math.min(85, mpReserve);
    derived.mpReservePct = mpReserve;
    derived.mpUsable = Math.max(1, Math.round(derived.maxMp * (1 - mpReserve / 100)));
    derived.mpReserved = Math.max(0, derived.maxMp - derived.mpUsable);

    /* ---------- 独特装备（暗金）机制 ---------- */
    const strTotal = strength + attrStr;
    // 漂泊者护甲：每 2 点力量 +1% 攻击伤害 / +1% 投射物伤害
    const atkDmgPct = Math.floor(strTotal / 2) * (umech.atkPctPer2Str || 0);
    const projDmgPct = Math.floor(strTotal / 2) * (umech.projPctPer2Str || 0);
    if (atkDmgPct) flatAtk = Math.round(flatAtk * (1 + atkDmgPct / 100));
    derived.unique = umech;
    derived.atkDmgPct = atkDmgPct;
    derived.projDmgPct = projDmgPct;
    derived.critImmune = !!umech.critImmune;          // 冠军胸甲：不吃暴击的额外伤害
    derived.stunImmune = !!umech.stunImmune;          // 锁链战靴：免疫晕眩
    derived.blockHealPct = umech.blockHealPct || 0;   // 瓦尔塔盾：格挡回血
    derived.thornsCritPct = umech.thornsCritPct || 0; // 荆皮护靴：荆棘暴击几率
    derived.armor = armor;
    derived.flatAtk = flatAtk;
    /* speedPct 是"移动速度加成合计"（装备 / 图鉴 / 灵根 / 敏捷 / 宝石都在里面）：
     *   以前只把它折算进 speedMult，没存回 derived.speedPct —— 面板读 speedMult 所以显示没问题，
     *   但调试钩子和"逐项核对灵根加成"的用例读 derived.speedPct 时永远是 0。这里补上口径。 */
    derived.speedPct = speedPct;
    derived.speedMult = Math.max(1, 1 + speedPct / 100);
    /* 全元素抗性：默认上限 75%，盾牌 / 衣服的「最大元素抗性上限 +X%」把上限往上抬。
     *   derived.res 给的是**生效值**（已按上限截断），原始值另存 resRaw 供面板显示。 */
    const resCap = RES_CAP_BASE + Math.min(30, resCapPct);
    derived.resRaw = res;
    derived.resCap = resCap;
    derived.res = Math.min(res, resCap);
    derived.thorns = thorns;
    /* 盾墙：持续状态期间常驻 +5% 格挡（技能没学 / 没开就不加） */
    if (shieldWall && knownSkills.some(function (s) { return s.id === 'shieldWall'; })) {
      blockPct += SHIELD_WALL_BLOCK_PCT;
    }
    // 格挡上限：默认 75%，盾牌的「格挡几率上限 +X%」可以抬上去
    /* 黑钢塔盾：每 5 点力量 +1% 格挡几率（力量＝装备力量 + 加点力量） */
    if (umech.blockPer5Str) {
      blockPct += Math.floor(strTotal / 5) * umech.blockPer5Str;
    }
    derived.blockCap = BLOCK_CAP_BASE + Math.min(30, blockCapPct);
    derived.blockPct = Math.min(derived.blockCap, blockPct);
    /* ---------- 防御上限统一（需求）----------
     *  · 常驻减伤合计 ≤ 90%（装备「总减伤上限 +X%」可抬，最高 99%）
     *  · 闪避几率 ≤ 75%、格挡几率 ≤ 75%（装备对应的「上限 +X%」可抬，最高 105%）
      * 闪避的来源：① 防具基底的闪避点数（换算成百分比，见 EVASION_RATING_CONST）
      *              ② 敏捷（加点 + 装备 / 图鉴）每 10 点 +1%
      *              ③ 装备 / 图鉴直接给的「闪避几率 +X%」
      */
    derived.reduceCapPct = Math.min(CAP_RAISE_MAX, Math.max(0, reduceCapPct));
    derived.reduceCap = Math.min(0.99, TOTAL_REDUCE_CAP + derived.reduceCapPct / 100);
    derived.dodgeFromAgi = Math.floor((agility + attrAgi) / 10);
    /* 闪避点数 → 闪避几率：和护甲同一种递减曲线，但闪避是"完全免伤"，
     * 常数给得更大（收益更慢）：闪避率 = 闪避点数 ÷（闪避点数 + 900） */
    derived.dodgeFromGear = Math.round((evasion / (evasion + EVASION_RATING_CONST)) * 1000) / 10;
    derived.dodgeCapPct = Math.min(CAP_RAISE_MAX, Math.max(0, dodgeCapPct));
    derived.dodgeCap = DODGE_CAP_BASE + derived.dodgeCapPct;
    derived.dodgeRaw = Math.max(0, dodgePct) + derived.dodgeFromAgi + derived.dodgeFromGear;
    derived.dodgePct = Math.min(derived.dodgeCap, derived.dodgeRaw);
    derived.strength = strength;
    derived.agility = agility;
    derived.intelligence = intelligence;
    derived.chaosRes = chaosRes;
    derived.critReduce = critReduce;
    derived.armorPct = armorPct;
    derived.lifeRegen = lifeRegen;
    derived.mpRegen = mpRegen;
    derived.extraArrowPct = Math.min(100, extraArrowPct);
    derived.atkSpeed = atkSpeed;
    derived.critChance = Math.min(100, critChance);
    derived.critDamage = critDamage;
    // 手套专属词缀
    derived.lifePerKill = lifePerKill;
    derived.mpPerKill = mpPerKill;
    derived.lifePerHit = lifePerHit;
    derived.poisonChance = Math.min(100, poisonChance);
    derived.bleedChance = Math.min(100, bleedChance);
    derived.igniteChance = Math.min(100, igniteChance);
    // 盾牌专属：额外减伤（和大减伤公式叠乘以外的独立一层）与吸纳
    derived.dmgReducePct = Math.min(60, dmgReducePct);
    // 物理伤害减免（盾牌 / 衣服）：单独一层，最多 60%
    derived.physReducePct = Math.min(60, physReducePct);
    derived.resCapPct = Math.min(30, resCapPct);
    derived.blockCapPct = Math.min(30, blockCapPct);
    derived.recoupPct = Math.min(50, recoupPct);
    derived.attrStr = attrStr;
    derived.attrAgi = attrAgi;
    derived.attrInt = attrInt;
    derived.relicInt = relicInt;
    derived.agilityTotal = agility + attrAgi;
    // 「100% 智力伤害」用的就是它：加点 + 圣物 + 装备/图鉴
    derived.intTotal = attrInt + relicInt + intelligence;
    // 圣物效果：头骨免疫中毒、图腾免疫控制
    derived.poisonImmune = hasSantosRelic('skull');
    derived.controlImmune = hasSantosRelic('totem');
    /* 免疫流血：目前还没有正式来源（留给门派武学 / 丹药 / 后续装备），先做成可读的开关，
     *   面板上就能显示"是 / 否"。 */
    derived.bleedImmune = !!bleedImmune;
    derived.strengthTotal = strength + attrStr;
    /* 灵力 = 法力槽（已合并）：
     *   · 上限 derived.maxMp（境界基准 ×2 + 装备/宝石/智力加成）
     *   · derived.spiritBase 只是"境界基准"那一项，供面板与调试核对公式
     *   · 练气期及以上才会"催动"（技能 +40%、护盾更强） */
    derived.spiritBase = spiritBaseOf(playerLevel());
    derived.spiritMax = derived.maxMp;
    derived.spiritCost = 0;                    // 技能消耗读技能自己的 cost；防御见 defendSpiritCostNow()
    derived.hasSpirit = true;                  // 所有人都有灵力（它就是法力）
    derived.spiritBoosted = spiritBoosted();
    // 智力不再提高法术伤害；法术伤害只来自技能石 / 词缀 / 灵根（天灵根会加）
    derived.spellDmgPct = spellDmgPctBonus;
    combat.maxHp = derived.maxHp;
    combat.maxMp = derived.maxMp;
    combat.hp = Math.min(combat.hp, combat.maxHp);
    combat.mp = Math.min(combat.mp, derived.mpUsable);
    refreshPetStats();
    if (ui.petPopup && !ui.petPopup.classList.contains('hidden')) renderPetPanel();
    updateBattleHud();
  }

  function isStaffEquipped() {
    return equipment.weapon && equipment.weapon.weaponType === '法杖';
  }

  // 法杖被动：装备后宠物自动出现，卸下法杖宠物消失
  // 召唤宠物素材：itch「free-desert-enemy-sprite-sheets-pixel-art」（每帧 48×48，横向排列）
  const PET_SPRITES = {
    scorpio: { name: '蝎子', idle: 4, walk: 4, attack: 4, hurt: 2 },
    hyena: { name: '鬣狗', idle: 4, walk: 6, attack: 6, hurt: 2 },
    snake: { name: '毒蛇', idle: 4, walk: 4, attack: 6, hurt: 2 },
    vulture: { name: '猎鹰', idle: 4, walk: 4, attack: 4, hurt: 2 }
  };
  /* 历史遗留的整体系数：召唤物伤害改走"固定基础伤害"之后，这里保持 1（不再额外缩放） */
  const SUMMON_DAMAGE_SCALE = 1;
  /* ---------- 召唤物基础数值（按需求统一）----------
   *   1 级：基础伤害 4~9、生命 75
   *   每提升 1 级：伤害 +15%、生命 +20%
   *   召唤物等级 = 玩家等级（召唤物跟着角色成长）；「召唤生物专精」再 +1 级。
   *   注意：旧的"按玩家攻击 35% / 灵力加成 / 整体 ×0.7"已经不再参与计算，
   *   这里的 4~9 就是**最终基础伤害**。 */
  const PET_BASE_DMG_MIN = 4;
  const PET_BASE_DMG_MAX = 9;
  const PET_BASE_HP = 75;
  const PET_DMG_PER_LEVEL = 0.15;
  const PET_HP_PER_LEVEL = 0.20;
  function summonLevelOf(p) {
    const extra = (p && p.skillId && hasMinionSupport(p.skillId, 'minionMastery')) ? 1 : 0;
    return Math.max(1, playerLevel() + extra);
  }
  function petDamageRangeOf(p) {
    const mult = 1 + PET_DMG_PER_LEVEL * (summonLevelOf(p) - 1);
    return [
      Math.max(1, Math.round(PET_BASE_DMG_MIN * mult)),
      Math.max(1, Math.round(PET_BASE_DMG_MAX * mult))
    ];
  }
  function petMaxHpOf(p) {
    const lv = summonLevelOf(p);
    const mods = minionGemMods(p);
    return Math.max(1, Math.round(
      PET_BASE_HP * (1 + PET_HP_PER_LEVEL * (lv - 1)) * (p.hpMult || 1) * mods.hpMult
    ));
  }
  const HYENA_DOUBLE_PCT = 0.35;      // 鬣狗：35% 几率再攻击一次
  const PET_SPRITE_KEYS = Object.keys(PET_SPRITES);
  const PET_FRAME_SIZE = 48;
  const PET_DRAW_SIZE = 66;   // 屏幕上的绘制尺寸
  const PET_SPRITE_FLIP = true; // 素材默认朝左，翻转让宠物面向玩家前进方向（朝右）

  function activatePet() {
    const maxHp = Math.max(1, Math.round(derived.maxHp * 0.35));
    const p = {
      hp: maxHp,
      maxHp: maxHp,
      name: '法杖灵宠',
      tier: 'common',
      level: 1,
      sprite: 'snake',
      source: '法杖被动（装备法杖自动出现）',
      sourceKind: 'staff',
      atkMult: 1,
      hpMult: 1,
      hurtT: 0,
      animT: 0,
      attackT: 0,
      walkT: rand(0, 10),
      seq: ++petSeq
    };
    addPet(p);
    return p;
  }

  function syncStaffPet() {
    if (isStaffEquipped()) {
      /* 「仆从（辅助）」在场时全局只能有一只召唤物：
       * 已经有召唤技能叫出来的宠物时，就把法杖灵宠收起来（否则会变成两只）。 */
      if (petCap() <= MINION_CAP_WITH_SERVANT) {
        const others = alivePets().filter(function (p) { return p.sourceKind !== 'staff'; });
        if (others.length >= MINION_CAP_WITH_SERVANT) {
          for (let i = pets.length - 1; i >= 0; i--) {
            if (pets[i].sourceKind === 'staff') pets.splice(i, 1);
          }
          return;
        }
      }
      if (!pets.some(function (p) { return p.sourceKind === 'staff' && p.hp > 0; })) activatePet();
    } else {
      // 只清理由法杖提供的灵宠；召唤技能 / 宠物蛋得到的宠物保留
      for (let i = pets.length - 1; i >= 0; i--) {
        if (pets[i].sourceKind === 'staff') pets.splice(i, 1);
      }
    }
  }

  // ---------- 森特欧斯的囚笼：头骨圣物附带的主动技能 ----------
  const WB_CAGE_SECONDS = 2;   // 「囚笼」控制时长（秒）
  const SANTOS_CAGE_SKILL = {
    id: 'santosCage', name: '森特欧斯的囚笼', type: 'control', cost: 8, level: 1,
    desc: '强行囚禁敌人 2 秒：期间敌人无法移动与攻击，并立刻造成 100% 智力（按当前智力结算）的伤害。'
  };

  // 带着头骨就会自动学会「囚笼」，丢掉/卖掉则撤回技能
  function syncSantosSkill() {
    const has = hasSantosRelic('skull');
    const idx = knownSkills.findIndex(function (s) { return s.id === 'santosCage'; });
    if (has && idx < 0) {
      knownSkills.push({
        id: SANTOS_CAGE_SKILL.id, name: SANTOS_CAGE_SKILL.name, desc: SANTOS_CAGE_SKILL.desc,
        cost: SANTOS_CAGE_SKILL.cost, type: SANTOS_CAGE_SKILL.type, level: 1, sockets: []
      });
      if (ui.spellPopup && !ui.spellPopup.classList.contains('hidden')) renderSpellList();
    } else if (!has && idx >= 0) {
      const sk = knownSkills[idx];
      (sk.sockets || []).forEach(function (g) {
        if (g) { g.socketedTo = null; inventory.push(g); }
      });
      knownSkills.splice(idx, 1);
      if (ui.spellPopup && !ui.spellPopup.classList.contains('hidden')) renderSpellList();
    }
  }

  // ---------- 宠物列表（多只共存，按等级从高到低排在玩家身后）----------
  function petLevelOf(tier) {
    return tier === 'rare' ? 3 : tier === 'magic' ? 2 : 1;
  }

  function sortPets() {
    pets.sort(function (a, b) {
      return (b.level || 1) - (a.level || 1) || (a.seq || 0) - (b.seq || 0);
    });
  }

  function addPet(p) {
    p.seq = p.seq || (++petSeq);
    p.level = p.level || petLevelOf(p.tier);
    pets.push(p);
    sortPets();
    return p;
  }

  function alivePets() {
    return pets.filter(function (p) { return p.hp > 0; });
  }

  // 出战宠物数量上限（避免无限堆叠）
  const PET_MAX = 4;

  function petAlive() {
    return alivePets().length > 0;
  }

  function leadPet() {
    return alivePets()[0] || null;
  }

  // 宠物排在玩家身后（玩家朝右，所以往后 = 屏幕左侧），按列表顺序依次向后
  function petScreenX(index) {
    return player.worldX - cameraX() - 76 - (index || 0) * 58;
  }

  // 换装备后宠物上限跟随玩家 35% 生命同步
  function refreshPetStats() {
    pets.forEach(function (p) {
      // 生命：1 级 75 点，每级 +20%（含召唤辅助：专精 +1 级 / 仆从「庞然大物」）
      const newMax = petMaxHpOf(p);
      const ratio = p.maxHp > 0 ? p.hp / p.maxHp : 1;
      p.maxHp = newMax;
      p.hp = Math.max(1, Math.min(newMax, Math.round(newMax * ratio)));
    });
  }

  function updateGold() {
    if (ui.goldValue) ui.goldValue.textContent = String(gold);
    // 金币是随时变动的（卖装备、世界BOSS奖励、搜尸体…），这里统一触发一次"防抖存档"，
    // 避免只改了内存数值、刷新后金币回退（之前线上就踩过这个坑）
    markSaveDirty();
  }

  // ---------- 玩家等级 / 经验 ----------
  // 升到下一级所需经验：Lv1→2 为 100，之后每级约 ×1.35 并取整到十位
  function stageInfo(stage) {
    return CULTIVATION_STAGES[clamp(stage, 1, MAX_STAGE) - 1];
  }

  function stageName(stage) {
    const s = stageInfo(stage);
    return s.sub ? s.realm + '·' + s.sub : s.realm;
  }

  // 每次小境界突破所需修为：首个阶段 100，之后每阶段 +25 并取整到十位
  function xpNeeded(stage) {
    if (stage >= MAX_STAGE) return Infinity;
    return Math.round((100 + 25 * (stage - 1)) / 10) * 10;
  }

  function hasSpiritItem() {
    return inventory.some(function (it) { return it.type === 'spiritItem'; });
  }

  function makeSpiritItem() {
    return {
      id: 'sp' + (++itemSeq),
      type: 'spiritItem',
      name: '天地灵物',
      icon: '灵',
      detail: '武者十境大圆满，服下可突破至炼气期',
      rarityColor: '#8fffd0'
    };
  }

  function updateLevelUI() {
    if (!ui.playerLv) return;
    ui.playerLv.textContent = stageName(player.level);
    const need = xpNeeded(player.level);
    if (player.bottleneck) {
      ui.xpFill.style.width = '100%';
      ui.xpText.textContent = '大圆满 · 需天地灵物';
    } else {
      const pct = need === Infinity ? 100 : clamp(player.xp / need, 0, 1) * 100;
      ui.xpFill.style.width = pct.toFixed(1) + '%';
      if (need === Infinity) ui.xpText.textContent = '已至道祖';
      else {
        const base = Math.floor(player.xp) + '/' + need;
        /* 天道·仙人：修为来自静修，HUD 上直接标出来（吸纳中还会显示每秒速率） */
        const tail = (faction === 'xian' && player.level > TRIBULATION_STAGE)
          ? (canAbsorbQi() ? '　灵脉吸纳 +' + XIU_PER_MIN + '/分' : '　天道·需在灵脉旁吸纳')
          : '';
        ui.xpText.textContent = base + tail;
      }
    }
    if (ui.btnBreak) {
      ui.btnBreak.classList.toggle('hidden', !(player.bottleneck && hasSpiritItem()));
    }
  }

  function gainXp(amount) {
    if (!amount || amount <= 0 || player.level >= MAX_STAGE) return;
    /* 天道 · 仙人：蜕凡入仙之后不再靠打怪涨修为（打怪给的修为一律不结算）。
     *   他们的修为来自"静修时吸纳天地灵力"，见 updateXiuLian() 里对 applyXp 的调用。 */
    if (faction === 'xian' && player.level > TRIBULATION_STAGE) {
      if (!xiuCombatHinted) {
        xiuCombatHinted = true;
        showToast('天道修士不靠杀伐精进：找个地方停下静修，天地灵力自会入体。');
        pushBattleLog('【天道】你以杀伐取修为——毫无寸进。静坐吐纳才是你的道。', 'system');
      }
      return;
    }
    applyXp(amount);
  }

  /* 真正结算修为（升级、给属性点、写战报）；静修吸纳也走这里。
   *   opts.quiet：静修是"每帧一点点"，用防抖落盘（markSaveDirty），别每帧写存档；
   *   打怪这种一次性结算仍然立刻 saveGame()，保证存档即时同步。 */
  function applyXp(amount, opts) {
    if (!amount || amount <= 0 || player.level >= MAX_STAGE) return;
    player.xp += amount;
    let advanced = false;
    let levelsGained = 0;
    while (player.level < MAX_STAGE && player.xp >= xpNeeded(player.level)) {
      // 武者十境大圆满：必须服下天地灵物才能蜕凡入炼气
      if (player.level === TRIBULATION_STAGE) {
        player.xp = xpNeeded(player.level);
        if (!player.bottleneck) {
          player.bottleneck = true;
          showToast('修为已至武者十境大圆满，需要「天地灵物」才能继续蜕变！');
        }
        break;
      }
      /* 【暂定的瓶颈逻辑】炼气·后期 → 筑基：修为满也不再自动破境，
       *   得站在自己的灵脉（仙人掌）旁"冲击瓶颈"（再点一次那棵仙人掌 / 按突破键），
       *   破境瞬间触发天地异象。逆仙那条线以后走雷劫，暂不套这套。 */
      /* 【瓶颈：炼气之后每一个小境界都要冲关】修为满了先卡住，不再自动进境。
       *   天道·仙人 → 站在自己的灵脉（仙人掌）旁再点它一下
       *   其他阵营   → 打赢一场（winBattle 里自动冲开） */
      // opts.force：冲关那一次直接放行（否则刚清掉 bottleneck 又会被这一关拦回来）
      if (player.level >= QI_STAGE_START && !(opts && opts.force)) {
        player.xp = xpNeeded(player.level);
        if (!player.bottleneck) {
          player.bottleneck = true;
          // 记下任务基线：只有瓶颈之后完成的才算数
          player.bottleneckBase = { kill: killsTotal, pvp: pvpKillTotal, cave: caveClearTotal };
          const needNow = bottleneckNeeds(player.level);
          showToast('修为已满，遇上瓶颈：做完三项破境任务即可冲关（详情看弹出面板）。');
          pushBattleLog('【瓶颈】' + stageName(player.level) + ' 修为已满：需完成 —— 击杀真实玩家 ' +
            needNow.pvpKill + ' 人 / 洞府探险 ' + needNow.cave + ' 次 / 击败野外敌人 ' +
            needNow.killMany + ' 名', 'reward');
          showBottleneckPanel();
        }
        break;
      }
      player.xp -= xpNeeded(player.level);
      player.level += 1;
      levelsGained += 1;
      advanced = true;
    }
    updateLevelUI();
    if (advanced) {
      // 每提升一个等级（境界）获得 ATTR_POINTS_PER_LEVEL 点可自由分配的属性点
      const gainedPoints = levelsGained * ATTR_POINTS_PER_LEVEL;
      player.attrPoints += gainedPoints;
      updateCharButton();
      if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
      const before = combat.maxHp;
      recalcPlayerStats();
      const gained = Math.max(0, combat.maxHp - before);
      combat.hp = Math.min(combat.maxHp, combat.hp + gained);
      // 突破后灵力（法力）回满
      refillSpirit();
      updateBattleHud();
      pushBattleLog(
        '境界突破：' + stageName(player.level) + '（生命 +' + gained + '，属性点 +' + gainedPoints + '）',
        'reward'
      );
      showToast(
        '境界突破：' + stageName(player.level) +
        '（生命 +' + gained + '，获得 ' + gainedPoints + ' 点属性点，可在「角色」窗口分配）'
      );
    }
    checkFirstQuestDone();   // 升到武者十境·大圆满就完成第一个任务
    if (opts && opts.quiet) markSaveDirty();
    else saveGame();
  }

  /* ============================================================
   * 阵营 · 武者大圆满的「命运分叉点」
   *   服下天地灵物之前必须择道（只有第一次）：
   *     仙途 = 献祭 BOSS 灵性，投名外来天道 → 天道赐法则祝福
   *     逆仙 = 接纳 BOSS 辟佑，承本土本源 → 此后再突破引神雷淬炼肉身
   *   当前版本只做这段剧情演绎：两条路的后续境界差异（雷劫伤害 / 法则加持 /
   *   身份隐匿）留到后续版本，先统一按原来的突破逻辑走。
   * ============================================================ */
  let faction = null;              // 'xian' | 'nixian' | null（尚未择道）
  let factionPendingItem = null;   // 弹窗打开时手上那件天地灵物
  let cgSeen = false;              // 开场 CG 是否看过（每个档案只播一次，可跳过）
  function factionDef(id) {
    return FACTIONS.filter(function (f) { return f.id === id; })[0] || null;
  }
  function factionName() {
    const def = factionDef(faction);
    return def ? def.name : '未择道';
  }

  // 服下天地灵物：没择过道就先弹「命运分叉点」，择完（或已择过）才真的突破
  function breakthrough() {
    if (!player.bottleneck) return;
    const item = inventory.filter(function (it) { return it.type === 'spiritItem'; })[0];
    if (!item) {
      showToast('需要「天地灵物」才能突破当前瓶颈。');
      return;
    }
    if (!faction) { openFactionPanel(item); return; }
    applyBreakthrough(item, null);
  }

  function applyBreakthrough(item, chosenFaction) {
    if (!item) return false;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    player.bottleneck = false;
    player.xp = 0;
    player.level = Math.min(MAX_STAGE, TRIBULATION_STAGE + 1);
    player.attrPoints += ATTR_POINTS_PER_LEVEL; // 蜕凡入仙同样视为提升一个境界
    if (chosenFaction) faction = chosenFaction;
    updateCharButton();
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    updateLevelUI();
    saveGame();
    const def = factionDef(faction);
    const tail = def ? ('　道统：' + def.name + '。') : '';
    showToast('服下「天地灵物」，蜕凡入仙：' + stageName(player.level) +
      '！（属性点 +' + ATTR_POINTS_PER_LEVEL + '）' + tail);
    // 蜕凡之后：给一次「随机灵根」的机会（第一次免费）
    if (!player.spiritRoot) openSpiritRootPanel(true);
    return true;
  }

  /* ============================================================
   * 灵根（灵根资质）
   *   凡人→炼气 的那一刻随机一条灵根（见表 SPIRIT_ROOTS）：
   *     · 第一次免费（突破时自动弹窗）
   *     · 不满意可以花 SPIRIT_ROOT_REROLL_COST 金币重新随机
   *     · mods 直接加到派生属性上（和图鉴增幅同一套字段）
   *   角色窗口「灵根资质」那行显示当前灵根，并提供重新随机按钮。
   * ============================================================ */
  let rootOffer = null;              // 正在让玩家挑选的那一条（还没接受）
  function spiritRootDef(id) {
    const key = id || (player.spiritRoot && player.spiritRoot.id);
    if (!key) return null;
    return SPIRIT_ROOTS.filter(function (r) { return r.id === key; })[0] || null;
  }
  function rollSpiritRoot() {
    const total = SPIRIT_ROOTS.reduce(function (s, r) { return s + (r.weight || 1); }, 0);
    let roll = Math.random() * total;
    for (let i = 0; i < SPIRIT_ROOTS.length; i++) {
      roll -= (SPIRIT_ROOTS[i].weight || 1);
      if (roll < 0) return SPIRIT_ROOTS[i];
    }
    return SPIRIT_ROOTS[SPIRIT_ROOTS.length - 1];
  }
  // 灵根给派生属性的加成（没有灵根就是空）
  function spiritRootBonus() {
    const def = spiritRootDef();
    return (def && def.mods) ? def.mods : {};
  }
  // 品阶 → 星级
  const ROOT_GRADE_STARS = { '极品': 5, '上品': 4, '良品': 3, '中品': 2, '下品': 1 };
  // '#ffd76a' → 'rgba(255,215,106,a)'（给 CSS 变量用，避免依赖 color-mix 的浏览器支持）
  function rgbaOf(hex, a) {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
    if (!m) return 'rgba(255, 215, 106, ' + a + ')';
    const v = parseInt(m[1], 16);
    return 'rgba(' + ((v >> 16) & 255) + ', ' + ((v >> 8) & 255) + ', ' + (v & 255) + ', ' + a + ')';
  }
  function renderSpiritRootPanel() {
    const cur = spiritRootDef();
    const cand = rootOffer;
    const col = (cand && cand.color) || '#ffd76a';
    // 玉盘：主题色 / 灵根首字 / 极品呼吸光
    if (ui.rootDisc) {
      ui.rootDisc.style.setProperty('--rootColor', col);
      ui.rootDisc.style.setProperty('--rootColorSoft', rgbaOf(col, 0.35));
      ui.rootDisc.classList.toggle('legend', !!(cand && cand.grade === '极品'));
    }
    if (ui.rootGlyph) ui.rootGlyph.textContent = cand ? String(cand.name).slice(0, 1) : '灵';
    if (ui.rootName) { ui.rootName.textContent = cand ? cand.name : '——'; ui.rootName.style.color = col; }
    if (ui.rootGrade) {
      ui.rootGrade.textContent = cand ? cand.grade : '';
      ui.rootGrade.style.color = col;
    }
    if (ui.rootStars) {
      const n = cand ? (ROOT_GRADE_STARS[cand.grade] || 1) : 0;
      ui.rootStars.textContent = n ? '★'.repeat(n) + '☆'.repeat(5 - n) : '';
    }
    if (ui.rootEffects) {
      ui.rootEffects.textContent = '';
      ((cand && cand.effects) || []).forEach(function (t) {
        const li = document.createElement('li');
        li.textContent = t;
        ui.rootEffects.appendChild(li);
      });
    }
    if (ui.rootDesc) ui.rootDesc.textContent = cand ? cand.desc : '';
    if (ui.rootCurrent) {
      ui.rootCurrent.textContent = cur
        ? ('当前灵根：' + cur.name + '（' + cur.grade + '）　' + cur.desc)
        : '当前还没有灵根';
    }
    if (ui.rootAccept) ui.rootAccept.disabled = !cand;
    if (ui.rootReroll) {
      const can = canRerollSpiritRoot();
      ui.rootReroll.disabled = !can.ok;
      ui.rootReroll.title = can.ok ? '消耗材料后随机一条新灵根' : can.reason;
      /* 第二行写代价；再往下给一行"够不够"的状态（缺什么一眼看出来） */
      if (ui.rootRerollCost) {
        ui.rootRerollCost.textContent = '天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC + ' + ' +
          SPIRIT_ROOT_REROLL_COST + ' 金币';
      }
      if (ui.rootCostHint) {
        ui.rootCostHint.textContent = can.ok
          ? ('条件已满足：天地灵物 ' + can.relics + ' 枚 · 金币 ' + gold)
          : ('暂不能重随：' + can.reason + '（现有 天地灵物 ' + can.relics + ' 枚 · 金币 ' + gold + '）');
        ui.rootCostHint.classList.toggle('ok', can.ok);
        ui.rootCostHint.classList.toggle('bad', !can.ok);
      }
    }
  }
  /* 重新随机灵根的代价：1 枚天地灵物 + 10000 金币 */
  function canRerollSpiritRoot() {
    const relics = inventory.filter(function (it) { return it.type === 'spiritItem'; }).length;
    if (relics < SPIRIT_ROOT_REROLL_RELIC) {
      return { ok: false, relics: relics, gold: gold, reason: '需要天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC + '（背包里没有）' };
    }
    if (gold < SPIRIT_ROOT_REROLL_COST) {
      return { ok: false, relics: relics, gold: gold, reason: '需要 ' + SPIRIT_ROOT_REROLL_COST + ' 金币（当前 ' + gold + '）' };
    }
    return { ok: true, relics: relics, gold: gold, reason: '' };
  }
  // 扣掉 1 枚天地灵物 + 10000 金币
  function paySpiritRootReroll() {
    for (let i = 0; i < SPIRIT_ROOT_REROLL_RELIC; i++) {
      const idx = inventory.map(function (it, k) { return it.type === 'spiritItem' ? k : -1; })
        .filter(function (k) { return k >= 0; })[0];
      if (idx >= 0) inventory.splice(idx, 1);
    }
    gold -= SPIRIT_ROOT_REROLL_COST;
    updateGold();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
  }

  /* 角色窗口里那个按钮只写「重测灵根」四个字，
   *   材料消耗说明（要花什么、够不够、现在有多少）全部放到这个确认窗里。 */
  function renderRootRerollPopup() {
    const can = canRerollSpiritRoot();
    if (ui.rootRerollPopupCost) {
      ui.rootRerollPopupCost.textContent = '天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC +
        '　+　' + SPIRIT_ROOT_REROLL_COST + ' 金币';
    }
    if (ui.rootRerollPopupOwn) {
      /* 条件不满足时**不在最下方写"为什么不能重测"**：
       *   上面那行消耗已经写清要花什么，点「确认重测」时也会弹提示说明原因，
       *   这里再挂一行红字只是噪音。满足条件才显示一行确认。 */
      const box = ui.rootRerollPopupOwn;
      if (can.ok) {
        box.textContent = '条件已满足　·　现有 天地灵物 ' + can.relics + ' 枚 · 金币 ' + can.gold;
        box.classList.remove('hidden');
      } else {
        box.textContent = '';
        box.classList.add('hidden');
      }
      box.classList.toggle('ok', can.ok);
      box.classList.remove('bad');
    }
    return can;
  }

  function openRootRerollPopup() {
    if (!ui.rootRerollPopup) return false;
    renderRootRerollPopup();
    ui.rootRerollPopup.classList.remove('hidden');
    return true;
  }

  function closeRootRerollPopup() {
    if (ui.rootRerollPopup) ui.rootRerollPopup.classList.add('hidden');
  }

  // 确认窗里点「确认重测」：材料不够就只提示、不扣钱也不弹测灵根面板
  function confirmRootReroll() {
    const can = renderRootRerollPopup();
    if (!can.ok) {
      showToast('重测灵根需要 天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC + ' + ' +
        SPIRIT_ROOT_REROLL_COST + ' 金币（' + can.reason + '）。');
      return false;
    }
    closeRootRerollPopup();
    return rerollSpiritRoot();
  }

  /* ============================================================
   * 测灵根 · 周围的"声音"（马里奥制造里玩家死亡后那种潮水般的弹幕）
   *   随机到极品灵根 → 一片惊叹；随机到伪灵根 → 一片奚落。
   *   位置 / 字号 / 旋转 / 延迟 / 飘动方向都随机，一波十几条挤上来。
   * ============================================================ */
  const ROOT_VOICES = {
    top: ['哇！天生神人', '注定飞升仙界！', '天生道种', '一步登天', '家族要翻身了！'],
    weak: ['伪灵根基本断了仙途', '镜花水月', '白瞎了测灵机会', '别妄想修仙了', '这种也来参加测灵',
      '哈哈，真是笑话！', '原来是个假货灵根，徒有其表。', '何苦上来献丑？',
      '真当自己有仙缘？', '伪灵根最是可笑，比无灵根更可悲']
  };
  const ROOT_VOICE_COUNT = 44;      // 一波多少条：要的就是"潮水拍上来"的密度
  const ROOT_VOICE_LIFE_MS = 5200;  // 这一波整体清场时间
  let rootVoiceTimer = null;

  // 灵根 → 该放哪一档声音：极品 = 惊叹；下品（伪灵根）= 奚落；其它不出声
  function rootVoiceKind(def) {
    if (!def) return null;
    if (def.grade === '极品') return 'top';
    if (def.grade === '下品') return 'weak';
    return null;
  }

  function spiritRootVoices(def) {
    const kind = rootVoiceKind(def);
    const host = ui.wrap || ui.hud;
    let box = document.getElementById('rootVoices');
    /* 这一档不出声（上品 / 良品 / 中品）：把上一波清掉，
     *   免得"上一次的惊叹"还挂在屏幕上误导玩家。 */
    if (!kind) {
      if (box) box.textContent = '';
      return 0;
    }
    if (!host) return 0;
    if (!box) {
      box = document.createElement('div');
      box.id = 'rootVoices';
      host.appendChild(box);
    }
    box.classList.toggle('weak', kind === 'weak');
    box.textContent = '';                       // 清掉上一波，免得叠在一起
    const pool = ROOT_VOICES[kind];
    for (let i = 0; i < ROOT_VOICE_COUNT; i++) {
      const el = document.createElement('span');
      el.className = 'rootVoice';
      // 错开取词：保证 5 句都能出现，再随机打散顺序
      el.textContent = pool[(i + Math.floor(Math.random() * pool.length)) % pool.length];
      /* 铺满整个画面（允许贴边甚至压出去一点）：不去对齐、不去避让，
       *   要的就是"从四面八方糊上来"的拥挤感。 */
      const left = -2 + Math.random() * 98;
      const top = 2 + Math.random() * 92;
      el.style.left = left.toFixed(2) + '%';
      el.style.top = top.toFixed(2) + '%';
      el.style.fontSize = (9.5 + Math.random() * 5.5).toFixed(1) + 'px';   // 更小 → 更密
      el.style.setProperty('--dx', ((Math.random() * 2 - 1) * 175).toFixed(0));
      /* 往上飘的距离按"头顶还剩多少空间"来定：
       *   靠近顶部的少飘一点，免得刚露面就飞出画面 —— 这样同一时刻屏幕上的人更多、更"糊"。 */
      const headroom = Math.max(46, top * 7.2);          // top 是百分比，舞台按 720 算
      el.style.setProperty('--dy', (-Math.min(300, headroom * (0.55 + Math.random() * 0.5))).toFixed(0));
      el.style.setProperty('--rot', ((Math.random() * 2 - 1) * 24).toFixed(1) + 'deg');
      el.style.animationDelay = (Math.random() * 1.05).toFixed(2) + 's';
      el.style.animationDuration = (1.9 + Math.random() * 1.4).toFixed(2) + 's';
      box.appendChild(el);
    }
    if (rootVoiceTimer) clearTimeout(rootVoiceTimer);
    rootVoiceTimer = setTimeout(function () {
      rootVoiceTimer = null;
      const b = document.getElementById('rootVoices');
      if (b) b.textContent = '';
    }, ROOT_VOICE_LIFE_MS);
    return ROOT_VOICE_COUNT;
  }

  /* 打开灵根面板：free = 这次随机不要钱（突破那一次 / 从没随过的人） */
  function openSpiritRootPanel(free) {
    const noCost = free === true || !player.spiritRoot;
    if (!noCost) {
      const can = canRerollSpiritRoot();
      if (!can.ok) {
        showToast('重新随机灵根需要 天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC + ' + ' +
          SPIRIT_ROOT_REROLL_COST + ' 金币（' + can.reason + '）。');
        return false;
      }
      paySpiritRootReroll();
    }
    rootOffer = rollSpiritRoot();
    renderSpiritRootPanel();
    spiritRootVoices(rootOffer);      // 周围的声音：极品一片惊叹 / 伪灵根一片奚落
    if (ui.rootPopup) ui.rootPopup.classList.remove('hidden');
    if (noCost) {
      pushBattleLog('【灵根】蜕凡之后，你测出了自己的灵根：' + rootOffer.name +
        '（' + rootOffer.grade + '）', 'reward');
    } else {
      pushBattleLog('【灵根】你又测了一次灵根（-天地灵物 ×' + SPIRIT_ROOT_REROLL_RELIC + '、-' +
        SPIRIT_ROOT_REROLL_COST + ' 金币）：' +
        rootOffer.name + '（' + rootOffer.grade + '）', 'reward');
    }
    return true;
  }
  function closeSpiritRootPanel() {
    if (ui.rootPopup) ui.rootPopup.classList.add('hidden');
  }
  // 接受当前这条（换掉旧的灵根）
  function acceptSpiritRoot() {
    if (!rootOffer) return false;
    const prev = player.spiritRoot;
    player.spiritRoot = {
      id: rootOffer.id, name: rootOffer.name, grade: rootOffer.grade,
      rerolls: (prev && prev.rerolls ? prev.rerolls : 0) + 1
    };
    const def = rootOffer;
    rootOffer = null;
    closeSpiritRootPanel();
    recalcPlayerStats();
    combat.hp = Math.min(combat.maxHp, Math.max(1, combat.hp));
    updateBattleHud();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    showToast('灵根已定：' + def.name + '（' + def.grade + '）—— ' + def.desc);
    pushBattleLog('【灵根】你选定「' + def.name + '」（' + def.grade + '）：' + def.desc, 'reward');
    saveGame();
    return true;
  }
  /* 角色窗口里的「重新随机」按钮：直接开面板（收 500 金币，随机一条给你挑） */
  function rerollSpiritRoot() { return openSpiritRootPanel(false); }

  function renderFactionPanel() {
    if (!ui.factionOptions) return;
    if (ui.factionIntro) ui.factionIntro.textContent = FACTION_INTRO;
    ui.factionOptions.textContent = '';
    FACTIONS.forEach(function (def) {
      const card = document.createElement('div');
      card.className = 'factionCard';
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.dataset.faction = def.id;

      const top = document.createElement('div');
      top.className = 'factionTop';
      const glyph = document.createElement('i');
      glyph.className = 'factionGlyph';
      glyph.textContent = def.glyph;
      glyph.style.color = def.color;
      const name = document.createElement('b');
      name.className = 'factionName';
      name.textContent = def.name;
      name.style.color = def.color;
      top.appendChild(glyph);
      top.appendChild(name);

      const title = document.createElement('div');
      title.className = 'factionTitle';
      title.textContent = '【' + def.title + '】';
      const desc = document.createElement('div');
      desc.className = 'factionDesc';
      desc.textContent = def.desc;
      const pick = document.createElement('div');
      pick.className = 'factionPick';
      pick.textContent = '点此择道';

      card.appendChild(top);
      card.appendChild(title);
      card.appendChild(desc);
      card.appendChild(pick);
      const choose = function (ev) { ev.preventDefault(); chooseFaction(def.id); };
      card.addEventListener('click', choose);
      card.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter' || ev.key === ' ') choose(ev);
      });
      ui.factionOptions.appendChild(card);
    });
  }

  function openFactionPanel(item) {
    if (!ui.factionPopup) return false;
    factionPendingItem = item || null;
    renderFactionPanel();
    ui.factionPopup.classList.remove('hidden');
    addBattleLogSafe('「天地灵物」在手中震颤 —— 你必须先决定：献祭给天道，还是接纳本土的辟佑。', 'reward');
    return true;
  }
  function closeFactionPanel() {
    if (ui.factionPopup) ui.factionPopup.classList.add('hidden');
  }

  /* 择道：记下阵营 → 立刻完成这次突破（灵物同时消耗掉） */
  function chooseFaction(id) {
    const def = factionDef(id);
    if (!def) return false;
    const item = factionPendingItem && inventory.some(function (it) { return it.id === factionPendingItem.id; })
      ? factionPendingItem
      : inventory.filter(function (it) { return it.type === 'spiritItem'; })[0];
    factionPendingItem = null;
    closeFactionPanel();
    if (!item) { showToast('灵物已经不在背包里了。'); return false; }
    applyBreakthrough(item, def.id);
    showToast('你选择了「' + def.name + '」：' + def.result);
    addBattleLogSafe('【命运分叉点】你选择了「' + def.name + '」——' + def.title, 'reward');
    saveGame();
    return true;
  }

  /* ============================================================
   * 任务：第一个任务「提升至武者大圆满」
   *   正式进入游戏时弹出一次；修为升到 武者十境·大圆满 即完成。
   * ============================================================ */
  function refreshQuestPanel() {
    if (ui.questNowStage) ui.questNowStage.textContent = stageName(player.level);
  }

  // 进入游戏时调用：新档案接任务弹窗；老档案只补一次完成判定
  function maybeStartFirstQuest() {
    if (quests.first.accepted) {
      checkFirstQuestDone();
      return;
    }
    // 已经超过这个境界的存档（老档 / 测试档）不再弹窗，直接记为已完成
    if (player.level >= QUEST_FIRST_STAGE) {
      quests.first.accepted = true;
      quests.first.done = true;
      markSaveDirty();
      return;
    }
    quests.first.accepted = true;
    refreshQuestPanel();
    if (ui.questPopup) ui.questPopup.classList.remove('hidden');
    pushBattleLog('接取任务：提升至武者大圆满（当前 ' + stageName(player.level) + '）', 'system');
    markSaveDirty();
  }

  function checkFirstQuestDone() {
    if (!quests.first.accepted || quests.first.done) return false;
    if (player.level < QUEST_FIRST_STAGE) return false;
    quests.first.done = true;
    showToast('★ 任务完成：提升至武者大圆满！接下来需要「天地灵物」才能蜕凡入仙。');
    pushBattleLog('★ 任务完成：提升至武者大圆满 · 下一步要拿到「天地灵物」（只在世界BOSS死亡时掉落，0.01%）', 'reward');
    markSaveDirty();
    return true;
  }

  function closeQuestPopup() {
    if (ui.questPopup) ui.questPopup.classList.add('hidden');
    saveGame();
  }

  // 血瓶充能进度：击败 10 个普通敌人 或 1 个精英/传奇敌人 → 充能 +1（上限 5 层）
  // ---------- 震撼大地：脚下荡开的冲击波（辅助「阳爪」用） ----------
  const shockWaves = [];

  function spawnShockWave(delay) {
    shockWaves.push({ t: -(delay || 0), life: 0.7, maxR: rand(150, 240) });
    if (shockWaves.length > 12) shockWaves.shift();
  }

  function updateShockWaves(dt) {
    for (let i = shockWaves.length - 1; i >= 0; i--) {
      shockWaves[i].t += dt;
      if (shockWaves[i].t >= shockWaves[i].life) shockWaves.splice(i, 1);
    }
  }

  function drawShockWavesGround() {
    if (!shockWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const gy = GROUND_Y + 6;
    shockWaves.forEach(function (w) {
      if (w.t < 0) return;
      const p = clamp(w.t / w.life, 0, 1);
      const r = w.maxR * (1 - Math.pow(1 - p, 2.4));
      const a = (1 - p) * (1 - p);
      if (a <= 0.01 || r <= 1) return;
      ctx.save();
      ctx.translate(cx, gy);
      ctx.scale(1, 0.36);
      const g = ctx.createRadialGradient(0, 0, r * 0.45, 0, 0, r);
      g.addColorStop(0, 'rgba(120, 90, 50, 0)');
      g.addColorStop(0.72, 'rgba(198, 152, 88, ' + (0.34 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 226, 164, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 228, 176, ' + (0.42 * a).toFixed(3) + ')';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.96, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
  }

  // ---------- 「阳爪」特效：2 秒内多个白色虚影分身突进前方敌人 ----------
  const YANGCLAW_FX_DUR = 2.0;          // 整套演出 2 秒内播完（分身放慢，残影看得清）
  const YANGCLAW_FX_MAX_CLONES = 5;
  let yangClawFx = null;
  let lastSkillCalc = null;             // 最近一次技能伤害计算（调试用：核对等级加成）

  function startYangClawFx() {
    if (!activeEnemy) return;
    const n = randInt(3, YANGCLAW_FX_MAX_CLONES);   // 随机 3~5 个分身
    const clones = [];
    for (let i = 0; i < n; i++) {
      clones.push({
        delay: (i / n) * 0.7,         // 前后错开出发，保证 2 秒内全部冲完
        dur: 1.15,                    // 单次突进拉长，白色残影才留得住
        offsetY: rand(-8, 8),
        scale: rand(0.9, 1.08),
        phase: rand(0, Math.PI * 2)
      });
    }
    // 终点不是第一个敌人，而是战场最右侧（穿透沿途所有敌人）
    const farRight = cameraX() + W + 160;
    yangClawFx = { t: 0, dur: YANGCLAW_FX_DUR, fromX: player.worldX, toX: farRight, clones: clones };
  }

  function updateYangClawFx(dt) {
    if (!yangClawFx) return;
    yangClawFx.t += dt;
    if (yangClawFx.t >= yangClawFx.dur) yangClawFx = null;
  }

  function drawYangClawFx() {
    if (!yangClawFx || !images.player_idle_1) return;
    const fx = yangClawFx;
    const fromX = fx.fromX - cameraX();
    const toX = fx.toX - cameraX();
    const scale = playerFx.scale * caveScale();

    fx.clones.forEach(function (c) {
      const local = fx.t - c.delay;
      if (local < 0 || local > c.dur) return;
      const p = clamp(local / c.dur, 0, 1);
      const ease = 1 - Math.pow(1 - p, 3);          // 突进：起步快、末尾收住
      const x = fromX + (toX - fromX) * ease;
      const alpha = (p < 0.18 ? p / 0.18 : 1 - (p - 0.18) / 0.82) * 0.62;
      if (alpha <= 0.02) return;

      ctx.save();
      // 冲刺拖影（白色横条）
      const trailW = Math.max(0, x - (fromX + (toX - fromX) * Math.max(0, ease - 0.22)));
      if (trailW > 4) {
        ctx.globalAlpha = alpha * 0.5;
        const tg = ctx.createLinearGradient(x - trailW, 0, x, 0);
        tg.addColorStop(0, 'rgba(255,255,255,0)');
        tg.addColorStop(1, 'rgba(255,255,255,0.75)');
        ctx.fillStyle = tg;
        ctx.fillRect(x - trailW, GROUND_Y - 118 + c.offsetY, trailW, 10);
      }
      // 白色虚幻分身（同一套玩家贴图，去色 + 提亮）
      ctx.globalAlpha = alpha;
      try { ctx.filter = 'saturate(0) brightness(2.6)'; } catch (e) { /* 浏览器不支持就算了 */ }
      ctx.imageSmoothingEnabled = false;
      drawPlayerFrame(images.player_idle_1, x, GROUND_Y + c.offsetY, scale * c.scale, PLAYER_SPRITE_FLIP, 0);
      ctx.restore();
    });
  }

  // ---------- 流血：敌方回合开始前结算一次 ----------
  /* ---------- 炎军（辅助）：燃烧层数 ---------- */
  const BURN_CHANCE = 0.5;        // 每次造成伤害 50% 几率叠 1 层
  const BURN_STACK_MAX = 20;      // 层数上限
  const BURN_PCT = 0.01;          // 每层每回合 = 玩家最大生命 1% 的真实伤害

  // 造成伤害时调用：命中就给敌人叠 1 层燃烧（「炎军」镶嵌在被辅助技能上才生效）
  function maybeApplyBurn(enemy, skillId) {
    if (!enemy || enemy.hp <= 0) return false;
    if (!skillId || !skillHasGem(skillId, 'flameLegion')) return false;
    if (Math.random() >= BURN_CHANCE) return false;
    const before = enemy.burnStacks || 0;
    enemy.burnStacks = Math.min(BURN_STACK_MAX, before + 1);
    if (enemy.burnStacks === before) return false;    // 已经满层
    addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 196, '燃烧 +1（' + enemy.burnStacks + ' 层）', '#ff9a3c');
    return true;
  }

  // 燃烧结算：每个敌人回合，每层造成玩家最大生命 1% 的真实伤害（无视护甲/抗性）
  function applyBurnTick() {
    const burning = battleAlive().filter(function (e) { return (e.burnStacks || 0) > 0; });
    if (!burning.length) return false;
    const per = Math.max(1, Math.round(combat.maxHp * BURN_PCT));
    const parts = [];
    burning.forEach(function (e) {
      const dmg = per * e.burnStacks;
      e.hp = Math.max(0, e.hp - dmg);
      e.hurtT = HURT_TIME;
      addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 84, dmg, '#ff7a2f');
      parts.push(enemyLabel(e, false) + ' −' + dmg + '（燃烧 ' + e.burnStacks + ' 层）');
    });
    pushBattleLog('燃烧结算（每层 ' + per + ' 点真实伤害）：' + parts.join('，'), 'player');
    setBattleLog('燃烧结算：' + parts.join('，'));
    updateBattleHud();
    return true;
  }

  /* ============================================================
   * 手套专属词缀的战斗效果
   *   ① 攻击每击中一名敌人获得 X 生命（命中即时回血）
   *   ② 攻击时有 X% 几率造成 中毒 / 流血 / 点燃
   *      · 中毒：3 回合持续掉血（走已有的 poison 结算）
   *      · 流血：3 回合持续掉血（走已有的 bleed 结算）
   *      · 点燃：叠 1 层燃烧（走已有的 burnStacks 结算）
   *   每次出手对每个被击中的敌人各判定一次。
   * ============================================================ */
  const GLOVE_DOT_TURNS = 3;

  /* ============================================================
   * 盾牌词缀：吸纳（Recoup）
   *   把"所受伤害的 X%"先记在池子里，接下来 4 秒内逐跳补回生命。
   *   参考流放之路的 Recoup：不是立刻回，而是分摊到几秒里。
   * ============================================================ */
  const RECOUP_SECONDS = 4;
  let recoupPool = 0;       // 还没补完的吸纳量
  let recoupT = 0;
  /* ---------- 玩家承伤公式（唯一入口）----------
   *   ① 护甲 / 元素抗性 / 混沌抗性 这一层：
   *        护甲部分 = armor / (armor + 120)（递减曲线）
   *          100  护甲 → 45.45%
   *          1000 护甲 → 89.29%
   *          1080 护甲 → 90%（再往上都被这一层的 90% 上限吃掉）
   *        整层 = 护甲部分 + 元素抗性/250 + 混沌抗性/300，封顶 = 总减伤上限（默认 90%）
   *   ② 盾牌词缀「受到的伤害降低 X%」（最多 60%）
   *   ③ 盾牌 / 衣服词缀「物理伤害减免 X%」（最多 60%）——**额外伤害减免**：
   *      它不参与 ① 的 90% 上限，而是在「护甲减伤之后的伤害」上再算一次，
   *      即 final = 原始 × (1 − 护甲/抗性/盾牌栈，封顶 90%) × (1 − 物理减免)。
   *   ④ 暴击倍数（冠军胸甲免疫暴击额外伤害）与上面相乘。
   *   注：「防御」姿态的临时减伤、格挡 / 闪避属于回合动作或几率免伤，不并入这 90% 里。
   */
  function playerReduceCap() {
    return Math.min(0.99, TOTAL_REDUCE_CAP + Math.max(0, derived.reduceCapPct || 0) / 100);
  }
  function armorResReduction(armor, res, chaosRes) {
    const a = Math.max(0, armor || 0);
    /* 护甲部分：先走正常递减曲线；越过 50% 这个拐点后，
     * 剩余的成长只按 ARMOR_POST_KNEE_MULT 的比例继续加（收益递减，渐近 62.5%） */
    const raw = a / (a + 120);
    const armorPart = raw <= ARMOR_SOFT_CAP
      ? raw
      : ARMOR_SOFT_CAP + (raw - ARMOR_SOFT_CAP) * ARMOR_POST_KNEE_MULT;
    return clamp(
      armorPart + (res || 0) / 250 + (chaosRes || 0) / 300,
      0,
      playerReduceCap()
    );
  }
  /* 敌人一次攻击打到玩家身上的减伤系数（护甲 / 元素抗性 / 混沌抗性） */
  function playerDamageReduction() {
    return armorResReduction(derived.armor, derived.res, derived.chaosRes);
  }
  /* 常驻减伤三层叠乘后的「承伤系数」（合计减伤封顶 90%） */
  function playerPassiveDamageMult() {
    const reduction = playerDamageReduction();
    const gearReduce = Math.min(0.6, (derived.dmgReducePct || 0) / 100);
    /* 物理伤害减免（盾牌 / 衣服的词缀）是「额外伤害减免」：
     *   在护甲/抗性减伤之后，对已经削弱过的伤害再乘一次 —— 不计入 90% 那条上限，
     *   所以带这条词缀的装备是实打实的额外收益。
     *   目前敌人的攻击在数值上都是"物理"（还没做元素系敌人），所以这一层对所有来袭攻击生效；
     *   等以后给敌人分了伤害类型，把这里按类型判断一下就行。 */
    const physReduce = Math.min(0.6, (derived.physReducePct || 0) / 100);
    const cap = playerReduceCap();
    // ①+② 主减伤栈：合计封顶 90%（装备「总减伤上限 +X%」可抬）
    const mainMult = Math.max(1 - cap, (1 - reduction) * (1 - gearReduce));
    return {
      reduction: reduction, gearReduce: gearReduce, physReduce: physReduce, cap: cap,
      mainMult: mainMult,
      mainReducePct: Math.round((1 - mainMult) * 1000) / 10,
      // ③ 额外层：在主减伤之后单独再减一次
      mult: mainMult * (1 - physReduce)
    };
  }
  /* 最终伤害 = 原始 × 常驻减伤（封顶 90%） × 暴击 */
  function playerDamageAfterReduction(raw, critMult) {
    const p = playerPassiveDamageMult();
    /* 冠军胸甲：不会受到暴击所造成的额外暴击伤害（暴击倍数按 1 处理） */
    const cm = derived.critImmune ? 1 : (critMult || 1);
    return {
      reduction: p.reduction, gearReduce: p.gearReduce, physReduce: p.physReduce,
      passiveMult: p.mult, cap: p.cap,
      mainReducePct: p.mainReducePct,
      totalReducePct: Math.round((1 - p.mult) * 1000) / 10,
      dmg: Math.max(1, Math.round((Number(raw) || 0) * p.mult * cm))
    };
  }
  /* 闪避判定：命中就完全不掉血（默认上限 75%，装备可抬） */
  function rollPlayerDodge() {
    const d = derived.dodgePct || 0;
    return d > 0 && Math.random() * 100 < d;
  }
  function addRecoup(amount) {
    if (!amount || amount <= 0) return 0;
    recoupPool += amount;
    if (recoupPool > 1e7) recoupPool = 1e7;
    return Math.round(recoupPool);
  }
  function updateRecoup(dt) {
    if (recoupPool <= 0) { recoupT = 0; return; }
    // 4 秒内把池子里的量均匀补掉
    const perSec = Math.max(recoupPool, 1) / RECOUP_SECONDS;
    const heal = Math.min(recoupPool, perSec * dt);
    recoupPool -= heal;
    recoupT += dt;
    if (recoupPool < 0.5) recoupPool = 0;
    if (combat.hp > 0 && combat.hp < combat.maxHp) {
      const before = combat.hp;
      combat.hp = Math.min(combat.maxHp, combat.hp + heal);
      const gained = combat.hp - before;
      if (gained >= 1) {
        addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 200, gained, '#ffe07a', true);
        updateBattleHud();
      }
    }
    if (recoupPool <= 0) recoupT = 0;
  }

  /* ============================================================
   * 「盾墙 · 碎石崩裂」
   *   盾墙激活时，每次成功格挡都会从身前射出碎石，对前方敌人造成
   *     「当前格挡几率% × 攻击力」的攻击伤害 + 25% 生命上限的真实伤害
   *   （例：50% 格挡 → 50% 攻击力的伤害 + 25% 生命的真实伤害）
   *
   *   碎石**带「投射物」标签**（见 defs.js 里盾墙的 tags: ['projectile']）：
   *     · 多重投射物：碎石从 5 块变 7 块，并且独立结算次数 1 → 3（每次伤害总降 25%）
   *     · 投射物加速：碎石飞得更快（startRockFx 里读 projectileSpeedMult）
   * ============================================================ */
  function shieldWallProc() {
    if (!shieldWall || !activeEnemy) return 0;
    if (!knownSkills.some(function (s) { return s.id === 'shieldWall'; })) return 0;
    const id = 'shieldWall';
    const blockPct = Math.max(0, derived.blockPct || 0);
    const atkPower = 28.5 + (derived.flatAtk || 0);                 // 普攻区间中值
    const atkPart = Math.max(1, Math.round(atkPower * blockPct / 100));
    const lifePart = Math.max(1, Math.round((derived.maxHp || 100) * SHIELD_WALL_PROC_LIFE_PCT / 100));
    const perHit = atkPart + lifePart;                               // 真实伤害：不吃护甲/抗性
    const hits = projectileHitsOf(id, 1);                            // 多重投射物 → 3 次独立结算
    const shards = projectileCountOf(id, ROCK_FX_COUNT);             // 碎石块数：5 → 7
    const targets = battleAlive().slice(0, 3);                       // 前方敌人（最多 3 个）
    startRockFx();                                                   // 碎石特效（块数/速度已按辅助调整）
    let total = 0;
    const parts = [];
    targets.forEach(function (e) {
      for (let h = 0; h < hits; h++) {
        if (e.hp <= 0) break;
        // 每一次碎石波单独结算、单独显示（多重投射物的 -25% 也在这里生效）
        const dmg = Math.max(1, Math.round(perHit * supportDamageMult(id, e)));
        e.hp = Math.max(0, e.hp - dmg);
        e.hurtT = HURT_TIME;
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 190 - h * 22, dmg, '#ffcf6a');
        total += dmg;
        parts.push(enemyLabel(e, false) + ' −' + dmg + (hits > 1 ? '(第' + (h + 1) + '波)' : ''));
      }
    });
    const supNote = skillHasGem(id, 'multipleProjectiles')
      ? '（多重投射物：碎石 ' + shards + ' 块、独立结算 ' + hits + ' 次，每次伤害总降 25%）'
      : '';
    setBattleLog('格挡触发「碎石崩裂」：' + parts.join('，') +
      '，每次 ' + perHit + ' 点真实伤害（' + blockPct + '% 格挡 × 攻击力 ' + atkPart +
      ' + 25% 生命 ' + lifePart + '）' + supNote);
    pushBattleLog('盾墙·碎石崩裂：' + parts.join('，') + supNote, 'player');
    updateBattleHud();
    return total;
  }

  /* 碎石崩裂把当前敌人打没了 → 走和荆棘反伤一样的收尾（避免卡在敌人回合） */
  function shieldWallDealtKill() {
    if (!activeEnemy || activeEnemy.hp > 0) return false;
    activeEnemy.hp = 0;
    setBattleLog('碎石崩裂把「' + enemyLabel(activeEnemy, false) + '」砸倒了！');
    updateBattleHud();
    if (!resolveEnemyDown()) return true;
    pendingVictory = 0.5;
    battleTurn = 'player';
    resetPlayerIdleTimer();
    setControls('battle');
    return true;
  }
  /* ---------- 独特「武者的刑具」：附加火焰伤害 + 熔岩骤雨 ---------- */
  const LAVA_RAIN_DUR = 1.0;        // 火雨持续 1 秒
  const LAVA_RAIN_BALLS = 5;        // 1 秒内 5 颗火球
  const LAVA_RAIN_PCT = 0.35;       // 每颗火球 = 玩家力量的 35%（火属性伤害）
  /* 熔岩骤雨的触发几率：
   *   null  = 正常口径（每 25 点力量 5%，力量不足 25 就不触发）
   *   填数字（如 100）= 临时测试用：每次击中按该百分比触发（100 = 必触发） */
  const LAVA_RAIN_OVERRIDE_PCT = null;
  let lavaRainFx = null;            // 纯表现层：伤害在触发那一刻一次性结算

  function fireArrowFramesReady() {
    for (let i = 1; i <= FIRE_ARROW_FRAMES; i++) {
      const im = images['fireArrow_' + i];
      if (!im || !im.complete || !im.naturalWidth) return false;
    }
    return true;
  }

  /* 火雨落点：从玩家头顶出发、沿 45° 斜线向右下坠落。
   *   45° 意味着"水平位移 = 竖直落差"，所以只要把起点定在敌人斜上方，
   *   箭矢就一定会落在敌人身上（起点 x 贴着玩家头顶，y 抬高到足够的高度）。 */
  function startLavaRainFx(target) {
    const headX = player.worldX - cameraX() + 26;      // 玩家头顶
    const headTop = GROUND_Y - 168;                    // 头顶上方（离地约 168px）
    const landX = (target.x - cameraX()) + 84;         // 落点：敌人身上
    const balls = [];
    for (let i = 0; i < LAVA_RAIN_BALLS; i++) {
      const sx = headX + rand(-44, 44);
      const want = landX + rand(-36, 72);              // 每颗火球落点略微散开
      // 45°：起点要比落点高"同样的水平距离"
      let sy = GROUND_Y - Math.max(80, want - sx);
      sy = Math.min(sy, headTop + rand(-30, 26));      // 至少从头顶上方开始
      balls.push({
        sx: sx,
        sy: sy,
        drop: Math.max(60, GROUND_Y - sy),             // 竖直落差 = 水平位移
        delay: i * (LAVA_RAIN_DUR / LAVA_RAIN_BALLS),
        seed: rand(0, 1)
      });
    }
    lavaRainFx = { t: 0, dur: LAVA_RAIN_DUR + 0.30, balls: balls, headX: headX, headTop: headTop };
  }

  function updateLavaRainFx(dt) {
    const f = lavaRainFx;
    if (!f) return;
    f.t += dt;
    if (f.t > f.dur) lavaRainFx = null;
  }

  function drawLavaRainFx() {
    const f = lavaRainFx;
    if (!f) return;
    const k = FIRE_ARROW_SCALE * caveScale();
    const ready = fireArrowFramesReady();
    ctx.save();
    f.balls.forEach(function (b, i) {
      const local = f.t - b.delay;
      if (local < 0) return;
      const travel = FIRE_ARROW_SPEED * local;         // 45°：x / y 同一增量
      if (travel > b.drop) return;                     // 已经落地（伤害在触发时已结算）
      const x = b.sx + travel;
      const y = b.sy + travel;
      const p = clamp(travel / b.drop, 0, 1);
      if (!ready) {
        // 素材没加载出来时的兜底：一条 45° 橙色火线
        ctx.strokeStyle = 'rgba(255,168,70,.9)';
        ctx.lineWidth = 6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(x - 46, y - 46);
        ctx.lineTo(x, y);
        ctx.stroke();
        return;
      }
      const idx = Math.min(FIRE_ARROW_FRAMES - 1, Math.floor(p * FIRE_ARROW_FRAMES));
      const img = images['fireArrow_' + (idx + 1)];
      ctx.save();
      ctx.globalAlpha = playerFx.alpha;
      ctx.imageSmoothingEnabled = true;
      ctx.translate(x, y);
      ctx.rotate(FIRE_ARROW_DEG);                      // 素材朝右 → 转 45° 就是向右下坠落
      ctx.drawImage(img, -FIRE_ARROW_CX * k, -FIRE_ARROW_CY * k,
        FIRE_ARROW_W * k, FIRE_ARROW_H * k);
      ctx.restore();
      if (fxTrace.length < 600) {
        fxTrace.push({
          kind: 'lavaArrow', t: Math.round(local * 1000), idx: i, frame: idx + 1,
          x: Math.round(x), y: Math.round(y), sx: Math.round(b.sx), sy: Math.round(b.sy),
          angle: 45, drop: Math.round(b.drop), headX: Math.round(f.headX), headTop: Math.round(f.headTop)
        });
      }
    });
    ctx.restore();
  }

  /* 命中时的独特装备判定：
   *   · 武者的刑具：每 25 点力量附加 X 火焰伤害；再按「每 25 点力量 5%」判定熔岩骤雨
   *   · **每一次击中都独立判定**（阳爪 6 连击 = 6 次判定，震撼大地的每次冲击波同样各判定一次）
   *   · 敌人已经被打倒时照样结算并把伤害列进战报（击杀那一下的溢出伤害也要看得见）
   *   · tag：给多段技能标上"第 N 击"，战报里就能分清是哪一下触发的
   *   返回本次由独特装备造成的伤害合计（含溢出）。 */
  function uniqueOnHit(enemy, tag) {
    if (!enemy) return 0;
    const mech = derived.unique || {};
    if (!mech.firePer25 && !mech.lavaRain && !mech.lightningPer20Int) return 0;
    const strTotal = derived.strengthTotal || 0;
    const stacks = Math.floor(strTotal / 25);
    const mark = tag ? '·' + tag : '';
    let dealt = 0;
    /* 「智行之手」：每 20 点智慧附加 1~10 闪电伤害（每 20 点独立掷一次，每次击中都算） */
    if (mech.lightningPer20Int) {
      const intTotal = derived.intTotal || 0;
      const intStacks = Math.floor(intTotal / 20);
      if (intStacks > 0) {
        let bolt = 0;
        for (let i = 0; i < intStacks; i++) {
          bolt += randInt(mech.lightningPer20Int.min || 1, mech.lightningPer20Int.max || 1);
        }
        enemy.hp = Math.max(0, enemy.hp - bolt);
        enemy.hurtT = HURT_TIME;
        addDamagePopup(enemy.x - cameraX() + 88, GROUND_Y - 224, bolt, '#8fd8ff');
        pushBattleLog('【智行之手' + mark + '】附加闪电伤害 ' + bolt + ' 点（智慧 ' + intTotal +
          ' → ' + intStacks + ' × 1~10）', 'player');
        dealt += bolt;
      }
    }
    // 附加火焰伤害（每 25 点力量 5~10）：每次击中都算
    if (mech.firePer25 && stacks > 0) {
      const fire = stacks * mech.firePer25;
      enemy.hp = Math.max(0, enemy.hp - fire);
      enemy.hurtT = HURT_TIME;
      addDamagePopup(enemy.x - cameraX() + 88, GROUND_Y - 238, fire, '#ff8a3c');
      pushBattleLog('【武者的刑具' + mark + '】附加火焰伤害 ' + fire + ' 点（力量 ' + strTotal +
        ' → ' + stacks + ' × ' + mech.firePer25 + '）', 'player');
      dealt += fire;
    }
    if (mech.lavaRain && stacks > 0) {
      // 正常口径：每有 25 点力量 5%；测试期被 LAVA_RAIN_OVERRIDE_PCT 顶成 100%
      const pct = (LAVA_RAIN_OVERRIDE_PCT === null)
        ? stacks * (mech.lavaRain.pct || 0)
        : LAVA_RAIN_OVERRIDE_PCT;
      // 调试/测试：window.__lavaRainForce 打开时必定触发
      if (pct > 0 && (window.__lavaRainForce || Math.random() * 100 < pct)) {
        const perBall = Math.max(1, Math.round(strTotal * LAVA_RAIN_PCT));
        const total = perBall * LAVA_RAIN_BALLS;
        enemy.hp = Math.max(0, enemy.hp - total);
        enemy.hurtT = HURT_TIME;
        for (let i = 0; i < LAVA_RAIN_BALLS; i++) {
          addDamagePopup(enemy.x - cameraX() + 88 + rand(-16, 16), GROUND_Y - 252 - i * 12,
            perBall, '#ff7a2a', false, i * (LAVA_RAIN_DUR / LAVA_RAIN_BALLS));
        }
        startLavaRainFx(enemy);
        /* 战报标记：把熔岩骤雨造成的伤害单独标出来（火球数 × 单颗 = 合计） */
        pushBattleLog('★【熔岩骤雨' + mark + '】' + LAVA_RAIN_BALLS + ' 颗火球 × ' + perBall +
          ' = ' + total + ' 点火焰伤害（力量 ' + strTotal + ' 的 35%）' +
          (LAVA_RAIN_OVERRIDE_PCT === null ? '' : '　【测试期：触发几率临时 100%】'), 'player');
        dealt += total;
      }
    }
    if (dealt) updateBattleHud();
    return dealt;
  }

  function gloveOnHit(enemy, skipUnique, tag) {
    if (!enemy) return false;
    /* 独特装备（武者的刑具）：放在"敌人是否已倒下"的判断之前 ——
     * 否则致命一击（尤其阳爪 6 连击直接打死）会把"击中时"的火伤/火雨整个跳掉。
     * 多段技能自己按"每一下"调用 uniqueOnHit 时，用 skipUnique 跳过这一步（避免重复判定）。 */
    if (!skipUnique) uniqueOnHit(enemy, tag);
    if (enemy.hp <= 0) { updateBattleHud(); return true; }
    let did = false;
    if (derived.lifePerHit > 0 && combat.hp < combat.maxHp) {
      const heal = Math.max(1, Math.round(derived.lifePerHit));
      combat.hp = Math.min(combat.maxHp, combat.hp + heal);
      addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 130, heal, '#6dff8d', true);
      did = true;
    }
    const atkMid = 28.5 + derived.flatAtk;
    if (derived.poisonChance > 0 && Math.random() * 100 < derived.poisonChance) {
      enemy.poison = { dmg: Math.max(1, Math.round(atkMid * 0.10)), turns: GLOVE_DOT_TURNS };
      addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 214, '中毒！', '#b6e05a');
      pushBattleLog('手套词缀：命中让「' + enemyLabel(enemy, false) + '」中毒（' + GLOVE_DOT_TURNS + ' 回合）', 'player');
      did = true;
    }
    if (derived.bleedChance > 0 && Math.random() * 100 < derived.bleedChance) {
      enemy.bleed = { dmg: Math.max(1, Math.round(atkMid * 0.12)), turns: GLOVE_DOT_TURNS };
      addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 232, '流血！', '#ff6a5a');
      pushBattleLog('手套词缀：命中让「' + enemyLabel(enemy, false) + '」流血（' + GLOVE_DOT_TURNS + ' 回合）', 'player');
      did = true;
    }
    if (derived.igniteChance > 0 && Math.random() * 100 < derived.igniteChance) {
      const before = enemy.burnStacks || 0;
      enemy.burnStacks = Math.min(BURN_STACK_MAX, before + 1);
      if (enemy.burnStacks > before) {
        addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 250,
          '点燃 +1（' + enemy.burnStacks + ' 层）', '#ff9a3c');
        pushBattleLog('手套词缀：命中点燃了「' + enemyLabel(enemy, false) +
          '」（燃烧 ' + enemy.burnStacks + ' 层）', 'player');
        did = true;
      }
    }
    if (did) updateBattleHud();
    return did;
  }

  // 击败敌人时的手套词缀：每击败一名敌人获得 X 生命 / X 灵力
  function gloveOnKill() {
    const life = Math.max(0, Math.round(derived.lifePerKill || 0));
    const mana = Math.max(0, Math.round(derived.mpPerKill || 0));
    if (!life && !mana) return false;
    if (life) combat.hp = Math.min(combat.maxHp, combat.hp + life);
    if (mana) combat.mp = Math.min(combat.maxMp, combat.mp + mana);
    if (life) addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 158, life, '#6dff8d', true);
    if (mana) addTextPopup(player.worldX - cameraX() - 82, GROUND_Y - 178, '灵力 +' + mana, '#9fd4ff');
    pushBattleLog('手套词缀：击败敌人回复 ' + life + ' 生命 / ' + mana + ' 灵力', 'reward');
    updateBattleHud();
    return true;
  }

  function applyBleedTick() {
    let any = false;
    const list = battleAlive().filter(function (e) { return e.bleed && e.bleed.turns > 0; });
    if (list.length) {
      const parts = [];
      list.forEach(function (e) {
        const dmg = Math.max(1, Math.round(e.bleed.dmg));
        e.hp = Math.max(0, e.hp - dmg);
        e.hurtT = HURT_TIME;
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 126, dmg, '#ff2b2b');
        e.bleed.turns -= 1;
        if (e.bleed.turns <= 0) e.bleed = null;
        parts.push(enemyLabel(e, false) + ' −' + dmg);
      });
      pushBattleLog('流血结算（每回合持续掉血）：' + parts.join('，'), 'enemy');
      setBattleLog('流血结算：' + parts.join('，'));
      updateBattleHud();
      any = true;
    }
    // 中毒结算（「有毒的孢子」造成，圣物「森特欧斯的头骨」免疫）
    const poisoned = battleAlive().filter(function (e) { return e.poison && e.poison.turns > 0; });
    if (poisoned.length) {
      const parts = [];
      poisoned.forEach(function (e) {
        const dmg = Math.max(1, Math.round(e.poison.dmg));
        e.hp = Math.max(0, e.hp - dmg);
        e.hurtT = HURT_TIME;
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 100, dmg, '#b6e05a');
        e.poison.turns -= 1;
        if (e.poison.turns <= 0) e.poison = null;
        parts.push(enemyLabel(e, false) + ' −' + dmg);
      });
      pushBattleLog('中毒结算（每回合持续掉血）：' + parts.join('，'), 'enemy');
      setBattleLog('中毒结算：' + parts.join('，'));
      updateBattleHud();
      any = true;
    }
    return any;
  }

  // ---------- 阵亡与复活：生命归零 → 回到 0km 重新出发 ----------
  let deathActive = false;
  // 调试/测试用：打开后角色永不阵亡（跑长流程的脚本用，正式游戏不会开启）
  let debugImmortal = false;
  window.__debugImmortal = function (on) { debugImmortal = on !== false; return debugImmortal; };

  // ---------- 「防御」姿态：本轮受到的敌人伤害 -35%，另有 5% 几率完全格挡 ----------
  const DEFEND_REDUCE = 0.35;   // 减伤 35%
  const DEFEND_BLOCK_PCT = 5;   // 完全格挡几率 5%
  let defending = false;        // 本回合是否处于防御姿态（敌方回合结束后清除）

  /* ============================================================
   * 灵力（= 角色的"法力"槽，已按需求合并）
   *   · 上限 = 当前境界升级所需修为 × 2（境界基准）+ 智力/装备/宝石等加成
   *     （例：炼气·初期 需要 850 → 灵力基准 1700）
   *   · 施放技能消耗技能自带的灵力（原"魔力消耗"），防御额外消耗上限的 2%
   *   · 练气期及以上：灵力"催动"——技能伤害 +40%、灵力护盾减伤 55% / 完全格挡 15%
   *     （武者境只把灵力当法力用，没有催动加成 → "练气期远强于武者境"）
   *   · 回复：装备的"灵力每回合恢复 +X"词缀、突破境界回满、打坐休息回满
   * ============================================================ */
  const SPIRIT_MIN_STAGE = 31;          // 练气期（炼气·初期）
  const SPIRIT_DEFEND_COST_PCT = 0.02;  // 防御消耗：灵力上限的 2%（至少 1 点）
  const SPIRIT_DMG_BOOST = 0.40;        // 灵力催动：技能伤害加成
  const SPIRIT_DEFEND_REDUCE = 0.55;    // 灵力护盾：减伤
  const SPIRIT_DEFEND_BLOCK_PCT = 15;   // 灵力护盾：完全格挡几率
  let defendSpiritPowered = false;      // 本次防御是否用了灵力护盾
  let bleedImmune = false;              // 免疫流血（框架开关：后续门派武学 / 丹药 / 装备可以点亮）

  // 灵力上限的"境界基准"：当前境界升级所需修为 × 2
  function spiritBaseOf(stage) {
    const s = Math.max(1, Math.round(stage || playerLevel()));
    return Math.max(1, Math.round(xpNeeded(s) * 2));
  }
  // 是否进入"灵力催动"境界（练气期及以上）
  function spiritBoosted() { return playerLevel() >= SPIRIT_MIN_STAGE; }
  // 一次防御的灵力消耗
  function defendSpiritCostNow() {
    if (!spiritBoosted()) return 0;
    return Math.max(1, Math.round((derived.maxMp || 0) * SPIRIT_DEFEND_COST_PCT));
  }
  /* 灵力催动的技能倍率（练气期及以上常驻）。
   *   武者境 = 1（只把灵力当法力用）。 */
  function spiritSkillMult() {
    return spiritBoosted() ? 1 + SPIRIT_DMG_BOOST : 1;
  }
  // 扣灵力（走的就是原来的法力槽 combat.mp）→ false = 灵力不足
  function paySpirit(cost) {
    if (!cost) return true;
    if ((combat.mp | 0) < cost) return false;
    combat.mp = (combat.mp | 0) - cost;
    return true;
  }
  // 灵力回满（突破 / 打坐用）
  function refillSpirit() {
    const before = Math.round(combat.mp);
    combat.mp = combat.maxMp;
    return Math.max(0, combat.maxMp - before);
  }
  // 本回合防御的减伤 / 完全格挡（灵力护盾更强）
  function defendReduceNow() { return defendSpiritPowered ? SPIRIT_DEFEND_REDUCE : DEFEND_REDUCE; }
  function defendBlockNow() { return defendSpiritPowered ? SPIRIT_DEFEND_BLOCK_PCT : DEFEND_BLOCK_PCT; }

  function triggerDeath(opts) {
    if (deathActive) return;
    if (debugImmortal) { combat.hp = 1; updateBattleHud(); return; }
    deathActive = true;
    const o = opts || {};
    /* 临时副本里阵亡：算"被踢出副本"——副本里捡到的物品 + 身上金币全部掉在原地
     * （已穿戴的装备不掉）。要先结算掉落，再退出房间/清掉副本实例。 */
    let activityLoss = null;
    let pvpLossBy = '';        // 被真人击败时的对手名字（用于弹窗 / 战报文案）
    if (activityRun || netRoomKind === 'ling') {
      const localX = Math.round(player.worldX - roomAnchorWorldX());
      if (o.remotePvp) {
        /* 被真人打倒（对方客户端结算后通知过来）：按 PvP 规则掉光背包 + 金币 */
        activityLoss = dropAllBagOnDeath();
        reportPvpLoss(o.remotePvp, activityLoss, localX);
        pvpLossBy = o.remotePvp.name || '道友';
      } else if (pvpDuel) {
        /* 神识交锋输了：掉光背包 + 金币，并把掉落报给对手 */
        const rival = { sid: (pvpDuel.foeSid || null), name: pvpDuel.foeName };
        pvpDuel = null;
        activityLoss = dropAllBagOnDeath();
        reportPvpLoss(rival, activityLoss, localX);
        pvpLossBy = rival.name || '道友';
      } else {
        activityLoss = dropActivityLootOnDeath();
      }
      if (activityRun) { activityNetLeave(); activityRun = null; }
      // 灵脉这边阵亡：传送演出/落点仙人掌一并收掉（房间由 updateLingPresence 自动退）
      lingTp = null; playerLiftY = 0; playerTpAlpha = 1;
      lingForcedCactus = null; lingNode = null; lingPvpPiles = [];
    }
    // 洞府阵亡惩罚：按洞府类型丢掉身上的装备（灵宝槽不参与，避免一次性毁掉极稀有物）
    const lostGear = dropEquippedOnDeath();

    // 收干净当前战斗
    pendingVictory = 0;
    slashFx = null;
    clearDefendState();
    airSlashFx = null;
    damagePopups = [];
    ally = null;
    battleGroup.length = 0;
    activeEnemy = null;
    corpse = null;
    input.moving = false;
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    gameState = 'dead';
    if (ui.deathInfo) {
      ui.deathInfo.textContent = '你走到了 ' + (Math.max(0, player.worldX - START_X) / KM_UNIT).toFixed(1) +
        ' km，击败了 ' + defeatedEnemies + ' 个敌人。';
    }
    renderDeathLoss(lostGear, activityLoss, pvpLossBy);
    ui.deathPopup.classList.remove('hidden');
    if (activityLoss && (activityLoss.items.length || activityLoss.gold)) {
      showToast(pvpLossBy
        ? ('被「道友·' + pvpLossBy + '」击败：背包里的 ' + activityLoss.items.length +
           ' 件物品与 ' + activityLoss.gold + ' 金币全掉了！')
        : ('副本陨落：掉了 ' + activityLoss.items.length + ' 件物品与 ' + activityLoss.gold + ' 金币！'));
      saveGame();
    }
    addBattleLogSafe('你被击倒，回到了 0km 起点。' +
      (lostGear.length ? '（洞府陨落：丢失 ' + lostGear.length + ' 件装备）' : '') +
      (activityLoss && (activityLoss.items.length || activityLoss.gold)
        ? (pvpLossBy
            ? '（败给「道友·' + pvpLossBy + '」：背包物品与金币全掉，丢 ' + activityLoss.items.length +
              ' 件物品与 ' + activityLoss.gold + ' 金币）'
            : '（副本陨落：丢失 ' + activityLoss.items.length + ' 件物品与 ' + activityLoss.gold + ' 金币）') : ''), 'enemy');
    if (autoAdventure) stopAutoAdventure('你倒下了，自动冒险结束。');
  }

  /* ============================================================
   * 洞府阵亡掉落：绿色丢 1 件、蓝色丢 2 件、金色丢全身
   *   从"身上已装备的槽位"里随机挑（灵宝槽除外），挑中即卸下丢失
   * ============================================================ */
  const CAVE_DEATH_LOSS = { green: 1, blue: 2, gold: Infinity };

  /* ============================================================
   * 临时副本阵亡掉落：副本里捡到的所有物品 + 身上所有金币全部掉在原地
   *   已经在身上穿戴的装备不掉（装备不在背包里，天然就不会被算进来）
   * ============================================================ */
  function dropActivityLootOnDeath() {
    const lost = inventory.filter(function (it) { return it && it.fromActivity; });
    const lostGold = Math.max(0, Math.round(gold));
    if (!lost.length && !lostGold) return { items: [], gold: 0, name: '' };
    const name = (activityRun && activityRun.def && activityRun.def.name) || '副本';
    if (lost.length) inventory = inventory.filter(function (it) { return !(it && it.fromActivity); });
    gold = 0;
    updateGold();
    return { items: lost, gold: lostGold, name: name };
  }

  /* 顺利把东西带出副本：清掉"副本内所得"标记。
   * 这批战利品已经安全到手，以后再在哪个副本里阵亡也不该再掉。 */
  function clearActivityLootFlags() {
    let n = 0;
    inventory.forEach(function (it) {
      if (it && it.fromActivity) { delete it.fromActivity; n++; }
    });
    return n;
  }

  function dropEquippedOnDeath() {
    if (!cave) return [];
    const def = cave.def || {};
    const limit = CAVE_DEATH_LOSS[def.id];
    if (!limit) return [];
    const slots = Object.keys(equipment).filter(function (s) {
      return s !== 'treasure' && equipment[s];
    });
    if (!slots.length) return [];
    // 洗牌后按数量取
    for (let i = slots.length - 1; i > 0; i--) {
      const j = randInt(0, i);
      const tmp = slots[i]; slots[i] = slots[j]; slots[j] = tmp;
    }
    const take = limit === Infinity ? slots.length : Math.min(limit, slots.length);
    const lost = [];
    for (let i = 0; i < take; i++) {
      const slot = slots[i];
      lost.push(equipment[slot]);
      equipment[slot] = null;
    }
    if (lost.length) {
      recalcPlayerStats();
      pushBattleLog('洞府陨落：' + (def.name || '洞府') + '中丢失了 ' + lost.length + ' 件装备（' +
        lost.map(function (g) { return g.name; }).join('、') + '）', 'enemy');
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
      showToast('洞府陨落：丢失 ' + lost.length + ' 件装备！' +
        (limit === Infinity ? '（金色洞府会丢光全身装备）' : ''));
      saveGame();
    }
    return lost;
  }

  function renderDeathLoss(lost, activityLoss, pvpLossBy) {
    if (!ui.deathLoss) return;
    ui.deathLoss.textContent = '';
    const actItems = (activityLoss && activityLoss.items) || [];
    const actGold = (activityLoss && activityLoss.gold) || 0;
    if (ui.deathNote) {
      if (pvpLossBy) {
        ui.deathNote.textContent = '被「道友·' + pvpLossBy + '」击败：背包里的 ' + actItems.length +
          ' 件物品和身上 ' + actGold + ' 枚金币全部掉在了副本里（对方可以拾取）；' +
          '已经穿戴在身上的装备不会掉落。';
      } else if (actItems.length || actGold) {
        ui.deathNote.textContent = '副本陨落：在「' + ((activityLoss && activityLoss.name) || '临时副本') +
          '」里捡到的 ' + actItems.length + ' 件物品和身上 ' + actGold +
          ' 枚金币全部掉在了副本里；已经穿戴在身上的装备不会掉落。';
      } else if (lost.length) {
        ui.deathNote.textContent = '洞府陨落：这次丢掉了 ' + lost.length +
          ' 件装备（绿色洞府丢 1 件 / 蓝色洞府丢 2 件 / 金色洞府丢全身）。';
      } else {
        ui.deathNote.textContent = '回到 0km 起点，生命与灵力恢复满值，装备与收获都会保留。';
      }
    }
    if (!lost.length && !actItems.length && !actGold) { ui.deathLoss.classList.add('hidden'); return; }
    const rows = lost.map(function (g) { return { item: g, meta: '装备' }; })
      .concat(actItems.map(function (it) {
        const t = it.type;
        const meta = it.slot ? '装备'
          : (t === 'spiritItem' ? '丹药' : (t === 'potion' ? '丹药' : (t === 'skillStone' || t === 'legendStone' ? '技能石' : '材料')));
        return { item: it, meta: meta };
      }));
    if (actGold > 0) rows.push({ item: { name: '金币 ×' + actGold, icon: '金', rarityColor: '#ffd76a' }, meta: '金币' });
    rows.forEach(function (r) {
      const g = r.item;
      const row = document.createElement('div');
      row.className = 'lootRow';
      const icon = document.createElement('div');
      icon.className = 'lootIcon';
      fillItemIcon(icon, g, g.icon || '?');
      icon.style.borderColor = g.rarityColor || 'rgba(255,255,255,.2)';
      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'lootName';
      name.textContent = g.name;
      name.style.color = g.rarityColor || '#ffe9c0';
      const meta = document.createElement('div');
      meta.className = 'lootMeta';
      meta.textContent = (g.slot && SLOT_DEFS[g.slot] ? SLOT_DEFS[g.slot].label : r.meta) + ' · 已丢失';
      info.appendChild(name);
      info.appendChild(meta);
      row.appendChild(icon);
      row.appendChild(info);
      ui.deathLoss.appendChild(row);
    });
    ui.deathLoss.classList.remove('hidden');
  }

  // 复活：回到起点重新上路（装备 / 金币 / 境界都保留）
  function reviveAtStart() {
    ui.deathPopup.classList.add('hidden');
    deathActive = false;
    player.worldX = START_X;
    player.vx = 0;
    input.moving = false;
    defeatedEnemies = 0;
    resumeWorldX = null;   // 阵亡后里程清零：重开/读档都从起点重新出发
    activeEnemy = null;
    battleGroup.length = 0;
    corpse = null;
    chest = null;
    cans = [];
    eliteCharges = 0;
    cave = null;
    caveEntrances.length = 0;
    caveNextX = player.worldX + rand(4200, 6200);
    legendMap = null;
    mirage = null;
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    combat.mp = derived.mpUsable;
    flaskCharges = 5;
    updateFlaskUI();
    gameState = 'walk';
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    updateLegendBar();
    updateCaveBar();
    updateEliteButton();
    updateDist();
    updateBattleHud();
    spawnNextEnemy();
    saveGame();
    showToast('你回到了 0km 起点，重新踏上武者之路。');
  }

  function addFlaskKill(isElite, silent) {
    // 满充能时不累计也不消耗进度：先用掉一层，进度继续有效
    if (flaskCharges >= 5) {
      if (!silent) showToast('血瓶已是满充能（5/5），先用掉一层再来积累进度。');
      updateFlaskUI();
      return;
    }
    if (isElite) {
      // 精英 / 传奇：每 FLASK_ELITE_NEED 个就加一层
      flaskKillElite += 1;
      if (flaskKillElite >= FLASK_ELITE_NEED) {
        flaskKillElite -= FLASK_ELITE_NEED;
        flaskCharges += 1;
        if (!silent) showToast('击败精英敌人，血瓶充能 +1（当前 ' + flaskCharges + '/5 层）');
      }
    } else {
      // 普通 / 蓝色：每 FLASK_COMMON_NEED 个加一层
      flaskKillCommon += 1;
      if (flaskKillCommon >= FLASK_COMMON_NEED) {
        flaskKillCommon -= FLASK_COMMON_NEED;
        flaskCharges += 1;
        if (!silent) {
          showToast('累计击败 ' + FLASK_COMMON_NEED + ' 个普通敌人，血瓶充能 +1（当前 ' + flaskCharges + '/5 层）');
        }
      }
    }
    updateFlaskUI();
  }

  function updateFlaskUI() {
    if (!ui.flaskCount) return;
    ui.flaskCount.textContent = String(flaskCharges);
    ui.btnFlask.disabled = flaskCharges <= 0;
    if (ui.btnFlask) {
      ui.btnFlask.title = '血瓶充能 ' + flaskCharges + ' / 5' +
        '\n点击恢复 30% 生命（消耗 1 层）' +
        '\n充能进度：普通敌人 ' + flaskKillCommon + '/' + FLASK_COMMON_NEED + ' 个（满 ' + FLASK_COMMON_NEED + ' 个 +1 层）' +
        ' · 精英敌人 ' + flaskKillElite + '/' + FLASK_ELITE_NEED + ' 个（每 ' + FLASK_ELITE_NEED + ' 个 +1 层）';
    }
  }

  // 左下角血瓶：恢复 30% 最大生命并消耗一层充能
  function useFlask() {
    if (flaskCharges <= 0) {
      showToast('血瓶没有充能了，击败敌人可以补充。');
      return;
    }
    if (combat.hp >= combat.maxHp) {
      showToast('生命已满，暂时不需要喝血瓶。');
      return;
    }
    const heal = Math.max(1, Math.round(combat.maxHp * 0.3));
    combat.hp = Math.min(combat.maxHp, combat.hp + heal);
    flaskCharges--;
    updateFlaskUI();
    updateBattleHud();
    if (gameState === 'battle') {
      setBattleLog('你喝下血瓶，恢复 ' + heal + ' 点生命（剩余充能 ' + flaskCharges + '）。');
    } else {
      showToast('你喝下血瓶，恢复 ' + heal + ' 点生命。');
    }
    // 真人决斗：治疗量也要报给服务端，否则对方看到的血量对不上
    if (pvpDuel && netChatReady() && netActRoom) {
      pvpDuel.myHp = Math.round(combat.hp);
      net.chat.send({ type: 'act:pvpAct', duelId: pvpDuel.id, kind: 'heal', hp: pvpDuel.myHp });
      pushBattleLog('你在交锋中喝下血瓶，恢复 ' + heal + ' 点生命', 'player');
    }
  }

  // ---------- 签到 / 活力 / 自动冒险 ----------
  function todayKey() {
    const d = new Date();
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  function loadSave() {
    savesDisabled = false;          // 开始读某个档案 → 恢复落盘
    // 先按"新档"归零，再套用存档数据 —— 避免上一条档案 / 旧版存档的脏数据残留
    resetProgressState();
    try {
      const raw = window.localStorage.getItem(activeSaveKey());
      if (!raw) { clampAttrPoints(); return; }
      const data = JSON.parse(raw);
      vitality = Math.max(0, Math.floor(data.vitality || 0));
      // 金币（新版才存；老存档没有就是 0）
      gold = Math.max(0, Math.floor(Number(data.gold) || 0));
      // 血瓶充能与进度
      if (typeof data.flaskCharges === 'number') flaskCharges = clamp(Math.round(data.flaskCharges), 0, 5);
      if (typeof data.flaskKillCommon === 'number') flaskKillCommon = Math.max(0, Math.floor(data.flaskKillCommon));
      if (typeof data.flaskKillElite === 'number') flaskKillElite = Math.max(0, Math.floor(data.flaskKillElite));
      newbieChestUsed = !!data.newbieChestUsed;
      // 回收品级（老存档没有就是全不勾，托管不会乱拆东西）
      recycleFilter = { common: false, magic: false, rare: false };
      if (data.recycleFilter && typeof data.recycleFilter === 'object') {
        Object.keys(recycleFilter).forEach(function (k) {
          recycleFilter[k] = !!data.recycleFilter[k];
        });
      }
      /* 灵脉仙人掌备忘（老存档没有就是空的）：点过一次的仙人掌编号与灵力上限要接着用，
       *   否则刷新一次页面又能把同一棵仙人掌重新掷一遍 = 换个方式刷。 */
      lingCactusSeen = {};
      if (data.lingCactus && typeof data.lingCactus === 'object') {
        Object.keys(data.lingCactus).slice(0, LING_CACTUS_MEMO_MAX).forEach(function (k) {
          const v = data.lingCactus[k];
          if (!v || typeof v !== 'object') return;
          const num = Math.round(Number(v.number) || 0);
          if (!(num >= LING_MIN && num <= LING_MAX)) return;
          const pm = clamp(Math.round(Number(v.poolMax) || LING_MAX), 1, LING_MAX);
          const pool = clamp(Math.round(Number(v.pool) || 0), 0, pm);
          lingCactusSeen[k] = { number: num, pool: pool, poolMax: pm };
        });
      }
      // 时装：老存档没有这个字段就是没穿
      costumes = { head: null };
      if (data.costumes && typeof data.costumes === 'object') {
        Object.keys(costumes).forEach(function (slotId) {
          const worn = data.costumes[slotId];
          const def = worn && costumeDef(worn.id);
          if (def) costumes[slotId] = { id: def.id, name: def.name, detail: def.desc || '', icon: def.icon };
        });
      }
      // 副本：当日已用挑战次数（老存档没有就是 0；跨天由 dungeonSyncDay 清零）
      if (data.dungeonRun && typeof data.dungeonRun === 'object') {
        dungeonRun.day = String(data.dungeonRun.day || '');
        // 今日已进入次数：不再写死"每天最多 3 次"（前 3 次双倍，之后靠门派贡献继续进）
        dungeonRun.used = Math.max(0, Math.min(999, Math.floor(Number(data.dungeonRun.used) || 0)));
      }
      // PVP 活动副本：上次进入时间（1 小时冷却；老存档没有就是 0 = 可进）
      activityLastEnterAt = Math.max(0, Math.floor(Number(data.activity && data.activity.lastEnterAt) || 0));
      // 门派：所属门派 + 贡献 + 今日门派任务（老存档没有就是"无门无派"）
      if (data.sect && typeof data.sect === 'object') {
        const sid = String(data.sect.id || '');
        sect.id = SECTS.some(function (s) { return s.id === sid; }) ? sid : null;
        sect.contribution = Math.max(0, Math.floor(Number(data.sect.contribution) || 0));
        sect.joinedAt = Math.max(0, Math.floor(Number(data.sect.joinedAt) || 0));
        sect.arts = {};
        if (data.sect.arts && typeof data.sect.arts === 'object') {
          Object.keys(data.sect.arts).forEach(function (k) {
            const v = Math.max(0, Math.floor(Number(data.sect.arts[k]) || 0));
            if (v > 0) sect.arts[k] = v;
          });
        }
        const st = data.sect.tasks || {};
        sect.tasks = {
          day: String(st.day || ''),
          progress: Object.assign({}, st.progress || {}),
          claimed: Object.assign({}, st.claimed || {})
        };
      }
      // 每日任务：日期 + 完成项 + 点数 + 今日是否已领活力（跨天由 dailyQuestSyncDay 清零）
      if (data.dailyQuests && typeof data.dailyQuests === 'object') {
        const dq = data.dailyQuests;
        const done = {};
        if (dq.done && typeof dq.done === 'object') {
          Object.keys(dq.done).forEach(function (k) {
            if (dq.done[k] && DAILY_QUESTS.some(function (q) { return q.id === k; })) done[k] = true;
          });
        }
        dailyQuests = {
          day: String(dq.day || ''),
          done: done,
          points: Math.max(0, Math.floor(Number(dq.points) || 0)),
          rewarded: !!dq.rewarded
        };
      }
      // 已学技能 + 技能上的镶嵌石（老存档没有该字段时保留初始「劈砍」）
      if (Array.isArray(data.skills) && data.skills.length) {
        knownSkills.length = 0;
        data.skills.forEach(function (s) {
          if (!s || !s.id || !s.name) return;
          if (isRemovedSkillId(s.id)) return;   // 已移除的技能不再恢复
          const sk = {
            id: s.id,
            name: s.name,
            desc: s.desc || '',
            cost: Math.max(0, Math.floor(s.cost || 0)),
            type: s.type || 'melee',
            level: Math.max(1, Math.floor(s.level || 1)),
            sockets: Array.isArray(s.sockets)
              ? s.sockets.filter(function (g) { return g && g.effect; })
              : []
          };
          // 镶嵌槽数量（默认 3，最多 6）；老存档没有该字段就按默认给
          sk.socketSlots = clamp(Math.floor(Number(s.socketSlots) || SOCKET_SLOTS_DEFAULT),
            SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
          if (Array.isArray(s.dmg) && s.dmg.length === 2) sk.dmg = [s.dmg[0], s.dmg[1]];
        sk.sockets.forEach(function (g) { g.socketedTo = s.id; });
        knownSkills.push(sk);
      });
      // 技能改名 / 调整基础数值后，老存档也跟着最新定义走（等级与镶嵌石保留）
      knownSkills.forEach(function (sk) {
        const def = skillDefById(sk.id);
        if (!def) return;
        sk.name = def.name;
        if (def.desc) sk.desc = def.desc;
        if (def.type) sk.type = def.type;
        if (def.cost != null) sk.cost = def.cost;
        if (Array.isArray(def.dmg) && def.dmg.length === 2) sk.dmg = [def.dmg[0], def.dmg[1]];
      });
        // 兜底：万一把技能都清了，至少留一个劈砍
        if (!knownSkills.length) knownSkills.push(starterSkill());
      }
      // 自动冒险的优先技能：存档里有、且该技能还在，才恢复
      autoSkillId = (data.autoSkillId && knownSkills.some(function (s) { return s.id === data.autoSkillId; }))
        ? data.autoSkillId : null;
      // 世界BOSS：刷新时刻由上海时间实时推演，存档只恢复本轮血量 / 今日奖励 / 击杀数
      if (data.worldBoss && typeof data.worldBoss === 'object') {
        const wd = data.worldBoss;
        wb.killed = Math.max(0, Math.floor(wd.killed || 0));
        wb.hp = Math.max(0, Math.floor(wd.hp || 0));
        wb.hpBossId = typeof wd.hpBossId === 'string' ? wd.hpBossId : '';
        wb.spawnedAt = Math.max(0, Number(wd.spawnedAt) || 0);
        wb.lastRewardDay = typeof wd.lastRewardDay === 'string' ? wd.lastRewardDay : '';
      }
      // 冒险进度：接着上次的里程继续走（除非是本档阵亡 / 新建档案）
      if (typeof data.distance === 'number' && isFinite(data.distance)) {
        const d = Math.max(START_X, Math.round(data.distance));
        resumeWorldX = d > START_X + 10 ? d : null;
      }
      if (typeof data.defeatedEnemies === 'number' && data.defeatedEnemies >= 0) {
        defeatedEnemies = Math.floor(data.defeatedEnemies);
      }
      // 普通冒险道路品级保底进度（老存档没有就是 0：先按 3 普通 → 蓝色重新累计）
      roadCommonKills = clamp(Math.floor(Number(data.roadCommonKills) || 0), 0, ROAD_BLUE_AFTER);
      roadMagicKills = clamp(Math.floor(Number(data.roadMagicKills) || 0), 0, ROAD_GOLD_AFTER);
      if (typeof data.killsTotal === 'number' && data.killsTotal >= 0) {
        killsTotal = Math.floor(data.killsTotal);
      } else {
        // 老存档没有这个字段：用当前这局的击杀数当个初值
        killsTotal = defeatedEnemies;
      }
      // 任务进度（老存档没有这个字段：留空 → 进游戏时补弹一次「第一个任务」）
      if (data.quests && data.quests.first && typeof data.quests.first === 'object') {
        quests.first.accepted = !!data.quests.first.accepted;
        quests.first.done = !!data.quests.first.done;
      }
      lastSignDate = data.lastSignDate || '';
      if (data.level) player.level = Math.max(1, Math.floor(data.level));
      if (data.xp) player.xp = Math.max(0, Math.floor(data.xp));
      // 等级兜底：0 / 空 / 非法值一律按 1 级处理
      player.level = clamp(Math.max(1, Math.floor(Number(data.level) || 1)), 1, MAX_STAGE);
      player.bottleneck = !!data.bottleneck;
      // 阵营：武者大圆满择道的结果（老存档没有 = 尚未择道）
      faction = factionDef(data.faction) ? data.faction : null;
      cgSeen = !!data.cgSeen;    // 老存档没有这个字段 = 还没看过开场 CG
      // 灵根（武者大圆满→炼气 突破后随机得到的资质）；老存档没有 = 还没测过
      player.spiritRoot = (data.spiritRoot && data.spiritRoot.id &&
        SPIRIT_ROOTS.some(function (r) { return r.id === data.spiritRoot.id; }))
        ? { id: data.spiritRoot.id, name: data.spiritRoot.name || null,
            grade: data.spiritRoot.grade || null, rerolls: data.spiritRoot.rerolls || 0 }
        : null;
      // 门派丹药的永久上限加成（老存档没有就是 0）
      player.hpMaxBonus = Math.max(0, Math.floor(Number(data.hpMaxBonus) || 0));
      player.mpMaxBonus = Math.max(0, Math.floor(Number(data.mpMaxBonus) || 0));
      if (data.onlineSeconds) onlineSeconds = Math.max(0, Math.floor(data.onlineSeconds));
      if (typeof data.onlineRewardIndex === 'number') onlineRewardIndex = Math.max(0, Math.floor(data.onlineRewardIndex));
      if (typeof data.onlinePending === 'number') onlinePending = Math.max(0, Math.floor(data.onlinePending));
      // 「气血反弹」的开关跟随技能本身：技能是会话状态（不写入存档），
      // 所以这里也不再从存档恢复开关，避免“没有技能却自带反噬/血色波纹”
      if (Array.isArray(data.codex)) {
        // 老存档里原「神级」的 id 映射到新的「暗金」id
        data.codex.forEach(function (id) { codexUnlocked[CODEX_LEGACY_IDS[id] || id] = true; });
      }
      // 聊天表情包：已激活的（老存档没有就是空；只认还在清单里的 id）
      chatStickers = {};
      if (data.chatStickers && typeof data.chatStickers === 'object') {
        Object.keys(data.chatStickers).forEach(function (k) {
          if (data.chatStickers[k] && stickerDef(k)) chatStickers[k] = true;
        });
      }
      if (data.gachaPity) gachaPity = Math.max(0, Math.floor(data.gachaPity));
      if (data.gachaCount) gachaCount = Math.max(0, Math.floor(data.gachaCount));
      if (data.caveToken) caveToken = Math.max(0, Math.floor(data.caveToken));
      // 属性点：老存档没有该字段时，按“每提升一个境界 +ATTR_POINTS_PER_LEVEL 点”补发
      if (data.attrs && typeof data.attrs === 'object') {
        ATTR_DEFS.forEach(function (def) {
          player.attrs[def.id] = Math.max(0, Math.floor(data.attrs[def.id] || 0));
        });
      }
      if (typeof data.attrPoints === 'number') {
        player.attrPoints = Math.max(0, Math.floor(data.attrPoints));
      } else {
        const spent = ATTR_DEFS.reduce(function (sum, def) {
          return sum + (player.attrs[def.id] || 0);
        }, 0);
        player.attrPoints = Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent);
      }
      // 装备与背包
      if (data.equipment && typeof data.equipment === 'object') {
        Object.keys(equipment).forEach(function (slot) {
          const it = data.equipment[slot];
          equipment[slot] = (it && typeof it === 'object') ? it : null;
        });
      }
      if (Array.isArray(data.inventory)) {
        inventory = data.inventory.filter(function (it) { return it && typeof it === 'object'; });
      }
      // 物品 id 计数器推到已有最大值，避免新建物品撞 id
      let maxItemId = 0;
      const scanId = function (it) {
        if (!it || !it.id) return;
        const n = parseInt(String(it.id).replace(/\D/g, ''), 10);
        if (!isNaN(n) && n > maxItemId) maxItemId = n;
      };
      Object.keys(equipment).forEach(function (slot) { scanId(equipment[slot]); });
      inventory.forEach(scanId);
      if (maxItemId > itemSeq) itemSeq = maxItemId;
      // 老物品补齐词缀数据（洗练/展示都要用）
      Object.keys(equipment).forEach(function (slot) {
        if (equipment[slot] && equipment[slot].type === 'equipment') ensureItemMods(equipment[slot]);
      });
      inventory.forEach(function (it) { if (it && it.type === 'equipment') ensureItemMods(it); });
      // 老存档的装备补美术图标（武器 / 头盔 / 衣服 / 鞋子 / 手套）
      Object.keys(equipment).forEach(function (slot) { ensureItemIcon(equipment[slot]); });
      inventory.forEach(function (it) { ensureItemIcon(it); });
      recalcPlayerStats();
      updateGold();
      updateFlaskUI();
    } catch (e) { /* file:// 下可能禁用 localStorage，忽略即可 */ }
    clampAttrPoints();
  }

  // 属性点不得超过"当前境界一共能拿到多少"（旧存档可能留下超量垃圾数据）
  function clampAttrPoints() {
    const spent = ATTR_DEFS.reduce(function (sum, def) {
      return sum + (player.attrs[def.id] || 0);
    }, 0);
    const maxAvailable = Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent);
    if (!(player.attrPoints >= 0)) player.attrPoints = 0;
    if (player.attrPoints > maxAvailable) player.attrPoints = maxAvailable;
    return player.attrPoints;
  }

  // 存档内容：同一份数据既写浏览器本地（离线兜底），也上传服务器
  function buildSavePayload() {
    return {
        schemaVersion: 1,
        savedAt: Date.now(),
        vitality: vitality,
        gold: gold,
        lastSignDate: lastSignDate,
        level: player.level,
        xp: Math.floor(player.xp),
        bottleneck: !!player.bottleneck,
        faction: faction,          // 武者大圆满择道的结果（null = 尚未择道）
        cgSeen: !!cgSeen,          // 开场 CG 看过没有（每个档案只播一次）
        onlineSeconds: Math.floor(onlineSeconds),
        onlineRewardIndex: onlineRewardIndex,
        onlinePending: onlinePending,
        codex: Object.keys(codexUnlocked),
        chatStickers: Object.assign({}, chatStickers),   // 已激活的聊天表情包
        gachaPity: gachaPity,
        gachaCount: gachaCount,
        caveToken: caveToken,
        attrs: { str: player.attrs.str, agi: player.attrs.agi, int: player.attrs.int },
        attrPoints: player.attrPoints,
        // 血瓶
        flaskCharges: flaskCharges,
        flaskKillCommon: flaskKillCommon,
        flaskKillElite: flaskKillElite,
        // 新手宝箱是否已经用过（每个档案只出一次）
        newbieChestUsed: newbieChestUsed,
        // 装备回收：勾选的品级（托管自动回收也按它来）
        recycleFilter: { common: !!recycleFilter.common, magic: !!recycleFilter.magic, rare: !!recycleFilter.rare },
        // 灵根（灵根资质）：随档保存
        spiritRoot: player.spiritRoot || null,
        // 门派丹药等来源的永久上限加成（随档保存，老存档没有就是 0）
        hpMaxBonus: Math.max(0, player.hpMaxBonus | 0),
        mpMaxBonus: Math.max(0, player.mpMaxBonus | 0),
        // 门派：所属门派 + 门派贡献 + 今日门派任务进度（框架版，细节后续再扩）
        sect: {
          id: sect.id || null,
          contribution: sect.contribution | 0,
          joinedAt: sect.joinedAt || 0,
          arts: Object.assign({}, sect.arts || {}),
          tasks: {
            day: (sect.tasks && sect.tasks.day) || '',
            progress: Object.assign({}, (sect.tasks && sect.tasks.progress) || {}),
            claimed: Object.assign({}, (sect.tasks && sect.tasks.claimed) || {})
          }
        },
        // 灵脉：每棵仙人掌的编号 / 灵力上限备忘（点过一次就定死，刷新页面也不会重掷）
        lingCactus: (function () {
          const out = {};
          Object.keys(lingCactusSeen).forEach(function (k) {
            const v = lingCactusSeen[k];
            if (v) out[k] = { number: v.number, pool: v.pool, poolMax: v.poolMax };
          });
          return out;
        })(),
        // 时装（收藏品，不画在角色身上）：每个槽位记 id 即可
        costumes: (function () {
          const out = {};
          Object.keys(costumes).forEach(function (k) { if (costumes[k]) out[k] = costumes[k]; });
          return out;
        })(),
        // 世界BOSS：刷新时刻由上海时间推演（不入档），存档只记本轮血量 / 今日奖励 / 击杀数
        // 副本：每日 3 次挑战机会（存"日期 + 已用次数"，跨天自动重置）
        dungeonRun: { day: dungeonRun.day, used: dungeonRun.used },
        // PVP 活动副本：上次进入的时间戳（用来算 1 小时冷却，刷新页面也不会重置）
        activity: { lastEnterAt: activityLastEnterAt },
        worldBoss: {
          spawnedAt: wb.spawnedAt,
          hp: wb.hp,
          hpBossId: wb.hpBossId,
          killed: wb.killed,
          lastRewardDay: wb.lastRewardDay
        },
        // 已学会的技能（含镶嵌的辅助/被动技能石）。召唤宠物是法杖被动，按装备实时推导，不入档
        skills: knownSkills.filter(function (s) { return s.id !== 'summonPet'; }),
        // 自动冒险优先使用的技能（法术窗口里「激活」的那个）
        autoSkillId: autoSkillId,
        // 装备与背包（物品都是纯数据，可以直接序列化）
        // 冒险进度：移动距离（里程）与已击败敌人数，读档时接着走
        distance: Math.round(player.worldX),
        defeatedEnemies: defeatedEnemies,
        killsTotal: killsTotal,
        // 普通冒险道路的品级保底进度（3 普通→蓝色、3 蓝色→金色）
        roadCommonKills: roadCommonKills,
        roadMagicKills: roadMagicKills,
        // 任务进度
        quests: { first: { accepted: !!quests.first.accepted, done: !!quests.first.done } },
        // 每日任务（日期 + 完成项 + 点数 + 是否已领活力）
        dailyQuests: {
          day: dailyQuests.day,
          done: (function () {
            const out = {};
            Object.keys(dailyQuests.done).forEach(function (k) { if (dailyQuests.done[k]) out[k] = true; });
            return out;
          })(),
          points: dailyQuests.points,
          rewarded: !!dailyQuests.rewarded
        },
        equipment: (function () {
          const out = {};
          Object.keys(equipment).forEach(function (slot) {
            if (equipment[slot]) out[slot] = equipment[slot];
          });
          return out;
        })(),
        inventory: inventory
    };
  }

  function saveGame() {
    if (savesDisabled) return;      // 删档之后、新档案落定之前不落盘
    let payload = null;
    try {
      payload = buildSavePayload();
      window.localStorage.setItem(activeSaveKey(), JSON.stringify(payload));
      // 档案索引里记一下最近玩的等级 / 时间
      if (account.hash) touchAccount(account.hash, player.level);
    } catch (e) { /* 忽略写入失败 */ }
    queueRemoteSave(payload);   // 联网时顺带同步到服务器
  }

  /* ---------- 云端存档同步 ---------- */
  // 节流：2.5 秒内的连续存档合并成一次上传，避免频繁刷服务器
  const REMOTE_SAVE_DELAY_MS = 1500;

  /* ---------- 自动存档（关键数值变动后落盘 + 定期兜底）----------
   * 背景：金币 / 活力 / 物品这些是"随时"变的，但以前只有少数事件才 saveGame()，
   *       结果云端刷新后金币回退。现在改成一有变动就标记 dirty，几秒后落盘。 */
  let saveDirty = false;
  let dirtySaveTimer = null;
  const DIRTY_SAVE_DELAY_MS = 3000;    // 变动后 3 秒落盘（合并短时间内的连续变动）
  const PERIODIC_SAVE_SEC = 30;        // 另外每 30 秒兜底存一次
  let periodicSaveClock = 0;

  function markSaveDirty() {
    if (savesDisabled) return;
    saveDirty = true;
    if (dirtySaveTimer) return;
    dirtySaveTimer = setTimeout(function () {
      dirtySaveTimer = null;
      if (!saveDirty) return;
      saveDirty = false;
      saveGame();
    }, DIRTY_SAVE_DELAY_MS);
  }

  function updatePeriodicSave(dt) {
    if (gameState !== 'walk' && gameState !== 'battle') return;
    // 每日任务跨天自动清零（挂着游戏过 0 点也不会把昨天的进度算到今天）
    const beforeDay = dailyQuests.day;
    dailyQuestSyncDay();
    if (beforeDay && beforeDay !== dailyQuests.day) {
      markSaveDirty();
      if (ui.dailyPopup && !ui.dailyPopup.classList.contains('hidden')) renderQuestPanel();
    }
    periodicSaveClock += dt;
    if (periodicSaveClock >= PERIODIC_SAVE_SEC) {
      periodicSaveClock = 0;
      saveGame();          // 兜底：即便某些改动没触发 markSaveDirty，也会定时落盘
    }
  }

  function queueRemoteSave(payload) {
    if (!net.accountOnline || !net.api || !net.token || !payload) return;
    net.pendingSave = payload;
    if (net.saveTimer) return;
    net.saveTimer = setTimeout(function () {
      net.saveTimer = null;
      flushRemoteSave();
    }, REMOTE_SAVE_DELAY_MS);
  }

  function flushRemoteSave() {
    if (!net.accountOnline || !net.api || !net.token) return Promise.resolve(false);
    const payload = net.pendingSave;
    net.pendingSave = null;
    if (!payload) return Promise.resolve(false);
    return net.api.pushSave(net.token, payload, payload.savedAt).then(function (r) {
      net.lastPushAt = Date.now();
      net.pushCount += 1;
      return r;
    }).catch(function (err) {
      // 上传失败：把内容放回队列，等下一次存档再试（不打扰玩家）
      net.pendingSave = net.pendingSave || payload;
      net.lastError = err && err.message ? err.message : '存档上传失败';
      if (err && err.code === 'unauthorized') {
        net.accountOnline = false;
        showToast('登录状态已失效，本局改为本地存档（重新登录可恢复云端同步）。');
      }
      return false;
    });
  }

  // 关页面 / 切后台时把还没上传的存档立刻推上去
  function flushRemoteSaveNow() {
    if (net.saveTimer) { clearTimeout(net.saveTimer); net.saveTimer = null; }
    return flushRemoteSave();
  }

  /* 页面要关掉/刷新时：不再用 fetch（可能来不及发出去就被浏览器取消），
   * 改用 sendBeacon 把"当前最新状态"直接投递到服务器。 */
  function flushOnPageHide() {
    /* 刚删完档、新档案还没落定时，绝不能把内存里的旧进度写回去 ——
     *   否则"删档 → 刷新页面"会让进度原封不动地回来（连里程都还在）。 */
    if (savesDisabled) return;
    let payload = null;
    try {
      payload = buildSavePayload();
      window.localStorage.setItem(activeSaveKey(), JSON.stringify(payload));
    } catch (e) { /* 忽略 */ }
    if (!payload || !net.accountOnline || !net.token || !net.serverUrl) return;
    try {
      const url = net.serverUrl + '/api/save?token=' + encodeURIComponent(net.token);
      const body = JSON.stringify({ save: payload, savedAt: payload.savedAt });
      if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } catch (e) { /* 忽略 */ }
  }

  // 改 HUD 按钮文字时只动 <span> 标签，别把图标（<img>）冲掉
  function setHudBtnLabel(btn, text) {
    if (!btn) return;
    const span = btn.querySelector('span');
    if (span) span.textContent = text;
    else btn.textContent = text;
  }

  function updateVitalityUI() {
    if (ui.vitalityValue) ui.vitalityValue.textContent = String(vitality);
    if (ui.btnSign) {
      setHudBtnLabel(ui.btnSign, lastSignDate === todayKey() ? '今日已签到' : '签到');
    }
    if (ui.btnAuto) {
      ui.btnAuto.classList.toggle('active', autoAdventure);
      ui.btnAuto.textContent = autoAdventure ? '托管中' : '托管';
      ui.btnAuto.title = autoAdventure
        ? '托管进行中（点击停止）'
        : '托管：自动打怪 / 搜尸体 / 喝药，每 1 点活力可托管 10km';
    }
  }

  // 在线奖励阶梯：第 1 次 3 分钟，之后 5 / 10 / 15 / 30 / 60 分钟，第 6 次以后固定 60 分钟
  const ONLINE_REWARD_STEPS = [180, 300, 600, 900, 1800, 3600];
  const ONLINE_REWARD_LAST = ONLINE_REWARD_STEPS[ONLINE_REWARD_STEPS.length - 1];
  let onlineRewardIndex = 0;   // 已经领过几次（第几次奖励 = 索引 + 1），随存档保存
  let onlinePending = 0;       // 已达门槛但还没点击领取的次数（在线奖励必须手动领）

  function onlineNextStep() {
    return ONLINE_REWARD_STEPS[Math.min(onlineRewardIndex, ONLINE_REWARD_STEPS.length - 1)];
  }

  function fmtMMSS(sec) {
    const t = Math.max(0, Math.floor(sec));
    return String(Math.floor(t / 60)).padStart(2, '0') + ':' + String(t % 60).padStart(2, '0');
  }

  function onlineStepText(sec) {
    return sec >= 60 ? (sec / 60) + ' 分钟' : sec + ' 秒';
  }

  function updateOnlineUI() {
    if (!ui.onlineTimer) return;
    const step = onlineNextStep();
    const total = ONLINE_REWARD_STEPS.length;
    const idx = Math.min(onlineRewardIndex + 1, total);
    if (onlinePending > 0) {
      ui.onlineTimer.textContent = '在线奖励 可领取 ×' + onlinePending;
      ui.onlineTimer.classList.add('claimable');
    } else {
      ui.onlineTimer.textContent = '在线奖励 ' + fmtMMSS(onlineSeconds) + ' / ' + fmtMMSS(step) +
        '（第 ' + idx + ' 次）';
      ui.onlineTimer.classList.remove('claimable');
    }
    ui.onlineTimer.title = '累计在线时长达到门槛后，需要手动点击「领取」才会获得 1 点活力：\n' +
      '第 1~6 次分别是 3 / 5 / 10 / 15 / 30 / 60 分钟，第 6 次之后每 60 分钟一次\n' +
      (onlinePending > 0
        ? '当前有 ' + onlinePending + ' 次还没领，点右边按钮领取'
        : '本次是第 ' + idx + ' 次，还差 ' + Math.ceil(Math.max(0, step - onlineSeconds) / 60) + ' 分钟');
    if (ui.btnOnlineClaim) {
      ui.btnOnlineClaim.classList.toggle('hidden', onlinePending <= 0);
      ui.btnOnlineClaim.textContent = onlinePending > 1 ? ('领取 ×' + onlinePending) : '领取';
      ui.btnOnlineClaim.title = '点击领取在线奖励：活力 +' + Math.max(1, onlinePending);
    }
  }

  function updateOnline(dt) {
    if (gameState !== 'walk' && gameState !== 'battle') return;
    onlineSeconds += dt;
    let reached = false;
    while (onlineSeconds >= onlineNextStep()) {
      const step = onlineNextStep();
      onlineSeconds -= step;
      onlineRewardIndex += 1;
      onlinePending += 1;      // 只记"可领取"，要玩家自己点
      reached = true;
    }
    if (reached) {
      saveGame();   // 阶梯进度也一起落盘
      showToast('在线奖励已达标：点右上角「领取」按钮领取活力 +' + onlinePending + '。');
      updateOnlineUI();
    }
    onlineUiTimer += dt;
    if (onlineUiTimer >= 0.25) {
      onlineUiTimer = 0;
      updateOnlineUI();
    }
  }

  // 手动领取在线奖励（活力不会自动到账）
  function claimOnlineReward() {
    if (onlinePending <= 0) {
      showToast('在线奖励还没到门槛，继续在线累计时长吧。');
      return 0;
    }
    const n = onlinePending;
    onlinePending = 0;
    vitality += n;
    saveGame();
    updateVitalityUI();
    updateOnlineUI();
    showToast('在线奖励已领取：活力 +' + n + '（当前活力 ' + vitality + '）');
    return n;
  }

  function signIn() {
    const today = todayKey();
    if (lastSignDate === today) {
      showToast('今天已经签到过了，明天再来。');
      return;
    }
    lastSignDate = today;
    vitality += 1;
    saveGame();
    updateVitalityUI();
    showToast('签到成功！活力 +1（当前活力 ' + vitality + '）。');
  }

  function stopAutoAdventure(message) {
    autoAdventure = false;
    autoKmLeft = 0;
    if (gameState === 'walk') {
      input.moving = false;
      syncMoveButton();
    }
    updateVitalityUI();
    if (message) showToast(message);
  }

  function startAutoAdventure() {
    if (autoAdventure) return;
    if (miningContinuous || miningFx) {
      showToast('正在挖矿，点击「停止挖矿」后才能自动冒险。');
      return;
    }
    if (cave) {
      showToast('洞府内需要手动探索，无法开启自动冒险。');
      return;
    }
    if (vitality <= 0) {
      showToast('活力不足，请先签到获取活力。');
      return;
    }
    // 前方有未开的宝箱也允许直接自动冒险（不再拦截，宝箱留着以后再回来点也行）
    // 点击即扣除 1 点活力，换取 10km 的自动冒险里程
    vitality -= 1;
    saveGame();
    autoAdventure = true;
    autoKmLeft = ENEMY_LEVEL_KM;
    autoLastX = player.worldX;
    updateVitalityUI();
    showToast('消耗 1 点活力，自动冒险开始（本次可冒险 10km）。');
    if (gameState === 'walk') {
      input.moving = true;
      syncMoveButton();
    }
  }

  function toggleAutoAdventure() {
    if (autoAdventure) stopAutoAdventure('已停止自动冒险。');
    else startAutoAdventure();
  }

  // 自动喝药：优先血瓶，其次背包里的回血药瓶
  function autoDrinkPotion() {
    if (combat.hp > combat.maxHp * 0.5) return;
    if (flaskCharges > 0) {
      useFlask();
      return;
    }
    const pot = inventory.filter(function (it) { return it.type === 'potion'; })[0];
    if (pot) usePotion(pot);
  }

  function updateAutoAdventure(dt) {
    if (!autoAdventure) return;
    if (pvpDuel) return;      // 决斗中托管不代打，必须自己出手
    if (gameState !== 'walk' && gameState !== 'battle') return;

    /* 托管自动回收：勾了品级才会动作，没勾就跟以前完全一样。
     *   放在「弹窗判断」之前 —— 不然玩家随手开着某个窗口，回收就停了；
     *   但回收窗口自己开着时跳过，别在玩家正勾品级的时候把东西拆了。 */
    if (!ui.recyclePopup || ui.recyclePopup.classList.contains('hidden')) autoRecycleSweep();

    // 有任何弹窗打开时暂停自动行为，避免误操作
    const popupOpen = document.querySelector('.popupOverlay:not(.hidden):not(#logPopup)');
    const passed = player.worldX - autoLastX;
    autoLastX = player.worldX;
    if (popupOpen) return;

    if (gameState === 'walk' && passed > 0) {
      autoKmLeft -= passed / KM_UNIT;
      while (autoKmLeft <= 0) {
        if (vitality >= 1) {
          vitality -= 1;
          autoKmLeft += ENEMY_LEVEL_KM;
          saveGame();
          updateVitalityUI();
          showToast('自动冒险继续：消耗 1 点活力，剩余活力 ' + vitality + '。');
        } else {
          stopAutoAdventure('10km 冒险里程已用完，活力不足，自动冒险结束。');
          return;
        }
      }
    }

    autoDrinkPotion();
    if (player.bottleneck && hasSpiritItem()) breakthrough();

    // 「强力射击」的完美时机：托管时自动判定（瞄准区间中段，模拟一个手稳的玩家）
    if (powerTimingActive()) {
      const t = powerCharge.timing;
      const mid = (POWER_TIMING_ZONE[0] + POWER_TIMING_ZONE[1]) / 2;
      if (Math.abs(t.pos - mid) < 0.05) pressPerfectTiming();
    }

    if (gameState === 'walk') {
      if (corpse && !corpse.searched) searchCorpse(true);
      if (!input.moving) {
        input.moving = true;
        syncMoveButton();
      }
      return;
    }

    // 战斗中：自动出手 / 自动施法
    if (pendingVictory > 0 || slashFx || battleTurn !== 'player') return;
    // 法术窗口里「激活」的技能优先；用不了（灵力不够 / 宠物已满）再退回劈砍 / 普通攻击
    if (autoSkillId) {
      const fav = knownSkills.filter(function (s) { return s.id === autoSkillId; })[0];
      if (fav && autoSkillUsable(fav)) {
        castSkill(fav);
        return;
      }
    }
    const slash = knownSkills.filter(function (s) { return s.id === 'slash'; })[0];
    if (slash && combat.mp >= slash.cost) castSkill(slash);
    else doPlayerAction('attack');
  }

  // 自动冒险时某个技能能不能用（避免空放浪费回合）
  function autoSkillUsable(skill) {
    if (!skill || skill.id === 'summonPet') return false;   // 法杖被动，不主动施放
    // 「强力射击」要弓：武器不对时自动冒险直接跳过它，免得空放浪费回合
    if (skill.id === 'powerShot' && !powerShotWeaponOk()) return false;
    if (combat.mp < skill.cost) return false;
    if (skill.type === 'summon') {
      // 「仆从」在场时上限只有 1 只，但可以再召唤一次把旧的换掉
      return hasServantGem() ? true : alivePets().length < petCap();
    }
    // 持续技能只负责"打开"（已开就不再自动点）
    if (skill.type === 'toggle') {
      if (skill.id === 'shieldWall') return !shieldWall;
      return !bloodRebound;
    }
    return true;
  }

  function equipItem(item) {
    if (!item || item.type !== 'equipment') return;
    if (!(item.slot in equipment)) return;
    /* 穿上之前先把词缀重算一遍：基底词缀（盾牌 +15% 格挡 / 长弓额外箭矢 / 首饰基底）
     * 永远跟着装备走，不会因为老存档、宝箱特制装备、调试写死数值之类的原因丢失。 */
    ensureItemMods(item);
    const old = equipment[item.slot];
    if (old) inventory.push(old);
    equipment[item.slot] = item;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    // 灵宝附带主动技能：装备后自动学会
    if (item.special && item.special.kind === 'skill' && item.special.skillId) {
      const learned = learnSkillById(item.special.skillId);
      if (learned) {
        showToast('灵宝传承：自动学会主动技能「' + learned.name + '」！');
        pushBattleLog('灵宝「' + item.special.name + '」传承：学会「' + learned.name + '」', 'reward');
      }
    }
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast(
      '已装备「' + item.name + '」' +
      (item.weaponType === '法杖' ? '，宠物自动现身跟随。' : '')
    );
  }

  function unequipSlot(slot) {
    const old = equipment[slot];
    if (!old) return;
    equipment[slot] = null;
    inventory.push(old);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast(
      '已卸下「' + old.name + '」' +
      (old.weaponType === '法杖' ? '，宠物消失了。' : '')
    );
  }

  function usePotion(item) {
    if (item.type !== 'potion') return;
    /* 门派丹药：可能回灵力、也可能永久加生命 / 灵力上限或三维属性 */
    if (item.pill) {
      const permanent = !!(item.hpMaxBonus || item.mpMaxBonus || item.attrBonus);
      if (item.mpGain && !permanent && combat.mp >= derived.mpUsable) {
        showToast('灵力已满，暂时不需要服用「' + item.name + '」。');
        return;
      }
      const parts = [];
      if (item.mpGain) {
        const before = combat.mp;
        combat.mp = Math.min(derived.mpUsable, combat.mp + item.mpGain);
        parts.push('灵力 +' + Math.round(combat.mp - before));
      }
      if (item.hpMaxBonus) {
        player.hpMaxBonus = Math.max(0, (player.hpMaxBonus | 0) + (item.hpMaxBonus | 0));
        parts.push('生命上限 +' + (item.hpMaxBonus | 0));
      }
      if (item.mpMaxBonus) {
        player.mpMaxBonus = Math.max(0, (player.mpMaxBonus | 0) + (item.mpMaxBonus | 0));
        parts.push('灵力上限 +' + (item.mpMaxBonus | 0));
      }
      if (item.attrBonus) {
        Object.keys(item.attrBonus).forEach(function (k) {
          if (!player.attrs[k]) player.attrs[k] = 0;
          player.attrs[k] += Math.max(0, item.attrBonus[k] | 0);
        });
        parts.push('力量 / 敏捷 / 智力 +1');
      }
      inventory = inventory.filter(function (it) { return it.id !== item.id; });
      recalcPlayerStats();
      if (item.hpMaxBonus) combat.hp = Math.min(combat.maxHp, combat.hp + (item.hpMaxBonus | 0));
      if (item.mpGain || item.mpMaxBonus) combat.mp = Math.min(derived.mpUsable, combat.mp + (item.mpGain || 0));
      updateBattleHud();
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      saveGame();
      showToast('服用「' + item.name + '」：' + (parts.join('　') || item.detail || '药力生效'));
      return;
    }
    if (combat.hp >= combat.maxHp) {
      showToast('生命已满，暂时不需要药水。');
      return;
    }
    combat.hp = Math.min(combat.maxHp, combat.hp + (item.heal || 25));
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('使用「' + item.name + '」，恢复 ' + (item.heal || 25) + ' 点生命。');
  }

  // 出售装备：价格随等阶与词缀强度提升
  function sellPrice(item) {
    const ratio = { common: 0.34, magic: 0.52, rare: 0.72, unique: 0.5 }[item.rarity] || 0.3;
    return Math.max(1, Math.round(item.price * ratio));
  }

  function sellItem(item) {
    if (!item || item.type !== 'equipment') return;
    const value = sellPrice(item);
    gold += value;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    updateGold();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('售出「' + item.name + '」，获得 ' + value + ' 金币。');
  }

  /* 掉落物（深渊战利品这类）出售：价格写在物品上，卖出直接换金币 */
  function sellLootItem(item) {
    if (!item || !item.sellGold) return;
    const value = Math.max(1, Math.round(item.sellGold));
    gold += value;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    updateGold();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    hideBagMenu();
    showToast('售出「' + item.name + '」，获得 ' + value + ' 金币。');
    pushBattleLog('【背包】售出「' + item.name + '」，金币 +' + value, 'reward');
    saveGame();
  }

  /* ============================================================
   * 装备回收（批量拆解）
   *   勾选品级 → 一键回收：按商店价直接卖成金币；每件额外有 3% 几率
   *   拆出「对应稀有度」的材料。传奇（灵宝）不参与，避免误拆。
   *   开启托管后，冒险途中拿到勾选品级的装备会自动走过来回收。
   * ============================================================ */
  function recycleTierDef(id) {
    return RECYCLE_TIERS.filter(function (t) { return t.id === id; })[0] || null;
  }

  // 玩家身上穿着的装备不在 inventory 里，所以回收永远不会动到已装备的东西
  function recycleTargets() {
    return inventory.filter(function (it) {
      return it && it.type === 'equipment' && !!recycleFilter[it.rarity];
    });
  }

  function recycleValueOf(list) {
    return (list || []).reduce(function (sum, it) { return sum + sellPrice(it); }, 0);
  }

  // 回收产出的材料（type 仍是 'material'，但不带 materialId，不会混进「镶嵌材料」库存）
  function makeRecycleMaterial(rarity) {
    const def = RECYCLE_MATERIALS[rarity];
    if (!def) return null;
    return {
      id: 'mat' + (++itemSeq),
      type: 'material',
      name: def.name,
      icon: def.icon,
      detail: def.detail,
      rarityName: def.rarityName,
      rarityColor: def.color,
      recycleRarity: rarity
    };
  }

  /* 真正执行回收：一次结算整批，返回 { count, gold, materials, byTier } */
  function recycleItems(list) {
    const out = { count: 0, gold: 0, materials: [], byTier: {} };
    if (!list || !list.length) return out;
    const ids = {};
    list.forEach(function (it) { ids[it.id] = true; });
    inventory = inventory.filter(function (it) { return !ids[it.id]; });
    list.forEach(function (it) {
      out.count += 1;
      out.gold += sellPrice(it);
      out.byTier[it.rarity] = (out.byTier[it.rarity] || 0) + 1;
      if (Math.random() < RECYCLE_MATERIAL_CHANCE) {
        const mat = makeRecycleMaterial(it.rarity);
        if (mat) { inventory.push(mat); out.materials.push(mat); }
      }
    });
    gold += out.gold;
    updateGold();
    markSaveDirty();
    return out;
  }

  // 回收结果的一句话说明（手动 / 自动共用）
  function recycleSummaryText(r, prefix) {
    const mats = {};
    r.materials.forEach(function (m) { mats[m.name] = (mats[m.name] || 0) + 1; });
    const matText = Object.keys(mats).map(function (n) { return n + '×' + mats[n]; }).join('、');
    return prefix + ' ' + r.count + ' 件装备，获得 ' + r.gold + ' 金币' +
      (matText ? '，拆出材料：' + matText : '') + '。';
  }

  // 「一键回收」按钮
  function recycleRunNow() {
    const targets = recycleTargets();
    if (!targets.length) {
      showToast('背包里没有勾选品级的装备，先勾一个品级再回收。');
      renderRecyclePanel();
      return null;
    }
    const r = recycleItems(targets);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderRecyclePanel();
    showToast(recycleSummaryText(r, '回收'));
    return r;
  }

  /* 托管自动回收：每帧扫一次背包，只有勾了品级且真有对应装备时才动手。
   *   没勾任何品级 → 直接返回，托管行为和以前完全一样。 */
  function autoRecycleSweep() {
    if (!autoAdventure) return;
    const targets = recycleTargets();
    if (!targets.length) return;
    const r = recycleItems(targets);
    if (!r.count) return;
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast(recycleSummaryText(r, '托管自动回收'));
  }

  // ---------- 素材加载 ----------
  function loadAssets() {
    ASSETS.forEach(function (item) {
      const img = new Image();
      img.onload = img.onerror = function () {
        loaded++;
        if (loadTask.active) {
          loadTask.real = loaded / ASSETS.length;
          loadTask.tip = '已载入 ' + loaded + ' / ' + ASSETS.length + ' 项资源';
        }
      };
      img.src = item[1];
      images[item[0]] = img;
    });
  }

  // ---------- 开始游戏前的资源加载进度条（左下角）----------
  const LOAD_MIN_TIME = 0.9;   // 进度条最短展示时间，避免一闪而过
  const loadTask = { active: false, real: 0, shown: 0, t: 0, tip: '', onDone: null };

  // ---------- 账号与存档 ----------
  // 存档按「账号 + 密码」分开：同样的账号密码 = 继续原档案；否则新建一个档案。
  // 数据都存在浏览器本地（localStorage），仅用于本地档案区分，不是安全校验。
  const ACCOUNT_KEY = 'homeward.accounts.v1';
  const LEGACY_SAVE_KEY = SAVE_KEY;      // 旧版单存档（已废弃，首次运行会清掉）
  const account = { name: '', ip: '', hash: '', isNew: false, nickname: '' };
  let loginConfirmNew = false;           // 兼容旧逻辑的复位标记（现在账号名唯一，不再允许同名新建）

  /* ============================================================
   * 联网状态（聊天 + 云端存档）
   *   服务器可用 → 存档以服务器为准、聊天走 WebSocket
   *   服务器不可用 → 自动回退到本地 localStorage + 本地聊天（单机也能玩）
   * ============================================================ */
  const NET_SERVER_KEY = 'homeward.net.server.v1';
  const NET_DEVICE_KEY = 'homeward.net.device.v1';

  // 本机设备标识：同一个浏览器（同一设备）刷新/重进不会被"单设备登录"挡住
  function deviceId() {
    try {
      let id = window.localStorage.getItem(NET_DEVICE_KEY);
      if (!id) {
        id = 'd' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.localStorage.setItem(NET_DEVICE_KEY, id);
      }
      return id;
    } catch (e) {
      return 'd-unknown';
    }
  }
  const net = {
    api: null,          // createApiClient 的实例
    chat: null,         // createChatClient 的实例
    token: '',
    serverUrl: '',      // 归一化后的服务器地址
    accountOnline: false, // 账号已连上服务器（能读写云端存档）
    chatState: 'offline', // 'connecting' | 'online' | 'offline'
    onlineCount: 0,
    saveTimer: null,
    lastPushAt: 0,
    pushCount: 0,
    lastError: '',
    identityBound: false,   // 是否已绑定实名（服务器账号级信息）
    profile: null,
    pendingChatText: ''     // 因为没实名被拦下的那条消息
  };

  function netServerFromInput(raw) {
    // 登录页那个 IP 输入框：空 = 当前页面同源（自有域名下走 nginx 反代 /api、/ws）；
    // 页面在第三方平台上时 normalizeServerUrl 会自动回落到正式域名
    return normalizeServerUrl(raw);
  }

  function netRememberServer(url) {
    net.serverUrl = url;
    try { window.localStorage.setItem(NET_SERVER_KEY, url); } catch (e) { /* 忽略 */ }
  }

  function accountHash(name, pwd) {
    let h = 0x811c9dc5;
    const s = String(name) + '\u0000' + String(pwd);
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return ('00000000' + h.toString(16)).slice(-8);
  }

  function saveKeyFor(hash) { return 'homeward.save.' + hash; }
  // 没有登录（例如测试直连 / 自动化）时继续用旧的本地存档 key，行为跟以前一致
  function activeSaveKey() { return account.hash ? saveKeyFor(account.hash) : LEGACY_SAVE_KEY; }

  // 新档案：把可存档的进度全部归零
  function resetProgressState() {
    vitality = 0;
    lastSignDate = '';
    player.level = 1;
    player.xp = 0;
    player.bottleneck = false;
    onlineSeconds = 0;
    onlineRewardIndex = 0;   // 在线奖励阶梯也从头开始
    onlinePending = 0;
    faction = null;          // 新档案：尚未择道
    cgSeen = false;          // 新档案：开场 CG 再播一次
    Object.keys(codexUnlocked).forEach(function (k) { delete codexUnlocked[k]; });
    chatStickers = {};       // 新档案：聊天表情包也清空
    gachaPity = 0;
    gachaCount = 0;
    caveToken = 0;
    player.attrs = { str: 0, agi: 0, int: 0 };
    player.attrPoints = 0;
    player.hpMaxBonus = 0;
    player.mpMaxBonus = 0;
    killsTotal = 0;            // 新档案：累计击杀从 0 开始
    // 血瓶：新档案给满 5 层充能，进度清零
    flaskCharges = 5;
    flaskKillCommon = 0;
    flaskKillElite = 0;
    newbieChestUsed = false;   // 新档案：新手宝箱重新给一次
    recycleFilter = { common: false, magic: false, rare: false };   // 新档案：回收品级默认全不勾
    lingCactusSeen = {};       // 新档案：仙人掌备忘清空
    quests.first = { accepted: false, done: false };   // 新档案：任务也从头开始
    dailyQuests = { day: '', done: {}, points: 0, rewarded: false };   // 新档案：每日任务清零
    activityLastEnterAt = 0;   // 新档案：PVP 活动副本的进入冷却也清零
    sect = {
      id: null, contribution: 0, joinedAt: 0,
      tasks: { day: '', progress: {}, claimed: {} }, arts: {}
    };   // 新档案：无门无派、未修任何门派法诀
    // 技能：新档案只保留初始「劈砍」
    knownSkills.length = 0;
    knownSkills.push(starterSkill());
    autoSkillId = null;   // 新档案没有「自动冒险优先技能」
    resumeWorldX = null;  // 新档案从起点开始
    defeatedEnemies = 0;
    roadCommonKills = 0;
    roadMagicKills = 0;
    // 世界BOSS：刷新时刻跟着上海时间走，新档案只需要清空本轮状态
    wb.phase = 'wait';
    wb.spawnedAt = 0;
    wb.expiresAt = 0;
    wb.maxHp = 0;
    wb.hp = 0;
    wb.killed = 0;
    wb.claimed = false;
    wb.lastRewardDay = '';
    wbOverride = null;
    updateVitalityUI();
    updateLevelUI();
    updateOnlineUI();
  }

  function readAccounts() {
    try {
      const data = JSON.parse(window.localStorage.getItem(ACCOUNT_KEY) || 'null');
      if (data && Array.isArray(data.accounts)) return data;
    } catch (e) { /* 忽略 */ }
    return { accounts: [] };
  }

  function writeAccounts(store) {
    try { window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(store)); } catch (e) { /* 忽略 */ }
  }

  function findAccountByName(name) {
    const store = readAccounts();
    // 账号名唯一：按"归一化 + 忽略大小写"比较（AAA / aaa / ＡＡＡ / "AAA " 视为同一个）
    const key = normalizeAccountName(name).toLowerCase();
    return store.accounts.filter(function (a) {
      return normalizeAccountName(a.name).toLowerCase() === key;
    });
  }

  // 账号名归一化：全角转半角、去零宽字符、折叠空格、首尾去空格
  function normalizeAccountName(name) {
    let s = String(name == null ? '' : name);
    try { s = s.normalize('NFKC'); } catch (e) { /* 忽略 */ }
    return s.replace(/[\u200b-\u200f\u2028\u2029\ufeff]/g, '').replace(/\s+/g, ' ').trim();
  }

  // 登记新档案（同名不同密码会各占一个档案，互不覆盖）
  function registerAccount(name, hash, extra) {
    const store = readAccounts();
    const exists = store.accounts.some(function (a) { return a.hash === hash; });
    if (!exists) store.accounts.push({ name: name, hash: hash, createdAt: Date.now(), lastPlayed: Date.now() });
    writeAccounts(store);
    return { name: name, hash: hash, ip: (extra && extra.ip) || '' };
  }

  function touchAccount(hash, level) {
    const store = readAccounts();
    store.accounts.forEach(function (a) {
      if (a.hash === hash) { a.lastPlayed = Date.now(); if (level) a.level = level; }
    });
    writeAccounts(store);
  }

  // 抹掉本机所有档案（含旧版单存档）——首次运行清一次，保证"从零开始"
  function resetAllSaves() {
    /* 删档期间禁止落盘：删完到"新档案真正落定"之间，
     *   任何一次 saveGame / 页面关闭时的 flush 都不能把旧进度写回来。 */
    savesDisabled = true;
    try {
      const store = readAccounts();
      store.accounts.forEach(function (a) { window.localStorage.removeItem(saveKeyFor(a.hash)); });
      window.localStorage.removeItem(ACCOUNT_KEY);
      window.localStorage.removeItem(LEGACY_SAVE_KEY);
    } catch (e) { /* 忽略 */ }
    Object.keys(window.localStorage).forEach(function (k) {
      if (k.indexOf('homeward.save.') === 0) window.localStorage.removeItem(k);
    });
  }

  // 自动化测试用：地址后面加 ?skiplogin=1 直接跳过登录页（正常打开不受影响）
  function skipLoginRequested() {
    try {
      const p = new URLSearchParams(location.search);
      const v = p.get('skiplogin');
      return v === '1' || v === 'true';
    } catch (e) {
      return false;
    }
  }

  function showLoginError(msg) {
    if (!ui.loginError) return;
    ui.loginError.textContent = msg;
    ui.loginError.classList.remove('hidden');
  }

  function resetLoginConfirm() {
    if (!loginConfirmNew) return;
    loginConfirmNew = false;
    if (ui.btnLogin) ui.btnLogin.textContent = '登录';
  }

  let loginBusy = false;

  /* ============================================================
   * 赞助 / 自愿捐赠
   *   收款码图片固定放这两个路径（没有图就显示占位提示，不影响其它功能）：
   *     public/assets/img/donate/alipay.png
   *     public/assets/img/donate/wechat.png
   * ============================================================ */
  const DONATE_CODES = [
    { el: 'donateAlipay', src: assetUrl('assets/img/donate/alipay.png') },
    { el: 'donateWechat', src: assetUrl('assets/img/donate/wechat.png') }
  ];
  let donateLoaded = false;

  function loadDonateCodes() {
    DONATE_CODES.forEach(function (c) {
      const img = ui[c.el];
      if (!img) return;
      const box = img.parentNode;
      img.onload = function () {
        if (img.naturalWidth > 0) box.classList.add('hasImg');
      };
      img.onerror = function () {
        box.classList.remove('hasImg');
      };
      if (!img.getAttribute('src')) img.setAttribute('src', c.src + '?v=1');
      else if (img.complete && img.naturalWidth > 0) box.classList.add('hasImg');
    });
    donateLoaded = true;
  }

  function openDonate() {
    if (!ui.donatePopup) return;
    if (!donateLoaded) loadDonateCodes();
    ui.donatePopup.classList.remove('hidden');
    hideItemTip();
  }

  function closeDonate() {
    if (ui.donatePopup) ui.donatePopup.classList.add('hidden');
  }

  /* 赞助入口开关：先暂时隐藏（按钮、弹窗、收款码都留在工程里，改回 true 就恢复） */
  const DONATE_ENABLED = false;

  /* ---------- 首页背景音乐：右手音量控制 ---------- */
  const MUSIC_SETTINGS_KEY = 'homeward.music.v1';
  let musicAudio = null;
  let musicVolume = 0.6;
  let sfxVolume = 0.7;
  let musicOpen = false;
  let musicMuted = false;
  (function readMusicSettings() {
    try {
      const raw = JSON.parse(window.localStorage.getItem(MUSIC_SETTINGS_KEY) || 'null');
      if (raw && typeof raw === 'object') {
        if (typeof raw.volume === 'number') musicVolume = clamp(raw.volume, 0, 1);
        if (typeof raw.sfxVolume === 'number') sfxVolume = clamp(raw.sfxVolume, 0, 1);
        musicMuted = !!raw.muted;
      }
    } catch (e) { /* 忽略 */ }
  })();
  function saveMusicSettings() {
    try {
      window.localStorage.setItem(MUSIC_SETTINGS_KEY, JSON.stringify({
        volume: musicVolume, sfxVolume: sfxVolume, muted: musicMuted
      }));
    } catch (e) { /* 忽略 */ }
  }
  function ensureBgm() {
    if (musicAudio) return musicAudio;
    musicAudio = new Audio(assetUrl('assets/audio/desert_loop.mp3'));
    musicAudio.loop = true;
    musicAudio.preload = 'auto';
    musicAudio.volume = musicMuted ? 0 : musicVolume;
    musicAudio.addEventListener('play', updateMusicUI);
    musicAudio.addEventListener('pause', updateMusicUI);
    return musicAudio;
  }
  function tryPlayBgm() {
    if (musicMuted || musicVolume <= 0) return;
    const a = ensureBgm();
    const p = a.play();
    if (p && p.catch) p.catch(function () { updateMusicUI(); });
  }

  /* ---------- 游戏音效 ---------- */
  const SFX_DEFS = {
    gachaSingle: { src: 'assets/audio/sfx_gacha_single_v1.wav', volume: 0.72 },
    run:         { src: 'assets/audio/sfx_run_loop_v1.wav',     volume: 0.34 },
    attack:      { src: 'assets/audio/sfx_attack_v1.wav',       volume: 0.62 },
    click:       { src: 'assets/audio/sfx_click_v1.wav',        volume: 0.46 },
    hover:       { src: 'assets/audio/sfx_hover_v1.wav',        volume: 0.30 },
    bloodRebound:{ src: 'assets/audio/sfx_blood_rebound_v1.wav',volume: 0.72 },
    // 拾取（装备 / 物品 / 金币 / 搜尸 / 战利品堆）：连点拾取要能叠音，见 SFX_VOICES
    pickup:      { src: 'assets/audio/sfx_pickup_v1.mp3',       volume: 0.66 }
  };
  /* 一个音效可以同时响几个"声部"：拾取这种会连着点的音效要叠音，别的单声道就够 */
  const SFX_VOICES = { pickup: 4 };
  const sfxPool = {};
  const sfxVoices = {};      // key -> Audio[]（sfxPool[key] === sfxVoices[key][0]）
  const sfxVoiceTurn = {};   // key -> 下一个要用第几个声部
  let runLoopAudio = null;
  let runLoopPlaying = false;
  let lastHoverSfxEl = null;
  let lastHoverSfxAt = 0;

  function ensureSfx(key) {
    if (!SFX_DEFS[key]) return null;
    if (sfxPool[key]) return sfxPool[key];
    const total = Math.max(1, SFX_VOICES[key] || 1);
    const voices = [];
    for (let i = 0; i < total; i++) {
      const a = new Audio(assetUrl(SFX_DEFS[key].src));
      a.preload = 'auto';
      a.volume = SFX_DEFS[key].volume;
      voices.push(a);
    }
    const a = voices[0];
    sfxPool[key] = a;
    sfxVoices[key] = voices;
    sfxVoiceTurn[key] = 0;
    return a;
  }
  function playSfx(key, volumeScale) {
    const def = SFX_DEFS[key];
    const a = ensureSfx(key);
    if (!def || !a) return false;
    const voices = sfxVoices[key] || [a];
    let audio = a;
    if (voices.length > 1) {
      // 优先用一个空闲声部；都还在响就轮着抢，保证"连着拾取"听得出是一下一下的
      let idle = null;
      for (let i = 0; i < voices.length; i++) {
        const v = voices[(sfxVoiceTurn[key] + i) % voices.length];
        if (v.paused || v.ended || v.currentTime === 0) { idle = v; sfxVoiceTurn[key] = (sfxVoiceTurn[key] + i + 1) % voices.length; break; }
      }
      audio = idle || voices[sfxVoiceTurn[key]];
      if (!idle) sfxVoiceTurn[key] = (sfxVoiceTurn[key] + 1) % voices.length;
    }
    try {
      audio.volume = clamp(def.volume * sfxVolume * (volumeScale === undefined ? 1 : volumeScale), 0, 1);
      audio.currentTime = 0;
      const p = audio.play();
      if (p && p.catch) p.catch(function () {});
    } catch (e) { return false; }
    return true;
  }
  function updateRunSfx(running) {
    if (running) {
      runLoopAudio = runLoopAudio || ensureSfx('run');
      if (!runLoopAudio) return;
      runLoopAudio.loop = true;
      runLoopAudio.volume = clamp(SFX_DEFS.run.volume * sfxVolume, 0, 1);
      if (sfxVolume <= 0) {
        if (runLoopPlaying) { runLoopAudio.pause(); runLoopPlaying = false; }
        return;
      }
      if (!runLoopPlaying) {
        runLoopPlaying = true;
        const p = runLoopAudio.play();
        if (p && p.catch) p.catch(function () { runLoopPlaying = false; });
      }
    } else if (runLoopPlaying && runLoopAudio) {
      runLoopPlaying = false;
      runLoopAudio.pause();
      try { runLoopAudio.currentTime = 0; } catch (e) { /* 忽略 */ }
    }
  }

  function updateMusicUI() {
    if (!ui.musicControl) return;
    const home = gameState === 'intro' || gameState === 'title';
    const wrap = ui.wrap || document.getElementById('wrap');
    const chatHead = document.querySelector('#chatPanel .chatHead');
    // 首页：右下角；进入游戏：移到聊天窗口顶部「世界频道」旁
    if (home && wrap && ui.musicControl.parentElement !== wrap) {
      wrap.appendChild(ui.musicControl);
    } else if (!home && chatHead && ui.musicControl.parentElement !== chatHead) {
      chatHead.insertBefore(ui.musicControl, ui.chatToggle || null);
    }
    ui.musicControl.classList.toggle('in-chat', !home);
    if (ui.chatPanel) ui.chatPanel.classList.toggle('music-open', !home && musicOpen);
    const chatVisible = !!(ui.chatPanel && !ui.chatPanel.classList.contains('hidden'));
    const show = home || (!home && chatVisible);
    ui.musicControl.classList.toggle('hidden', !show);
    ui.musicControl.classList.toggle('open', musicOpen);
    const pct = Math.round(musicVolume * 100);
    if (ui.musicVolume) ui.musicVolume.value = String(pct);
    if (ui.musicVolumeText) ui.musicVolumeText.textContent = (musicMuted || pct <= 0) ? '静音' : (pct + '%');
    const sfxPct = Math.round(sfxVolume * 100);
    if (ui.sfxVolume) ui.sfxVolume.value = String(sfxPct);
    if (ui.sfxVolumeText) ui.sfxVolumeText.textContent = sfxPct <= 0 ? '静音' : (sfxPct + '%');
    if (ui.btnMusic) {
      const playing = !!(musicAudio && !musicAudio.paused && !musicMuted && musicVolume > 0);
      ui.btnMusic.classList.toggle('playing', playing);
      ui.btnMusic.textContent = (musicMuted || pct <= 0) ? '🔇' : '♪';
      ui.btnMusic.title = '背景音乐：' + ((musicMuted || pct <= 0) ? '静音' : pct + '%');
    }
  }

  // 首页（路上休息场景 / 标题页）显示「赞助」按钮；进游戏后隐藏，免得挡住命令轮盘
  function updateDonateButton() {
    if (!ui.btnDonate) return;
    const loginOpen = ui.loginScreen && !ui.loginScreen.classList.contains('hidden');
    const popupOpen = ui.donatePopup && !ui.donatePopup.classList.contains('hidden');
    const show = DONATE_ENABLED && (gameState === 'intro' || gameState === 'title') && !loginOpen && !popupOpen;
    ui.btnDonate.classList.toggle('hidden', !show);
    updateMusicUI();
    updateBeianBar();
  }

  /* 备案信息条：只挂在首页（点角色登录前）和标题页，进游戏后收起，
     免得压住右下角命令轮盘和左下角聊天窗 */
  function updateBeianBar() {
    if (!ui.beianBar) return;
    const show = gameState === 'intro' || gameState === 'title';
    ui.beianBar.classList.toggle('hidden', !show);
    // 同时给舞台底部留出备案条的高度：备案信息就落在游戏画面下方，
    // 不会贴着路上的角色；进游戏后把高度收回去，画面恢复满屏
    const stageEl = document.getElementById('stage');
    if (stageEl && stageEl.classList.contains('beianOn') !== show) {
      stageEl.classList.toggle('beianOn', show);
      window.dispatchEvent(new Event('resize'));   // 舞台变矮了，HUD 重新排一次
    }
  }

  /* ============================================================
   * 首页状态（intro）：打开网页先看到"角色在路上休息"，
   * 点角色才弹出登录窗口（而不是一进来就糊玩家一脸登录框）
   * ============================================================ */
  function enterIntroIdle() {
    input.moving = false;
    player.vx = 0;
    playerAtkT = 0;
    playerResting = true;
    restPoseIdx = INTRO_REST_POSE;   // 固定用 player_rest_3.png 这张
    restPoseTimer = 0;
    preloadIntroPlayerFrames();      // 首页也要能看到角色（否则路上只有背景）
    updateDonateButton();
    hideIntroDialog();
    scheduleIntroDialog();           // 停在首页 10 秒还没登录 → 引路人开口
  }

  /* ============================================================
   * 首页引导剧情：停在首页 10 秒，且这 10 秒内没有被弹出账号窗口，
   * 就出现引路人对白（点它可以进登录窗口）。
   * ============================================================ */
  const INTRO_DIALOG_DELAY_MS = 10000;
  let introDialogTimer = null;

  function cancelIntroDialogTimer() {
    if (introDialogTimer) {
      clearTimeout(introDialogTimer);
      introDialogTimer = null;
    }
  }

  function introDialogVisible() {
    return !!ui.introDialog && !ui.introDialog.classList.contains('hidden');
  }

  // 收起对白并取消计时（已经登录 / 离开首页时用）
  function hideIntroDialog() {
    cancelIntroDialogTimer();
    if (ui.introDialog) ui.introDialog.classList.add('hidden');
  }

  // 重新计时：只有在"还停在首页、账号窗口也没开"的时候才会真的弹出来
  function scheduleIntroDialog() {
    cancelIntroDialogTimer();
    if (!ui.introDialog) return;
    introDialogTimer = setTimeout(function () {
      introDialogTimer = null;
      if (gameState !== 'intro') return;
      if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return;
      ui.introDialog.classList.remove('hidden');
    }, INTRO_DIALOG_DELAY_MS);
  }

  // 点到角色身上了吗（用角色贴图的实际大小做判定，留一点余量）
  function hitPlayerAt(sx, sy) {
    const scale = playerFx.scale * caveScale();
    const px = player.worldX - cameraX();
    const halfW = (PLAYER_FRAME.w * scale) * 0.42;
    return Math.abs(sx - px) <= halfW &&
      sy >= GROUND_Y - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * scale - 12 &&
      sy <= GROUND_Y + 26 * scale;
  }

  function openLoginFromIntro() {
    if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return;
    hideIntroDialog();                 // 登录窗口一开，引路人就让位
    ui.title.classList.add('hidden');
    ui.loginScreen.classList.remove('hidden');
    if (ui.loginHelp) ui.loginHelp.classList.add('hidden');
    updateDonateButton();
    if (ui.loginUser) {
      ui.loginUser.focus();
      if (!ui.loginUser.value) ui.loginUser.value = '';
    }
  }

  // 点登录窗口之外的空白处：收起登录窗口，回到首页那个"角色在路上休息"的场景
  function closeLoginFromIntro() {
    if (!ui.loginScreen || ui.loginScreen.classList.contains('hidden')) return;
    if (loginBusy) return;                 // 正在连服务器，别把窗口中途收掉
    ui.loginScreen.classList.add('hidden');
    if (ui.loginError) ui.loginError.classList.add('hidden');
    if (ui.loginHelp) ui.loginHelp.classList.add('hidden');
    updateDonateButton();                  // 「赞助」按钮和底部备案条重新出现
    scheduleIntroDialog();                 // 又回到首页了，10 秒后引路人会再开口
  }

  // 登录：优先连服务器（账号 + 云端存档 + 聊天），连不上再回退到本地单机档案
  async function doLogin() {
    if (ui.loginScreen.classList.contains('hidden')) return; // 已经登录过了
    if (loginBusy) return;
    const name = (ui.loginUser.value || '').trim();
    const pwd = (ui.loginPwd.value || '').trim();
    /* 服务器地址：登录框里的「服务器 IP」已按需求移除 —— 默认走当前页面同源
     *   （自有域名下由 nginx 反代 /api、/ws；挂在第三方平台时 normalizeServerUrl 会自动回落到正式域名）。
     *   本机调试/自动化可以用 __debugSetServer() 指定地址。 */
    const ip = ui.loginIp ? (ui.loginIp.value || '').trim() : '';
    if (!name) { showLoginError('请填写账号'); ui.loginUser.focus(); return; }
    if (!pwd) { showLoginError('请填写密码'); ui.loginPwd.focus(); return; }

    const serverUrl = netServerFromInput(ip);
    // 页面被第三方平台托管时，登录框默认填的可能是平台域名（location.host），
    // 而平台域名上没有我们的后端。连不上时自动再试一次正式域名，试通了就记住它。
    const officialUrl = defaultServerOrigin();
    const candidates = serverUrl === officialUrl ? [serverUrl] : [serverUrl, officialUrl];
    loginBusy = true;
    setLoginButton('连接服务器…', true);
    let loginResult = null;
    let serverErr = null;
    let usedUrl = serverUrl;
    for (let ci = 0; ci < candidates.length; ci++) {
      try {
        const api = createApiClient({ baseUrl: candidates[ci] });
        try {
          loginResult = await api.loginWithDevice(name, pwd, deviceId());
        } catch (err) {
          if (err.code === 'no_user') {
            // 服务器上还没有这个账号 → 注册后用同样的账号密码登录（等于新建档案）
            await api.register(name, pwd);
            loginResult = await api.loginWithDevice(name, pwd, deviceId());
          } else {
            throw err;
          }
        }
        net.api = api;
        net.token = loginResult.token;
        net.accountOnline = true;
        net.lastError = '';
        usedUrl = candidates[ci];
        netRememberServer(usedUrl);
        serverErr = null;
        break;
      } catch (err) {
        serverErr = err;
        net.accountOnline = false;
        net.token = '';
        // 服务器明确说"账号被别人占了 / 密码不对 / 被限流" → 这是真错误，不能静默当单机
        if (err && (err.code === 'user_exists' || err.code === 'bad_pwd' || err.code === 'locked' ||
          err.code === 'already_online')) {
          loginBusy = false;
          setLoginButton('登录', false);
          showLoginError('服务器拒绝：' + err.message);
          return;
        }
        net.lastError = err && err.message ? err.message : '连接失败';
        // 只有"连不上/被挡"才换下一个地址重试；其它错误直接停
        if (!err || err.code !== 'offline') break;
      }
    }
    if (loginResult && usedUrl !== serverUrl && ui.loginIp) {
      ui.loginIp.value = usedUrl.replace(/^https?:\/\//, '');
      showToast('当前页面的域名上没有服务器，已自动改用 ' + usedUrl + '。');
    }
    loginBusy = false;
    setLoginButton('登录', false);
    if (!loginResult) {
      // 连不上服务器 → 走本地单机档案（和以前完全一样）
      showToast('连不上服务器（' + net.lastError + '），已切换到本地单机档案。');
      // 登录框里没有服务器输入框了：本地回退分支也把"实际用的地址"记进档案，标题页才显示得出来
      doLocalLogin(name, pwd, (ip || String(net.serverUrl || '').replace(/^https?:\/\//, '')));
      return;
    }
    finishServerLogin(name, pwd, ip, loginResult);
  }

  function setLoginButton(text, disabled) {
    if (!ui.btnLogin) return;
    ui.btnLogin.textContent = text;
    ui.btnLogin.disabled = !!disabled;
  }

  /* ---------- 全服重置纪元 ----------
   * 服务器执行 tools/reset-all.cjs 会把 data/meta.json 的 epoch +1，登录接口会带回来。
   * 客户端发现服务器纪元比自己记的大 → 说明服务器清过档，于是把本机所有账号与存档也清掉，
   * 避免"服务器没存档 → 用本地档推上去"把旧进度复活。
   * 老服务器不带这个字段时 epoch=0，行为跟以前完全一致。 */
  const WIPE_EPOCH_KEY = 'homeward.wipeEpoch.v1';

  function localWipeEpoch() {
    try { return Math.max(0, Math.floor(Number(window.localStorage.getItem(WIPE_EPOCH_KEY)) || 0)); }
    catch (e) { return 0; }
  }

  function applyServerWipeEpoch(rawEpoch) {
    const epoch = Math.max(0, Math.floor(Number(rawEpoch) || 0));
    if (!epoch) return false;                 // 老服务器 / 没重置过：不动本地
    const local = localWipeEpoch();
    if (epoch <= local) {
      if (epoch < local) {                    // 换了服务器（纪元反而更小）→ 以服务器的为准
        try { window.localStorage.setItem(WIPE_EPOCH_KEY, String(epoch)); } catch (e) { /* 忽略 */ }
      }
      return false;
    }
    resetAllSaves();                          // 本机所有档案 + 存档一起清
    try { window.localStorage.setItem(WIPE_EPOCH_KEY, String(epoch)); } catch (e) { /* 忽略 */ }
    return true;
  }

  // 服务器登录成功：决定用云端存档还是本地较新的存档
  function finishServerLogin(name, pwd, ip, loginResult) {
    const hash = accountHash(name, pwd);
    const key = saveKeyFor(hash);
    // 服务器做过"全服重置"（纪元变大）→ 先把本机所有旧档作废，
    // 否则下面会走 local-first，把浏览器里那份旧存档又推回云端
    const wipedByServer = applyServerWipeEpoch(loginResult && loginResult.saveEpoch);
    let localSave = null;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) localSave = JSON.parse(raw);
    } catch (e) { /* 忽略 */ }

    const serverSave = loginResult.save || null;
    const serverSavedAt = Number(loginResult.savedAt) || Number(serverSave && serverSave.savedAt) || 0;
    const localSavedAt = Number(localSave && localSave.savedAt) || 0;
    let source = 'new';
    let localIgnored = false;   // 本机那份被忽略（云端没有存档，或本机那份更旧/更新）

    /* 以云端为权威副本：登录后一律用服务器的存档。
       以前这里还有"本地更新 → 推给服务器"的分支，结果老客户端会把浏览器里那份旧存档
       覆盖到云端（服务器清过档也会被推回来）。那个行为已经去掉，客户端只读不写。 */
    if (serverSave) {
      try { window.localStorage.setItem(key, JSON.stringify(serverSave)); } catch (e) { /* 忽略 */ }
      source = 'server';
      // 本机那份比云端还新：只提示，绝不上传（避免覆盖云端）
      localIgnored = !!(localSave && localSavedAt > serverSavedAt);
    } else if (localSave) {
      // 云端没有存档 → 同样以云端为准：把本机那份丢掉，从零开始。
      // （只"不上传"是不够的——之后第一次自动存档照样会把它写进云端）
      try { window.localStorage.removeItem(key); } catch (e) { /* 忽略 */ }
      localSave = null;
      localIgnored = true;
    }

    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = source === 'new';
    // 昵称：服务器上有就用服务器的；服务器没有则用本机缓存（并尝试同步上去）
    const serverNick = (loginResult.user && loginResult.user.nickname) || '';
    const localNick = loadLocalNickname();
    account.nickname = serverNick || localNick || '';
    if (serverNick) {
      saveLocalNickname(serverNick);
    } else if (localNick && net.accountOnline && net.api && net.token) {
      net.api.setNickname(net.token, localNick).catch(function () { /* 重名或含屏蔽词就算了 */ });
    }
    updateNickUI();
    registerAccount(name, hash, { ip: ip });
    loadSave();
    enterTitleScreen(ip);

    const level = player.level || 1;
    if (wipedByServer) {
      showToast('服务器已重置全服数据，本机旧存档作废：' + name + ' 从零开始。');
    } else if (source === 'server') {
      showToast(localIgnored
        ? ('欢迎回来，' + name + '（以云端存档为准 · 境界 ' + level + '；本机那份旧存档已忽略，不会再上传覆盖）')
        : ('欢迎回来，' + name + '（已从服务器读取存档 · 境界 ' + level + '）'));
    } else {
      showToast('已创建云端档案：' + name + '（从零开始）');
    }
  }

  // 服务器不可用时的本地档案登录（原逻辑）
  function doLocalLogin(name, pwd, ip) {

    const hash = accountHash(name, pwd);
    const sameName = findAccountByName(name);
    const sameAccount = sameName.filter(function (a) { return a.hash === hash; })[0];

    // ① 账号 + 密码都对得上 → 继续这个档案
    if (sameAccount) {
    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = false;
    account.nickname = loadLocalNickname();
    updateNickUI();
    registerAccount(name, hash, { ip: ip });   // 顺便刷新 lastPlayed
      loadSave();                                 // 读这个账号的存档
      enterTitleScreen(ip);
      showToast('欢迎回来，' + name + '（继续原档案，境界 ' + (player.level || 1) + '）');
      return;
    }

    // ② 账号名已被占用（唯一性）：不允许用同名新建档案，直接提示
    if (sameName.length) {
      showLoginError('账号名「' + sameName[0].name + '」已被占用（账号名唯一，不能重复创建）。' +
        '如果这是你的账号，请填写正确密码后登录；想换账号请换一个名字。');
      loginConfirmNew = false;
      return;
    }

    // ③ 全新账号 → 新档案，从零开始
    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = true;
    account.nickname = loadLocalNickname();
    updateNickUI();
    // 新档案：先把旧版单存档彻底删掉，再把进度归零，保证真的从零开始
    try { window.localStorage.removeItem(LEGACY_SAVE_KEY); } catch (e) { /* 忽略 */ }
    resetProgressState();
    registerAccount(name, hash, { ip: ip });
    loadSave();                                   // 新档案没有数据 → 走默认值
    enterTitleScreen(ip);
    showToast('已创建新档案：' + name + '（从零开始）');
  }

  // 登录成功 → 露出标题页（开始游戏）
  // 删档：先弹确认，确认后才真的清
  function askWipeSaves() {
    if (ui.wipePopup) ui.wipePopup.classList.remove('hidden');
  }

  function closeWipeConfirm() {
    if (ui.wipePopup) ui.wipePopup.classList.add('hidden');
  }

  function doWipeSaves() {
    resetAllSaves();
    account.name = '';
    account.ip = '';
    account.hash = '';
    account.isNew = false;
    resetProgressState();
    closeWipeConfirm();
    if (ui.loginUser) ui.loginUser.value = '';
    if (ui.loginPwd) ui.loginPwd.value = '';
    if (ui.loginUser) ui.loginUser.focus();
    showLoginError('已删除本机所有档案，请重新输入账号创建新档案。');
    showToast('已删除本机所有档案。');
  }

  function enterTitleScreen(ip) {
    hideIntroDialog();
    ui.loginError.classList.add('hidden');
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.remove('hidden');
    gameState = 'title';        // 离开首页（角色休息）状态
    updateDonateButton();
    // 账号确定后再连聊天（昵称要用账号名）；单机模式保持本地聊天
    if (net.accountOnline) connectChat();
    updateNickUI();
    if (ui.btnLogin) ui.btnLogin.textContent = '登录';
    loginConfirmNew = false;
    if (ui.titleUser) {
      ui.titleUser.textContent = '当前账号：' + account.name +
        '　·　服务器：' + (ip || account.ip) +
        '　·　' + (account.isNew ? '新档案（从零开始）' : '继续原档案 · 境界 ' + (player.level || 1));
    }
    if (ui.start) ui.start.focus();
  }

  function updateLoadUi() {
    if (!ui.loadPanel) return;
    const pct = Math.round(loadTask.shown * 100);
    ui.loadFill.style.width = pct + '%';
    ui.loadPct.textContent = pct + '%';
    ui.loadTip.textContent = loadTask.tip || '正在准备场景…';
  }

  function beginStartGame() {
    if (loadTask.active) return;
    if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return; // 必须先登录
    loadTask.active = true;
    loadTask.real = loaded / ASSETS.length;
    loadTask.shown = 0;
    loadTask.t = 0;
    loadTask.tip = '正在载入场景贴图…';
    loadTask.onDone = cgGateThenStart;    // 资源加载完 → （需要的话先播开场 CG）→ 进入场景
    ui.title.classList.add('hidden');
    ui.loadPanel.classList.remove('hidden');
    updateLoadUi();
    if (loaded < ASSETS.length) loadAssets();
    else loadTask.real = 1;
  }

  function updateLoading(dt) {
    if (!loadTask.active) return;
    loadTask.t += dt;
    // 进度按「最短展示时间」匀速推进，且不会超过真实加载进度：
    // 资源秒载完时也能看到进度条从头走到尾；资源大时则真实等待
    const ramp = Math.min(1, loadTask.t / LOAD_MIN_TIME);
    const target = Math.min(loadTask.real, ramp);
    loadTask.shown += (target - loadTask.shown) * Math.min(1, dt * 10);
    if (loadTask.shown > target) loadTask.shown = target;
    if (loadTask.real >= 1 && ramp >= 1 && loadTask.shown > 0.985) loadTask.shown = 1;
    updateLoadUi();
    if (loadTask.shown >= 0.999 && loadTask.real >= 1 && loadTask.t >= LOAD_MIN_TIME) {
      loadTask.active = false;
      ui.loadPanel.classList.add('hidden');
      const done = loadTask.onDone;
      loadTask.onDone = null;
      if (done) done();
    }
  }

  /* ============================================================
   * 开场 CG（幻灯片 · 静态插画蒙太奇）
   *   位置：登录校验成功 → 点「开始游戏」→ 资源加载完成 → **开场 CG** → 进入主场景
   *   表现：每张静态插画 + 缓慢推拉（Ken Burns）+ 图间淡入淡出；
   *         旁白逐行浮现，底部一行字幕；右上角随时可跳过（Esc / 空格也行）。
   *   插画：优先读 assets/img/cg/cg_N.png（后续用 AI 出的图直接丢进去即可）；
   *         缺图时用程序绘制的剪影分镜兜底，所以现在没有美术也能跑。
   * ============================================================ */
  const CG_SLIDE_FADE = 0.9;     // 每张图的淡入 / 淡出时长（秒）
  const CG_MIN_SEC = 6.5;        // 单张最短停留
  const CG_MAX_SEC = 12;         // 单张最长停留
  const cg = {
    active: false,
    i: 0,
    t: 0,
    dur: CG_MIN_SEC,
    onDone: null,
   fadeOut: -1,      // > 0 表示正在淡出（跳过 / 播完）
   pre: 0,           // > 0 表示还在等立绘预载（先压住画面，避免闪一下主场景）
   images: {},
   artTried: false,
   shownLines: -1
 };

  /* 每张图停留多久：优先用分镜自己写的 dur（现在只有标题，没有旁白可估字数了）；
   * 老数据（带 narration 的）仍按字数估，兼容以前的分镜表。 */
  function cgSlideDuration(slide) {
    if (slide && typeof slide.dur === 'number' && slide.dur > 0) return slide.dur;
    const chars = (slide.narration || []).join('').length;
    return clamp(4.4 + chars * 0.058, CG_MIN_SEC, CG_MAX_SEC);
  }
  /* 是否要播开场 CG：档案没看过就播。
   *   调试/自动化开关：URL 带 ?skiplogin=1（测试入口）时默认不播，
   *   想看就把开关打开：?skiplogin=1&cg=1；也可以清掉档案（新档案必播）。 */
  function cgShouldPlay() {
    try {
      const q = new URLSearchParams(window.location.search || '');
      if (q.get('skiplogin') === '1' && q.get('cg') !== '1') return false;
    } catch (e) { /* 取不到参数就当正常玩家 */ }
    return !cgSeen;
  }
  // 真正开始播（onDone：播完 / 跳过后回到流程）
  function playCgIntro(onDone) {
    if (!ui.cgScreen || cg.active) return false;
    cg.active = true;
    cg.i = 0;
    cg.t = 0;
    cg.dur = cgSlideDuration(CG_SLIDES[0]);
    cg.fadeOut = -1;
    cg.shownLines = -1;
    cg.onDone = onDone || null;
    cgLoadArt();
    /* 第一张分镜要用的立绘先拉下来再开播（最多等 1.5 秒）。
     * 等的时候画面压黑，所以不会先闪一下主场景再跳回 CG；
     * 后面的张由 cgPrePaintNext 提前下载，开场不会被整包素材拖住。 */
    cg.pre = 1;
    cgPreloadArt(CG_SLIDE_ART[CG_SLIDES[0].id], 1500).then(function () {
      if (!cg.active) return;
      cg.pre = 0;
      cgRenderText();
    });
    cgRenderChrome();
    ui.cgScreen.classList.remove('hidden', 'fading');
    cgRenderText();
    return true;
  }
  function cgLoadArt() {
    if (cg.artTried) return;
    cg.artTried = true;
    /* 先探第一张：能拿到就说明正式插画到位了，再把 8 张都拉下来；
     * 没有正式插画时不逐张去试，省掉 7 条 404（正式插画是可选的美化项）。 */
    const probe = new Image();
    probe.onload = function () {
      cg.images[CG_SLIDES[0].id] = probe;
      CG_SLIDES.slice(1).forEach(function (s) {
        const im = new Image();
        im.onload = function () { cg.images[s.id] = im; };
        im.onerror = function () { /* 缺这一张：这张用程序绘制的分镜兜底 */ };
        im.src = assetUrl(CG_ART_DIR + s.art);
      });
    };
    probe.src = assetUrl(CG_ART_DIR + CG_SLIDES[0].art);
  }
  // 跳过（也用于播完最后一帧）
  function cgSkip() {
    if (!cg.active || cg.fadeOut >= 0) return false;
    cg.fadeOut = 0.45;
    if (ui.cgScreen) ui.cgScreen.classList.add('fading');
    cgMarkSeen();
    return true;
  }
  function cgFinish() {
    cg.active = false;
    if (ui.cgScreen) { ui.cgScreen.classList.add('hidden'); ui.cgScreen.classList.remove('fading'); }
    cgMarkSeen();
    const done = cg.onDone;
    cg.onDone = null;
    if (done) done();
  }
  function cgMarkSeen() {
    if (cgSeen) return;
    cgSeen = true;
    saveGame();     // 看过就写档：下次登录不再重复播
  }
  // 底部进度：第几张 / 标题 / 小圆点
  function cgRenderChrome() {
    if (ui.cgIndex) ui.cgIndex.textContent = (cg.i + 1) + ' / ' + CG_SLIDES.length;
    if (ui.cgTitle) ui.cgTitle.textContent = CG_SLIDES[cg.i].title || '';
    if (ui.cgDots) {
      ui.cgDots.textContent = '';
      CG_SLIDES.forEach(function (s, i) {
        const d = document.createElement('i');
        if (i === cg.i) d.className = 'on';
        ui.cgDots.appendChild(d);
      });
    }
  }
  // 旁白 + 字幕（旁白逐行浮现）
  function cgRenderText() {
    const slide = CG_SLIDES[cg.i];
    if (!slide) return;
    /* 按需求：CG 只留标题（cgTitle）—— 旁白与字幕那两行小字不再渲染。
     *   （文案还留在 defs.js 的 CG_SLIDES 里当设定资料，需要时随时能再打开） */
    cg.shownLines = 0;
    cgRenderChrome();
  }
  function cgRevealLines(t) {
    // 旁白 / 字幕已移除：这里不再逐行浮现（留空函数，主循环照旧调用）
    void t;
  }
  // 每帧推进（由主循环调用；后台也会照常走）
  function updateCg(dt) {
    if (!cg.active) return;
    if (cg.pre) return;              // 还在等素材：时间不推进，旁白也不浮
    if (cg.fadeOut >= 0) {
      cg.fadeOut -= dt;
      if (cg.fadeOut <= 0) cgFinish();
      return;
    }
    cg.t += dt;
    cgRevealLines(cg.t);
    if (cg.t >= cg.dur - CG_SLIDE_FADE) {
      if (cg.i >= CG_SLIDES.length - 1) { cgSkip(); return; }
      cg.i += 1;
      cg.t = 0;
      cg.dur = cgSlideDuration(CG_SLIDES[cg.i]);
      cgRenderText();
    }
  }
  let cgTime = 0;      // 特效相位（尘埃 / 灵光流动）
  function drawCgFrame() {
    if (!cg.active || !ui.cgCanvas) return;
    const g = ui.cgCanvas.getContext('2d');
    const W = ui.cgCanvas.width, H = ui.cgCanvas.height;
    cgTime += 1 / 60;
    g.clearRect(0, 0, W, H);
    g.fillStyle = '#04060a';
    g.fillRect(0, 0, W, H);
    // 还在等立绘预载：只给一片压黑的底 + 一点微光，等好了再开始推拉
    if (cg.pre) {
      const gl = g.createRadialGradient(W * 0.5, H * 0.5, 20, W * 0.5, H * 0.5, H * 0.8);
      gl.addColorStop(0, 'rgba(120, 140, 170, ' + (0.05 + 0.03 * Math.sin(cgTime * 2)).toFixed(3) + ')');
      gl.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = gl;
      g.fillRect(0, 0, W, H);
      return;
    }
    const cur = CG_SLIDES[cg.i];
    const fadeIn = clamp(cg.t / CG_SLIDE_FADE, 0, 1);
    // 上一张淡出 + 本张淡入（真正的交叉溶解）
    if (cg.i > 0 && fadeIn < 1) {
      cgDrawSlide(g, CG_SLIDES[cg.i - 1], (1 - fadeIn) * 0.9, 1, W, H);
    }
    cgDrawSlide(g, cur, fadeIn, 0, W, H);
    // 飘动的尘埃 / 灵光（让静态画面有呼吸感）
    g.save();
    g.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 46; i++) {
      const seed = i * 97.13;
      const x = ((seed * 7.3) % W + cgTime * (6 + (i % 5) * 3)) % W;
      const y = (H * 0.25) + ((seed * 3.7) % (H * 0.6)) + Math.sin(cgTime * 0.5 + i) * 12;
      const a = 0.05 + 0.05 * Math.sin(cgTime * 1.3 + i);
      g.fillStyle = 'rgba(255, 236, 200, ' + a.toFixed(3) + ')';
      g.beginPath();
      g.arc(x, y, 1.2 + (i % 3) * 0.5, 0, Math.PI * 2);
      g.fill();
    }
    g.restore();
  }
  /* 画一张幻灯片：有美术就用美术（带 Ken Burns 推拉），没有就用程序绘制的分镜 */
  /* 正式插画的"适配"：宽高比和画框差太多时不能硬拉 —— 957×231 这种 4.14:1 的横条
   *   硬拉成 16:9 会被上下抻长 2.3 倍，人物全变形。
   *   差得不多（≤12%）→ 直接铺满（肉眼看不出）；
   *   差得多 → 居中完整显示，四周用"同一张图放大 + 模糊压暗"补满，不会出现黑边。 */
  function cgFitArt(g, img, W, H) {
    const iw = img.naturalWidth, ih = img.naturalHeight;
    const want = W / H, got = iw / ih;
    if (Math.abs(got - want) / want <= 0.12) { g.drawImage(img, 0, 0, W, H); return 'fill'; }
    g.save();
    try {                                  // 底：放大模糊压暗，把画面铺满
      g.filter = 'blur(' + Math.round(H * 0.045) + 'px) brightness(.6) saturate(.85)';
      const k = Math.max(W / iw, H / ih) * 1.14;
      g.drawImage(img, (W - iw * k) / 2, (H - ih * k) / 2, iw * k, ih * k);
      g.filter = 'none';
    } catch (e) { /* 浏览器不支持 filter 就只画主体 */ }
    g.restore();
    const k2 = Math.min(W / iw, H / ih);   // 主体：完整放进画框
    const dw = iw * k2, dh = ih * k2;
    if (dh < H - 1) {                      // 上下留白的地方压一道同色暗幕，字幕压上去更清楚
      g.save();
      g.fillStyle = 'rgba(6,8,12,.28)';
      g.fillRect(0, 0, W, (H - dh) / 2);
      g.fillRect(0, H - (H - dh) / 2, W, (H - dh) / 2);
      g.restore();
    }
    g.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
    return 'contain';
  }
  function cgDrawSlide(g, slide, alpha, dir, W, H) {
    if (!slide || alpha <= 0.01) return;
    const p = clamp(cg.t / Math.max(0.001, cg.dur), 0, 1);   // 0→1 推拉进度
    const zoom = 1.045 + 0.085 * p;
    const panX = (dir >= 1 ? -1 : 1) * (p - 0.5) * 46;
    const panY = -6 + p * 12;
    g.save();
    g.globalAlpha = alpha;
    g.translate(W / 2 + panX, H / 2 + panY);
    g.scale(zoom, zoom);
    g.translate(-W / 2, -H / 2);
    const img = cg.images[slide.id];
    if (img && img.complete && img.naturalWidth > 0) {
      cgFitArt(g, img, W, H);
    } else {
      // 程序绘制的分镜：用预渲染好的位图（噪声/体积光只算一次）
      const bmp = cgSlideBitmap(slide);
      if (bmp) g.drawImage(bmp, 0, 0, W, H);
    }
    g.restore();
    // 每张图自带的一层氛围光晕（统一色调）
    g.save();
    g.globalAlpha = alpha * 0.5;
    const gr = g.createRadialGradient(W * 0.5, H * 0.42, 40, W * 0.5, H * 0.42, H * 0.85);
    gr.addColorStop(0, 'rgba(255, 232, 190, .10)');
    gr.addColorStop(1, 'rgba(0, 0, 0, 0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, W, H);
    g.restore();
  }
  /* ============================================================
   * 分镜绘画管线（程序绘制的"绘本插画"）
   *   要点：不追求矢量剪影那种硬边，而是分多层画出一张完整的画：
   *     天光渐变 → 体积云 → 丁达尔光柱 → 远景山脊（空气透视）→ 中景 →
   *     地面纹理（噪声）→ 植被/碎石 → 主体（带关节的人形/巨兽）→ 体积光斑 →
   *     泛光(bloom) → 调色 + 暗角 + 颗粒
   *   由于要逐像素算噪声，**每张只在第一次出现时画一次**并缓存成位图，
   *   之后每帧只做缓慢推拉（Ken Burns）贴图，性能不受影响。
   * ============================================================ */
  const CG_PAINT_W = 1536;     // 预渲染分辨率（比舞台大一点，推拉时不糊）
  const CG_PAINT_H = 864;
  const cgBitmaps = {};        // slideId -> canvas
  const cgPainting = {};       // slideId -> true（正在画）

  // ---------- 基础工具 ----------
  function cgHash(x, y, seed) {
    const n = Math.sin(x * 127.1 + y * 311.7 + seed * 74.7) * 43758.5453;
    return n - Math.floor(n);
  }
  function cgSmooth(t) { return t * t * (3 - 2 * t); }
  // 二维值噪声（双线性插值）
  function cgNoise(x, y, seed) {
    const xi = Math.floor(x), yi = Math.floor(y);
    const xf = cgSmooth(x - xi), yf = cgSmooth(y - yi);
    const a = cgHash(xi, yi, seed), b = cgHash(xi + 1, yi, seed);
    const c = cgHash(xi, yi + 1, seed), d = cgHash(xi + 1, yi + 1, seed);
    return (a * (1 - xf) + b * xf) * (1 - yf) + (c * (1 - xf) + d * xf) * yf;
  }
  // 分形噪声（多八度）
  function cgFbm(x, y, seed, oct, gain) {
    let s = 0, amp = 1, f = 1, norm = 0;
    const octaves = oct || 4;
    for (let i = 0; i < octaves; i++) {
      s += cgNoise(x * f, y * f, seed + i * 7.3) * amp;
      norm += amp;
      amp *= (gain === undefined ? 0.5 : gain);
      f *= 2;
    }
    return s / norm;
  }
  function cgRgb(r, gg, b, a) { return 'rgba(' + r + ',' + gg + ',' + b + ',' + a + ')'; }

  // 天空：多段渐变 + 太阳/裂隙的光晕
  function cgSky(g, W, H, stops) {
    const gr = g.createLinearGradient(0, 0, 0, H);
    stops.forEach(function (s) { gr.addColorStop(s[0], s[1]); });
    g.fillStyle = gr;
    g.fillRect(0, 0, W, H);
  }
  function cgLight(g, x, y, r, rgb, a) {
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, 'rgba(' + rgb + ',' + a + ')');
    gr.addColorStop(0.35, 'rgba(' + rgb + ',' + (a * 0.42) + ')');
    gr.addColorStop(1, 'rgba(' + rgb + ',0)');
    g.fillStyle = gr;
    g.beginPath();
    g.arc(x, y, r, 0, Math.PI * 2);
    g.fill();
  }
  /* 体积云：逐像素 fbm 算在半分辨率的临时画布上，再放大贴回主画布。
   * （云本来就是软的，半分辨率肉眼看不出差别，但快 4 倍） */
  function cgCloudLayer(g, W, H, yTop, yBot, seed, rgbTop, rgbBot, cover, soft) {
    const bandH = Math.max(4, Math.round(yBot - yTop));
    const k = 0.5;
    const cw = Math.max(4, Math.round(W * k)), ch = Math.max(4, Math.round(bandH * k));
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tg = tmp.getContext('2d');
    const img = tg.createImageData(cw, ch);
    const d = img.data;
    for (let y = 0; y < ch; y++) {
      const v = y / ch;
      const band = Math.sin(Math.PI * Math.min(1, v * soft));
      for (let x = 0; x < cw; x++) {
        const n = cgFbm((x / k) * 0.0032, ((y / k) + yTop) * 0.0075 + seed, seed, 4, 0.55);
        let a = (n - (1 - cover)) * 3.1 * band;
        if (a <= 0.012) continue;
        if (a > 1) a = 1;
        const i = (y * cw + x) * 4;
        d[i] = rgbTop[0] + (rgbBot[0] - rgbTop[0]) * v;
        d[i + 1] = rgbTop[1] + (rgbBot[1] - rgbTop[1]) * v;
        d[i + 2] = rgbTop[2] + (rgbBot[2] - rgbTop[2]) * v;
        d[i + 3] = Math.round(a * 235);
      }
    }
    tg.putImageData(img, 0, 0);
    g.save();
    g.imageSmoothingEnabled = true;
    g.drawImage(tmp, 0, 0, cw, ch, 0, yTop, W, bandH);
    g.restore();
  }
  /* 山脊：用噪声做真实起伏的轮廓，带"远处更淡更蓝"的空气透视 + 向光侧轮廓光 */
  function cgRidge(g, W, H, opt) {
    const baseY = opt.baseY, amp = opt.amp, color = opt.color;
    const seed = opt.seed, detail = opt.detail || 130, rim = opt.rim;
    const prof = [];
    for (let x = 0; x <= W; x += 3) {
      const t = x / W;
      const n = cgFbm(t * 5.2 + seed, seed * 0.37, seed, 4, 0.5);
      const big = Math.sin(t * Math.PI * (1.1 + (seed % 3) * 0.35) + seed) * 0.5 + 0.5;
      prof.push({ x: x, y: baseY - amp * (0.34 * big + 0.76 * n) });
    }
    g.save();
    g.beginPath();
    g.moveTo(0, H);
    prof.forEach(function (p) { g.lineTo(p.x, p.y); });
    g.lineTo(W, H);
    g.closePath();
    // 山体本身：上亮下暗
    const minY = Math.min.apply(null, prof.map(function (p) { return p.y; }));
    const grd = g.createLinearGradient(0, minY, 0, H);
    grd.addColorStop(0, color[0]);
    grd.addColorStop(1, color[1]);
    g.fillStyle = grd;
    g.fill();
    // 向光侧的轮廓光：沿山脊再描一条更亮的细线
    if (rim) {
      g.globalCompositeOperation = 'lighter';
      g.strokeStyle = rim;
      g.lineWidth = opt.rimW || 2;
      g.beginPath();
      prof.forEach(function (p, i) { if (i === 0) g.moveTo(p.x, p.y); else g.lineTo(p.x, p.y); });
      g.stroke();
    }
    g.restore();
    // 山脚雾气：把层与层分开，立刻有纵深
    if (opt.haze) {
      const hz = g.createLinearGradient(0, baseY - amp * 0.5, 0, baseY + 40);
      hz.addColorStop(0, 'rgba(' + opt.haze + ',0)');
      hz.addColorStop(0.55, 'rgba(' + opt.haze + ',' + (opt.hazeA || 0.5) + ')');
      hz.addColorStop(1, 'rgba(' + opt.haze + ',0)');
      g.fillStyle = hz;
      g.fillRect(0, baseY - amp * 0.5, W, amp * 0.5 + 41);
    }
  }
  // 丁达尔光柱：从光源 (x,y) 往下/往外的锥形光（不需要画布宽高）
  function cgGodRays(g, x, y, len, spread, n, rgb, alpha) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const ang = (t - 0.5) * spread;
      const w0 = 8 + Math.abs(Math.sin(i * 3.1)) * 26;
      const w1 = w0 * (2.4 + Math.abs(Math.cos(i * 1.7)));
      const x1 = x + Math.sin(ang) * len;
      const y1 = y + Math.cos(ang) * len;
      const a = alpha * (0.35 + 0.65 * Math.abs(Math.sin(i * 2.3)));
      const grd = g.createLinearGradient(x, y, x1, y1);
      grd.addColorStop(0, 'rgba(' + rgb + ',' + a + ')');
      grd.addColorStop(1, 'rgba(' + rgb + ',0)');
      g.fillStyle = grd;
      g.beginPath();
      g.moveTo(x - w0 / 2, y);
      g.lineTo(x + w0 / 2, y);
      g.lineTo(x1 + w1 / 2, y1);
      g.lineTo(x1 - w1 / 2, y1);
      g.closePath();
      g.fill();
    }
    g.restore();
  }
  // 地面：噪声沙地纹理（同样半分辨率算再放大）
  function cgGround(g, W, H, top, rgb1, rgb2, seed) {
    const y0 = Math.floor(top);
    const bandH = H - y0;
    if (bandH <= 2) return;
    const k = 0.5;
    const cw = Math.max(4, Math.round(W * k)), ch = Math.max(4, Math.round(bandH * k));
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tg = tmp.getContext('2d');
    const img = tg.createImageData(cw, ch);
    const d = img.data;
    for (let y = 0; y < ch; y++) {
      const v = y / ch;
      for (let x = 0; x < cw; x++) {
        const n = cgFbm((x / k) * 0.012, ((y / k) + y0) * 0.02 + seed, seed, 4, 0.55);
        // 细颗粒：沙地的"沙"感来自这一层高频噪声
        const fine = cgFbm((x / k) * 0.075, ((y / k) + y0) * 0.12 + seed * 2.1, seed + 5.3, 2, 0.5);
        // 风纹：横向拉得很长的低频噪声，像被风吹出来的沙垄
        const wind = cgFbm((x / k) * 0.0035, ((y / k) + y0) * 0.11 + seed * 0.7, seed + 9.1, 2, 0.5);
        const i = (y * cw + x) * 4;
        const kk = Math.min(1, Math.max(0, v * 0.74 + n * 0.5 + (fine - 0.5) * 0.2 + (wind - 0.5) * 0.16));
        d[i] = rgb1[0] + (rgb2[0] - rgb1[0]) * kk;
        d[i + 1] = rgb1[1] + (rgb2[1] - rgb1[1]) * kk;
        d[i + 2] = rgb1[2] + (rgb2[2] - rgb1[2]) * kk;
        d[i + 3] = 255;
      }
    }
    tg.putImageData(img, 0, 0);
    g.save();
    g.imageSmoothingEnabled = true;
    g.drawImage(tmp, 0, 0, cw, ch, 0, y0, W, bandH);
    g.restore();
  }
  // 碎石 / 植被剪影（近景压脚，构图更稳）
  function cgRocks(g, W, H, baseY, n, seed, color, scale) {
    g.save();
    g.fillStyle = color;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const x = W * (0.02 + t * 0.96) + Math.sin(i * 5.1 + seed) * 60;
      const w = (26 + cgHash(i, 3, seed) * 90) * (scale || 1);
      const h = (12 + cgHash(i, 7, seed) * 34) * (scale || 1);
      const y = baseY + cgHash(i, 11, seed) * 26 * (scale || 1);
      g.beginPath();
      g.moveTo(x - w / 2, y);
      g.quadraticCurveTo(x - w * 0.32, y - h, x + w * 0.06, y - h * 0.92);
      g.quadraticCurveTo(x + w * 0.42, y - h * 0.7, x + w / 2, y);
      g.closePath();
      g.fill();
    }
    g.restore();
  }
  function cgGrass(g, W, H, baseY, n, seed, color) {
    g.save();
    g.strokeStyle = color;
    g.lineCap = 'round';
    for (let i = 0; i < n; i++) {
      const x = W * ((i + 0.5) / n) + Math.sin(i * 3.7 + seed) * 40;
      const h = 18 + cgHash(i, 5, seed) * 46;
      const lean = (cgHash(i, 9, seed) - 0.5) * 26;
      g.lineWidth = 1.6 + cgHash(i, 13, seed) * 1.8;
      g.beginPath();
      g.moveTo(x, baseY + cgHash(i, 17, seed) * 18);
      g.quadraticCurveTo(x + lean * 0.4, baseY - h * 0.6, x + lean, baseY - h);
      g.stroke();
    }
    g.restore();
  }
  // 远景飞鸟（给尺度感）
  function cgBirds(g, x, y, n, size, color) {
    g.save();
    g.strokeStyle = color;
    g.lineCap = 'round';
    for (let i = 0; i < n; i++) {
      const bx = x + i * size * 3.1 - n * size * 1.2;
      const by = y + Math.sin(i * 1.7) * size * 2.2;
      const s = size * (0.7 + (i % 3) * 0.2);
      g.lineWidth = Math.max(1, s * 0.22);
      g.beginPath();
      g.moveTo(bx - s, by);
      g.quadraticCurveTo(bx - s * 0.4, by - s * 0.7, bx, by - s * 0.15);
      g.quadraticCurveTo(bx + s * 0.4, by - s * 0.7, bx + s, by);
      g.stroke();
    }
    g.restore();
  }
  /* 人形：带关节、衣摆、轮廓光（比原来那几根直线自然得多）
   *   cfg = { x, baseY, h, pose, color, rim, rimColor, flip, cloth, cape, weapon } */
  function cgFigure(g, cfg) {
    const h = cfg.h, s = h / 100;
    const col = cfg.color || 'rgba(7,8,13,.97)';
    const pose = cfg.pose || 'stand';
    const rimCol = cfg.rimColor || 'rgba(255,226,180,.55)';
    g.save();
    g.translate(cfg.x, cfg.baseY);
    if (cfg.flip) g.scale(-1, 1);
    g.fillStyle = col;
    g.strokeStyle = col;
    g.lineCap = 'round';
    g.lineJoin = 'round';

    // 关节（单位：以 100 为身高，y 向上为负）
    const hip = -46, chest = -72, neck = -84, headC = -92;
    const shoulder = 8, hipW = 7;
    let knee = [0, -24], foot = [0, 0], elbow = [0, -58], hand = [0, -44];
    let headTilt = 0, lean = 0;
    if (pose === 'kneel') {
      knee = [4, -22]; foot = [-10, 0]; elbow = [6, -58]; hand = [4, -76]; headTilt = 4;
    } else if (pose === 'arms') {
      elbow = [-16, -62]; hand = [-30, -76];
      knee = [-6, -24]; foot = [-12, 0];
    } else if (pose === 'walk') {
      knee = [10, -22]; foot = [18, 0]; elbow = [-8, -58]; hand = [-14, -46]; lean = 2;
    } else if (pose === 'guard') {
      elbow = [12, -58]; hand = [16, -50]; knee = [4, -24]; foot = [6, 0];
    }

    if (cfg.cape) {           // 披风：在背后的一块飘动布料
      g.beginPath();
      g.moveTo(-shoulder * s * 0.9, chest * s);
      g.quadraticCurveTo(-30 * s, hip * s, -18 * s, 2 * s);
      g.quadraticCurveTo(-6 * s, hip * s * 1.05, shoulder * s, chest * s);
      g.closePath();
      g.fill();
    }
    if (cfg.cloth) {          // 长袍下摆
      g.beginPath();
      g.moveTo(-shoulder * s, chest * s);
      g.lineTo(-hipW * s - 5 * s, hip * s);
      g.quadraticCurveTo(-13 * s, -8 * s, -15 * s, 0);
      g.lineTo(15 * s, 0);
      g.quadraticCurveTo(13 * s, -8 * s, hipW * s + 5 * s, hip * s);
      g.lineTo(shoulder * s, chest * s);
      g.closePath();
      g.fill();
    }
    // 躯干（肩宽 → 腰细 → 胯）
    g.beginPath();
    g.moveTo(-shoulder * s, chest * s);
    g.quadraticCurveTo(-shoulder * 0.9 * s, (chest + hip) / 2 * s, -hipW * s, hip * s);
    g.lineTo(hipW * s, hip * s);
    g.quadraticCurveTo(shoulder * 0.9 * s, (chest + hip) / 2 * s, shoulder * s, chest * s);
    g.quadraticCurveTo(0, chest * s - 4 * s, -shoulder * s, chest * s);
    g.closePath();
    g.fill();
    // 颈 + 头 + 发/冠
    g.lineWidth = 4.6 * s;
    g.beginPath();
    g.moveTo(lean * s, chest * s);
    g.lineTo(lean * s, neck * s);
    g.stroke();
    g.beginPath();
    g.arc(lean * s, (headC + headTilt) * s, 7.6 * s, 0, Math.PI * 2);
    g.fill();
    if (cfg.hair !== false) {
      g.beginPath();
      g.ellipse(lean * s - 1.5 * s, (headC + headTilt - 2) * s, 8.4 * s, 7.2 * s, -0.3, Math.PI * 0.05, Math.PI * 1.15);
      g.fill();
    }
    // 四肢（上段粗、下段细，两段各画一次做出关节）
    const limb = function (x0, y0, x1, y1, w0, w1) {
      g.lineWidth = w0 * s;
      g.beginPath();
      g.moveTo(x0 * s, y0 * s);
      g.lineTo((x0 + x1) / 2 * s, (y0 + y1) / 2 * s);
      g.stroke();
      g.lineWidth = w1 * s;
      g.beginPath();
      g.moveTo((x0 + x1) / 2 * s, (y0 + y1) / 2 * s);
      g.lineTo(x1 * s, y1 * s);
      g.stroke();
    };
    if (pose !== 'arms') limb(-shoulder * 0.85, chest + 2, elbow[0], elbow[1], 5.6, 4.6);
    else limb(-shoulder * 0.85, chest + 2, elbow[0], elbow[1], 5.6, 4.6);
    limb(shoulder * 0.85, chest + 2, pose === 'arms' ? -elbow[0] : elbow[0], elbow[1], 5.6, 4.6);
    if (pose === 'kneel') {
      limb(hip * 0 + 0 * 1, hip, knee[0], knee[1], 6.4, 5.2);          // 大腿
      limb(knee[0], knee[1], foot[0], foot[1] - 12, 5.2, 4.2);          // 小腿（跪）
    } else {
      limb(-2, hip, knee[0], knee[1], 6.6, 5.2);
      limb(knee[0], knee[1], foot[0], foot[1], 5.2, 4.2);
      limb(2, hip, -knee[0] * 0.4, knee[1] - 2, 6.6, 5.2);
      limb(-knee[0] * 0.4, knee[1] - 2, foot[0] - 2, foot[1], 5.2, 4.2);
    }
    // 手
    g.beginPath();
    g.arc((pose === 'arms' ? -elbow[0] : elbow[0]) * s, elbow[1] * s, 3.4 * s, 0, Math.PI * 2);
    g.fill();
    // 武器（长杆）
    if (cfg.weapon) {
      g.strokeStyle = col;
      g.lineWidth = 2.6 * s;
      g.beginPath();
      g.moveTo(elbow[0] * s - 6 * s, elbow[1] * s + 40 * s);
      g.lineTo(elbow[0] * s + 10 * s, elbow[1] * s - 70 * s);
      g.stroke();
    }
    // 边缘光：人体外轮廓来一道暖光，立刻从"黑影"变成"有体积"
    if (cfg.rim !== false) {
      g.globalCompositeOperation = 'lighter';
      g.strokeStyle = rimCol;
      g.lineWidth = 2.2;
      g.beginPath();
      g.moveTo(shoulder * s, chest * s);
      g.quadraticCurveTo(shoulder * 1.05 * s, (chest + hip) / 2 * s, hipW * 1.1 * s, hip * s);
      g.moveTo(lean * s + 7 * s, (headC + headTilt - 2) * s);
      g.quadraticCurveTo(shoulder * s, chest * s, shoulder * s, chest * s);
      g.moveTo(shoulder * 0.85 * s, chest * s);
      g.lineTo(elbow[0] * s, elbow[1] * s);
      g.stroke();
    }
    g.restore();
  }
  // 巨兽（本土原生生灵）：隆背 + 脊刺 + 尾部 + 四肢剪影，比原来的"土包"有辨识度
  function cgBeast(g, cfg) {
    const s = cfg.scale || 1;
    const x = cfg.x, y = cfg.baseY;
    g.save();
    g.translate(x, y);
    g.scale(s, s);
    g.fillStyle = cfg.color || 'rgba(10,12,18,.96)';
    g.beginPath();
    g.moveTo(-330, 0);
    g.quadraticCurveTo(-300, -120, -210, -150);
    g.quadraticCurveTo(-150, -196, -60, -178);
    g.quadraticCurveTo(20, -208, 110, -168);
    g.quadraticCurveTo(230, -170, 300, -96);
    g.quadraticCurveTo(360, -40, 370, 0);
    g.closePath();
    g.fill();
    // 脊刺
    for (let i = -4; i <= 5; i++) {
      const bx = -30 + i * 52;
      const by = -168 - 26 * Math.cos(i * 0.42);
      g.beginPath();
      g.moveTo(bx - 14, by + 6);
      g.lineTo(bx + (i % 2 ? 6 : -2), by - (40 + (i % 3) * 12));
      g.lineTo(bx + 15, by + 6);
      g.closePath();
      g.fill();
    }
    // 前肢踏地
    g.beginPath();
    g.moveTo(-160, -60); g.lineTo(-186, 0); g.lineTo(-138, 0); g.lineTo(-118, -66);
    g.closePath(); g.fill();
    g.beginPath();
    g.moveTo(150, -70); g.lineTo(126, 0); g.lineTo(182, 0); g.lineTo(196, -74);
    g.closePath(); g.fill();
    if (cfg.rim !== false) {
      g.globalCompositeOperation = 'lighter';
      g.strokeStyle = cfg.rimColor || 'rgba(255,226,180,.35)';
      g.lineWidth = 3;
      g.beginPath();
      g.moveTo(-300, -128);
      g.quadraticCurveTo(-150, -196, 20, -196);
      g.quadraticCurveTo(190, -176, 330, -80);
      g.stroke();
    }
    g.restore();
  }
  // 天穹裂隙（域外天道撕开的口子）
  function cgRift(g, x, top, h2, w2, rgb) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    const grd = g.createLinearGradient(x, top, x, top + h2);
    grd.addColorStop(0, 'rgba(' + rgb + ',.95)');
    grd.addColorStop(1, 'rgba(' + rgb + ',0)');
    g.fillStyle = grd;
    g.beginPath();
    let px = x;
    g.moveTo(px - w2 * 0.1, top);
    for (let yy = top; yy <= top + h2; yy += 26) {
      px = x + Math.sin(yy * 0.045) * w2 * 0.5;
      g.lineTo(px - w2 * 0.06, yy);
    }
    for (let yy = top + h2; yy >= top; yy -= 26) {
      px = x + Math.sin(yy * 0.045) * w2 * 0.5;
      g.lineTo(px + w2 * (0.06 + 0.5 * (1 - (yy - top) / h2)), yy);
    }
    g.closePath();
    g.fill();
    g.restore();
  }
  // 天上敕令法环
  function cgSeal(g, x, y, r, rgb, alpha) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    g.strokeStyle = 'rgba(' + rgb + ',' + alpha + ')';
    for (let i = 0; i < 3; i++) {
      const rr = r * (0.55 + i * 0.28);
      g.lineWidth = 2.6 - i * 0.6;
      g.save();
      g.translate(x, y);
      g.scale(1, 0.42);
      g.beginPath();
      g.arc(0, 0, rr, 0, Math.PI * 2);
      g.stroke();
      const seg = 12 + i * 6;
      for (let k = 0; k < seg; k++) {
        const a2 = (Math.PI * 2 * k) / seg + i * 0.2;
        g.beginPath();
        g.moveTo(Math.cos(a2) * rr * 0.92, Math.sin(a2) * rr * 0.92);
        g.lineTo(Math.cos(a2) * rr * 1.08, Math.sin(a2) * rr * 1.08);
        g.stroke();
      }
      g.restore();
    }
    g.restore();
  }
  // 灵气流（一条条上升/汇聚的光丝）
  function cgQiStream(g, x0, y0, x1, y1, bend, rgb, w, alpha) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    g.strokeStyle = 'rgba(' + rgb + ',' + alpha + ')';
    g.lineWidth = w;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(x0, y0);
    g.quadraticCurveTo((x0 + x1) / 2 + bend, (y0 + y1) / 2 + Math.abs(bend) * 0.2, x1, y1);
    g.stroke();
    g.restore();
  }
  // 空气里的浮尘 / 灵光点
  function cgMotes(g, W, H, n, seed, rgb, alpha, spread) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    for (let i = 0; i < n; i++) {
      const x = cgHash(i, 1, seed) * W;
      const y = H * (0.15 + cgHash(i, 2, seed) * (spread || 0.75));
      const r = 0.7 + cgHash(i, 3, seed) * 1.9;
      const a = alpha * (0.25 + cgHash(i, 4, seed) * 0.75);
      const gr = g.createRadialGradient(x, y, 0, x, y, r * 4);
      gr.addColorStop(0, 'rgba(' + rgb + ',' + a + ')');
      gr.addColorStop(1, 'rgba(' + rgb + ',0)');
      g.fillStyle = gr;
      g.beginPath();
      g.arc(x, y, r * 4, 0, Math.PI * 2);
      g.fill();
    }
    g.restore();
  }
  // 泛光：把亮部抽出来模糊再叠回去（画质提升最明显的一步）
  function cgBloom(g, W, H, strength) {
    try {
      const tmp = document.createElement('canvas');
      const k = 0.25;
      tmp.width = Math.max(2, Math.round(W * k));
      tmp.height = Math.max(2, Math.round(H * k));
      const tg = tmp.getContext('2d');
      tg.filter = 'blur(6px) brightness(1.35) saturate(1.15)';
      tg.drawImage(g.canvas, 0, 0, tmp.width, tmp.height);
      tg.filter = 'none';
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.globalAlpha = strength === undefined ? 0.42 : strength;
      g.drawImage(tmp, 0, 0, W, H);
      g.restore();
    } catch (e) { /* 浏览器不支持 filter 就跳过 */ }
  }
  // 调色 + 暗角 + 颗粒（让它像一张画，而不是一组矢量色块）
  function cgPostFx(g, W, H, opt) {
    const o = opt || {};
    if (o.grade) {
      g.save();
      g.globalCompositeOperation = 'overlay';
      g.globalAlpha = o.gradeA === undefined ? 0.35 : o.gradeA;
      g.fillStyle = o.grade;
      g.fillRect(0, 0, W, H);
      g.restore();
    }
    // 暗角
    const vg = g.createRadialGradient(W / 2, H * 0.46, H * 0.32, W / 2, H * 0.5, H * 0.95);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,' + (o.vignette === undefined ? 0.55 : o.vignette) + ')');
    g.fillStyle = vg;
    g.fillRect(0, 0, W, H);
    // 颗粒：用一张预生成的小噪点铺满（比逐像素改全画布快得多）
    if (o.grain !== false) {
      const amt = o.grainA === undefined ? 9 : o.grainA;
      const tile = cgGrainTile();
      const pat = g.createPattern(tile, 'repeat');
      if (pat) {
        g.save();
        g.globalCompositeOperation = 'overlay';
        g.globalAlpha = Math.min(0.5, amt / 40);
        g.fillStyle = pat;
        g.fillRect(0, 0, W, H);
        g.restore();
      }
    }
  }
  // 256×256 的噪点贴片（只生成一次）
  let cgGrain = null;
  function cgGrainTile() {
    if (cgGrain) return cgGrain;
    const S = 256;
    const c = document.createElement('canvas');
    c.width = S; c.height = S;
    const g = c.getContext('2d');
    const img = g.createImageData(S, S);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      const v = 118 + Math.round(cgHash(i * 0.013, i * 0.007, 3.7) * 74);
      d[i] = v; d[i + 1] = v; d[i + 2] = v; d[i + 3] = 255;
    }
    g.putImageData(img, 0, 0);
    cgGrain = c;
    return c;
  }
  /* ---------- 空气层：太阳 / 扬沙 / 前景虚化 ----------
   *   都是"看不见但少了就假"的东西：有太阳画面才有主光方向，有扬沙远景和
   *   近景才分得开，有前景虚化镜头才有景深。 */
  function cgSun(g, x, y, r, rgb, a) {
    g.save();
    g.globalCompositeOperation = 'lighter';
    const gr = g.createRadialGradient(x, y, 0, x, y, r);
    gr.addColorStop(0, 'rgba(' + rgb + ',' + (a * 1.2) + ')');
    gr.addColorStop(0.09, 'rgba(' + rgb + ',' + (a * 0.8) + ')');
    gr.addColorStop(0.32, 'rgba(' + rgb + ',' + (a * 0.24) + ')');
    gr.addColorStop(1, 'rgba(' + rgb + ',0)');
    g.fillStyle = gr;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
    g.restore();
  }
  /* 地面扬沙 / 贴地雾：横向拉伸的噪声带（风一吹就有方向感） */
  function cgDust(g, W, H, yTop, yBot, seed, rgb, aMax, streak) {
    const bandH = Math.max(4, Math.round(yBot - yTop));
    const k = 0.5;
    const cw = Math.max(4, Math.round(W * k)), ch = Math.max(4, Math.round(bandH * k));
    const tmp = document.createElement('canvas');
    tmp.width = cw; tmp.height = ch;
    const tg = tmp.getContext('2d');
    const img = tg.createImageData(cw, ch);
    const d = img.data;
    const sx = Math.max(1, streak || 6);
    for (let y = 0; y < ch; y++) {
      const v = y / ch;
      const band = Math.sin(Math.PI * Math.min(1, v));         // 上下都要淡出，不然有一条硬边
      for (let x = 0; x < cw; x++) {
        const n = cgFbm((x / k) / sx * 0.02, ((y / k) + yTop) * 0.028 + seed, seed, 3, 0.6);
        const a = (n - 0.42) * 2.5 * band;
        if (a <= 0.01) continue;
        const i = (y * cw + x) * 4;
        d[i] = rgb[0]; d[i + 1] = rgb[1]; d[i + 2] = rgb[2];
        d[i + 3] = Math.round(Math.min(1, a) * aMax * 255);
      }
    }
    tg.putImageData(img, 0, 0);
    g.save();
    g.imageSmoothingEnabled = true;
    g.drawImage(tmp, 0, 0, cw, ch, 0, yTop, W, bandH);
    g.restore();
  }
  /* 前景虚化：一块"离镜头很近"的暗地形糊在画面最前面 → 立刻有景深 */
  function cgFgBlur(g, W, H, y, seed, color, scale) {
    const s = scale || 1;
    g.save();
    g.filter = 'blur(' + Math.round(13 * s) + 'px)';
    g.fillStyle = color;
    g.beginPath();
    g.moveTo(-60, H + 60);
    g.lineTo(-60, y);
    const n = 10;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = -60 + t * (W + 120);
      const yy = y - cgFbm(t * 3.1 + seed, seed, seed, 3, 0.5) * 48 * s;
      g.lineTo(x, yy);
    }
    g.lineTo(W + 60, H + 60);
    g.closePath();
    g.fill();
    g.restore();
  }
  /* ---------- 真实美术层：把游戏里的立绘 / 模型直接画进分镜 ----------
   *   环境还是程序画的（天空 / 云 / 山脊 / 沙地 / 沙尘），但"人和兽"一律用
   *   游戏里已经在用的美术资源 —— 分镜和本体同一套画风，就不会像剪影那样生硬。
   *   为了不出现"贴上去的"感觉，每张立绘都做三件事：
   *     ① 按不透明外框裁切（alpha bbox）：自动去掉透明留白，按"脚底"对齐落地
   *     ② 按场景色调重新调色（source-atop 只染立绘本身，不会糊到背景）
   *     ③ 背后加一圈环境色轮廓光（模糊剪影 + lighter），逆光场景一眼就成立
   *   立绘与处理结果都按 key 缓存，只算一次。 */
  const cgArt = {};        // art -> Image
  const cgArtBoxes = {};   // art -> { x, y, w, h } 不透明外框
  const cgArtToned = {};   // art|tint|a|lift -> canvas（已调色的立绘）
  const cgArtRims = {};    // art|rgb|blur -> canvas（纯轮廓光晕）
  // 调试开关：?cgart=0 时不画真实立绘（用来做"A/B 差分"，确认立绘画在了该画的地方）
  const cgNoArt = (function () {
    try { return new URLSearchParams(window.location.search || '').get('cgart') === '0'; }
    catch (e) { return false; }
  })();

  function cgArtImg(art) {
    let im = cgArt[art];
    if (!im) {
      im = new Image();
      im.decoding = 'async';
      im.src = assetUrl('assets/img/' + art);
      cgArt[art] = im;
      /* 素材补到了：把先前"用兜底剪影画的"那几张作废 —— 下一帧重新画一遍，
       * 这样即使开播时超时放行，慢网络下也不会一直停在兜底画面上。 */
      im.addEventListener('load', function () {
        Object.keys(CG_SLIDE_ART).forEach(function (id) {
          if (CG_SLIDE_ART[id].indexOf(art) >= 0) cgBitmaps[id] = null;
        });
      }, { once: true });
    }
    return im;
  }
  function cgArtBox(art) {
    const im = cgArtImg(art);
    if (!im || !im.complete || !im.naturalWidth) return null;
    if (cgArtBoxes[art]) return cgArtBoxes[art];
    let box = { x: 0, y: 0, w: im.naturalWidth, h: im.naturalHeight };
    try {   // 挂第三方平台时素材是跨域的，读像素会被安全策略拦 → 退回"整帧"
      const c = document.createElement('canvas');
      c.width = im.naturalWidth; c.height = im.naturalHeight;
      const g = c.getContext('2d');
      g.drawImage(im, 0, 0);
      const d = g.getImageData(0, 0, c.width, c.height).data;
      let x0 = c.width, y0 = c.height, x1 = -1, y1 = -1;
      for (let y = 0; y < c.height; y++) {
        const row = y * c.width;
        for (let x = 0; x < c.width; x++) {
          if (d[(row + x) * 4 + 3] < 12) continue;
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
      if (x1 > x0 && y1 > y0) box = { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
    } catch (e) { /* 读不到就用整帧 */ }
    cgArtBoxes[art] = box;
    return box;
  }
  function cgArtTone(art, tint, tintA, lift) {
    const box = cgArtBox(art);
    if (!box) return null;
    const key = art + '|' + (tint || '') + '|' + (tintA === undefined ? '' : tintA) + '|' + (lift || 0);
    if (cgArtToned[key]) return cgArtToned[key];
    const c = document.createElement('canvas');
    c.width = box.w; c.height = box.h;
    const g = c.getContext('2d');
    g.drawImage(cgArtImg(art), box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
    if (tint) {          // 环境色：把立绘整体压进场景色调里
      g.save();
      g.globalCompositeOperation = 'source-atop';
      g.globalAlpha = tintA === undefined ? 0.26 : tintA;
      g.fillStyle = tint;
      g.fillRect(0, 0, box.w, box.h);
      g.restore();
    }
    if (lift) {          // 上亮下暗：给立绘一点体积，免得是一块死平的颜色
      g.save();
      g.globalCompositeOperation = 'source-atop';
      const gr = g.createLinearGradient(0, 0, 0, box.h);
      gr.addColorStop(0, 'rgba(255,242,222,' + (lift * 0.9) + ')');
      gr.addColorStop(0.5, 'rgba(255,242,222,0)');
      gr.addColorStop(1, 'rgba(0,0,0,' + (lift * 1.6) + ')');
      g.fillStyle = gr;
      g.fillRect(0, 0, box.w, box.h);
      g.restore();
    }
    cgArtToned[key] = c;
    return c;
  }
  /* 纯轮廓光晕：把本体画到画布外，只让它的模糊影子落在画布上 */
  function cgArtRim(art, rgb, blur) {
    const box = cgArtBox(art);
    if (!box) return null;
    const b = Math.max(2, Math.round(blur || 12));
    const key = art + '|' + rgb + '|' + b;
    if (cgArtRims[key]) return cgArtRims[key];
    const c = document.createElement('canvas');
    c.width = box.w + b * 4; c.height = box.h + b * 4;
    const g = c.getContext('2d');
    g.shadowColor = 'rgb(' + rgb + ')';
    g.shadowBlur = b;
    g.shadowOffsetX = -c.width;
    g.drawImage(cgArtImg(art), box.x, box.y, box.w, box.h, b * 2 + c.width, b * 2, box.w, box.h);
    cgArtRims[key] = c;
    return c;
  }
  /* 把一张真实立绘画进分镜
   *   cfg = { art, x, baseY, h, flip, tint, tintA, lift, rim, rimA, rimBlur, shadow, alpha }
   *   h = 目标身高（按不透明区域算）；baseY = 脚底落点；flip = 素材本身朝右，要朝左就翻。 */
  function cgSprite(g, cfg) {
    if (cgNoArt) return false;
    const box = cgArtBox(cfg.art);
    const img = cgArtTone(cfg.art, cfg.tint, cfg.tintA, cfg.lift);
    // 素材还没到位 / 404 / 跨域读不到：退回程序画的剪影，画面至少是"有人有兽"的
    if (!box || !img) { cgFallbackFigure(g, cfg); return false; }
    const sc = cfg.h / box.h;
    const dw = box.w * sc, dh = box.h * sc;
    g.save();
    g.globalAlpha = cfg.alpha === undefined ? 1 : cfg.alpha;
    g.translate(cfg.x, cfg.baseY);
    if (cfg.flip) g.scale(-1, 1);
    // ① 落地接触阴影：人是"踩"在地上的，不是飘着的
    if (cfg.shadow !== false) {
      const sw = Math.max(10, dw * 0.66), sr = sw * 0.5;
      const gr = g.createRadialGradient(0, 0, 1, 0, 0, sr);
      gr.addColorStop(0, 'rgba(0,0,0,.52)');
      gr.addColorStop(0.6, 'rgba(0,0,0,.22)');
      gr.addColorStop(1, 'rgba(0,0,0,0)');
      g.save();
      g.translate(0, 3);
      g.scale(1, Math.max(0.1, (sw * 0.16) / sr));
      g.fillStyle = gr;
      g.beginPath(); g.arc(0, 0, sr, 0, Math.PI * 2); g.fill();
      g.restore();
    }
    // ② 轮廓光：画在本体下面，从边缘透出来
    const rimW = cfg.rimBlur === undefined ? 12 : cfg.rimBlur;
    if (cfg.rim) {
      const rim = cgArtRim(cfg.art, cfg.rim, rimW);
      if (rim) {
        const pad = rimW * 2;
        g.save();
        g.globalCompositeOperation = 'lighter';
        g.globalAlpha = g.globalAlpha * (cfg.rimA === undefined ? 0.5 : cfg.rimA);
      g.drawImage(rim, -dw / 2 - pad * sc, -dh - pad * sc, rim.width * sc, rim.height * sc);
        g.restore();
      }
    }
    g.drawImage(img, -dw / 2, -dh, dw, dh);
    g.restore();
    return true;
  }
  /* 立绘缺失时的兜底剪影（保证"素材挂了"也不会开天窗） */
  function cgFallbackFigure(g, cfg) {
    if (/^boss\//.test(cfg.art)) {
      cgBeast(g, { x: cfg.x, baseY: cfg.baseY, scale: Math.max(0.4, cfg.h / 200),
        color: 'rgba(10,12,18,.96)', rimColor: cfg.rim ? 'rgba(' + cfg.rim + ',.3)' : 'rgba(255,226,180,.3)' });
    } else {
      cgFigure(g, { x: cfg.x, baseY: cfg.baseY, h: cfg.h, pose: 'stand', flip: cfg.flip,
        color: 'rgba(7,8,13,.97)', cloth: true,
        rimColor: cfg.rim ? 'rgba(' + cfg.rim + ',.6)' : 'rgba(255,232,190,.6)' });
    }
  }
  /* 每张分镜各自要用哪些真实素材。
   *   —— 按张分开登记，是因为开播时只需要等第一张（几百 KB），
   *      其余的张在它自己出现前 8 秒就开始下载，开场不会被素材拖住。 */
  const CG_SLIDE_ART = {
    cg1: ['boss/rockcolossus.webp', 'boss/earthmother.webp'],
    cg2: ['boss/shadowbeast.webp', 'boss/frostfang.webp'],
    cg3: ['boss/earthmother.webp', 'enemy/qigai_1.png', 'enemy/youmin_1.png',
      'enemy/nongfu_1.png', 'enemy/kuaiji_1.png', 'enemy/bingfei_1.png'],
    /* CG 现在只有 4 张（原 1/2/3/5）：这里只留还画得到的分镜素材，
     * 免得开播前白下载一堆用不上的立绘（cg4/6/7/8 的程序绘制分支还在，随时能恢复）。 */
    cg5: ['boss/earthmother.webp', 'cg/hero_stand_3.png']
  };
  // 全部素材（调试用：一份清单看加载情况）
  const CG_ART_USED = (function () {
    const seen = {}, list = [];
    Object.keys(CG_SLIDE_ART).forEach(function (id) {
      CG_SLIDE_ART[id].forEach(function (art) { if (!seen[art]) { seen[art] = 1; list.push(art); } });
    });
    return list;
  })();
  // 这一组素材都到位了吗（顺带把 alpha 外框算好，画的时候就不用再等）
  function cgArtReady(list) {
    const l = list || [];
    for (let i = 0; i < l.length; i++) {
      const im = cgArtImg(l[i]);
      if (!im.complete || !im.naturalWidth) return false;
      cgArtBox(l[i]);
    }
    return true;
  }
  const cgArtWaits = {};
  /* 等一组素材（capMs 到点就放行：加载失败也要能继续，画面自然退回程序剪影） */
  function cgPreloadArt(list, capMs) {
    const key = (list || []).join(',');
    if (cgArtWaits[key]) return cgArtWaits[key];
    const p = new Promise(function (res) {
      let left = (list || []).length;
      if (!left) { res(true); return; }
      let ended = false;
      const done = function () { if (!ended && --left <= 0) { ended = true; res(true); } };
      list.forEach(function (art) {
        const im = cgArtImg(art);
        if (im.complete && im.naturalWidth) { done(); return; }
        im.addEventListener('load', done, { once: true });
        im.addEventListener('error', done, { once: true });
      });
      setTimeout(function () { if (!ended) { ended = true; res(true); } }, capMs || 1500);
    });
    cgArtWaits[key] = p;
    return p;
  }
  /* 每张分镜的完整绘制（只做一次，结果缓存） */
  function cgPaintScene(g, id, W, H) {
    const gy = H * 0.72;      // 地平线
    if (id === 'cg1') {
      // ① 远古原初：苍莽天地 · 巨兽蛰伏 · 灵气在地脉间流转
      cgSky(g, W, H, [[0, '#0c1c2c'], [0.28, '#1f3346'], [0.54, '#4a5a62'], [0.78, '#8c7050'], [1, '#6d5a40']]);
      cgSun(g, W * 0.76, H * 0.22, H * 0.66, '255, 226, 168', 0.5);
      cgCloudLayer(g, W, H, H * 0.0, H * 0.44, 11.3, [255, 236, 206], [148, 148, 154], 0.44, 1.5);
      cgGodRays(g, W * 0.76, H * 0.2, H * 0.8, 1.5, 12, '255, 236, 190', 0.15);
      // 最远的一层山：空气透视最重，颜色最淡
      cgRidge(g, W, H, { baseY: gy - 118, amp: 200, seed: 3.1, color: ['#5f7488', '#33465a'], rim: 'rgba(255,226,180,.24)', haze: '198, 208, 212', hazeA: 0.64 });
      cgRidge(g, W, H, { baseY: gy - 46, amp: 158, seed: 7.7, color: ['#3a4d5c', '#1d2936'], rim: 'rgba(255,214,160,.20)', haze: '162, 178, 188', hazeA: 0.54 });
      // 原生生灵：巨兽伏在远山之间（下半身会被近处的山脊挡住 → 有纵深，不是贴上去的）
      cgSprite(g, { art: 'boss/rockcolossus.webp', x: W * 0.60, baseY: gy + 20, h: H * 0.46,
        tint: '#3d4d66', tintA: 0.36, lift: 0.10, rim: '255, 214, 150', rimA: 0.32, rimBlur: 16 });
      cgSprite(g, { art: 'boss/earthmother.webp', x: W * 0.19, baseY: gy - 4, h: H * 0.17,
        tint: '#5d6f86', tintA: 0.46, lift: 0.06, rim: '224, 236, 255', rimA: 0.22, rimBlur: 10 });
      cgGodRays(g, W * 0.76, H * 0.22, H * 0.7, 1.9, 8, '255, 240, 210', 0.10);
      cgRidge(g, W, H, { baseY: gy + 46, amp: 124, seed: 13.2, color: ['#20293a', '#0f141d'], rim: 'rgba(255,206,150,.22)', haze: '126, 136, 146', hazeA: 0.44 });
      cgGround(g, W, H, gy + 42, [104, 84, 58], [44, 36, 28], 5.5);
      cgDust(g, W, H, gy + 26, gy + 200, 6.9, [216, 192, 158], 0.34, 7);
      for (let i = 0; i < 9; i++) {
        cgQiStream(g, W * (0.05 + i * 0.055), gy + 120, W * (0.5 + i * 0.05), H * (0.16 + i * 0.045),
          (i % 2 ? 1 : -1) * (70 + i * 12), '150, 236, 255', 2.6, 0.26);
      }
      cgMotes(g, W, H, 150, 2.7, '255, 236, 200', 0.5, 0.8);
      cgBirds(g, W * 0.3, H * 0.23, 6, 9, 'rgba(20,26,34,.5)');
      cgFgBlur(g, W, H, H * 0.965, 3.7, 'rgba(9,10,15,.9)', 1);
      cgPostFx(g, W, H, { grade: '#2a3a4a', gradeA: 0.22, vignette: 0.5, grainA: 8 });
    } else if (id === 'cg2') {
      // ② 域外天道降临：天穹撕裂，光辉倾泻，灵气被抽往天外
      cgSky(g, W, H, [[0, '#05070e'], [0.36, '#0b1120'], [0.7, '#1d2029'], [1, '#3a3126']]);
      cgCloudLayer(g, W, H, H * 0.05, H * 0.5, 4.4, [70, 84, 110], [26, 30, 40], 0.62, 1.35);
      cgRift(g, W * 0.5, 0, H * 0.68, 120, '220, 240, 255');
      cgLight(g, W * 0.5, H * 0.06, H * 0.95, '210, 236, 255', 0.42);
      cgGodRays(g, W * 0.5, H * 0.04, H * 1.05, 0.62, 13, '220, 240, 255', 0.16);
      cgRidge(g, W, H, { baseY: gy + 6, amp: 170, seed: 9.4, color: ['#161d2a', '#0a0e16'], rim: 'rgba(190,226,255,.30)', haze: '120, 150, 185', hazeA: 0.5 });
      // 两只原生生灵背光站在旷野上：逆光成剪影，只有边缘一圈冷光
      cgSprite(g, { art: 'boss/shadowbeast.webp', x: W * 0.30, baseY: gy + 84, h: H * 0.30,
        tint: '#0c1422', tintA: 0.62, rim: '196, 226, 255', rimA: 0.5, rimBlur: 14 });
      cgSprite(g, { art: 'boss/frostfang.webp', x: W * 0.69, baseY: gy + 96, h: H * 0.24,
        tint: '#0c1422', tintA: 0.62, rim: '212, 238, 255', rimA: 0.42, rimBlur: 12 });
      cgGround(g, W, H, gy + 30, [58, 56, 60], [22, 22, 26], 8.1);
      cgDust(g, W, H, gy + 16, gy + 210, 8.9, [150, 176, 208], 0.3, 8);
      for (let i = 0; i < 11; i++) {      // 灵气被抽往裂隙
        const x = W * (0.08 + i * 0.084);
        cgQiStream(g, x, gy - 6, x + (W * 0.5 - x) * 0.8, H * (0.1 + 0.02 * (i % 3)),
          (i % 2 ? 1 : -1) * 60, '150, 226, 255', 2.4, 0.30);
      }
      cgRocks(g, W, H, gy + 150, 7, 3.3, 'rgba(8,10,14,.95)', 1.5);
      cgMotes(g, W, H, 120, 6.1, '200, 232, 255', 0.42, 0.7);
      cgFgBlur(g, W, H, H * 0.975, 8.1, 'rgba(6,8,12,.92)', 1.1);
      cgPostFx(g, W, H, { grade: '#16283c', gradeA: 0.3, vignette: 0.62, grainA: 9 });
    } else if (id === 'cg3') {
      // ③ 污名化：天降敕令法环，巨兽被打上灰暗印记，众生畏避
      cgSky(g, W, H, [[0, '#1a1520'], [0.4, '#2b232a'], [0.72, '#463a30'], [1, '#5d4a36']]);
      cgCloudLayer(g, W, H, H * 0.02, H * 0.4, 2.2, [200, 186, 200], [90, 82, 92], 0.5, 1.6);
      cgSeal(g, W * 0.5, H * 0.2, 150, '255, 232, 190', 0.5);
      cgLight(g, W * 0.5, H * 0.2, H * 0.6, '255, 226, 170', 0.3);
      cgGodRays(g, W * 0.5, H * 0.14, H * 0.8, 1.1, 9, '255, 232, 190', 0.11);
      cgRidge(g, W, H, { baseY: gy - 40, amp: 150, seed: 5.9, color: ['#4a4148', '#26212a'], rim: 'rgba(255,220,180,.18)', haze: '170, 158, 160', hazeA: 0.5 });
      // 守护大地的原生巨兽：正被天道打上"妖魔"的灰暗印记
      cgSprite(g, { art: 'boss/earthmother.webp', x: W * 0.28, baseY: gy + 46, h: H * 0.40,
        tint: '#3a2f3e', tintA: 0.42, lift: 0.08, rim: '255, 210, 170', rimA: 0.3, rimBlur: 14 });
      cgLight(g, W * 0.28, gy - 78, 166, '96, 84, 116', 0.5);       // 巨兽身上的灰暗印记
      cgGround(g, W, H, gy + 30, [92, 74, 56], [40, 33, 26], 12.6);
      // 远处一小群畏缩的凡人（用游戏里的敌人模型当"众生"，翻过来面朝巨兽）
      const folk = [
        ['enemy/qigai_1.png', 0.60, 148], ['enemy/youmin_1.png', 0.665, 162],
        ['enemy/nongfu_1.png', 0.728, 140], ['enemy/kuaiji_1.png', 0.792, 154],
        ['enemy/bingfei_1.png', 0.856, 138]
      ];
      folk.forEach(function (f, i) {
        cgSprite(g, { art: f[0], x: W * f[1], baseY: gy + 92 + (i % 2) * 10, h: f[2],
          flip: true, tint: '#2a2430', tintA: 0.4, lift: 0.05, rim: '255, 214, 170', rimA: 0.24, rimBlur: 8 });
      });
      cgRocks(g, W, H, gy + 190, 6, 7.4, 'rgba(10,8,12,.95)', 1.4);
      cgMotes(g, W, H, 110, 8.8, '220, 200, 200', 0.34, 0.72);
      cgFgBlur(g, W, H, H * 0.97, 5.9, 'rgba(10,8,12,.9)', 1);
      cgPostFx(g, W, H, { grade: '#3a2a2a', gradeA: 0.3, vignette: 0.6, grainA: 9 });
    } else if (id === 'cg4') {
      // ④ 修行之路：山巅武者，前路两条光路分叉
      cgSky(g, W, H, [[0, '#0a1020'], [0.34, '#1b2540'], [0.66, '#3d3a45'], [1, '#786046']]);
      cgCloudLayer(g, W, H, H * 0.24, H * 0.6, 15.7, [220, 214, 226], [120, 110, 120], 0.55, 1.3);
      cgLight(g, W * 0.5, H * 0.3, H * 0.62, '255, 226, 180', 0.26);
      cgSun(g, W * 0.5, H * 0.3, H * 0.5, '255, 230, 186', 0.3);
      // 云海之上露出的远峰
      cgRidge(g, W, H, { baseY: H * 0.64, amp: 96, seed: 33.1, color: ['#4c5668', '#2a3242'], rim: 'rgba(255,224,180,.2)', haze: '204, 210, 222', hazeA: 0.62 });
      cgCloudLayer(g, W, H, H * 0.5, H * 0.84, 24.5, [236, 232, 240], [104, 100, 112], 0.62, 1.15);
      cgRidge(g, W, H, { baseY: H * 0.82, amp: 240, seed: 1.7, color: ['#232a3a', '#12161f'], rim: 'rgba(255,214,160,.22)', haze: '180, 190, 205', hazeA: 0.45 });
      cgGround(g, W, H, H * 0.84, [70, 62, 56], [26, 24, 26], 21.3);
      // 两条分叉的光路（左：仙途冷光；右：逆仙暖光）
      cgQiStream(g, W * 0.5, H * 0.72, W * 0.12, H * 0.22, -90, '160, 200, 255', 9, 0.5);
      cgQiStream(g, W * 0.5, H * 0.72, W * 0.88, H * 0.24, 90, '255, 196, 120', 9, 0.5);
      cgLight(g, W * 0.12, H * 0.22, H * 0.3, '160, 200, 255', 0.4);
      cgLight(g, W * 0.88, H * 0.24, H * 0.3, '255, 196, 120', 0.38);
      // 立于山巅的武者（高清立绘）
      cgSprite(g, { art: 'cg/hero_stand_2.png', x: W * 0.5, baseY: H * 0.745, h: H * 0.42,
        tint: '#2c3450', tintA: 0.3, lift: 0.12, rim: '255, 232, 190', rimA: 0.5, rimBlur: 16 });
      cgRocks(g, W, H, H * 0.94, 6, 9.9, 'rgba(8,9,14,.96)', 2.2);
      cgDust(g, W, H, H * 0.8, H, 19.3, [206, 208, 220], 0.26, 9);
      cgMotes(g, W, H, 130, 4.2, '255, 240, 210', 0.4, 0.75);
      cgBirds(g, W * 0.7, H * 0.2, 4, 8, 'rgba(24,28,38,.45)');
      cgFgBlur(g, W, H, H * 0.985, 1.3, 'rgba(10,11,16,.88)', 1);
      cgPostFx(g, W, H, { grade: '#22304a', gradeA: 0.24, vignette: 0.58, grainA: 8 });
    } else if (id === 'cg5') {
      // ⑤ 仙途：献祭本源、俯首归顺，流光自地面汇入天穹
      cgSky(g, W, H, [[0, '#0a1224'], [0.34, '#16203a'], [0.66, '#2e3141'], [1, '#5e4c38']]);
      cgCloudLayer(g, W, H, H * 0.02, H * 0.42, 6.6, [236, 240, 255], [110, 118, 140], 0.48, 1.5);
      cgRift(g, W * 0.5, 0, H * 0.46, 90, '240, 250, 255');
      cgLight(g, W * 0.5, H * 0.12, H * 0.9, '236, 246, 255', 0.4);
      cgGodRays(g, W * 0.5, H * 0.08, H * 0.95, 0.75, 11, '240, 248, 255', 0.13);
      cgRidge(g, W, H, { baseY: gy - 20, amp: 140, seed: 12.9, color: ['#2c3446', '#161c28'], rim: 'rgba(240,248,255,.22)', haze: '170, 190, 215', hazeA: 0.5 });
      // 被献祭的原生生灵：倒在左侧，灵性正被抽走（压低透明度 + 冷色）
      cgSprite(g, { art: 'boss/earthmother.webp', x: W * 0.20, baseY: gy + 58, h: H * 0.26,
        tint: '#28324a', tintA: 0.5, alpha: 0.9, lift: 0.05, rim: '190, 214, 255', rimA: 0.24, rimBlur: 12 });
      cgGround(g, W, H, gy + 20, [86, 76, 60], [34, 30, 26], 17.4);
      cgDust(g, W, H, gy + 14, gy + 190, 14.2, [212, 216, 232], 0.3, 8);
      // 俯首献祭的武者（俯身姿态的高清立绘）+ 从身上升起的灵丝
      cgSprite(g, { art: 'cg/hero_stand_3.png', x: W * 0.5, baseY: gy + 116, h: H * 0.36,
        tint: '#2b3450', tintA: 0.28, lift: 0.14, rim: '255, 246, 214', rimA: 0.55, rimBlur: 16 });
      for (let i = 0; i < 8; i++) {
        cgQiStream(g, W * (0.44 + i * 0.016), gy + 60, W * (0.485 + i * 0.004), H * 0.14,
          (i % 2 ? 1 : -1) * 34, '255, 246, 214', 2.8, 0.36);
      }
      cgLight(g, W * 0.5, gy + 40, 260, '255, 246, 214', 0.26);
      cgRocks(g, W, H, H * 0.95, 5, 15.1, 'rgba(8,9,14,.95)', 2);
      cgMotes(g, W, H, 140, 3.9, '255, 248, 220', 0.46, 0.7);
      cgFgBlur(g, W, H, H * 0.975, 15.9, 'rgba(8,9,14,.9)', 1);
      cgPostFx(g, W, H, { grade: '#1e2c46', gradeA: 0.26, vignette: 0.6, grainA: 8 });
    } else if (id === 'cg6') {
      // ⑥ 逆仙：接纳大地传承，黑云压顶、雷劫酝酿
      cgSky(g, W, H, [[0, '#05080c'], [0.36, '#0c1216'], [0.68, '#1d2119'], [1, '#3d3524']]);
      cgCloudLayer(g, W, H, H * 0.0, H * 0.44, 9.1, [58, 64, 78], [12, 14, 18], 0.72, 1.2);
      cgCloudLayer(g, W, H, H * 0.16, H * 0.5, 3.3, [90, 96, 112], [24, 26, 32], 0.5, 1.4);
      cgRidge(g, W, H, { baseY: gy + 10, amp: 170, seed: 19.4, color: ['#1a2018', '#0b0f0c'], rim: 'rgba(160, 240, 190, .22)', haze: '120, 150, 130', hazeA: 0.5 });
      // 大地原生巨兽：和武者并肩，象征"承接本土本源"
      cgSprite(g, { art: 'boss/rockcolossus.webp', x: W * 0.78, baseY: gy + 66, h: H * 0.34,
        tint: '#1b2a22', tintA: 0.5, alpha: 0.86, rim: '160, 240, 190', rimA: 0.22, rimBlur: 14 });
      cgGround(g, W, H, gy + 34, [64, 66, 48], [22, 24, 18], 23.8);
      // 裂开的雷云里第一道雷
      g.save();
      g.strokeStyle = 'rgba(226, 244, 255, .92)';
      g.lineWidth = 4;
      g.globalCompositeOperation = 'lighter';
      g.beginPath();
      g.moveTo(W * 0.5, H * 0.12);
      g.lineTo(W * 0.465, H * 0.3); g.lineTo(W * 0.535, H * 0.38);
      g.lineTo(W * 0.49, H * 0.52); g.lineTo(W * 0.52, H * 0.6);
      g.stroke();
      g.restore();
      cgLight(g, W * 0.5, H * 0.22, H * 0.5, '200, 230, 255', 0.22);
      cgDust(g, W, H, gy + 20, gy + 230, 24.6, [126, 152, 132], 0.28, 7);
      // 接住大地之力的武者（高清立绘，绿色地光轮廓）
      cgSprite(g, { art: 'cg/hero_stand_1.png', x: W * 0.5, baseY: gy + 158, h: H * 0.44,
        tint: '#1c2c24', tintA: 0.32, lift: 0.12, rim: '150, 240, 190', rimA: 0.6, rimBlur: 16 });
      cgLight(g, W * 0.5, gy + 120, 300, '150, 236, 190', 0.26);
      for (let i = 0; i < 9; i++) {
        cgQiStream(g, W * (0.08 + i * 0.105), gy + 150, W * 0.5, gy - 40,
          (i % 2 ? 1 : -1) * 70, '160, 240, 200', 3.2, 0.30);
      }
      cgRocks(g, W, H, H * 0.96, 7, 11.7, 'rgba(6,8,8,.96)', 2.4);
      cgMotes(g, W, H, 120, 7.1, '180, 255, 210', 0.36, 0.72);
      cgFgBlur(g, W, H, H * 0.98, 21.7, 'rgba(5,7,7,.92)', 1.05);
      cgPostFx(g, W, H, { grade: '#1c2c24', gradeA: 0.3, vignette: 0.64, grainA: 10 });
    } else if (id === 'cg7') {
      // ⑦ 两大阵营对峙：左侧天光仙人（持械列阵），右侧大地逆仙（隐于山林）
      cgSky(g, W, H, [[0, '#0b1220'], [0.4, '#1d2230'], [0.72, '#3b3226'], [1, '#54452f']]);
      const left = g.createLinearGradient(0, 0, W * 0.55, 0);
      left.addColorStop(0, 'rgba(198, 226, 255, .32)');
      left.addColorStop(1, 'rgba(198, 226, 255, 0)');
      g.fillStyle = left; g.fillRect(0, 0, W * 0.55, H);
      const right = g.createLinearGradient(W, 0, W * 0.45, 0);
      right.addColorStop(0, 'rgba(255, 196, 120, .28)');
      right.addColorStop(1, 'rgba(255, 196, 120, 0)');
      g.fillStyle = right; g.fillRect(W * 0.45, 0, W * 0.55, H);
      cgCloudLayer(g, W, H, H * 0.04, H * 0.4, 5.2, [214, 220, 236], [80, 80, 96], 0.46, 1.5);
      cgRidge(g, W, H, { baseY: gy + 16, amp: 130, seed: 31.2, color: ['#232a36', '#111621'], rim: 'rgba(255,226,180,.18)', haze: '150, 160, 175', hazeA: 0.46 });
      // 逆仙阵后方的巨大虚影（本土力量的化身，隐在山林里）
      cgSprite(g, { art: 'boss/rebelgeneral.webp', x: W * 0.80, baseY: gy + 54, h: H * 0.30,
        tint: '#2a2620', tintA: 0.5, alpha: 0.4, rim: '255, 196, 120', rimA: 0.18, rimBlur: 18 });
      cgGround(g, W, H, gy + 40, [78, 68, 54], [28, 26, 24], 33.5);
      cgDust(g, W, H, gy + 26, gy + 220, 35.4, [186, 180, 172], 0.3, 8);
      // 仙阵（左）：真实立绘，天光冷色轮廓，朝右
      const xianPose = ['cg/hero_swing_1.png', 'cg/hero_stand_1.png', 'cg/hero_swing_2.png'];
      const xianH = [196, 214, 190];
      for (let i = 0; i < 3; i++) {
        cgSprite(g, { art: xianPose[i], x: W * (0.105 + i * 0.145), baseY: gy + 128 + (i % 2) * 10, h: xianH[i],
          tint: '#1b2536', tintA: 0.3, lift: 0.1, rim: '198, 226, 255', rimA: 0.62, rimBlur: 14 });
      }
      // 逆仙阵（右）：真实立绘，大地暖色轮廓，翻过来朝左
      const niPose = ['cg/hero_stand_2.png', 'cg/hero_stand_3.png', 'cg/hero_run_1.png'];
      const niH = [214, 192, 202];
      for (let i = 0; i < 3; i++) {
        cgSprite(g, { art: niPose[i], x: W * (0.605 + i * 0.145), baseY: gy + 134 + (i % 2) * 10, h: niH[i],
          flip: true, tint: '#2c2418', tintA: 0.3, lift: 0.1, rim: '255, 196, 120', rimA: 0.62, rimBlur: 14 });
      }
      g.save();       // 中间的地裂分界
      g.strokeStyle = 'rgba(255, 236, 200, .35)';
      g.lineWidth = 2.4;
      g.beginPath();
      g.moveTo(W * 0.5, H * 0.08);
      g.lineTo(W * 0.494, H * 0.42); g.lineTo(W * 0.506, H * 0.7); g.lineTo(W * 0.5, H * 0.98);
      g.stroke();
      g.restore();
      cgLight(g, W * 0.18, H * 0.3, H * 0.55, '198, 226, 255', 0.24);
      cgLight(g, W * 0.84, H * 0.32, H * 0.55, '255, 196, 120', 0.22);
      cgRocks(g, W, H, H * 0.97, 8, 2.4, 'rgba(6,7,10,.96)', 2.2);
      cgMotes(g, W, H, 120, 12.3, '255, 232, 200', 0.34, 0.72);
      cgFgBlur(g, W, H, H * 0.985, 27.2, 'rgba(8,9,13,.9)', 1);
      cgPostFx(g, W, H, { grade: '#2a2a3a', gradeA: 0.26, vignette: 0.62, grainA: 9 });
    } else {
      // ⑧ 故事开篇：雾中苏醒，远处巨兽低吼，两条路的光一左一右
      cgSky(g, W, H, [[0, '#0c1018'], [0.36, '#1a1f28'], [0.7, '#33312e'], [1, '#594a38']]);
      cgCloudLayer(g, W, H, H * 0.1, H * 0.52, 21.4, [200, 200, 212], [70, 70, 82], 0.52, 1.35);
      cgLight(g, W * 0.5, H * 0.3, H * 0.62, '255, 236, 200', 0.24);
      cgGodRays(g, W * 0.56, H * 0.16, H * 0.8, 1.25, 9, '255, 236, 200', 0.10);
      // 远处雾里的巨兽：只是影子（低透明度 + 冷灰），暗示"世界还没走远"
      cgSprite(g, { art: 'boss/moltenwall.webp', x: W * 0.78, baseY: gy - 2, h: H * 0.30,
        tint: '#2b2d36', tintA: 0.6, alpha: 0.55, rim: '255, 226, 180', rimA: 0.18, rimBlur: 16, shadow: false });
      cgRidge(g, W, H, { baseY: gy + 6, amp: 130, seed: 41.7, color: ['#2a2b30', '#15161b'], rim: 'rgba(255,226,180,.16)', haze: '170, 168, 170', hazeA: 0.52 });
      cgGround(g, W, H, gy + 34, [78, 70, 58], [30, 28, 26], 43.1);
      cgDust(g, W, H, gy + 24, gy + 250, 46.8, [182, 180, 186], 0.3, 9);
      // 刚苏醒、重新上路的武者（高清立绘，带行进姿态）
      cgSprite(g, { art: 'cg/hero_run_1.png', x: W * 0.4, baseY: gy + 158, h: H * 0.40,
        tint: '#2a3038', tintA: 0.28, lift: 0.12, rim: '255, 240, 210', rimA: 0.55, rimBlur: 16 });
      cgQiStream(g, W * 0.4, gy + 96, W * 0.14, H * 0.34, -70, '160, 200, 255', 5, 0.34);
      cgQiStream(g, W * 0.4, gy + 96, W * 0.66, H * 0.36, 70, '255, 196, 120', 5, 0.34);
      cgLight(g, W * 0.14, H * 0.34, H * 0.34, '160, 200, 255', 0.3);
      cgLight(g, W * 0.66, H * 0.36, H * 0.34, '255, 196, 120', 0.28);
      // 前景迷雾（把近景压住，画面立刻有纵深）
      const mist = g.createLinearGradient(0, gy + 60, 0, H);
      mist.addColorStop(0, 'rgba(196, 196, 210, 0)');
      mist.addColorStop(0.55, 'rgba(186, 188, 202, .28)');
      mist.addColorStop(1, 'rgba(170, 172, 188, .5)');
      g.fillStyle = mist;
      g.fillRect(0, gy + 60, W, H - gy - 60);
      cgGrass(g, W, H, H * 0.99, 26, 5.5, 'rgba(30, 34, 30, .85)');
      cgMotes(g, W, H, 140, 9.4, '255, 240, 210', 0.4, 0.72);
      cgFgBlur(g, W, H, H * 0.99, 44.6, 'rgba(9,10,13,.88)', 1);
      cgPostFx(g, W, H, { grade: '#2a3038', gradeA: 0.24, vignette: 0.58, grainA: 8 });
    }
    cgBloom(g, W, H, 0.34);      // 泛光放在最后一步，亮部才会"发光"
  }
  /* 取（或生成）某张分镜的位图；生成过程较重，所以只做一次并缓存。
   *   画完当前这张后，顺手把下一张也预生成好（异步），换页时就不会卡一下。 */
  function cgSlideBitmap(slide) {
    if (!slide) return null;
    if (cgBitmaps[slide.id]) return cgBitmaps[slide.id];
    // 这张要用的立绘还没到：先不画（等下一帧），别拿"空画面"去缓存
    if (!cgArtReady(CG_SLIDE_ART[slide.id])) return null;
    const c = document.createElement('canvas');
    c.width = CG_PAINT_W;
    c.height = CG_PAINT_H;
    const g = c.getContext('2d');
    cgPaintScene(g, slide.id, CG_PAINT_W, CG_PAINT_H);
    cgBitmaps[slide.id] = c;
    cgPrePaintNext(slide);
    return c;
  }
  /* 预生成下一张：先把那张要用的立绘下下来，再画。
   *   （提前一张的时间去准备，换页时就不会出现"素材还没到"的兜底画面） */
  function cgPrePaintNext(slide) {
    const idx = CG_SLIDES.indexOf(slide);
    const next = CG_SLIDES[idx + 1];
    if (!next || cgBitmaps[next.id] || cgPainting[next.id]) return;
    cgPainting[next.id] = true;
    const need = CG_SLIDE_ART[next.id] || [];
    cgPreloadArt(need, 1200).then(function () {
      if (!cgBitmaps[next.id]) {
        const c = document.createElement('canvas');
        c.width = CG_PAINT_W; c.height = CG_PAINT_H;
        cgPaintScene(c.getContext('2d'), next.id, CG_PAINT_W, CG_PAINT_H);
        cgBitmaps[next.id] = c;
      }
      cgPainting[next.id] = false;
    });
  }
  // 资源加载完 → （需要的话先播开场 CG）→ 进入主场景
  function cgGateThenStart() {
    if (cgShouldPlay()) { playCgIntro(startScene); return; }
    startScene();
  }

  // ---------- 冒险道路的敌人品级轮换 ----------
  // 纯查询：下一个该刷什么品级（不消耗进度）
  function roadNextEnemyTier() {
    if (roadMagicKills >= ROAD_GOLD_AFTER) return 'elite';
    if (roadCommonKills >= ROAD_BLUE_AFTER) return 'magic';
    return 'common';
  }
  // 真正刷怪时取一次：取到蓝色 / 金色就消耗对应进度
  function roadTakeNextEnemyTier() {
    const tier = roadNextEnemyTier();
    if (tier === 'elite') roadMagicKills = 0;
    else if (tier === 'magic') roadCommonKills = 0;
    return tier;
  }
  // 击杀结算时累计：普通→蓝色保底，蓝色→金色保底
  function roadKillTick(tier) {
    if (tier === 'common') {
      roadCommonKills = Math.min(ROAD_BLUE_AFTER, roadCommonKills + 1);
      markSaveDirty();
    } else if (tier === 'magic') {
      roadMagicKills = Math.min(ROAD_GOLD_AFTER, roadMagicKills + 1);
      markSaveDirty();
    }
  }

  /* 敌人属性统一口径：
   *   · 10 / 20 / 30 级普通基础属性额外 ×3
   *   · 蓝色困难 = 白色普通 ×3.5
   *   · 金色稀有（精英挑战）= 白色普通 ×8（原 10，已按需求降低 20%）
   *   · 传奇保留高于金色的定位 */
  function enemyLevelStatBoost(level) {
    return (level === 10 || level === 20 || level === 30) ? 3 : 1;
  }
  function enemyBaseStats(level) {
    const boost = enemyLevelStatBoost(level);
    return {
      hp: Math.round((62 + level * 16) * boost),
      atk: Math.round((8 + level * 3) * boost)
    };
  }
  const ENEMY_TIER_STAT_MULT = {
    common: { hp: 1, atk: 1 },
    magic:  { hp: 3.5, atk: 3.5 },
    elite:  { hp: 8, atk: 10 },      // 金色稀有：基础伤害 8 → 10 倍（更疼）
    legend: { hp: 15, atk: 10 }
  };
  /* 金色稀有敌人的"元素伤害"：
   *   · 普通攻击额外附带一部分攻击力的元素伤害；
   *   · 火球 / 雷电这类元素技能的倍率再乘一档。 */
  const ELITE_ELEM_ATK_PCT = 0.25;     // 普攻附带 25% 攻击力的元素伤害
  const ELITE_ELEM_SKILL_MULT = 1.35;  // 元素技能伤害再 ×1.35
  function enemyScaledStats(level, tier) {
    const base = enemyBaseStats(level);
    const mult = ENEMY_TIER_STAT_MULT[tier] || ENEMY_TIER_STAT_MULT.common;
    return {
      hp: Math.max(1, Math.round(base.hp * mult.hp)),
      atk: Math.max(1, Math.round(base.atk * mult.atk))
    };
  }

  // ---------- 敌人生成 ----------
  function spawnNextEnemy() {
    if (cave) return; // 洞府里用 spawnCaveEnemy
    // 敌人等级由所处的路程决定：0~10km 为 Lv.1，之后每 10km 提升一级
    let x = START_X + FIRST_ENEMY_DIST + defeatedEnemies * ENEMY_GAP;
    // 读档续玩 / 瞬移后：敌人必须刷在玩家前方，否则会一路遇不到人
    if (x < player.worldX + 300) x = player.worldX + rand(700, 1100);
    const km = Math.max(0, (x - START_X) / KM_UNIT);
    const level = 1 + Math.floor(km / ENEMY_LEVEL_KM);
    // 战斗用移速：围绕 100% 随机浮动，随等级略增
    const combatSpeed = clamp(1 + level * 0.02 + rand(-0.06, 0.05), 0.88, 1.8);
    const kind = randEnemySprite();
    /* 普通冒险道路按击杀保底轮换：3 普通 → 1 蓝色；3 蓝色 → 1 金色稀有（精英）。
     *   其它入口（洞府 / 传奇地图 / 临时副本）不走这里，因此不会被计入。 */
    const tier = roadTakeNextEnemyTier();
    const stats = enemyScaledStats(level, tier);
    const prefix = tier === 'elite' ? '精英' : '';
    activeEnemy = {
      sprite: kind.key,
      kindName: kind.name,
      name: prefix + kind.name,
      elite: tier === 'elite',
      tier: tier,
      level: level,
      baseX: x,             // 敌人巡逻区域的中心
      x: x,                 // 敌人当前世界坐标（随巡逻变化）
      offset: 0,
      dir: Math.random() < 0.5 ? -1 : 1,
      speed: rand(20, 42),
      combatSpeed: combatSpeed,
      openingAttack: false,
      range: rand(55, 95),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),
      hurtT: 0,
      maxHp: stats.hp,
      hp: stats.hp,
      atk: stats.atk
    };
    // 金色稀有：和精英拦路者一样自带一个技能
    if (tier === 'elite') {
      const a = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
      activeEnemy.skill = { id: a.id, name: a.name, type: a.type };
      activeEnemy.healUsed = false;
    }
  }

  // ---------- 精英挑战 ----------
  function playerLevel() {
    return player.level || 1;
  }

  function spawnEliteEnemy(count) {
    const level = playerLevel();
    const n = Math.max(1, Math.min(3, Math.floor(count || randInt(1, 3))));
    const x0 = player.worldX + 900;
    const group = [];
    for (let i = 0; i < n; i++) {
      const e = buildEnemy(level, 'elite', { worldX: x0 + i * 118 });
      e.eliteChallenge = true;
      e.baseX = x0 + i * 118;
      e.x = e.baseX;
      group.push(e);
    }
    activeEnemy = group[0];
    pendingEliteGroup = group;
    battleGroup.length = 0;
    group.forEach(function (e) { battleGroup.push(e); });
    return group;
  }

  // 传奇敌人：比精英更强，携带两个技能，掉落奖励更好
  function spawnLegendEnemy() {
    const level = playerLevel() + 2;
    const x = player.worldX + 900;
    const stats = enemyScaledStats(level, 'legend');
    const a = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
    const b = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
    const skillA = { id: a.id, name: a.name, type: a.type };
    const skillB = { id: b.id, name: b.name, type: b.type };
    const kindL = randEnemySprite();
    activeEnemy = {
      sprite: kindL.key,
      kindName: kindL.name,
      name: '传奇' + kindL.name,
      elite: true,
      legend: true,
      tier: 'legend',
      skill: skillA,
      skills: [skillA, skillB],
      healUsed: false,
      level: level,
      baseX: x,
      x: x,
      offset: 0,
      dir: -1,
      speed: rand(8, 18),
      combatSpeed: clamp(1 + level * 0.03 + rand(0, 0.1), 1, 2.2),
      openingAttack: false,
      range: rand(30, 60),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),
      hurtT: 0,
      maxHp: stats.hp,
      hp: stats.hp,
      atk: stats.atk
    };
  }

  function tierName(tier) {
    return tier === 'legend' ? '传奇' : tier === 'elite' ? '精英' : tier === 'magic' ? '蓝色' : '普通';
  }

  // 敌人的显示名：优先用类型名（兵匪 / 乞丐 …），没有就退回品级名
  function enemyLabel(e, withLevel) {
    if (!e) return '';
    const base = e.name || tierName(e.tier);
    return withLevel === false ? base : base + '（Lv.' + e.level + '）';
  }

  // ---------- 敌人美术：取帧 & 按锚点贴图 ----------
  function enemySpriteDef(enemy) {
    const key = enemy && enemy.sprite;
    for (let i = 0; i < ENEMY_SPRITES.length; i++) if (ENEMY_SPRITES[i].key === key) return ENEMY_SPRITES[i];
    return ENEMY_SPRITES[0];
  }

  // 只有移动帧：赶路按"一轮 ENEMY_WALK_CYCLE 秒"播，战斗站定时慢慢循环
  function enemyFrameImage(enemy, fps) {
    const def = enemySpriteDef(enemy);
    const t = enemyAnimClock + (enemy && enemy.animPhase ? enemy.animPhase : 0);
    const idx = Math.abs(Math.floor(t * fps)) % def.frames;
    const im = images[def.key + '_' + (idx + 1)];
    return im && im.complete && im.naturalWidth > 0 ? im : null;
  }

  // 世界BOSS 立绘：锚点在水平中心、脚底压在落地线上；带呼吸起伏与缓慢摇摆
  function drawWorldBossArt(img, sx, groundY, scale) {
    if (!img) return false;
    const k = scale * WB_BOSS_ART.scaleAdj;      // 导出图是显示尺寸的 2 倍
    const w = WB_BOSS_ART.w * k;
    const h = WB_BOSS_ART.h * k;
    const breathe = Math.sin(enemyAnimClock * 1.45) * 3.2;      // 呼吸起伏
    const sway = Math.sin(enemyAnimClock * 0.7) * 2.2;          // 左右微摆
    const top = groundY + 4 - (WB_BOSS_ART.h - WB_BOSS_ART.basePad) * k + breathe;
    // 脚下多一层厚重的暗影，压住这个庞然大物
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = 'rgba(48, 16, 8, .55)';
    ctx.beginPath();
    ctx.ellipse(sx - 10 * k, groundY + 6, 92 * k, 11 * k, -0.04, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.drawImage(img, sx - w / 2 + sway, top, w, h);
    lastBossArtRect = { x: Math.round(sx - w / 2 + sway), y: Math.round(top), w: Math.round(w), h: Math.round(h),
      centerX: Math.round(sx), bottom: Math.round(top + h) };
    return true;
  }

  /* ============================================================
   * 世界BOSS 时装 → 身后的 BOSS 虚影
   *   穿上某件世界BOSS 时装（defs.js 里配了 phantom: '<bossId>'）后，
   *   冒险 / 战斗画面里玩家身后会浮起该 BOSS 的虚影：
   *     · 半透明偏冷的"魂体"色调（避免和本体一样实）
   *     · 比本体小得多（战场立绘 665×608，虚影高约 268）
   *     · 脚底与玩家踩同一条落地线，水平紧贴玩家"背后"（朝向的反侧）
   *     · 画在玩家之前 → 永远在玩家身后
   * ============================================================ */
  const WB_GHOST = {
    h: 268,          // 虚影高度（玩家立绘约 195px，比玩家高一头）
    behind: 18,      // 相对玩家中心，朝"背后"再偏一点
    alpha: 0.4,      // 整体透明度
    tint: 'rgba(158, 96, 214, .5)'   // 魂体染色（偏紫）
  };
  let ghostTintCache = { key: '', canvas: null };
  let lastPhantomRect = null;   // 调试/测试用：虚影这一帧画在哪

  /* ------------------------------------------------------------
   * 世界BOSS 时装：击杀充能 → 给一个技能充能 → 那一次伤害 500%~1000%
   *   · 穿着「世界BOSS 时装」（定义里带 phantom 的那种）才会积累充能
   *   · 每击败一名普通敌人 +5%、精英敌人 +15%，攒到 100% 就"充能完毕"
   *   · 在技能窗口里挑一个技能点「⚡充能」→ 掷一次增幅（500%~1000%）
   *   · 该技能下一次施放时伤害按这个增幅放大（同一次施放的多段 / 飞行物都算），然后消耗掉
   * ------------------------------------------------------------ */
  const COSTUME_CHARGE_PER_KILL = 1;         // 普通敌人：+1%
  const COSTUME_CHARGE_PER_ELITE = 3;        // 精英敌人：+3%
  const COSTUME_CHARGE_PER_BOSS = 15;        // 世界BOSS：+15%
  const COSTUME_CHARGE_FULL = 100;
  const COSTUME_CHARGE_MIN_PCT = 500;        // 增幅下限 500%
  const COSTUME_CHARGE_MAX_PCT = 1000;       // 增幅上限 1000%
  const COSTUME_CHARGE_LINGER_MS = 5000;     // 兜底：充能用掉后最多再挂 5 秒（等飞行物落地）
  const COSTUME_CHARGE_WINDOW_MS = 2500;     // （保留给 UI/文档的时间窗口说明）
  let costumeCharge = 0;                     // 0 ~ 100 充能进度
  let costumeChargeUsed = 0;                 // 统计：已经消耗过几次充能（调试用）
  let costumeChargeActive = null;            // { skillId, pct, t0, token } 已挂到某个技能上的充能
  let castToken = 0;                         // 每次 castSkill 递增：充能只放大它绑定的那一次施放

  // 穿着世界BOSS 时装吗（有虚影的时装 = 世界BOSS 时装）
  function hasBossCostume() { return !!wornBossPhantom(); }

  // 击杀敌人 → 积累充能（labelText 只用于战报，例如「精英敌人」/「世界BOSS「森特欧斯萨满」」）
  function addCostumeCharge(pct, labelText) {
    if (!hasBossCostume()) return false;
    if (costumeCharge >= COSTUME_CHARGE_FULL) return false;      // 满了就存着，等玩家去用
    costumeCharge = Math.min(COSTUME_CHARGE_FULL, costumeCharge + pct);
    if (costumeCharge >= COSTUME_CHARGE_FULL) {
      showToast('时装充能完毕：去技能窗口挑一个技能「⚡充能」（伤害 ' +
        COSTUME_CHARGE_MIN_PCT + '%~' + COSTUME_CHARGE_MAX_PCT + '% 增幅）');
      pushBattleLog('时装充能完毕（' + (labelText || '击杀结算') +
        '）：可在技能窗口给一个技能充能', 'reward');
      if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    }
    return true;
  }

  // 在技能窗口点「⚡充能」：满充能才能用，一次只能挂一个技能
  function chargeSkillWithCostume(skill) {
    if (!skill) return null;
    if (!hasBossCostume()) { showToast('需要先装备一件世界BOSS 时装（带虚影的那种）才能充能。'); return null; }
    if (costumeCharge < COSTUME_CHARGE_FULL) {
      showToast('充能进度 ' + Math.round(costumeCharge) + '% / 100%：击杀敌人可以积累充能。');
      return null;
    }
    if (costumeChargeActive) {
      showToast('已经给「' + (skillById(costumeChargeActive.skillId) || {}).name + '」充过能了，先用掉再充下一个。');
      return null;
    }
    const pct = randInt(COSTUME_CHARGE_MIN_PCT, COSTUME_CHARGE_MAX_PCT);
    costumeChargeActive = { skillId: skill.id, pct: pct, t0: 0 };
    costumeCharge = 0;                       // 进度清零，重新攒
    showToast('已给「' + skill.name + '」充能：下一次施放伤害 +' + pct + '%');
    pushBattleLog('时装充能：「' + skill.name + '」下一次施放伤害 +' + pct + '%', 'reward');
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return { skill: skill.name, pct: pct };
  }

  // 伤害结算用：这个技能这一次施放吃到的充能倍率（没有就是 1）
  function costumeChargeMult(skillId) {
    const a = costumeChargeActive;
    if (!a || a.skillId !== skillId) return 1;
    const nowMs = () => ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now());
    if (a.token === undefined) {
      // 第一次结算 → 把充能绑到"当前这一次施放"上（同一次施放的多段 / 飞行物都算）
      a.token = castToken;
      a.t0 = nowMs();
      const nm = (skillById(skillId) || {}).name || skillId;
      pushBattleLog('时装充能生效：「' + nm + '」这一次施放伤害 +' + a.pct + '%', 'reward');
      addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 206, '充能 +' + a.pct + '%', '#c89bff');
    }
    // 已经不是这一次施放了 → 该充能作废（正常在 castSkill 里就清掉了，这里是兜底）
    if (a.token !== castToken) { costumeChargeActive = null; costumeChargeUsed++; return 1; }
    return 1 + a.pct / 100;
  }
  /* 主循环：一次施放的结算窗口过了还没被下一次施放清掉 → 兜底释放
   *   （powerShot 的箭矢要飞一会儿才落地，所以给 5 秒的宽限） */
  function updateCostumeCharge() {
    const a = costumeChargeActive;
    if (!a || a.token === undefined || !a.t0) return;
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    if (now - a.t0 > COSTUME_CHARGE_LINGER_MS) {
      costumeChargeActive = null;
      costumeChargeUsed++;
      if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    }
  }

  // 当前穿着的时装是否带虚影；带了就返回它要用的立绘
  function wornBossPhantom() {
    const worn = equippedCostume('head');
    if (!worn) return null;
    const def = costumeDef(worn.id);
    const bossId = def && def.phantom;
    if (!bossId) return null;
    const key = wbBossArtKey(bossId);
    const img = images[key];
    if (!img || !img.complete || img.naturalWidth <= 0) return { bossId: bossId, key: key, img: null };
    return { bossId: bossId, key: key, img: img };
  }

  // 把立绘染成"魂体色"（只染不透明部分，缓存起来不每帧重画）
  function ghostTinted(key, img) {
    if (ghostTintCache.key === key && ghostTintCache.canvas) return ghostTintCache.canvas;
    const c = document.createElement('canvas');
    c.width = img.naturalWidth; c.height = img.naturalHeight;
    const g = c.getContext('2d');
    g.drawImage(img, 0, 0);
    g.globalCompositeOperation = 'source-atop';   // 只覆盖有像素的地方
    g.fillStyle = WB_GHOST.tint;
    g.fillRect(0, 0, c.width, c.height);
    g.globalCompositeOperation = 'source-over';
    ghostTintCache = { key: key, canvas: c };
    return c;
  }

  /* faceDir：玩家朝向（+1 朝右 / -1 朝左）。虚影站在朝向的**反侧**＝身后。
   * 返回 true 表示这一帧确实画了虚影 */
  function drawBossPhantom(sx, groundY, scale, faceDir, alpha) {
    const art = wornBossPhantom();
    if (!art) { lastPhantomRect = null; return false; }
    if (!art.img) { lastPhantomRect = null; return false; }   // 图还没加载出来：先不画
    const k = (WB_GHOST.h * scale) / WB_BOSS_ART.h;           // 按"目标高度"换算
    const w = WB_BOSS_ART.w * k;
    const h = WB_BOSS_ART.h * k;
    const breathe = Math.sin(enemyAnimClock * 1.25) * 3 * scale;   // 轻微呼吸
    const sway = Math.sin(enemyAnimClock * 0.55) * 2 * scale;      // 缓慢摇摆
    const cx = sx - faceDir * WB_GHOST.behind * scale + sway;
    // 脚底和玩家踩同一条线（和本体一样留 basePad）
    const top = groundY + 4 - (WB_BOSS_ART.h - WB_BOSS_ART.basePad) * k + breathe;
    const a = (alpha === undefined ? 1 : alpha) * WB_GHOST.alpha;
    ctx.save();
    // 背后一圈魂气，让虚影和场景分层
    const g = ctx.createRadialGradient(cx, groundY - h * 0.52, 6, cx, groundY - h * 0.52, h * 0.62);
    g.addColorStop(0, 'rgba(126, 74, 196, ' + (0.22 * a).toFixed(3) + ')');
    g.addColorStop(1, 'rgba(126, 74, 196, 0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, groundY - h * 0.52, h * 0.62, 0, Math.PI * 2);
    ctx.fill();
    // 本体：魂体色 + 半透明
    ctx.globalAlpha = a;
    ctx.drawImage(ghostTinted(art.key, art.img), cx - w / 2, top, w, h);
    // 再叠一层淡淡的亮边，像"虚影在发光"
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = a * 0.35;
    ctx.drawImage(ghostTinted(art.key, art.img), cx - w / 2 - 1.5, top - 1.5, w + 3, h + 3);
    ctx.restore();
    lastPhantomRect = {
      bossId: art.bossId, key: art.key,
      x: Math.round(cx - w / 2), y: Math.round(top), w: Math.round(w), h: Math.round(h),
      centerX: Math.round(cx), bottom: Math.round(top + h), alpha: Math.round(a * 100) / 100
    };
    return true;
  }

  // 整帧贴图：锚点（腿脚重心）对齐到 sx，脚底基线固定在 groundY + 4
  // def 可选：传入该模型定义后，可用 def.faceRight 单独覆盖素材朝向
  function drawEnemySprite(img, sx, groundY, scale, faceRight, bob, def) {
    if (!img) return false;
    const artFacesRight = def && def.faceRight !== undefined ? def.faceRight : ENEMY_ART_FACES_RIGHT;
    const flip = artFacesRight ? !faceRight : !!faceRight;
    const w = ENEMY_FRAME.w * scale;
    const h = ENEMY_FRAME.h * scale;
    const top = groundY + 4 - (ENEMY_FRAME.h - ENEMY_FRAME.basePad) * scale + (bob || 0) * scale;
    const ax = ENEMY_FRAME.anchorX * scale;
    if (flip) {
      ctx.save();
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -ax, top, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(img, sx - ax, top, w, h);
    }
    return true;
  }

  // 传奇BOSS地图第 N 波敌人
  function spawnWaveEnemy(tier) {
    const level = playerLevel() + (tier === 'legend' ? 2 : 0);
    const x = player.worldX + KM_UNIT;
    activeEnemy = buildEnemy(level, tier, { worldX: x });
  }

  // 按品级造一个敌人（洞府 / 地图波次共用）
  function buildEnemy(level, tier, opts) {
    const options = opts || {};
    const x = options.worldX !== undefined ? options.worldX : player.worldX + 900;
    const stats = enemyScaledStats(level, tier);
    /* 用哪套模型：副本可以指定（options.sprite，比如深渊入侵的邪恶法师），
     * 其它地方照旧从通用池里随机挑一种（兵匪、乞丐、强盗…）。 */
    const kind = (options.sprite && enemySpriteByKey(options.sprite)) || randEnemySprite();
    const prefix = tier === 'elite' ? '精英' : tier === 'legend' ? '传奇' : '';
    const enemy = {
      sprite: kind.key,          // 用哪套美术
      kindName: kind.name,       // 类型名（兵匪 / 乞丐 …）
      name: prefix + kind.name,  // 显示名：普通怪就是类型名，精英/传奇会带前缀
      tier: tier,
      elite: tier === 'elite' || tier === 'legend',
      legend: tier === 'legend',
      level: level,
      baseX: x,
      x: x,
      offset: 0,
      dir: -1,
      speed: rand(12, 26),
      combatSpeed: clamp(1 + level * 0.02 + rand(0, 0.06), 0.9, 2.1),
      openingAttack: false,
      range: rand(45, 80),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),   // 让同类敌人的帧错开，不要整齐划一
      hurtT: 0,
      maxHp: stats.hp,
      atk: stats.atk
    };
    enemy.hp = enemy.maxHp;
    // 洞府里的敌人随机带一种特殊增幅
    if (options.cave) enemy.caveBuff = CAVE_BUFFS[randInt(0, CAVE_BUFFS.length - 1)];
    if (tier === 'elite' || tier === 'legend') {
      const a = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
      const skillA = { id: a.id, name: a.name, type: a.type };
      enemy.skill = skillA;
      if (tier === 'legend') {
        const b = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
        const skillB = { id: b.id, name: b.name, type: b.type };
        enemy.skills = [skillA, skillB];
      }
      enemy.healUsed = false;
    }
    return enemy;
  }

  function updateLegendBar() {
    if (!ui.legendBar) return;
    if (!legendMap) {
      ui.legendBar.classList.add('hidden');
      return;
    }
    ui.legendBar.classList.remove('hidden');
    ui.legendWave.textContent = legendMap.wave + ' / 10 波';
    ui.legendKm.textContent = (legendMap.km || 0).toFixed(1) + ' / 10 km';
  }

  function spawnNextLegendWave() {
    if (!legendMap) return;
    legendMap.wave += 1;
    const wave = legendMap.wave;
    const tier = wave >= 10 ? 'legend' : (wave % 3 === 1 ? 'common' : wave % 3 === 2 ? 'magic' : 'elite');
    spawnWaveEnemy(tier);
    updateLegendBar();
    pushBattleLog('传奇BOSS地图 第 ' + wave + ' / 10 波：' + tierName(tier) + '敌人出现', 'system');
    showToast('第 ' + wave + ' / 10 波：' + tierName(tier) + '敌人出现！');
  }

  function useLegendTicket(item) {
    if (!item || item.type !== 'legendTicket') return;
    if (gameState !== 'walk') {
      showToast('只能在上路时使用传奇BOSS挑战卷。');
      return;
    }
    if (legendMap) {
      showToast('你已经在传奇BOSS地图中了。');
      return;
    }
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    legendMap = { wave: 0, startX: player.worldX, km: 0 };
    updateEliteButton();
    spawnNextLegendWave();
    input.moving = true;
    syncMoveButton();
    showToast('传奇BOSS挑战开始：10km 路程，共 10 波敌人！');
  }

  // ---------- 精英挑战按钮：超时平移到右侧边缘 ----------
  const ELITE_DOCK_WAIT = 10;    // 秒：在屏幕中央等待点击的时间
  const ELITE_DOCK_MARGIN = 14;  // 贴到右边缘时留的边距
  const ELITE_DOCK_SCALE = 0.62; // 停靠后缩小的比例（和 CSS 里的 scale 保持一致）
  let eliteDockWait = ELITE_DOCK_WAIT; // 调试可改
  let eliteDockTimer = null;
  let eliteDocked = false;
  let eliteDockPending = false;  // 计时到了但按钮当时被藏起来（战斗中），等重新出现再靠边

  // 平移量：让按钮右边缘贴住舞台右侧（Y 轴不动）
  function eliteDockShift() {
    if (!ui.wrap || !ui.btnElite) return 0;
    const wrapRect = ui.wrap.getBoundingClientRect();
    // 按钮水平居中 + 停靠时会缩小，所以右边缘按缩放后的宽度算
    const halfW = (ui.btnElite.offsetWidth || 122) / 2;
    const centeredRight = wrapRect.left + wrapRect.width / 2 + halfW * ELITE_DOCK_SCALE;
    return Math.round((wrapRect.right - ELITE_DOCK_MARGIN) - centeredRight);
  }

  function armEliteDockTimer() {
    if (eliteDockTimer) clearTimeout(eliteDockTimer);
    eliteDockTimer = setTimeout(function () {
      eliteDockTimer = null;
      dockEliteButton();
    }, Math.max(0, eliteDockWait) * 1000);
  }

  function dockEliteButton() {
    if (!ui.btnElite) return;
    if (ui.btnElite.classList.contains('hidden')) {
      // 战斗中按钮被临时藏起来：记下来，等它重新出现时直接靠边
      eliteDockPending = true;
      return;
    }
    const shift = eliteDockShift();
    ui.btnElite.style.setProperty('--eliteShift', shift + 'px');
    eliteDocked = true;
    eliteDockPending = false;
    ui.btnElite.classList.add('docked');
  }

  // 复原到屏幕中央（下次出现时重新计时）
  function resetEliteDock() {
    if (eliteDockTimer) {
      clearTimeout(eliteDockTimer);
      eliteDockTimer = null;
    }
    eliteDocked = false;
    eliteDockPending = false;
    if (!ui.btnElite) return;
    ui.btnElite.classList.remove('docked');
    ui.btnElite.style.removeProperty('--eliteShift');
  }

  // 窗口尺寸变化（4399 全屏 / 手机旋转）：贴边位置跟着重算
  window.addEventListener('resize', function () {
    layoutHud();
    if (!eliteDocked || !ui.btnElite) return;
    const shift = eliteDockShift();
    if (shift > 0) ui.btnElite.style.setProperty('--eliteShift', shift + 'px');
  });

  // ---------- HUD 自适应：舞台（#wrap）变矮时，左侧按钮列收紧、聊天窗变矮 ----------
  // 否则小窗口下聊天窗会盖住「世界BOSS / 抽卡」等按钮，点不到。
  let hudLayoutClock = 0;
  function layoutHud() {
    const wrapEl = ui.wrap || document.getElementById('wrap');
    if (!wrapEl) return;
    if (ui.hud && ui.hud.classList.contains('hidden')) return;   // 还没进游戏就不用量了
    const wrapH = wrapEl.clientHeight || 720;
    const k = clamp(wrapH / 720, 0.5, 1);
    const tight = k < 0.88;
    const veryTight = k < 0.75;
    if (ui.hud) {
      ui.hud.classList.toggle('hudTight', tight);
      ui.hud.classList.toggle('hudVeryTight', veryTight);
    }
    // 聊天窗：窗口越矮越扁（文字大小不变，只减行数）
    const chatH = Math.max(70, Math.round(148 * k * k));
    wrapEl.style.setProperty('--chatListH', chatH + 'px');
    /* 左侧功能列（养成 / 交易两组）依次往下排：从「金币 / 活力」那一行下面开始，
     * 每组按实际高度顺排，两组之间留一点间距。
     * 新增按钮后列会变长 —— 排完检查有没有压到聊天窗，压到就把整列等比缩小再排。 */
    const groups = document.querySelectorAll('#hud > .hudMenu');
    const gapPx = Math.round((tight ? 10 : 14) * k);
    const chatEl = ui.chatPanel || document.getElementById('chatPanel');
    const wrapTop = wrapEl.getBoundingClientRect().top;
    let colScale = tight ? 0.9 : 1;
    for (let guard = 0; guard < 10; guard++) {
      if (ui.hud) ui.hud.style.setProperty('--hudColScale', colScale.toFixed(2));
      let top = 126;
      for (let i = 0; i < groups.length; i++) {
        groups[i].style.top = Math.round(top) + 'px';
        const h = (groups[i].offsetHeight || 180) * colScale;
        top += h + (i === groups.length - 1 ? 0 : gapPx);
      }
      const bottom = top;   // 最后一组的视觉底部
      const chatTop = chatEl && !chatEl.classList.contains('hidden')
        ? (chatEl.getBoundingClientRect().top - wrapTop) : wrapEl.clientHeight;
      if (bottom <= chatTop - 8 || colScale <= 0.62) break;
      colScale = Math.max(0.62, colScale - 0.05);
    }
    // 战斗条也跟着舞台高度微调，避免小窗口压住按钮列
    if (ui.wbBar) ui.wbBar.style.top = Math.round(66 * Math.max(0.7, k)) + 'px';
  }

  function updateEliteButton() {
    if (!ui.btnElite) return;
    const show = eliteCharges > 0 && gameState === 'walk' && !legendMap && !cave;
    ui.btnElite.classList.toggle('hidden', !show);
    ui.eliteCount.textContent = String(Math.max(1, eliteCharges));
    // 中央出现后 10 秒内没人点，就平移到右侧边缘继续等着（Y 轴不变）
    if (show) {
      if (eliteDocked) {
        // 已经靠边了：战斗中藏起来再出现也保持靠边缩小，不再回到屏幕中央
        eliteDockPending = false;
      } else if (eliteDockPending) {
        dockEliteButton();
      } else if (!eliteDockTimer) {
        armEliteDockTimer();
      }
    }
    // 隐藏（战斗 / 洞府 / 传奇地图）时**不重置**停靠状态：
    // 计时器照常走，到点会记成 eliteDockPending，重新出现时直接靠边。
  }

  function learnSkillFromPool(pool) {
    const available = pool.filter(function (s) {
      return (!s.minStage || player.level >= s.minStage) &&
        !knownSkills.some(function (k) { return k.id === s.id; });
    });
    if (!available.length) return null;
    return learnSkillFromPoolById(available[randInt(0, available.length - 1)]);
  }

  // 按 id 学一个技能（灵宝传承用）
  function learnSkillById(id) {
    if (!id) return null;
    // 门派武学也在可学列表里（但它们不会出现在技能石 / 灵宝的随机池里）
    const all = ACTIVE_SKILL_POOL.concat(SKILL_POOL).concat(SECT_SKILLS);
    for (let i = 0; i < all.length; i++) {
      if (all[i].id === id) return learnSkillFromPoolById(all[i]);
    }
    return null;
  }

  function makeActiveSkillStone() {
    return {
      id: 'stone' + (++itemSeq),
      type: 'skillStone',
      name: '主动技能石',
      icon: '技',
      detail: '使用后随机习得一个主动技能',
      rarityColor: '#ffd265'
    };
  }

  function makeLegendStone() {
    return {
      id: 'lg' + (++itemSeq),
      type: 'legendStone',
      name: '传奇Boss召唤石',
      icon: '召',
      detail: '使用后在身前召唤传奇敌人Boss（更强、奖励更好）',
      rarityColor: '#ff9be0'
    };
  }

  function useLegendStone(item) {
    if (!item || item.type !== 'legendStone') return;
    if (gameState !== 'walk') {
      showToast('只能在路上使用传奇Boss召唤石。');
      return;
    }
    if (activeEnemy && activeEnemy.legend) {
      showToast('传奇敌人已经在前方了。');
      return;
    }
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    spawnLegendEnemy();
    input.moving = true;
    syncMoveButton();
    showToast('传奇Boss召唤石生效：传奇敌人出现在前方！');
  }

  function makeLegendTicket() {
    return {
      id: 'tk' + (++itemSeq),
      type: 'legendTicket',
      name: '传奇BOSS挑战卷',
      icon: '卷',
      detail: '使用后开启 10km 传奇BOSS地图（共 10 波敌人）',
      rarityColor: '#ffb3f0'
    };
  }

  function makePetEgg() {
    return {
      id: 'egg' + (++itemSeq),
      type: 'petEgg',
      name: '宠物蛋',
      icon: '蛋',
      detail: '使用后孵化随机品级宠物（蝎子 / 鬣狗 / 毒蛇 / 猎鹰）',
      rarityColor: '#c8ffd9'
    };
  }

  function makePetSkillBook() {
    return {
      id: 'book' + (++itemSeq),
      type: 'petBook',
      name: '宠物技能书',
      icon: '书',
      detail: '对宠物使用：20% 几率提升品级与属性',
      rarityColor: '#ffe9a8'
    };
  }

  function petTierName(tier) {
    return tier === 'rare' ? '稀有' : tier === 'magic' ? '蓝色' : '普通';
  }

  function usePetEgg(item) {
    if (!item || item.type !== 'petEgg') return;
    // 宠物蛋孵化的也是沙漠生物（与召唤技能同一套素材）
    const pick = PET_SPRITE_KEYS[randInt(0, PET_SPRITE_KEYS.length - 1)];
    const name = PET_SPRITES[pick].name;
    const roll = Math.random();
    const tier = roll < 0.5 ? 'common' : roll < 0.85 ? 'magic' : 'rare';
    const mult = tier === 'rare' ? 1.5 : tier === 'magic' ? 1.15 : 0.85;
    const p = {
      name: name,
      tier: tier,
      level: petLevelOf(tier),
      sprite: pick,
      source: '宠物蛋孵化',
      sourceKind: 'egg',
      atkMult: mult,
      hpMult: mult,
      hp: 1,
      maxHp: 1,
      hurtT: 0,
      animT: 0,
      attackT: 0,
      walkT: rand(0, 10)
    };
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    addPet(p);
    refreshPetStats();
    p.hp = p.maxHp;
    recalcPlayerStats(); // 宠物在场 → 占用 50% 灵力上限
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
    showToast('孵化出' + petTierName(tier) + '宠物「' + name + '」（等级 ' + p.level + '）！');
  }

  function usePetSkillBook(item) {
    if (!item || item.type !== 'petBook') return;
    if (!petAlive()) {
      showToast('还没有宠物。装备法杖会出现灵宠，学会召唤技能也能召唤宠物。');
      return;
    }
    // 随机挑一只出战宠物来强化
    const target = alivePets()[randInt(0, alivePets().length - 1)];
    const success = Math.random() < 0.2;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (success) {
      target.tier = target.tier === 'common' ? 'magic' : 'rare';
      target.level = petLevelOf(target.tier);
      target.atkMult = (target.atkMult || 1) * 1.15;
      target.hpMult = (target.hpMult || 1) * 1.15;
      sortPets();
      refreshPetStats();
      target.hp = target.maxHp;
      recalcPlayerStats(); // 召唤物在场 → 占用 50% 灵力上限
      if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
      showToast('技能书融入成功！「' + target.name + '」提升为' + petTierName(target.tier) + '宠物（等级 ' + target.level + '）。');
    } else {
      showToast('技能书没有融入，宠物似乎不太感兴趣……');
    }
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
  }

  function useActiveSkillStone(item) {
    if (!item || item.type !== 'skillStone') return;
    usingStone = item;
    // 不再单独弹窗：直接跳到「技能」窗口，在里面点击技能学习 / 升级
    closeGearPanel();
    openSkillPanel();
    showToast('正在使用主动技能石：点击想学习或升级的技能。');
  }

  // 消耗一颗技能石，并把模式切换到下一颗（没有就退出技能石模式）
  function consumeStone(message) {
    if (!usingStone) return;
    inventory = inventory.filter(function (it) { return it.id !== usingStone.id; });
    const next = inventory.filter(function (it) { return it.type === 'skillStone'; })[0];
    usingStone = next || null;
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (usingStone) {
      renderSkillPanel();
      showToast('主动技能石生效：' + message + '　还有 ' + countSkillStones() + ' 颗技能石可继续使用。');
    } else {
      closeSkillPanel();
      showToast('主动技能石生效：' + message);
    }
    saveGame();
  }

  function countSkillStones() {
    return inventory.filter(function (it) { return it.type === 'skillStone'; }).length;
  }

  // 技能石模式下点击技能：未学会 → 学习；已学会 → 升 1 级
  function useStoneOnSkill(defId, learned) {
    if (!usingStone) {
      showToast(learned ? '需要主动技能石才能升级技能。' : '需要主动技能石才能学习这个技能。');
      return;
    }
    const known = knownSkills.filter(function (k) { return k.id === defId; })[0];
    if (known) {
      const lv = (known.level || 1) + 1;
      known.level = lv;
      consumeStone('「' + known.name + '」升级到 Lv.' + lv);
      return;
    }
    const def = visibleSkillDefs().filter(function (e) { return e.def.id === defId; })[0];
    if (!def) return;
    const skill = learnSkillFromPoolById(def.def);
    if (!skill) return;
    consumeStone('学会新技能「' + skill.name + '」');
  }

  // 按技能定义直接学习（技能石自选）
  function learnSkillFromPoolById(def) {
    if (!def) return null;
    if (knownSkills.some(function (k) { return k.id === def.id; })) return null;
    const skill = {
      id: def.id,
      name: def.name,
      desc: def.desc,
      cost: def.cost,
      type: def.type,
      level: 1,
      sockets: []
    };
    if (def.dmg) skill.dmg = [def.dmg[0], def.dmg[1]];
    knownSkills.push(skill);
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return skill;
  }

  function tryEliteChallenge() {
    if (gameState !== 'walk' || eliteCharges <= 0) return;
    if (cave) {
      showToast('洞府里不能发起精英挑战，先离开洞府。');
      return;
    }
    eliteCharges -= 1;
    // 用掉一次机会：如果后面还有次数，按钮保持原样；用完则复位（下次出现重新居中计时）
    if (eliteCharges <= 0) resetEliteDock();
    updateEliteButton();
    // 精英挑战不再打断自动冒险：自动冒险会继续自动应战（含精英 / 传奇）
    const group = spawnEliteEnemy();
    input.moving = true;
    syncMoveButton();
    showToast('精英拦路者 ×' + group.length + ' 出现（每个自带技能），准备迎战！');
  }

  // ---------- 马路上的易拉罐 ----------
  function spawnCan() {
    cans.push({
      worldX: player.worldX + rand(900, 1500),
      y: GROUND_Y + 30 + rand(0, 8),
      vx: -rand(90, 170),
      rot: rand(0, Math.PI * 2),
      spin: -rand(2, 4),
      kicked: false,
      vy: 0,
      kvx: 0
    });
  }

  function updateCans(dt) {
    if (gameState !== 'walk') return;
    canTimer -= dt;
    if (canTimer <= 0) {
      canTimer = rand(7, 13);
      if (cans.length < 3) spawnCan();
    }
    for (let i = cans.length - 1; i >= 0; i--) {
      const c = cans[i];
      if (c.homing) {
        c.homingT += dt;
        const u = clamp(c.homingT / c.homingDur, 0, 1);
        c.worldX = c.homingStartX + (c.homingTargetX - c.homingStartX) * u;
        c.y = c.homingStartY + (GROUND_Y - 118 - c.homingStartY) * u -
          Math.sin(u * Math.PI) * 118;
        c.rot += c.spin * dt;
        if (u >= 1) {
          triggerCanEnemyStrike(c);
          cans.splice(i, 1);
        }
      } else if (c.kicked) {
        c.worldX += c.kvx * dt;
        c.y += c.vy * dt;
        c.vy += 980 * dt;
        c.rot += c.spin * dt;
        if (c.y > H + 140 || c.worldX - cameraX() > W + 260) cans.splice(i, 1);
      } else {
        c.worldX += c.vx * dt;
        c.rot += c.spin * dt;
        if (c.worldX < player.worldX - 340) cans.splice(i, 1);
      }
    }
  }

  // ---------- 马路上的遗迹洞府 ----------
  function pickCaveDef() {
    const total = CAVE_DEFS.reduce(function (s, d) { return s + d.weight; }, 0);
    let r = Math.random() * total;
    for (let i = 0; i < CAVE_DEFS.length; i++) {
      r -= CAVE_DEFS[i].weight;
      if (r <= 0) return CAVE_DEFS[i];
    }
    return CAVE_DEFS[0];
  }

  // 每隔一段路在玩家前方生成一个洞府入口
  function updateCaveSpawn() {
    if (legendMap || cave || caveTransition) return;
    if (sectScene) return;   // 门派山门里不刷洞府入口
    // 角色只能前进：已经落在身后的入口直接清掉，不再占着名额
    for (let i = caveEntrances.length - 1; i >= 0; i--) {
      if (caveEntrances[i].worldX < player.worldX - 2600) caveEntrances.splice(i, 1);
    }
    if (caveNextX <= 0) {
      caveNextX = player.worldX + rand(4200, 6200);
      return;
    }
    if (player.worldX < caveNextX) return;
    // 前方不远处已经有入口就顺延，避免挤在一起（不会再永久卡住）
    if (caveEntrances.some(function (c) {
      return !c.used && c.worldX > player.worldX - 1500;
    })) {
      caveNextX = player.worldX + rand(2600, 4200);
      return;
    }
    caveEntrances.push({
      def: pickCaveDef(),
      worldX: player.worldX + rand(1600, 2800),
      used: false
    });
    caveNextX = player.worldX + rand(5200, 7600);
  }

  function caveEntranceAt(sx, sy) {
    for (let i = 0; i < caveEntrances.length; i++) {
      const c = caveEntrances[i];
      const cxs = c.worldX - cameraX();
      // 命中范围跟着美术资源走（宽度按素材实际比例，底座压在路面下沿）
      if (Math.abs(sx - cxs) < CAVE_IMG_H * caveImgRatio() * 0.5 + 10 &&
          sy > GROUND_Y - CAVE_IMG_H - 40 && sy < H) return c;
    }
    return null;
  }

  // 点击洞府：金色洞府要消耗令牌，进洞后手动探索
  function clickCaveEntrance(entrance) {
    if (cave) {
      showToast('已经在「' + cave.def.name + '」里了，先离开再进别的洞府。');
      return;
    }
    if (gameState !== 'walk') return;
    if (entrance.def.token && caveToken <= 0) {
      showToast('「' + entrance.def.name + '」需要金色洞府令牌，去活力兑换里换一枚（' +
        CAVE_TOKEN_COST + ' 活力）。');
      return;
    }
    if (entrance.def.token) {
      caveToken -= 1;
      saveGame();
      showToast('消耗 1 枚金色洞府令牌，进入「' + entrance.def.name + '」。');
    }
    // 绿色 / 蓝色洞府可以随时进出：入口不消耗，通关后才标记为已探索
    beginCaveEnter(entrance.def, entrance);
  }

  // ---------- 进入洞府的过场动画：走近洞口 → 缩小没入 → 进入洞府 ----------
  const CAVE_ENTER_DUR = 1.25;
  let caveTransition = null;
  const playerFx = { scale: 1, alpha: 1 };

  function beginCaveEnter(def, entrance) {
    if (caveTransition || cave) return;
    input.moving = false;
    syncMoveButton();
    const dust = [];
    for (let i = 0; i < 30; i++) {
      dust.push({
        t: 0,
        life: rand(0.5, 1.15),
        ang: rand(0, Math.PI * 2),
        r: rand(8, 46),
        vr: rand(60, 190),
        size: rand(1.5, 4.5),
        vy: rand(-30, -6)
      });
    }
    caveTransition = {
      t: 0,
      dur: CAVE_ENTER_DUR,
      fromX: player.worldX,
      toX: entrance.worldX - 26,
      def: def,
      entrance: entrance,
      dust: dust
    };
    showToast('正在进入「' + def.name + '」……');
  }

  function updateCaveTransition(dt) {
    if (!caveTransition) return;
    const ct = caveTransition;
    ct.t += dt;
    ct.dust.forEach(function (d) { d.t += dt; });
    const p = clamp(ct.t / ct.dur, 0, 1);
    const walkEnd = 0.55;

    if (p <= walkEnd) {
      // 阶段一：走向洞口（播放走路动画）
      const k = p / walkEnd;
      const e = k * k * (3 - 2 * k);
      player.worldX = ct.fromX + (ct.toX - ct.fromX) * e;
      player.vx = 1;
      player.walkT += dt * 8;
      playerFx.scale = 1;
      playerFx.alpha = 1;
    } else {
      // 阶段二：缩小、变淡，被洞口吞没
      const k = (p - walkEnd) / (1 - walkEnd);
      player.worldX = ct.toX;
      player.vx = 0;
      playerFx.scale = Math.max(0.12, 1 - k * 0.88);
      playerFx.alpha = Math.max(0, 1 - k * 1.1);
    }

    if (p >= 1) {
      const def = ct.def;
      const entrance = ct.entrance;
      caveTransition = null;
      playerFx.scale = 1;
      playerFx.alpha = 1;
      enterCave(def, entrance);
    }
  }

  // 过场特效：洞口的光尘与扩散光环 + 收尾时的颜色白闪
  function drawCaveEnterFx() {
    if (!caveTransition) return;
    const ct = caveTransition;
    const p = clamp(ct.t / ct.dur, 0, 1);
    const def = ct.def;
    const sx = ct.entrance.worldX - cameraX();
    const baseY = GROUND_Y - 90;

    // 光环
    const rings = 3;
    for (let i = 0; i < rings; i++) {
      const rp = clamp((p - i * 0.08) / 0.55, 0, 1);
      if (rp <= 0 || rp >= 1) continue;
      ctx.strokeStyle = def.glow.replace(/[\d.]+\)$/, (0.5 * (1 - rp)).toFixed(3) + ')');
      ctx.lineWidth = 3 - rp * 2;
      ctx.beginPath();
      ctx.ellipse(sx, baseY, 40 + rp * 170, 26 + rp * 110, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 光尘
    ct.dust.forEach(function (d) {
      if (d.t >= d.life) return;
      const k = d.t / d.life;
      const r = d.r + d.vr * d.t;
      const x = sx + Math.cos(d.ang) * r;
      const y = baseY + Math.sin(d.ang) * r * 0.6 + d.vy * d.t * k;
      ctx.fillStyle = def.glow.replace(/[\d.]+\)$/, (0.7 * (1 - k)).toFixed(3) + ')');
      ctx.beginPath();
      ctx.arc(x, y, d.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // 结尾：颜色闪光覆盖，衔接洞内画面
    if (p > 0.6) {
      const k = (p - 0.6) / 0.4;
      ctx.fillStyle = def.glow.replace(/[\d.]+\)$/, (0.55 * k).toFixed(3) + ')');
      ctx.fillRect(0, 0, W, H);
    }
  }

  function enterCave(def, entrance) {
    if (autoAdventure) stopAutoAdventure('洞府内需要手动探索，已退出自动冒险。');
    input.moving = false;
    syncMoveButton();
    cave = { def: def, remaining: def.enemies, total: def.enemies, killed: 0, entrance: entrance || null };
    activeEnemy = null;
    battleGroup.length = 0;
    addBattleLogSafe('进入「' + def.name + '」：里面共有 ' + def.enemies + ' 个敌人，需要手动探索。', 'reward');
    updateCaveBar();
    showToast('进入「' + def.name + '」（敌人 ' + def.enemies + ' 个）· 手动点击「移动」前进，点击「离开洞府」可随时出去。');
    spawnCaveEnemy();
    dailyQuestTick('cave');      // 每日任务：进入一次洞府
    sectTaskTick('cave');        // 门派任务：完成一次洞府探险（进门即计数，框架口径）
  }

  function leaveCave() {
    // 在山门里点的是「离开山门」
    if (sectScene) { leaveSectScene('你离开了' + sectName() + '山门，回到冒险路上。'); return; }
    if (!cave) return;
    const killed = cave.killed;
    const name = cave.def.name;
    const wasActivity = !!cave.def.activity;
    // 如果在战斗中离开，必须把战斗状态一起收干净，否则战斗色调 / 战斗 UI 会一直留着
    if (gameState === 'battle') {
      pendingVictory = 0;
      battleTurn = 'player';
      enemyTurnTimer = 0;
      playerIdleTimer = 0;
      slashFx = null;
      clearDefendState();
      airSlashFx = null;
      damagePopups = [];
      ally = null;
      enemyMinionTurns = 0;
      battleGroup.length = 0;
      showBattleUi(false);
      setControls('walk');
      syncMoveButton();
      gameState = 'walk';
    }
    cave = null;
    battleGroup.length = 0;
    activeEnemy = null;
    if (wasActivity) {
      activityNetLeave();
      activityRun = null;
      clearActivityLootFlags();   // 活着走出副本：这次带出来的战利品算"安全到手"
    }
    updateCaveBar();
    addBattleLogSafe('离开「' + name + '」（本次击败 ' + killed + ' 个敌人）。', 'system');
    showToast('已离开「' + name + '」，回到马路上。');
    spawnNextEnemy();
  }

  // 洞府里敌人品级按洞府类型抽取
  /* ============================================================
   * 洞府敌人的特殊增幅
   *   每个洞府里的敌人随机带一种（野外敌人没有）：
   *     震慑    首次攻击把你击晕 1 回合
   *     烈焰光环 每秒灼烧你 10% 最大生命（真实伤害），脚下有火焰光环
   *     点燃    每次攻击点燃你（每层每回合掉 1% 生命），手上有火焰
   *     寒冰    攻击时 30% 几率冰冻你 1 回合，脚下有寒冰光环
   *     反伤    被你攻击时反伤 50% 物理伤害
   * ============================================================ */
  const CAVE_BUFFS = [
    { id: 'stunFirst', name: '震慑', short: '震', color: '#ffd76a', desc: '首次攻击把你击晕 1 回合' },
    { id: 'flameAura', name: '烈焰光环', short: '焰', color: '#ff8a3c', desc: '每秒灼烧你 10% 最大生命（真实伤害）' },
    { id: 'ignite', name: '点燃', short: '燃', color: '#ff6a3c', desc: '每次攻击点燃你（每层每回合 1% 生命）' },
    { id: 'freeze', name: '寒冰', short: '冰', color: '#8fd7ff', desc: '攻击时 30% 几率把你冰冻 1 回合' },
    { id: 'reflect', name: '反伤', short: '反', color: '#c0a0ff', desc: '被你攻击时反伤 50% 物理伤害' }
  ];
  const CAVE_FREEZE_PCT = 30;      // 寒冰：攻击时冰冻几率（%）
  const CAVE_BURN_MAX = 5;         // 点燃：玩家身上最多叠几层
  const CAVE_AURA_PCT = 10;        // 烈焰光环：每秒灼烧 % 最大生命
  const CAVE_REFLECT_PCT = 50;     // 反伤：反弹 % 物理伤害
  const PLAYER_SKIP_TURN_SEC = 0.9;// 被控住跳过回合后，隔多久敌人再动

  // 敌人一次攻击打中玩家后，结算它带的增幅（震慑 / 点燃 / 寒冰）
  function caveEnemyOnHit(enemy) {
    if (!enemy || !enemy.caveBuff) return;
    const b = enemy.caveBuff;
    const px = player.worldX - cameraX();
    if (b.id === 'stunFirst') {
      if (enemy.stunUsed) return;
      enemy.stunUsed = true;
      /* 锁链战靴：免疫晕眩（震慑照样消耗掉，只是晕不住你） */
      if (derived.stunImmune) {
        addTextPopup(px - 88, GROUND_Y - 214, '免疫晕眩', '#9fd4ff');
        pushBattleLog('「' + enemyLabel(enemy, false) +
          '」的震慑被「锁链战靴」免疫了（免疫晕眩）', 'player');
        return;
      }
      playerStunTurns = Math.max(playerStunTurns, 1);
      addTextPopup(px - 88, GROUND_Y - 214, '被击晕！', '#ffd76a');
      pushBattleLog('「' + enemyLabel(enemy, false) + '」的首次攻击把你击晕了：下一回合无法行动', 'enemy');
      return;
    }
    if (b.id === 'ignite') {
      playerBurnStacks = Math.min(CAVE_BURN_MAX, playerBurnStacks + 1);
      addTextPopup(px - 88, GROUND_Y - 244, '点燃 ×' + playerBurnStacks, '#ff8a3c');
      pushBattleLog('你被「' + enemyLabel(enemy, false) + '」点燃了（' + playerBurnStacks +
        ' 层，每回合损失 ' + (playerBurnStacks * Math.max(1, Math.round(combat.maxHp * 0.01))) + ' 点真实伤害）', 'enemy');
      return;
    }
    if (b.id === 'freeze' && Math.random() * 100 < CAVE_FREEZE_PCT) {
      playerFrozenTurns = Math.max(playerFrozenTurns, 1);
      addTextPopup(px - 88, GROUND_Y - 214, '被冰冻！', '#8fd7ff');
      pushBattleLog('「' + enemyLabel(enemy, false) + '」的寒冰把你冻住了：下一回合无法行动（' +
        CAVE_FREEZE_PCT + '% 几率）', 'enemy');
    }
  }

  // 玩家身上的「点燃」：每次敌方回合结算一次，每层 1% 最大生命真实伤害
  function applyPlayerBurnTick() {
    if (playerBurnStacks <= 0) return 0;
    const dmg = Math.max(1, Math.round(combat.maxHp * 0.01)) * playerBurnStacks;
    combat.hp = Math.max(0, combat.hp - dmg);
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 226, dmg, '#ff8a3c');
    pushBattleLog('你身上的燃烧（' + playerBurnStacks + ' 层）造成 ' + dmg + ' 点真实伤害', 'enemy');
    updateBattleHud();
    return dmg;
  }

  // 被洞府敌人反伤：反弹本次物理伤害的 50%（按护甲/抗性折算后打在玩家身上）
  function caveReflectOnPlayerHit(enemy, dmg) {
    if (!enemy || !enemy.caveBuff || enemy.caveBuff.id !== 'reflect') return 0;
    if (!dmg || dmg <= 0 || enemy.hp <= 0) return 0;
    const reduction = playerDamageReduction();
    const back = Math.max(1, Math.round(dmg * (CAVE_REFLECT_PCT / 100) * (1 - reduction)));
    combat.hp = Math.max(0, combat.hp - back);
    player.hurtT = HURT_TIME;
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 168, back, '#c0a0ff');
    pushBattleLog('「' + enemyLabel(enemy, false) + '」反伤 ' + back + ' 点（你造成 ' + dmg +
      ' 的 ' + CAVE_REFLECT_PCT + '%，已按护甲/抗性折算）', 'enemy');
    updateBattleHud();
    return back;
  }

  // 玩家被击晕 / 冰冻：跳过这一回合（敌人接着动一次）
  function consumePlayerSkipTurn() {
    if (playerStunTurns <= 0 && playerFrozenTurns <= 0) return false;
    let why;
    if (playerFrozenTurns > 0) { playerFrozenTurns -= 1; why = '被寒冰冻住'; }
    else { playerStunTurns -= 1; why = '被击晕'; }
    addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 190, '无法行动', '#9fd4ff');
    setBattleLog('你' + why + '，这一回合无法行动！');
    pushBattleLog('你' + why + '，跳过本回合（敌人继续行动）', 'enemy');
    battleTurn = 'enemy';
    enemyTurnTimer = PLAYER_SKIP_TURN_SEC;
    setControls('battle');
    updateBattleHud();
    return true;
  }

  // 「烈焰光环」：每秒灼烧 10% 最大生命（真实伤害），敌人脚下有火焰光环
  function updateCaveEnemyBuffs(dt) {
    if (gameState !== 'battle') { caveAuraClock = 0; return; }
    const aura = battleAlive().filter(function (e) {
      return e.caveBuff && e.caveBuff.id === 'flameAura';
    });
    if (!aura.length) { caveAuraClock = 0; return; }
    caveAuraClock += dt;
    if (caveAuraClock < 1) return;
    caveAuraClock -= 1;
    const dmg = Math.max(1, Math.round(combat.maxHp * (CAVE_AURA_PCT / 100)));
    combat.hp = Math.max(0, combat.hp - dmg);
    player.hurtT = Math.max(player.hurtT, 0.12);
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 202, dmg, '#ff8a3c');
    pushBattleLog('「烈焰光环」灼烧：' + dmg + ' 点真实伤害（每秒 ' + CAVE_AURA_PCT + '% 最大生命）', 'enemy');
    updateBattleHud();
  }

  /* 洞府 / 临时副本里敌人的等级：
   *   普通洞府 = 玩家等级（金色 +2、蓝色 +1）；
   *   临时副本 = 跟着玩家等级走，但被夹在「对应境界」的区间里
   *     （minEnemyStage ~ enemyStage）：同境界玩家打自己水平的怪，
   *      低境界硬闯高阶副本则会直接对上那个境界的怪 —— 很危险，也捡不到漏。 */
  function caveEnemyLevel(def) {
    if (!def) return playerLevel();
    if (def.activity) {
      const lo = clamp(def.minEnemyStage || 1, 1, MAX_STAGE);
      const hi = clamp(def.enemyStage || playerLevel(), lo, MAX_STAGE);
      return clamp(playerLevel(), lo, hi);
    }
    return playerLevel() + (def.id === 'gold' ? 2 : def.id === 'blue' ? 1 : 0);
  }

  /* 这张副本地图用哪些敌人模型：
   *   · def.sprites      —— 普通 / 魔法杂兵（深渊入侵 = 食尸者 / 蛆虫兽 / 步兵 / 怨魂）
   *   · def.eliteSprites —— 精英（稀有）那一档（深渊入侵 = 破败守墓骑士）
   *   · def.boss         —— 关卡BOSS（深渊入侵 = 邪恶法师，只在最后一只出场）
   * 没配就从通用池随机 —— 洞府 / 其它副本照旧。 */
  function pickCaveSprite(def, tier) {
    if (!def) return null;
    const isElite = (tier === 'elite' || tier === 'legend');
    let list = isElite && def.eliteSprites && def.eliteSprites.length ? def.eliteSprites : def.sprites;
    if (!list || !list.length) return null;
    return list[randInt(0, list.length - 1)];
  }

  /* 关卡BOSS（每日副本最后一只）：血更厚、打得更疼、体型更大，头顶挂「关卡BOSS」牌子 */
  const DUNGEON_BOSS_HP_MULT = 2.5;
  const DUNGEON_BOSS_ATK_MULT = 1.3;
  const DUNGEON_BOSS_SCALE = 1.3;
  function makeEnemyBoss(enemy, def) {
    if (!enemy) return enemy;
    const spriteDef = enemySpriteByKey(enemy.sprite);
    enemy.boss = true;
    enemy.elite = true;                  // 精英光环 / 技能提示 / 血条配色都按精英那套走
    enemy.legend = false;
    enemy.kindName = spriteDef ? spriteDef.name : (enemy.kindName || '敌人');
    enemy.name = (def && def.bossTitle ? def.bossTitle + '·' : '关卡BOSS·') + enemy.kindName;
    enemy.maxHp = Math.max(1, Math.round(enemy.maxHp * DUNGEON_BOSS_HP_MULT));
    enemy.hp = enemy.maxHp;
    enemy.atk = Math.max(1, Math.round(enemy.atk * DUNGEON_BOSS_ATK_MULT));
    enemy.bossScale = DUNGEON_BOSS_SCALE;
    return enemy;
  }

  function spawnCaveEnemy() {
    if (!cave || cave.remaining <= 0) return;
    const def = cave.def;
    /* 关卡BOSS：这张地图配了 def.boss 时，清到只剩最后一只就换成 BOSS 出场 */
    const isBoss = !!def.boss && !cave.bossSpawned && cave.remaining <= 1;
    const tier = isBoss ? (def.bossTier || 'elite') : def.tiers[randInt(0, def.tiers.length - 1)];
    const enemy = buildEnemy(caveEnemyLevel(def), tier, {
      worldX: player.worldX + (isBoss ? rand(620, 820) : rand(520, 760)),
      cave: true,
      sprite: isBoss ? def.boss : pickCaveSprite(def, tier)
    });
    if (isBoss) {
      cave.bossSpawned = true;
      makeEnemyBoss(enemy, def);
      addBattleLogSafe('★ 关卡BOSS「' + enemy.name + '」出现了！', 'enemy');
      setBattleLog('★「' + enemy.name + '」挡在前面——这是这一关的 BOSS。');
    }
    battleGroup.length = 0;
    battleGroup.push(enemy);
    activeEnemy = enemy;
    battleTurn = 'player';
    resetPlayerIdleTimer();
    enemyTurnTimer = 0;
    gameState = 'walk';
    updateCaveBar();
  }

  // 战斗中附近的敌人被吸引过来，变成多对一
  function tryCaveJoinBattle() {
    if (!cave || cave.remaining <= 1) return false;
    if (battleGroup.length >= CAVE_JOIN_MAX) return false;
    if (Math.random() >= CAVE_JOIN_CHANCE) return false;
    const def = cave.def;
    const tier = def.tiers[randInt(0, def.tiers.length - 1)];
    addCaveHelper(tier, def);
    addBattleLogSafe('附近的' + tierName(tier) + '敌人被战斗声引来，加入了战斗！（多对一）', 'enemy');
    // 追加在最新的战报后面，别把这一回合的围攻伤害提示顶掉
    const prevLog = ui.battleLog.textContent || '';
    setBattleLog((prevLog ? prevLog + ' ' : '') +
      '又有敌人加入围攻！现在同时面对 ' + battleGroup.filter(aliveEnemy).length + ' 个敌人。');
    updateBattleHud();
    updateCaveBar();
    return true;
  }

  // 新加入的敌人不会站得很远：从后方小跑过来，补到"当前敌人身后"的位置
  function addCaveHelper(tier, def) {
    const level = caveEnemyLevel(def);
    const anchor = activeEnemy ? activeEnemy.x : player.worldX + 600;
    const slot = battleGroup.length;                 // 第几个（0 是当前目标）
    const targetX = anchor + 118 + Math.max(0, slot - 1) * 96; // 当前敌人身后，间距 96
    const extra = buildEnemy(level, tier, {
      worldX: targetX + 260, cave: true, sprite: pickCaveSprite(def, tier)   // 先从更远处出现
    });
    extra.joined = true;
    extra.moveToX = targetX;                          // 再走到阵位
    battleGroup.push(extra);
    return extra;
  }

  function aliveEnemy(e) { return e && e.hp > 0; }

  // 当前这场战斗里还站着的敌人
  function battleAlive() {
    return battleGroup.filter(aliveEnemy);
  }

  // 目标被击倒：同伙还在就继续打（多对一），全清才结算胜利。
  // 返回 true 表示整场战斗结束（可以走原有胜利流程）
  function resolveEnemyDown() {
    /* 独特装备（暗金）：击倒「稀有金色（精英）及以上」的敌人时 0.03% 掉落（全域生效） */
    rollUniqueFromEnemy(activeEnemy);
    /* 门派任务：每倒下一个敌人都算一次「击败敌人 / 击败同境界敌人」
     *（放在这里而不是"整场胜利"那里 —— 多人围攻 / 洞府里逐个倒下也要计数） */
    sectKillTick(activeEnemy);
    if (cave) {
      cave.killed += 1;
      cave.remaining = Math.max(0, cave.remaining - 1);
      updateCaveBar();
    }
    const rest = battleAlive();
    if (rest.length) {
      activeEnemy = rest[0];
      activeEnemy.hp = Math.max(1, activeEnemy.hp);
      setBattleLog('你击倒了一个敌人，但附近还有 ' + rest.length + ' 个敌人围了上来！');
      pushBattleLog('击倒一名敌人，仍有 ' + rest.length + ' 名敌人（多对一）', 'enemy');
      updateBattleHud();
      battleTurn = 'player';
      resetPlayerIdleTimer();
      setControls('battle');
      return false;
    }
    return true;
  }

  function addBattleLogSafe(text, kind) {
    pushBattleLog(text, kind);
  }

  // 洞府状态条
  function updateCaveBar() {
    if (!ui.caveBar) return;
    /* 门派山门也用这条状态栏：显示门派名 + 提示，按钮改成「离开山门」 */
    if (!cave && sectScene) {
      ui.caveBar.classList.remove('hidden');
      ui.caveName.textContent = sectScene.def.name + ' · 山门';
      ui.caveName.style.color = sectScene.def.color || '#8fd0ff';
      ui.caveLeft.textContent = '点击路边建筑进入内部';
      ui.caveKilled.textContent = '建筑 ' + sectScene.buildings.length + ' 处　·　门派贡献 ' + (sect.contribution | 0);
      if (ui.caveLeave) ui.caveLeave.textContent = '离开山门';
      return;
    }
    const show = !!cave;
    ui.caveBar.classList.toggle('hidden', !show);
    if (!show) return;
    ui.caveName.textContent = cave.def.name;
    ui.caveName.style.color = cave.def.color;
    ui.caveLeft.textContent = '剩余敌人 ' + cave.remaining + ' / ' + cave.total;
    ui.caveKilled.textContent = '已击败 ' + cave.killed;
    // 临时公共副本：标出"活动"、剩余时间和公共地图属性
    if (cave.def.activity) {
      const left = activityRun ? activityRemainMs(activityRun, wbNow()) : 0;
      const floor = activityRun ? activityRun.floor : 1;
      const floors = activityRun ? activityRun.floors : 3;
      ui.caveLeft.textContent = '第 ' + floor + ' / ' + floors + ' 层 · 点击屏幕左右移动';
      ui.caveKilled.textContent = '已击败 ' + cave.killed + ' · 剩余 ' + activityTimeText(left) +
        ' · 点物品拾取 · 点玩家开战' +
        (netActCount > 1 ? (' · 同层道友 ' + (netActCount - 1)) : '');
      if (ui.caveLeave) ui.caveLeave.textContent = '离开副本';
    } else if (cave.def.invasion) {
      // 每日副本（入侵）：额外标出"这次是不是双倍场"
      ui.caveKilled.textContent = '已击败 ' + cave.killed +
        (cave.def.invasionDouble ? ' · ★双倍场（掉落 / 经验 / 金币 ×2）' : ' · 普通场');
      if (ui.caveLeave) ui.caveLeave.textContent = '离开副本';
    } else if (ui.caveLeave && ui.caveLeave.textContent !== '离开洞府') {
      ui.caveLeave.textContent = '离开洞府';
    }
  }

  // 清空洞府：发放通关奖励并回到马路
  function finishCave() {
    if (!cave) return;
    const def = cave.def;
    const killed = cave.killed;
    const rewardMul = dungeonRewardMul();          // 每日副本的"双倍场"：金币 / 掉落 ×2
    const invasionDef = def.invasion || null;      // 这一天的入侵（不是入侵副本就是 null）
    // 临时副本用 rewardTier 映射到洞府的奖励档位（green / blue / gold）
    const rewardTier = def.rewardTier || def.id;
    const goldGain = (rewardTier === 'gold' ? randInt(320, 520)
      : rewardTier === 'blue' ? randInt(140, 220) : randInt(60, 110)) * rewardMul;
    gold += goldGain;
    updateGold();
    const drops = [];
    let treasureDrop = null;   // 金色洞府的那件灵宝（战报要单独点名）
    for (let roll = 0; roll < rewardMul; roll++) {
      if (rewardTier === 'green') {
        drops.push(makeEquipment(playerLevel(), 'magic'));
      } else if (rewardTier === 'blue') {
        drops.push(makeEquipment(playerLevel(), 'rare'));
      } else {
        drops.push(makeEquipment(playerLevel(), 'rare'));
        drops.push(makeEquipment(playerLevel(), 'rare'));
        drops.push(makeActiveSkillStone());
        // 金色洞府终点：额外掉落一件「灵宝」传奇装备（自带特殊属性 / 主动技能）—— 只可能有一件
        if (!treasureDrop) {
          treasureDrop = makeTreasureItem(playerLevel() + 2);
          drops.push(treasureDrop);
        }
      }
    }
    /* 每日副本（入侵）：通关必给这一天的专属入侵材料（双倍场给两份） */
    const invasionMat = invasionMaterialOf(invasionDef);
    if (invasionMat) {
      for (let roll = 0; roll < rewardMul; roll++) drops.push(makeInvasionMaterial(invasionMat));
    }
    /* 深渊入侵：通关再必给一份「深渊战利品」（双倍场给两份） */
    if (invasionHasLoot(invasionDef)) {
      for (let roll = 0; roll < rewardMul; roll++) {
        const loot = makeDungeonLootItem(invasionDef);
        if (loot) drops.push(loot);
      }
    }
    /* 门派丹殿差事：洞府里采药 —— 每次通关采到 2~4 份「药材」（入侵副本不算采药） */
    const herbN = invasionDef ? 0 : randInt(2, 4);
    if (herbN) drops.push(makeSectMaterial('herb', herbN));
    drops.forEach(function (it) { inventory.push(it); });
    playSfx('pickup');                            // 通关奖励入袋：拾取音效
    if (cave.entrance) cave.entrance.used = true; // 通关后该入口不再可用
    cave = null;
    if (!invasionDef) {
      caveClearTotal += 1;                        // 破境任务：完成洞府探险 +1（副本不算）
      checkBottleneckTask();
      sectTaskTick('herb', herbN);                // 门派任务：采集药材
    }
    battleGroup.length = 0;
    updateCaveBar();
    addBattleLogSafe('通关「' + def.name + '」！共击败 ' + killed + ' 个敌人，获得 ' + goldGain +
      ' 金币与 ' + drops.length + ' 件战利品。' +
      (rewardMul > 1 ? '（★双倍场：金币 / 掉落 ×2）' : '') +
      (treasureDrop ? '（其中包含灵宝：' + treasureDrop.name + '）' : '') +
      (herbN ? ('（另采到药材 ×' + herbN + '）') : ''), 'reward');
    showToast('通关「' + def.name + '」！金币 +' + goldGain + '，战利品 ' + drops.length + ' 件已放入背包。' +
      (invasionMat ? (' 专属材料「' + invasionMat.name + '」已入袋。') : '') +
      (treasureDrop ? ' 灵宝「' + treasureDrop.name + '」到手！' : '') +
      (herbN ? (' 药材 ×' + herbN + ' 已入袋。') : ''));
    spawnNextEnemy();
    updateEliteButton();
    updateDist();
  }

  // 当前屏幕上是否有一个可被易拉罐锁定的前方敌人
  function kickCanEnemyTarget() {
    if (!activeEnemy || activeEnemy.hp <= 0) return null;
    if (activeEnemy.x <= player.worldX + 20) return null;   // 只打前方
    const sx = activeEnemy.x - cameraX();
    if (sx < 20 || sx > W - 20) return null;               // 必须在当前屏幕内
    return activeEnemy;
  }

  function triggerCanEnemyStrike(c) {
    const enemy = c.homingEnemy;
    if (!enemy || enemy.hp <= 0 || activeEnemy !== enemy || gameState !== 'walk') {
      lastCanStrike = { hit: false, reason: 'target_gone' };
      return false;
    }
    // 强制拉入战斗
    startBattle();
    activeEnemy.stunTurns = Math.max(1, activeEnemy.stunTurns || 0);
    activeEnemy.hurtT = HURT_TIME;
    pushBattleLog('易拉罐飞出，正中「' + (activeEnemy.name || '敌人') + '」！敌人被击晕 1 回合', 'reward');
    showToast('易拉罐击中敌人，强制进入战斗并击晕 1 回合！');
    lastCanStrike = { hit: true, name: activeEnemy.name, stunned: true, stunTurns: activeEnemy.stunTurns };
    return true;
  }

  function kickCan(c) {
    lingRollDrop('你一脚踢开易拉罐');     // 灵脉：踢罐子有几率拾到灵气编号
    if (c.kicked) return;
    const target = kickCanEnemyTarget();
    // 50%：易拉罐飞向前方屏幕内的敌人，强制开战并击晕 1 回合
    if (target && Math.random() < 0.5) {
      c.kicked = true;
      c.homing = true;
      c.homingEnemy = target;
      c.homingT = 0;
      c.homingDur = 0.52;
      c.homingStartX = c.worldX;
      c.homingStartY = c.y;
      c.homingTargetX = target.x - 32;
      c.spin = rand(12, 20);
      showToast('易拉罐飞向前方的敌人！');
      return;
    }
    c.kicked = true;
    c.kvx = rand(340, 620);
    c.vy = -rand(330, 470);
    c.spin = rand(9, 16);
    showToast('你一脚把易拉罐踢飞了！');
    // 有 1% 概率从易拉罐里掉出一颗辅助技能石
    if (Math.random() < 0.01) {
      const opt = GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)];
      const gem = gemOptionToItem(opt);
      inventory.push(gem);
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      setTimeout(function () {
        showToast('易拉罐里滚出一颗辅助技能石：' + gem.detail);
      }, 900);
    }
    // 罐里可能还有脏水，较大概率溅玩家一身
    if (Math.random() < 0.7) splashDirtyWater();
  }

  function splashDirtyWater() {
    playerDirtyT = 7;
    const sx = player.worldX - cameraX();
    for (let i = 0; i < 16; i++) {
      dirtyDrops.push({
        x: sx + rand(-16, 16),
        y: GROUND_Y - rand(70, 150),
        vx: rand(-150, 150),
        vy: -rand(60, 230),
        t: 0,
        life: rand(0.5, 1),
        size: rand(2, 5)
      });
    }
    showToast('易拉罐里的脏水溅了你一身！');
  }

  // 调试/测试：在玩家前方放一个易拉罐并立即踢它
  window.__debugKickCan = function () {
    const c = {
      worldX: player.worldX + 220,
      y: GROUND_Y + 30,
      vx: 0, rot: 0, spin: 1,
      kicked: false, vy: 0, kvx: 0
    };
    cans.push(c);
    const target = kickCanEnemyTarget();
    kickCan(c);
    return {
      targetVisible: !!target,
      targetName: target ? target.name : null,
      kicked: !!c.kicked,
      homing: !!c.homing,
      homingDur: c.homingDur || null
    };
  };
  window.__debugCanStrike = function () { return lastCanStrike; };

  // ---------- 海市蜃楼：路上偶尔出现的"其他玩家"虚影 ----------
  const MIRAGE_CHANCE = 0.3;      // 每次检查时出现的概率（较低）
  const MIRAGE_CHECK_MIN = 20;    // 检查间隔（秒）
  const MIRAGE_CHECK_MAX = 40;
  const MIRAGE_FIRST_KM = 1;      // 第一个 1km 处必然出现一次
  let mirageFirstSpawned = false;
  const MIRAGE_MODES = ['walk', 'walk', 'walk', 'run', 'idle', 'battle'];

  function spawnMirage(forceMode, ahead) {
    // 出现在玩家身侧（身前一 / 身后），不会离太远
    const side = ahead ? 1 : (Math.random() < 0.5 ? -1 : 1);
    const dist = ahead ? rand(180, 300) : rand(140, 340);
    const mode = forceMode || MIRAGE_MODES[randInt(0, MIRAGE_MODES.length - 1)];
    mirage = {
      t: 0,
      life: rand(1.7, 2.7),      // 很快消失：1.7~2.7 秒
      worldX: player.worldX + side * dist,
      mode: mode,
      facing: side > 0 ? 1 : -1, // 面向自己前进的方向
      phase: rand(0, Math.PI * 2),
      walkT: rand(0, 10),
      clashT: 0,
    };
    if (mode === 'battle') {
      mirage.enemyWx = mirage.worldX + 150;
      mirage.enemyHurtT = 0;
      mirage.enemySprite = randEnemySprite().key;
    }
    return mirage;
  }

  function updateMirage(dt) {
    if (gameState === 'battle') return;   // 战斗中不推进、也不新刷虚影（战斗场景只留交战双方）
    if (mirage) {
      mirage.t += dt;
      mirage.walkT += dt * (mirage.mode === 'run' ? 16 : 8);
      if (mirage.clashT > 0) mirage.clashT = Math.max(0, mirage.clashT - dt);
      if (mirage.enemyHurtT > 0) mirage.enemyHurtT = Math.max(0, mirage.enemyHurtT - dt);
      // 战斗类虚影每隔一会儿"交手"一次
      if (mirage.mode === 'battle' && mirage.clashT <= 0) {
        mirage.clashT = rand(0.45, 0.8);
        mirage.enemyHurtT = 0.18;
      }
      if (mirage.t >= mirage.life) mirage = null;
      return;
    }
    // 只在野外马路上出现（洞府 / 传奇地图里不出）
    if (gameState !== 'walk' || cave || legendMap || caveTransition) {
      mirageTimer = Math.max(mirageTimer, 6);
      return;
    }
    // 第一个 1km 处必然出现一次（脚本化的"偶遇"，出现在前方更容易看到）
    if (!mirageFirstSpawned && (player.worldX - START_X) / KM_UNIT >= MIRAGE_FIRST_KM) {
      mirageFirstSpawned = true;
      spawnMirage(Math.random() < 0.5 ? 'walk' : 'run', true);
      return;
    }
    mirageTimer -= dt;
    if (mirageTimer > 0) return;
    mirageTimer = rand(MIRAGE_CHECK_MIN, MIRAGE_CHECK_MAX); // 下次检查间隔
    if (Math.random() < MIRAGE_CHANCE) spawnMirage();
  }

  function drawMirageGhost(img, worldX, groundY, scale, alpha, t, phase, flip, frameKind) {
    if (!img) return;
    const sx = worldX - cameraX();
    // frameKind: 'player' | 'enemy' = 整帧统一画布的新素材；其它 = 旧的 256×256 精灵图裁剪
    const whole = frameKind === 'player' || frameKind === 'enemy';
    const geom = frameKind === 'player' ? PLAYER_FRAME : ENEMY_FRAME;
    const src = whole
      ? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight }
      : { x: 42, y: 48, w: 176, h: 208 };
    const dw = (whole ? geom.w : 148) * scale;
    const dh = (whole ? geom.h : 178) * scale;
    const top = whole
      ? groundY + 4 - (geom.h - geom.basePad) * scale
      : groundY - dh + 4;
    const slices = 10;
    ctx.save();
    ctx.globalAlpha = alpha;
    // 幽灵感：压低饱和度、提高亮度
    try { ctx.filter = 'saturate(0.15) brightness(1.55)'; } catch (e) { /* 不支持就算了 */ }
    for (let i = 0; i < slices; i++) {
      const srcY = src.y + (src.h / slices) * i;
      const srcH = src.h / slices;
      // 热浪扭曲：越靠上抖动越明显
      const shimmer = Math.sin(t * 7 + phase + i * 0.85) * 3.2 * (1 - i / slices);
      ctx.save();
      if (flip < 0) {
        ctx.translate(sx, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(img, src.x, srcY, src.w, srcH, -dw / 2 + shimmer, top + (dh / slices) * i, dw, dh / slices);
      } else {
        ctx.drawImage(img, src.x, srcY, src.w, srcH, sx - dw / 2 + shimmer, top + (dh / slices) * i, dw, dh / slices);
      }
      ctx.restore();
    }
    ctx.restore();
  }

  function drawMirage() {
    /* 战斗中不画"虚影"行人：战斗场景只保留背景 + 交战双方，
     *   否则路上会同时出现好几个角色（虚影 + 敌人 + 自己）。 */
    if (gameState === 'battle') return;
    if (!mirage || !images.player_idle_1) return;
    const m = mirage;
    sceneActors.mirage = true;          // 调试/测试：这一帧确实画了虚影
    // 淡入很快、淡出也快
    const alpha = Math.min(1, m.t / 0.22) * Math.min(1, (m.life - m.t) / 0.5) * 0.62;
    if (alpha <= 0.01) return;
    const sx = m.worldX - cameraX();
    if (sx < -320 || sx > W + 320) return;

    const groundY = GROUND_Y + 4;

    // ① 沙尘热浪：一层朦胧的光晕
    const haze = ctx.createRadialGradient(sx, groundY - 80, 10, sx, groundY - 80, 170);
    haze.addColorStop(0, 'rgba(255, 246, 220, ' + (0.3 * alpha).toFixed(3) + ')');
    haze.addColorStop(0.45, 'rgba(255, 226, 176, ' + (0.16 * alpha).toFixed(3) + ')');
    haze.addColorStop(1, 'rgba(255, 210, 150, 0)');
    ctx.fillStyle = haze;
    ctx.beginPath();
    ctx.ellipse(sx, groundY - 80, 170, 130, 0, 0, Math.PI * 2);
    ctx.fill();

    // ② 脚下淡淡的影子
    ctx.fillStyle = 'rgba(120, 90, 60, ' + (0.18 * alpha).toFixed(3) + ')';
    ctx.beginPath();
    ctx.ellipse(sx - 10, groundY + 6, 54, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // ③ 虚影本体：走路 / 奔跑 / 站立 / 战斗（与玩家同一套素材）
    let frame = images.player_idle_1;
    if (m.mode === 'walk' || m.mode === 'run') {
      const step = Math.floor(m.walkT * (m.mode === 'run' ? 0.9 : 0.6)) % 4;
      frame = images['player_idle_' + (step + 1)] || frame;
    }
    drawMirageGhost(frame, m.worldX, groundY, 1, alpha, m.t, m.phase, m.facing, 'player');

    // ④ 战斗虚影：对面还有一个敌人，间隔闪出交手火花
    if (m.mode === 'battle' && images.enemyIdle) {
      const ex = m.enemyWx - cameraX();
      const ehaze = ctx.createRadialGradient(ex, groundY - 80, 10, ex, groundY - 80, 140);
      ehaze.addColorStop(0, 'rgba(255, 236, 210, ' + (0.24 * alpha).toFixed(3) + ')');
      ehaze.addColorStop(1, 'rgba(255, 210, 150, 0)');
      ctx.fillStyle = ehaze;
      ctx.beginPath();
      ctx.ellipse(ex, groundY - 80, 140, 110, 0, 0, Math.PI * 2);
      ctx.fill();
      const recoil = m.enemyHurtT > 0 ? 6 : 0;
      const ghostDef = enemySpriteDef({ sprite: m.enemySprite });
      const ghostImg = images[ghostDef.key + '_1'];
      drawMirageGhost(
        ghostImg && ghostImg.complete && ghostImg.naturalWidth > 0 ? ghostImg : images.enemyIdle,
        m.enemyWx + recoil, groundY, 1, alpha * 0.9, m.t + 1.3, m.phase + 2, -1,
        ghostImg && ghostImg.complete && ghostImg.naturalWidth > 0 ? 'enemy' : undefined
      );
      // 交手火花
      if (m.clashT > 0.12) {
        const k = Math.max(0, (m.clashT - 0.12) / 0.6);
        const fx = (sx + ex) / 2;
        const fy = groundY - 120;
        const g = ctx.createRadialGradient(fx, fy, 0, fx, fy, 46);
        g.addColorStop(0, 'rgba(255, 252, 240, ' + (0.75 * k * alpha).toFixed(3) + ')');
        g.addColorStop(0.4, 'rgba(255, 180, 120, ' + (0.45 * k * alpha).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255, 140, 80, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(fx, fy, 46, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ⑤ 顶部再压一层薄雾，强化"海市蜃楼"的边界
    ctx.save();
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = 'rgba(255, 240, 210, .5)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const yy = groundY - 40 - i * 42 + Math.sin(m.t * 4 + i) * 2;
      ctx.beginPath();
      ctx.moveTo(sx - 70 + i * 6, yy);
      ctx.quadraticCurveTo(sx, yy - 6, sx + 70 - i * 6, yy + 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ---------- 赶路扬尘：沙漠里跑起来身后扬起沙尘 ----------
  function getDustSprite() {
    if (dustSprite) return dustSprite;
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 48;
    const g = c.getContext('2d');
    const rg = g.createRadialGradient(32, 25, 1, 32, 25, 30);
    rg.addColorStop(0, 'rgba(240, 219, 180, 0.95)');
    rg.addColorStop(0.5, 'rgba(216, 184, 138, 0.5)');
    rg.addColorStop(1, 'rgba(198, 162, 112, 0)');
    g.fillStyle = rg;
    g.beginPath();
    g.ellipse(32, 25, 30, 21, 0, 0, Math.PI * 2);
    g.fill();
    dustSprite = c;
    return c;
  }

  // 在玩家脚后方撒一把尘土（back 用来让"刚停下"的那一团再靠后一点）
  function spawnRunDust(n, back) {
    const wx = player.worldX - cameraX();
    for (let i = 0; i < n; i++) {
      if (runDust.length >= 48) return;
      runDust.push({
        x: wx - rand(20, 46 + (back || 0)),   // 屏幕坐标：脚后跟偏后
        y: GROUND_Y - rand(2, 14),            // 脚踝高度（贴着地面往上一点）
        vx: -rand(18, 62),
        vy: -rand(3, 22),
        r: rand(4, 8.5),
        grow: rand(8, 20),
        t: 0,
        life: rand(0.42, 0.8)
      });
    }
  }

  function updateRunDust(dt) {
    // 临时副本里点屏幕左右走也要扬尘（向左走同样算）
    const running = gameState === 'walk' && !deathActive && playerMovingNow() && playerAtkT <= 0;
    updateRunSfx(running);
    if (running) {
      runDustTimer -= dt;
      if (runDustTimer <= 0) {
        // 跑得越快扬尘越密
        runDustTimer = rand(0.07, 0.13);
        spawnRunDust(player.vx > 210 ? 2 : 1, 0);
      }
    } else {
      runDustTimer = 0;
      if (wasRunning) spawnRunDust(2, 14);   // 刚停下：补一小团收尾的尘土
    }
    wasRunning = running;

    for (let i = runDust.length - 1; i >= 0; i--) {
      const d = runDust[i];
      d.t += dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.vx *= (1 - Math.min(1, dt * 1.8));
      d.vy -= 16 * dt;      // 慢慢往上飘
      d.r += d.grow * dt;   // 越飘越散
      if (d.t >= d.life) runDust.splice(i, 1);
    }
  }

  function drawRunDust() {
    if (!runDust.length) return;
    const sprite = getDustSprite();
    ctx.save();
    runDust.forEach(function (d) {
      if (d.x < -90 || d.x > W + 90) return;
      const p = clamp(d.t / d.life, 0, 1);
      const a = (p < 0.22 ? p / 0.22 : 1 - (p - 0.22) / 0.78) * 0.55;
      if (a <= 0.02) return;
      ctx.globalAlpha = a;
      ctx.drawImage(sprite, d.x - d.r * 1.5, d.y - d.r * 1.1, d.r * 3, d.r * 2.2);
      dustDrawn += 1;
    });
    ctx.restore();
  }

  function updateDirty(dt) {
    if (playerDirtyT > 0) playerDirtyT = Math.max(0, playerDirtyT - dt);
    for (let i = dirtyDrops.length - 1; i >= 0; i--) {
      const d = dirtyDrops[i];
      d.t += dt;
      d.vy += 900 * dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.t >= d.life) dirtyDrops.splice(i, 1);
    }
  }

  /* ============================================================
   * 天道 · 仙人：静修吸纳天地灵力
   *   武者大圆满择「仙途」之后，不再靠打怪涨修为 ——
   *   在野外停下（休息）时，周身会自动吸纳天地灵力化为修为。
   *   演出是「环境级」的，完全不动角色动作：
   *     ① 环境被抽空：四周光线压暗 + 中心一层灵力薄雾
   *     ② 地面：缓慢旋转的灵力法阵 + 一圈圈向内收拢的涟漪
   *     ③ 天地灵气：大量灵光微粒自四周螺旋式被吸进身体（带拖尾）
   *     ④ 沙尘倒卷：地面的沙粒被反向卷向角色
   *     ⑤ 吸收瞬间：胸口一次小小的灵光脉冲
   *     ⑥ 一道自下而上的淡光柱，随呼吸脉动
   * ============================================================ */
  /* 吸纳速度：按"每分钟多少点灵力"来定（原来 30 多点/秒 → 练气瞬间冲到筑基，已大幅调慢）
   *   想调快/调慢只改这一个数：3 = 每分钟 3 点。 */
  let XIU_PER_MIN = 10;                // 每分钟 10 点（丹药以后往上加；调试可临时改，见 __debugSetXiuPerMin）
  const XIU_BASE_RATE = XIU_PER_MIN / 60;   // 每秒 = 0.05 点
  const XIU_RATE_PER_LEVEL = 0;             // 不再按境界叠加（各境界耗时一致，靠境界需求区分）
  const QI_STAGE_START = TRIBULATION_STAGE + 1;        // 炼气·初期（31）
  const QI_BOTTLENECK_STAGE = QI_STAGE_START + 2;      // 炼气·后期（33）→ 冲击筑基的瓶颈
  const XIU_RAMP_SEC = 1.6;           // 渐入：坐下后多久到满强度
  const XIU_FADE_SEC = 0.9;           // 起身后多久淡完
  const XIU_MOTE_MAX = 140;           // 同屏灵气微粒上限
  const XIU_RGB = '150, 236, 255';    // 灵力色（青白）
  const XIU_GOLD = '255, 234, 168';   // 偶尔掺一点金，层次感
  const xiu = {
    amp: 0,        // 0~1 当前演出强度（渐入渐出）
    acc: 0,        // 还没兑现的修为小数
    seconds: 0,    // 累计静修时长（特效相位用）
    gained: 0,     // 这一段静修累计吸纳了多少修为
    moteT: 0, ringT: 0, pulse: 0, uiT: 0
  };
  const xiuMotes = [];
  const xiuRings = [];
  let xiuMoteSeq = 0;
  let xiuHinted = false;       // 第一次静修时提示一次
  let xiuCombatHinted = false; // 第一次"打怪不涨修为"时提示一次

  function xiuRate() { return XIU_PER_MIN / 60; }      // 每秒（读的是可调常量：丹药/调试都能改速度）
  function xiuActive() { return xiu.amp > 0.02; }
  let xiuMpChanged = false;                            // 打坐回灵力时用来节流刷 HUD

  /* 灵脉仙人掌：每帧刷"从仙人掌被抽向玩家"的灵气 */
  function updateLing(dt) {
    lingClock += dt;
    updateSkyOmen(dt);
    if (lingBloom) { lingBloom.t += dt; if (lingBloom.t >= LING_BOOM_SEC) lingBloom = null; }
    if (lingNode) {
      // 视差滚动：每帧按当前镜头重算屏幕位置（玩家走动时它会缓慢横向滑动）
      lingNode.screenX = lingNodeScreenX();
    }
    const sucking = canAbsorbQi() && xiu.amp > 0.05;
    if (sucking && lingNode) {
      lingSpawnT -= dt;
      if (lingSpawnT <= 0) {
        lingSpawnT = rand(0.03, 0.08) / Math.max(0.25, xiu.amp);
        const sx = lingNodeScreenX();
        const sy = lingNode.baseY - rand(40, 130) * (lingNode.sc || 1);
        lingMotes.push({ x: sx + rand(-16, 16), y: sy, t: 0, life: rand(0.5, 0.85) });
      }
    }
    for (let i = lingMotes.length - 1; i >= 0; i--) {
      const m = lingMotes[i];
      m.t += dt;
      const k = Math.min(1, m.t / m.life);
      // 目标：角色胸口（越接近越快，像被"吸"过去）
      const tx = player.worldX - cameraX(), ty = GROUND_Y - 120;
      m.x += (tx - m.x) * Math.min(1, dt * (2.2 + k * 6));
      m.y += (ty - m.y) * Math.min(1, dt * (2.2 + k * 6));
      if (k >= 1 || Math.abs(m.x - tx) < 12) lingMotes.splice(i, 1);
    }
  }
  let lingSpawnT = 0;
  let lingClock = 0;

  /* 画灵脉：仙人掌明暗闪烁 + 头顶编号 + 被抽走的灵气 */
  function drawLingFx() {
    if (!lingNode) return;
    const sx = lingNodeScreenX();
    if (sx < -220 || sx > W + 220) return;
    const sc = lingNode.sc || 1;
    const sucking = canAbsorbQi() && xiu.amp > 0.05;
    const flick = 0.5 + 0.5 * Math.sin(lingClock * 3.1) * Math.sin(lingClock * 1.7);
    ctx.save();
    // ① 明暗不定：给仙人掌位置压一层呼吸的灵光（原图本身不动，用叠加光做出"忽明忽暗"）
    ctx.globalCompositeOperation = 'lighter';
    const glowA = (0.16 + 0.22 * flick) * (sucking ? 1.35 : 0.8);
    const rg = ctx.createRadialGradient(sx, lingNode.baseY - 62 * sc, 4, sx, lingNode.baseY - 62 * sc, 96 * sc);
    rg.addColorStop(0, 'rgba(' + lingRgb + ',' + glowA.toFixed(3) + ')');
    rg.addColorStop(0.55, 'rgba(' + lingRgb + ',' + (glowA * 0.32).toFixed(3) + ')');
    rg.addColorStop(1, 'rgba(' + lingRgb + ',0)');
    ctx.fillStyle = rg;
    ctx.beginPath();
    ctx.arc(sx, lingNode.baseY - 62 * sc, 96 * sc, 0, Math.PI * 2);
    ctx.fill();
    // ② 被抽走的灵气：一条从仙人掌连到角色的"灵丝" + 沿线飞舞的微粒
    if (sucking) {
      const px = player.worldX - cameraX(), py = GROUND_Y - 118;
      ctx.strokeStyle = 'rgba(' + lingRgb + ',' + (0.20 + 0.18 * flick).toFixed(3) + ')';
      ctx.lineWidth = 3.2;
      ctx.beginPath();
      ctx.moveTo(sx, lingNode.baseY - 74 * sc);
      ctx.quadraticCurveTo((sx + px) / 2, Math.min(lingNode.baseY - 150 * sc, py - 90), px, py);
      ctx.stroke();
    }
    lingMotes.forEach(function (m) {
      const k = Math.min(1, m.t / m.life);
      ctx.fillStyle = 'rgba(' + lingRgb + ',' + (0.75 * (1 - k * 0.4)).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(m.x, m.y, 2.4 + (1 - k) * 2.2, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
    // ④ 灵力耗尽的爆炸：白光一闪 + 向外炸开的碎片
    if (lingBloom) {
      const bk = lingBloom.t / lingBloom.dur;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const r = 40 + bk * 220;
      const bg = ctx.createRadialGradient(lingBloom.x, lingBloom.y - 60, 2, lingBloom.x, lingBloom.y - 60, r);
      bg.addColorStop(0, 'rgba(255, 255, 255, ' + (0.85 * (1 - bk)).toFixed(3) + ')');
      bg.addColorStop(0.5, 'rgba(190, 255, 220, ' + (0.35 * (1 - bk)).toFixed(3) + ')');
      bg.addColorStop(1, 'rgba(160, 240, 200, 0)');
      ctx.fillStyle = bg;
      ctx.beginPath();
      ctx.arc(lingBloom.x, lingBloom.y - 60, r, 0, Math.PI * 2);
      ctx.fill();
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI * 2 * i) / 10 + bk * 1.2;
        const rr = 30 + bk * 190;
        ctx.fillStyle = 'rgba(210, 255, 235, ' + (0.9 * (1 - bk)).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(lingBloom.x + Math.cos(a) * rr, lingBloom.y - 60 + Math.sin(a) * rr * 0.7,
          3 + (1 - bk) * 5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }
    // ③ 头顶编号：这棵仙人掌是"谁"的灵脉，一眼能看见
    ctx.save();
    ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(10, 20, 16, .72)';
    const label = '灵脉 #' + lingNode.number +
      (lingNode.owner ? ' · ' + lingNode.owner : '') +
      (lingNode.poolMax ? '　灵力 ' + Math.max(0, Math.round(lingNode.pool)) + '/' + lingNode.poolMax : '');
    const wLabel = ctx.measureText(label).width + 14;
    ctx.fillRect(sx - wLabel / 2, lingNode.baseY - 176 * sc, wLabel, 20);
    ctx.fillStyle = 'rgb(' + lingRgb + ')';
    ctx.fillText(label, sx, lingNode.baseY - 162 * sc);
    ctx.restore();
  }
  /* 境界门槛：只要蜕凡入仙（炼气及以后）就能吸纳 —— 不再限制阵营
   *   （仙人 / 逆仙 / 尚未择道都可以在仙人掌旁修炼，按需求放开） */
  function xiuFactionOk() { return player.level > TRIBULATION_STAGE; }
  // 现在能不能吸纳：野外、停下、不在特殊地图
  function canAbsorbQi() {
    if (!xiuFactionOk()) return false;
    if (gameState !== 'walk') return false;
    if (deathActive || caveTransition) return false;
    if (input.moving || player.vx > 6) return false;   // 走动 / 赶路中不修炼
    if (activityRun) return false;                     // 临时副本（PVP）里不修炼
    if (worldBossRun) return false;                    // 世界BOSS 战场里不修炼
    /* 练气之后不再是"原地一坐就有"：必须先点中一棵仙人掌，把灵力从它身上抽出来 */
    if (!lingNodeActive()) return false;
    return true;
  }

  /* ============================================================
   * 灵脉仙人掌（炼气期 · 天道修士的吸灵媒介）
   *   练气之后不能再靠"原地静修"白拿灵力：
   *     ① 在屏幕上点一棵仙人掌 → 它成为你的「灵脉」，占一个 1~1000 的编号（云端登记）
   *     ② 站在它旁边停下 → 灵力从仙人掌被抽到角色身上（沿用原来那套吸纳特效）
   *     ③ 作为灵脉的仙人掌会明暗不定地闪烁，头顶挂编号
   *   其它玩家（任意境界）踢罐子 / 击杀敌人时有几率抽到随机编号，
   *     抽中的编号如果正好是别人登记的灵脉 → 提示「发现异常灵气聚集地」。
   * ============================================================ */
  const LING_MIN = 1;
  const LING_MAX = 1000;
  const LING_ABSORB_RANGE = 190;     // 站在仙人掌多近（屏幕像素）才能吸
  /* 灵脉旁"打坐回灵力"：每秒最多恢复 6% 最大灵力（还要乘打坐强度与这棵灵脉的灵力占比）——
   *   这是练气期之后唯一的常规回灵力手段（战斗胜利不再回满、路上休息也不回）。 */
  const LING_MP_REFILL_PCT = 0.06;
  const LING_DROP_PCT = 3;           // 踢罐子 / 击杀敌人掉编号的几率
  let lingNode = null;               // { number, worldX, factor, baseY, sc, owner, registered }
  let lingCactus = [];               // 本帧画出来的仙人掌（点击判定用）
  let lingMotes = [];                // 从仙人掌被抽向玩家的灵气
  let lingPrompt = null;             // 待处理的「发现异常灵气聚集地」
  let lingHinted = false;
  let lingDropPending = false;       // 抽编号请求在飞（免得一脚罐子连抽好几次）
  let lingLocalNodes = [];           // 离线（本地模式）时的编号库：本地登记过的灵脉编号
  /* 每棵仙人掌里也是一个世界：它的灵力是有限的（1~1000，点中的那一刻随机决定），
   *   吸干就会枯萎 → 虚化 → 爆炸，并弹出「救命」的夸张艺术字。 */
  let lingBloom = null;              // { t, dur, x, y } 爆炸演出
  const LING_BOOM_SEC = 1.1;

  /* ============================================================
   * 【测试功能 · 稍后移除】灵脉定位
   *   输入 1~1000 的编号 → 头顶虚空开一枚小型漩涡 → 传送到那处灵脉旁。
   *   编号要是有人登记、而且那人正守在自己的灵脉旁修炼，落地后就能看到他，
   *   点他即可用神识锁定并发起交锋（复用副本那套 PvP 协议）。
   *   反过来：守在自己灵脉旁修炼的人，会看到灵脉上空开漩涡、来客从漩涡里掉到身边。
   *   实时同步就复用副本的房间：room = 'ling:<编号>'。
   * ============================================================ */
  const LING_TP_IN_SEC = 1.15;       // 头顶漩涡把人吸进去
  const LING_TP_OUT_SEC = 0.8;       // 落地后从漩涡里掉出来
  const LING_PEER_DROP_SEC = 0.9;    // 别人出现在我这边时的"掉落"时长
  const LING_TP_OFFSET = 130;        // 落点：仙人掌摆在玩家左侧多少像素
  let lingTp = null;                 // 传送演出 { t, phase:'in'|'mid', number, owner }
  let lingForcedCactus = null;       // 传送落点那棵仙人掌（程序化装饰里未必有，自己画）
  let lingPvpPiles = [];             // 灵脉这边的 PvP 战利品堆（副本那套挂在 run.items 上）
  let playerLiftY = 0;               // 被漩涡吸起来时把立绘抬高
  let playerTpAlpha = 1;             // 传送中的整体透明度
  let netRoomKind = null;            // 当前房间类型：'act'（副本）/ 'ling'（灵脉）
  let netLingNumber = null;          // 我正在的灵脉编号
  let netLingJoined = false;         // 这一轮进房请求是否真的发出去了（没发出去要重试）
  let netLingJoinTryAt = 0;          // 上次尝试进房的时间（重试节流用）
  let lingPosAcc = 0;                // 位置上报节流
  let lingPeerPlates = [];           // 这一帧画出来的真人名牌（点击判定用）
  /* 这一帧实际画出来的"非战斗角色"（虚影行人 / 旧尸体 / 灵脉旁的真人）：
   *   战斗场景应当一个都不画 —— 留个可观测的口，测试/排查直接看这里。 */
  const sceneActors = { mirage: false, corpse: false, peers: 0 };


  // 灵脉那棵仙人掌当前在屏幕上的 x（装饰带是视差滚动，得跟着 cam 走）
  function lingNodeScreenX() {
    if (!lingNode) return -9999;
    return lingNode.worldX - cameraX() * lingNode.factor;
  }
  // 现在能不能从灵脉吸灵：人在附近 + 停下 + 野外
  function lingNodeActive() {
    if (!lingNode) return false;
    if (gameState !== 'walk' || deathActive || caveTransition) return false;
    return Math.abs((player.worldX - cameraX()) - lingNodeScreenX()) <= LING_ABSORB_RANGE;
  }
  const lingRgb = '168, 255, 214';

  // 把一棵仙人掌登记成灵脉（本地先记，能连服务器就上报真正的编号）
  function lingActivateCactus(hit) {
    if (!xiuFactionOk()) {
      showToast('只有炼气期之后的天道修士，才能借仙人掌吸纳天地灵力。');
      return null;
    }
    const key = lingCactusKey(hit);
    const memo = lingCactusSeen[key];
    /* 这棵仙人掌已经点过了 → 编号和灵力上限都是**定死**的，绝不重掷。
     *   以前每点一次都重新随一次，站在原地反复点就能一直刷新灵力上限 / 编号，
     *   等于无限刷修为 —— 这里改成"一棵仙人掌一辈子一个编号、一份灵力"。 */
    if (memo) {
      if (lingNode && lingNode.key === key) {
        showToast('这棵仙人掌已经是你的灵脉 #' + lingNode.number +
          '（还剩 ' + Math.max(0, Math.round(lingNode.pool)) + ' 灵力）。');
        return lingNode;
      }
      lingNode = { number: memo.number, worldX: hit.worldX, factor: hit.factor, baseY: hit.baseY,
        sc: hit.sc, registered: true, pool: Math.max(0, memo.pool), poolMax: memo.poolMax,
        drain: 0, key: key };
      lingMotes.length = 0;
      showToast(lingNode.pool > 0
        ? ('这棵仙人掌就是你的灵脉 #' + memo.number + '（还剩 ' + Math.round(lingNode.pool) + ' 灵力）。')
        : ('这棵仙人掌的灵力已经被你吸干了（灵脉 #' + memo.number + '）。'));
      return lingNode;
    }
    const number = LING_MIN + Math.floor(Math.random() * (LING_MAX - LING_MIN + 1));
    // 这棵仙人掌的灵力上限：只在"第一次点它"的时候随机 1~1000，然后记下来
    const pool = randInt(LING_MIN, LING_MAX);
    lingCactusRemember(key, number, pool, pool);
    lingNode = { number: number, worldX: hit.worldX, factor: hit.factor, baseY: hit.baseY,
      sc: hit.sc, registered: false, pool: pool, poolMax: pool, drain: 0, key: key };
    lingMotes.length = 0;
    showToast('灵脉已激活：编号 #' + number + '，内含灵力 ' + pool + ' 点（吸干它就会枯萎）');
    pushBattleLog('【灵脉】你把这棵仙人掌认成了自己的灵脉，编号 #' + number +
      '（编号会登记到云端，别的修士在路上可能撞见它）', 'reward');
    if (!lingHinted) {
      lingHinted = true;
      showToast('点中的仙人掌成为你的灵脉：站在旁边停下，灵力会源源不断被抽进体内。');
    }
    // 上报云端（离线/本地模式就只存在本地）
    try {
      const base = (typeof net !== 'undefined' && net.serverUrl) ? net.serverUrl : '';
      if (base && net.token) {
        fetch(base + '/api/ling/register?token=' + encodeURIComponent(net.token), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ number: number })
        }).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
          if (j && j.number) {
            lingNode.number = j.number;
            lingNode.registered = true;
            if (lingCactusSeen[key]) lingCactusSeen[key].number = j.number;   // 备忘里也换成云端给的编号
            showToast('灵脉编号已登记到云端：#' + j.number);
          }
        }).catch(function () { /* 离线：就用本地编号 */ });
      } else {
        lingLocalNodes.push({ number: number, owner: account.name || 'local', at: Date.now() });
      }
    } catch (e) { /* 忽略 */ }
    return lingNode;
  }

  /* ---------- 每棵仙人掌的"编号 + 灵力上限"备忘 ----------
   *   键 = 视差排 factor + 世界坐标（两者一起才能唯一确定一棵仙人掌）。
   *   只在第一次点它时掷一次，之后（含读档）都沿用同一个编号与同一份灵力。 */
  function lingCactusKey(hit) {
    const f = (hit && hit.factor !== undefined) ? hit.factor : 'x';
    return f + ':' + Math.round(hit ? hit.worldX : 0);
  }
  function lingCactusRemember(key, number, pool, poolMax) {
    const keys = Object.keys(lingCactusSeen);
    if (keys.length >= LING_CACTUS_MEMO_MAX && !lingCactusSeen[key]) delete lingCactusSeen[keys[0]];
    lingCactusSeen[key] = { number: number, pool: pool, poolMax: poolMax };
    return lingCactusSeen[key];
  }
  function lingCactusUpdatePool(key, pool) {
    const m = key ? lingCactusSeen[key] : null;
    if (m) m.pool = Math.max(0, Math.round(pool));
  }

  // 点击判定：点到的仙人掌（屏幕坐标）
  /* 天地异象（破境演出）：压暗天光 + 连续雷光 + 环绕灵气旋涡 + 居中横幅，约 4.5 秒 */
  const SKY_OMEN_SEC = 4.5;
  let skyOmen = null;                // { t, dur, title }
  /* 练气期后：远处天空偶尔划过的高阶修仙者飞虹 */
  let skyFlight = null;              // { t, dur, x0, x1, y, dir, hue }
  let skyFlightTimer = rand(16, 32);
  /* 两条丝线"触碰"时炸开的烟尘（用导入的烟尘帧播一遍就丢） */
  let skyFlightPuffs = [];           // { x, y, t, dur, scale }
  let skyFlightPuffCd = 0;           // 触发冷却，避免同一瞬间连着炸
  function startSkyOmen(title) {
    skyOmen = { t: 0, dur: SKY_OMEN_SEC, title: title || '天地异象 · 灵潮涌动' };
    pushBattleLog('【天地异象】' + skyOmen.title, 'reward');
  }
  function updateSkyOmen(dt) { if (skyOmen) { skyOmen.t += dt; if (skyOmen.t >= skyOmen.dur) skyOmen = null; } }
  function updateSkyFlight(dt) {
    updateSkyPuffs(dt);        // 烟尘自己播完（飞虹消失也不打断）
    if (skyFlightPuffCd > 0) skyFlightPuffCd = Math.max(0, skyFlightPuffCd - dt);
    // 只有进入练气期后的普通冒险/战斗场景才出现
    const eligible = playerLevel() > TRIBULATION_STAGE &&
      (gameState === 'walk' || gameState === 'battle') &&
      !cave && !activityRun && !sectScene && !worldBossRun && !legendMap;
    if (!eligible) {
      skyFlight = null;
      skyFlightPuffs.length = 0;
      skyFlightTimer = rand(16, 32);
      return;
    }
    if (skyFlight) {
      skyFlight.t += dt;
      updateSkyFlightTouch(dt);
      if (skyFlight.t >= skyFlight.dur) {
        skyFlight = null;
        skyFlightTimer = rand(18, 38);
      }
      return;
    }
    skyFlightTimer -= dt;
    if (skyFlightTimer > 0) return;
    const dir = Math.random() < 0.5 ? 1 : -1;
    const y = rand(118, 232);
    const dur = rand(6.5, 10);
    skyFlight = {
      t: 0, dur: dur, dir: dir, y: y,
      x0: dir > 0 ? -120 : W + 120,
      x1: dir > 0 ? W + 120 : -120,
      hue: rand(0, 360), scale: rand(0.7, 1.05), phaseOffset: 0,
      ampSeed: rand(0, 500),          // 每条飞虹一套自己的随机振幅轨迹
      art: randInt(1, SKY_FLIGHT_ART_VARIANTS)   // 随机三种飞虹美术
    };
  }
  /* 烟尘粒子推进（飞虹没了也要继续播完） */
  function updateSkyPuffs(dt) {
    for (let i = skyFlightPuffs.length - 1; i >= 0; i--) {
      skyFlightPuffs[i].t += dt;
      if (skyFlightPuffs[i].t >= skyFlightPuffs[i].dur) skyFlightPuffs.splice(i, 1);
    }
  }
  /* 飞虹：用程序生成的透明底精灵图绘制（tools/make_sky_streak.cjs）。
   *  · 两条极细丝线：a / b 各一张，第二条上下镜像 + 反向摆动 → 一路穿插交错（穿梭感）；
   *  · 精灵图头部在右侧：向左飞时水平翻转；
   *  · 越靠天顶 = 越远 → 画得更小更淡（景深）；
   *  · 细芯先正常合成（浅色天空上看得见颜色），再用 lighter 叠一层亮芯提神。 */
  /* 精灵图里头部所在的横向比例：生成脚本把头部放在 x = 宽 - 22 处（精灵图 300×44） */
  const SKY_FLIGHT_HEAD_ANCHOR = (300 - 22) / 300;
  const SKY_FLIGHT_ART = [
    null,
    ['skyFlight_1a', 'skyFlight_1b'],   // 六色飞虹：暖丝 + 冷丝
    ['skyFlight_2a', 'skyFlight_2b'],   // 青白剑气
    ['skyFlight_3a', 'skyFlight_3b']    // 金赤仙光
  ];
  /* 两条丝线的正弦穿梭：
   *   丝线素材是直的细线，游戏里把它沿 y(d) = A·sin(k·d + ωt) 这条**会流动的正弦曲线**
   *   分片旋转贴图铺开 —— 波峰波谷会沿线往尾部跑（真正的正弦运动），不是整条线上下浮动。
   *   两条线**刻意不同步**（见 SKY_THREAD_WAVES）：第二条波速更快、波长略短、振幅略小，
   *   于是它一路追上来 —— 两条线时而分开、时而几乎并线（"追击"的错觉），
   *   不会像镜像那样永远保持完全对称（部分位置对不齐是特意的）。 */
  const SKY_WAVE_AMP = 4.2;                  // 第一条线的基础振幅（屏幕像素，跟缩放走）
  const SKY_WAVE_LOOPS = 1.25;               // 第一条线沿线方向有几个波
  const SKY_WAVE_SPEED = 3.4;                // 第一条线的波形流动速度（rad/s）
  const SKY_THREAD_SEG = 28;                 // 分片数：越大越平滑（每片约 10px）
  /* 两条线各自的波形倍率（振幅 / 波长 / 波速，波速差就是"追击"的来源）。
   * 振幅与波长都贴得很近（0.94 / 0.96），这样两条线的 Y 间距始终不大：
   * 最大也只有 ampA+ampB ≈ 8px，不会出现"某一刻突然拉得很开"。 */
  const SKY_THREAD_WAVES = [
    { amp: 1, loops: 1, speed: 1, phase: 0 },
    { amp: 0.94, loops: 0.96, speed: 1.16, phase: Math.PI * 0.8 }
  ];
  /* ---------- 随机调幅（AM）简谐：每个周期的峰值随机拉长 / 缩短 ----------
   *   · 振幅按"周期编号"散列取值 —— 没有固定调制信号，也不是任何周期函数在调制；
   *   · 相邻周期之间用 smoothstep 过渡，所以波形不会出现折角或跳变；
   *   · 上限就是基础振幅（只缩短不放大），两条线的 Y 间距不会因此被拉大。 */
  const SKY_AMP_MUL_MIN = 0.45;      // 最矮的峰 = 基础振幅的 45%
  const SKY_AMP_MUL_MAX = 1;         // 最高的峰 = 基础振幅
  function skyThreadAmpMul(seed, n) {
    return SKY_AMP_MUL_MIN + (SKY_AMP_MUL_MAX - SKY_AMP_MUL_MIN) *
      hash01(seed * 13.7 + n * 1.6180339887 + 0.37);
  }
  /* ---------- 单侧"随机拉开间距"的分量 ----------
   *   只作用在**第二条**丝线上：它会在 Y 轴上随机漂开、过一会儿又回到缠绕状态，
   *   于是两条线不是永远紧紧缠在一起。
   *   用慢速值噪声：每 SKY_SEP_STEP 秒换一个随机目标、中间 smoothstep 过渡，
   *   所以不会出现"某一刻突然拉大"；再用 pow(·,1.5) 把常驻状态压回贴紧。 */
  const SKY_SEP_MAX = 7;         // 最大额外间距（屏幕像素，跟缩放走）
  const SKY_SEP_STEP = 1.5;      // 每 1.5 秒换一个随机目标
  function skySeparationAt(t, seed) {
    const u = t / SKY_SEP_STEP;
    const n = Math.floor(u);
    const fr = u - n;
    const sm = fr * fr * (3 - 2 * fr);
    const a = hash01(seed * 7.31 + n * 2.17 + 0.61);
    const b = hash01(seed * 7.31 + (n + 1) * 2.17 + 0.61);
    const v = (a * (1 - sm) + b * sm - 0.5) * 2;        // -1 ~ 1
    return SKY_SEP_MAX * (v < 0 ? -1 : 1) * Math.pow(Math.abs(v), 1.5);
  }
  /* 把一条丝线沿正弦路径铺开。headX/headY = 头部锚点，dir = 行进方向（+1 向右）。
   * d = 素材轴上"距头部锚点"的距离（素材像素），d 越大越靠尾巴。 */
  function drawSkyFlightThread(spr, headX, headY, dir, sc, wave) {
    const SW = spr.width, SH = spr.height;
    const segW = SW / SKY_THREAD_SEG;
    const halfW = segW * sc / 2, halfH = SH * sc / 2;
    const k = Math.PI * 2 * wave.loops;
    const pt = function (d) {
      return {
        x: headX - dir * d * sc,
        y: headY + wave.amp * Math.sin(k * (d / SW) + wave.phase) * sc
      };
    };
    for (let i = 0; i < SKY_THREAD_SEG; i++) {
      const dCenter = SW * SKY_FLIGHT_HEAD_ANCHOR - (i + 0.5) * segW;
      const p = pt(dCenter);
      // 切线方向 = 素材 +x 方向（也就是朝向头部的一侧）
      const p1 = pt(dCenter + 2), p2 = pt(dCenter - 2);
      const ang = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(ang);
      ctx.drawImage(spr, i * segW, 0, segW, SH, -halfW, -halfH, segW * sc, SH * sc);
      ctx.restore();
    }
  }
  // 程序兜底：两条极细的曲线（精灵图没到位时用）
  function drawSkyFlightPromo(x, y, dir, sc, a) {
    const pair = [['#ffb066', 0], ['#8fd0ff', 1]];
    for (let i = 0; i < pair.length; i++) {
      const w = SKY_THREAD_WAVES[pair[i][1]];
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = a * 0.5;
      ctx.strokeStyle = pair[i][0];
      ctx.lineWidth = 1.3 * sc;
      ctx.beginPath();
      const k = Math.PI * 2 * w.loops;
      for (let s = 0; s <= 1.001; s += 0.05) {
        const px = x - dir * s * 280 * sc;
        const py = y + SKY_WAVE_AMP * w.amp * Math.sin(k * s + w.phase) * sc;
        if (s === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
    }
  }
  /* 这一帧飞虹的几何：位置 / 朝向 / 缩放 / 透明度 / 两条线各自的波形。
   * 绘制与"丝线相触"判定共用它，保证判定和画面完全对得上。 */
  function skyFlightView() {
    if (!skyFlight) return null;
    const p = clamp(skyFlight.t / skyFlight.dur, 0, 1);
    const ease = 1 - Math.pow(1 - p, 1.7);
    const x = skyFlight.x0 + (skyFlight.x1 - skyFlight.x0) * ease;
    const y = skyFlight.y + Math.sin(p * Math.PI * 2) * 12;
    const fade = Math.sin(Math.PI * p);                    // 进/出屏都淡一点
    const dir = skyFlight.dir;
    // 景深：天越高的那条飞虹越远（更小、更淡）
    const depth = clamp((skyFlight.y - 110) / 130, 0, 1);
    const sc = skyFlight.scale * (1.18 - 0.5 * depth);
    const a = fade * (0.95 - 0.32 * depth) * 0.86;
    // 两条线各按自己的波速/波长跑（第二条更快、更短）→ 会互相追
    const common = skyFlight.phaseOffset || 0;
    // 波时间：默认跟着飞行时间走；调试时可以用 __debugSkyFlightWaveTime 单独拨（不动机位）
    const waveT = (skyFlight.waveTime === undefined || skyFlight.waveTime === null)
      ? skyFlight.t : skyFlight.waveTime;
    const threads = SKY_THREAD_WAVES.map(function (w, ti) {
      // 第二条丝线额外带一个"随机拉开"的 Y 偏移（第一条不动）
      const bias = ti === 1 ? skySeparationAt(skyFlight.t, skyFlight.ampSeed || 0) : 0;
      return {
        amp: SKY_WAVE_AMP * w.amp,
        loops: SKY_WAVE_LOOPS * w.loops,
        phase: waveT * SKY_WAVE_SPEED * w.speed + w.phase + common,
        ampSeed: (skyFlight.ampSeed || 0) + ti * 11.3,   // 每条线一条独立的随机振幅轨迹
        yBias: bias
      };
    });
    return { x: x, y: y, dir: dir, sc: sc, a: a, threads: threads, waveT: waveT };
  }
  /* 两条丝线在某一帧的纵向偏移（d = 距头部锚点的素材像素距离）。
   * 振幅是"随机调幅"的：先把相位换算成周期编号，再按编号取一个随机振幅倍率，
   * 相邻周期之间 smoothstep 过渡 —— 于是每个周期的峰高都不一样，且没有固定调制。 */
  function skyThreadOffsetAt(wave, d, sc) {
    const k = Math.PI * 2 * wave.loops;
    const ph = k * (d / 300) + wave.phase;
    const u = ph / (Math.PI * 2);
    const n = Math.floor(u);
    const fr = u - n;
    const blend = fr * fr * (3 - 2 * fr);
    const mul = skyThreadAmpMul(wave.ampSeed, n) * (1 - blend) +
      skyThreadAmpMul(wave.ampSeed, n + 1) * blend;
    // yBias：整条丝线在 Y 上的随机偏移（只有第二条会带，用来"随机拉开间距"）
    return wave.amp * mul * Math.sin(ph) * sc + (wave.yBias || 0) * sc;
  }
  /* 丝线相触检测：沿线找两条线靠得最近的位置，够近就炸一朵烟尘 */
  const SKY_TOUCH_GAP = 1.6;      // 判定阈值（屏幕像素）
  const SKY_TOUCH_SPAN = 300;     // 沿线采样范围（素材像素）
  function updateSkyFlightTouch(dt) {
    const view = skyFlightView();
    if (!view || view.a < 0.3) return;          // 太淡（刚进出屏）不触发
    if (skyFlightPuffCd > 0) return;
    const w0 = view.threads[0], w1 = view.threads[1];
    let best = null;
    for (let i = 0; i <= 30; i++) {
      const d = (i / 30) * SKY_TOUCH_SPAN;
      const y0 = skyThreadOffsetAt(w0, d, view.sc);
      const y1 = skyThreadOffsetAt(w1, d, view.sc);
      const gap = Math.abs(y0 - y1);
      if (!best || gap < best.gap) {
        best = {
          gap: gap, d: d,
          x: view.x - view.dir * d * view.sc,     // 丝线上该点的屏幕位置
          y: view.y + (y0 + y1) / 2
        };
      }
    }
    if (!best || best.gap > SKY_TOUCH_GAP) return;
    skyFlightPuffs.push({
      x: best.x, y: best.y, t: 0,
      dur: 0.55,
      scale: view.sc * 0.42        // 相对素材尺寸（128px 帧 → 约 54px）
    });
    skyFlightPuffCd = 0.5;
  }
  /* 烟尘：沿用导入的 10 帧（越到后面越大越淡） */
  function drawSkyPuffs() {
    if (!skyFlightPuffs.length) return;
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';
    skyFlightPuffs.forEach(function (pf) {
      const k = clamp(pf.t / pf.dur, 0, 1);
      const idx = 1 + Math.min(SKY_SMOKE_FRAMES - 1, Math.floor(k * SKY_SMOKE_FRAMES));
      const spr = images['skySmoke_' + idx];
      if (!spr || !spr.complete || !spr.naturalWidth) return;
      const scale = pf.scale * (0.7 + 0.75 * k);
      const w = spr.width * scale, h = spr.height * scale;
      ctx.globalAlpha = Math.sin(Math.PI * Math.min(1, k * 1.06)) * 0.8;
      ctx.drawImage(spr, pf.x - w / 2, pf.y - h / 2, w, h);
    });
    ctx.restore();
    ctx.globalAlpha = 1;
  }
  function drawSkyFlight() {
    const view = skyFlightView();
    if (!view) return;
    const pair = SKY_FLIGHT_ART[skyFlight.art || 1] || SKY_FLIGHT_ART[1];
    const x = view.x, y = view.y, dir = view.dir, sc = view.sc, a = view.a;
    ctx.save();
    for (let i = 0; i < 2; i++) {
      const spr = images[pair[i]];
      const wave = view.threads[i];
      if (spr && spr.complete && spr.naturalWidth) {
        // ① 丝线本体：正常合成，浅色天空上能看清颜色
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = a;
        drawSkyFlightThread(spr, x, y, dir, sc, wave);
        // ② 亮芯：加亮一层，做出"仙光"的通透感
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = a * 0.42;
        drawSkyFlightThread(spr, x, y, dir, sc, wave);
      } else if (i === 0) {
        drawSkyFlightPromo(x, y, dir, sc, a);
      }
    }
    ctx.restore();
  }
  function drawSkyOmen() {
    if (!skyOmen) return;
    const k = skyOmen.t / skyOmen.dur;
    const a = Math.sin(Math.PI * Math.min(1, k * 1.15));       // 淡入淡出
    ctx.save();
    ctx.fillStyle = 'rgba(6, 14, 22, ' + (0.55 * a).toFixed(3) + ')';
    ctx.fillRect(0, 0, W, H);
    // 雷光：随机闪几下
    const flash = Math.max(0, Math.sin(k * 26) * (1 - k));
    if (flash > 0.25) {
      ctx.globalCompositeOperation = 'lighter';
      const g = ctx.createLinearGradient(0, 0, 0, H * 0.7);
      g.addColorStop(0, 'rgba(200, 232, 255, ' + (0.5 * flash * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(120, 190, 255, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H * 0.7);
    }
    // 灵气旋涡：一圈圈往外扩的青色涟漪
    ctx.globalCompositeOperation = 'lighter';
    const cx = player.worldX - cameraX(), cy = GROUND_Y - 130;
    for (let i = 0; i < 3; i++) {
      const r = (60 + ((k * 3 + i / 3) % 1) * 420);
      ctx.strokeStyle = 'rgba(160, 240, 220, ' + (0.18 * a * (1 - (r - 60) / 420)).toFixed(3) + ')';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(cx, cy + 40, r, r * 0.35, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    // 横幅
    ctx.save();
    ctx.globalAlpha = a;
    ctx.textAlign = 'center';
    ctx.font = 'bold 30px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = 'rgba(8, 16, 22, .6)';
    ctx.fillRect(W / 2 - 210, H * 0.2 - 34, 420, 52);
    ctx.fillStyle = '#a8ffd6';
    ctx.fillText(skyOmen.title, W / 2, H * 0.2 + 3);
    ctx.font = '15px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = 'rgba(200, 255, 232, .85)';
    ctx.fillText('天雷在云上滚过，天地灵力向你体内奔涌', W / 2, H * 0.2 + 30);
    ctx.restore();
  }
  /* ============================================================
   * 瓶颈 · 破境任务（任何阵营都一样）
   *   炼气之后每个小境界修为满都会卡瓶颈 —— **不需要天地灵物**（那件东西只在
   *   凡人→炼气的第一次蜕变用），而是要做完三项任务才能冲关：
   *     ① 击杀真实玩家（临时副本里的真人交锋，对方脱战不算）
   *     ② 完成洞府探险（打通一次洞府）
   *     ③ 击败野外敌人
   *   三项**都要完成**；境界越高，要求数量越多（见 bottleneckNeeds）。
   * ============================================================ */
  /* 练气初期（31 级）的起步要求 + 每往后一个小境界的增量：
   *   练气初期：真人 1 / 洞府 1 / 野外 15
   *   练气中期：真人 3 / 洞府 3 / 野外 30   （+2 / +2 / +15）
   *   练气后期：真人 5 / 洞府 5 / 野外 45 …以此类推
   *   想调曲线只动这两个常量。 */
  const BOTTLENECK_BASE = { pvpKill: 1, cave: 1, killMany: 15 };
  const BOTTLENECK_STEP = { pvpKill: 2, cave: 2, killMany: 15 };
  let pvpKillTotal = 0;                 // 真人击杀累计（破境任务用）
  let caveClearTotal = 0;               // 洞府通关累计（破境任务用）
  /* 当前境界的破境任务要求：按"小境界"线性递增 */
  function bottleneckNeeds(stage) {
    const step = Math.max(0, Math.round(stage) - QI_STAGE_START);
    return {
      pvpKill: BOTTLENECK_BASE.pvpKill + step * BOTTLENECK_STEP.pvpKill,
      cave: BOTTLENECK_BASE.cave + step * BOTTLENECK_STEP.cave,
      killMany: BOTTLENECK_BASE.killMany + step * BOTTLENECK_STEP.killMany
    };
  }
  function bottleneckTasks() {
    const base = player.bottleneckBase || { kill: 0, pvp: 0, cave: 0 };
    const need = bottleneckNeeds(player.level);
    return [
      { id: 'pvpKill', label: '击杀真实玩家', need: need.pvpKill, unit: '人',
        have: Math.max(0, pvpKillTotal - (base.pvp || 0)), hint: '去临时副本找真人交锋（对方脱战不算）' },
      { id: 'cave', label: '完成洞府探险', need: need.cave, unit: '次',
        have: Math.max(0, caveClearTotal - (base.cave || 0)), hint: '在路边找到洞府入口打到底' },
      { id: 'killMany', label: '击败野外敌人', need: need.killMany, unit: '名',
        have: Math.max(0, killsTotal - (base.kill || 0)), hint: '冒险途中击杀敌人即可累计' }
    ];
  }
  function renderBottleneckTasks() {
    if (!ui.bottleneckList) return;
    ui.bottleneckList.textContent = '';
    bottleneckTasks().forEach(function (t) {
      const row = document.createElement('div');
      row.className = 'socketSkill' + (t.have >= t.need ? ' done' : '');
      const name = document.createElement('span');
      name.textContent = '「' + t.label + '」　' + Math.min(t.have, t.need) + ' / ' + t.need + t.unit +
        (t.have >= t.need ? '　✔' : '');
      const meta = document.createElement('small');
      meta.textContent = t.have >= t.need ? '已完成' : t.hint;
      row.appendChild(name);
      row.appendChild(meta);
      ui.bottleneckList.appendChild(row);
    });
  }
  function showBottleneckPanel() {
    renderBottleneckTasks();
    if (ui.bottleneckPopup) ui.bottleneckPopup.classList.remove('hidden');
  }
  function hideBottleneckPanel() {
    if (ui.bottleneckPopup) ui.bottleneckPopup.classList.add('hidden');
  }
  /* 每次击杀 / 真人击杀 / 洞府通关之后检查：**三项都完成**才冲关 */
  function checkBottleneckTask() {
    if (!player.bottleneck) return false;
    // 武者大圆满那道坎要「天地灵物」，不走破境任务（别把任务当成灵物用）
    if (player.level < QI_STAGE_START) return false;
    const list = bottleneckTasks();
    const allDone = list.every(function (t) { return t.have >= t.need; });
    if (!allDone) {
      if (ui.bottleneckPopup && !ui.bottleneckPopup.classList.contains('hidden')) renderBottleneckTasks();
      return false;
    }
    hideBottleneckPanel();
    showToast('三项破境任务全部完成：瓶颈已破，你进境了！');
    pushBattleLog('【瓶颈】三项破境任务完成（击杀真人 / 洞府探险 / 野外击杀）：瓶颈已破', 'reward');
    return breakQiBottleneck(true);
  }

  /* 仙人掌灵力被吸干：枯萎 → 虚化（从画面上消失）→ 爆炸 + 「救命」 */
  let lingBoomTimer = null;
  let lingDead = [];                 // 已经枯掉的仙人掌（本局不再出现）
  /* 每棵仙人掌的编号 / 灵力上限备忘（点过一次就定死，随存档保存）：
   *   { '0.55:123456': { number, pool, poolMax } } */
  let lingCactusSeen = {};
  const LING_CACTUS_MEMO_MAX = 300;  // 记太多就丢掉最早的，避免存档无限膨胀
  function lingExhausted() {
    const at = lingNode ? { x: lingNodeScreenX(), y: lingNode.baseY, sc: lingNode.sc,
      worldX: lingNode.worldX, factor: lingNode.factor } : null;
    if (lingNode && lingNode.key) lingCactusUpdatePool(lingNode.key, 0);
    if (at) lingDead.push({ worldX: at.worldX, factor: at.factor });
    lingNode = null;
    lingMotes.length = 0;
    if (at) lingBloom = { t: 0, dur: LING_BOOM_SEC, x: at.x, y: at.y, sc: at.sc || 1 };
    showToast('仙人掌的灵力被你吸干了 —— 它里面也是一个世界，如今枯萎爆开。');
    pushBattleLog('【灵脉】仙人掌灵力耗尽：枯萎 → 虚化 → 炸开（每棵仙人掌里也是一个世界）', 'reward');
    if (ui.lingBoom) {
      /* 位置贴着"刚消失的那棵仙人掌"的右上角（画布坐标 → 页面坐标要按缩放换算） */
      try {
        const cv = ui.canvas || document.getElementById('game');
        const rect = cv.getBoundingClientRect();
        const px = rect.left + at.x * (rect.width / cv.width);
        const py = rect.top + at.y * (rect.height / cv.height);
        ui.lingBoom.style.left = Math.round(px + 34 * (at.sc || 1)) + 'px';
        ui.lingBoom.style.top = Math.round(py - 150 * (at.sc || 1)) + 'px';
      } catch (e) { /* 换算失败就留在左上角，不影响玩法 */ }
      ui.lingBoom.classList.remove('hidden');
      clearTimeout(lingBoomTimer);
      lingBoomTimer = setTimeout(function () { if (ui.lingBoom) ui.lingBoom.classList.add('hidden'); }, 2600);
    }
  }

  function breakQiBottleneck(fromBattle) {
    if (!player.bottleneck) return false;
    // 仙人必须站在灵脉旁；其他阵营靠"打赢一场"（fromBattle = true 时直接放行）
    if (!fromBattle && !lingNodeActive()) {
      showToast('瓶颈需要借灵脉之力：回到你的仙人掌旁边再冲。');
      return false;
    }
    player.bottleneck = false;
    startSkyOmen('天地异象 · 灵潮涌动');
    showToast('瓶颈已破 —— 天地异象显现，你进境了！');
    applyXp(1, { quiet: false, force: true });   // 修为已满，这一下就跨过去（force = 直接放行）
    return true;
  }
  window.__debugBreakBottleneck = function () { return breakQiBottleneck(); };
  window.__debugSkyOmen = function () {
    return skyOmen ? { active: true, t: Math.round(skyOmen.t * 100) / 100, title: skyOmen.title } : { active: false };
  };

  function lingCactusAt(sx, sy) {
    let best = null, bestD = 999;
    lingCactus.forEach(function (c) {
      const dx = Math.abs(sx - c.screenX);
      if (dx > 58 * c.sc) return;
      if (sy < c.baseY - 150 * c.sc || sy > c.baseY + 14) return;
      if (dx < bestD) { bestD = dx; best = c; }
    });
    return best;
  }

  /* 踢罐子 / 击杀敌人 → 3% 掉一个随机编号；抽中别人登记的灵脉就提示"异常灵气聚集地" */
  function lingRollDrop(why) {
    if (Math.random() * 100 >= LING_DROP_PCT) return false;
    const localRoll = function () {
      const n = LING_MIN + Math.floor(Math.random() * (LING_MAX - LING_MIN + 1));
      const hit = lingLocalNodes.filter(function (x) { return x.number === n; })[0] || null;
      return { number: n, hit: !!hit, node: hit };
    };
    const finish = function (r) {
      if (!r) return;
      pushBattleLog('【灵脉】' + why + '，你拾到一串灵气编号 #' + r.number + '（' + r.number + '/1000）', 'system');
      if (r.hit) lingShowPrompt(r.number, r.node);
      else showToast('拾到灵气编号 #' + r.number + '：方圆并无异常。');
    };
    try {
      const base = (typeof net !== 'undefined' && net.serverUrl) ? net.serverUrl : '';
      if (base && !lingDropPending) {
        lingDropPending = true;
        fetch(base + '/api/ling/roll').then(function (r) { return r.ok ? r.json() : null; })
          .then(function (j) { lingDropPending = false; finish(j || localRoll()); })
          .catch(function () { lingDropPending = false; finish(localRoll()); });
        return true;
      }
    } catch (e) { /* 忽略 */ }
    finish(localRoll());
    return true;
  }

  // 「发现异常灵气聚集地」提示：去一探（打机缘敌人）/ 跳过
  function lingShowPrompt(number, node) {
    lingPrompt = { number: number, node: node || null };
    if (ui.lingPrompt) {
      ui.lingPrompt.classList.remove('hidden');
      if (ui.lingPromptText) {
        ui.lingPromptText.textContent = '发现异常灵气聚集地（编号 #' + number +
          (node && node.owner ? '·' + node.owner : '') + '）,是否前去一探?';
      }
    }
    pushBattleLog('【灵脉】编号 #' + number + ' 对应着一处异常灵气聚集地（' +
      ((node && node.owner) || '未知修士') + ' 的灵脉）', 'reward');
  }
  function lingClosePrompt() {
    lingPrompt = null;
    if (ui.lingPrompt) ui.lingPrompt.classList.add('hidden');
  }
  // 去一探：把附近的一只敌人变成"机缘"敌人（打赢给机缘奖励）
  function lingGoInvestigate() {
    const p = lingPrompt;
    lingClosePrompt();
    if (!p) return false;
    // 到了灵气聚集地：先拾一份机缘（金币 + 灵气），在场的敌人再额外标成"机缘守卫"
    const g = 200 + Math.floor(Math.random() * 400);
    gold += g;
    updateGold();
    applyXp(6 + Math.floor(Math.random() * 10), { quiet: true });
    if (activeEnemy) {
      activeEnemy.lingNode = p.number;
      activeEnemy.lingLoot = true;
      setBattleLog('你循着灵气赶到 #' + p.number + ' 的聚集地：拾得 ' + g +
        ' 金币，眼前这只「' + (activeEnemy.name || '守卫') + '」还守着更多机缘。');
      pushBattleLog('【灵脉】# ' + p.number + ' 聚集地：+' + g + ' 金币，机缘守卫在场（击杀再得一份）', 'reward');
    } else {
      showToast('你在灵气聚集地打坐片刻，得了 ' + g + ' 金币与一缕机缘（#' + p.number + '）。');
      pushBattleLog('【灵脉】你在 #' + p.number + ' 的聚集地静坐片刻，拾得机缘 ' + g + ' 金币', 'reward');
    }
    return true;
  }

  // 机缘敌人被击杀时的额外奖励
  function lingLootOnKill(enemy) {
    if (!enemy || !enemy.lingLoot) return false;
    const g = 500 + Math.floor(Math.random() * 800);
    gold += g; updateGold();
    applyXp(20 + Math.floor(Math.random() * 30), { quiet: true });
    showToast('机缘守卫倒下：+ ' + g + ' 金币、+ 灵气（#' + enemy.lingNode + '）');
    pushBattleLog('【灵脉】击杀机缘守卫（#' + enemy.lingNode + '）：+' + g + ' 金币、灵气入体', 'reward');
    return true;
  }

  /* ---------- 灵脉定位：传送演出 + 真人房间 ---------- */
  function lingAnchorScreenX() { return lingNode ? lingNodeScreenX() : (player.worldX - cameraX()); }
  // 相对灵脉的屏幕偏移（0 = 正站在灵脉上）：两边都用它做"层内坐标"
  function lingLocalX() { return Math.round((player.worldX - cameraX()) - lingAnchorScreenX()); }

  function lingSelfPayload() {
    return {
      x: lingLocalX(),
      hp: combat.maxHp ? clamp(combat.hp / combat.maxHp, 0, 1) : 1,
      level: playerLevel(),
      realm: stageText(playerLevel()),
      action: lingNodeActive() ? 'rest' : 'idle',
      speed: Math.round((derived.speedMult || 1) * 1000) / 1000,
      atkSpd: Math.round(attackSpeedMult() * 1000) / 1000,
      sect: mySectId()          // 门派：同门弟子之间不可互相神识锁定（门规）
    };
  }
  // 进/留在 'ling:<编号>' 房间（和副本共用一套 netMembers / netActRoom）
  function lingNetJoin(number) {
    /* 同一个房间的"重试进房"不能清名单：离线时进房请求发不出去，
     *   会一直重试 —— 每帧清一次的话，已经同步到的名单会被反复冲掉。 */
    const sameRoom = (netRoomKind === 'ling' && netLingNumber === number);
    netLingNumber = number;
    netRoomKind = 'ling';
    netActRoom = 'ling:' + number;
    if (!sameRoom) {
      netActSid = null;
      netActCount = 0;
      netMembers.clear();
    }
    netLingJoined = false;
    /* 聊天没连上时**不能记成"已进房"**：以前这里设完字段就返回，
     *   而 updateLingPresence 看到"房间号已经是这个"就直接 return，
     *   于是这个人永远没真正进房、别人传送过来自然看不到他。现在等连上会自动重试。 */
    if (!netChatReady()) return false;
    net.chat.send({ type: 'act:join', room: netActRoom, self: lingSelfPayload() });
    netLingJoined = true;
    return true;
  }
  function lingNetLeave() {
    if (netRoomKind === 'ling') {
      if (netChatReady() && netActRoom) net.chat.send({ type: 'act:leave' });
      netActRoom = null;
      netRoomKind = null;
      netActCount = 0;
      netMembers.clear();
    }
    netLingNumber = null;
    netLingJoined = false;
  }
  // 站在自己灵脉旁修炼 → 自动进房；走开 → 退房（副本里那套优先）
  function updateLingPresence() {
    if (activityRun) return;
    /* 交锋期间绝不能退房：灵脉的"在不在房间"是按"站在灵脉旁"算的，
     *   而战斗中 lingNodeActive() 必然为假 —— 一退房，服务端立刻判定"对方脱战"，
     *   战斗刚开打就结束（表现就是"只是被锁了一下，没有真的打起来"）。 */
    if (pvpDuel || pvpPending || pvpOffered) return;
    const want = (lingNode && lingNodeActive()) ? lingNode.number : null;
    // 已经在房里，而且这次的进房请求**确实发出去了**才算数（否则重试）
    if (want && netRoomKind === 'ling' && netLingNumber === want) {
      if (netLingJoined) return;
      // 没发出去过：节流重试（每秒一次），等聊天连上就自动补进房
      const now = Date.now();
      if (now - netLingJoinTryAt < 1000) return;
      netLingJoinTryAt = now;
      lingNetJoin(want);
      return;
    }
    if (want) lingNetJoin(want);
    else if (netRoomKind === 'ling') lingNetLeave();
  }
  // 灵脉房间的位置同步：10Hz 上报 + 真人插值（和副本那套同构）
  function updateLingNet(dt) {
    if (netRoomKind !== 'ling' || !netLingNumber) return;
    lingPosAcc += dt;
    if (lingPosAcc >= 0.1) {
      lingPosAcc = 0;
      if (netChatReady() && netActRoom) {
        const p = lingSelfPayload();
        net.chat.send({ type: 'act:pos', x: p.x, hp: p.hp, action: p.action,
          level: p.level, realm: p.realm, speed: p.speed, atkSpd: p.atkSpd });
      }
    }
    const now = Date.now();
    netMembers.forEach(function (m, sid) {
      if (now - m.ts > NET_MEMBER_TTL_MS) { netMembers.delete(sid); return; }
      if (m.drawX === undefined) m.drawX = m.localX;
      m.drawX += (m.localX - m.drawX) * Math.min(1, dt * 8);
      if (m.spawnT > 0) m.spawnT = Math.max(0, m.spawnT - dt);
    });
  }

  /* 传送到指定编号的灵脉旁：先放演出，落地在 updateLingTeleport 里结算 */
  function lingLocateTo(number, owner) {
    const n = clamp(Math.round(Number(number) || 0), LING_MIN, LING_MAX);
    if (!Number(number)) { showToast('请先填一个灵脉编号（1~' + LING_MAX + '）。'); return false; }
    if (lingTp) return false;
    if (gameState !== 'walk') { showToast('只能在路上传送（战斗 / 副本里不行）。'); return false; }
    lingTp = { t: 0, phase: 'in', number: n, owner: owner || '' };
    pushBattleLog('【灵脉】你以神识锁定编号 #' + n + (owner ? '（' + owner + ' 的灵脉）' : '') +
      '，头顶虚空裂开一枚小型漩涡……', 'system');
    return true;
  }

  function updateLingTeleport(dt) {
    if (!lingTp) { playerLiftY += (0 - playerLiftY) * Math.min(1, dt * 6); playerTpAlpha = 1; return; }
    lingTp.t += dt;
    if (lingTp.phase === 'in') {
      // 头顶漩涡把人往上吸：立绘抬高 + 淡出
      const k = clamp(lingTp.t / LING_TP_IN_SEC, 0, 1);
      playerLiftY = -170 * k * k;
      playerTpAlpha = 1 - k * 0.95;
      if (lingTp.t >= LING_TP_IN_SEC) {
        lingArriveAtVein(lingTp.number, lingTp.owner);
        lingTp.phase = 'mid';
        lingTp.t = 0;
      }
      return;
    }
    // 落地：从上方掉回地面
    const k = clamp(lingTp.t / LING_TP_OUT_SEC, 0, 1);
    playerLiftY = -170 * (1 - k) * (1 - k);
    playerTpAlpha = 0.15 + k * 0.85;
    if (lingTp.t >= LING_TP_OUT_SEC) {
      playerLiftY = 0;
      playerTpAlpha = 1;
      lingTp = null;
    }
  }

  /* 落地：把这棵仙人掌搬到玩家旁边，编号用玩家指定的那个 */
  function lingArriveAtVein(number, owner) {
    const factor = 0.55, baseY = 618, sc = 1.0;   // 近排装饰：看得清、点得到
    const cam = cameraX();
    const worldX = (player.worldX - cam) - LING_TP_OFFSET + cam * factor;
    lingForcedCactus = { worldX: worldX, factor: factor, baseY: baseY, sc: sc };
    const pool = randInt(LING_MIN, LING_MAX);
    /* 传送到的那棵仙人掌同样"点一次就定死"：记进备忘，之后不管点几次都还是这个编号与这份灵力 */
    const tpKey = lingCactusKey({ factor: factor, worldX: worldX });
    lingCactusRemember(tpKey, number, pool, pool);
    lingNode = { number: number, worldX: worldX, factor: factor, baseY: baseY, sc: sc,
      registered: true, pool: pool, poolMax: pool, drain: 0,
      owner: owner || '', located: true, key: tpKey };
    lingMotes.length = 0;
    input.moving = false;
    syncMoveButton();
    showToast('你从漩涡里落在灵脉 #' + number + (owner ? '（' + owner + '）' : '') + ' 旁。');
    pushBattleLog('【灵脉】落地：#' + number + (owner ? '（' + owner + ' 的灵脉）' : '') +
      '　灵力 ' + pool + ' 点', 'system');
    /* 落地后确保前方有下一个拦路者：路上没有敌人时玩家会"原地跑"走不动（见 update 里那段）。 */
    if (!activeEnemy && !cave && !worldBossRun) spawnNextEnemy();
    lingNetJoin(number);        // 进这个编号的房间，看看有没有真人守着
    /* 等服务器把房间名单发回来，明确告诉玩家这里有没有人 ——
     *   以前传送过去只能自己瞪大眼睛找，没人也不知道是"编号不对"还是"人家不在"。 */
    setTimeout(function () {
      if (netLingNumber !== number || netRoomKind !== 'ling') return;   // 已经换地方了
      const n = netMembers.size;
      showToast(n
        ? ('此处有 ' + n + ' 位道友正在修炼（编号 #' + number + '）')
        : ('编号 #' + number + ' 旁暂时没有其他道友在修炼。'));
      pushBattleLog('【灵脉】#' + number + (n
        ? (' 附近有 ' + n + ' 位道友正在修炼：' + Array.from(netMembers.values()).map(function (m) {
            return '道友·' + m.name;
          }).join('、'))
        : ' 附近暂时没有其他道友在修炼'), 'system');
    }, 1500);
  }

  /* 灵脉房间里别人的位置 → 屏幕坐标（x 是"相对灵脉的偏移"） */
  function lingPeerScreenX(localX) { return lingAnchorScreenX() + localX; }
  // 点真人 → 神识锁定
  function lingHitTestPeer(sx, sy) {
    if (netRoomKind !== 'ling') return null;
    let best = null, bestD = 999;
    netMembers.forEach(function (m) {
      const localX = (m.drawX === undefined ? m.localX : m.drawX);
      const bx = lingPeerScreenX(localX);
      const dx = Math.abs(sx - bx);
      if (dx > 52 || dx >= bestD) return;
      if (sy < GROUND_Y - 190 || sy > GROUND_Y + 10) return;
      bestD = dx; best = m;
    });
    return best;
  }

  /* ---------- 灵脉定位（测试用，已下架）----------
   *   验收完已移除按钮与窗口，避免线上玩家使用。
   *   传送核心 lingLocateTo(编号, 主人名) 保留：自动化用例通过 __debugLingLocate 调用它，
   *   灵脉房间的真人同步（lingNetJoin / updateLingPresence / drawLingPeers）也是正式玩法的一部分。 */

  // 灵气微粒贴图（预渲染一次，避免每帧造渐变）
  let xiuSprites = null;
  function getXiuSprite(kind) {
    if (!xiuSprites) {
      const make = function (rgb, core) {
        const S = 64;
        const c = document.createElement('canvas');
        c.width = S; c.height = S;
        const g = c.getContext('2d');
        const rg = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
        rg.addColorStop(0, 'rgba(255,255,255,' + core + ')');
        rg.addColorStop(0.22, 'rgba(' + rgb + ',0.85)');
        rg.addColorStop(0.58, 'rgba(' + rgb + ',0.26)');
        rg.addColorStop(1, 'rgba(' + rgb + ',0)');
        g.fillStyle = rg;
        g.fillRect(0, 0, S, S);
        return c;
      };
      xiuSprites = { qi: make(XIU_RGB, '0.95'), gold: make(XIU_GOLD, '0.9') };
    }
    return xiuSprites[kind] || xiuSprites.qi;
  }

  // 生成一撮灵气微粒（从四周/地面被吸向角色）
  function spawnXiuMotes(n) {
    for (let i = 0; i < n; i++) {
      if (xiuMotes.length >= XIU_MOTE_MAX) return;
      const dust = Math.random() < 0.28;                  // 一部分是"被卷起的沙尘"
      const ang = rand(0, Math.PI * 2);
      const r0 = rand(dust ? 120 : 200, dust ? 260 : 430);
      xiuMotes.push({
        id: ++xiuMoteSeq,
        ang: ang,
        r: r0,
        r0: r0,
        spin: rand(-0.55, 0.55) * (Math.random() < 0.5 ? 1 : -1),
        vIn: rand(66, 128) + 26 * xiu.amp,
        size: dust ? rand(16, 30) : rand(9, 20),
        t: 0,
        life: rand(1.4, 2.6),
        dust: dust,
        gold: !dust && Math.random() < 0.18,
        bloom: rand(0.6, 1.15)
      });
    }
  }

  function updateXiuLian(dt) {
    const want = canAbsorbQi();
    // 渐入渐出：坐下慢慢起效，起身很快收干净
    xiu.amp = clamp(xiu.amp + (want ? dt / XIU_RAMP_SEC : -dt / XIU_FADE_SEC), 0, 1);
    if (xiu.pulse > 0) xiu.pulse = Math.max(0, xiu.pulse - dt * 2.6);

    if (want && !xiuHinted) {
      xiuHinted = true;
      showToast('在灵脉（仙人掌）旁停下即可吸纳灵力修炼。');
      pushBattleLog('【灵脉·吐纳】你盘坐吐纳，周遭天地灵力开始朝你汇聚。', 'reward');
    }

    if (xiuActive()) {
      xiu.seconds += dt;
      // 刷微粒 / 地面涟漪
      xiu.moteT -= dt;
      if (xiu.moteT <= 0) {
        xiu.moteT = rand(0.02, 0.055) / Math.max(0.2, xiu.amp);
        spawnXiuMotes(1 + (Math.random() < xiu.amp * 0.75 ? 1 : 0));
      }
      xiu.ringT -= dt;
      if (xiu.ringT <= 0) {
        xiu.ringT = rand(0.5, 0.9);
        xiuRings.push({ t: 0, life: rand(1.0, 1.4), gold: Math.random() < 0.25 });
      }
    }

    // 微粒：向心加速 + 螺旋 + 贴到身上就"被吸收"
    for (let i = xiuMotes.length - 1; i >= 0; i--) {
      const m = xiuMotes[i];
      m.t += dt;
      const pull = m.vIn * (1 + (1 - m.r / m.r0) * 1.5);     // 越近吸得越猛
      m.r -= pull * dt;
      m.ang += m.spin * dt * (1 + (1 - m.r / m.r0));
      if (m.r <= 24 || m.t >= m.life) {
        xiuMotes.splice(i, 1);
        if (m.r <= 24) {
          xiu.pulse = Math.min(1, xiu.pulse + (m.dust ? 0.18 : 0.4));
          xiu.gained += 0;                                    // 修为在下面统一结算
        }
      }
    }
    for (let i = xiuRings.length - 1; i >= 0; i--) {
      xiuRings[i].t += dt;
      if (xiuRings[i].t >= xiuRings[i].life) xiuRings.splice(i, 1);
    }

    // 修为：按强度累积（静修所得走 applyXp，绕开"打怪才涨修为"的限制）
    if (want) {
      const gain = xiuRate() * xiu.amp * dt;
      xiu.acc += gain;
      /* ---------- 打坐吐纳：在灵脉旁才会回灵力 ----------
       *   回得多快取决于这棵灵脉还剩多少灵力（灵脉越足回得越快，被吸干就回不了），
       *   所以"回灵力"跟"修炼"用的是同一个地方：都得先找到并守住一条灵脉。 */
      if (lingNode && lingNode.poolMax > 0 && combat.mp < derived.mpUsable) {
        const poolFrac = clamp(lingNode.pool / lingNode.poolMax, 0, 1);
        const mpRate = LING_MP_REFILL_PCT * derived.mpUsable * poolFrac * xiu.amp;
        if (mpRate > 0) {
          combat.mp = Math.min(derived.mpUsable, combat.mp + mpRate * dt);
          xiuMpChanged = true;
        }
      }
      if (xiu.acc >= 1) {
        const n = Math.floor(xiu.acc);
        xiu.acc -= n;
        applyXp(n, { quiet: true });   // 静修：防抖落盘
        xiu.gained += n;
        // 灵脉的灵力是有限的：吸多少就从仙人掌里扣多少，吸干就枯萎爆炸
        if (lingNode && lingNode.pool > 0) {
          lingNode.pool = Math.max(0, lingNode.pool - n);
          lingCactusUpdatePool(lingNode.key, lingNode.pool);   // 剩余量写回备忘，读档后接着用同一个上限
          if (lingNode.pool <= 0) lingExhausted();
        }
      }
      // HUD 上的"静修 +X/s"每秒刷几次
      xiu.uiT -= dt;
      if (xiu.uiT <= 0) {
        xiu.uiT = 0.25;
        updateLevelUI();
        if (xiuMpChanged) { xiuMpChanged = false; updateBattleHud(); }
      }
    }
  }

  /* ---------- 地面层：环境压暗 / 灵力薄雾 / 法阵 / 内收涟漪 ---------- */
  function drawXiuLianGround() {
    if (!xiuActive()) return;
    const a = xiu.amp;
    const sx = player.worldX - cameraX();
    const cy = GROUND_Y - 96;                    // 灵力汇聚中心（胸口高度）
    const t = xiu.seconds;
    ctx.save();

    // ① 环境：外圈压暗（像灵力被抽走）+ 中心青色薄雾
    const vign = ctx.createRadialGradient(sx, cy, 70, sx, cy, 660);
    vign.addColorStop(0, 'rgba(4, 10, 20, 0)');
    vign.addColorStop(0.42, 'rgba(4, 10, 20, ' + (0.10 * a).toFixed(3) + ')');
    vign.addColorStop(1, 'rgba(2, 6, 12, ' + (0.36 * a).toFixed(3) + ')');
    ctx.fillStyle = vign;
    ctx.fillRect(0, 0, W, H);

    const mistR = 300 + 90 * Math.sin(t * 0.9);
    const mist = ctx.createRadialGradient(sx, cy, 8, sx, cy, mistR);
    mist.addColorStop(0, 'rgba(' + XIU_RGB + ', ' + (0.17 * a).toFixed(3) + ')');
    mist.addColorStop(0.5, 'rgba(' + XIU_RGB + ', ' + (0.07 * a).toFixed(3) + ')');
    mist.addColorStop(1, 'rgba(' + XIU_RGB + ', 0)');
    ctx.fillStyle = mist;
    ctx.beginPath();
    ctx.arc(sx, cy, mistR, 0, Math.PI * 2);
    ctx.fill();

    // ② 地面法阵：两圈反向旋转的刻度环（透视压扁）
    const gy = GROUND_Y + 4;
    const ring = function (radius, rot, seg, gapRatio, lw, alpha) {
      ctx.save();
      ctx.translate(sx, gy);
      ctx.scale(1, 0.3);
      ctx.rotate(rot);
      ctx.lineWidth = lw;
      ctx.strokeStyle = 'rgba(' + XIU_RGB + ',' + alpha.toFixed(3) + ')';
      const step = Math.PI * 2 / seg;
      for (let i = 0; i < seg; i++) {
        ctx.beginPath();
        ctx.arc(0, 0, radius, i * step, i * step + step * (1 - gapRatio));
        ctx.stroke();
      }
      ctx.restore();
    };
    ring(126 * a + 60, t * 0.22, 22, 0.45, 2.2, 0.42 * a);
    ring(168 * a + 74, -t * 0.15, 12, 0.62, 1.6, 0.30 * a);
    ctx.save();
    ctx.translate(sx, gy);
    ctx.scale(1, 0.3);
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = 'rgba(' + XIU_RGB + ',' + (0.5 * a).toFixed(3) + ')';
    for (let i = 0; i < 8; i++) {                  // 八道向内的符文刻线
      const ang = t * 0.3 + i * Math.PI / 4;
      const r1 = 150 * a + 66, r2 = 118 * a + 54;
      ctx.beginPath();
      ctx.moveTo(Math.cos(ang) * r1, Math.sin(ang) * r1);
      ctx.lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2);
      ctx.stroke();
    }
    ctx.restore();
    // 脚下的灵光
    const foot = ctx.createRadialGradient(sx, gy, 4, sx, gy, 120 * (0.9 + 0.1 * Math.sin(t * 1.7)));
    foot.addColorStop(0, 'rgba(' + XIU_RGB + ',' + (0.34 * a).toFixed(3) + ')');
    foot.addColorStop(1, 'rgba(' + XIU_RGB + ',0)');
    ctx.fillStyle = foot;
    ctx.beginPath();
    ctx.ellipse(sx, gy, 130 * a + 30, 34 * a + 10, 0, 0, Math.PI * 2);
    ctx.fill();

    // ③ 向内收拢的涟漪（气血反弹是往外荡，这里正好相反）
    ctx.globalCompositeOperation = 'lighter';
    xiuRings.forEach(function (r) {
      const p = clamp(r.t / r.life, 0, 1);
      const rad = (1 - Math.pow(1 - p, 2)) * 210 + 24;
      const alpha = Math.sin(Math.PI * p) * 0.5 * a;
      const rgb = r.gold ? XIU_GOLD : XIU_RGB;
      ctx.lineWidth = 2.4 * (1 - p) + 0.8;
      ctx.strokeStyle = 'rgba(' + rgb + ',' + alpha.toFixed(3) + ')';
      ctx.beginPath();
      ctx.ellipse(sx, gy, rad, rad * 0.3, 0, 0, Math.PI * 2);
      ctx.stroke();
    });
    ctx.restore();
  }

  /* ---------- 前景层：光柱 / 灵气微粒 / 螺旋丝带 / 吸收脉冲 ---------- */
  function drawXiuLianFront() {
    if (!xiuActive()) return;
    const a = xiu.amp;
    const sx = player.worldX - cameraX();
    const cy = GROUND_Y - 96;
    const t = xiu.seconds;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // ⑥ 自下而上的淡光柱（随呼吸脉动 + 轻微上移的 shimmer）
    const colW = 96 + 18 * Math.sin(t * 1.3);
    const colTop = GROUND_Y - 300;
    const col = ctx.createLinearGradient(0, GROUND_Y + 6, 0, colTop);
    col.addColorStop(0, 'rgba(' + XIU_RGB + ',' + (0.26 * a).toFixed(3) + ')');
    col.addColorStop(0.45, 'rgba(' + XIU_RGB + ',' + (0.10 * a).toFixed(3) + ')');
    col.addColorStop(1, 'rgba(' + XIU_RGB + ',0)');
    ctx.fillStyle = col;
    ctx.fillRect(sx - colW / 2, colTop, colW, GROUND_Y + 6 - colTop);
    const shimmer = (t * 60) % 140;
    const sh = ctx.createLinearGradient(0, GROUND_Y - shimmer, 0, GROUND_Y - shimmer - 120);
    sh.addColorStop(0, 'rgba(255,255,255,0)');
    sh.addColorStop(0.5, 'rgba(' + XIU_RGB + ',' + (0.10 * a).toFixed(3) + ')');
    sh.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sh;
    ctx.fillRect(sx - colW / 2, GROUND_Y - shimmer - 120, colW, 120);

    // ⑤ 吸收脉冲：胸口一圈扩散的灵光 + 一点高光
    if (xiu.pulse > 0.02) {
      const pr = (1 - xiu.pulse) * 46 + 12;
      const pa = xiu.pulse * 0.55 * a;
      ctx.strokeStyle = 'rgba(255,255,255,' + pa.toFixed(3) + ')';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.arc(sx, cy, pr, 0, Math.PI * 2);
      ctx.stroke();
      const core = ctx.createRadialGradient(sx, cy, 0, sx, cy, 64 * (0.6 + xiu.pulse * 0.7));
      core.addColorStop(0, 'rgba(255,255,255,' + (0.30 * xiu.pulse * a).toFixed(3) + ')');
      core.addColorStop(0.4, 'rgba(' + XIU_RGB + ',' + (0.18 * xiu.pulse * a).toFixed(3) + ')');
      core.addColorStop(1, 'rgba(' + XIU_RGB + ',0)');
      ctx.fillStyle = core;
      ctx.beginPath();
      ctx.arc(sx, cy, 70, 0, Math.PI * 2);
      ctx.fill();
    }

    // ④③ 灵气微粒：贴图 + 两段拖尾（朝向角色，越近越亮）
    const dust = getDustSprite();
    xiuMotes.forEach(function (m) {
      const px = sx + Math.cos(m.ang) * m.r;
      const py = cy + Math.sin(m.ang) * m.r * 0.9;
      // 拖尾：沿着"往中心"的方向往回退一点
      const tx = sx + Math.cos(m.ang) * Math.min(m.r0, m.r + m.size * 1.8 + 14);
      const ty = cy + Math.sin(m.ang) * Math.min(m.r0, m.r + m.size * 1.8 + 14) * 0.9;
      const near = 1 - clamp(m.r / m.r0, 0, 1);
      const fade = Math.min(1, m.t / 0.35) * (1 - Math.max(0, (m.r - 24) < 0 ? 1 : 0));
      if (m.dust) {
        ctx.globalAlpha = (0.16 + near * 0.3) * a;
        ctx.drawImage(dust, px - m.size, py - m.size * 0.7, m.size * 2, m.size * 1.4);
        ctx.globalAlpha = (0.22 + near * 0.4) * a;
        ctx.drawImage(dust, tx - m.size * 0.7, ty - m.size * 0.5, m.size * 1.4, m.size);
      } else {
        const sp = getXiuSprite(m.gold ? 'gold' : 'qi');
        ctx.globalAlpha = (0.30 + near * 0.55) * fade * a * m.bloom;
        ctx.drawImage(sp, px - m.size / 2, py - m.size / 2, m.size, m.size);
        ctx.globalAlpha = (0.14 + near * 0.26) * fade * a;
        ctx.drawImage(sp, tx - m.size * 0.75, ty - m.size * 0.75, m.size * 1.5, m.size * 1.5);
      }
    });
    ctx.globalAlpha = 1;

    // 螺旋丝带：几道绕着角色盘进去的灵气线
    ctx.lineWidth = 1.6;
    for (let i = 0; i < 4; i++) {
      const ph = (t * 0.55 + i * 0.25) % 1;
      const r0 = 230 - ph * 190;
      const base = t * (0.5 + i * 0.12) + i * 1.7;
      ctx.strokeStyle = 'rgba(' + XIU_RGB + ',' + (0.20 * (1 - ph) * a).toFixed(3) + ')';
      ctx.beginPath();
      for (let k = 0; k <= 22; k++) {
        const q = k / 22;
        const ang = base + q * 1.5;
        const rr = r0 * (1 - q * 0.42);
        const x = sx + Math.cos(ang) * rr;
        const y = cy + Math.sin(ang) * rr * 0.9;
        if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  // 玩家身上的脏水污渍
  function drawDirtyOnPlayer() {
    if (playerDirtyT <= 0) return;
    const sx = player.worldX - cameraX();
    const a = Math.min(1, playerDirtyT / 1.6) * 0.55;
    const stains = [
      [-18, -120, 16, 7], [14, -96, 12, 6], [-6, -64, 18, 8],
      [22, -140, 9, 5], [-30, -78, 10, 5]
    ];
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx - 80, GROUND_Y - 182, 160, 184);
    ctx.clip();
    ctx.fillStyle = 'rgba(84, 74, 42, ' + a.toFixed(3) + ')';
    stains.forEach(function (s, idx) {
      ctx.beginPath();
      ctx.ellipse(sx + s[0], GROUND_Y + s[1], s[2], s[3], (idx - 2) * 0.24, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawDirtyDrops() {
    ctx.save();
    for (let i = 0; i < dirtyDrops.length; i++) {
      const d = dirtyDrops[i];
      const a = Math.max(0, 1 - d.t / d.life);
      ctx.globalAlpha = a * 0.85;
      ctx.fillStyle = '#6f6330';
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.size * (0.6 + a * 0.4), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawCans() {
    const cam = cameraX();
    for (let i = 0; i < cans.length; i++) {
      const c = cans[i];
      const sx = c.worldX - cam;
      if (sx < -80 || sx > W + 80) continue;
      ctx.save();
      ctx.translate(sx, c.y);
      ctx.rotate(c.rot);
      ctx.fillStyle = '#c6ccd4';
      roundRect(-9, -15, 18, 30, 4);
      ctx.fill();
      ctx.fillStyle = '#eef2f6';
      ctx.fillRect(-8, -14, 5, 28);
      ctx.fillStyle = '#98a0a8';
      ctx.beginPath();
      ctx.ellipse(0, -15, 9, 3.6, 0, 0, Math.PI * 2);
      ctx.ellipse(0, 15, 9, 3.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#818a92';
      ctx.lineWidth = 1.2;
      roundRect(-9, -15, 18, 30, 4);
      ctx.stroke();
      ctx.restore();
    }
  }

  // ---------- 界面 ----------
  function updateDist() {
    const px = Math.max(0, player.worldX - START_X);
    const km = px / KM_UNIT;
    ui.dist.innerHTML = km.toFixed(1) + ' <small>km</small>';
  }

  // mode: 'walk' 走路可用；'battle' 战斗按钮可用
  function setControls(mode) {
    const inBattle = mode === 'battle';
    const playerCanAct = inBattle && battleTurn === 'player' && pendingVictory <= 0;
    ui.btnMove.disabled = inBattle;
    ui.btnAttack.disabled = !playerCanAct;
    // 法术按钮：非战斗时也能点开技能窗口看看（只是不能施法），战斗轮到自己出手时才真正能放
    ui.btnSpell.disabled = inBattle && !playerCanAct;
    ui.btnDefend.disabled = !playerCanAct;
  }

  function syncMoveButton() {
    const moving = (gameState === 'walk' || worldBossLootActive()) && input.moving;
    ui.btnMove.classList.toggle('active', moving);
    // 按钮文字表示“点击后会做什么”：赶路中显示「休息」，休息中显示「移动」
    const label = moving ? '休息' : '移动';
    if (ui.btnMove.textContent !== label) ui.btnMove.textContent = label;
  }

  function formatLogTime() {
    const t = Math.max(0, Math.floor(battleLogClock));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }

  function guessLogKind(text, kind) {
    if (kind) return kind;
    if (/你损失|敌人|拦路者|精英|传奇|反击|抢先/.test(text)) return 'enemy';
    if (/^你|你(发起|使出|施展|喝下|召唤|装备)/.test(text)) return 'player';
    return 'system';
  }

  function pushBattleLog(text, kind) {
    if (!text) return;
    battleLogHistory.push({
      time: formatLogTime(),
      text: text,
      kind: guessLogKind(text, kind)
    });
    if (battleLogHistory.length > 200) battleLogHistory.shift();
    if (!ui.logPopup.classList.contains('hidden')) renderBattleLogPanel();
  }

  function renderBattleLogPanel() {
    ui.logList.textContent = '';
    if (!battleLogHistory.length) {
      const empty = document.createElement('div');
      empty.className = 'logRow system';
      const span = document.createElement('span');
      span.className = 'logText';
      span.textContent = '暂无战斗记录，去路上遭遇敌人吧。';
      empty.appendChild(span);
      ui.logList.appendChild(empty);
      return;
    }
    battleLogHistory.forEach(function (entry) {
      const row = document.createElement('div');
      row.className = 'logRow ' + entry.kind;
      const time = document.createElement('span');
      time.className = 'logTime';
      time.textContent = '[' + entry.time + ']';
      const text = document.createElement('span');
      text.className = 'logText';
      text.textContent = entry.text;
      row.appendChild(time);
      row.appendChild(text);
      ui.logList.appendChild(row);
    });
    ui.logList.scrollTop = ui.logList.scrollHeight;
  }

  function openBattleLog() {
    renderBattleLogPanel();
    ui.logPopup.classList.remove('hidden');
    document.getElementById('wrap').classList.add('log-open');
  }

  function closeBattleLog() {
    // 战斗日志只在玩家手动关闭窗口时清空（战斗结束 / 搜索尸体都不再清空）
    battleLogHistory.length = 0;
    renderBattleLogPanel();
    ui.logPopup.classList.add('hidden');
    document.getElementById('wrap').classList.remove('log-open');
  }

  // 清空日志（只在开始新一局、或玩家关闭日志窗口时调用）
  function clearBattleLog() {
    battleLogHistory.length = 0;
    if (!ui.logPopup.classList.contains('hidden')) renderBattleLogPanel();
  }

  // ---------- 左下角聊天窗口（本地模式：暂未联网，仅本地显示） ----------
  const CHAT_MAX_ROWS = 60;
  const CHAT_NICK = '旅人';
  const CHAT_CHANNEL = 'world';
  let chatInited = false;
  let chatLocalHintShown = false;
  let chatOfflineNoticed = false;   // 「正在重连」只提示一次，连上了再重置（避免刷屏）
  const chatSeenKeys = new Set();   // 去重（服务器会把历史 + 自己的发言都广播回来）

  // 聊天昵称：优先用玩家自定的昵称，其次账号名，最后默认"旅人"
  function chatNick() { return account.nickname || account.name || CHAT_NICK; }
  function chatKey(m) { return String(m.ts || 0) + '|' + String(m.text || ''); }

  function updateChatStatus() {
    if (!ui.chatStatus) return;
    if (net.chatState === 'online') {
      /* 不显示在线人数：人少时反而让玩家觉得"没人"，也让"新玩家/机器人"的加入无从对照。
       * 这里显示的是"连接状态"（连接频道），世界频道这个名字留给左边的标题。 */
      ui.chatStatus.textContent = '连接频道' + (net.identityBound ? '' : ' · 未实名');
      ui.chatStatus.title = net.identityBound
        ? ('已实名：' + (net.profile && net.profile.realName ? net.profile.realName : '') +
           (net.profile && net.profile.idCardMasked ? '（' + net.profile.idCardMasked + '）' : ''))
        : '按实名制要求：首次发言前需要绑定姓名与身份证号（点击聊天输入框发送即可触发）';
      ui.chatStatus.classList.add('online');
      ui.chatStatus.classList.remove('offline');
    } else if (net.chatState === 'connecting') {
      ui.chatStatus.textContent = '连接频道 · 连接中…';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
    } else if (net.accountOnline) {
      // 账号连上服务器了，但聊天长连接还没通（多半是 nginx 没配 /ws 反代）
      ui.chatStatus.textContent = '聊天未连接（点击重试）';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
      ui.chatStatus.title = '聊天长连接没建立：请确认 nginx 里 /ws 的反代配置（Upgrade / Connection 两行）后重载 nginx，' +
        '然后点这里重试。后端地址：' + net.serverUrl;
    } else {
      ui.chatStatus.textContent = '本地模式（未联网）';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
    }
  }

  // 手动重连聊天（点聊天窗右上角状态即可触发）
  function reconnectChat() {
    if (!net.accountOnline) {
      showToast('当前是本地单机模式（没连上服务器账号），无法连接世界频道。');
      return;
    }
    if (net.chat) { try { net.chat.close(); } catch (e) { /* 忽略 */ } net.chat = null; }
    net.chatState = 'connecting';
    updateChatStatus();
    connectChat();
    showToast('正在重新连接世界频道…');
  }

  // 连服务器聊天（登录成功后调用；连不上会自动重连，不影响单机玩法）
  function connectChat() {
    if (net.chat) return net.chat;
    // 单机模式（没连上服务器账号）不连聊天：避免对着不存在的 /ws 反复重连刷报错
    if (!net.accountOnline) {
      net.chatState = 'offline';
      updateChatStatus();
      return null;
    }
    if (!net.serverUrl) net.serverUrl = netServerFromInput(ui.loginIp ? ui.loginIp.value : '');
    net.chat = createChatClient({
      // token 走查询串（服务器据此把昵称绑定为账号名，防冒充）
      url: wsUrlFrom(net.serverUrl, net.token ? { token: net.token } : {}),
      nick: chatNick(),
      token: net.token,
      onStatus: chatStatusChanged,
      onMessage: onChatServerMessage
    });
    refreshProfile();
    return net.chat;
  }

  /* 聊天连接状态变了：刷状态栏；连回线上时给一条提示（断线期间只提示过一次"正在重连"，
   *   连回来顺手把那面旗子复位，下次真断线还能再提示一次）。 */
  function chatStatusChanged(st) {
    net.chatState = st;
    updateChatStatus();
    if (st !== 'online') return;
    if (chatOfflineNoticed) {
      pushChatMessage('', '世界频道已重新连接。', 'system');
      chatOfflineNoticed = false;
    } else {
      pushChatMessage('', '已连接世界频道（' + net.serverUrl + '）', 'system');
    }
    // 已经站在副本里 → 连上就补进"真人房间"
    if (activityRun) activityNetJoin();
    if (sectScene) sectNetJoin();
  }

  /* ---------- 实名信息（服务器账号级）---------- */
  function refreshProfile() {
    if (!net.api || !net.token) { net.identityBound = false; net.profile = null; return Promise.resolve(null); }
    return net.api.profile(net.token).then(function (r) {
      net.profile = r.profile || null;
      net.identityBound = !!(r.profile && r.profile.bound);
      updateChatStatus();
      return net.profile;
    }).catch(function () { return null; });
  }

  function showIdentityError(text) {
    if (!ui.identityError) return;
    if (!text) { ui.identityError.classList.add('hidden'); return; }
    ui.identityError.textContent = text;
    ui.identityError.classList.remove('hidden');
  }

  // 打开实名绑定窗口（pendingText：绑定成功后自动补发的那条消息）
  function openIdentityPanel(pendingText) {
    if (!ui.identityPopup) return;
    net.pendingChatText = pendingText || '';
    showIdentityError('');
    ui.identityPopup.classList.remove('hidden');
    if (ui.idRealName) { ui.idRealName.focus(); }
  }

  function closeIdentityPanel() {
    if (!ui.identityPopup) return;
    ui.identityPopup.classList.add('hidden');
    showIdentityError('');
    net.pendingChatText = '';
  }

  /* ============================================================
   * 昵称：默认用账号名，玩家可在「角色」窗口改成自己的昵称
   *   联网：存服务器账号（唯一、过屏蔽词）；单机：存本机 localStorage
   *   改完聊天会用新昵称重新连接，战斗信息里的名字也一起换
   * ============================================================ */
  const NICK_KEY_PREFIX = 'homeward.nick.';

  function nickStorageKey() { return NICK_KEY_PREFIX + (account.hash || account.name || 'default'); }

  function loadLocalNickname() {
    try { return window.localStorage.getItem(nickStorageKey()) || ''; } catch (e) { return ''; }
  }

  function saveLocalNickname(nick) {
    try { window.localStorage.setItem(nickStorageKey(), nick || ''); } catch (e) { /* 忽略 */ }
  }

  // 昵称变了：刷新界面（战斗信息里的名字）+ 聊天用新昵称重连
  function applyNicknameChange(nick) {
    account.nickname = nick || '';
    const who = document.querySelector('#battleHud .who');
    if (who) who.textContent = chatNick();
    updateNickUI();
    if (net.accountOnline) reconnectChat();
  }

  function updateNickUI() {
    if (ui.nickNow) ui.nickNow.textContent = chatNick();
    if (ui.btnEditNick) ui.btnEditNick.title = '当前昵称：' + chatNick();
  }

  function showNickError(text) {
    if (!ui.nickError) return;
    if (!text) { ui.nickError.classList.add('hidden'); return; }
    ui.nickError.textContent = text;
    ui.nickError.classList.remove('hidden');
  }

  function openNickPanel() {
    if (!ui.nickPopup) return;
    updateNickUI();
    showNickError('');
    if (ui.nickInput) {
      ui.nickInput.value = account.nickname || account.name || '';
      ui.nickInput.focus();
      ui.nickInput.select();
    }
    ui.nickPopup.classList.remove('hidden');
  }

  function closeNickPanel() {
    if (!ui.nickPopup) return;
    ui.nickPopup.classList.add('hidden');
    showNickError('');
  }

  function submitNickname() {
    const nick = (ui.nickInput && ui.nickInput.value ? ui.nickInput.value : '').trim();
    if (nick.length < 2 || nick.length > 12) {
      showNickError('昵称需要 2~12 个字符');
      return Promise.resolve(false);
    }
    if (/[\u0000-\u001f]/.test(nick)) { showNickError('昵称不能包含特殊字符'); return Promise.resolve(false); }

    // 联网：交给服务器校验（唯一性 + 屏蔽词），成功后本地也记一份
    if (net.accountOnline && net.api && net.token) {
      if (ui.nickSubmit) { ui.nickSubmit.disabled = true; ui.nickSubmit.textContent = '保存中…'; }
      return net.api.setNickname(net.token, nick).then(function (r) {
        const finalNick = (r.profile && r.profile.nickname) || nick;
        saveLocalNickname(finalNick);
        applyNicknameChange(finalNick);
        closeNickPanel();
        showToast('昵称已改为「' + finalNick + '」，世界频道会用这个昵称。');
        return true;
      }).catch(function (err) {
        showNickError((err && err.message) || '保存失败，请稍后再试');
        return false;
      }).then(function (r) {
        if (ui.nickSubmit) { ui.nickSubmit.disabled = false; ui.nickSubmit.textContent = '保存'; }
        return r;
      });
    }
    // 单机：只存本机
    saveLocalNickname(nick);
    applyNicknameChange(nick);
    closeNickPanel();
    showToast('昵称已改为「' + nick + '」（本机存档，联网后可在账号里同步）。');
    return Promise.resolve(true);
  }

  function submitIdentity() {
    if (!ui.identityPopup || ui.identityPopup.classList.contains('hidden')) return Promise.resolve(false);
    const realName = (ui.idRealName.value || '').trim();
    const idCard = (ui.idCardNo.value || '').trim().toUpperCase();
    if (!realName) { showIdentityError('请填写姓名'); ui.idRealName.focus(); return Promise.resolve(false); }
    if (!idCard) { showIdentityError('请填写身份证号'); ui.idCardNo.focus(); return Promise.resolve(false); }
    if (!/^\d{17}[\dX]$/.test(idCard)) { showIdentityError('身份证号需要 18 位（末位可能是 X）'); ui.idCardNo.focus(); return Promise.resolve(false); }
    if (!net.api || !net.token) { showIdentityError('当前未连服务器账号，无法绑定实名'); return Promise.resolve(false); }

    if (ui.identitySubmit) { ui.identitySubmit.disabled = true; ui.identitySubmit.textContent = '提交中…'; }
    return net.api.bindIdentity(net.token, realName, idCard).then(function (r) {
      net.profile = r.profile || null;
      net.identityBound = !!(r.profile && r.profile.bound);
      updateChatStatus();
      const text = net.pendingChatText;
      if (ui.identityPopup) ui.identityPopup.classList.add('hidden');
      showIdentityError('');
      showToast('实名信息已绑定：' + (net.profile.realName || realName) +
        '（' + (net.profile.idCardMasked || '') + '）');
      if (text) {
        const share = net.pendingChatShare || '';
        net.pendingChatText = '';
        net.pendingChatShare = '';
        sendChatText(text, share);     // 自动补发刚才那条（物品分享负载一起带上）
      }
      return true;
    }).catch(function (err) {
      showIdentityError((err && err.message) || '绑定失败，请稍后再试');
      return false;
    }).then(function (r) {
      if (ui.identitySubmit) { ui.identitySubmit.disabled = false; ui.identitySubmit.textContent = '提交绑定'; }
      return r;
    });
  }

  function onChatServerMessage(msg) {
    if (!msg || !msg.type) return;
    // 活动副本的房间同步消息（真人位置）
    if (msg.type.indexOf('act:') === 0) { onActivityNetMessage(msg); return; }
    if (msg.type === 'hello') return;
    if (msg.type === 'welcome') {
      net.onlineCount = msg.online || 1;
      updateChatStatus();
      (msg.history || []).forEach(function (m) {
        const key = chatKey(m);
        if (chatSeenKeys.has(key)) return;
        chatSeenKeys.add(key);
        pushChatMessage(m.nick || '', m.text || '', m.nick === chatNick() ? 'me' : 'other',
          m.share || '', m.ts);       // 用服务器记的发送时间，而不是"现在"
      });
      return;
    }
    if (msg.type === 'online') {
      // 服务器已经不再下发在线人数；这里保留兼容处理但不显示
      return;
    }
    if (msg.type === 'chat') {
      const key = chatKey(msg);
      if (chatSeenKeys.has(key)) return;
      chatSeenKeys.add(key);
      if (msg.kind === 'system') pushChatMessage('', msg.text || '', 'system', '', msg.ts);
      else {
        pushChatMessage(msg.nick || '', msg.text || '', msg.nick === chatNick() ? 'me' : 'other',
          msg.share || '', msg.ts);
        if (msg.nick === chatNick()) net.pendingChatText = '';   // 自己的消息已经成功发出
      }
      return;
    }
    if (msg.type === 'error') {
      if (msg.code === 'need_identity') {
        // 服务器要求实名：弹出绑定窗口，绑定成功后会补发刚才那条
        const pending = net.pendingChatText || '';
        net.pendingChatText = '';
        pushChatMessage('', '（需要先绑定实名信息才能在世界频道发言）', 'system');
        openIdentityPanel(pending);
        return;
      }
      if (msg.code === 'need_login') {
        pushChatMessage('', '（请先用账号登录后再发言）', 'system');
        return;
      }
      pushChatMessage('', '（' + (msg.message || '发送失败') + '）', 'system');
    }
  }

  /* 聊天时间：优先用**服务器给的时间戳**（每条消息自己带的发送时刻），
   * 这样历史记录补发回来时不会"全部变成当前玩家登录/连接的时间"。
   * 没有时间戳（本地机器人 / 单机回显）才退回本机当前时间。 */
  function chatTimeText(ts) {
    const n = Number(ts);
    const d = (isFinite(n) && n > 0) ? new Date(n < 1e12 ? n * 1000 : n) : new Date();
    return String(d.getHours()).padStart(2, '0') + ':' +
      String(d.getMinutes()).padStart(2, '0');
  }

  /* 聊天正文渲染：把表情包的 token（如 [僵尸1]）换成小图，其余部分仍走 textContent（防 XSS） */
  function appendChatTextWithStickers(host, text) {
    const s = String(text == null ? '' : text);
    let idx = 0;
    while (idx < s.length) {
      let hit = null, at = -1;
      CHAT_STICKERS.forEach(function (st) {
        const p = s.indexOf(st.token, idx);
        if (p >= 0 && (at < 0 || p < at)) { at = p; hit = st; }
      });
      if (!hit) { host.appendChild(document.createTextNode(s.slice(idx))); break; }
      if (at > idx) host.appendChild(document.createTextNode(s.slice(idx, at)));
      const img = document.createElement('img');
      img.className = 'chatSticker';
      img.src = assetUrl(hit.art);
      img.alt = hit.name;
      img.title = hit.name;
      host.appendChild(img);
      idx = at + hit.token.length;
    }
  }

  function pushChatMessage(name, text, kind, share, ts) {
    if (!ui.chatList) return;
    const row = document.createElement('div');
    row.className = 'chatRow' + (kind ? ' ' + kind : '');

    const time = document.createElement('span');
    time.className = 'chatTime';
    time.textContent = '[' + chatTimeText(ts) + ']';
    const tn = Number(ts);
    if (isFinite(tn) && tn > 0) {
      const full = new Date(tn < 1e12 ? tn * 1000 : tn);
      time.dataset.ts = String(tn);      // 测试/排查用：原始时间戳
      time.title = full.toLocaleString();
    }
    row.appendChild(time);

    if (name) {
      const who = document.createElement('span');
      who.className = 'chatName';
      who.textContent = name + '：';
      row.appendChild(who);
    }

    const content = document.createElement('span');
    content.className = 'chatText';
    const shared = parseItemShare(share);
    if (shared) {
      /* 物品分享：正文只放"【分享】+ 名字"，名字是可点的链接，
       * 点一下才弹出这件物品的详细属性（其它玩家也能点，数据来自负载） */
      const tag = document.createElement('span');
      tag.className = 'chatShareTag';
      tag.textContent = '【分享】';
      content.appendChild(tag);
      const link = document.createElement('a');
      link.className = 'chatShareLink';
      link.textContent = shared.n;
      link.setAttribute('role', 'button');
      link.setAttribute('tabindex', '0');
      const rar = RARITY_DEFS[shared.r];
      link.style.color = (rar && rar.color) || '#ffe6bb';
      link.title = '点击查看「' + shared.n + '」的详细属性';
      const open = function (ev) { ev.preventDefault(); ev.stopPropagation(); openSharedItemTip(link, shared); };
      link.addEventListener('click', open);
      link.addEventListener('keydown', function (ev) { if (ev.key === 'Enter' || ev.key === ' ') open(ev); });
      content.appendChild(link);
    } else {
      appendChatTextWithStickers(content, text);   // 表情包 token → 图片，其余照旧 textContent（避免 XSS）
    }
    row.appendChild(content);

    ui.chatList.appendChild(row);
    while (ui.chatList.children.length > CHAT_MAX_ROWS) {
      ui.chatList.removeChild(ui.chatList.firstChild);
    }
    ui.chatList.scrollTop = ui.chatList.scrollHeight;
    /* 聊天窗收起时来了新消息 → 世界频道旁边亮一个小红点（自己发的不算） */
    if (kind !== 'me' && chatCollapsed()) setChatDot(true);
  }

  // 发送：联网时发给服务器（服务器会广播回来，含自己）；离线时只在本机回显
  function sendChatMessage() {
    if (!ui.chatInput) return;
    const text = (ui.chatInput.value || '').trim().slice(0, 100);
    if (!text) return;
    ui.chatInput.value = '';
    ui.chatInput.focus();
    toggleChatEmojiPanel(false);   // 发完把表情面板收起来
    sendChatText(text);
    dailyQuestTick('chat');      // 每日任务：聊天窗口发言一次
    sectTaskTick('chat');        // 门派任务：世界频道发言
  }

  // share：可选的"物品分享负载"（JSON 字符串），服务器原样带回来，用来渲染可点的物品链接
  function sendChatText(text, share) {
    /* 没激活的表情包发不出去：发之前把锁着的 token 去掉（防止手打 / 粘贴绕过去） */
    const raw = String(text == null ? '' : text);
    const cleaned = stripLockedStickers(raw);
    if (cleaned !== raw) showToast('没有激活的表情包发不出去，已经自动去掉了。');
    text = cleaned.trim();
    if (!text) return;
    if (net.chat && net.chatState === 'online') {
      net.pendingChatText = text;   // 万一被"未实名"拦下，绑定后要补发这条
      net.pendingChatShare = share || '';
      net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text, share: share || undefined });
      return;   // 不本地回显：等服务器广播回来，避免出现两条
    }
    // 账号已经登录服务器，只是聊天长连接断了：消息进队列，连上后自动补发（不要当成单机）
    if (net.accountOnline && net.chat) {
      net.pendingChatText = text;
      net.pendingChatShare = share || '';
      net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text, share: share || undefined });
      /* 断线提示只刷一次：以前每按一次回车就插一条，窗口里堆满同一句话。
       *   连上之后（见 connectChat 的 onStatus）会复位，下次真断线还能再提示。 */
      if (!chatOfflineNoticed) {
        chatOfflineNoticed = true;
        pushChatMessage('', '（聊天连接已断开，正在重连：消息会在连上后自动发出）', 'system');
      }
      if (net.chatState !== 'connecting') reconnectChat();
      return;
    }
    pushChatMessage(chatNick(), text, 'me', share);
    if (!chatLocalHintShown) {
      chatLocalHintShown = true;
      pushChatMessage('', '（本地单机模式：消息只显示在本机，其它玩家看不到）', 'system');
    }
    maybeBotReply();
  }

  function setupChat() {
    if (chatInited) return;
    chatInited = true;
    updateChatStatus();
    pushChatMessage('', '欢迎来到世界频道。', 'system');
    if (!net.chat) {
      pushChatMessage('', '当前未连接服务器（本地模式）：消息只显示在本机。', 'system');
    }
    pushChatMessage('', '输入消息后按回车发送，右上角可「收起」。', 'system');
    // 启动机器人随机发言（间隔 30~120 秒随机抖动）
    scheduleChatBot();
  }

  function toggleChatCollapse() {
    if (!ui.chatPanel || !ui.chatToggle) return;
    const collapsed = ui.chatPanel.classList.toggle('collapsed');
    ui.chatToggle.textContent = collapsed ? '展开' : '收起';
    if (!collapsed) ui.chatInput.focus();
    if (!collapsed) setChatDot(false);          // 展开就算已读
    if (collapsed) toggleChatEmojiPanel(false);   // 收起聊天时顺手收掉表情面板
  }

  /* 世界频道有新消息时的小红点：只在聊天窗"收起"状态下亮，展开即视为已读 */
  function setChatDot(on) {
    if (!ui.chatDot) return false;
    ui.chatDot.classList.toggle('hidden', !on);
    return !!on;
  }
  function chatCollapsed() {
    return !!(ui.chatPanel && ui.chatPanel.classList.contains('collapsed'));
  }

  /* ---------- 聊天默认表情 ----------
   * 面板里的表情是固定的一份默认清单（分组），点一下插到输入框光标处（可以连着点几个、
   * 也可以和文字混着发）。表情本身是普通字符，服务器只过滤控制字符，所以能正常收发。 */
  const CHAT_EMOJI_GROUPS = [
    { name: '表情', list: ['😀', '😄', '😁', '😂', '🤣', '😊', '🙂', '😉', '😍', '🤩',
      '😘', '😜', '🤔', '🤨', '😐', '😴', '😪', '😭', '😱', '😡',
      '🥺', '😳', '🤯', '😇', '🙃', '😅', '😮', '🤤'] },
    { name: '手势', list: ['👍', '👎', '👌', '✌️', '🤝', '🙏', '👏', '🙌',
      '💪', '✊', '👋', '🤙', '🫡', '🤞'] },
    { name: '其他', list: ['❤️', '💔', '🔥', '✨', '⭐', '🎉', '💎', '🗡️',
      '🏹', '🛡️', '🧧', '💰', '🍖', '🍵', '🌙', '☀️', '⚡', '💀'] }
  ];
  let chatEmojiBuiltKey = '';   // 面板内容签名：激活状态变了就重建（多一个能发的表情）
  function buildChatEmojiPanel() {
    if (!ui.chatEmojiPanel) return;
    const sig = 'v1:' + CHAT_STICKERS.map(function (s) { return chatStickers[s.id] ? '1' : '0'; }).join('');
    if (ui.chatEmojiPanel.childElementCount && chatEmojiBuiltKey === sig) return;
    chatEmojiBuiltKey = sig;
    ui.chatEmojiPanel.textContent = '';
    /* ① 僵尸表情包：激活过的才点得动；没激活的锁着（点不动 = 也发不出去） */
    if (CHAT_STICKERS.length) {
      const st = document.createElement('span');
      st.className = 'emojiGroup';
      st.textContent = '僵尸表情（已激活 ' + stickerUnlockedCount() + '/' + CHAT_STICKERS.length + '）';
      ui.chatEmojiPanel.appendChild(st);
      CHAT_STICKERS.forEach(function (s) {
        const on = !!chatStickers[s.id];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'emojiItem sticker' + (on ? '' : ' locked');
        b.title = on
          ? ('发送「' + s.name + '」')
          : ('「' + s.name + '」还没激活——' + s.from + '，拿到后在背包里点「使用」');
        b.setAttribute('aria-label', s.name + (on ? '' : '（未激活）'));
        const img = document.createElement('img');
        img.className = 'emojiImg';
        img.alt = s.name;
        img.src = assetUrl(s.art);
        b.appendChild(img);
        if (!on) {
          const lock = document.createElement('span');
          lock.className = 'emojiLock';
          lock.textContent = '🔒';
          b.appendChild(lock);
          b.disabled = true;                    // 未激活：表情栏里发不出去
        } else {
          b.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            insertChatEmoji(s.token);
          });
        }
        ui.chatEmojiPanel.appendChild(b);
      });
    }
    CHAT_EMOJI_GROUPS.forEach(function (g) {
      if (!g.list || !g.list.length) return;
      const title = document.createElement('span');
      title.className = 'emojiGroup';
      title.textContent = g.name + '（' + g.list.length + '）';
      ui.chatEmojiPanel.appendChild(title);
      g.list.forEach(function (ch) {
        const b = document.createElement('button');
        b.type = 'button';                    // 别触发 form 提交
        b.className = 'emojiItem';
        b.textContent = ch;
        b.title = '插入表情 ' + ch;
        b.setAttribute('aria-label', ch);
        b.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          insertChatEmoji(ch);
        });
        ui.chatEmojiPanel.appendChild(b);
      });
    });
  }
  function insertChatEmoji(ch) {
    if (!ui.chatInput || !ch) return false;
    // 表情包 token：没激活的直接拒绝（表情栏里本来就点不到，这里再兜一层）
    const stk = stickerByToken(ch);
    if (stk && !stickerUnlocked(stk.id)) {
      showToast('「' + stk.name + '」还没激活，发不出去。');
      return false;
    }
    const el = ui.chatInput;
    const max = Number(el.getAttribute('maxlength')) || 100;
    const start = typeof el.selectionStart === 'number' ? el.selectionStart : el.value.length;
    const end = typeof el.selectionEnd === 'number' ? el.selectionEnd : el.value.length;
    el.value = (el.value.slice(0, start) + ch + el.value.slice(end)).slice(0, max);
    const pos = Math.min(max, start + ch.length);
    try { el.setSelectionRange(pos, pos); } catch (e) { /* 忽略 */ }
    el.focus();
    return true;
  }
  function toggleChatEmojiPanel(show) {
    if (!ui.chatEmojiPanel) return false;
    const on = show === undefined ? ui.chatEmojiPanel.classList.contains('hidden') : !!show;
    buildChatEmojiPanel();
    ui.chatEmojiPanel.classList.toggle('hidden', !on);
    if (ui.chatEmoji) ui.chatEmoji.classList.toggle('on', on);
    if (ui.chatPanel) ui.chatPanel.classList.toggle('music-open', on);   // 允许面板溢出显示
    return on;
  }

  // ---------- 聊天机器人（本地模拟在线玩家，非联网） ----------
  const CHAT_BOTS = [
    { name: '夜雨行舟', lines: ['有人组队吗？', '刚打完精英，掉了一件黄装。', '这词缀也太看运气了……', '气血反弹配荆棘好用吗？'] },
    { name: '一剑霜寒', lines: ['求带，我在半路被拦路者堵住了。', '刚突破到炼气期，感觉强了不少。', '商店刷新了一件好装备。'] },
    { name: '沙丘拾荒者', lines: ['易拉罐里居然能开出技能石，笑死。', '今天运气不错，捡了两块灵物。', '谁有多余的宠物蛋？'] },
    { name: '青竹小道友', lines: ['手动玩了两小时，才到武者三境。', '自动冒险真省心，挂机去了。', '传奇BOSS太猛了，被秒了。'] },
    { name: '剑胆琴心', lines: ['组队刷传奇地图，还差一个人。', '有人在吗？', '刚才被精英反伤打死了。'] }
  ];
  const CHAT_BOT_MIN_DELAY = 30;   // 最短 30 秒
  const CHAT_BOT_MAX_DELAY = 120;  // 最长 120 秒，且每次都会抖动，避免固定节奏
  const chatBotDelays = [];        // 调试用：最近几次机器人发言间隔（秒）
  let chatBotTimer = null;
  let chatBotLastLine = '';

  function randomChatBotDelay() {
    const base = CHAT_BOT_MIN_DELAY + Math.random() * (CHAT_BOT_MAX_DELAY - CHAT_BOT_MIN_DELAY);
    const jitter = (Math.random() - 0.5) * 12;
    return Math.max(15, base + jitter);
  }

  function pickBotLine(bot) {
    let line = bot.lines[randInt(0, bot.lines.length - 1)];
    let guard = 0;
    // 避免连续两次说同一句
    while (line === chatBotLastLine && guard < 6) {
      line = bot.lines[randInt(0, bot.lines.length - 1)];
      guard += 1;
    }
    return line;
  }

  function scheduleChatBot(delaySec) {
    if (chatBotTimer) clearTimeout(chatBotTimer);
    const delay = (delaySec || randomChatBotDelay()) * 1000;
    chatBotDelays.push(Math.round(delay / 1000));
    if (chatBotDelays.length > 20) chatBotDelays.shift();
    window.__chatBotDelays = chatBotDelays;   // 调试用：可查看实际随机间隔
    chatBotTimer = setTimeout(function () {
      chatBotTimer = null;
      if (document.hidden) {           // 页面切到后台就不冒泡
        scheduleChatBot();
        return;
      }
      // 已经连上真实世界频道了就不再插机器人发言（免得和真人聊天混在一起）
      if (net.chatState === 'online') {
        scheduleChatBot();
        return;
      }
      const bot = CHAT_BOTS[randInt(0, CHAT_BOTS.length - 1)];
      const line = pickBotLine(bot);
      chatBotLastLine = line;
      pushChatMessage(bot.name, line, 'bot');
      scheduleChatBot();
    }, delay);
  }

  // 玩家发言后有小概率被机器人接话（延迟也是随机的）
  function maybeBotReply() {
    if (Math.random() > 0.15) return;
    const bot = CHAT_BOTS[randInt(0, CHAT_BOTS.length - 1)];
    const delay = (2 + Math.random() * 7) * 1000;
    setTimeout(function () {
      const line = pickBotLine(bot);
      chatBotLastLine = line;
      pushChatMessage(bot.name, line, 'bot');
    }, delay);
  }

  // ---------- 图鉴（普通 / 魔法 / 稀有 / 传奇 / 暗金）& 抽卡 ----------
  const codexUnlocked = {};   // 图鉴 id -> true（激活后永久增幅）
  let gachaPity = 0;          // 距上次传奇 / 暗金的抽数
  let gachaCount = 0;         // 累计抽卡次数
  let gachaResultList = [];
  let codexTierFilter = 'all'; // 图鉴窗口当前查看的阶级

  function codexItemBonusText(item) {
    return Object.keys(item.bonus).map(function (k) {
      return (STAT_LABELS[k] || k) + ' +' + item.bonus[k];
    }).join('　');
  }

  function codexTotalBonus() {
    const total = {};
    CODEX_ITEMS.forEach(function (it) {
      if (!codexUnlocked[it.id]) return;
      Object.keys(it.bonus).forEach(function (k) {
        total[k] = (total[k] || 0) + it.bonus[k];
      });
    });
    return total;
  }

  function codexTotalBonusText() {
    const total = codexTotalBonus();
    const keys = Object.keys(total);
    if (!keys.length) return '尚未激活任何图鉴';
    return keys.map(function (k) {
      return (STAT_LABELS[k] || k) + ' +' + total[k];
    }).join('　');
  }

  function unlockCodex(id) {
    if (codexUnlocked[id]) return false;
    codexUnlocked[id] = true;
    recalcPlayerStats();
    return true;
  }

  function codexTierInfo(tierId) {
    return CODEX_TIERS.filter(function (t) { return t.id === tierId; })[0] || CODEX_TIERS[0];
  }

  // ---------- 图鉴悬停提示（显示该藏品的属性增幅）----------
  function hideCodexTip() {
    if (ui.codexTip) ui.codexTip.classList.add('hidden');
  }

  function showCodexTip(cell, item) {
    if (!ui.codexTip) return;
    const unlocked = !!codexUnlocked[item.id];
    const info = codexTierInfo(item.tier);
    const tip = ui.codexTip;

    tip.textContent = '';
    tip.style.borderColor = info.color;

    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = info.color;
    const name = document.createElement('b');
    name.textContent = unlocked ? item.name : '未收集';
    const tier = document.createElement('span');
    tier.textContent = info.name;
    tier.style.color = info.color;
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(tier);
    tip.appendChild(head);

    if (unlocked) {
      const mods = document.createElement('div');
      mods.className = 'tipMods';
      const title = document.createElement('em');
      title.textContent = '属性增幅';
      mods.appendChild(title);
      Object.keys(item.bonus).forEach(function (k) {
        const line = document.createElement('b');
        line.textContent = (STAT_LABELS[k] || k) + ' +' + item.bonus[k];
        mods.appendChild(line);
      });
      tip.appendChild(mods);

      const desc = document.createElement('div');
      desc.className = 'tipDesc';
      desc.textContent = item.type + ' · ' + item.desc;
      tip.appendChild(desc);
    } else {
      const mods = document.createElement('div');
      mods.className = 'tipMods locked';
      const line = document.createElement('b');
      line.textContent = info.gacha === false ? info.source : '遗迹探索 / 抽卡获得';
      mods.appendChild(line);
      tip.appendChild(mods);

      const desc = document.createElement('div');
      desc.className = 'tipDesc';
      desc.textContent = '收集后永久增幅角色属性';
      tip.appendChild(desc);
    }

    // 先显示再量尺寸，位置以 #wrap 为基准（保证不被窗口裁掉）
    tip.classList.remove('hidden');
    const wrapRect = (ui.wrap || document.body).getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = cellRect.left - wrapRect.left + cellRect.width / 2 - tw / 2;
    let top = cellRect.top - wrapRect.top - th - 8;
    if (top < 6) top = cellRect.bottom - wrapRect.top + 8; // 上方放不下 → 放到下方
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  // 每个阶级的收集进度：{ common: { got, total }, ... }
  function codexTierCounts() {
    const counts = {};
    CODEX_TIERS.forEach(function (t) { counts[t.id] = { got: 0, total: 0 }; });
    CODEX_ITEMS.forEach(function (it) {
      const c = counts[it.tier];
      if (!c) return;
      c.total += 1;
      if (codexUnlocked[it.id]) c.got += 1;
    });
    return counts;
  }

  // 图鉴窗口的「阶级入口」：普通 / 魔法 / 稀有 / 传奇 / 暗金（外加「全部」）
  function renderCodexTiers() {
    if (!ui.codexTiers) return;
    ui.codexTiers.textContent = '';
    const counts = codexTierCounts();
    const totalGot = CODEX_ITEMS.filter(function (it) { return codexUnlocked[it.id]; }).length;
    const entries = [{ id: 'all', name: '全部', color: '#ffd76a', note: '所有阶级的藏品' }]
      .concat(CODEX_TIERS);

    entries.forEach(function (t) {
      const isAll = t.id === 'all';
      const c = isAll ? { got: totalGot, total: CODEX_ITEMS.length } : counts[t.id];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'codexTab' + (codexTierFilter === t.id ? ' active' : '');
      btn.setAttribute('data-tier', t.id);
      btn.style.setProperty('--tierColor', t.color);

      const name = document.createElement('b');
      name.textContent = t.name;
      btn.appendChild(name);

      const prog = document.createElement('small');
      prog.textContent = c.got + ' / ' + c.total;
      btn.appendChild(prog);

      const rate = document.createElement('em');
      rate.textContent = isAll
        ? ('共 ' + CODEX_ITEMS.length + ' 件藏品')
        : (t.gacha === false
          ? t.source
          : ('单抽 ' + t.rate + '% · 十连 ' + t.rateTen + '%'));
      btn.appendChild(rate);

      btn.title = isAll ? '显示全部阶级' : (t.name + '：' + t.note);
      btn.addEventListener('click', function () {
        if (codexTierFilter === t.id) return;
        codexTierFilter = t.id;
        renderCodexGrid();
      });
      ui.codexTiers.appendChild(btn);
    });
  }

  function renderCodexGrid() {
    renderCodexTiers();
    ui.codexGrid.textContent = '';
    const list = CODEX_ITEMS.filter(function (it) {
      return codexTierFilter === 'all' || it.tier === codexTierFilter;
    });

    list.forEach(function (it) {
      const unlocked = !!codexUnlocked[it.id];
      const info = codexTierInfo(it.tier);
      const cell = document.createElement('div');
      cell.className = 'codexCell tier-' + it.tier + (unlocked ? ' unlocked' : ' locked');

      const icon = document.createElement('div');
      icon.className = 'codexIcon';
      icon.textContent = unlocked ? it.icon : '?';
      cell.appendChild(icon);

      const name = document.createElement('div');
      name.className = 'codexName';
      name.textContent = unlocked ? it.name : '未收集';
      cell.appendChild(name);

      const tier = document.createElement('div');
      tier.className = 'gcTier';
      tier.style.color = info.color;
      tier.textContent = info.name;
      cell.appendChild(tier);

      // 用自定义悬停窗口展示增幅，这里只留 aria-label（避免原生 tooltip 叠在上面）
      cell.setAttribute('aria-label', unlocked
        ? (it.name + '：' + codexItemBonusText(it) + '（' + it.desc + '）')
        : ('尚未收集 · ' + info.name + '（' +
          (info.gacha === false ? info.source : '遗迹探索 / 抽卡获得') + '）'));
      // 鼠标移上去显示属性增幅窗口，移开就关掉
      cell.addEventListener('mouseenter', function () { showCodexTip(cell, it); });
      cell.addEventListener('mouseleave', hideCodexTip);
      ui.codexGrid.appendChild(cell);
    });

    const got = CODEX_ITEMS.filter(function (it) { return codexUnlocked[it.id]; }).length;
    const counts = codexTierCounts();
    const view = codexTierFilter === 'all'
      ? ('全部 ' + got + ' / ' + CODEX_ITEMS.length)
      : (codexTierInfo(codexTierFilter).name + ' ' + counts[codexTierFilter].got +
        ' / ' + counts[codexTierFilter].total);
    ui.codexProgress.textContent = '已收集 ' + got + ' / ' + CODEX_ITEMS.length +
      '　·　当前查看：' + view + '（激活后永久增幅角色属性）';
    ui.codexBonusText.textContent = codexTotalBonusText();
  }

  function renderGacha() {
    ui.gachaGold.textContent = String(gold);
    ui.gachaPity.textContent = gachaPity + ' / ' + GACHA_PITY_MAX;
    ui.gachaCount.textContent = String(gachaCount);
    // 价格文案跟着常量走，改价不用再改 HTML
    if (ui.gachaOne) ui.gachaOne.textContent = '单抽 · ' + GACHA_SINGLE_COST;
    if (ui.gachaTen) ui.gachaTen.textContent = '十连 · ' + GACHA_TEN_COST;
    if (ui.gachaCostNote) {
      ui.gachaCostNote.textContent = '单抽 ' + GACHA_SINGLE_COST + ' 金币 · 十连 ' + GACHA_TEN_COST +
        ' 金币（十连限流：最多 ' + GACHA_TEN_CAPS.rare + ' 件稀有、' + GACHA_TEN_CAPS.legend +
        ' 件传奇，并保底一件稀有及以上；暗金不参与抽卡）';
    }
    renderGachaRates();
  }

  // 各阶级图鉴的获取几率一览（单抽 / 十连两套概率）
  function renderGachaRates() {
    if (!ui.gachaRates) return;
    ui.gachaRates.textContent = '';

    const head = document.createElement('div');
    head.className = 'rateRow rateHead';
    head.innerHTML = '<span>图鉴阶级</span><span>单抽</span><span>十连</span>';
    ui.gachaRates.appendChild(head);

    CODEX_TIERS.forEach(function (t) {
      const row = document.createElement('div');
      row.className = 'rateRow';
      const noGacha = t.gacha === false;

      const name = document.createElement('span');
      name.className = 'rateName';
      const dot = document.createElement('i');
      dot.style.background = t.color;
      const text = document.createElement('b');
      text.textContent = t.name;
      text.style.color = t.color;
      name.appendChild(dot);
      name.appendChild(text);
      row.appendChild(name);

      // 不参与抽卡与保底的阶级（暗金）：两列合并为一格说明
      if (noGacha) {
        const ev = document.createElement('span');
        ev.className = 'rateEvent';
        ev.textContent = t.source;
        row.appendChild(ev);
        row.title = t.name + '：' + t.note + '（不参与抽卡与保底）';
        ui.gachaRates.appendChild(row);
        return;
      }

      const one = document.createElement('span');
      one.className = 'rateVal';
      one.textContent = t.rate + '%';

      const ten = document.createElement('span');
      ten.className = 'rateVal' +
        (t.rateTen > t.rate ? ' up' : t.rateTen < t.rate ? ' down' : '');
      ten.textContent = t.rateTen + '%';

      row.appendChild(one);
      row.appendChild(ten);
      row.title = t.name + '：' + t.note +
        '（重复获得补偿 ' + t.comp + ' 金币）';
      ui.gachaRates.appendChild(row);
    });

    const foot = document.createElement('p');
    foot.className = 'rateFoot';
    foot.textContent =
      '十连每一抽的稀有以上几率更低（红色数值），但有数量上限：每次十连最多 ' +
      GACHA_TEN_CAPS.rare + ' 件稀有、最多 ' + GACHA_TEN_CAPS.legend + ' 件传奇及以上；' +
      '十连必得一件稀有及以上；累计 ' + GACHA_PITY_MAX + ' 抽未出传奇时，下一抽必出传奇；' +
      '暗金图鉴不参与抽卡与保底，只能通过活动等方式获得（暂未开放）。';
    ui.gachaRates.appendChild(foot);
  }

  function renderGachaResults(withRevealAnim) {
    ui.gachaResults.textContent = '';
    // 单抽只有 1 张卡时，让卡片在结果区里上下左右居中
    ui.gachaResults.classList.toggle('single', gachaResultList.length === 1);
    gachaResultList.forEach(function (r, idx) {
      const card = document.createElement('div');
      card.className = 'gachaCard';
      card.style.borderColor = r.color;
      if (r.tier === 'god') card.style.boxShadow = '0 0 14px rgba(255,200,80,.55)';
      // 抽卡动画播完后揭晓：卡片逐张翻出来
      if (withRevealAnim) {
        card.classList.add('reveal');
        card.style.animationDelay = (idx * 55) + 'ms';
      }

      const tier = document.createElement('div');
      tier.className = 'gcTier';
      tier.style.color = r.color;
      tier.textContent = r.tierName;
      card.appendChild(tier);

      const icon = document.createElement('div');
      icon.className = 'codexIcon';
      icon.textContent = r.icon;
      card.appendChild(icon);

      const name = document.createElement('div');
      name.className = 'gcName';
      name.textContent = r.name;
      card.appendChild(name);

      if (r.isNew) {
        const nw = document.createElement('div');
        nw.className = 'gcNew';
        nw.textContent = 'NEW · 图鉴激活';
        card.appendChild(nw);
      } else if (r.note) {
        const nt = document.createElement('div');
        nt.className = 'gcNew';
        nt.textContent = r.note;
        card.appendChild(nt);
      }
      card.title = r.detail || '';
      ui.gachaResults.appendChild(card);
    });
  }

  // 抽到某个阶级的图鉴（优先给还没收集的）；tierId 由调用方决定
  function rollGachaOnce(tierId) {
    if (!tierId) tierId = rollGachaTier(false);
    const info = codexTierInfo(tierId);
    const pool = CODEX_ITEMS.filter(function (it) { return it.tier === tierId; });
    const locked = pool.filter(function (it) { return !codexUnlocked[it.id]; });
    const list = locked.length ? locked : pool;
    const pick = list[randInt(0, list.length - 1)];
    const isNew = unlockCodex(pick.id);
    let note = '';
    if (!isNew) {
      gold += info.comp;
      updateGold();
      note = '重复 +' + info.comp + ' 金币';
    }
    return {
      tier: tierId, tierName: info.name, color: info.color,
      name: pick.name, icon: pick.icon, isNew: isNew, note: note,
      detail: isNew ? ('图鉴激活：' + codexItemBonusText(pick)) : note
    };
  }

  // 抽卡阶级：只从参与抽卡的阶级里摇（暗金 gacha:false，永远不会被摇到）
  function rollGachaTier(tenPull) {
    const r = Math.random() * 100;
    let acc = 0;
    for (let i = 0; i < CODEX_TIERS.length; i++) {
      const t = CODEX_TIERS[i];
      if (t.gacha === false || (!tenPull ? t.rate <= 0 : t.rateTen <= 0)) continue;
      acc += tenPull ? t.rateTen : t.rate;
      if (r < acc) return t.id;
    }
    return CODEX_TIERS.filter(function (t) { return t.gacha !== false; })[0].id;
  }

  // 30 抽保底：必出传奇（暗金不参与保底，只能靠活动等其它方式获得）
  function rollPityTier() {
    return 'legend';
  }

  // 十连限流：稀有最多 GACHA_TEN_CAPS.rare 件，传奇 + 暗金 合计最多 GACHA_TEN_CAPS.legend 件
  function clampTierForTen(tierId, used) {
    if (tierRank(tierId) >= tierRank('legend')) {
      if (used.legend < GACHA_TEN_CAPS.legend) return tierId;
      tierId = 'rare'; // 传奇 / 暗金名额用完 → 降级为稀有
    }
    if (tierId === 'rare') {
      if (used.rare < GACHA_TEN_CAPS.rare) return 'rare';
      return 'magic'; // 稀有也满了 → 降级为魔法
    }
    return tierId;
  }

  function countTierUse(tierId, used) {
    if (tierRank(tierId) >= tierRank('legend')) used.legend += 1;
    else if (tierId === 'rare') used.rare += 1;
  }

  function tierRank(tierId) {
    for (let i = 0; i < CODEX_TIERS.length; i++) {
      if (CODEX_TIERS[i].id === tierId) return i;
    }
    return 0;
  }

  /* ============================================================
   * 抽卡动画
   *   点单抽 / 十连后：先把整个面板盖住播一段动画（卡背浮动 + 光晕 + 转光），
   *   动画播完才把抽到的图鉴卡片揭晓出来（逐张翻出）。
   *   动画期间锁定两个抽卡按钮，避免连点重复扣钱。
   * ============================================================ */
  const GACHA_ANIM_MS_SINGLE = 1200;
  const GACHA_ANIM_MS_TEN = 1600;
  let gachaAnim = null;          // { timer, playing }

  function gachaAnimPlaying() {
    return !!(gachaAnim && gachaAnim.playing);
  }

  function playGachaAnimation(results, onDone) {
    if (!ui.gachaStage) {
      if (onDone) onDone();      // 没有动画容器就直接出结果，别把东西吞了
      return;
    }
    // 光晕颜色取本次最高品级
    let best = results[0];
    results.forEach(function (r) { if (tierRank(r.tier) > tierRank(best.tier)) best = r; });
    if (ui.gachaHalo) ui.gachaHalo.style.color = best ? best.color : '#ffd08a';

    if (ui.gachaStageCards) ui.gachaStageCards.textContent = '';
    const ten = results.length > 1;
    void ten;
    /* 加载界面（需求）：一张居中的竖版鎏金卡牌 + 浮雕「道」+ 卡牌底部小字，
     * 不再按结果数量铺一排卡背。tip 文案固定成"正在加载天道之力..."。 */
    if (ui.gachaStageTip) ui.gachaStageTip.textContent = '正在加载天道之力...';
    ui.gachaStage.classList.remove('hidden');
    ui.gachaResults.textContent = '';      // 动画期间结果区保持空白
    if (ui.gachaOne) ui.gachaOne.disabled = true;
    if (ui.gachaTen) ui.gachaTen.disabled = true;

    gachaAnim = { playing: true, timer: null };
    const ms = ten ? GACHA_ANIM_MS_TEN : GACHA_ANIM_MS_SINGLE;
    gachaAnim.timer = setTimeout(function () {
      finishGachaAnimation(onDone);
    }, ms);
  }

  // 收尾：收起动画、解锁按钮、揭晓图鉴（可以在动画中途被关闭窗口调用）
  function finishGachaAnimation(onDone) {
    if (!gachaAnim) return;
    if (gachaAnim.timer) clearTimeout(gachaAnim.timer);
    gachaAnim = null;
    if (ui.gachaStage) ui.gachaStage.classList.add('hidden');
    if (ui.gachaStageCards) ui.gachaStageCards.textContent = '';
    if (ui.gachaOne) ui.gachaOne.disabled = false;
    if (ui.gachaTen) ui.gachaTen.disabled = false;
    if (onDone) onDone();
  }

  function doGacha(times) {
    const ten = times >= 10;
    if (!ten) playSfx('gachaSingle');
    const cost = ten ? GACHA_TEN_COST : GACHA_SINGLE_COST;
    if (gold < cost) {
      showToast('金币不足，抽卡需要 ' + cost + ' 金币。');
      return;
    }
    dailyQuestTick('gacha');     // 每日任务：进行一次抽卡（单抽 / 十连都算）
    gold -= cost;
    updateGold();

    const results = [];
    let rarePlus = 0;
    const usedTiers = { rare: 0, legend: 0 }; // 十连限流计数
    for (let i = 0; i < times; i++) {
      gachaCount += 1;
      gachaPity += 1;
      const forced = gachaPity >= GACHA_PITY_MAX ? rollPityTier() : null;
      let tierId = forced || rollGachaTier(ten);
      // 十连限流只作用于自然抽取，保底（30 抽必出传奇/暗金）不受限制
      if (ten && !forced) tierId = clampTierForTen(tierId, usedTiers);
      countTierUse(tierId, usedTiers);
      const res = rollGachaOnce(tierId);
      if (tierRank(res.tier) >= tierRank('legend')) gachaPity = 0;
      if (tierRank(res.tier) >= tierRank(GACHA_TEN_MIN_TIER)) rarePlus += 1;
      results.push(res);
    }

    // 十连保底：至少一件稀有及以上
    if (ten && rarePlus === 0) {
      const res = rollGachaOnce('rare');
      usedTiers.rare += 1;
      res.tierName = res.tierName + '（保底）';
      results[results.length - 1] = {
        tier: res.tier, tierName: res.tierName, color: res.color,
        name: res.name, icon: res.icon, isNew: res.isNew, note: res.note,
        detail: res.detail
      };
    }

    gachaResultList = results;
    renderGacha();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    saveGame();
    const newCount = results.filter(function (r) { return r.isNew; }).length;
    // 先播动画，动画播完才揭晓图鉴；提示也放到揭晓之后
    playGachaAnimation(results, function () {
      renderGachaResults(true);
      showToast(newCount
        ? ('抽卡完成：新激活 ' + newCount + ' 件图鉴！')
        : '抽卡完成（都是重复藏品，已折算金币）。');
    });
  }

  // 图鉴窗口：只显示图鉴内容
  function openCodex() {
    closeGacha(); // 两个模块互不共存：打开图鉴时收起抽卡窗口
    renderCodexGrid();
    ui.codexPopup.classList.remove('hidden');
  }

  function closeCodex() {
    hideCodexTip();
    ui.codexPopup.classList.add('hidden');
  }

  // 抽卡窗口：只显示抽卡内容
  function openGacha() {
    closeCodex(); // 两个模块互不共存：打开抽卡时收起图鉴窗口
    renderGacha();
    renderGachaResults();
    ui.gachaPopup.classList.remove('hidden');
  }

  function closeGacha() {
    // 动画播到一半就关窗口：直接跳到结果，别把已经抽到的图鉴吞了
    if (gachaAnimPlaying()) finishGachaAnimation(function () { renderGachaResults(false); });
    ui.gachaPopup.classList.add('hidden');
  }

  /* ============================================================
   * 副本（入侵）· 每日轮换
   *   一周 7 天，每天开放 1 个副本，全是同一个 00:00（东八区）整点轮换，
   *   7 个副本各占一天 —— 不重复、不冲突。
   *   进入模式：
   *     · 每天前 DUNGEON_DOUBLE_TRIES 次进入 = 双倍场（掉落 / 经验 / 金币 ×2）；
   *     · 双倍次数用完**不封进入次数**（不再写死"每天只能进 3 次"），
   *       改为每次进入消耗 DUNGEON_CONTRIB_COST 点门派贡献。
   *   进入后是一套"连打"的小关卡（复用洞府关卡的推进方式）：敌人按这一天的
   *   入侵名单刷，击杀有几率掉这一天的入侵材料，通关再结算金币 / 装备 / 特殊物品。
   * ============================================================ */
  const DUNGEON_WEEKDAYS = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
  const DUNGEON_DOUBLE_TRIES = 3;      // 每天前 3 次是双倍场（次日 00:00 重置）
  const DUNGEON_CONTRIB_COST = 50;     // 双倍用完后，每次进入消耗的门派贡献
  const DUNGEON_DAILY_TRIES = DUNGEON_DOUBLE_TRIES;   // 兼容旧名字（= 双倍次数）
  const INVASION_DROP_CHANCE = 0.35;   // 入侵副本里，击杀敌人掉专属入侵材料的几率
  const ABYSS_GIFT_DROP_CHANCE = 0.22; // 深渊入侵：邪恶法师掉「深渊战利品」的几率（双倍场掷两次）
  let dungeonTestOpenOverride = null;  // 测试常开名单的运行时覆盖（null = 用 defs.js 里的配置）
  function dungeonTestOpenIds() { return dungeonTestOpenOverride || DUNGEON_TEST_OPEN; }
  function dungeonIsTestOpen(id) { return dungeonTestOpenIds().indexOf(id) >= 0; }
  /* 今天能不能进这个副本：排期轮到它，或者它在"测试常开"名单里 */
  function dungeonEnterableNow(def, ms) {
    if (!def) return false;
    const now = (typeof ms === 'number') ? ms : wbNow();
    return dungeonByWeekday(dungeonWeekday(now)) === def || dungeonIsTestOpen(def.id);
  }
  // 当天的挑战次数：跨天自动清零，写进档案（换设备 / 刷新都按服务器时间继续算）
  let dungeonRun = { day: '', used: 0 };

  // 把"当天"对齐到东八区；跨天就把已用次数清零
  function dungeonSyncDay(ms) {
    const day = shanghaiDayKey(ms == null ? wbNow() : ms);
    if (dungeonRun.day !== day) {
      dungeonRun.day = day;
      dungeonRun.used = 0;
    }
    return day;
  }
  // 今日还剩几次"双倍场"
  function dungeonTriesLeft(ms) {
    dungeonSyncDay(ms);
    return Math.max(0, DUNGEON_DOUBLE_TRIES - dungeonRun.used);
  }
  function dungeonDoubleNow(ms) { return dungeonTriesLeft(ms) > 0; }
  /* 这次进入要不要花钱：前 3 次免费（双倍场）；之后每次 50 点门派贡献。
   * 返回 null = 可以进；返回字符串 = 不能进的原因。 */
  function dungeonEntryCostReason(ms) {
    const now = (typeof ms === 'number') ? ms : wbNow();
    if (dungeonDoubleNow(now)) return null;
    if (!sect.id) {
      return '今日双倍次数已用完：再进副本需要先加入门派，并消耗 ' +
        DUNGEON_CONTRIB_COST + ' 点门派贡献。';
    }
    if (Math.max(0, sect.contribution | 0) < DUNGEON_CONTRIB_COST) {
      return '今日双倍次数已用完：本次进入需要 ' + DUNGEON_CONTRIB_COST +
        ' 点门派贡献（当前 ' + Math.max(0, sect.contribution | 0) + ' 点）';
    }
    return null;
  }
  /* 副本里的奖励倍率：这一场是"双倍场"就 ×2（掉落 / 经验 / 金币） */
  function dungeonRewardMul() {
    return (cave && cave.def && cave.def.invasionDouble) ? 2 : 1;
  }
  /* 当前这场副本是哪一天的入侵（不在入侵副本里就是 null） */
  function currentInvasionDef() {
    return (cave && cave.def && cave.def.invasion) ? cave.def.invasion : null;
  }
  /* 这一天的入侵对应哪种镶嵌材料（裂隙碎片 / 灾变残骸 / …） */
  function invasionMaterialOf(dungeonDef) {
    if (!dungeonDef) return null;
    return INVASION_MATERIALS.filter(function (m) {
      return m.id === dungeonDef.materialId;
    })[0] || null;
  }
  /* 消耗 1 次挑战机会（老的调试钩子还在用；用完返回 false） */
  function useDungeonTry() {
    if (dungeonTriesLeft() <= 0) return false;
    dungeonRun.used += 1;
    markSaveDirty();
    return true;
  }

  // 东八区星期：JS 的 0=周日，转成 1=周一 … 7=周日
  function dungeonWeekday(ms) {
    const wd = shanghaiParts(ms).wd;
    return wd === 0 ? 7 : wd;
  }
  function dungeonByWeekday(wd) {
    return DUNGEONS.filter(function (d) { return d.weekday === wd; })[0] || null;
  }
  // 下一次轮换时刻：下一个 00:00（东八区）
  function dungeonNextRotateAt(ms) {
    return shanghaiDayStart(ms) + 86400000;
  }
  function dungeonCountdownText(ms) {
    const left = Math.max(0, dungeonNextRotateAt(ms) - ms);
    const h = Math.floor(left / 3600000);
    const mi = Math.floor((left % 3600000) / 60000);
    const s = Math.floor((left % 60000) / 1000);
    return '距离轮换 ' + pad2(h) + ':' + pad2(mi) + ':' + pad2(s);
  }

  /* ---------- 进入今天的入侵副本 ----------
   *   opts.free = true 时是调试直进：不收费、不计次数、也不是双倍场（老用例照旧）。 */
  function enterInvasionDungeon(def, opts) {
    const free = !!(opts && opts.free);
    const now = wbNow();
    const target = def || dungeonByWeekday(dungeonWeekday(now));
    if (!target) { showToast('今天没有开放的副本。'); return false; }
    if (cave) {
      showToast(cave.def.activity ? '先离开当前的临时副本，再进「' + target.name + '」。'
        : '先离开当前的' + (cave.def.invasion ? '副本' : '洞府') + '，再进「' + target.name + '」。');
      return false;
    }
    if (gameState !== 'walk' && gameState !== 'title') {
      showToast('战斗 / 结算中，先处理完再进副本。');
      return false;
    }
    if (!free && !dungeonEnterableNow(target, now)) {
      showToast('「' + target.name + '」不在今天开放，今天开的是「' +
        (dungeonByWeekday(dungeonWeekday(now)) || {}).name + '」。');
      return false;
    }
    /* "测试常开"的副本（defs.js 的 DUNGEON_TEST_OPEN）：验证期间免消耗（不扣贡献） */
    const testOpen = !free && dungeonIsTestOpen(target.id);
    const doubleThis = !free && dungeonDoubleNow(now);
    if (!free && !testOpen) {
      const why = dungeonEntryCostReason(now);
      if (why) { showToast(why); return false; }
    }
    const caveDef = {
      id: target.id, name: target.name, color: target.accent,
      glow: 'rgba(255, 215, 106, .5)',
      enemies: target.enemies || 12,
      token: false, weight: 0,
      note: target.reward,
      tiers: target.tiers || ['common', 'common', 'magic'],
      rewardTier: target.rewardTier || 'green',
      sprites: target.sprites || null,             // 普通 / 魔法杂兵用哪些模型
      eliteSprites: target.eliteSprites || null,   // 精英（稀有）那一档用哪些模型
      boss: target.boss || null,                   // 关卡BOSS：清到剩最后一只出场
      bossTitle: target.bossTitle || '',           // BOSS 名号（例：深渊之主·邪恶法师）
      bossTier: target.bossTier || 'elite',
      scene: target.scene || null,                 // 专属环境场景 key（迷雾副本 = 'fog'）
      loot: target.loot || null,         // 掉落物清单 key（深渊 = 'abyss'，迷雾 = 'fog'）
      invasion: target,                 // 标记：这是"每日副本"，结算 / 掉落都看它
      invasionDouble: doubleThis
    };
    if (autoAdventure) stopAutoAdventure('副本内需要手动探索，已退出自动冒险。');
    input.moving = false;
    syncMoveButton();
    closeDungeonPanel();
    cave = { def: caveDef, remaining: caveDef.enemies, total: caveDef.enemies, killed: 0, entrance: null };
    activeEnemy = null;
    battleGroup.length = 0;
    /* 记账：今天已进第几次 + 收费（前 3 次免费双倍，之后扣门派贡献；测试常开不收费） */
    if (!free) {
      dungeonSyncDay(now);
      dungeonRun.used += 1;
      if (!doubleThis && !testOpen) {
        sect.contribution = Math.max(0, Math.floor(sect.contribution || 0) - DUNGEON_CONTRIB_COST);
        updateSectUI();
        if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
        if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
      }
      saveGame();
    }
    addBattleLogSafe('进入每日副本「' + target.name + '」：共 ' + caveDef.enemies + ' 个敌人，需要手动探索。' +
      (free ? '（调试直进）' : (testOpen
        ? '（测试常开：免消耗）' + (doubleThis ? '，本次是双倍场（掉落 / 经验 / 金币 ×2）' : '')
        : (doubleThis
        ? '本次是双倍场：掉落 / 经验 / 金币 ×2（今日还剩 ' + dungeonTriesLeft(now) + ' 次双倍）。'
        : '本次消耗 ' + DUNGEON_CONTRIB_COST + ' 点门派贡献（今日双倍次数已用完，当前贡献 ' +
          Math.max(0, Math.floor(sect.contribution || 0)) + '）。'))), 'reward');
    updateCaveBar();
    spawnCaveEnemy();
    showToast('进入「' + target.name + '」（敌人 ' + caveDef.enemies + ' 个）' +
      (free ? '' : (testOpen ? '　·　测试常开（免消耗）' + (doubleThis ? '　·　双倍场' : '')
        : (doubleThis ? '　·　双倍场：掉落 / 经验 / 金币 ×2' : '　·　门派贡献 -' + DUNGEON_CONTRIB_COST))) +
      '。点「移动」前进，随时可点「离开洞府」出来。');
    return true;
  }

  let dungeonRenderedDay = '';

  function dungeonCardEl(def, isToday) {
    const card = document.createElement('div');
    card.className = 'dungeonCard' + (isToday ? ' today' : '');
    if (isToday) card.style.borderColor = def.accent;

    const head = document.createElement('div');
    head.className = 'dcHead';
    const day = document.createElement('span');
    day.className = 'dcDay';
    day.textContent = DUNGEON_WEEKDAYS[def.weekday - 1];
    const name = document.createElement('b');
    name.className = 'dcName';
    name.textContent = def.name;
    name.style.color = def.accent;
    head.appendChild(day);
    head.appendChild(name);
    if (isToday) {
      const tag = document.createElement('span');
      tag.className = 'dcTodayTag';
      tag.textContent = '今日开放';
      head.appendChild(tag);
    } else if (dungeonIsTestOpen(def.id)) {
      const tag = document.createElement('span');
      // 用自己的 class：别和"今日开放"那个标签混在一起（测试 / 文案都要能分辨）
      tag.className = 'dcTestTag';
      tag.textContent = '测试 · 常开';
      head.appendChild(tag);
    }
    card.appendChild(head);

    const reward = document.createElement('div');
    reward.className = 'dcReward';
    reward.textContent = '核心产出：' + def.reward;
    card.appendChild(reward);

    const ul = document.createElement('ul');
    ul.className = 'dcLines';
    def.lines.forEach(function (t) {
      const li = document.createElement('li');
      li.textContent = t;
      ul.appendChild(li);
    });
    card.appendChild(ul);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'dcEnter';
    if (!isToday && !dungeonIsTestOpen(def.id)) {
      btn.textContent = '未到开放日';
      btn.disabled = true;
      btn.title = DUNGEON_WEEKDAYS[def.weekday - 1] + ' 才开放（当天 00:00 东八区轮换）';
    } else {
      const left = dungeonTriesLeft();
      if (dungeonIsTestOpen(def.id) && def.weekday !== dungeonWeekday()) {
        // 测试常开的副本：不限星期、免消耗
        btn.textContent = left > 0
          ? ('进入副本 · 测试常开（双倍 ' + left + '/' + DUNGEON_DOUBLE_TRIES + '）')
          : '进入副本 · 测试常开';
        btn.title = '测试常开：不限星期、免消耗（不扣门派贡献）';
      } else {
        btn.textContent = left > 0
          ? '进入副本 · 双倍 ' + left + '/' + DUNGEON_DOUBLE_TRIES
          : '进入副本 · 贡献 -' + DUNGEON_CONTRIB_COST;
        btn.title = left > 0
          ? '今日前 ' + DUNGEON_DOUBLE_TRIES + ' 次是双倍场：掉落 / 经验 / 金币 ×2'
          : '今日双倍次数已用完：每次进入消耗 ' + DUNGEON_CONTRIB_COST + ' 点门派贡献（不限次数）';
      }
      btn.addEventListener('click', function () { enterInvasionDungeon(def); });
    }
    card.appendChild(btn);
    return card;
  }

  function renderDungeonPanel() {
    const now = wbNow();
    const before = dungeonRun.day;
    dungeonSyncDay(now);
    // 跨天（挑战次数清零）顺手落一次盘，别把"昨天的已用次数"留在服务器上
    if (before && before !== dungeonRun.day) markSaveDirty();
    dungeonRenderedDay = shanghaiDayKey(now);
    const wd = dungeonWeekday(now);
    const today = dungeonByWeekday(wd);

    if (ui.dungeonCountdown) ui.dungeonCountdown.textContent = dungeonCountdownText(now);

    // ① 今日开放：单独一大块，最醒目
    if (ui.dungeonToday) {
      ui.dungeonToday.textContent = '';
      if (today) {
        const label = document.createElement('div');
        label.className = 'dtLabel';
        label.textContent = '今日开放 · ' + DUNGEON_WEEKDAYS[today.weekday - 1] + '（东八区上海时间）';

        const name = document.createElement('div');
        name.className = 'dtName';
        name.textContent = today.name;
        name.style.color = today.accent;

        const reward = document.createElement('div');
        reward.className = 'dtReward';
        reward.textContent = '核心产出：' + today.reward;

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dtEnter';
        const left = dungeonTriesLeft(now);
        const costWhy = dungeonEntryCostReason(now);
        if (costWhy) {
          btn.textContent = '贡献不足';
          btn.disabled = true;
        } else {
          btn.textContent = left > 0
            ? '进入副本 · 双倍 ' + left + '/' + DUNGEON_DOUBLE_TRIES
            : '进入副本 · 贡献 -' + DUNGEON_CONTRIB_COST;
          btn.addEventListener('click', function () { enterInvasionDungeon(today); });
        }
        btn.title = left > 0
          ? '今日前 ' + DUNGEON_DOUBLE_TRIES + ' 次是双倍场：掉落 / 经验 / 金币 ×2'
          : '今日双倍次数已用完：每次进入消耗 ' + DUNGEON_CONTRIB_COST + ' 点门派贡献（不限次数）';
        btn.style.borderColor = today.accent;

        // 今日双倍次数：3 个点，用了就灭
        const tries = document.createElement('div');
        tries.className = 'dtTries';
        const triesText = document.createElement('span');
        triesText.textContent = '今日双倍 ' + left + ' / ' + DUNGEON_DOUBLE_TRIES +
          '（之后每次 ' + DUNGEON_CONTRIB_COST + ' 点门派贡献）';
        const pips = document.createElement('span');
        pips.className = 'dtPips';
        for (let i = 0; i < DUNGEON_DOUBLE_TRIES; i++) {
          const dot = document.createElement('i');
          dot.className = i < left ? 'on' : 'off';
          pips.appendChild(dot);
        }
        tries.appendChild(triesText);
        tries.appendChild(pips);
        tries.title = '每天前 ' + DUNGEON_DOUBLE_TRIES + ' 次进入是双倍场（掉落 / 经验 / 金币 ×2）；' +
          '用完后照样能进，不再限制进入次数，改为每次消耗 ' + DUNGEON_CONTRIB_COST +
          ' 点门派贡献。当天 00:00（东八区）重置。';

        /* 状态提示：双倍还剩几次 / 该花多少贡献 */
        const cost = document.createElement('div');
        cost.className = 'dtCost';
        cost.textContent = left > 0
          ? ('本次双倍：掉落 / 经验 / 金币 ×2　·　今日已进入 ' + dungeonRun.used + ' 次')
          : (costWhy || ('本次消耗 ' + DUNGEON_CONTRIB_COST + ' 点门派贡献（当前贡献 ' +
            Math.max(0, sect.contribution | 0) + '）　·　今日已进入 ' + dungeonRun.used + ' 次'));
        cost.style.color = left > 0 ? '#ffd76a' : '#ff9a7a';

        ui.dungeonToday.appendChild(label);
        ui.dungeonToday.appendChild(name);
        ui.dungeonToday.appendChild(reward);
        ui.dungeonToday.appendChild(tries);
        ui.dungeonToday.appendChild(cost);
        ui.dungeonToday.appendChild(btn);
      }
    }

    /* ①' 测试常开的副本（defs.js 的 DUNGEON_TEST_OPEN）：不按星期，随时能进。
     *     验收完把名单清空，这一段就不会再出现。 */
    if (ui.dungeonTestOpen) {
      ui.dungeonTestOpen.textContent = '';
      const testList = DUNGEONS.filter(function (d) { return dungeonIsTestOpen(d.id); });
      ui.dungeonTestOpen.classList.toggle('hidden', !testList.length);
      testList.forEach(function (def) {
        const row = document.createElement('div');
        row.className = 'dtTestRow';
        row.setAttribute('data-dungeon', def.id);
        const left = dungeonTriesLeft(now);
        const head = document.createElement('div');
        head.className = 'dtTestHead';
        const name = document.createElement('b');
        name.textContent = def.name;
        name.style.color = def.accent;
        const tag = document.createElement('span');
        tag.className = 'dcTestTag';
        tag.textContent = '测试 · 常开';
        head.appendChild(name);
        head.appendChild(tag);
        const info = document.createElement('span');
        info.className = 'dtTestInfo';
        // 测试常开：不限星期、不扣贡献（方便验收）；前 3 次仍然是双倍场
        info.textContent = '测试常开 · 免消耗' + (left > 0
          ? ('　·　今日双倍剩 ' + left + '/' + DUNGEON_DOUBLE_TRIES)
          : '　·　今日双倍已用完（掉落按普通档）');
        info.style.color = left > 0 ? '#ffd76a' : '#d9c7ff';
        head.appendChild(info);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dtEnter';
        btn.textContent = left > 0
          ? ('进入副本 · 测试常开（双倍 ' + left + '/' + DUNGEON_DOUBLE_TRIES + '）')
          : '进入副本 · 测试常开';
        btn.title = '测试常开：不限星期、免消耗（不扣门派贡献）';
        btn.addEventListener('click', function () { enterInvasionDungeon(def); });
        btn.style.borderColor = def.accent;
        row.appendChild(head);
        row.appendChild(btn);
        ui.dungeonTestOpen.appendChild(row);
      });
    }

    // ② 本周排期：周一到周日 7 个格子，今天高亮
    if (ui.dungeonWeek) {
      ui.dungeonWeek.textContent = '';
      DUNGEON_WEEKDAYS.forEach(function (dayName, i) {
        const wdI = i + 1;
        const def = dungeonByWeekday(wdI);
        const chip = document.createElement('div');
        chip.className = 'dwChip' + (wdI === wd ? ' today' : '');
        if (def) chip.title = dayName + ' · ' + def.name;
        const d = document.createElement('span');
        d.className = 'dwDay';
        d.textContent = dayName;
        const n = document.createElement('span');
        n.className = 'dwName';
        n.textContent = def ? def.name : '—';
        if (def) n.style.color = def.accent;
        chip.appendChild(d);
        chip.appendChild(n);
        ui.dungeonWeek.appendChild(chip);
      });
    }

    // ③ 7 个副本的完整说明
    if (ui.dungeonList) {
      ui.dungeonList.textContent = '';
      DUNGEONS.slice().sort(function (a, b) { return a.weekday - b.weekday; }).forEach(function (def) {
        ui.dungeonList.appendChild(dungeonCardEl(def, def.weekday === wd));
      });
    }
  }

  function openDungeonPanel() {
    closeCodex();     // 和图鉴 / 抽卡互不共存，避免叠在一起
    closeGacha();
    renderDungeonPanel();
    ui.dungeonPopup.classList.remove('hidden');
  }

  function closeDungeonPanel() {
    ui.dungeonPopup.classList.add('hidden');
  }

  // 每秒刷新倒计时；跨天时整块重建（"今日开放"要跟着换）
  let dungeonClockAcc = 0;
  function updateDungeonClock(dt) {
    if (!ui.dungeonPopup || ui.dungeonPopup.classList.contains('hidden')) return;
    dungeonClockAcc += dt;
    if (dungeonClockAcc < 1) return;
    dungeonClockAcc = 0;
    const now = wbNow();
    if (shanghaiDayKey(now) !== dungeonRenderedDay) { renderDungeonPanel(); return; }
    if (ui.dungeonCountdown) ui.dungeonCountdown.textContent = dungeonCountdownText(now);
  }

  // 调试用：副本状态（今天是哪个、剩余次数、还有多久轮换）
  window.__debugDungeonState = function () {
    const now = wbNow();
    const wd = dungeonWeekday(now);
    const def = dungeonByWeekday(wd);
    return {
      day: dungeonSyncDay(now),
      weekday: wd,
      weekdayName: DUNGEON_WEEKDAYS[wd - 1],
      dungeon: def ? def.name : null,
      dungeonId: def ? def.id : null,
      triesMax: DUNGEON_DOUBLE_TRIES,
      triesLeft: dungeonTriesLeft(now),
      used: dungeonRun.used,
      doubleLeft: dungeonTriesLeft(now),
      doubleMax: DUNGEON_DOUBLE_TRIES,
      contribCost: DUNGEON_CONTRIB_COST,
      contribution: Math.max(0, sect.contribution | 0),
      hasSect: !!sect.id,
      costWhy: dungeonEntryCostReason(now),
      rewardMul: dungeonRewardMul(),
      testOpen: dungeonTestOpenIds().slice(),
      todayEnterable: def ? dungeonEnterableNow(def, now) : false,
      inside: cave && cave.def && cave.def.invasion
        ? { id: cave.def.invasion.id, name: cave.def.name, double: !!cave.def.invasionDouble,
            scene: cave.def.scene || null,
            remaining: cave.remaining, total: cave.total, killed: cave.killed }
        : null,
      nextRotateInSec: Math.max(0, Math.round((dungeonNextRotateAt(now) - now) / 1000))
    };
  };
  /* 调试/测试用：进今天的入侵副本。
   *   不传 opts（或 opts.gate 不是 true）= 调试直进：不收费、不计次数、不是双倍场；
   *   传 { gate: true } = 走真实规则（双倍次数 / 门派贡献 / 次数累加）。 */
  window.__debugDungeonEnter = function (id, opts) {
    const def = id ? DUNGEONS.filter(function (d) { return d.id === id; })[0] : null;
    const free = !(opts && opts.gate);
    return enterInvasionDungeon(def || undefined, free ? { free: true } : null);
  };
  // 调试/测试用：把"今日已进入次数"设成 n（用来验证"双倍用完 → 消耗门派贡献"）
  window.__debugDungeonSetUsed = function (n) {
    dungeonSyncDay(wbNow());
    dungeonRun.used = Math.max(0, Math.floor(Number(n) || 0));
    saveGame();
    if (ui.dungeonPopup && !ui.dungeonPopup.classList.contains('hidden')) renderDungeonPanel();
    return window.__debugDungeonState();
  };
  /* 调试/测试用：运行时开关某个副本的「测试常开」（不改代码也能恢复正式排期） */
  window.__debugDungeonTestOpen = function (id, on) {
    const cur = dungeonTestOpenIds().slice();
    const idx = cur.indexOf(id);
    if (on === false) { if (idx >= 0) cur.splice(idx, 1); }
    else if (idx < 0) cur.push(id);
    dungeonTestOpenOverride = cur;
    if (ui.dungeonPopup && !ui.dungeonPopup.classList.contains('hidden')) renderDungeonPanel();
    return cur;
  };
  /* 调试/测试用：直接把当前这场入侵副本"通关"掉，看结算（金币 / 掉落 / 专属材料）。 */
  window.__debugDungeonFinish = function () {
    if (!cave || !cave.def || !cave.def.invasion) return null;
    const def = cave.def;
    const mul = dungeonRewardMul();
    const goldBefore = gold;
    const beforeCount = inventory.length;
    cave.remaining = 0;
    cave.killed = cave.total;
    finishCave();
    const added = inventory.slice(beforeCount);
    return {
      id: def.invasion.id, name: def.name, double: !!def.invasionDouble, mul: mul,
      goldGain: gold - goldBefore,
      added: added.map(function (it) { return it.name; }),
      materialCount: added.filter(function (it) {
        return it && it.materialId === def.invasion.materialId;
      }).length
    };
  };
  /* 调试/测试用：当前这场战斗里活着的敌人用的是哪套模型（副本敌人验收用） */
  window.__debugDungeonEnemySprites = function () {
    return battleAlive().map(function (e) { return { name: e.name, sprite: e.sprite, tier: e.tier }; });
  };
  /* 调试/测试用：把这张地图直接推到"最后一只"并刷出关卡BOSS */
  window.__debugDungeonForceBoss = function () {
    if (!cave || !cave.def || !cave.def.invasion || !cave.def.boss) return null;
    cave.remaining = 1;
    cave.bossSpawned = false;
    spawnCaveEnemy();
    return window.__debugEnemyInfo();
  };
  /* 调试/测试用：某张副本的敌人名单（杂兵 / 精英 / BOSS） */
  window.__debugDungeonRoster = function (id) {
    const def = DUNGEONS.filter(function (d) { return d.id === (id || 'abyss'); })[0];
    if (!def) return null;
    return {
      id: def.id, name: def.name,
      sprites: def.sprites || [], eliteSprites: def.eliteSprites || [],
      boss: def.boss || null, bossTitle: def.bossTitle || '',
      tiers: def.tiers || [], loot: def.loot || null, gift: !!def.loot
    };
  };
  /* 调试/测试用：每日副本的"环境场景"状态（迷雾副本验收用） */
  window.__debugFogScene = function () {
    return {
      active: fogSceneActive(),
      scene: (cave && cave.def && cave.def.scene) || null,
      built: !!fogScene.built,
      tileW: FOG_TILE_W,
      layers: fogScene.layers.map(function (L) {
        return { parallax: L.cfg.parallax, alpha: L.cfg.alpha, blur: L.cfg.blur, tone: L.cfg.tone,
          w: L.cv.width, h: L.cv.height };
      }),
      bands: fogScene.bands.length
    };
  };
  /* 调试/测试用：深渊副本的环境场景状态 */
  window.__debugAbyssScene = function () {
    return {
      active: abyssSceneActive(),
      scene: (cave && cave.def && cave.def.scene) || null,
      built: !!abyssScene.built,
      tileW: ABYSS_TILE_W,
      layers: abyssScene.layers.map(function (L) {
        return { parallax: L.cfg.parallax, alpha: L.cfg.alpha, blur: L.cfg.blur, tone: L.cfg.tone };
      }),
      crystals: abyssScene.crystals.length,
      shards: abyssScene.crystals.reduce(function (n, c) { return n + c.shards.length; }, 0),
      motes: abyssScene.motes.length
    };
  };
  /* 调试/测试用：按副本 + 档次抽 n 次敌人模型（验证"精英档只会出精英模型"这类规则） */
  window.__debugPickCaveSprite = function (id, tier, n) {
    const def = DUNGEONS.filter(function (d) { return d.id === (id || 'abyss'); })[0];
    if (!def) return null;
    const caveDef = { sprites: def.sprites || null, eliteSprites: def.eliteSprites || null, boss: def.boss || null };
    const out = [];
    const times = Math.max(1, Math.min(400, Math.floor(Number(n) || 20)));
    for (let i = 0; i < times; i++) out.push(pickCaveSprite(caveDef, tier || 'common'));
    return { tier: tier || 'common', samples: out };
  };
  /* 调试/测试用：深渊战利品的配置（美术 / 售价） */
  window.__debugLootDefs = function (key) {
    const set = DUNGEON_LOOT[key || 'abyss'] || [];
    return set.map(function (d) {
      return { id: d.id, name: d.name, art: d.art, sell: d.sell, color: d.color };
    });
  };
  window.__debugGiftDefs = function () { return window.__debugLootDefs('abyss'); };
  // 调试用：消耗 n 次挑战机会（验证双倍次数用尽后的表现）
  window.__debugUseDungeonTry = function (n) {
    const times = Math.max(1, Math.floor(n || 1));
    let done = 0;
    for (let i = 0; i < times; i++) { if (useDungeonTry()) done++; }
    if (ui.dungeonPopup && !ui.dungeonPopup.classList.contains('hidden')) renderDungeonPanel();
    return { used: done, left: dungeonTriesLeft() };
  };
  // 调试用：把今日次数恢复满
  window.__debugResetDungeonTries = function () {
    dungeonRun.used = 0;
    if (ui.dungeonPopup && !ui.dungeonPopup.classList.contains('hidden')) renderDungeonPanel();
    return dungeonTriesLeft();
  };

  /* ============================================================
   * 修仙门派（框架版）
   *   · 加入门槛：练气期（playerLevel() ≥ def.minStage，炼气·初期 = 31）
   *   · 门派任务：每日一轮，做完在门派窗口里领 门派贡献 / 经验 / 金币
   *   · 门派武学：只能用门派贡献兑换；学会后直接进 knownSkills，可在战斗里施放
   *   · 同门保护：同一门派的弟子之间不能互相神识锁定（偷袭 / 抢灵脉）
   *   细节（更多门派、门派战、职位、商店）后续再加，这里先把数据与规则留好口子。
   * ============================================================ */
  let sect = {
    id: null, contribution: 0, joinedAt: 0,
    tasks: { day: '', progress: {}, claimed: {} },
    arts: {}                 // 已参悟的门派法诀层数：{ fengji: 2, lianxue: 1 }
  };

  function sectDef(id) {
    return SECTS.filter(function (s) { return s.id === (id || sect.id); })[0] || null;
  }
  function curSectDef() { return sectDef(sect.id); }
  function sectName() { const d = curSectDef(); return d ? d.name : ''; }
  function hasSect() { return !!curSectDef(); }
  // 当前角色所属门派的 id（给联机同步用；没门派就是空串）
  function mySectId() { return hasSect() ? sect.id : ''; }

  // 能不能拜入：返回 null = 可以，否则是"为什么不行"
  function sectJoinReason(def) {
    const d = def || SECTS[0];
    if (!d) return '暂无可加入的门派。';
    if (hasSect()) return '你已经是「' + sectName() + '」的弟子了。';
    if (playerLevel() < (d.minStage || 1)) {
      return '「' + d.name + '」只收 ' + (d.realmText || '练气期') + '及以上修士（当前：' +
        stageName(playerLevel()) + '）';
    }
    return null;
  }
  function joinSect(defId) {
    const d = sectDef(defId || (SECTS[0] || {}).id);
    const why = sectJoinReason(d);
    if (why) { showToast(why); return false; }
    sect.id = d.id;
    sect.joinedAt = Date.now();
    sect.contribution = Math.max(0, sect.contribution | 0);
    if (!sect.arts || typeof sect.arts !== 'object') sect.arts = {};
    sect.tasks = { day: todayKey(), progress: {}, claimed: {} };
    saveGame();
    updateSectUI();
    showToast('已拜入「' + d.name + '」，成为门下弟子。');
    pushBattleLog('★ 你拜入「' + d.name + '」，成为门下弟子（门派贡献 ' + sect.contribution + '）', 'reward');
    if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }

  /* ---------- 门派任务（每日一轮，进度按行为埋点累加） ---------- */
  function sectSyncDay() {
    const day = todayKey();
    if (!sect.tasks || typeof sect.tasks !== 'object') sect.tasks = { day: '', progress: {}, claimed: {} };
    if (!sect.tasks.progress) sect.tasks.progress = {};
    if (!sect.tasks.claimed) sect.tasks.claimed = {};
    if (sect.tasks.day !== day) {
      sect.tasks = { day: day, progress: {}, claimed: {} };
      if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    }
    return sect.tasks.day;
  }
  function sectTaskProgress(id) {
    sectSyncDay();
    return Math.max(0, Math.floor(sect.tasks.progress[id] || 0));
  }
  /* 门派任务现在按殿宇下发：把所有殿宇的任务拍平成一张表（id 全局唯一） */
  function sectAllTasks() {
    const def = curSectDef();
    if (!def) return [];
    const out = [];
    (def.buildings || []).forEach(function (b) {
      (b.tasks || []).forEach(function (t) {
        out.push({
          id: t.id, name: t.name, track: t.track, target: t.target,
          reward: t.reward || {}, buildingId: b.id, buildingName: b.name
        });
      });
    });
    return out;
  }
  function sectTaskDefById(id) {
    return sectAllTasks().filter(function (t) { return t.id === id; })[0] || null;
  }
  function sectTaskClaimed(id) {
    sectSyncDay();
    return !!sect.tasks.claimed[id];
  }
  function sectTaskDone(def) { return sectTaskProgress(def.id) >= (def.target || 1); }
  /* 行为埋点：track 命中就累加（和每日任务的埋点共用同一批调用点） */
  function sectTaskTick(track, n) {
    if (!hasSect() || !track) return false;
    const list = sectAllTasks().filter(function (t) { return t.track === track; });
    if (!list.length) return false;
    sectSyncDay();
    let changed = false;
    list.forEach(function (t) {
      const before = sectTaskProgress(t.id);
      if (before >= (t.target || 1)) return;
      const after = Math.min(t.target || 1, before + Math.max(1, Math.floor(n || 1)));
      sect.tasks.progress[t.id] = after;
      changed = true;
      if (after >= (t.target || 1) && !sect.tasks.claimed[t.id]) {
        showToast('门派任务「' + t.name + '」已完成，去「' + t.buildingName + '」领奖励');
        if (ui.btnSect) ui.btnSect.classList.add('questDot');
      }
    });
    if (changed) {
      markSaveDirty();
      if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
      if (ui.sectRoomOverlay && !ui.sectRoomOverlay.classList.contains('hidden')) renderSectRoom();
    }
    return changed;
  }
  /* 拜访殿宇：只给"别的殿"的任务计数（自己那座殿不算拜访） */
  function sectVisitTick(buildingId) {
    if (!hasSect() || !buildingId) return false;
    const list = sectAllTasks().filter(function (t) {
      return t.track === 'visitHall' && t.buildingId !== buildingId;
    });
    if (!list.length) return false;
    sectSyncDay();
    let changed = false;
    list.forEach(function (t) {
      const before = sectTaskProgress(t.id);
      if (before >= (t.target || 1)) return;
      const after = Math.min(t.target || 1, before + 1);
      sect.tasks.progress[t.id] = after;
      changed = true;
      if (after >= (t.target || 1) && !sect.tasks.claimed[t.id]) {
        showToast('门派任务「' + t.name + '」已完成，回「' + t.buildingName + '」领奖励');
        if (ui.btnSect) ui.btnSect.classList.add('questDot');
      }
    });
    if (changed) {
      markSaveDirty();
      if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    }
    return changed;
  }
  /* 击杀敌人：同时喂"击败敌人"和"击败同境界敌人"两档任务 */
  function sectKillTick(enemy) {
    sectTaskTick('kill');
    if (!enemy || typeof enemy.level !== 'number') return false;
    const sameRealm = bigRealmOfStage(enemy.level).index === bigRealmOfStage(playerLevel()).index;
    if (sameRealm) sectTaskTick('killSameRealm');
    return sameRealm;
  }
  function claimSectTask(defId) {
    const d = curSectDef();
    if (!d) { showToast('你还没有加入门派。'); return false; }
    const t = sectTaskDefById(defId);
    if (!t) return false;
    if (sectTaskClaimed(t.id)) { showToast('这项门派任务今天的奖励已经领过了。'); return false; }
    if (!sectTaskDone(t)) {
      showToast('还没完成：' + t.name + '（' + sectTaskProgress(t.id) + '/' + (t.target || 1) + '）');
      return false;
    }
    const r = t.reward || {};
    sect.tasks.claimed[t.id] = true;
    sect.contribution = Math.max(0, (sect.contribution | 0) + (r.contribution || 0));
    if (r.gold) { gold += r.gold; updateGold(); }
    if (r.xp) gainXp(r.xp);
    saveGame();
    if (ui.btnSect) ui.btnSect.classList.remove('questDot');
    showToast('门派任务「' + t.name + '」奖励：贡献 +' + (r.contribution || 0) +
      '　经验 +' + (r.xp || 0) + '　金币 +' + (r.gold || 0));
    pushBattleLog('【门派任务】' + t.name + ' 完成：贡献 +' + (r.contribution || 0) +
      '、经验 +' + (r.xp || 0) + '、金币 +' + (r.gold || 0), 'reward');
    renderSectPanel();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }
  /* 一行门派任务（门派窗口 / 建筑内部共用） */
  function sectTaskRow(t) {
    const prog = sectTaskProgress(t.id);
    const done = prog >= (t.target || 1);
    const claimed = sectTaskClaimed(t.id);
    const row = document.createElement('div');
    row.className = 'sectRow' + (done ? ' done' : '') + (claimed ? ' claimed' : '');
    row.setAttribute('data-sect-task', t.id);
    const body = document.createElement('div');
    body.className = 'sectRowBody';
    const nm = document.createElement('b');
    nm.textContent = t.name;
    const hint = document.createElement('em');
    const r = t.reward || {};
    hint.textContent = (t.buildingName ? '【' + t.buildingName + '】' : '') +
      '进度 ' + Math.min(prog, t.target || 1) + ' / ' + (t.target || 1) +
      '　·　奖励：贡献 +' + (r.contribution || 0) + '、经验 +' + (r.xp || 0) + '、金币 +' + (r.gold || 0);
    body.appendChild(nm);
    body.appendChild(hint);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sectTaskBtn' + (claimed ? ' done' : '');
    btn.textContent = claimed ? '已领取' : (done ? '领取' : '进行中');
    btn.disabled = claimed || !done;
    btn.addEventListener('click', function () { claimSectTask(t.id); });
    row.appendChild(body);
    row.appendChild(btn);
    return row;
  }

  /* ---------- 门派武学：只能拿贡献换 ---------- */
  /* ============================================================
   * 门派修炼法诀（在修炼殿参悟）
   *   ① 封极法：按层锁住某个部位，只有该件装备"核心词缀同源统一"才吃增幅；
   *      每层独立判定；七层・封极圆满把增幅倍率整体翻倍。
   *   ② 炼血刀诀：近战武器被动，按双方生命百分比差额增伤 / 吸血。
   * ============================================================ */
  // 词缀"同源"分类：同一类的词缀算同源（如 力量 / 攻击 / 防御 / 生命 …）
  const AFFIX_FAMILY = {
    strength: '力量', strInt: '力量', strDex: '力量',
    agility: '敏捷', intDex: '敏捷',
    intelligence: '智慧',
    allAttr: '综合',
    atk: '攻击', atkSpeed: '攻击', extraArrowPct: '攻击',
    critChance: '暴击', critDamage: '暴击',
    armor: '防御', armorPct: '防御', blockPct: '防御', blockCapPct: '防御',
    critReduce: '防御', physReducePct: '防御', dmgReducePct: '防御', recoupPct: '防御',
    hp: '生命', lifeRegen: '生命', lifePerKill: '生命', lifePerHit: '生命',
    mp: '灵力', mpRegen: '灵力', mpPerKill: '灵力',
    res: '抗性', chaosRes: '抗性', resCapPct: '抗性',
    speedPct: '移动',
    thorns: '荆棘',
    poisonChance: '异常', bleedChance: '异常', igniteChance: '异常'
  };
  function affixFamilyOf(key) { return AFFIX_FAMILY[key] || '其它'; }
  function itemCoreMods(item) {
    return ((item && item.mods) || []).filter(function (m) { return m && !m.base; });
  }
  /* 这件装备的"核心词缀"是否同源统一：没有核心词缀 = 不算统一 */
  function itemAffixUniform(item) {
    const mods = itemCoreMods(item);
    if (!mods.length) return false;
    const fam = affixFamilyOf(mods[0].key);
    return mods.every(function (m) { return affixFamilyOf(m.key) === fam; });
  }
  function itemAffixFamilyName(item) {
    const mods = itemCoreMods(item);
    return mods.length ? affixFamilyOf(mods[0].key) : null;
  }
  function sectArtDef(id) {
    const d = curSectDef();
    if (!d) return null;
    return (d.arts || []).filter(function (a) { return a.id === id; })[0] || null;
  }
  function artLayerCount(id) { return (sect.arts && sect.arts[id]) | 0; }
  function artLayers(def) { return (def && def.layers) || []; }
  // 封极法：某个部位当前吃的增幅倍率（1 = 没加成）
  function fengjiLayerForSlot(slot) {
    const def = sectArtDef('fengji');
    if (!def) return null;
    const learned = Math.min(artLayerCount('fengji'), artLayers(def).length);
    for (let i = 0; i < learned; i++) {
      const L = artLayers(def)[i];
      if (L.slot === slot) return L;
    }
    return null;
  }
  function fengjiSlotMult(slot) {
    const L = fengjiLayerForSlot(slot);
    if (!L) return 1;
    const g = equipment[slot];
    if (!g || !itemAffixUniform(g)) return 1;      // 词缀不统一 → 这件完全不吃加成
    const base = 1 + (L.boostPct || 0) / 100;
    // 七层・封极圆满：所有达标装备的增幅倍率整体翻倍
    return artLayerCount('fengji') >= artLayers(sectArtDef('fengji')).length
      ? 1 + (base - 1) * 2 : base;
  }
  // 参悟下一层（必须按顺序；贡献不足 / 已圆满会被拦下）
  function learnSectArt(artId) {
    const def = sectArtDef(artId);
    if (!def) return false;
    const learned = artLayerCount(artId);
    const layers = artLayers(def);
    if (learned >= layers.length) { showToast('「' + def.name + '」已经修至圆满。'); return false; }
    const L = layers[learned];
    if ((sect.contribution | 0) < (L.price || 0)) {
      showToast('门派贡献不足：参悟「' + L.name + '」需要 ' + L.price + ' 点贡献（当前 ' +
        (sect.contribution | 0) + ' 点）。');
      return false;
    }
    sect.contribution -= (L.price || 0);
    if (!sect.arts) sect.arts = {};
    sect.arts[artId] = learned + 1;
    recalcPlayerStats();
    saveGame();
    const slotText = L.slot && SLOT_DEFS[L.slot] ? '（锁定' + SLOT_DEFS[L.slot].label + '位）' : '';
    showToast('参悟成功：' + def.name + '·' + L.name + slotText);
    pushBattleLog('【门派法诀】你参悟了「' + def.name + '·' + L.name + '」' + slotText, 'reward');
    renderSectRoom();
    if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }
  // 炼血刀诀：近战武器才生效
  function lianxueActive() {
    if (!artLayerCount('lianxue')) return false;
    const wt = weaponTypeEquipped();
    return wt === '短剑' || wt === '爪';
  }

  function sectSkillDefOf(id) {
    for (let i = 0; i < SECTS.length; i++) {
      const hit = (SECTS[i].skills || []).filter(function (s) { return s.id === id; })[0];
      if (hit) return hit;
    }
    return null;
  }
  function exchangeSectSkill(defId) {
    const d = curSectDef();
    if (!d) { showToast('你还没有加入门派，无法兑换门派武学。'); return false; }
    const sk = (d.skills || []).filter(function (s) { return s.id === defId; })[0];
    if (!sk) return false;
    const owned = knownSkills.filter(function (k) { return k.id === sk.id; })[0] || null;
    const price = sk.price || 0;
    if ((sect.contribution | 0) < price) {
      showToast('门派贡献不足：兑换「' + sk.name + '」需要 ' + price + ' 点（当前 ' +
        (sect.contribution | 0) + ' 点）。');
      return false;
    }
    sect.contribution = (sect.contribution | 0) - price;
    let levelText;
    if (owned) {
      owned.level = (owned.level || 1) + 1;    // 已经会了 → 花贡献升 1 级
      levelText = '升至 ' + owned.level + ' 级';
    } else {
      const learned = learnSkillById(sk.id);
      levelText = learned ? '已学会' : '已学会（重复兑换）';
    }
    saveGame();
    showToast('兑换成功：「' + sk.name + '」' + levelText + '　贡献 -' + price);
    pushBattleLog('【门派武学】消耗 ' + price + ' 点门派贡献，' + sk.name + ' ' + levelText, 'reward');
    renderSectPanel();
    if (ui.spellPopup && !ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return true;
  }

  /* ============================================================
   * 门派窗口
   * ============================================================ */
  function sectClaimableCount() {
    if (!curSectDef()) return 0;
    return sectAllTasks().filter(function (t) {
      return sectTaskDone(t) && !sectTaskClaimed(t.id);
    }).length;
  }
  function updateSectUI() {
    if (!ui.btnSect) return;
    ui.btnSect.classList.toggle('questDot', sectClaimableCount() > 0);
  }
  function openSectPanel() {
    if (!ui.sectPopup) return false;
    closeCodex();
    closeGacha();
    closeDungeonPanel();
    closeActivityPanel();
    closeQuestPanel();
    renderSectPanel();
    ui.sectPopup.classList.remove('hidden');
    return true;
  }
  function closeSectPanel() {
    hideSkillTip();
    if (ui.sectPopup) ui.sectPopup.classList.add('hidden');
  }
  function renderSectPanel() {
    const host = ui.sectBody;
    if (!host) return;
    hideSkillTip();
    sectSyncDay();
    host.textContent = '';
    const def = curSectDef() || SECTS[0];
    if (!def) return;

    // 顶部：门派名 + 说明
    const head = document.createElement('div');
    head.className = 'sectHead';
    const title = document.createElement('b');
    title.className = 'sectTitle';
    title.textContent = def.name + '　' + (def.tagline || '');
    title.style.color = def.color || '#8fd0ff';
    head.appendChild(title);
    const sub = document.createElement('em');
    sub.className = 'sectSub';
    sub.textContent = def.desc || '';
    head.appendChild(sub);
    host.appendChild(head);

    // 状态条：已入门 → 贡献；未入门 → 入门条件
    const state = document.createElement('div');
    state.className = 'sectState';
    if (hasSect()) {
      state.textContent = '所属门派：' + sectName() + '　·　门派贡献：' + (sect.contribution | 0) +
        '　·　入门时间：' + new Date(sect.joinedAt || Date.now()).toLocaleDateString();
    } else {
      const why = sectJoinReason(def);
      state.textContent = (why ? '暂不能拜入：' + why : '可以拜入「' + def.name + '」') +
        '　·　' + (def.joinHint || '');
    }
    host.appendChild(state);

    // 未入门：给一个申请按钮
    if (!hasSect()) {
      const joinRow = document.createElement('div');
      joinRow.className = 'sectJoinRow';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'primary';
      btn.id = 'sectJoinBtn';
      const why = sectJoinReason(def);
      btn.textContent = why ? '条件不足' : '申请加入「' + def.name + '」';
      btn.disabled = !!why;
      btn.addEventListener('click', function () { joinSect(def.id); });
      joinRow.appendChild(btn);
      host.appendChild(joinRow);
    }

    if (!hasSect()) return;   // 没入门就不展示任务 / 武学（细节后续再放）

    /* 操作行：进入山门地图 + 门派贡献（按钮布局按需求重整过） */
    const actions = document.createElement('div');
    actions.className = 'sectActions';
    const enterBtn = document.createElement('button');
    enterBtn.type = 'button';
    enterBtn.className = 'sectEnterBtn';
    enterBtn.id = 'sectEnterMapBtn';
    enterBtn.textContent = '进入' + def.name + '（山门）';
    enterBtn.addEventListener('click', function () { enterSectScene(); });
    const contrib = document.createElement('span');
    contrib.className = 'sectContrib';
    contrib.textContent = '门派贡献：' + (sect.contribution | 0) +
      '　·　山门内建筑：' + ((def.buildings || []).length) + ' 处';
    actions.appendChild(enterBtn);
    actions.appendChild(contrib);
    host.appendChild(actions);

    // 门派任务（按殿宇分组：每座殿给自己的差事）
    const th = document.createElement('h3');
    th.className = 'sectH3';
    th.textContent = '门派任务（按殿宇下发 · 每日一轮）';
    host.appendChild(th);
    const taskList = document.createElement('div');
    taskList.className = 'sectList';
    sectAllTasks().forEach(function (t) { taskList.appendChild(sectTaskRow(t)); });
    host.appendChild(taskList);

    // 门派武学
    const sh = document.createElement('h3');
    sh.className = 'sectH3';
    sh.textContent = '门派武学（只能用门派贡献兑换）';
    host.appendChild(sh);
    const skillList = document.createElement('div');
    skillList.className = 'sectList';
    (def.skills || []).forEach(function (sk) {
      const owned = knownSkills.filter(function (k) { return k.id === sk.id; })[0] || null;
      const row = document.createElement('div');
      row.className = 'sectRow' + (owned ? ' owned' : '');
      row.setAttribute('data-sect-skill', sk.id);
      const body = document.createElement('div');
      body.className = 'sectRowBody';
      const nm = document.createElement('b');
      nm.textContent = sk.name + (owned ? '（已学 · Lv' + (owned.level || 1) + '）' : '');
      const hint = document.createElement('em');
      hint.textContent = (sk.desc || '') + '　·　消耗贡献 ' + (sk.price || 0) +
        (sk.type === 'spell' ? '　·　灵力 ' + (sk.cost || 0) : '');
      body.appendChild(nm);
      body.appendChild(hint);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sectTaskBtn';
      btn.textContent = (sect.contribution | 0) >= (sk.price || 0)
        ? (owned ? '升 1 级' : '兑换') : '贡献不足';
      btn.disabled = (sect.contribution | 0) < (sk.price || 0);
      btn.addEventListener('click', function () { exchangeSectSkill(sk.id); });
      row.appendChild(body);
      row.appendChild(btn);
      // 鼠标停在门派武学上：显示这门武学的完整信息（伤害 / 灵力 / 贡献 / 学习状态）
      row.addEventListener('mouseenter', function () { showSectSkillTip(row, sk, owned); });
      row.addEventListener('mouseleave', hideSkillTip);
      skillList.appendChild(row);
    });
    host.appendChild(skillList);

    /* 门派法诀：封极法 / 炼血刀诀。
     *   已按需求从「修炼殿」内部移到门派窗口的「门派武学」下方，参悟入口也统一放这里。 */
    const ah = document.createElement('h3');
    ah.className = 'sectH3';
    ah.textContent = '门派法诀（用门派贡献参悟）';
    host.appendChild(ah);
    const artList = document.createElement('div');
    artList.className = 'sectList';
    ((def.arts) || []).forEach(function (art) {
      const learned = artLayerCount(art.id);
      const layers = artLayers(art);
      const next = layers[learned] || null;
      const row = document.createElement('div');
      row.className = 'sectRow' + (learned >= layers.length ? ' owned' : '');
      row.setAttribute('data-sect-art', art.id);
      row.addEventListener('mouseenter', function () { showSectArtTip(row, art, learned, next); });
      row.addEventListener('mouseleave', hideSkillTip);
      const body = document.createElement('div');
      body.className = 'sectRowBody';
      const nm = document.createElement('b');
      nm.textContent = art.name + '（已参悟 ' + learned + ' / ' + layers.length + ' 层）';
      const hint = document.createElement('em');
      hint.textContent = next
        ? ('下一层：' + next.name + '　·　' + (next.desc || ''))
        : ('已修至圆满。' + (art.desc || ''));
      body.appendChild(nm);
      body.appendChild(hint);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sectTaskBtn';
      btn.textContent = next ? ('参悟（贡献 ' + next.price + '）') : '已圆满';
      btn.disabled = !next || (sect.contribution | 0) < next.price;
      if (next) btn.addEventListener('click', function () { learnSectArt(art.id); });
      row.appendChild(body);
      row.appendChild(btn);
      artList.appendChild(row);

      /* 封极法：把"每个已参悟部位当前是否生效"直接摆出来 */
      if (art.id === 'fengji' && learned > 0) {
        layers.slice(0, learned).forEach(function (L) {
          const g = L.slot ? equipment[L.slot] : null;
          const ok = !!(g && itemAffixUniform(g));
          const info = document.createElement('em');
          info.className = 'sectArtState' + (ok ? ' on' : '');
          info.textContent = L.name + ' → ' + (L.slot
            ? ((SLOT_DEFS[L.slot] ? SLOT_DEFS[L.slot].label : L.slot) + '：' +
              (g ? (ok ? ('生效 · 词缀统一（' + (itemAffixFamilyName(g) || '') + '），增幅 +' +
                  Math.round((fengjiSlotMult(L.slot) - 1) * 100) + '%')
                : '未生效 · 该件装备词缀不统一')
              : '未生效 · 该部位还没有装备'))
            : '全部位：达标装备的增幅倍率翻倍');
          artList.appendChild(info);
        });
      }
    });
    host.appendChild(artList);

    const foot = document.createElement('div');
    foot.className = 'sectFoot';
    foot.textContent = '门规：同门弟子之间不可互相神识锁定、偷袭或抢夺灵脉。';
    host.appendChild(foot);
  }

  /* ============================================================
   * 门派山门（场景版）= 一条有限长度的直线道路 + 路边建筑
   *   · 和冒险道路同一套走路方式（点画面 / 按「移动」前进），只是两端有边界
   *   · 建筑像洞府入口一样立在路边（先用洞府的美术，后续替换）
   *   · 点建筑 → 进内部面板；点空地 → 照常走路
   *   · 进入条件：已拜入该门派
   * ============================================================ */
  let sectRoomId = null;   // 当前打开的建筑 id
  let sectScene = null;    // { def, minX, maxX, buildings: [{ id, name, worldX, color, glow }] }
  let sectMoveDir = 0;     // 点屏幕左/右移动：-1 左 / 1 右
  let sectHolding = false; // 长按持续移动
  let sectTapLeft = 0;     // 点一下只走一小段
  let sectPressDist = 0;   // 本次按下已经走的距离
  const SECT_MOVE_SPEED = 210;
  const SECT_TAP_STEP = 120;
  const SECT_ROAD_GAP = 430;      // 建筑之间的间距（世界像素）
  const SECT_ROAD_PAD = 420;      // 道路两端的留白
  // 门派各殿宇独立美术：h = 场景里的目标高度（宽度按原图比例算）
  const SECT_BUILDING_ART = {
    hall:       { key: 'sectBuildHall',       h: 300, title: 'sectTitleHall' },
    alchemy:    { key: 'sectBuildAlchemy',    h: 260, title: 'sectTitleAlchemy' },
    cultivation:{ key: 'sectBuildCultivation',h: 300, title: 'sectTitleCultivation' },
    reception:  { key: 'sectBuildReception',  h: 260, title: 'sectTitleReception' },
    mine:       { key: 'sectBuildMine',       h: 250, title: 'sectTitleMine' }
  };
  function sectBuildingArt(b) {
    const cfg = SECT_BUILDING_ART[b && b.id] || null;
    const key = cfg ? cfg.key : 'caveEntrance';
    const raw = images[key];
    const img = (raw && raw.complete && raw.naturalWidth > 0) ? raw : images.caveEntrance;
    const dh = cfg ? cfg.h : CAVE_IMG_H;
    const ratio = (img && img.complete && img.naturalWidth > 0)
      ? (img.naturalHeight / img.naturalWidth) : caveImgRatio();
    return { key: key, img: img, dh: dh, dw: dh / ratio, title: cfg ? cfg.title : null };
  }

  // 山门里的 NPC 玩家：本地生成、在道路范围内缓慢来回走动
  const SECT_NPC_ROLES = ['守山弟子', '药房药童', '传功师兄', '矿脉执事'];
  function makeSectNpcs(def, baseX, span) {
    const usable = Math.max(320, span - 360);
    return SECT_NPC_ROLES.map(function (role, i) {
      const home = baseX + 160 + usable * (i / Math.max(1, SECT_NPC_ROLES.length - 1));
      return {
        id: 'sectNpc' + i,
        name: '道友·' + role,
        localX: home - baseX,
        homeX: home - baseX,
        dir: i % 2 ? -1 : 1,
        speed: rand(14, 24),
        range: rand(70, 150),
        phase: rand(0, 8),
        realm: stageText(Math.max(def.minStage || 31, 31)),
        action: 'idle'
      };
    });
  }

  // 进山门：把玩家放到"山门这条路"的起点，之后就是普通走路
  function enterSectScene() {
    const def = curSectDef();
    if (!def) { showToast('还没有拜入任何门派，先加入门派再进山门。'); return false; }
    if (sectScene) { closeSectPanel(); return true; }   // 已经在山门里
    if (autoAdventure) stopAutoAdventure('进入山门需要自己走动，已退出自动冒险。');
    closeSectPanel();
    closeSectRoom();
    const list = def.buildings || [];
    const startX = player.worldX + SECT_ROAD_PAD;
    const buildings = list.map(function (b, i) {
      return {
        id: b.id, name: b.name, glyph: b.glyph || '殿', desc: b.desc || '',
        worldX: startX + (i + 1) * SECT_ROAD_GAP,
        color: b.color || def.color || '#8fd0ff',
        glow: b.glow || def.glow || 'rgba(143,208,255,.45)'
      };
    });
    sectScene = {
      def: def,
      baseX: startX,
      minX: startX - 60,
      maxX: startX + (buildings.length + 1) * SECT_ROAD_GAP,
      buildings: buildings,
      npcs: makeSectNpcs(def, startX, (buildings.length + 1) * SECT_ROAD_GAP)
    };
    player.worldX = sectScene.minX + 40;
    player.vx = 0;
    sectStopMove();
    input.moving = false;
    syncMoveButton();
    activeEnemy = null;
    battleGroup.length = 0;
    caveEntrances.length = 0;      // 山门里不刷洞府入口 / 拦路者
    cave = null;
    gameState = 'walk';
    setControls('walk');
    updateCaveBar();
    sectNetJoin();
    showToast('已进入「' + def.name + '」山门：点击路边建筑进入内部，点画面左右走动。');
    pushBattleLog('【门派】你回到了' + def.name + '的山门（' + buildings.length +
      ' 处建筑：' + buildings.map(function (b) { return b.name; }).join('、') + '）', 'reward');
    return true;
  }
  function leaveSectScene(msg) {
    if (miningContinuous || miningFx) stopSectMining('离开山门，已停止挖矿。');
    sectNetLeave();
    closeSectRoom();          // 不论在不在山门里，建筑内部面板都该收掉
    if (!sectScene) return false;
    sectScene = null;
    sectStopMove();
    player.vx = 0;
    input.moving = false;
    syncMoveButton();
    updateCaveBar();
    if (msg) { showToast(msg); pushBattleLog(msg, 'system'); }
    return true;
  }
  function sectStopMove() {
    sectMoveDir = 0;
    sectHolding = false;
    sectTapLeft = 0;
    sectPressDist = 0;
    player.vx = 0;
  }

  // 点屏幕左/右：只走一小段；长按持续移动（和 PVP 活动地图同一套手感）
  function sectTapMove(sx) {
    if (!sectScene) return;
    const playerSx = player.worldX - cameraX();
    sectMoveDir = sx < playerSx ? -1 : 1;
    sectHolding = false;
    sectTapLeft = SECT_TAP_STEP;
    sectPressDist = 0;
  }

  // 山门道路上的自由左右移动（限制在 [minX, maxX]）
  function updateSectSceneWalk(dt) {
    // 挖矿期间锁死移动：必须点「停止挖矿」后才能走动
    if (miningContinuous || miningFx) {
      sectStopMove();
      input.moving = false;
      syncMoveButton();
      return;
    }
    if (sectMoveDir !== 0) {
      const spd = SECT_MOVE_SPEED * (derived.speedMult || 1);
      const beforeX = player.worldX;
      player.worldX = clamp(player.worldX + sectMoveDir * spd * dt, sectScene.minX, sectScene.maxX);
      player.walkT += dt * 9;
      player.vx = sectMoveDir * spd;
      const moved = Math.abs(player.worldX - beforeX);
      if (sectHolding) sectPressDist += moved;
      if (sectTapLeft > 0) {
        sectTapLeft -= moved;
        if (sectTapLeft <= 0) sectStopMove();
      }
      if (player.worldX <= sectScene.minX || player.worldX >= sectScene.maxX) sectStopMove();
    } else if (player.vx) {
      player.vx = 0;
    }
  }
  // 山门建筑：每座殿宇使用自己的美术，点头顶/身上都能进
  function sectSceneBuildingAt(sx, sy) {
    if (!sectScene) return null;
    const baseY = GROUND_Y + 10;
    for (let i = 0; i < sectScene.buildings.length; i++) {
      const b = sectScene.buildings[i];
      const art = sectBuildingArt(b);
      const dh = art.dh;
      const dw = art.dw;
      const bx = b.worldX - cameraX();
      if (sx >= bx - dw * 0.55 && sx <= bx + dw * 0.55 &&
          sy >= baseY - dh - 26 && sy <= baseY + 26) {
        return b;
      }
    }
    return null;
  }
  function drawSectScene() {
    if (!sectScene) return;
    const baseY = GROUND_Y + 10;
    sectScene.buildings.forEach(function (b) {
      const art = sectBuildingArt(b);
      const dh = art.dh;
      const dw = art.dw;
      const img = art.img;
      const sx = b.worldX - cameraX();
      if (sx < -dw || sx > W + dw) return;
      ctx.save();
      // 地面投影
      ctx.fillStyle = 'rgba(30, 22, 16, .5)';
      ctx.beginPath();
      ctx.ellipse(sx - 6, baseY + 2, dw * 0.42, 16, 0, 0, Math.PI * 2);
      ctx.fill();
      // 门派色光晕
      const glowY = baseY - dh * 0.46;
      const glowR = dw * 0.62;
      const glow = ctx.createRadialGradient(sx, glowY, 8, sx, glowY, glowR);
      glow.addColorStop(0, b.glow);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(sx, glowY, glowR, 0, Math.PI * 2);
      ctx.fill();
      if (img && img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, sx - dw / 2, baseY - dh, dw, dh);
        const tint = ctx.createRadialGradient(sx, baseY - dh * 0.4, 6, sx, baseY - dh * 0.4, dw * 0.4);
        tint.addColorStop(0, b.color);
        tint.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.26;
        ctx.fillStyle = tint;
        ctx.beginPath();
        ctx.arc(sx, baseY - dh * 0.4, dw * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
      } else {
        // 素材没加载出来时的兜底：一个带门派色的门洞
        ctx.fillStyle = 'rgba(40, 28, 16, .92)';
        ctx.fillRect(sx - 52, baseY - 130, 104, 130);
        ctx.fillStyle = b.color;
        ctx.fillRect(sx - 34, baseY - 96, 68, 96);
      }
      // 建筑标题：用专用标题图片，不再画文字牌
      const titleImg = art.title ? images[art.title] : null;
      if (titleImg && titleImg.complete && titleImg.naturalWidth > 0) {
        const tw = clamp(dw * 0.62, 96, 154);
        const th = tw * (titleImg.naturalHeight / titleImg.naturalWidth);
        ctx.drawImage(titleImg, sx - tw / 2, baseY - dh - th - 4, tw, th);
      }
      ctx.restore();
    });
  }

  function sectBuildingDef(id) {
    const def = curSectDef();
    if (!def) return null;
    return (def.buildings || []).filter(function (b) { return b.id === id; })[0] || null;
  }
  function openSectRoom(id) {
    const b = sectBuildingDef(id);
    if (!b || !ui.sectRoomOverlay) return false;
    sectRoomId = id;
    renderSectRoom();
    ui.sectRoomOverlay.classList.remove('hidden');
    sectVisitTick(id);   // 门派任务「拜访其他殿宇」：进别的殿就算一次
    return true;
  }
  function closeSectRoom() {
    if (ui.sectRoomOverlay) ui.sectRoomOverlay.classList.add('hidden');
    sectRoomId = null;
  }

  /* 丹殿：炼制一枚丹药（消耗贡献 + 药材 / 矿石） */
  function refineSectPill(recipeId) {
    const r = sectPillRecipe(recipeId);
    if (!r) return false;
    if ((sect.contribution | 0) < r.contribution) {
      showToast('门派贡献不足：「' + r.name + '」需要 ' + r.contribution + ' 点贡献（当前 ' +
        (sect.contribution | 0) + ' 点）。');
      return false;
    }
    if (sectMaterialCount('herb') < r.herb) {
      showToast('药材不足：「' + r.name + '」需要药材 ×' + r.herb + '（当前 ' +
        sectMaterialCount('herb') + '）。去洞府采集药材后再来。');
      return false;
    }
    if (sectMaterialCount('ore') < r.ore) {
      showToast('矿石不足：「' + r.name + '」需要矿石 ×' + r.ore + '（当前 ' +
        sectMaterialCount('ore') + '）。去门派矿山开采后再来。');
      return false;
    }
    sect.contribution -= r.contribution;
    if (r.herb) consumeSectMaterial('herb', r.herb);
    if (r.ore) consumeSectMaterial('ore', r.ore);
    const pill = makeSectPill(r);
    inventory.push(pill);
    sectTaskTick('refine', 1);   // 门派任务：炼制 3 次丹药
    saveGame();
    showToast('丹成：「' + pill.name + '」已放入背包（' + (r.detail || '') + '）。');
    pushBattleLog('【丹殿】炼成「' + pill.name + '」，消耗门派贡献 ' + r.contribution + '、药材 ' +
      r.herb + (r.ore ? ('、矿石 ' + r.ore) : ''), 'reward');
    renderSectRoom();
    if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    return true;
  }

  function appendSectRecipePanel(host) {
    const rh = document.createElement('h3');
    rh.className = 'sectH3';
    rh.textContent = '丹方炼制（贡献 + 材料 → 丹药）';
    host.appendChild(rh);
    const list = document.createElement('div');
    list.className = 'sectList';
    SECT_PILL_RECIPES.forEach(function (r) {
      const herbN = sectMaterialCount('herb');
      const oreN = sectMaterialCount('ore');
      const can = (sect.contribution | 0) >= r.contribution &&
        herbN >= r.herb && oreN >= r.ore;
      const row = document.createElement('div');
      row.className = 'sectRecipeRow' + (can ? ' canmake' : '');
      row.setAttribute('data-sect-recipe', r.id);
      const body = document.createElement('div');
      body.className = 'sectRowBody';
      const nm = document.createElement('b');
      nm.textContent = r.name + '　·　' + (r.detail || '');
      const hint = document.createElement('em');
      hint.textContent = '消耗：贡献 ' + r.contribution +
        (r.herb ? ('　药材 ' + r.herb + '（有 ' + herbN + '）') : '') +
        (r.ore ? ('　矿石 ' + r.ore + '（有 ' + oreN + '）') : '');
      body.appendChild(nm);
      body.appendChild(hint);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sectTaskBtn';
      btn.textContent = can ? '炼制' : '材料不足';
      btn.disabled = !can;
      if (can) btn.addEventListener('click', function () { refineSectPill(r.id); });
      row.appendChild(body);
      row.appendChild(btn);
      list.appendChild(row);
    });
    host.appendChild(list);
  }

  /* ---------- 门派矿山：挖矿动作 + 矿石抛起/落回/入包 ---------- */
  function updateMineStopButton() {
    if (!ui.btnMineStop) return;
    ui.btnMineStop.classList.toggle('hidden', !miningContinuous);
  }

  // 一轮挖矿：动作动画 → 矿石入包 → 矿石抛物线消散
  function beginMiningCycle() {
    if (miningFx) return false;
    const amount = randInt(1, 2);        // 每轮产出 1~2 块（原来是 1~3）
    if (autoAdventure) stopAutoAdventure('挖矿需要自己动手，已退出自动冒险。');
    closeSectRoom();
    input.moving = false;
    player.vx = 0;
    syncMoveButton();
    miningFx = {
      t: 0,
      amount: amount,
      spawned: false,
      done: false,
      particles: []
    };
    mineOreDrawn = 0;
    lastMineFrameKey = null;
    pushBattleLog('【门派矿山】你抡起矿镐，开始开凿矿脉……', 'system');
    return true;
  }

  // 点击「开凿矿石」：进入持续挖矿，直到点「停止挖矿」
  function startSectMining() {
    if (miningContinuous || miningFx) {
      showToast('已经在持续挖矿了，点击「停止挖矿」可以结束。');
      return false;
    }
    miningContinuous = true;
    miningAnimClock = 0;
    updateMineStopButton();
    const ok = beginMiningCycle();
    if (!ok) {
      miningContinuous = false;
      updateMineStopButton();
      return false;
    }
    showToast('开始持续挖矿：矿石会自动入包，点「停止挖矿」结束。');
    return true;
  }

  function stopSectMining(msg) {
    if (!miningContinuous && !miningFx) return false;
    miningContinuous = false;
    updateMineStopButton();
    showToast(msg || '已停止持续挖矿。');
    // 如果正好停在"两轮之间"，当前没有动画，直接把矿山面板恢复
    if (!miningFx && sectScene && gameState === 'walk' && !sectRoomId) {
      setTimeout(function () {
        if (sectScene && gameState === 'walk' && !sectRoomId && !miningContinuous) openSectRoom('mine');
      }, 180);
    }
    return true;
  }

  function spawnMiningOreParticles(amount) {
    const baseX = player.worldX;
    for (let i = 0; i < amount; i++) {
      miningFx.particles.push({
        icon: 'ore_' + randInt(1, 7),
        worldX: baseX + rand(-34, 34),
        x: 0,
        h: 0,
        vx: rand(-46, 46),
        vh: rand(285, 355),
        rot: rand(0, Math.PI * 2),
        spin: rand(-7.5, 7.5),
        landT: 0,
        delay: i * 0.075
      });
    }
  }

  function updateMiningFx(dt) {
    if (!miningFx) {
      // 两轮之间也继续播挖矿动作，避免矿石飞行时突然切回休息帧
      if (miningContinuous) miningAnimClock += dt;
      return;
    }
    miningAnimClock += dt;
    miningFx.t += dt;
    // 挖矿动作播完：真正把矿石放进背包，并从地面抛起
    if (!miningFx.spawned && miningFx.t >= MINE_ANIM_SEC) {
      miningFx.spawned = true;
      inventory.push(makeSectMaterial('ore', miningFx.amount));
      sectTaskTick('mine', 1);
      saveGame();
      spawnMiningOreParticles(miningFx.amount);
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    }
    if (!miningFx.spawned) return;
    const gravity = 880;
    miningFx.particles.forEach(function (p) {
      if (p.delay > 0) { p.delay -= dt; return; }
      if (p.landT > 0) { p.landT += dt; return; }
      p.x += p.vx * dt;
      p.vh -= gravity * dt;
      p.h += p.vh * dt;
      p.rot += p.spin * dt;
      if (p.h <= 0 && p.vh < 0) {
        p.h = 0;
        p.vh = 0;
        p.landT = 0.001;      // 落地后开始淡出
      }
    });
    const alive = miningFx.particles.filter(function (p) { return p.landT < 0.58; });
    if (!alive.length) {
      const amount = miningFx.amount;
      miningFx = null;
      showToast('挖矿获得「矿石」×' + amount + '，已存入背包。');
      pushBattleLog('【门派矿山】矿石 ×' + amount + ' 已收入背包', 'reward');
      // 持续挖矿：一轮结束后自动接下一轮；点停止后才把矿山面板还回来
      if (miningContinuous && sectScene && gameState === 'walk') {
        setTimeout(function () {
          if (miningContinuous && !miningFx && sectScene && gameState === 'walk') beginMiningCycle();
        }, MINE_CYCLE_GAP_MS);
      } else {
        miningContinuous = false;
        updateMineStopButton();
        if (sectScene && gameState === 'walk') {
          setTimeout(function () {
            if (sectScene && gameState === 'walk' && !sectRoomId && !miningContinuous) openSectRoom('mine');
          }, 260);
        }
      }
    }
  }

  function drawMiningFx() {
    if (!miningFx || !miningFx.spawned) return;
    miningFx.particles.forEach(function (p) {
      if (p.delay > 0 || p.landT >= 0.58) return;
      const img = images[p.icon];
      if (!img || !img.complete || !img.naturalWidth) return;
      const px = p.worldX + p.x - cameraX();
      const py = GROUND_Y + 4 - p.h;
      const alpha = p.landT > 0 ? Math.max(0, 1 - p.landT / 0.58) : 1;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(px, py - 10);
      ctx.rotate(p.rot);
      ctx.drawImage(img, -10, -10, 20, 20);
      ctx.restore();
      mineOreDrawn++;
    });
  }

  function renderSectRoom() {
    const b = sectBuildingDef(sectRoomId);
    if (!b || !ui.sectRoomBody) return;
    if (ui.sectRoomTitle) ui.sectRoomTitle.textContent = b.name;
    const host = ui.sectRoomBody;
    host.textContent = '';

    const desc = document.createElement('div');
    desc.className = 'sectRoomDesc';
    desc.textContent = b.desc || '';
    host.appendChild(desc);

    const info = document.createElement('div');
    info.className = 'sectRoomDesc';
    info.textContent = '门派贡献：' + (sect.contribution | 0) +
      '　·　灵力：' + (combat.mp | 0) + ' / ' + combat.maxMp;
    host.appendChild(info);

    const actRow = document.createElement('div');
    actRow.className = 'sectRoomActions';
    const mkBtn = function (text, fn, disabled) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sectTaskBtn';
      btn.textContent = text;
      btn.disabled = !!disabled;
      if (!disabled) btn.addEventListener('click', fn);
      return btn;
    };

    if (b.id === 'alchemy') {
      const cost = 10;
      const btn = mkBtn('兑换回血药瓶（贡献 ' + cost + '）', function () {
        if ((sect.contribution | 0) < cost) { showToast('门派贡献不足：医疗丹药需要 ' + cost + ' 点贡献。'); return; }
        sect.contribution -= cost;
        const pot = makePotion(playerLevel() + 2);
        inventory.push(pot);
        saveGame();
        showToast('丹殿兑换成功：' + pot.name + '（' + pot.detail + '）');
        pushBattleLog('【丹殿】用 ' + cost + ' 点门派贡献换了一瓶「' + pot.name + '」', 'reward');
        renderSectRoom();
        updateCaveBar();
      }, (sect.contribution | 0) < cost);
      actRow.appendChild(btn);
    } else if (b.id === 'mine') {
      // 门派矿山：开凿矿石（播挖矿动画 → 矿石抛起/落回 → 入背包 → 回到矿山面板）
      const oreBtn = mkBtn('开凿矿石（持续挖矿）', function () {
        startSectMining();
      }, false);
      actRow.appendChild(oreBtn);
    } else if (b.id === 'cultivation') {
      const btn = mkBtn('打坐吐纳（灵力回满）', function () {
        if (combat.mp >= combat.maxMp) { showToast('灵力已经是满的。'); return; }
        refillSpirit();
        updateBattleHud();
        showToast('在修炼殿吐纳，灵力已回满（' + combat.maxMp + '）。');
        pushBattleLog('【修炼殿】你打坐吐纳，灵力回满（' + combat.maxMp + '）', 'reward');
        renderSectRoom();
      }, false);
      actRow.appendChild(btn);
    } else if (b.id === 'reception') {
      const btn = mkBtn('查看门派任务', function () {
        closeSectRoom();
        openSectPanel();
      }, false);
      actRow.appendChild(btn);
    } else {
      // 大殿：门派信息（后续放掌门 / 门规 / 门派战等）
      const btn = mkBtn('查看门派详情', function () {
        closeSectRoom();
        openSectPanel();
      }, false);
      actRow.appendChild(btn);
    }

    const todo = document.createElement('div');

    /* 丹殿：除兑换回血药瓶外，这里提供多个丹方炼制入口 */
    if (b.id === 'alchemy') appendSectRecipePanel(host);

    /* 本殿下发的门派任务（每座殿各管一摊） */
    const hallTasks = (b.tasks || []).map(function (t) {
      return {
        id: t.id, name: t.name, track: t.track, target: t.target,
        reward: t.reward || {}, buildingId: b.id, buildingName: b.name
      };
    });
    if (hallTasks.length) {
      const th2 = document.createElement('h3');
      th2.className = 'sectH3';
      th2.textContent = b.name + ' 的门派任务';
      host.appendChild(th2);
      const list2 = document.createElement('div');
      list2.className = 'sectList';
      hallTasks.forEach(function (t) { list2.appendChild(sectTaskRow(t)); });
      host.appendChild(list2);
    }

    todo.className = 'sectRoomDesc';
    todo.textContent = '（' + b.name + '内部玩法仍在建设：后续会在这里接入对应的门派系统。）';
    host.appendChild(actRow);
    host.appendChild(todo);
  }

  /* ============================================================
   * 每日任务
   *   7 项日常，各自给点数；凑满 100 点自动赠送 5 点活力（每天一次）。
   *   点数合计正好 100（10+10+20+20+10+10+20），也就是"全部做完 = 领活力"。
   *   跨天（本机日期变化）自动清零重来；进度写进存档，刷新 / 换设备都接得上。
   * ============================================================ */
  const DAILY_QUEST_GOAL = 100;        // 满点数
  const DAILY_QUEST_REWARD = 5;        // 满点数送的活力
  const DAILY_QUESTS = [
    { id: 'share', name: '分享一次装备', points: 10, hint: '在背包里对装备点「分享」' },
    { id: 'chat', name: '聊天窗口发言一次', points: 10, hint: '在世界频道发一条消息' },
    { id: 'cave', name: '进入一次洞府', points: 20, hint: '路上遇到洞府后点「进入」' },
    { id: 'reforge', name: '洗练一次装备', points: 20, hint: '角色装备栏里的「洗练」' },
    { id: 'battle', name: '进行一次战斗', points: 10, hint: '路上遇到敌人打一场' },
    { id: 'shop', name: '商店购买过一次装备', points: 10, hint: '在商店花金币买一件装备' },
    { id: 'gacha', name: '进行一次抽卡', points: 20, hint: '图鉴抽卡（单抽 / 十连都算）' }
  ];
  // { day, done:{id:true}, points, rewarded }
  let dailyQuests = { day: '', done: {}, points: 0, rewarded: false };

  function dailyQuestSyncDay() {
    const day = todayKey();
    if (dailyQuests.day !== day) {
      dailyQuests = { day: day, done: {}, points: 0, rewarded: false };
      dailyQuestRendered = '';
    }
    return dailyQuests.day;
  }
  function dailyQuestPoints() {
    dailyQuestSyncDay();
    let n = 0;
    DAILY_QUESTS.forEach(function (q) { if (dailyQuests.done[q.id]) n += q.points; });
    dailyQuests.points = n;
    return n;
  }
  function dailyQuestRemain() { return Math.max(0, DAILY_QUEST_GOAL - dailyQuestPoints()); }

  /* 完成一项日常（重复调用不会重复给分） */
  function dailyQuestTick(id) {
    const def = DAILY_QUESTS.filter(function (q) { return q.id === id; })[0];
    if (!def) return false;
    dailyQuestSyncDay();
    if (dailyQuests.done[id]) return false;
    dailyQuests.done[id] = true;
    const pts = dailyQuestPoints();
    if (ui.dailyPopup && !ui.dailyPopup.classList.contains('hidden')) renderQuestPanel();
    if (ui.dailyPopup && ui.dailyPopup.classList.contains('hidden')) {
      markQuestBadge();       // 窗口没开时，在按钮上点个红点提示"有进度"
    }
    if (pts >= DAILY_QUEST_GOAL && !dailyQuests.rewarded) grantDailyQuestReward();
    else showToast('每日任务「' + def.name + '」完成　+' + def.points + ' 点（' + pts + '/' + DAILY_QUEST_GOAL + '）');
    markSaveDirty();
    return true;
  }

  function grantDailyQuestReward() {
    if (dailyQuests.rewarded) return false;
    dailyQuests.rewarded = true;
    vitality += DAILY_QUEST_REWARD;
    updateVitalityUI();
    showToast('每日任务全部完成！活力 +' + DAILY_QUEST_REWARD + '（当前活力 ' + vitality + '）');
    pushBattleLog('每日任务达成 ' + DAILY_QUEST_GOAL + ' 点：赠送活力 +' + DAILY_QUEST_REWARD, 'reward');
    if (ui.dailyPopup && !ui.dailyPopup.classList.contains('hidden')) renderQuestPanel();
    markSaveDirty();
    saveGame();
    return true;
  }

  /* 按钮上的"有进度"小红点（关着窗口时也能看见） */
  function markQuestBadge() {
    if (!ui.btnQuest) return;
    ui.btnQuest.classList.add('questDot');
  }
  function clearQuestBadge() {
    if (ui.btnQuest) ui.btnQuest.classList.remove('questDot');
  }

  let dailyQuestRendered = '';
  function renderQuestPanel() {
    if (!ui.dailyList) return;
    dailyQuestSyncDay();
    const pts = dailyQuestPoints();
    const left = dailyQuestRemain();
    if (ui.dailyHead) {
      ui.dailyHead.textContent = '今日已得 ' + pts + ' / ' + DAILY_QUEST_GOAL + ' 点　·　' +
        (left > 0 ? ('再得 ' + left + ' 点可领 ' + DAILY_QUEST_REWARD + ' 点活力') :
          (dailyQuests.rewarded ? ('已领取活力 +' + DAILY_QUEST_REWARD) : '可领活力 +' + DAILY_QUEST_REWARD));
    }
    if (ui.dailyBarFill) ui.dailyBarFill.style.width = Math.min(100, pts / DAILY_QUEST_GOAL * 100) + '%';
    ui.dailyList.textContent = '';
    DAILY_QUESTS.forEach(function (q) {
      const done = !!dailyQuests.done[q.id];
      const row = document.createElement('div');
      row.className = 'dailyRow' + (done ? ' done' : '');
      const mark = document.createElement('i');
      mark.className = 'dailyMark';
      mark.textContent = done ? '✔' : '';
      const body = document.createElement('div');
      body.className = 'dailyBody';
      const name = document.createElement('b');
      name.textContent = q.name;
      const hint = document.createElement('em');
      hint.textContent = q.hint;
      body.appendChild(name);
      body.appendChild(hint);
      const score = document.createElement('span');
      score.className = 'dailyScore';
      score.textContent = '+' + q.points;
      row.appendChild(mark);
      row.appendChild(body);
      row.appendChild(score);
      ui.dailyList.appendChild(row);
    });
    if (ui.dailyFoot) {
      ui.dailyFoot.textContent = dailyQuests.rewarded
        ? ('今日奖励已领取（活力 +' + DAILY_QUEST_REWARD + '）　·　每日 0 点刷新')
        : ('满 ' + DAILY_QUEST_GOAL + ' 点自动赠送 ' + DAILY_QUEST_REWARD + ' 点活力　·　每日 0 点刷新');
    }
    dailyQuestRendered = dailyQuests.day;
  }

  function openQuestPanel() {
    if (!ui.dailyPopup) return;
    clearQuestBadge();
    renderQuestPanel();
    ui.dailyPopup.classList.remove('hidden');
  }
  function closeQuestPanel() {
    if (ui.dailyPopup) ui.dailyPopup.classList.add('hidden');
  }

  /* ============================================================
   * 活动 · 临时公共副本
   *   ① 每 12 小时（东八区 00:00 / 12:00）为一个时段，用"时段序号"当随机种子，
   *      所以同一时段里所有人看到的副本完全一致（不需要服务器）。
   *   ② 每个时段随机开 1~4 个临时副本，每个只存在 60 分钟。
   *   ③ 境界限制：玩家境界超过副本对应境界（比 maxStage 高）就进不去。
   *   ④ 场景复用洞府实例，额外画"其他道友"的虚影 —— 模拟公共地图。
   * ============================================================ */
  const ACTIVITY_PERIOD_MS = ACTIVITY_PERIOD_HOURS * 3600 * 1000;
  const ACTIVITY_LIFETIME_MS = ACTIVITY_LIFETIME_MIN * 60 * 1000;
  const ACTIVITY_TZ_OFFSET_MS = 8 * 3600 * 1000;   // 让时段边界落在东八区的 00:00 / 12:00
  let activityRun = null;          // 当前所在的临时副本 { def, endsAt, enteredAt }
  /* PVP 活动副本的进入限制：
   *   · 每次进入消耗 1 点活力
   *   · 进入后要等 1 小时才能再进（时间戳随档保存，刷新/换设备都算数） */
  const ACTIVITY_ENTER_COST = 1;                        // 进入消耗的活力
  const ACTIVITY_ENTER_COOLDOWN_MS = 60 * 60 * 1000;    // 进入冷却：1 小时
  let activityLastEnterAt = 0;                          // 上次进入的时间戳（0 = 没进过）
  let activityClockAcc = 0;
  let activityRenderedKey = null;
  let activitySpawnOverride = null;
  let activityTestOpenOverride = null;  // 测试常开名单的运行时覆盖（null = 用 defs 里的配置）
  let activityGod = false;           // 调试/测试用：副本内不掉血（跑长流程用）
  /* ---------- 副本里的"真人"（房间同步：只做看得见，还没做战斗仲裁） ---------- */
  const netMembers = new Map();      // sid -> { sid, name, x, drawX, hp, level, realm, action, ts }
  let netActRoom = null;             // 当前加入的房间（副本id:层）
  let netActSid = null;              // 服务器分配的我的 sid
  let netActCount = 0;               // 房间人数（含自己）
  let netPosAcc = 0;                 // 位置上报节流
  const NET_MEMBER_TTL_MS = 70000;   // 多久没收到某个人的位置才算他真的走了（和后端 stale 对齐）
  const NET_HEARTBEAT_MS = 5000;     // 独立心跳：网页切后台也不靠主循环，保证不会被当成掉线
  function activityTestOpenIds() {
    return activityTestOpenOverride || ACTIVITY_TEST_OPEN;
  }
  function activityIsTestOpen(id) {
    return activityTestOpenIds().indexOf(id) >= 0;
  }

  /* ---------- 副本"真人"同步：进房 / 上报 / 离房 ---------- */
  function netChatReady() {
    return !!(net.chat && net.chatState === 'online');
  }
  function activityRoomKey() {
    return activityRun ? (activityRun.def.id + ':' + activityRun.floor) : null;
  }
  // 我现在在干什么（给别人看的动作）
  function netActionOf() {
    if (sectScene) {
      if (miningContinuous || miningFx) return 'mine';
      if (sectMoveDir !== 0 || Math.abs(player.vx) > 1) return 'walk';
      return 'idle';
    }
    if (!activityRun) return 'idle';
    if (activityRun.pick) return 'pick';
    if (activityRun.moveDir !== 0) return 'walk';
    if (gameState === 'battle') return 'fight';
    return 'idle';
  }
  /* 是不是同门弟子（两边都得有门派，且 id 相同才算） */
  function netSameSect(member) {
    const mine = mySectId();
    if (!mine || !member) return false;
    return String(member.sect || '') === mine;
  }
  function activitySelfPayload() {
    return {
      // 坐标要换成「本层内相对坐标」：每个人是从自己公路位置进来的，绝对坐标对不上
      x: Math.round(player.worldX - (activityRun ? activityRun.baseX : 0)),
      hp: combat.maxHp ? clamp(combat.hp / combat.maxHp, 0, 1) : 1,
      level: playerLevel(),
      realm: stageText(playerLevel()),
      action: netActionOf(),
      // 移动速度：只用于"逃跑判定"（跑得比发起者快才跑得掉）
      speed: Math.round((derived.speedMult || 1) * 1000) / 1000,
      // 攻击速度：PvP 先手判定用（1 = 100%，越高越先手）
      atkSpd: Math.round(attackSpeedMult() * 1000) / 1000,
      // 门派：同门弟子之间不能互相神识锁定（偷袭 / 抢灵脉）
      sect: mySectId()
    };
  }
  // 进副本 / 换层都调用它：服务器会把我们挪到新房间
  function activityNetJoin() {
    const room = activityRoomKey();
    if (!room) return false;
    netMembers.clear();
    netActSid = null;
    netActRoom = null;
    if (!netChatReady()) { updateCaveBar(); return false; }
    netActRoom = room;
    net.chat.send({ type: 'act:join', room: room, self: activitySelfPayload() });
    return true;
  }
  function activityNetLeave() {
    if (netChatReady() && netActRoom) net.chat.send({ type: 'act:leave' });
    netActRoom = null;
    netActSid = null;
    netActCount = 0;
    netMembers.clear();
  }

  /* ---------- 门派山门：同门派玩家同屏（复用 act 房间同步） ---------- */
  function sectRoomKey() {
    return sectScene ? ('sect:' + sectScene.def.id) : null;
  }
  function sectSelfPayload() {
    return {
      x: sectScene ? Math.round(player.worldX - sectScene.baseX) : 0,
      hp: combat.maxHp ? clamp(combat.hp / combat.maxHp, 0, 1) : 1,
      level: playerLevel(),
      realm: stageText(playerLevel()),
      action: netActionOf(),
      speed: Math.round((derived.speedMult || 1) * 1000) / 1000,
      atkSpd: Math.round(attackSpeedMult() * 1000) / 1000,
      sect: mySectId()
    };
  }
  function sectNetJoin() {
    const room = sectRoomKey();
    if (!room) return false;
    netMembers.clear();
    netActSid = null;
    netActRoom = null;
    if (!netChatReady()) return false;
    netRoomKind = 'sect';
    netActRoom = room;
    net.chat.send({ type: 'act:join', room: room, self: sectSelfPayload() });
    return true;
  }
  function sectNetLeave() {
    if (netRoomKind !== 'sect') return false;
    if (netChatReady() && netActRoom) net.chat.send({ type: 'act:leave' });
    netActRoom = null;
    netActSid = null;
    netActCount = 0;
    netRoomKind = null;
    netMembers.clear();
    return true;
  }
  function updateSectNet(dt) {
    if (!sectScene) return;
    updateSectNpcs(dt);
    if (netRoomKind !== 'sect' || netActRoom !== sectRoomKey()) sectNetJoin();
    netPosAcc += dt;
    if (netPosAcc >= 0.1) {
      netPosAcc = 0;
      if (netChatReady() && netActRoom === sectRoomKey()) {
        const p = sectSelfPayload();
        net.chat.send({ type: 'act:pos', x: p.x, hp: p.hp, action: p.action,
          level: p.level, realm: p.realm, speed: p.speed, atkSpd: p.atkSpd, sect: p.sect });
      }
    }
    const now = Date.now();
    netMembers.forEach(function (m, sid) {
      if (now - m.ts > NET_MEMBER_TTL_MS) {
        netMembers.delete(sid);
        activityNetSync();
        return;
      }
      if (m.drawX === undefined) m.drawX = m.localX;
      m.drawX += (m.localX - m.drawX) * Math.min(1, dt * 8);
      if (Math.abs(m.localX - (m.lastLocalX === undefined ? m.localX : m.lastLocalX)) > 0.5) {
        m.facing = m.localX > m.lastLocalX ? 1 : -1;
      }
      m.lastLocalX = m.localX;
    });
  }
  /* 重新要一份房间名单（不改房间、不打扰别人）。
   * 本地把某个很久没消息的人删掉之后调用，他还真在房间里就能补回来。
   * 5 秒最多发一次，避免一直打服务器。 */
  let netSyncTs = 0;
  function activityNetSync(force) {
    if (!netChatReady() || !netActRoom) return false;
    const now = Date.now();
    if (!force && now - netSyncTs < 5000) return false;
    netSyncTs = now;
    net.chat.send({ type: 'act:sync' });
    return true;
  }
  // 每帧推进：位置上报（10Hz 节流）、真人位置插值（画面上平滑一点）
  function updateActivityNet(dt) {
    if (!activityRun) return;
    if (netActRoom !== activityRoomKey()) activityNetJoin();   // 换层了 → 重新进房
    netPosAcc += dt;
    if (netPosAcc >= 0.1) {
      netPosAcc = 0;
      if (netChatReady() && netActRoom) {
        const p = activitySelfPayload();
        net.chat.send({ type: 'act:pos', x: p.x, hp: p.hp, action: p.action,
          level: p.level, realm: p.realm, speed: p.speed, atkSpd: p.atkSpd });
      }
    }
    const now = Date.now();
    netMembers.forEach(function (m, sid) {
      /* 多久没消息才算他走了：给 70 秒。
       * 后台标签页的定时器会被浏览器压到一分钟一次，6 秒就删人会让"其实还在副本里"的
       * 玩家凭空消失；真正的离开服务端会广播 act:leave，不用靠这个兜底。 */
      if (now - m.ts > NET_MEMBER_TTL_MS) {
        netMembers.delete(sid);
        activityNetSync();          // 他要是还在房间，重新拉名单把他补回来
        return;
      }
      if (m.drawX === undefined) m.drawX = m.localX;
      m.drawX += (m.localX - m.drawX) * Math.min(1, dt * 8);
      // 朝哪边：按层内坐标的变化方向判断（服务器只同步位置，不同步朝向）
      if (Math.abs(m.localX - (m.lastLocalX === undefined ? m.localX : m.lastLocalX)) > 0.5) {
        m.facing = m.localX > m.lastLocalX ? 1 : -1;
      }
      m.lastLocalX = m.localX;
    });
  }
  // 收到服务器广播（在 onChatServerMessage 里被调用）
  function onActivityNetMessage(msg) {
    if (msg.type === 'act:state') {
      netActSid = msg.self || null;
      netActCount = msg.count || 0;
      netMembers.clear();
      (msg.members || []).forEach(function (m) {
        const known = netMembers.has(m.sid);
        const nm = addNetMember(m);
        if (nm && netRoomKind === 'ling' && !known) nm.spawnT = LING_PEER_DROP_SEC;
      });
      updateCaveBar();
      return true;
    }
    if (msg.type === 'act:join') {
      const nm = addNetMember(msg.member);
      // 灵脉房间：新人是从灵脉上空的漩涡里掉下来的
      if (nm && netRoomKind === 'ling') nm.spawnT = LING_PEER_DROP_SEC;
      updateCaveBar();
      return true;
    }
    if (msg.type === 'act:pos') {
      const m = netMembers.get(msg.sid);
      if (m) {
        m.localX = msg.x; m.hp = msg.hp;
        if (msg.action) m.action = msg.action;
        if (typeof msg.speed === 'number') m.speed = msg.speed;
        if (typeof msg.atkSpd === 'number') m.atkSpd = msg.atkSpd;
        m.ts = Date.now();
      } else if (msg.sid !== netActSid) {
        // 不认识的 sid：本地可能把他超时删掉了，重新拉一次名单
        activityNetSync();
      }
      return true;
    }
    if (msg.type === 'act:leave') {
      netMembers.delete(msg.sid);
      updateCaveBar();
      return true;
    }
    if (msg.type === 'act:count') {
      netActCount = msg.count || 0;
      updateCaveBar();
      return true;
    }
    /* 服务器说"你已经不在房间里了"（超时被清 / 服务重启过）：
     * 立刻重新进房，别人马上又能看到你。 */
    if (msg.type === 'act:expired') {
      if (activityRun) activityNetJoin();
      else if (sectScene) sectNetJoin();
      else if (netRoomKind === 'ling' && netLingNumber) lingNetJoin(netLingNumber);
      return true;
    }
    /* ---------- 真人切磋 ---------- */
    if (msg.type === 'act:challenge') {
      /* 门规兜底：同门弟子的锁定一律不弹窗（服务端也会拦，这里防旧版服务端） */
      if (msg.fromSect && mySectId() && String(msg.fromSect) === mySectId()) {
        if (netChatReady() && netActRoom) net.chat.send({ type: 'act:pvpReply', duelId: msg.duelId, accept: false });
        pushBattleLog('【门规】「道友·' + msg.fromName + '」与你同属' + sectName() + '，这次锁定被回绝了', 'system');
        showToast('同门弟子不可互相神识锁定，已自动回绝。');
        return true;
      }
      /* 被锁定：弹窗让玩家选「进入战斗 / 逃跑」 */
      pvpOffered = {
        duelId: msg.duelId, fromSid: msg.from, fromName: msg.fromName,
        fromRealm: msg.fromRealm || '', fromLevel: msg.fromLevel || 1, fromSpeed: msg.fromSpeed || 1
      };
      openDuelPopup(pvpOffered);
      pushBattleLog('「道友·' + msg.fromName + '」用神识锁定了你——可选择迎战或逃跑', 'enemy');
      return true;
    }
    if (msg.type === 'act:challengeSent') {
      if (pvpPending) pvpPending.duelId = msg.duelId;
      return true;
    }
    if (msg.type === 'act:pvpFail') {
      showToast(msg.message || '挑战失败：对方已经不在这层了。');
      pvpPending = null;
      return true;
    }
    if (msg.type === 'act:pvpEscape') {
      if (msg.self) {
        showToast('你避开了「道友·' + msg.byName + '」的神识锁定。');
      } else {
        showToast('「道友·' + msg.byName + '」选择了逃跑，这场交锋作罢。');
        pushBattleLog('「道友·' + msg.byName + '」选择了逃跑，交锋取消', 'system');
      }
      pvpPending = null;
      pvpOffered = null;
      closeDuelPopup();
      return true;
    }
    if (msg.type === 'act:pvpCollect') {
      // 双方都点了"进入战斗"（或 10 秒倒计时到点被强制开战）→ 上报属性 / 装备细节
      pvpOffered = null;
      closeDuelPopup();
      if (msg.forced) {
        showToast(msg.message || '强制开战：正在上报属性……');
        pushBattleLog(msg.message || '对方的锁定倒计时结束，强制开战（发起方先手）', 'enemy');
      } else {
        showToast('双方都同意交手，正在交换功法……');
      }
      sendPvpLoadout(msg.duelId);
      return true;
    }
    if (msg.type === 'act:pvpFleeFail') {
      pvpOffered = null;
      closeDuelPopup();
      showToast(msg.message || '逃跑失败：你的移速不如对方。');
      pushBattleLog('逃跑失败（移速 ' + (msg.mySpeed || 1).toFixed(2) + ' ≤ 对方 ' +
        (msg.foeSpeed || 1).toFixed(2) + '），被强行拉入战斗', 'enemy');
      return true;
    }
    if (msg.type === 'act:pvpTurn') {
      const d = pvpDuel;
      if (d) {
        d.myTurn = msg.turn === 'me';
        pvpTurnClock = 0;
        battleTurn = d.myTurn ? 'player' : 'enemy';
        if (d.myTurn) defending = false;    // 又轮到自己 → 上一回合的防御姿态结束（护盾收起来）
        updatePvpControlState();
        if (msg.message) { setBattleLog(msg.message); showToast(msg.message); }
        pushBattleLog(msg.message || '本回合超时，自动跳过', 'system');
        if (d.myTurn) resetPlayerIdleTimer();
        updateBattleHud();
      }
      return true;
    }
    if (msg.type === 'act:pvpWait') {
      if (msg.message) setBattleLog(msg.message);
      return true;
    }
    if (msg.type === 'act:pvpStart') {
      pvpPending = null;
      startPvpDuel(msg);
      return true;
    }
    if (msg.type === 'act:pvpState') {
      const d = pvpDuel;
      if (d) {
        if (typeof msg.myHp === 'number') d.myHp = Math.max(1, msg.myHp);
        if (typeof msg.foeHp === 'number') d.foeHp = Math.max(0, msg.foeHp);
        const myTurn = msg.turn === 'me';
        if (myTurn !== d.myTurn) {
          d.myTurn = myTurn;
          pvpTurnClock = 0;
          updatePvpControlState();
          if (myTurn) {
            battleTurn = 'player';
            defending = false;      // 又轮到自己 → 防御姿态结束（护盾收起）
            resetPlayerIdleTimer();
          } else {
            battleTurn = 'enemy';
          }
        }
        if (activeEnemy) activeEnemy.hp = Math.max(1, d.foeHp);
        if (msg.hit) {
          addDamagePopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 178, msg.hit, '#ffd07a');
        }
        if (msg.message) setBattleLog(msg.message);
        else if (msg.hit) setBattleLog('你命中「道友·' + d.foeName + '」' + msg.hit + ' 点，对方生命 ' + d.foeHp + '。');
        updateBattleHud();
      }
      return true;
    }
    if (msg.type === 'act:pvpHit') {
      const d = pvpDuel;
      if (d) {
        if (typeof msg.myHp === 'number') d.myHp = Math.max(1, msg.myHp);
        if (typeof msg.foeHp === 'number') d.foeHp = Math.max(0, msg.foeHp);
        if (activeEnemy) activeEnemy.hp = Math.max(1, d.foeHp);
        // 我被打：扣显示血量（本地不低于 1，真正阵亡由服务端判定）
        combat.hp = Math.max(1, d.myHp);
        player.hurtT = HURT_TIME;
        pvpFoeAtkT = PLAYER_ATK_DUR;      // 对方出手了 → 播一次攻击动作
        addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 148, msg.dmg, '#ff8f72');
        setBattleLog('「道友·' + (msg.fromName || d.foeName) + '」击中你 ' + msg.dmg +
          ' 点' + (msg.guarding ? '（你防御减伤 35%）' : '') + '，你的生命 ' + combat.hp + '。');
        pushBattleLog('「道友·' + (msg.fromName || d.foeName) + '」对你造成 ' + msg.dmg + ' 点伤害', 'enemy');
        d.myTurn = msg.turn === 'me';
        pvpTurnClock = 0;
        updatePvpControlState();
        battleTurn = d.myTurn ? 'player' : 'enemy';
        updateBattleHud();
      }
      return true;
    }
    if (msg.type === 'act:pvpAck') {
      const d = pvpDuel;
      // 防御这类"结束回合"的动作会带上新的回合归属，要跟着切换按钮可用状态
      if (d && (msg.turn === 'me' || msg.turn === 'them')) {
        const myTurn = msg.turn === 'me';
        if (myTurn !== d.myTurn) {
          d.myTurn = myTurn;
          pvpTurnClock = 0;
          battleTurn = myTurn ? 'player' : 'enemy';
          if (myTurn) resetPlayerIdleTimer();
          updatePvpControlState();
        }
      }
      if (msg.message) { setBattleLog(msg.message); showToast(msg.message); }
      if (msg.kind === 'guardFoe') pvpFoeAtkT = PLAYER_ATK_DUR;   // 对方摆防御 = 对方出手
      return true;
    }
    if (msg.type === 'act:pvpEnd') {
      if (msg.iWon === false && !msg.noDrop) {
        // 输了：掉光背包 + 金币，并把掉落报给对手
        applyPvpDefeat(msg.foeSid || msg.from, msg.foeName || '道友');
      } else {
        endPvpDuel(msg);
      }
      return true;
    }
    if (msg.type === 'act:pvpLoot') {
      addActivityLootPile(msg);
      return true;
    }
    return false;
  }
  function addNetMember(m) {
    if (!m || !m.sid || m.sid === netActSid) return null;
    const member = {
      sid: m.sid, name: m.name || '道友',
      localX: Number(m.x) || 0, drawX: Number(m.x) || 0,   // 层内相对坐标
      hp: typeof m.hp === 'number' ? m.hp : 1,
      level: m.level || 1, realm: m.realm || '', action: m.action || 'idle',
      speed: typeof m.speed === 'number' ? m.speed : 1,        // 移动速度：逃跑判定
      atkSpd: typeof m.atkSpd === 'number' ? m.atkSpd : 1,     // 攻击速度：先手判定
      sect: String(m.sect || ''),                              // 门派 id：同门之间禁止相残
      ts: Date.now()
    };
    netMembers.set(m.sid, member);
    return member;
  }

  /* PvP / 房间同步的坐标锚点：副本 → 本层起点 baseX；灵脉 → 那棵仙人掌的世界坐标。
   *   两边都用「相对锚点的偏移」当层内坐标，战斗站位 / 掉落位置才对得上。 */
  function roomAnchorWorldX() {
    if (activityRun) return activityRun.baseX;
    if (sectScene) return sectScene.baseX;
    if (lingNode) return cameraX() + lingAnchorScreenX();
    return player.worldX;
  }

  /* ============================================================
   * 副本内真人切磋（PvP）
   *   规则：点其他真人 = 强行开战；先手由双方**攻击速度**决定（逃跑才看移动速度）；
   *         败者背包里的东西（含装备）与身上金币全部掉在原地，
   *         胜者能捡（掉落在胜者客户端生成"战利品堆"）。
   *         已经穿戴在身上的装备不掉。
     *   结算：由发起方客户端打一遍（对手按同步过来的等级 / 境界 / 攻速还原），
   *         结果回传服务器转发；败者把实际掉落报给胜者。
   * ============================================================ */
  /* 一次正式决斗：
   *   pending（我锁定了别人，等他点"进入战斗 / 逃跑"）
   *   collect（双方把属性/装备上报给服务端）
   *   fight  （服务端记账血量与回合，两边同时开打）
   */
  let pvpDuel = null;      // 正在进行的决斗 { id, foeName, foe:{...}, myTurn, myHp, foeHp, noDrop }
  let pvpPending = null;   // 我已发出锁定：{ duelId, sid, name }
  let pvpOffered = null;   // 有人锁定我，等我在弹窗里选：{ duelId, fromName, fromRealm, fromLevel, fromSpeed }
  let pvpForcedDamage = 0; // 调试/测试用：下一次交锋出手固定打出这么多伤害
  let pvpFoeAtkT = 0;      // 交锋中"对方出手"的动作计时（只在对方出手时播攻击帧）
  window.__debugPvpForceDamage = function (n) {
    pvpForcedDamage = Math.max(0, Math.round(Number(n) || 0));
    return pvpForcedDamage;
  };
  // 调试/测试用：手动灌一条服务器消息进来（验证客户端对各种协议分支的处理）
  window.__debugPvpInject = function (msg) {
    if (!msg || !msg.type) return false;
    return onActivityNetMessage(msg) === true;
  };
  let pvpTurnMs = 20000;      // 单回合时限（服务端会在 pvpStart 里给）
  let pvpTurnClock = 0;       // 本回合已过去的秒数（本地显示用）
  let pvpReplyClock = 0;      // 锁定弹窗倒计时（本地显示用）
  const pvpReplyMs = 10000;   // 被锁定方 10 秒内必须选择

  /* 两处倒计时的本地显示：锁定弹窗 / 本回合剩余时间 */
  function updatePvpClocks(dt) {
    if (pvpFoeAtkT > 0) pvpFoeAtkT = Math.max(0, pvpFoeAtkT - dt);
    if (pvpOffered && ui.duelPopup && !ui.duelPopup.classList.contains('hidden')) {
      pvpReplyClock += dt;
      const left = Math.max(0, Math.ceil(pvpReplyMs / 1000 - pvpReplyClock));
      if (ui.duelCountdown) {
        ui.duelCountdown.textContent = left > 0
          ? (left + ' 秒内不选择 → 强制开战（由发起方先手）')
          : '倒计时结束：正在强制开战……';
      }
    }
    if (pvpDuel && gameState === 'battle' && ui.pvpTurnRow) {
      pvpTurnClock += dt;
      const left = Math.max(0, Math.ceil(pvpTurnMs / 1000 - pvpTurnClock));
      ui.pvpTurnRow.classList.remove('hidden', 'mine', 'urgent');
      if (pvpDuel.myTurn) {
        ui.pvpTurnRow.classList.add('mine');
        if (left <= 5) ui.pvpTurnRow.classList.add('urgent');
        ui.pvpTurnRow.textContent = '你的回合 · 剩 ' + left + ' 秒（超时自动跳过）';
      } else {
        ui.pvpTurnRow.textContent = '对方回合 · 剩 ' + left + ' 秒';
      }
    } else if (ui.pvpTurnRow && !ui.pvpTurnRow.classList.contains('hidden')) {
      ui.pvpTurnRow.classList.add('hidden');
    }
  }

  // 点真人 → 神识锁定对方（对方会弹窗选择"进入战斗 / 逃跑"）
  function requestPvpDuel(m) {
    const run = activityRun;
    if (!m || !netActRoom) return false;
    if (!run && netRoomKind !== 'ling') return false;   // 只在副本 / 灵脉这类公共房间里发起
    if (gameState !== 'walk') return false;
    if (!netChatReady()) {
      showToast('未联网：切磋需要连上服务器才能发起。');
      return false;
    }
    if (pvpDuel) { showToast('你正在与「道友·' + pvpDuel.foeName + '」交锋中。'); return false; }
    if (pvpPending) { showToast('已经在等「道友·' + pvpPending.name + '」回应了。'); return false; }
    /* 门规：同门弟子之间不能互相神识锁定（偷袭 / 抢灵脉）。
     *   这里先在本地拦一道；服务端 act:challenge 里还有同样的校验兜底。 */
    if (netSameSect(m)) {
      showToast('「道友·' + m.name + '」与你是同门弟子，门规禁止同门相残。');
      pushBattleLog('【门规】你与「道友·' + m.name + '」同属' + sectName() + '，不可互相神识锁定', 'system');
      return false;
    }
    pvpPending = { duelId: null, sid: m.sid, name: m.name };
    if (netChatReady() && netActRoom) net.chat.send({ type: 'act:challenge', target: m.sid });
    showToast('已用神识锁定「道友·' + m.name + '」，等他选择迎战或逃跑……');
    pushBattleLog('你对「道友·' + m.name + '」发起神识锁定（对方可选择迎战 / 逃跑）', 'player');
    return true;
  }

  // 被锁定的一方在弹窗里做选择
  function openDuelPopup(off) {
    if (!ui.duelPopup) return false;
    /* 开局/新任务的「任务卡」是铺在舞台上的弹层，会压在锁定窗上面把两个按钮挡掉
     *   （以前就是这个问题：被锁的人点不到"进入战斗/逃跑"，只能等倒计时被强拉走）。 */
    closeQuestPopup();
    pvpReplyClock = 0;
    if (ui.duelInfo) {
      ui.duelInfo.textContent = '「道友·' + off.fromName + '」用神识锁定了你' +
        (off.fromRealm ? ('（' + off.fromRealm + '　移速 ' + (off.fromSpeed || 1).toFixed(2) + '）') : '') + '。';
    }
    if (ui.duelCountdown) {
      ui.duelCountdown.textContent = Math.ceil(pvpReplyMs / 1000) +
        ' 秒内不选择 → 强制开战（由发起方先手）';
    }
    ui.duelPopup.classList.remove('hidden');
    return true;
  }
  function closeDuelPopup() {
    if (ui.duelPopup) ui.duelPopup.classList.add('hidden');
  }

  /* 被拉进真人交锋时，把"浏览类"弹窗统一收掉（背包 / 商店 / 图鉴 / 任务卡 …）。
   *   它们都铺满舞台，留着会挡住战斗按钮，而 PvP 每回合只有 20 秒。 */
  function closeBrowsingPopupsForDuel() {
    [[ui.charPopup, closeCharPanel], [ui.gearPopup, closeGearPanel], [ui.shopPopup, closeShop],
      [ui.codexPopup, closeCodex], [ui.gachaPopup, closeGacha], [ui.petPopup, closePetPanel],
      [ui.skillPopup, closeSkillPanel], [ui.questPopup, closeQuestPopup],
      [ui.dailyPopup, closeQuestPanel], [ui.dungeonPopup, closeDungeonPanel],
      [ui.activityPopup, closeActivityPanel], [ui.sectPopup, closeSectPanel]].forEach(function (pair) {
      const box = pair[0];
      if (box && !box.classList.contains('hidden') && typeof pair[1] === 'function') pair[1]();
    });
  }

  function replyPvpDuel(accept) {
    const off = pvpOffered;
    if (!off) { closeDuelPopup(); return false; }
    pvpOffered = null;
    closeDuelPopup();
    if (netChatReady() && netActRoom) {
      net.chat.send({ type: 'act:pvpReply', duelId: off.duelId, accept: !!accept });
    }
    if (accept) {
      showToast('你选择迎战「道友·' + off.fromName + '」——正在交换双方功法……');
      pushBattleLog('你接受了「道友·' + off.fromName + '」的挑战，双方战斗开始', 'enemy');
    } else {
      showToast('你选择避战，脱离了对方的神识锁定。');
      pushBattleLog('你躲开了「道友·' + off.fromName + '」的神识锁定', 'system');
      // 逃跑：往旁边挪一段，顺便给自己一点脱离感
      if (activityRun) {
        const dir = Math.random() < 0.5 ? -1 : 1;
        player.worldX = clamp(player.worldX + dir * 260, activityRun.minX, activityRun.maxX);
      }
    }
    return true;
  }

  // 服务端要属性 / 装备细节 → 把本方这一份报上去
  function sendPvpLoadout(duelId) {
    if (!netChatReady() || !netActRoom) return false;
    const gear = [];
    Object.keys(equipment).forEach(function (k) {
      if (equipment[k] && equipment[k].name) gear.push(equipment[k].name);
    });
    net.chat.send({
      type: 'act:pvpLoadout', duelId: duelId,
      loadout: {
        name: chatNick(),
        realm: stageText(playerLevel()),
        level: playerLevel(),
        maxHp: Math.round(combat.maxHp),
        hp: Math.round(combat.hp),
        atk: Math.round(derived.flatAtk),
        armor: Math.round(derived.armor),
        res: Math.round(derived.res),
        speed: Math.round((derived.speedMult || 1) * 1000) / 1000,
        // 攻击速度：服务端用它决定 PvP 先手（speed 只留给"逃跑判定"）
        atkSpd: Math.round(attackSpeedMult() * 1000) / 1000,
        weapon: (equipment.weapon && equipment.weapon.name) || '',
        gear: gear,
        skills: knownSkills.map(function (s) { return s.name; }).slice(0, 12),
        pets: alivePets().length
      }
    });
    return true;
  }

  /* 服务端把双方属性都拿到手 → 正式开打：两边同时被拉进战斗 */
  function startPvpDuel(msg) {
    if (!activityRun && netRoomKind !== 'ling') return false;
    /* 被拉进交锋前，把挡在前面的"浏览类"窗口收掉：
     *   它们都是铺满舞台的遮罩，留着会挡住 攻击 / 防御 / 法术，
     *   而真人交锋每回合只有 20 秒 —— 点不到按钮就等于白送一回合。 */
    closeBrowsingPopupsForDuel();
    const foe = msg.foe || {};
    const localX = (function () {
      const m = netMembers.get(pvpPending && pvpPending.sid);
      if (m) return (m.drawX === undefined ? m.localX : m.drawX);
      return 420;
    })();
    const enemyLevel = clamp(foe.level || playerLevel(), 1, MAX_STAGE);
    const built = buildEnemy(enemyLevel, 'elite', { worldX: roomAnchorWorldX() + localX, cave: false });
    built.sprite = 'player';
    built.playerModel = true;
    built.name = '道友·' + (foe.name || '道友');
    built.kindName = '道友';
    built.maxHp = Math.max(1, Math.round(foe.maxHp || built.maxHp));
    built.hp = Math.max(1, Math.round((msg.hp && msg.hp.foe) || built.maxHp));
    built.atk = Math.max(1, Math.round(foe.atk || built.atk));
    // 对手的"战斗速度"= 它的攻击速度（先手判定用；老版本没带 atkSpd 就退回移速）
    built.combatSpeed = foe.atkSpd || foe.speed || 1;
    /* 真人对手：不是"精英怪"，不套精英光环、不显示"技能：xxx"标牌 */
    built.pvpFoe = true;
    built.elite = false;
    built.legend = false;
    built.skill = null;
    built.skills = null;
    built.pvpRestPose = randInt(0, PLAYER_REST_FRAMES - 1);   // 对方站着时固定用一张休息姿态
    pvpFoeAtkT = 0;
    pvpDuel = {
      id: msg.duelId, foeName: foe.name || '道友', foe: foe, foeSid: msg.foeSid || null,
      myHp: Math.max(1, Math.round((msg.hp && msg.hp.me) || combat.hp)),
      foeHp: built.hp,
      maxHp: Math.max(1, Math.round(foe.maxHp || built.maxHp)),
      myTurn: msg.first === 'me', noDrop: false, over: false, forced: !!msg.forced
    };
    pvpTurnMs = Math.max(5000, Math.round(msg.turnMs || 20000));
    pvpTurnClock = 0;
    activeEnemy = built;
    battleGroup.length = 0;
    battleGroup.push(built);
    activityStopMove();
    activityCancelPick();
    startBattle();
    battleTurn = pvpDuel.myTurn ? 'player' : 'enemy';
    if (pvpDuel.myTurn) { enemyTurnTimer = 0; playerIdleTimer = 10; }
    else { enemyTurnTimer = 0; playerIdleTimer = 0; }
    const foeDesc = '道友·' + pvpDuel.foeName + '（' + (foe.realm || '境界未知') +
      '　攻速 ' + (foe.atkSpd || 1).toFixed(2) +
      '　移速 ' + (foe.speed || 1).toFixed(2) + '　生命 ' + pvpDuel.maxHp + '）';
    setBattleLog('战斗开始：' + foeDesc + '　先手：' + (pvpDuel.myTurn ? '你' : '对方') +
      '　（每回合 ' + Math.round(pvpTurnMs / 1000) + ' 秒）');
    pushBattleLog('与「道友·' + pvpDuel.foeName + '」的战斗开始（先手：' +
      (pvpDuel.myTurn ? '你' : '对方') + (msg.forced ? '　※ 对方超时未回应，强制由你方先手' : '（攻速决定）') + '）', 'player');
    showToast(pvpDuel.myTurn ? '战斗开始——你先手！' : '战斗开始——对方先手，等他出手。');
    updatePvpControlState();
    return true;
  }

  // 决斗中：只有轮到自己出手时按钮才可用
  function updatePvpControlState() {
    if (!pvpDuel || !ui.btnAttack) return;
    ui.btnAttack.disabled = !pvpDuel.myTurn;
    if (ui.btnDefend) ui.btnDefend.disabled = !pvpDuel.myTurn;
    if (ui.btnSpell) ui.btnSpell.disabled = !pvpDuel.myTurn;
  }

  /* 决斗里的一次普攻：伤害沿用野外普攻那套公式，结果交给服务端记账 */
  function pvpPlayerAttack() {
    const d = pvpDuel;
    if (!d || !activeEnemy || gameState !== 'battle') return false;
    if (!d.myTurn) { showToast('还没轮到你出手。'); return false; }
    resetPlayerIdleTimer();
    playPlayerAttack();
    startAttackFx(activeEnemy.x);
    const baseDmg = Math.max(1, Math.round(
      (randInt(23 + derived.flatAtk, 34 + derived.flatAtk) + Math.floor(activeEnemy.level / 3)) *
      gemDamageMult('attack')
    ));
    const hit = rollPlayerDamage(baseDmg);
    let dmg = hit.dmg;
    if (derived.atkSpeed > 0 && Math.random() * 100 < derived.atkSpeed) dmg += Math.max(1, Math.round(dmg * 0.6));
    if (pvpForcedDamage > 0) { dmg = pvpForcedDamage; pvpForcedDamage = 0; }   // 测试钩子
    d.myTurn = false;
    pvpTurnClock = 0;
    updatePvpControlState();
    addDamagePopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 178, dmg, hit.crit ? '#ff4d4d' : '#ffd07a');
    setBattleLog('你一击命中「道友·' + d.foeName + '」' + (hit.crit ? '（暴击）' : '') +
      '，正在等对方的反应……');
    pushBattleLog('你出手：对「道友·' + d.foeName + '」造成 ' + dmg + ' 点伤害（等待服务端结算）', 'player');
    if (netChatReady() && netActRoom) {
      net.chat.send({ type: 'act:pvpAct', duelId: d.id, kind: 'attack', dmg: dmg });
    }
    return true;
  }

 function pvpGuard() {
    const d = pvpDuel;
    if (!d) return false;
    if (!d.myTurn) { showToast('还没轮到你出手。'); return false; }
    d.myTurn = false;
    pvpTurnClock = 0;
    updatePvpControlState();
    playPlayerAttack();    // 防御也是一次"动作"：播一次出手帧
    /* 和野外防御保持一致：同样把护盾特效放出来。
     *   以前交锋里的防御只写了一句战报、特效不放，看着像"防御没生效"。
     *   defending 会在下一次轮到自己时清掉（见 act:pvpTurn / act:pvpState）。 */
    defending = true;
    startDefendFx();
    addTextPopup(player.worldX - cameraX() - 86, GROUND_Y - 150, '防御', '#9fd4ff');
    setBattleLog('你摆出防御姿态（下一次受到伤害 -35%），本回合结束，等待对方出手……');
    if (netChatReady() && netActRoom) net.chat.send({ type: 'act:pvpAct', duelId: d.id, kind: 'guard' });
    return true;
  }

  /* 交锋中施法：伤害沿用野外施法那套公式，算完交给服务端记账；用法术同样结束本回合 */
  function pvpCastSkill(skill) {
    const d = pvpDuel;
    if (!d || !skill || !activeEnemy) return false;
    if (!d.myTurn) { setBattleLog('还没轮到你出手。'); return false; }
    if (skill.id === 'summonPet') { setBattleLog('「召唤宠物」由法杖被动生效。'); return false; }
    if (skill.type === 'toggle') { showToast('持续类技能在交锋中暂不可用。'); return false; }
    if (skill.id === 'powerShot') { showToast('蓄力类技能在交锋中暂不可用（下一版）。'); return false; }
    if (combat.mp < skill.cost) { setBattleLog('灵力不足，无法施展「' + skill.name + '」。'); updateBattleHud(); return false; }
    combat.mp -= skill.cost;
    resetPlayerIdleTimer();
    playPlayerAttack();
    // 治疗类：回血 + 结束回合
    if (skill.type === 'heal') {
      const heal = Math.max(1, Math.round(combat.maxHp * 0.35));
      combat.hp = Math.min(combat.maxHp, combat.hp + heal);
      d.myHp = Math.round(combat.hp);
      d.myTurn = false;
      pvpTurnClock = 0;
      updatePvpControlState();
      addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 160, heal, '#6dff8d', true);
      setBattleLog('你施展「' + skill.name + '」，恢复 ' + heal + ' 点生命，本回合结束。');
      pushBattleLog('交锋中施放「' + skill.name + '」：恢复 ' + heal + ' 点生命', 'player');
      if (netChatReady() && netActRoom) net.chat.send({ type: 'act:pvpAct', duelId: d.id, kind: 'heal', hp: d.myHp });
      updateBattleHud();
      return true;
    }
    // 召唤类：本地召唤（跟随后面），同样结束回合
    if (skill.type === 'summon') {
      const okSummon = castSkillSummonOnly(skill);
      if (okSummon) {
        d.myTurn = false;
        pvpTurnClock = 0;
        updatePvpControlState();
        setBattleLog('你召唤出伙伴助阵（交锋中宠物暂不参与伤害），本回合结束。');
        if (netChatReady() && netActRoom) net.chat.send({ type: 'act:pvpAct', duelId: d.id, kind: 'guard', passive: true });
      }
      return okSummon;
    }
    if (!skill.dmg) { showToast('这个技能在交锋中用不上。'); combat.mp = Math.min(combat.maxMp, combat.mp + skill.cost); return false; }
    // 伤害类：和野外同一个公式
    const attrSpell = skill.type === 'spell' ? (derived.spellDmgPct || 0) : 0;
    const mult = (1 + (skillGemSum(skill.id, ['weaponDamage', 'meleeDamage', 'spellDamage']) + attrSpell) / 100) *
      skillDamageLevelMult(skill);
    const supMain = supportDamageMult(skill.id, activeEnemy);
    let total = 0;
    if (skill.id === 'yangClaw') {
      const atkMid = 28.5 + derived.flatAtk;
      const per = Math.max(1, Math.round(atkMid * (skill.atkPct || 15) / 100 * mult * supMain));
      for (let i = 0; i < 6; i++) total += rollPlayerDamage(per).dmg;
    } else {
      const base = Math.max(1, Math.round(
        (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(activeEnemy.level / 3)) * mult * supMain));
      total = rollPlayerDamage(base).dmg;
    }
    if (pvpForcedDamage > 0) { total = pvpForcedDamage; pvpForcedDamage = 0; }   // 测试钩子
    d.myTurn = false;
    pvpTurnClock = 0;
    updatePvpControlState();
    addDamagePopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 178, total, '#ffb34d');
    setBattleLog('你施展「' + skill.name + '」，命中「道友·' + d.foeName + '」' + total + ' 点，等待服务端结算。');
    pushBattleLog('交锋中施放「' + skill.name + '」：对「道友·' + d.foeName + '」造成 ' + total + ' 点伤害', 'player');
    if (netChatReady() && netActRoom) {
      net.chat.send({ type: 'act:pvpAct', duelId: d.id, kind: 'skill', skillName: skill.name, dmg: total });
    }
    updateBattleHud();
    return true;
  }

  // 只做"召唤出宠物"这一件事（复用野外召唤逻辑里与伤害无关的部分）
  function castSkillSummonOnly(skill) {
    const profile = skill.id === 'summonScorpion' ? { sprite: 'scorpio', atkMult: 1.1 }
      : skill.id === 'summonHyena' ? { sprite: 'hyena', atkMult: 1.15 }
      : skill.id === 'summonSnake' ? { sprite: 'snake', atkMult: 1.0 }
      : skill.id === 'summonVulture' ? { sprite: 'vulture', atkMult: 1.05 }
      : skill.id === 'summonBats' ? { sprite: 'vulture', atkMult: 1.3 }
      : { sprite: 'scorpio', atkMult: 1.1 };
    if (alivePets().length >= petCap() && !hasServantGem()) {
      setBattleLog('出战宠物已达上限（' + petCap() + ' 只），无法再召唤。');
      return false;
    }
    addPet({
      name: PET_SPRITES[profile.sprite].name, tier: 'magic', level: petLevelOf('magic'),
      sprite: profile.sprite, source: '召唤技能「' + skill.name + '」', sourceKind: 'skill',
      atkMult: profile.atkMult * skillLevelMult(skill), hpMult: 1, hp: 1, maxHp: 1,
      hurtT: 0, animT: 0, attackT: 0, walkT: rand(0, 10), skillId: skill.id
    });
    refreshPetStats();
    alivePets().forEach(function (p) { if (p.hp < 1) p.hp = p.maxHp; });
    recalcPlayerStats();
    if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
    return true;
  }

  // 决斗结束（服务端判定）
  function endPvpDuel(msg) {
    const d = pvpDuel;
    pvpDuel = null;
    if (!d) return false;
    if (msg && msg.foeHp !== undefined && activeEnemy) activeEnemy.hp = Math.max(0, msg.foeHp);
    /* 交锋结束：把"防御姿态 + 护盾特效"一起收掉。
     *   以前这里只收战斗 UI，交锋里点过防御的话，护盾会一直举着不消失。 */
    clearDefendState();
    if (gameState === 'battle') {
      showBattleUi(false);
      setControls('walk');
      syncMoveButton();
      gameState = 'walk';
      activeEnemy = null;
      battleGroup.length = 0;
    }
    if (msg && msg.iWon) {
      const noDrop = !!msg.noDrop;
      if (!noDrop) {
        pvpKillTotal += 1;                 // 破境任务：击杀真实玩家（脱战不算）
        checkBottleneckTask();
      }
      showToast(noDrop
        ? ('「道友·' + (msg.foeName || d.foeName) + '」脱离了战斗。')
        : ('你赢了！「道友·' + (msg.foeName || d.foeName) + '」的战利品掉在这一层。'));
      pushBattleLog('战斗结束：你击败了「道友·' + (msg.foeName || d.foeName) + '」' +
        (noDrop ? '（对方脱战，没有掉落）' : '，对方的战利品掉在原地'), noDrop ? 'system' : 'reward');
    } else {
      showToast('你在神识交锋中落败……');
      pushBattleLog('战斗结束：你不敌「道友·' + (msg.foeName || d.foeName) + '」', 'enemy');
    }
    updateCaveBar();
    return true;
  }

  // 决斗中转层 / 离开副本都要先把这场收掉
  function abortPvpDuelIfAny(reason) {
    if (!pvpDuel) return false;
    const d = pvpDuel;
    pvpDuel = null;
    if (gameState === 'battle') {
      showBattleUi(false);
      setControls('walk');
      syncMoveButton();
      gameState = 'walk';
      activeEnemy = null;
      battleGroup.length = 0;
    }
    pushBattleLog('你脱离了与「道友·' + d.foeName + '」的交锋（' + (reason || '离开') + '）', 'system');
    return true;
  }

  /* 输给真人（或被真人打倒）：背包里的东西（含装备）与身上金币全掉，已穿戴的装备不掉 */
  function dropAllBagOnDeath() {
    const lost = inventory.slice();
    const lostGold = Math.max(0, Math.round(gold));
    if (!lost.length && !lostGold) return { items: [], gold: 0, name: '' };
    const name = (activityRun && activityRun.def && activityRun.def.name) || '临时副本';
    inventory = [];
    gold = 0;
    updateGold();
    return { items: lost, gold: lostGold, name: name };
  }

  // 把"我输了"和"我掉了什么"回传给对手（对手会在自己那边生成战利品堆）
  function reportPvpLoss(rival, loss, localX) {
    if (!rival || !netChatReady() || !netActRoom) return false;
    if (loss && (loss.items.length || loss.gold)) {
      // 胜负已经由服务端广播过，这里只把"我掉了什么"交给胜者
      net.chat.send({
        type: 'act:pvpDrop', target: rival.sid,
        items: loss.items.map(serializeDropItem), gold: loss.gold, x: Math.round(localX || 0)
      });
    }
    return true;
  }

  // 掉落物跨客户端序列化：去掉本地运行时字段（图片对象 / 缓存）
  function serializeDropItem(it) {
    const out = {};
    if (!it || typeof it !== 'object') return out;
    Object.keys(it).forEach(function (k) {
      const v = it[k];
      if (v === null || v === undefined) return;
      if (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean') out[k] = v;
      else if ((k === 'mods' || k === 'baseMods') && Array.isArray(v)) {
        out[k] = v.filter(function (m) { return m && typeof m === 'object'; }).map(function (m) {
          return { key: m.key, value: m.value, value2: m.value2 || 0, bandIdx: m.bandIdx || 0, locked: !!m.locked };
        });
      } else if (k === 'special' && v && typeof v === 'object') {
        const sp = {};
        Object.keys(v).forEach(function (k2) {
          if (typeof v[k2] === 'number' || typeof v[k2] === 'string') sp[k2] = v[k2];
        });
        out.special = sp;
      }
    });
    return out;
  }

  // 收到别人的掉落：还原成可用物品
  function prepareDropItem(raw) {
    const it = Object.assign({}, raw || {});
    it.id = 'pvp' + (++itemSeq);
    if (!it.type) it.type = 'material';
    if (!it.name) it.name = '无名物品';
    if (it.type === 'equipment') ensureItemIcon(it);
    return it;
  }

  /* 胜者这边：把对方的掉落摆在地上（只有他能看到 / 能捡） */
  function addActivityLootPile(msg) {
    const run = activityRun;
    if (!run && netRoomKind !== 'ling') return false;
    const pile = {
      id: 'spoil' + (++itemSeq),
      kind: 'spoils',
      worldX: roomAnchorWorldX() + (Number(msg.x) || 0),
      items: (Array.isArray(msg.items) ? msg.items : []).map(prepareDropItem),
      gold: Math.max(0, Math.round(Number(msg.gold) || 0)),
      fromName: msg.fromName || '道友',
      t: 0,
      taken: false
    };
    if (run) run.items.push(pile); else lingPvpPiles.push(pile);
    const desc = pile.items.length + ' 件物品' + (pile.gold ? (' + ' + pile.gold + ' 金币') : '');
    showToast('「道友·' + pile.fromName + '」的战利品掉在你旁边（' + desc + '）——点它就能捡走。');
    pushBattleLog('「道友·' + pile.fromName + '」的战利品掉在原地：' + desc + '（点地上的战利品堆拾取）', 'reward');
    return true;
  }

  function takeActivityPile(pile) {
    if (!pile || pile.taken) return false;
    pile.taken = true;
    playSfx('pickup');                  // 拾取音效（真人爆出来的战利品堆）
    let n = 0;
    (pile.items || []).forEach(function (it) {
      it.fromActivity = true;       // 副本里捡到的：副本内阵亡还是会掉（沿用现有规则）
      inventory.push(it);
      n++;
    });
    if (pile.gold) gold += pile.gold;
    updateGold();
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('捡走了「道友·' + pile.fromName + '」的战利品：' + n + ' 件物品' +
      (pile.gold ? (' + ' + pile.gold + ' 金币') : '') + '。');
    pushBattleLog('你拾取了真人「道友·' + pile.fromName + '」的战利品：' + n + ' 件物品' +
      (pile.gold ? (' + ' + pile.gold + ' 金币') : ''), 'reward');
    return true;
  }

  /* 被真人打倒（对方客户端结算后通知过来）：掉光背包与金币，并把掉落报给胜者 */
  function applyPvpDefeat(bySid, byName) {
    if ((!activityRun && netRoomKind !== 'ling') || deathActive) return false;
    pvpDuel = null;
    pvpPending = null;
    closeDuelPopup();
    showToast('你被「道友·' + byName + '」击败，背包物品与金币全部掉在了原地！');
    triggerDeath({ remotePvp: { sid: bySid, name: byName } });
    return true;
  }

  function activityWindowIndex(t) {
    return Math.floor((t + ACTIVITY_TZ_OFFSET_MS) / ACTIVITY_PERIOD_MS);
  }
  function activityWindowStart(t) {
    return activityWindowIndex(t) * ACTIVITY_PERIOD_MS - ACTIVITY_TZ_OFFSET_MS;
  }
  // 固定种子的伪随机：同一时段 → 同一批副本
  function activityRng(seed) {
    let s = (seed >>> 0) * 2654435761 % 4294967296;
    return function () {
      s = (s * 1664525 + 1013904223) % 4294967296;
      return s / 4294967296;
    };
  }
  function currentActivities(t) {
    const now = (typeof t === 'number') ? t : wbNow();
    const start = activityWindowStart(now);
    const rng = activityRng(activityWindowIndex(now));
    const pool = ACTIVITY_DUNGEONS.slice();
    for (let i = pool.length - 1; i > 0; i--) {           // 洗牌
      const j = Math.floor(rng() * (i + 1));
      const tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp;
    }
    const count = 1 + Math.floor(rng() * ACTIVITY_DUNGEONS.length);   // 1~4 个
    const picked = pool.slice(0, Math.min(count, pool.length));
    /* 每个副本在"自己那一格时间"里随机挑一个时刻开启（保证落在本时段内、且开得下 60 分钟）。
     * 这样一整个 12 小时里都可能有副本开着，而不是只在时段开头那一小时有。 */
    const slotLen = ACTIVITY_PERIOD_MS / picked.length;
    const entries = picked.map(function (def, i) {
      const maxOffset = Math.max(0, slotLen - ACTIVITY_LIFETIME_MS);
      const spawnAt = Math.round(start + i * slotLen + rng() * maxOffset);
      return { def: def, spawnedAt: spawnAt, endsAt: spawnAt + ACTIVITY_LIFETIME_MS };
    });
    if (activitySpawnOverride) {
      entries.forEach(function (e) {
        e.spawnedAt = activitySpawnOverride;
        e.endsAt = activitySpawnOverride + ACTIVITY_LIFETIME_MS;
      });
    }
    /* 测试期间"常开"的副本：滚动给一个 60 分钟窗口，永远处于开启状态，
     * 并且不限境界（正式玩法里受排期 + 境界限制）。 */
    activityTestOpenIds().forEach(function (id) {
      const def = ACTIVITY_DUNGEONS.filter(function (d) { return d.id === id; })[0];
      if (!def) return;
      const already = entries.filter(function (e) { return e.def.id === id; })[0];
      const entry = already || { def: def };
      entry.spawnedAt = now - 1000;
      entry.endsAt = now + ACTIVITY_LIFETIME_MS;
      entry.testOpen = true;
      if (!already) entries.push(entry);
    });
    return entries;
  }
  function activityRemainMs(entry, now) {
    return Math.max(0, entry.endsAt - ((typeof now === 'number') ? now : wbNow()));
  }
  function activityActive(entry, now) {
    const t = (typeof now === 'number') ? now : wbNow();
    return t >= entry.spawnedAt && activityRemainMs(entry, t) > 0;
  }
  function activityWaitMs(entry, now) {
    return Math.max(0, entry.spawnedAt - ((typeof now === 'number') ? now : wbNow()));
  }
  // 能不能进：境界不能超过副本对应境界
  function activityGateReason(def) {
    // 测试期"常开"的副本：不限境界（窗口里会标「测试」）
    if (def && activityIsTestOpen(def.id)) return null;
    const stage = playerLevel();
    if (stage > (def.maxStage || 99)) {
      return '你的境界（' + stageName(stage) + '）已超过「' + def.realm + '」，高阶修士进不去低阶秘境';
    }
    return null;
  }
  function activityTimeText(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60), s = total % 60;
    if (m >= 60) return Math.floor(m / 60) + ' 小时 ' + (m % 60) + ' 分';
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }
  /* PVP 活动副本的进入冷却：还差多久才能再进（毫秒） */
  function activityCooldownLeft(now) {
    const t = (typeof now === 'number') ? now : wbNow();
    if (!activityLastEnterAt) return 0;
    return Math.max(0, activityLastEnterAt + ACTIVITY_ENTER_COOLDOWN_MS - t);
  }
  function activityCooldownReason(now) {
    const left = activityCooldownLeft(now);
    if (left <= 0) return null;
    return '刚进过副本，还需等待 ' + activityTimeText(left) + ' 才能再次进入（每次进入间隔 1 小时）';
  }

  function activityClockLabel(t) {
    const d = new Date(t);
    const h = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return h + ':' + mm;
  }

  function openActivityPanel() {
    closeCodex();
    closeGacha();
    closeDungeonPanel();
    toggleActivityHelp(false);
    renderActivityPanel();
    ui.activityPopup.classList.remove('hidden');
  }
  function closeActivityPanel() {
    toggleActivityHelp(false);
    if (ui.activityPopup) ui.activityPopup.classList.add('hidden');
  }

  function toggleActivityHelp(show) {
    if (!ui.activityHelp) return false;
    const on = show === undefined ? ui.activityHelp.classList.contains('hidden') : !!show;
    ui.activityHelp.classList.toggle('hidden', !on);
    if (ui.activityHelpBtn) ui.activityHelpBtn.classList.toggle('on', on);
    return on;
  }

  function renderActivityPanel() {
    if (!ui.activityList) return;
    const now = wbNow();
    const list = currentActivities(now);
    const start = activityWindowStart(now);
    const end = start + ACTIVITY_PERIOD_MS;
    activityRenderedKey = activityWindowIndex(now);
    if (ui.activityWindow) {
      ui.activityWindow.textContent = activityClockLabel(start) + ' ~ ' + activityClockLabel(end);
    }
    if (ui.activityNext) {
      ui.activityNext.textContent = activityTimeText(end - now);
    }
    const enterable = list.filter(function (e) {
      return activityActive(e, now) && !activityGateReason(e.def) && activityCooldownLeft(now) <= 0;
    }).length;
    if (ui.activityCount) ui.activityCount.textContent = String(enterable);

    ui.activityList.textContent = '';
    const cdLeft = activityCooldownLeft(now);
    list.forEach(function (entry) {
      const def = entry.def;
      const left = activityRemainMs(entry, now);
      const wait = activityWaitMs(entry, now);
      const open = wait <= 0 && left > 0;
      const reason = activityGateReason(def);
      const row = document.createElement('div');
      row.className = 'activityRow' + (open ? '' : ' activityRowOver') +
        (reason ? ' activityRowLocked' : '');
      row.setAttribute('data-activity', def.id);
      row.setAttribute('data-state', open ? 'open' : (wait > 0 ? 'wait' : 'over'));
      row.style.setProperty('--actColor', def.color || '#ffd76a');

      const head = document.createElement('div');
      head.className = 'activityHead';
      const name = document.createElement('b');
      name.className = 'activityName';
      name.textContent = def.name;
      const tag = document.createElement('span');
      tag.className = 'activityTag';
      tag.textContent = def.realm;
      const clock = document.createElement('span');
      clock.className = 'activityClock';
      clock.textContent = open ? ('剩余 ' + activityTimeText(left))
        : (wait > 0 ? (activityTimeText(wait) + ' 后开启') : '已结束');
      const isTestOpen = activityIsTestOpen(def.id);
      if (isTestOpen) row.classList.add('activityRowTest');
      head.appendChild(name);
      head.appendChild(tag);
      // 红色 PVP 标记：这是玩家互斗的活动
      const pvpChip = document.createElement('span');
      pvpChip.className = 'activityPvpChip';
      pvpChip.textContent = 'PVP';
      head.appendChild(pvpChip);
      if (isTestOpen) {
        const testTag = document.createElement('span');
        testTag.className = 'activityTestTag';
        testTag.textContent = '测试 · 常开';
        head.appendChild(testTag);
      }
      head.appendChild(clock);

      const note = document.createElement('p');
      note.className = 'activityNote';
      note.textContent = def.note + ' · 敌人 ' + def.enemies + ' 个 · 公共地图' +
        ' · 进入消耗 ' + ACTIVITY_ENTER_COST + ' 点活力 · 每次进入后冷却 ' +
        (ACTIVITY_ENTER_COOLDOWN_MS / 60000) + ' 分钟' +
        (isTestOpen ? ' · 测试期间常开、不限境界' : '');

      const act = document.createElement('div');
      act.className = 'activityAct';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'activityEnter';
      if (!open) {
        btn.textContent = wait > 0 ? '未开启' : '已结束';
        btn.disabled = true;
      } else if (cdLeft > 0) {
        btn.textContent = '冷却中';
        btn.disabled = true;
      } else if (vitality < ACTIVITY_ENTER_COST) {
        btn.textContent = '活力不足';
        btn.disabled = true;
      } else if (reason) {
        btn.textContent = '境界不符';
        btn.disabled = true;
      } else {
        btn.textContent = '进入';
        btn.addEventListener('click', function () { enterActivity(def); });
      }
      act.appendChild(btn);
      if (cdLeft > 0) {
        const cdWhy = document.createElement('span');
        cdWhy.className = 'activityWhy';
        cdWhy.textContent = activityCooldownReason(now);
        act.appendChild(cdWhy);
      } else if (vitality < ACTIVITY_ENTER_COST) {
        const vWhy = document.createElement('span');
        vWhy.className = 'activityWhy';
        vWhy.textContent = '进入需要 ' + ACTIVITY_ENTER_COST + ' 点活力（当前 ' + vitality + ' 点）';
        act.appendChild(vWhy);
      } else if (reason) {
        const why = document.createElement('span');
        why.className = 'activityWhy';
        why.textContent = reason;
        act.appendChild(why);
      }
      row.appendChild(head);
      row.appendChild(note);
      row.appendChild(act);
      ui.activityList.appendChild(row);
    });
  }

  // 每秒刷新窗口里的倒计时；时段换了就整块重建
  function updateActivityClock(dt) {
    if (!ui.activityPopup || ui.activityPopup.classList.contains('hidden')) return;
    activityClockAcc += dt;
    if (activityClockAcc < 1) return;
    activityClockAcc = 0;
    const now = wbNow();
    if (activityRenderedKey !== activityWindowIndex(now)) { renderActivityPanel(); return; }
    if (ui.activityNext) ui.activityNext.textContent = activityTimeText(activityWindowStart(now) + ACTIVITY_PERIOD_MS - now);
    let stateChanged = false;
    Array.prototype.forEach.call(ui.activityList.querySelectorAll('.activityRow'), function (row) {
      const id = row.getAttribute('data-activity');
      const entry = currentActivities(now).filter(function (e) { return e.def.id === id; })[0];
      const clock = row.querySelector('.activityClock');
      if (!entry || !clock) return;
      const wait = activityWaitMs(entry, now);
      const left = activityRemainMs(entry, now);
      const state = wait > 0 ? 'wait' : (left > 0 ? 'open' : 'over');
      // 副本从"未开启"变"开启"（或结束）时整块重画，按钮状态才会跟着变
      if (row.getAttribute('data-state') !== state) { stateChanged = true; return; }
      clock.textContent = state === 'open' ? ('剩余 ' + activityTimeText(left))
        : (state === 'wait' ? (activityTimeText(wait) + ' 后开启') : '已结束');
    });
    if (stateChanged) renderActivityPanel();
  }

  /* ---------- 临时公共副本：3 层 PVP 拾取地图 ----------
   *   场景里没有 BOSS、没有虚影，站着的都是"其他玩家"（本作暂用机器人代替，
   *   但用实体立绘画，不是半透明海市蜃楼），以及本副本境界的怪 / 材料 / 丹药 / 装备。
   *   玩法要点：
   *     · 点击屏幕左 / 右半边 → 向左 / 向右走（不再是"移动"按钮）
   *     · 材料、丹药：点一下直接进背包；装备：要站在上面 10 秒（进度条），
   *       这 10 秒里如果被其他玩家发现，有可能被拉进战斗
   *     · 点击其他玩家 → 强行把他拉进战斗（PVP）
   *     · 每个副本 3 层，每层随机落点，通往下一层的传送门随机出现
   * ------------------------------------------------------------ */
  const ACT_FLOOR_W = 2200;        // 每层可走范围（px）
  const ACT_PICK_SEC = 10;         // 装备需要停留的秒数
  const ACT_MOVE_SPEED = 230;      // 点击左右移动的速度
  const ACT_CLICK_R = 32;          // 点物品 / 点玩家的判定半径（屏幕 px，对准图标才触发）
  const ACT_PORTAL_CLICK_R = 60;   // 传送门的判定半径
  const ACT_PORTAL_HIT = 64;       // 走到传送门多近就自动进下一层
  /* 点一下走多远：点一次只挪一小段，按住不放才一直走（和手游一致）。
   * 按住超过这个距离就等于"长按"，松手就停，不再补那一步。 */
  const ACT_TAP_STEP = 120;
  const CORPSE_CLEAR = 130;        // 怪周围给尸体留出的空地半径（物品不许压在这里）
  const ACTIVITY_BOT_NAMES = ['凌霜', '铁牛', '余七', '白鹭', '沈砂', '阿罗', '念青山', '陆青', '照夜', '木三'];

  // 本副本境界的"最顶级"装备：按 装备等级 = 该境界的等级 生成
  function activityGearItem() {
    const def = activityRun ? activityRun.def : ACTIVITY_DUNGEONS[0];
    const lv = Math.min(MAX_STAGE, def.enemyStage || playerLevel());
    const rarity = Math.random() < 0.3 ? 'legend' : 'rare';
    return makeEquipment(lv, rarity);
  }
  // 入侵材料：裂隙 / 灾变 / 迷雾 / 驱灵 / 深渊 / 精华 / 流亡者
  function makeInvasionMaterial(def) {
    const d = def || INVASION_MATERIALS[randInt(0, INVASION_MATERIALS.length - 1)];
    return {
      id: 'inv' + (++itemSeq),
      type: 'material',
      materialId: d.id,
      name: d.name,
      icon: d.icon,
      detail: d.detail,
      rarityName: '材料',
      rarityColor: d.color
    };
  }
  /* 每日副本的"掉落物"（能卖的）：深渊入侵 = 深渊战利品，迷雾入侵 = 僵尸王掉落物。
   *   清单按副本 def 的 loot: '<key>' 去 DUNGEON_LOOT 里取（见 defs.js）。 */
  function dungeonLootSet(def) {
    const key = def && def.loot;
    return (key && DUNGEON_LOOT[key]) ? DUNGEON_LOOT[key] : null;
  }
  function makeDungeonLootItem(def) {
    const set = dungeonLootSet(def);
    if (!set || !set.length) return null;
    const d = set[randInt(0, set.length - 1)];
    return {
      id: 'loot' + (++itemSeq),
      type: 'material',
      lootId: d.id,               // 属于哪条掉落物
      lootSet: def.loot,          // 来自哪张副本的清单
      name: d.name,
      icon: d.icon,
      iconArt: d.art,             // 走 itemIconUrl 的美术图标分支
      iconArtSmooth: true,        // 高清插画：平滑缩放（不要像素化）
      sellGold: d.sell,           // 背包里可以直接「出售」
      detail: d.detail,
      rarityName: '战利品',
      rarityColor: d.color
    };
  }
  // 当前这场副本有没有配掉落物清单
  function invasionHasLoot(def) { return !!dungeonLootSet(def); }

  /* ============================================================
   * 聊天表情包（贴纸）
   *   来源：迷雾副本的关卡BOSS「僵尸王」——击杀时 0.1% 掉一枚（见 STICKER_DROP_PCT）
   *   拿到后是背包里的一件道具，点「使用/激活」把它记进 chatStickers（随档保存）；
   *   只有激活过的，才会出现在聊天表情栏里、才能发出去。
   * ============================================================ */
  const STICKER_DROP_PCT = 0.1;         // 掉落几率（%）：0.1% = 千分之一
  let stickerDropPctOverride = null;    // 调试/测试用：临时改掉落率（null = 用上面的 0.1%）
  let chatStickers = {};                // stickerId -> true（已激活）
  function stickerDropPct() {
    return stickerDropPctOverride === null ? STICKER_DROP_PCT : stickerDropPctOverride;
  }
  function stickerDef(id) {
    return CHAT_STICKERS.filter(function (s) { return s.id === id; })[0] || null;
  }
  function stickerByToken(text) {
    const t = String(text || '');
    return CHAT_STICKERS.filter(function (s) { return s.token === t; })[0] || null;
  }
  function stickerUnlocked(id) { return !!chatStickers[id]; }
  /* 把"没激活的表情包 token"从要发出去的文本里剔掉（激活过的保留） */
  function stripLockedStickers(text) {
    let out = String(text == null ? '' : text);
    CHAT_STICKERS.forEach(function (s) {
      if (chatStickers[s.id]) return;
      if (out.indexOf(s.token) >= 0) out = out.split(s.token).join('');
    });
    return out;
  }
  function stickerUnlockedCount() {
    return CHAT_STICKERS.filter(function (s) { return chatStickers[s.id]; }).length;
  }
  // 掉落物：背包里那件「表情包」
  function makeStickerItem(def) {
    const s = def || CHAT_STICKERS[randInt(0, CHAT_STICKERS.length - 1)];
    return {
      id: 'stk' + (++itemSeq),
      type: 'sticker',
      stickerId: s.id,
      name: s.name,
      icon: '表',
      iconArt: s.art,
      iconArtSmooth: true,
      detail: '聊天表情包：在背包里点「使用/激活」，之后聊天窗口的表情栏里就能发送它了。（' + s.from + '）',
      rarityName: '表情包',
      rarityColor: '#b9a6ff'
    };
  }
  /* 使用 / 激活一枚表情包 */
  function useStickerItem(item) {
    if (!item || item.type !== 'sticker') return false;
    const s = stickerDef(item.stickerId);
    if (!s) return false;
    if (chatStickers[s.id]) {
      showToast('「' + s.name + '」已经激活过了，聊天表情栏里就能发。');
      return false;
    }
    chatStickers[s.id] = true;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    buildChatEmojiPanel(true);          // 面板重建：这一枚立刻能发
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    hideBagMenu();
    updateGold();
    saveGame();
    showToast('★ 已激活聊天表情「' + s.name + '」：聊天窗口的表情栏里可以发送了。');
    pushBattleLog('【表情包】激活「' + s.name + '」（已激活 ' + stickerUnlockedCount() + '/' +
      CHAT_STICKERS.length + '）', 'reward');
    return true;
  }
  /* 关卡BOSS 击杀：极小几率掉一枚还没激活的表情包（全都激活了就不再掉） */
  function rollStickerDrop() {
    const pool = CHAT_STICKERS.filter(function (s) { return !chatStickers[s.id]; });
    if (!pool.length) return null;
    if (Math.random() * 100 >= stickerDropPct()) return null;
    return pool[randInt(0, pool.length - 1)];
  }
  /* 临时副本里的普通材料：
   * 绝大部分是各种"入侵材料"；少量回血药瓶 / 主动技能石。
   * 天地灵物是极品，不在这里出现（只在第 3 层固定出 1 个）。 */
  function activityMaterialItem() {
    const roll = Math.random();
    if (roll < 0.88) return makeInvasionMaterial();
    if (roll < 0.96) return makePotion(Math.min(MAX_STAGE, (activityRun ? activityRun.def.enemyStage : playerLevel())));
    return makeActiveSkillStone();
  }
  function activityItemKindName(item) {
    if (item.type === 'spiritItem') return '丹药 · 天地灵物';
    if (item.type === 'potion') return '丹药 · 回血药瓶';
    if (item.type === 'skillStone') return '材料 · 主动技能石';
    if (item.type === 'material') return '材料 · ' + item.name;
    return '装备';
  }

  // 布置一层：随机落点 + 遍地物品 + 其他玩家 + 本境界的怪 + 随机位置的传送门
  /* 这一层用什么种子：时段 + 副本 + 层数 → 全服所有人算出的是同一张地图
   * （物品 / 怪的坐标、种类、传送门位置都一样；玩家自己的落点仍然是随机的） */
  function activityFloorRng(floor) {
    const key = activityWindowIndex(wbNow()) + ':' + (activityRun ? activityRun.def.id : '') + ':' + floor;
    let h = 2166136261 >>> 0;
    for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
    return activityRng(h);
  }

  function activityBuildFloor(floor) {
    const def = activityRun.def;
    const baseX = player.worldX;
    const lrng = activityFloorRng(floor);
    const lInt = function (a, b) { return a + Math.floor(lrng() * (b - a + 1)); };
    const lRand = function (a, b) { return a + lrng() * (b - a); };
    activityRun.floor = floor;
    activityRun.baseX = baseX;
    activityRun.minX = baseX - 260;
    activityRun.maxX = baseX + ACT_FLOOR_W;
    activityRun.items = [];
    activityRun.bots = [];
    activityRun.monsters = [];
    activityRun.pick = null;
    activityRun.moveDir = 0;
    activityRun.tapLeft = 0;        // 点一下还要走完的剩余距离
    activityRun.holding = false;    // 手指/鼠标是不是还按着
    activityRun.pressDist = 0;      // 这次按住已经走了多远

    /* 传送门先把位置定下来，地上的物品随后全部避开它的判定圈：
     * 以前两者的判定圈会重叠——点传送门会先摸到旁边的装备 / 材料，
     * 第 3 层那个唯一的天地灵物甚至会被"点传送门"顺手捡走。 */
    const portalX = baseX + ACT_FLOOR_W * (0.45 + lrng() * 0.5);
    activityRun.portal = { x: portalX, nextFloor: floor + 1 };
    const PORTAL_CLEAR = ACT_PORTAL_CLICK_R + ACT_CLICK_R + 24;   // 物品离传送门的最小间距
    const ITEM_LO = baseX + 80, ITEM_HI = baseX + ACT_FLOOR_W - 60;
    const clearOfPortal = function (x) {
      if (Math.abs(x - portalX) >= PORTAL_CLEAR) return x;
      const side = x <= portalX ? -1 : 1;
      let out = portalX + side * PORTAL_CLEAR;
      if (out < ITEM_LO || out > ITEM_HI) out = portalX - side * PORTAL_CLEAR;
      return clamp(out, ITEM_LO, ITEM_HI);
    };

    // ① 装备（每层 5~8 件，随处可见；越深的层越多越好）
    const gearN = lInt(5, 8) + (floor - 1);
    for (let i = 0; i < gearN; i++) {
      activityRun.items.push({
        id: 'ag' + (++itemSeq),
        kind: 'gear',
        item: activityGearItem(),
        worldX: clearOfPortal(baseX + 220 + lrng() * (ACT_FLOOR_W - 320)),
        taken: false
      });
    }
    // ② 材料 / 丹药（每层 4~7 个，点一下就拿）
    const matN = lInt(4, 7) + (floor - 1);
    for (let i = 0; i < matN; i++) {
      activityRun.items.push({
        id: 'am' + (++itemSeq),
        kind: 'mat',
        item: activityMaterialItem(),
        worldX: clearOfPortal(baseX + 160 + lrng() * (ACT_FLOOR_W - 240)),
        taken: false
      });
    }
    /* ③ 天地灵物：极品材料，只在第 3 层出现，整个副本只出 1 个（保证唯一性）。
     *    落点用"离传送门和别的物品都最远"的候选，免得被压在别的图标底下点不到。 */
    if (floor >= 3 && !activityRun.spiritTaken) {
      let spiritX = baseX + ACT_FLOOR_W * 0.72, spiritGap = -1;
      for (let i = 0; i < 48; i++) {
        const cand = clearOfPortal(baseX + 140 + lrng() * (ACT_FLOOR_W - 220));
        let gap = Math.abs(cand - portalX);
        activityRun.items.forEach(function (it) { gap = Math.min(gap, Math.abs(it.worldX - cand)); });
        if (gap > spiritGap) { spiritGap = gap; spiritX = cand; }
      }
      activityRun.items.push({
        id: 'asp' + (++itemSeq),
        kind: 'spirit',
        item: makeSpiritItem(),
        worldX: spiritX,
        taken: false
      });
    }
    // ③ 其他玩家（2~4 个，机器人；真人由服务器广播过来）
    const botN = lInt(2, 4);
    for (let i = 0; i < botN; i++) {
      activityRun.bots.push({
        id: 'ab' + i + '_' + (++itemSeq),
        name: ACTIVITY_BOT_NAMES[lInt(0, ACTIVITY_BOT_NAMES.length - 1)],
        worldX: baseX + 320 + lrng() * (ACT_FLOOR_W - 420),
        facing: lrng() < 0.5 ? -1 : 1,
        walkT: lRand(0, 6),
        phase: lRand(0, Math.PI * 2),
        dir: lrng() < 0.5 ? -1 : 1,
        tier: lrng() < 0.25 ? 'elite' : 'magic'
      });
    }
    // ④ 本境界的怪：等级按副本境界，属性 1~3 倍
    const monN = lInt(3, 5) + (floor - 1);
    for (let i = 0; i < monN; i++) {
      const tier = def.tiers[lInt(0, def.tiers.length - 1)];
      const mult = Math.round(lRand(1, 3) * 100) / 100;
      activityRun.monsters.push({
        id: 'ak' + i + '_' + (++itemSeq),
        level: clamp(def.enemyStage || playerLevel(), 1, MAX_STAGE),
        tier: tier,
        mult: mult,
        sprite: randEnemySprite().key,
        worldX: baseX + 260 + lrng() * (ACT_FLOOR_W - 380),
        walkT: lRand(0, 6),
        dir: lrng() < 0.5 ? -1 : 1
      });
    }
    /* ④' 别让物品压在怪身上：打完架尸体就留在怪原来的位置，
     *     尸体底下叠着一堆装备/材料时，"点尸体搜刮"会先点到装备，尸体根本点不到。
     *     这里把离怪太近的物品挪到别处，给每只怪留出一圈空地（尸体一定点得到）。 */
    if (activityRun.monsters.length) {
      const monXs = activityRun.monsters.map(function (m) { return m.worldX; });
      activityRun.items.forEach(function (it) {
        let tooClose = false;
        monXs.forEach(function (mx) { if (Math.abs(it.worldX - mx) < CORPSE_CLEAR) tooClose = true; });
        if (!tooClose) return;
        let best = it.worldX, bestGap = -1;
        for (let k = 0; k < 24; k++) {
          const cand = clearOfPortal(baseX + 140 + lrng() * (ACT_FLOOR_W - 220));
          let gap = Math.abs(cand - portalX);
          monXs.forEach(function (mx) { gap = Math.min(gap, Math.abs(cand - mx)); });
          activityRun.items.forEach(function (o) {
            if (o !== it) gap = Math.min(gap, Math.abs(cand - o.worldX));
          });
          if (gap > bestGap) { bestGap = gap; best = cand; }
        }
        it.worldX = best;
      });
    }
    /* 玩家随机落点（第一层 / 换层都用它）：
     * 在左侧这一带里挑"离怪和其他玩家最远"的位置，免得一落地就被拖进战斗。
     * 注意：落点是每个玩家自己的，不参与上面的"同种子"布局。 */
    let spawnX = baseX + 60, bestGap = -1;
    for (let cand = baseX + 40; cand <= baseX + 420; cand += 20) {
      let gap = Infinity;
      activityRun.monsters.forEach(function (m) { gap = Math.min(gap, Math.abs(m.worldX - cand)); });
      activityRun.bots.forEach(function (b) { gap = Math.min(gap, Math.abs(b.worldX - cand)); });
      if (gap > bestGap) { bestGap = gap; spawnX = cand; }
    }
    player.worldX = spawnX;
    player.vx = 0;
    input.moving = false;
    syncMoveButton();
    activityNetJoin();          // 进这一层的"真人房间"（没联网就自动跳过）
    updateCaveBar();
  }

  function enterActivity(def, forcedEntry, opts) {
    const free = !!(opts && opts.free);   // 调试直进：跳过活力 / 冷却校验
    const now = wbNow();
    const entry = forcedEntry ||
      currentActivities(now).filter(function (e) { return e.def.id === def.id; })[0];
    if (!entry) { showToast('这个副本不在当前时段里。'); return false; }
    if (now < entry.spawnedAt) {
      showToast('「' + def.name + '」还没开启，' + activityTimeText(entry.spawnedAt - now) + ' 后开启。');
      return false;
    }
    if (!activityActive(entry, now)) { showToast('「' + def.name + '」已经结束了。'); return false; }
    const reason = activityGateReason(def);
    if (reason) { showToast(reason); return false; }
    /* 进入限制：冷却 1 小时 + 消耗 1 点活力 */
    if (!free) {
      const cd = activityCooldownReason(now);
      if (cd) { showToast(cd); return false; }
      if (vitality < ACTIVITY_ENTER_COST) {
        showToast('活力不足：进入「' + def.name + '」需要 ' + ACTIVITY_ENTER_COST +
          ' 点活力（当前 ' + vitality + ' 点）。');
        return false;
      }
    }
    closeActivityPanel();
    // 副本里要频繁点屏幕，把会挡住画面的任务弹窗先收起来
    if (ui.questPopup && !ui.questPopup.classList.contains('hidden')) closeQuestPopup();
    const caveDef = {
      id: def.id, name: def.name, color: def.color, glow: def.glow,
      enemies: 0, tiers: def.tiers, token: false, weight: 0,
      note: def.note, activity: true, maxStage: def.maxStage, realm: def.realm,
      // 敌人等级区间 / 奖励档位都跟着副本走
      enemyStage: def.enemyStage, minEnemyStage: def.minEnemyStage, rewardTier: def.rewardTier
    };
    // 直接建实例（不走 enterCave：副本里不刷"洞府怪"，怪物由本层布局生成）
    if (autoAdventure) stopAutoAdventure('副本内需要手动探索，已退出自动冒险。');
    input.moving = false;
    syncMoveButton();
    cave = { def: caveDef, remaining: 0, total: 0, killed: 0, entrance: null };
    activeEnemy = null;
    battleGroup.length = 0;
    activityRun = { def: def, endsAt: entry.endsAt, enteredAt: now, floor: 1, floors: 3 };
    /* 记冷却 + 扣活力（不论是否调试直进，都算"进过一次"） */
    activityLastEnterAt = now;
    if (!free) {
      vitality = Math.max(0, vitality - ACTIVITY_ENTER_COST);
      updateVitalityUI();
    }
    saveGame();
    activityBuildFloor(1);
    addBattleLogSafe('进入 PVP 副本「' + def.name + '」（第 1/3 层）：点击屏幕左右移动，' +
      '点物品拾取、点其他玩家可强行开战；限时 ' + activityTimeText(activityRemainMs(entry, now)) +
      '。本次消耗 ' + (free ? 0 : ACTIVITY_ENTER_COST) + ' 点活力，' +
      '下次进入需等 ' + (ACTIVITY_ENTER_COOLDOWN_MS / 60000) + ' 分钟' +
      '。⚠ 副本内阵亡会掉光副本里捡到的物品与身上金币（已穿戴的装备不掉）。', 'reward');
    showToast('进入「' + def.name + '」第 1 层' +
      (free ? '' : '（活力 -' + ACTIVITY_ENTER_COST + '）') +
      ' · 点屏幕左右移动，点物品拾取，点玩家开战。');
    return true;
  }

  function leaveActivity(msg) {
    abortPvpDuelIfAny('离开副本');
    activityNetLeave();          // 先退出"真人房间"
    activityRun = null;
    clearActivityLootFlags();    // 人活着出来了：这批战利品算"安全到手"
    if (cave) leaveCave();
    if (msg) {
      showToast(msg);
      pushBattleLog(msg, 'system');
    }
    updateCaveBar();
  }

  // 到点（或本时段结束）就把玩家传送出来
  function updateActivityRunClock() {
    if (!activityRun) return;
    if (wbNow() >= activityRun.endsAt) {
      const nm = activityRun.def.name;
      leaveActivity('「' + nm + '」的时限到了，你被传送回公路。');
    }
  }

  function activityWorldFromScreen(clientX) {
    const rect = canvas.getBoundingClientRect();
    const sx = (clientX - rect.left) * (canvas.width / rect.width);
    return { sx: sx, worldX: cameraX() + sx };
  }

  // 点物品：材料丹药直接拿；装备开始 10 秒拾取
  function activityClickItem(it, hitWorldX) {
    if (!activityRun || !it || it.taken) return false;
    if (it.kind === 'mat' || it.kind === 'spirit') {
      it.taken = true;
      it.item.fromActivity = true;      // 副本内所得：副本里阵亡会掉回去
      inventory.push(it.item);
      playSfx('pickup');                // 拾取音效（材料 / 丹药 / 天地灵物）
      if (it.kind === 'spirit') {
        activityRun.spiritTaken = true;    // 本副本已经出过这一个：换层/重铺都不再生成
        showToast('★★ 拾到极品材料「天地灵物」！已放入背包。');
        pushBattleLog('在「' + activityRun.def.name + '」第 3 层拾到极品材料：天地灵物', 'reward');
      } else {
        showToast('拾取「' + it.item.name + '」（' + activityItemKindName(it.item) + '）已进背包。');
        pushBattleLog('在「' + activityRun.def.name + '」拾取 ' + activityItemKindName(it.item) +
          '：' + it.item.name, 'reward');
      }
      saveGame();
      return true;
    }
    // 装备：走过去 + 站定 10 秒
    activityRun.pick = { itemId: it.id, t: 0, worldX: it.worldX };
    activityStopMove();
    showToast('开始拾取「' + it.item.name + '」：需要原地停留 ' + ACT_PICK_SEC + ' 秒，期间别被打断。');
    return true;
  }

  function activityFinishPick() {
    const run = activityRun;
    if (!run || !run.pick) return;
    const it = run.items.filter(function (x) { return x.id === run.pick.itemId; })[0];
    run.pick = null;
    if (!it || it.taken) return;
    it.taken = true;
    it.item.fromActivity = true;        // 副本内所得：副本里阵亡会掉回去
    inventory.push(it.item);
    playSfx('pickup');                  // 拾取音效（装备）
    showToast('拾取成功：「' + it.item.name + '」已放入背包。');
    pushBattleLog('在「' + run.def.name + '」拾到装备：' + it.item.name, 'reward');
    saveGame();
  }

  function activityCancelPick(reason) {
    const run = activityRun;
    if (!run || !run.pick) return;
    run.pick = null;
    if (reason) showToast(reason);
  }

  // 停下脚步（点空地走完一小段 / 开始拾取 / 开战前都要收住）
  function activityStopMove() {
    const run = activityRun;
    if (!run) return;
    run.moveDir = 0;
    run.tapLeft = 0;
    run.holding = false;
    run.pressDist = 0;
    player.vx = 0;
  }

  /* 点一下空地：只走一小段（ACT_TAP_STEP 像素）就停，想一直走要长按。
   * 以角色自身为界：点他左边往左走，点右边往右走。 */
  function activityTapMove(sxA) {
    const run = activityRun;
    if (!run) return;
    const playerSx = player.worldX - cameraX();
    run.moveDir = sxA < playerSx ? -1 : 1;
    run.tapLeft = ACT_TAP_STEP;
    run.holding = false;
    run.pressDist = 0;
    activityCancelPick();
  }

  /* 临时副本里"这一点点到了什么"：命中就地处理掉并返回 true；
   * 点空地返回 false（交给"走一小步 / 长按连续走"处理）。
   * 顺序：物品 → 地上可搜索的尸体 → 真人 → 其他道友 → 怪 → 传送门。 */
  function activityHitTest(sxA, syA) {
    const run = activityRun;
    if (!run) return false;
    const worldA = cameraX() + sxA;
    const near = function (x) { return Math.abs(x - worldA) <= ACT_CLICK_R; };
    // ① 材料 / 丹药（点一下就拿）
    const mat = run.items.filter(function (it) { return !it.taken && it.kind === 'mat' && near(it.worldX); })[0];
    if (mat) { activityClickItem(mat, worldA); return true; }
    // ①' 天地灵物（极品材料，也是点一下就拿）
    const spirit = run.items.filter(function (it) { return !it.taken && it.kind === 'spirit' && near(it.worldX); })[0];
    if (spirit) { activityClickItem(spirit, worldA); return true; }
    // ② 装备（10 秒拾取）
    const gear = run.items.filter(function (it) { return !it.taken && it.kind === 'gear' && near(it.worldX); })[0];
    if (gear) { activityClickItem(gear, worldA); return true; }
    /* ②' 打完怪留下的尸体：副本里也要能点开搜刮
     *     （以前这一支被"副本点击"整段 return 挡掉了，尸体点了没反应） */
    if (corpse && !corpse.searched) {
      const csx = corpse.worldX - cameraX();
      if (Math.abs(sxA - csx) < 115 && syA > GROUND_Y - 300 && syA < H) { searchCorpse(); return true; }
    }
    // ②'' 真人切磋掉在地上的战利品堆（只有胜者这一侧会生成）
    const pile = run.items.filter(function (it) {
      return !it.taken && it.kind === 'spoils' && near(it.worldX);
    })[0];
    if (pile) { takeActivityPile(pile); return true; }
    // ③ 其他玩家 → 强行开战
    // ③-0 真人玩家（服务器同步过来的）：点他 = 发起 PvP
    const real = Array.from(netMembers.values()).filter(function (m) {
      const localX = (m.drawX === undefined ? m.localX : m.drawX);
      return Math.abs((run.baseX + localX) - worldA) <= ACT_CLICK_R;
    })[0];
    if (real) {
      requestPvpDuel(real);
      return true;
    }
    const bot = run.bots.filter(function (b) { return near(b.worldX); })[0];
    if (bot) {
      startActivityFight({ id: bot.id, name: bot.name, worldX: bot.worldX, level: playerLevel(), tier: bot.tier }, true);
      return true;
    }
    // ④ 怪 → 也能直接开打
    const mon = run.monsters.filter(function (m) { return near(m.worldX); })[0];
    if (mon) { startActivityFight(mon, false); return true; }
    // ⑤ 传送门
    if (run.portal && Math.abs(run.portal.x - worldA) <= ACT_PORTAL_CLICK_R) { activityNextFloor(); return true; }
    return false;
  }

  // 点其他玩家 / 点怪 → 强行开战
  function startActivityFight(target, isPlayer) {
    const run = activityRun;
    if (!run || !target) return false;
    if (gameState !== 'walk') return false;
    const mult = isPlayer ? 1.15 : (target.mult || 1);
    const foe = buildEnemy(target.level || run.def.enemyStage || playerLevel(),
      target.tier || 'magic', { worldX: target.worldX, cave: false });
    if (isPlayer) {
      foe.sprite = 'player';          // 用玩家立绘：对面是"其他玩家"
      foe.playerModel = true;
      foe.name = '道友·' + target.name;
      foe.kindName = '道友';
      // 真人：先手看双方攻击速度（对手的攻速由服务器同步过来）
      if (typeof target.combatSpeed === 'number') foe.combatSpeed = target.combatSpeed;
      if (target.pvpSid) foe.pvpSid = target.pvpSid;
    } else {
      foe.sprite = target.sprite || foe.sprite;
      foe.name = '（' + mult.toFixed(1) + ' 倍）' + foe.name;
    }
    foe.maxHp = Math.round(foe.maxHp * mult);
    foe.hp = foe.maxHp;
    foe.atk = Math.round(foe.atk * mult);
    foe.activityKind = isPlayer ? 'pvp' : 'monster';
    foe.activityTargetId = target.id;
    activeEnemy = foe;
    battleGroup.length = 0;
    battleGroup.push(foe);
    activityCancelPick();
    activityStopMove();          // 开战后收住脚步，免得打完自动接着走
    startBattle();
    if (isPlayer) pushBattleLog('你主动出手，把「道友·' + target.name + '」拉进了战斗！', 'player');
    return true;
  }

  // 战斗结束后回到本层
  function activityAfterFight(kind, id) {
    const run = activityRun;
    if (!run) return;
    if (kind === 'pvp') run.bots = run.bots.filter(function (b) { return b.id !== id; });
    if (kind === 'monster') run.monsters = run.monsters.filter(function (m) { return m.id !== id; });
    activeEnemy = null;
    battleGroup.length = 0;
    updateCaveBar();
  }

  // 换层 / 通关
  function activityNextFloor() {
    const run = activityRun;
    if (!run) return;
    abortPvpDuelIfAny('换层');    // 换层前先把交锋收掉（对手那边会收到"对方脱战"）
    if (run.floor >= run.floors) {
      const name = run.def.name;
      activityRun = null;
      clearActivityLootFlags();   // 三层走完=成功带出：这批战利品不再算"副本内所得"
      finishCave();
      showToast('你走完了「' + name + '」的 3 层，带着战利品被传送出副本。');
      return;
    }
    showToast('传送门亮起——进入第 ' + (run.floor + 1) + ' 层。');
    pushBattleLog('通过传送门，进入「' + run.def.name + '」第 ' + (run.floor + 1) + ' 层', 'system');
    activityBuildFloor(run.floor + 1);
  }

  // 每帧推进临时副本（走路 / 拾取 / 其他玩家 / 怪 / 传送门）
  function updateActivityRun(dt) {
    const run = activityRun;
    if (!run || gameState !== 'walk') return;
    if (activityGod) combat.hp = combat.maxHp;   // 调试：测试长流程时不被打死
    const def = run.def;

    // ① 点击左右移动：点一下走一小段（tapLeft 递减到 0 就停），按住不放才是连续走
    if (run.moveDir !== 0 && !run.pick) {
      const spd = ACT_MOVE_SPEED * (derived.speedMult || 1);
      const beforeX = player.worldX;
      player.worldX = clamp(player.worldX + run.moveDir * spd * dt, run.minX, run.maxX);
      player.walkT += dt * 9;
      player.animT += dt;
      player.vx = run.moveDir * spd;      // 让"走路动画 / 扬尘"这些按 vx 判断的逻辑也跟着生效
      const moved = Math.abs(player.worldX - beforeX);
      if (run.holding) run.pressDist += moved;             // 长按：一直走，直到松手
      if (run.tapLeft > 0) {                                // 点一下：走完这一小段就停
        run.tapLeft -= moved;
        if (run.tapLeft <= 0) { run.tapLeft = 0; run.moveDir = 0; player.vx = 0; }
      }
      if (player.worldX <= run.minX || player.worldX >= run.maxX) run.moveDir = 0;
    } else if (player.vx) {
      player.vx = 0;
    }

    // ② 装备拾取进度：站着别动，10 秒
    if (run.pick) {
      const it = run.items.filter(function (x) { return x.id === run.pick.itemId; })[0];
      if (!it) {
        run.pick = null;
        player.vx = 0;
      } else {
        const dist = Math.abs(player.worldX - it.worldX);
        if (dist > 40) {
          // 还没走到跟前：自动走过去（走过去也算移动，进度不涨）
          const dir = it.worldX > player.worldX ? 1 : -1;
          player.worldX = clamp(player.worldX + dir * ACT_MOVE_SPEED * dt, run.minX, run.maxX);
          player.walkT += dt * 9;
          player.animT += dt;
          player.vx = dir * ACT_MOVE_SPEED;
        } else {
          run.pick.t += dt;
          player.vx = 0;
          if (run.pick.t >= ACT_PICK_SEC) activityFinishPick();
        }
      }
    }

    // ③ 其他玩家：巡逻 + 发现你在拾取就抢你
    run.bots.forEach(function (b) {
      b.phase += dt;
      b.walkT += dt * 7;
      b.worldX += b.dir * 34 * dt;
      if (b.worldX < run.minX + 60) b.dir = 1;
      if (b.worldX > run.maxX - 60) b.dir = -1;
      b.facing = b.dir >= 0 ? 1 : -1;
      if (run.pick && !b.aggro && !run.noAggro) {
        const near = Math.abs(b.worldX - player.worldX) < 300;
        if (near && run.pick.t > 1.2 && Math.random() < dt * 0.55) {
          b.aggro = true;
          pushBattleLog('「道友·' + b.name + '」发现了正在拾取的你，抢先出手！', 'enemy');
          startActivityFight({ id: b.id, name: b.name, worldX: b.worldX, level: playerLevel(), tier: b.tier }, true);
        }
      }
    });

    /* ④ 怪：驻守在本层（不巡逻，免得玩家一走动就被拖进战斗、拾取总被打断）。
     *    走到它面前（或点它）才会开打；强度 1~3 倍。 */
    run.monsters.forEach(function (m) {
      m.walkT += dt * 6;
      // 正在拾取（含走向拾取目标）时不被怪打断：拾取的风险只来自"其他玩家"
      if (!run.pick && Math.abs(m.worldX - player.worldX) <= ENEMY_GAP_PX && !m.dead) {
        startActivityFight(m, false);
      }
    });

    /* ⑤ 传送门：走到就进下一层。
     *    正在拾取装备（自动走过去 / 原地读条）时先不触发，
     *    免得"去捡传送门旁边的装备"被直接传送走、读条白读。 */
    if (!run.pick && run.portal && Math.abs(player.worldX - run.portal.x) <= ACT_PORTAL_HIT) {
      activityNextFloor();
    }
  }

  /* 地上物品用的图标对象：装备走 gearIconUrl，材料/丹药/技能石直接用预载素材 */
  function activityItemImage(item) {
    if (!item) return null;
    if (item.type === 'equipment') {
      if (!item.iconImg) ensureItemIcon(item);
      if (!item._actImg) {
        const url = gearIconUrl(item.iconSet, item.iconImg);
        if (!url) return null;
        item._actImg = new Image();
        item._actImg.src = url;
      }
      return (item._actImg.complete && item._actImg.naturalWidth > 0) ? item._actImg : null;
    }
    const url = itemIconUrl(item);
    if (!url) return null;
    const keys = Object.keys(images);
    for (let i = 0; i < keys.length; i++) {
      const im = images[keys[i]];
      if (im && im.src === url && im.complete && im.naturalWidth > 0) return im;
    }
    return null;
  }

  /* ---------- 临时副本场景绘制（这层地面 / 物品 / 其他玩家 / 怪 / 传送门） ---------- */
 function drawActivityScene(cam) {
    const run = activityRun;
    activityPlateDbg = [];          // 这一帧的名牌从零记（调试/测试用）
    if (!cave || !cave.def.activity || !run) return;
    /* 真人交锋中：背景里的其他道友 / 怪统一虚化（环境与地形保持原样） */
    const duelDim = !!pvpDuel;
    const groundY = GROUND_Y + 4;
    const def0 = run.def;
    // 场地底色：暗下来一点 + 副本主色，和公路/洞府区分开
    ctx.save();
    ctx.fillStyle = 'rgba(10, 8, 14, .38)';
    ctx.fillRect(-20, -20, W + 40, H + 40);
    const bg = ctx.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, 'rgba(0,0,0,0)');
    bg.addColorStop(0.55, def0.glow || 'rgba(255,215,106,.18)');
    bg.addColorStop(1, 'rgba(0,0,0,.35)');
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = bg;
    ctx.fillRect(-20, -20, W + 40, H + 40);
    ctx.restore();

    // ① 本层的"公共地图"地板：横向色带 + 左右边界墙，让人一眼看出这是副本内的场地
    const def = run.def;
    const x0 = run.minX - cam, x1 = run.maxX - cam;
    ctx.save();
    ctx.globalAlpha = 0.5;
    const gg = ctx.createLinearGradient(0, GROUND_Y - 10, 0, GROUND_Y + 90);
    gg.addColorStop(0, def.glow || 'rgba(255,215,106,.35)');
    gg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gg;
    ctx.fillRect(x0, GROUND_Y - 10, x1 - x0, 100);
    ctx.restore();
    ctx.save();
    ctx.strokeStyle = def.color || '#ffd76a';
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 3;
    [x0, x1].forEach(function (wx) {
      if (wx < -40 || wx > W + 40) return;
      ctx.beginPath();
      ctx.moveTo(wx, GROUND_Y - 150);
      ctx.lineTo(wx, GROUND_Y + 26);
      ctx.stroke();
    });
    ctx.restore();

    // ② 传送门（随机位置）：圆环 + 光柱
    if (run.portal) {
      const px = run.portal.x - cam;
      const t = player.animT;
      ctx.save();
      ctx.globalAlpha = 0.85;
      const pr = 52 + Math.sin(t * 3) * 4;
      const pg = ctx.createRadialGradient(px, GROUND_Y - 70, 6, px, GROUND_Y - 70, pr * 1.6);
      pg.addColorStop(0, 'rgba(255,255,255,.75)');
      pg.addColorStop(0.5, def.glow || 'rgba(255,215,106,.45)');
      pg.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.arc(px, GROUND_Y - 70, pr * 1.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = def.color || '#ffd76a';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.ellipse(px, GROUND_Y - 70, 34, 62, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = '#ffe6bb';
      ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(run.floor >= run.floors ? '出口 · 离开副本' : ('传送门 · 第 ' + (run.floor + 1) + ' 层'),
        px, GROUND_Y - 150);
      ctx.restore();
    }

    // ③ 地上的物品：装备有光柱 + 图标；材料 / 丹药是小图标（点一下就拿）
    run.items.forEach(function (it) {
      if (it.taken) return;
      const ix = it.worldX - cam;
      if (ix < -140 || ix > W + 140) return;
      const isGear = it.kind === 'gear';
      const isSpirit = it.kind === 'spirit';
      const isPile = it.kind === 'spoils';
      const rar = (it.item && it.item.rarity) || (isGear ? 'rare' : 'common');
      /* 真人切磋的战利品堆：金色光柱 + 小箱子，一眼能认出来 */
      if (isPile) {
        drawDropPillar(ix, { color: '#ffd76a', tier: 4 });
        ctx.save();
        ctx.fillStyle = '#8a5a1e';
        ctx.fillRect(ix - 22, GROUND_Y - 44, 44, 30);
        ctx.fillStyle = '#c98a35';
        ctx.fillRect(ix - 24, GROUND_Y - 52, 48, 10);
        ctx.fillStyle = '#ffe9a8';
        ctx.fillRect(ix - 4, GROUND_Y - 40, 8, 12);
        ctx.textAlign = 'center';
        ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
        ctx.fillStyle = '#ffd76a';
        ctx.fillText('道友·' + it.fromName + ' 的战利品', ix, GROUND_Y - 62);
        ctx.fillStyle = '#ffe9c0';
        ctx.fillText(it.items.length + ' 件' + (it.gold ? (' + ' + it.gold + ' 金币') : '') + ' · 点一下全拿走',
          ix, GROUND_Y - 46);
        ctx.restore();
      }
      // 装备按品级打光柱（复用尸体掉落那套"暗黑风光柱"）
      if (!isPile && (isGear || isSpirit)) {
        drawDropPillar(ix, {
          color: (it.item && it.item.rarityColor) || (isSpirit ? '#8fffd0' : '#7fc9ff'),
          tier: isSpirit ? 4 : (DROP_PILLAR_TIER[rar] || 3)
        });
      }
      if (isPile) return;    // 战利品堆已经画完了
      const iconImg = activityItemImage(it.item);
      ctx.save();
      if (iconImg) {
        ctx.drawImage(iconImg, ix - 21, GROUND_Y - 62, 42, 42);
      } else {
        ctx.fillStyle = (it.item && it.item.rarityColor) || (isGear ? '#7fc9ff' : '#9ef0a8');
        ctx.beginPath();
        ctx.arc(ix, GROUND_Y - 40, 13, 0, Math.PI * 2);
        ctx.fill();
        // 材料用汉字做图标（裂隙 / 灾变 / 迷雾 …）
        if (!isGear) {
          ctx.fillStyle = 'rgba(20,10,4,.85)';
          ctx.font = 'bold 15px "Microsoft YaHei", sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText((it.item && it.item.icon) || '料', ix, GROUND_Y - 35);
        }
      }
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      ctx.fillStyle = isSpirit ? '#8fffd0' : (isGear ? '#cfe9ff' : '#b8f0c0');
      ctx.fillText(isSpirit ? '★ 天地灵物（极品）'
        : (isGear ? ('装备 · 需停留 ' + ACT_PICK_SEC + 's') : '点一下即取'), ix, GROUND_Y - 72);
      ctx.restore();

      // 拾取进度（10 秒）
      if (run.pick && run.pick.itemId === it.id) {
        const p = clamp(run.pick.t / ACT_PICK_SEC, 0, 1);
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,.55)';
        ctx.fillRect(ix - 44, GROUND_Y - 104, 88, 12);
        ctx.fillStyle = '#9ef0a8';
        ctx.fillRect(ix - 42, GROUND_Y - 102, 84 * p, 8);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('拾取中 ' + Math.max(0, ACT_PICK_SEC - run.pick.t).toFixed(1) + 's', ix, GROUND_Y - 110);
        ctx.restore();
      }
    });

    // ④ 其他玩家（实体立绘 + 名牌；点他就能强行开战）
    if (duelDim) ctx.globalAlpha = 0.18;      // 交锋中：其他道友虚化
    run.bots.forEach(function (b) {
      const bx = b.worldX - cam;
      if (bx < -180 || bx > W + 180) return;
      if (images.player_idle_1) {
        const step = Math.floor(b.walkT * 0.6) % 4;
        const frame = images['player_idle_' + (step + 1)] || images.player_idle_1;
        const sc = 0.92 * caveScale();
        const w = PLAYER_FRAME.w * sc, h = PLAYER_FRAME.h * sc;
        const top = groundY + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * sc;
        ctx.save();
        if (b.facing < 0) {
          ctx.translate(bx, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(frame, -w / 2, top, w, h);
        } else {
          ctx.drawImage(frame, bx - w / 2, top, w, h);
        }
        ctx.restore();
      }
      // 名牌走统一绘制：道友·名字 +（第二行）提示，和真人 / 自己同一套字体
      if (!duelDim) {
        drawActorPlate(bx, groundY, b.name, '', {
          sub: b.aggro ? '正在抢你的拾取' : '点击可强行开战',
          aggro: !!b.aggro
        });
      }
    });
    if (duelDim) ctx.globalAlpha = 1;

    // ⑤ 本境界的怪（1~3 倍强度，靠过去就开打）
    if (duelDim) ctx.globalAlpha = 0.18;      // 交锋中：本层的怪虚化
    run.monsters.forEach(function (m) {
      const mx = m.worldX - cam;
      if (mx < -180 || mx > W + 180) return;
      const img = images[m.sprite + '_1'];
      if (img && img.complete && img.naturalWidth > 0) {
        const sc = (m.tier === 'elite' ? 1.1 : 1) * caveScale();
        drawEnemySprite(img, mx, groundY, sc, m.dir > 0, Math.sin(m.walkT * 4) * 1.2,
          ENEMY_SPRITES.filter(function (s) { return s.key === m.sprite; })[0]);
      }
      if (!duelDim) {
        ctx.save();
        ctx.textAlign = 'center';
        ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
        ctx.fillStyle = m.mult >= 2 ? '#ff9a8a' : '#ffd08a';
        ctx.fillText('Lv.' + m.level + ' ×' + m.mult.toFixed(1), mx, GROUND_Y - 176);
        ctx.restore();
      }
    });
    if (duelDim) ctx.globalAlpha = 1;

    /* ⑥ 真人玩家（服务器广播过来的位置）
     *    不再单独标「真人」：和本机其他道友用同一套名牌（道友·名字 + 境界），
     *    只多一条小血条（血量是服务器同步过来的，看得到就顺手显示）。 */
    if (duelDim) ctx.globalAlpha = 0.18;      // 交锋中：其他真人虚化
    netMembers.forEach(function (m) {
      const localX = (m.drawX === undefined ? m.localX : m.drawX);
      const bx = (run.baseX + localX) - cam;   // 层内相对坐标 → 本机世界坐标
      if (bx < -220 || bx > W + 220) return;
      if (images.player_idle_1) {
        const walking = m.action === 'walk';
        const step = Math.floor((player.animT + m.sid.length) * (walking ? 7 : 3)) % 4;
        const frame = images['player_idle_' + (step + 1)] || images.player_idle_1;
        const sc = 0.92;
        const w = PLAYER_FRAME.w * sc, h = PLAYER_FRAME.h * sc;
        const top = groundY + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * sc;
        ctx.save();
        if (m.facing < 0) {          // 朝左走就把立绘镜像过来
          ctx.translate(bx, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(frame, -w / 2, top, w, h);
        } else {
          ctx.drawImage(frame, bx - w / 2, top, w, h);
        }
        ctx.restore();
      }
      if (!duelDim) {
        drawActorPlate(bx, groundY, m.name, m.realm || '', {
          bar: true, hp: m.hp, sub: m.action === 'fight' ? '交战中' : ''
        });
      }
    });
    if (duelDim) ctx.globalAlpha = 1;

    /* ⑦ 自己头顶：同样一块名牌（自己的昵称 + 当前境界）。
     *    自己不带「道友·」前缀，只有别的玩家 / 机器人才加。 */
    if (!duelDim) {
      drawActorPlate(player.worldX - cam, groundY, chatNick(), stageText(playerLevel()), { self: true });
    }
  }

  /* 道友名牌：名字 + 境界（可选小血条），本机玩家 / 真人 / 机器人共用一套字体和排版 */
  let activityPlateDbg = [];        // 调试/测试用：这一帧画出来的名牌
  function drawActorPlate(bx, groundY, name, realm, opt) {
    const o = opt || {};
    // 自己只写名字；其他玩家 / 机器人才加「道友·」前缀
    const label = o.self ? name : ('道友·' + name);
    activityPlateDbg.push({ text: label, name: name, realm: realm || '',
      x: Math.round(bx), self: !!o.self, sub: o.sub || '' });
    const y = groundY - 172;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
    ctx.fillStyle = o.self ? '#ffe9a8' : (o.aggro ? '#ff9a8a' : '#bfe9ff');
    ctx.fillText(label, bx, y);
    if (realm) {
      ctx.font = '11px "Microsoft YaHei", sans-serif';
      ctx.fillStyle = o.self ? 'rgba(255,236,180,.85)' : 'rgba(200, 240, 255, .8)';
      ctx.fillText(realm + (o.sub ? ('　' + o.sub) : ''), bx, y + 15);
    } else if (o.sub) {
      ctx.font = '11px "Microsoft YaHei", sans-serif';
      ctx.fillStyle = 'rgba(200, 240, 255, .8)';
      ctx.fillText(o.sub, bx, y + 15);
    }
    if (o.bar) {
      const hpw = 54, hpx = bx - hpw / 2, hpy = y + 24;
      ctx.fillStyle = 'rgba(0,0,0,.55)';
      ctx.fillRect(hpx - 1, hpy - 1, hpw + 2, 6);
      ctx.fillStyle = o.hp > 0.3 ? '#6ef08a' : '#ff7a6a';
      ctx.fillRect(hpx, hpy, hpw * clamp(o.hp === undefined ? 1 : o.hp, 0, 1), 4);
    }
    ctx.restore();
  }

  // ---------- 活动：调试 / 测试钩子 ----------
  window.__debugActivity = function () {
    const now = wbNow();
    const list = currentActivities(now);
    return {
      windowStart: activityWindowStart(now),
      windowEnd: activityWindowStart(now) + ACTIVITY_PERIOD_MS,
      nextInSec: Math.max(0, Math.round((activityWindowStart(now) + ACTIVITY_PERIOD_MS - now) / 1000)),
      count: list.length,
      list: list.map(function (e) {
        const wait = activityWaitMs(e, now);
        return {
          id: e.def.id, name: e.def.name, realm: e.def.realm, maxStage: e.def.maxStage,
          enemies: e.def.enemies, remainSec: Math.round(activityRemainMs(e, now) / 1000),
          waitSec: Math.round(wait / 1000),
          state: wait > 0 ? 'wait' : (activityRemainMs(e, now) > 0 ? 'open' : 'over'),
          testOpen: activityIsTestOpen(e.def.id),
          gate: activityGateReason(e.def)
        };
      }),
      all: ACTIVITY_DUNGEONS.map(function (d) {
        return { id: d.id, name: d.name, realm: d.realm, maxStage: d.maxStage,
          testOpen: activityIsTestOpen(d.id), gate: activityGateReason(d) };
      }),
      inside: activityRun ? {
        id: activityRun.def.id, name: activityRun.def.name,
        remainSec: Math.round(activityRemainMs(activityRun, now) / 1000),
        floor: activityRun.floor, floors: activityRun.floors,
        bots: activityRun.bots.length,
        monsters: activityRun.monsters.length,
        items: activityRun.items.filter(function (it) { return !it.taken; }).length,
        gearItems: activityRun.items.filter(function (it) { return !it.taken && it.kind === 'gear'; }).length,
        matItems: activityRun.items.filter(function (it) { return !it.taken && it.kind === 'mat'; }).length,
        spiritItems: activityRun.items.filter(function (it) { return !it.taken && it.kind === 'spirit'; }).length,
        lootPiles: activityRun.items.filter(function (it) { return !it.taken && it.kind === 'spoils'; })
          .map(function (it) {
            return { from: it.fromName, items: it.items.length, gold: it.gold, worldX: Math.round(it.worldX) };
          }),
        materialNames: activityRun.items.filter(function (it) {
          return !it.taken && it.kind === 'mat';
        }).map(function (it) { return it.item ? it.item.name : ''; }),
        picking: activityRun.pick ? {
          itemId: activityRun.pick.itemId,
          t: Math.round(activityRun.pick.t * 10) / 10
        } : null,
        portalX: activityRun.portal ? Math.round(activityRun.portal.x) : null,
        playerX: Math.round(player.worldX),
        minX: Math.round(activityRun.minX), maxX: Math.round(activityRun.maxX)
      } : null,
      monsters: activityRun ? activityRun.monsters.map(function (m) {
        return { id: m.id, level: m.level, tier: m.tier, mult: m.mult, worldX: Math.round(m.worldX) };
      }) : [],
      stage: playerLevel(), stageName: stageName(playerLevel())
    };
  };
  // 调试用：直接进某个临时副本（不走窗口按钮）
  window.__debugActivityEnter = function (id, opts2) {
    const def = ACTIVITY_DUNGEONS.filter(function (d) { return d.id === id; })[0] || ACTIVITY_DUNGEONS[0];
    // 调试：直接开场，不受"本时段随机到哪几个"的限制
    const now = wbNow();
    /* 调试直进默认跳过"1 点活力 / 1 小时冷却"的校验（老用例还是照旧能随时进场）；
     * 传 { gate: true } 时走真实校验（用来验证限制本身）。 */
    const opts = (opts2 && opts2.gate) ? null : { free: true };
    return enterActivity(def, { def: def, spawnedAt: now - 1000, endsAt: now + ACTIVITY_LIFETIME_MS }, opts);
  };
  // 调试/测试用：查 / 改活动副本的进入冷却（传 0 或负数 = 立刻可再进）
  window.__debugActivityCooldown = function (ms) {
    if (typeof ms === 'number') activityLastEnterAt = (ms > 0) ? (wbNow() - ACTIVITY_ENTER_COOLDOWN_MS + ms) : 0;
    return {
      lastEnterAt: activityLastEnterAt,
      leftMs: activityCooldownLeft(),
      leftText: activityTimeText(activityCooldownLeft()),
      cost: ACTIVITY_ENTER_COST,
      cooldownMs: ACTIVITY_ENTER_COOLDOWN_MS,
      vitality: vitality
    };
  };
  window.__debugActivityCanEnter = function (id) {
    const def = ACTIVITY_DUNGEONS.filter(function (d) { return d.id === id; })[0] || ACTIVITY_DUNGEONS[0];
    const now = wbNow();
    return {
      def: def.id,
      cooldown: activityCooldownReason(now),
      gate: activityGateReason(def),
      vitality: vitality,
      cost: ACTIVITY_ENTER_COST,
      canEnter: !activityCooldownReason(now) && !activityGateReason(def) && vitality >= ACTIVITY_ENTER_COST
    };
  };
  // 调试/测试用：直接设活力（验证"进入消耗 1 点活力"）
  window.__debugSetVitality = function (n) {
    vitality = Math.max(0, Math.floor(Number(n) || 0));
    updateVitalityUI();
    saveGame();
    return vitality;
  };

  /* ---------- 调试钩子：门派（框架版） ---------- */
  window.__debugSect = function () {
    const d = curSectDef();
    return {
      id: sect.id || null,
      name: d ? d.name : null,
      contribution: sect.contribution | 0,
      joinedAt: sect.joinedAt || 0,
      joinReason: sectJoinReason(SECTS[0]),
      canJoin: !sectJoinReason(SECTS[0]),
      stage: playerLevel(), stageText: stageName(playerLevel()),
      minStage: SECTS[0] ? SECTS[0].minStage : null,
      list: SECTS.map(function (s) {
        const taskCount = (s.buildings || []).reduce(function (sum, b) { return sum + ((b.tasks || []).length); }, 0);
        return {
          id: s.id, name: s.name, minStage: s.minStage,
          tasks: taskCount, buildings: (s.buildings || []).length, skills: (s.skills || []).length
        };
      }),
      buildings: ((d && d.buildings) || []).map(function (b) {
        return { id: b.id, name: b.name, tasks: (b.tasks || []).map(function (t) { return t.id; }) };
      }),
      tasks: sectAllTasks().map(function (t) {
        return {
          id: t.id, name: t.name, track: t.track, target: t.target,
          buildingId: t.buildingId, buildingName: t.buildingName,
          progress: sectTaskProgress(t.id), done: sectTaskDone(t), claimed: sectTaskClaimed(t.id),
          reward: Object.assign({}, t.reward || {})
        };
      }),
      skills: ((d && d.skills) || []).map(function (s) {
        const owned = knownSkills.filter(function (k) { return k.id === s.id; })[0] || null;
        return {
          id: s.id, name: s.name, price: s.price,
          owned: !!owned, level: owned ? (owned.level || 1) : 0
        };
      }),
      arts: ((d && d.arts) || []).map(function (a) {
        return { id: a.id, name: a.name, learned: artLayerCount(a.id), total: artLayers(a).length };
      })
    };
  };
  window.__debugJoinSect = function (id) {
    const ok = joinSect(id || (SECTS[0] || {}).id);
    return { ok: ok, sect: window.__debugSect() };
  };
  window.__debugSectTaskTick = function (track, n) { return sectTaskTick(track, n); };
  window.__debugSectClaim = function (id) { return claimSectTask(id); };
  window.__debugSectExchange = function (id) { return exchangeSectSkill(id); };
  // 直接加/减门派贡献（测试用）
  window.__debugSectContribution = function (n) {
    sect.contribution = Math.max(0, (sect.contribution | 0) + Math.floor(Number(n) || 0));
    saveGame();
    if (ui.sectPopup && !ui.sectPopup.classList.contains('hidden')) renderSectPanel();
    if (ui.charPopup && !ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return sect.contribution;
  };
  // 丹药炼制：材料数量 / 直接发材料 / 直接炼制（测试与预览用）
  window.__debugSectMaterials = function () {
    return { herb: sectMaterialCount('herb'), ore: sectMaterialCount('ore') };
  };
  window.__debugSectGrantMaterial = function (kind, n) {
    const k = kind === 'ore' ? 'ore' : 'herb';
    const count = Math.max(1, Math.floor(Number(n) || 1));
    inventory.push(makeSectMaterial(k, count));
    saveGame();
    if (ui.sectRoomOverlay && !ui.sectRoomOverlay.classList.contains('hidden')) renderSectRoom();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return window.__debugSectMaterials();
  };
  // 矿石鉴定调试：查看矿石 / 鉴定 / 推进三步加工
  window.__debugOreItems = function () {
    return inventory.filter(function (it) { return it && it.sectMaterial === 'ore'; }).map(function (it) {
      return {
        id: it.id, name: it.name, stack: it.stack || 1,
        identified: !!it.oreIdentified, finished: !!it.oreFinished,
        score: it.oreScore || 0, steps: (it.oreSteps || []).slice(), value: oreValue(it),
        materialKind: oreMaterialKind(it), attr: it.oreAttr || null, attrLevel: it.oreAttrLevel || null
      };
    });
  };
  window.__debugOrePanel = function () {
    return {
      open: !!(ui.oreProcessPopup && !ui.oreProcessPopup.classList.contains('hidden')),
      animating: !!(oreProcess && oreProcess.anim),
      item: oreProcess && oreProcess.item ? {
        id: oreProcess.item.id, name: oreProcess.item.name,
        score: oreProcess.item.oreScore | 0,
        steps: (oreProcess.item.oreSteps || []).slice(),
        finished: !!oreProcess.item.oreFinished,
        value: oreValue(oreProcess.item)
      } : null,
      gold: gold
    };
  };
  window.__debugOreIdentify = function (idx) {
    const list = inventory.filter(function (it) { return it && it.sectMaterial === 'ore' && oreIsRaw(it); });
    return identifyOreItem(list[Math.max(0, Math.floor(idx || 0))]);
  };
  window.__debugOreStep = function (stepId) { return startOreStep(stepId); };
  /* 调试/测试用：用**真实的加工流程**跑 n 块矿石，统计产出（评分 / 种类 / 属性石等级）。
   * 仅用于验收"属性石等级分布"，跑完把探针造成的日志与背包变化还原。 */
  window.__debugOreSample = function (n) {
    const total = Math.max(1, Math.min(2000, Math.floor(n || 1)));
    const logLen = battleLogHistory.length;
    const invLen = inventory.length;
    const out = {
      total: total, min: 100, max: 0, sum: 0, ge70: 0,
      kinds: { affix: 0, attribute: 0, none: 0 },
      attrLevels: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
    };
    for (let i = 0; i < total; i++) {
      const it = {
        id: 'smp' + i, type: 'material', sectMaterial: 'ore', oreIdentified: true,
        oreScore: randInt(20, 68), oreSteps: [], oreFinished: false
      };
      oreProcess = { item: it, anim: null };
      ORE_STEPS.forEach(function (st) {
        oreProcess.anim = { kind: st.id, t: 0, dur: st.dur, delta: oreStepDelta(st, it.oreScore) };
        finishOreStep(st);
      });
      const sc = it.oreScore | 0;
      out.sum += sc;
      out.min = Math.min(out.min, sc);
      out.max = Math.max(out.max, sc);
      if (sc >= 70) out.ge70++;
      if (it.oreMaterialKind === 'attribute') {
        out.kinds.attribute++;
        out.attrLevels[it.oreAttrLevel || 1]++;
      } else if (it.oreMaterialKind === 'affix') out.kinds.affix++;
      else out.kinds.none++;
    }
    oreProcess = null;
    battleLogHistory.length = logLen;
    inventory.length = invLen;
    if (ui.oreProcessPopup) ui.oreProcessPopup.classList.add('hidden');
    return out;
  };
  window.__debugMakeAttributeOre = function (attr, level, count) {
    const n = Math.max(1, Math.floor(count || 1));
    const made = [];
    for (let i = 0; i < n; i++) {
      const it = makeAttributeOre(attr || 'str', level || 1);
      inventory.push(it);
      made.push(it);
    }
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return made.map(function (it) { return { id: it.id, name: it.name, attr: it.oreAttr, level: it.oreAttrLevel }; });
  };
  window.__debugUseAttributeOre = function (idx) {
    const list = inventory.filter(function (it) { return oreMaterialKind(it) === 'attribute'; });
    return useAttributeOre(list[Math.max(0, Math.floor(idx || 0))]);
  };
  window.__debugCombineAttributeOre = function (idx) {
    const list = inventory.filter(function (it) { return oreMaterialKind(it) === 'attribute'; });
    return combineAttributeOres(list[Math.max(0, Math.floor(idx || 0))]);
  };
  window.__debugOreSell = function () {
    const it = oreProcess && oreProcess.item;
    const before = gold;
    const okDone = sellOreItem(it);
    return { ok: okDone, goldBefore: before, goldAfter: gold };
  };
  /* 调试/测试用：一键出售未鉴定矿石 */
  window.__debugSellAllRawOres = function () { return sellAllRawOres(); };
  window.__debugOreContribute = function () {
    const it = oreProcess && oreProcess.item;
    const before = sect.contribution | 0;
    const okDone = contributeOreItem(it);
    return { ok: okDone, contributionBefore: before, contributionAfter: sect.contribution | 0 };
  };
  window.__debugSectRecipes = function () {
    return SECT_PILL_RECIPES.map(function (r) {
      return {
        id: r.id, name: r.name, contribution: r.contribution,
        herb: r.herb, ore: r.ore, detail: r.detail
      };
    });
  };
  window.__debugSectRefine = function (id) { return refineSectPill(id); };
  // 挖矿：启动 / 查询当前动画与矿石粒子状态
  window.__debugStartMine = function () { return startSectMining(); };
  window.__debugStopMine = function (msg) { return stopSectMining(msg); };
  window.__debugMineState = function () {
    return {
      active: !!miningFx,
      continuous: miningContinuous,
      t: miningFx ? Math.round(miningFx.t * 1000) / 1000 : null,
      amount: miningFx ? miningFx.amount : null,
      spawned: miningFx ? !!miningFx.spawned : false,
      oreDrawn: mineOreDrawn,
      lastFrame: lastPlayerFrameKey,
      mineFrame: lastMineFrameKey,
      particles: miningFx ? miningFx.particles.map(function (p) {
        return { icon: p.icon, h: Math.round(p.h), x: Math.round(p.x), landT: Math.round(p.landT * 1000) / 1000 };
      }) : [],
      framesLoaded: (function () {
        let n = 0;
        for (let i = 1; i <= PLAYER_MINE_FRAMES; i++) {
          const im = images['player_mine_' + i];
          if (im && im.complete && im.naturalWidth > 0) n++;
        }
        return n;
      })(),
      oreLoaded: (function () {
        let n = 0;
        for (let i = 1; i <= 7; i++) {
          const im = images['ore_' + i];
          if (im && im.complete && im.naturalWidth > 0) n++;
        }
        return n;
      })()
    };
  };
  // 同门判定的纯函数探针：给一个门派 id，看会不会被门规拦下
  window.__debugSectSameGuard = function (sectId) { return netSameSect({ sect: sectId || '' }); };
  window.__debugSectOpen = function () { return openSectPanel(); };
  window.__debugSectClose = function () { closeSectPanel(); return true; };
  /* ---------- 调试钩子：门派山门地图 ---------- */
  window.__debugSectMap = function () {
    const def = curSectDef();
    const scene = sectScene;
    return {
      inSect: hasSect(),
      inScene: !!scene,                       // 是否正站在"山门这条路"上
      mapOpen: !!scene,                       // 兼容旧断言名
      minX: scene ? Math.round(scene.minX) : null,
      maxX: scene ? Math.round(scene.maxX) : null,
      playerX: Math.round(player.worldX),
      roomOpen: !!(ui.sectRoomOverlay && !ui.sectRoomOverlay.classList.contains('hidden')),
      roomId: sectRoomId,
      npcs: scene ? (scene.npcs || []).map(function (n) {
        return { id: n.id, name: n.name, localX: Math.round(n.localX), action: n.action };
      }) : [],
      buildings: scene
        ? scene.buildings.map(function (b) { return { id: b.id, name: b.name, worldX: Math.round(b.worldX) }; })
        : ((def && def.buildings) || []).map(function (b) { return { id: b.id, name: b.name, x: b.x, y: b.y }; })
    };
  };
  window.__debugSectPeers = function () {
    return {
      npcs: sectScene ? (sectScene.npcs || []).length : 0,
      real: netMembers.size,
      drawn: sceneActors.peers,
      moveDir: sectMoveDir,
      holding: sectHolding,
      playerX: Math.round(player.worldX)
    };
  };
  window.__debugAddSectPeer = function (name, localX) {
    const n = netMembers.size + 1;
    netRoomKind = 'sect';
    netActRoom = sectRoomKey();
    netMembers.set('dbg_peer_' + n, {
      sid: 'dbg_peer_' + n, name: name || ('同门' + n),
      localX: Number(localX) || 160, drawX: Number(localX) || 160,
      hp: 1, level: playerLevel(), realm: stageText(playerLevel()),
      action: 'idle', speed: 1, atkSpd: 1, sect: mySectId(), ts: Date.now()
    });
    return window.__debugSectPeers();
  };
  window.__debugSectOpenMap = function () { return enterSectScene(); };
  window.__debugSectCloseMap = function () { return leaveSectScene(); };
  window.__debugSectEnterBuilding = function (id) { return openSectRoom(id || 'hall'); };
  // 山门建筑在屏幕上的 x（点击判定用；相机 = 玩家世界坐标 − 玩家屏幕坐标）
  window.__debugSectBuildingScreenX = function (id) {
    const b = sectScene && sectScene.buildings.filter(function (x) { return x.id === id; })[0];
    return b ? Math.round(b.worldX - cameraX()) : null;
  };
  window.__debugSectEmptyScreenX = function (dir) {
    if (!sectScene) return null;
    const d = dir < 0 ? -1 : 1;
    const psx = player.worldX - cameraX();
    for (let step = 140; step <= 560; step += 20) {
      const sx = psx + d * step;
      if (sx < 12 || sx > W - 12) continue;
      if (!sectSceneBuildingAt(sx, GROUND_Y - 60)) return Math.round(sx);
    }
    return null;
  };
  // 山门建筑美术：每座殿宇使用的素材 key / 是否已加载 / 绘制宽高
  window.__debugSectBuildingArt = function () {
    const def = curSectDef();
    const list = ((def && def.buildings) || []).map(function (b) {
      const art = sectBuildingArt(b);
      const cfg = SECT_BUILDING_ART[b.id] || null;
      return {
        id: b.id, name: b.name,
        key: cfg ? cfg.key : null,
        src: cfg ? (ASSETS.filter(function (a) { return a[0] === cfg.key; })[0] || [])[1] : null,
        title: cfg ? cfg.title : null,
        titleSrc: cfg ? (ASSETS.filter(function (a) { return a[0] === cfg.title; })[0] || [])[1] : null,
        titleLoaded: !!(cfg && images[cfg.title] && images[cfg.title].complete && images[cfg.title].naturalWidth > 0),
        loaded: !!(art.img && art.img.complete && art.img.naturalWidth > 0),
        w: Math.round(art.dw), h: Math.round(art.dh)
      };
    });
    return { list: list, allLoaded: list.every(function (x) { return x.loaded && x.titleLoaded; }) };
  };
  // 把角色挪到山门道路上的指定世界坐标（截图 / 用例用）
  window.__debugSectGoto = function (worldX) {
    if (!sectScene) return null;
    player.worldX = clamp(Number(worldX) || player.worldX, sectScene.minX, sectScene.maxX);
    player.vx = 0;
    input.moving = false;
    syncMoveButton();
    return Math.round(player.worldX);
  };
  window.__debugSectRoomText = function () {
    return ui.sectRoomBody ? ui.sectRoomBody.textContent.replace(/\s+/g, ' ').trim() : null;
  };
  /* ---------- 调试钩子：门派法诀 ---------- */
  window.__debugSectArts = function () {
    const d = curSectDef();
    return {
      contribution: sect.contribution | 0,
      arts: ((d && d.arts) || []).map(function (a) {
        const learned = artLayerCount(a.id);
        const layers = artLayers(a);
        return {
          id: a.id, name: a.name, learned: learned, total: layers.length,
          nextPrice: layers[learned] ? layers[learned].price : null,
          layers: layers.map(function (L, i) {
            return { id: L.id, name: L.name, slot: L.slot, price: L.price, boostPct: L.boostPct, learned: i < learned };
          })
        };
      }),
      fengji: {
        learned: artLayerCount('fengji'),
        full: artLayerCount('fengji') >= artLayers(sectArtDef('fengji')).length,
        slots: ['weapon', 'clothes', 'helmet', 'gloves', 'jewelry', 'shoes'].map(function (s) {
          const g = equipment[s];
          return {
            slot: s, hasItem: !!g, uniform: g ? itemAffixUniform(g) : false,
            family: g ? itemAffixFamilyName(g) : null,
            mult: fengjiSlotMult(s)
          };
        })
      },
      lianxue: { learned: artLayerCount('lianxue'), active: lianxueActive(), weapon: weaponTypeEquipped() || null }
    };
  };
  window.__debugLearnSectArt = function (id) { return learnSectArt(id); };
  window.__debugLastBlood = function () {
    return lastBloodInfo ? Object.assign({}, lastBloodInfo) : null;
  };
  // 直接对当前敌人掷一次玩家伤害（不结束回合）：用来单独验证"炼血"等命中类效果
  window.__debugRollHit = function (base) {
    const r = rollPlayerDamage(Math.max(1, Math.round(Number(base) || 10)), activeEnemy);
    updateBattleHud();
    return {
      dmg: r.dmg, crit: r.crit,
      blood: r.blood ? Object.assign({}, r.blood) : null,
      hp: Math.round(combat.hp), maxHp: combat.maxHp
    };
  };
  // 造一件指定词缀的装备并穿上（测"封极法"的词缀统一判定 / 增幅）
  window.__debugWearGear = function (slot, mods, rarity, weaponKind) {
    const key = slot || 'weapon';
    const it = makeEquipment(player.level, rarity || 'common', key, weaponKind || null);
    it.mods = (mods || []).map(function (m) {
      return { key: m.key, value: m.value || 1, value2: m.value2 || 0, bandIdx: -1, locked: false };
    });
    it.modCount = it.mods.length;
    applyItemMods(it);
    equipItem(it);
    return {
      slot: key, name: it.name,
      mods: it.mods.map(function (m) { return m.key; }),
      stats: { atk: it.atk, armor: it.armor, strength: it.strength, hp: it.hp, critChance: it.critChance }
    };
  };
  window.__debugItemUniform = function (slot) {
    const g = equipment[slot];
    return {
      slot: slot, hasItem: !!g, uniform: g ? itemAffixUniform(g) : false,
      family: g ? itemAffixFamilyName(g) : null,
      mods: g ? itemCoreMods(g).map(function (m) { return m.key; }) : []
    };
  };
  // 调试/演示用：把这批副本改成"现在就开"（on=false 恢复按排期）
  window.__debugActivityForceSpawn = function (on) {
    activitySpawnOverride = (on === false) ? null : wbNow();
    if (ui.activityPopup && !ui.activityPopup.classList.contains('hidden')) renderActivityPanel();
    return window.__debugActivity();
  };
  // 调试用：把当前副本的时间直接走完（验证"到点被传送出来"）
  window.__debugActivityExpire = function () {
    if (!activityRun) return null;
    activityRun.endsAt = wbNow() - 1;
    updateActivityRunClock();
    return window.__debugActivity();
  };
  // 调试用：同一时段生成两次，验证"所有人看到的是同一批"
  window.__debugActivitySchedule = function (t) {
    return currentActivities(typeof t === 'number' ? t : wbNow()).map(function (e) {
      return e.def.id;
    });
  };
  // 调试/测试用：取"离玩家最近的材料 / 装备 / 其他玩家"的世界坐标（方便脚本去点它）
  function activityNearestWorldX(filterFn) {
    if (!activityRun) return null;
    const list = activityRun.items.filter(filterFn).concat(activityRun.bots.map(function (b) {
      return { worldX: b.worldX };
    }));
    if (!list.length) return null;
    list.sort(function (a, b) {
      return Math.abs(a.worldX - player.worldX) - Math.abs(b.worldX - player.worldX);
    });
    return Math.round(list[0].worldX);
  }
  window.__debugActivityMatX = function () {
    return activityRun ? (activityRun.items.filter(function (it) {
      return !it.taken && it.kind === 'mat';
    }).sort(function (a, b) {
      return Math.abs(a.worldX - player.worldX) - Math.abs(b.worldX - player.worldX);
    })[0] || {}).worldX || null : null;
  };
  window.__debugActivityGearX = function () {
    return activityRun ? (activityRun.items.filter(function (it) {
      return !it.taken && it.kind === 'gear';
    }).sort(function (a, b) {
      return Math.abs(a.worldX - player.worldX) - Math.abs(b.worldX - player.worldX);
    })[0] || {}).worldX || null : null;
  };
  /* 调试/测试用：找一个"附近没有物体"的**画布横坐标**，点它就会按 dir 方向走。
   *   返回的是 canvas 像素坐标（和点击事件用同一套坐标），避免脚本再算相机。 */
  window.__debugActivityEmptyScreenX = function (dir) {
    if (!activityRun) return null;
    const dx = (dir < 0 ? -1 : 1);
    const pSx = player.worldX - cameraX();
    const busy = [];
    activityRun.items.forEach(function (it) { if (!it.taken) busy.push(it.worldX); });
    activityRun.bots.forEach(function (b) { busy.push(b.worldX); });
    activityRun.monsters.forEach(function (m) { busy.push(m.worldX); });
    // 真人也要避开：点到真人会去"打招呼"，不是走路
    netMembers.forEach(function (m) {
      busy.push(activityRun.baseX + (m.drawX === undefined ? m.localX : m.drawX));
    });
    if (activityRun.portal) busy.push(activityRun.portal.x);
    const cam = cameraX();
    for (let i = 1; i <= 30; i++) {
      const sx = pSx + dx * i * 40;
      if (sx < 60 || sx > W - 60) break;
      const wx = cam + sx;
      if (wx < activityRun.minX + 40 || wx > activityRun.maxX - 40) continue;
      let clear = true;
      for (let k = 0; k < busy.length; k++) if (Math.abs(busy[k] - wx) < 70) { clear = false; break; }
      if (clear) return Math.round(sx);
    }
    return null;
  };
  /* 调试/测试用：清掉本层的怪、并让其他玩家暂时不会主动抢你（拾取流程就能稳定跑完）。
   * 其他玩家本身保留 —— 点他们照样能强行开战。 */
  // 调试/测试用：副本里"真人"同步的状态（房间、人数、成员列表）
  window.__debugActivityNet = function () {
    return {
      connected: !!(net.chat && net.chatState === 'online'),
      chatState: net.chatState || null,
      serverUrl: net.serverUrl || null,
      nick: chatNick(),
      room: netActRoom,
      sid: netActSid,
      count: netActCount,
      pvp: pvpDuel ? {
        id: pvpDuel.id, name: pvpDuel.foeName, myTurn: pvpDuel.myTurn,
        myHp: pvpDuel.myHp, foeHp: pvpDuel.foeHp, foeSpeed: (pvpDuel.foe && pvpDuel.foe.speed) || 1,
        foeAtkSpd: (pvpDuel.foe && pvpDuel.foe.atkSpd) || 1,
        foeElite: !!(activeEnemy && activeEnemy.elite),
        foeSkill: (activeEnemy && activeEnemy.skill) ? activeEnemy.skill.name : null,
        foeFrame: (activeEnemy && activeEnemy.curFrameKey) || null,
        foeAtk: Math.round(pvpFoeAtkT * 100) / 100,
        bgFade: 0.18                // 交锋中背景角色（怪 / 其他道友）的透明度
      } : null,
      pvpPending: pvpPending ? { name: pvpPending.name } : null,
      pvpOffered: pvpOffered ? { fromName: pvpOffered.fromName } : null,
      mySpeed: Math.round((derived.speedMult || 1) * 1000) / 1000,
      myAtkSpd: Math.round(attackSpeedMult() * 1000) / 1000,
      selfX: activityRun ? Math.round(player.worldX - activityRun.baseX) : null,   // 层内相对坐标
      members: Array.from(netMembers.values()).map(function (m) {
        return {
          sid: m.sid, name: m.name, x: Math.round(m.localX),
          drawX: Math.round(m.drawX === undefined ? m.localX : m.drawX),
          hp: Math.round(m.hp * 100) / 100, realm: m.realm, level: m.level,
          action: m.action, facing: m.facing === undefined ? 1 : m.facing,
          speed: m.speed === undefined ? 1 : m.speed,
          atkSpd: m.atkSpd === undefined ? 1 : m.atkSpd,
          ageMs: Date.now() - m.ts
        };
      })
    };
  };
  // 调试/测试用：这一帧画出来的头顶名牌（自己 + 场上的道友）
  window.__debugActivityPlates = function () {
    return activityPlateDbg.slice();
  };
  // 调试/演示用：直接跳到下一层 / 把角色挪到指定世界坐标（截图用）
  window.__debugActivityNextFloor = function () {
    if (!activityRun) return null;
    activityNextFloor();
    return window.__debugActivity().inside;
  };
  window.__debugActivityGoto = function (worldX) {
    if (!activityRun) return null;
    player.worldX = clamp(Number(worldX) || player.worldX, activityRun.minX, activityRun.maxX);
    player.vx = 0;
    activityRun.moveDir = 0;
    return Math.round(player.worldX);
  };
  // 调试/测试用：运行时开关某个副本的「测试常开」（不改代码也能恢复正式排期）
  window.__debugActivityTestOpen = function (id, on) {
    const cur = activityTestOpenIds().slice();
    const idx = cur.indexOf(id);
    if (on === false) { if (idx >= 0) cur.splice(idx, 1); }
    else if (idx < 0) cur.push(id);
    activityTestOpenOverride = cur;
    if (ui.activityPopup && !ui.activityPopup.classList.contains('hidden')) renderActivityPanel();
    return cur;
  };
  // 调试/测试用：副本内打不死（跑长流程的测试用）
  window.__debugActivityGod = function (on) {
    activityGod = on !== false;
    return activityGod;
  };
  window.__debugActivityClearThreats = function () {
    if (!activityRun) return false;
    activityRun.monsters = [];
    activityRun.noAggro = true;
    return true;
  };
  // 调试/测试用：天地灵物（极品材料）在世界里的位置
  window.__debugActivitySpiritX = function () {
    if (!activityRun) return null;
    const it = activityRun.items.filter(function (x) { return !x.taken && x.kind === 'spirit'; })[0];
    return it ? Math.round(it.worldX) : null;
  };
  window.__debugActivityBotX = function () {
    if (!activityRun || !activityRun.bots.length) return null;
    return activityRun.bots.slice().sort(function (a, b) {
      return Math.abs(a.worldX - player.worldX) - Math.abs(b.worldX - player.worldX);
    })[0].worldX;
  };
  /* 调试/测试用：找一个"附近没有任何物体"的世界坐标（dir=1 往右找、-1 往左找）。
   * 点空地才会触发"左半边往左走 / 右半边往右走"，测试要靠它拿到干净的点。 */
  window.__debugActivityEmptyX = function (dir) {
    if (!activityRun) return null;
    const dx = (dir < 0 ? -1 : 1);
    const step = 40;
    const busy = [];
    activityRun.items.forEach(function (it) { if (!it.taken) busy.push(it.worldX); });
    activityRun.bots.forEach(function (b) { busy.push(b.worldX); });
    activityRun.monsters.forEach(function (m) { busy.push(m.worldX); });
    if (activityRun.portal) busy.push(activityRun.portal.x);
    const isClear = function (x) {
      if (x < activityRun.minX + 80 || x > activityRun.maxX - 80) return false;
      for (let i = 0; i < busy.length; i++) if (Math.abs(busy[i] - x) < 80) return false;
      return true;
    };
    for (let i = 1; i <= 110; i++) {
      const x = player.worldX + dx * i * step;
      if (isClear(x)) return Math.round(x);
    }
    // 实在找不到"完全干净"的位置，就退而求其次：取"最空"的那个点
    let bestX = null, bestGap = -1;
    for (let i = 1; i <= 110; i++) {
      const x = player.worldX + dx * i * step;
      if (x < activityRun.minX + 40 || x > activityRun.maxX - 40) continue;
      let gap = Infinity;
      for (let k = 0; k < busy.length; k++) gap = Math.min(gap, Math.abs(busy[k] - x));
      if (gap > bestGap) { bestGap = gap; bestX = x; }
    }
    if (bestX !== null) return Math.round(bestX);
    // 兜底：直接给"那个方向的场地边缘"，点它一定能往那个方向走
    return Math.round(dx < 0 ? activityRun.minX + 40 : activityRun.maxX - 40);
  };
  // 调试/测试用：停下脚步（否则计算点击坐标时角色还在走，会点偏）
  window.__debugActivityStop = function () {
    if (!activityRun) return false;
    activityStopMove();
    return true;
  };
  // 调试用：当前副本的运行时状态（moveDir / 拾取 / 本层对象）
  window.__debugActivityRun = function () {
    if (!activityRun) return null;
    return {
      def: activityRun.def.id, floor: activityRun.floor,
      moveDir: activityRun.moveDir,
      pick: activityRun.pick ? { itemId: activityRun.pick.itemId, t: Math.round(activityRun.pick.t * 10) / 10 } : null,
      baseX: Math.round(activityRun.baseX),
      items: activityRun.items.map(function (it) {
        return { id: it.id, kind: it.kind, worldX: Math.round(it.worldX),
          localX: Math.round(it.worldX - activityRun.baseX), taken: it.taken,
          name: it.item ? it.item.name : ('战利品 · ' + (it.fromName || '道友')) };
      }),
      bots: activityRun.bots.map(function (x) {
        return { id: x.id, name: x.name, worldX: Math.round(x.worldX),
          localX: Math.round(x.worldX - activityRun.baseX), aggro: !!x.aggro };
      }),
      monsters: activityRun.monsters.map(function (x) {
        return { id: x.id, level: x.level, tier: x.tier, mult: x.mult,
          worldX: Math.round(x.worldX), localX: Math.round(x.worldX - activityRun.baseX) };
      }),
      portalX: activityRun.portal ? Math.round(activityRun.portal.x) : null,
      portalLocalX: activityRun.portal ? Math.round(activityRun.portal.x - activityRun.baseX) : null,
      minX: Math.round(activityRun.minX), maxX: Math.round(activityRun.maxX),
      playerX: Math.round(player.worldX)
    };
  };


  function setBattleLog(text) {
    ui.battleLog.textContent = text;
    pushBattleLog(text);
  }

  function updateBattleHud() {
    layoutBattleLogBtn();   // 战斗日志按钮跟着战斗信息面板走
    const pp = clamp(combat.hp / combat.maxHp, 0, 1) * 100;
    ui.playerHpBar.style.width = pp.toFixed(1) + '%';
    ui.playerHpText.textContent =
      Math.max(0, Math.ceil(combat.hp)) + ' / ' + combat.maxHp;
    // 灵力条：可用的部分 + 被持续 / 召唤技能占用的保留段
    const usablePct = clamp(derived.mpUsable / Math.max(1, combat.maxMp), 0, 1) * 100;
    if (ui.playerMpBar) ui.playerMpBar.style.width = usablePct.toFixed(1) + '%';
    if (ui.playerMpReserve) {
      ui.playerMpReserve.style.width = (100 - usablePct).toFixed(1) + '%';
    }
    // 显示「当前灵力 / 最大灵力」，被占用的部分另外标注（不再只显示扣掉占用后的值）
    ui.mpText.textContent = '灵力 ' + Math.ceil(combat.mp) + ' / ' + combat.maxMp +
      (derived.mpReservePct > 0
        ? '　（已占用 ' + derived.mpReservePct + '%，当前可用上限 ' + derived.mpUsable + '）'
        : '');
  }

  function showBattleUi(show) {
    ui.battleTint.classList.toggle('hidden', !show);
    ui.battleHud.classList.toggle('hidden', !show);
    // 战斗日志按钮：默认不显示，开战后出现在右上角战斗信息下方
    if (ui.btnLog) {
      ui.btnLog.classList.toggle('hidden', !show);
      if (show) layoutBattleLogBtn();
    }
  }

  // 「战斗日志」按钮贴在右上角战斗信息面板的正下方（面板高度会随文案变化，所以动态量）
  let logBtnClock = 0;
  function layoutBattleLogBtn() {
    if (!ui.btnLog || !ui.battleHud) return;
    const wrapEl = ui.wrap || document.body;
    if (ui.battleHud.classList.contains('hidden')) return;
    const wr = wrapEl.getBoundingClientRect();
    const hr = ui.battleHud.getBoundingClientRect();
    ui.btnLog.style.top = Math.round(hr.bottom - wr.top + 8) + 'px';
    ui.btnLog.style.right = Math.max(8, Math.round(wr.right - hr.right)) + 'px';
  }

  // ---------- 状态切换 ----------
 function startScene() {
    // 读档续玩：接着上次的里程继续（新档案 / 阵亡后回到起点时 resumeWorldX 为 null）
    player.worldX = resumeWorldX && resumeWorldX > START_X ? resumeWorldX : START_X;
    player.vx = 0;
    // 联网校时改到「打开世界BOSS窗口」时再做（避免开局多一次外部请求，失败也无影响）
    layoutHud();          // HUD 按舞台高度自适应（小窗口收窄按钮列 / 聊天窗）
   player.walkT = 0;
   player.animT = 0;
   playerAtkT = 0;
   // 休息姿态不重置：加载页 → 进入场景是同一个姿势，别在这一下突然换一个
   input.moving = false;
    player.hurtT = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 0;
    ally = null;
    corpse = null;
    // 新手宝箱：每个档案只给一次（已经用过就不再出现，随存档记录）
    if (!newbieChestUsed) {
      newbieChestUsed = true;
      chest = {
        worldX: START_X + 520,
        opened: false,
        weaponTaken: false,
        gemTaken: false,
        weapons: WEAPON_KINDS.map(function (kind) {
          const item = makeEquipment(1, 'common', 'weapon', kind.name);
          if (kind.name === '盾牌') {
            /* 宝箱盾牌：随机词缀固定成 1 条「荆棘伤害 +4」。
             * 注意：以前这里是把 item 上各项数值手动清零 —— 那把**基底词缀
             * 「格挡几率 +15%」也一起抹掉了**（拿到盾牌却 0% 格挡，角色面板显示 0，
             * 战斗里永远不触发格挡；重开游戏又会被 ensureItemMods 修回来，看起来更玄）。
             * 正确做法：换掉随机词缀，再走一遍 applyItemMods —— 基底照旧生效。 */
            item.mods = [{ key: 'thorns', value: 4, value2: 0, bandIdx: 0, locked: false }];
            item.modCount = 1;
            applyItemMods(item);
            item.price = rollGearPrice(item.rarity);
          }
          return item;
        }),
        chosenWeapon: null,
        gemItem: null,
        stoneItem: null,
        stoneTaken: false
      };
      saveGame();   // 立刻落盘，之后重开游戏不会再刷出这个宝箱
    } else {
      chest = null;
    }
    pets.length = 0; // 新一局清空出战宠物
    mirageFirstSpawned = false; // 新一局：第一个 1km 还会必出一次虚影
    pendingVictory = 0;
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    combat.mp = derived.mpUsable;
    defeatedEnemies = 0;
    damagePopups = [];
    bloodWaves.length = 0;
    bloodWaveTimer = 0;
    spawnNextEnemy();
    // 只有在"自动化测试直连"模式（?skiplogin=1）下才发放测试资源，
    // 正常玩（哪怕在本机 127.0.0.1 上）不再有金币/活力金手指。
    if (!testGoldGranted && isTestEnvironment() && skipLoginRequested()) {
      testGoldGranted = true;
      gold = Math.max(gold, TEST_GOLD);
      vitality += TEST_VITALITY;
      saveGame(); // 立刻落盘，刷新/再登录时继续累加
    }
    /* 【测试用赠送 · 用完删掉】账号 AAA → 背包里补一把「武者的刑具」 */
    maybeGrantTestUnique();
    /* 门派：跨天重置今日门派任务；顺带刷新 HUD 按钮上的小红点 */
    sectSyncDay();
    updateSectUI();
    updateGold();
    autoAdventure = false;
    autoKmLeft = 0;
    updateVitalityUI();
    updateLevelUI();
    updateOnlineUI();
    cans = [];
    canTimer = rand(6, 10);
    eliteCharges = 0;
    ally = null;
    legendMap = null;
    // 洞府：新一局清空入口与当前洞府状态（令牌数量保留在存档里）
    cave = null;
    caveEntrances.length = 0;
    caveNextX = player.worldX + rand(4200, 6200);
    battleGroup.length = 0;
    updateCaveBar();
    // 血瓶充能沿用存档里的值（新档案在 resetProgressState 里给满 5 层）
    updateFlaskUI();

    gameState = 'walk';
    ui.title.classList.add('hidden');
    ui.hud.classList.remove('hidden');
    ui.controls.classList.remove('hidden');
    ui.flaskWrap.classList.remove('hidden');
    ui.chatPanel.classList.remove('hidden');
    updateDonateButton();   // 进入游戏后隐藏首页的「赞助」按钮
    setupChat();
    // 战斗信息里的名字用玩家昵称（默认账号名）
    const whoEl = document.querySelector('#battleHud .who');
    if (whoEl) whoEl.textContent = chatNick();
    updateNickUI();
    updateEliteButton();
    updateLegendBar();
    clearBattleLog();
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    updateDist();
    setBattleLog('');
    if (testGoldGranted) {
      showToast('【测试环境】已发放 ' + TEST_GOLD + ' 金币 · ' + TEST_VITALITY + ' 活力');
    }
    maybeStartFirstQuest();   // 正式进入游戏：弹出第一个任务「提升至武者大圆满」
  }

  /* 先手判定用的"攻击速度"（1 = 100%）
   *   玩家 = 100% + 装备/词缀给的攻速加成（derived.atkSpeed 是百分数）
   *   敌人 = 它自己的 combatSpeed（见 buildEnemy）
   * 注意：移动速度（derived.speedMult）只影响野外赶路快慢，不再参与先手。 */
  function attackSpeedMult() {
    return Math.max(0.2, 1 + (derived.atkSpeed || 0) / 100);
  }

  function startBattle() {
    if (gameState !== 'walk' || !activeEnemy) return;
    gameState = 'battle';
    realmNotice = { player: 0, enemy: 0 };   // 每场战斗只提示一次"境界压制"
    dailyQuestTick('battle');    // 每日任务：进行一次战斗
    input.moving = false;
    player.vx = 0;
    player.worldX = activeEnemy.x - ENEMY_GAP_PX;
    // 路边遭遇也要把当前目标放进战斗队列（battleGroup[0] = activeEnemy）：
    // 群攻（破空斩）/ 阳爪流血 / 震撼大地的冲击波都是按 battleGroup 取目标。
    // 系统赠送的精英挑战：1~3 个精英同场，进战时整组保留。
    const eliteGroup = pendingEliteGroup.length && pendingEliteGroup.indexOf(activeEnemy) >= 0
      ? pendingEliteGroup.slice() : null;
    battleGroup.length = 0;
    if (eliteGroup) {
      eliteGroup.forEach(function (e) { battleGroup.push(e); });
      pendingEliteGroup = [];
    } else {
      battleGroup.push(activeEnemy);
    }

    /* 先手判定：比较双方的**攻击速度**，攻速高的一方先行动。
     *   玩家攻速 = 100% + 装备/词缀给的攻速加成；敌人用它自己的战斗速度（combatSpeed）。
     *   移动速度不再参与先手，它只负责野外赶路的实际移动快慢。 */
    const playerSpd = attackSpeedMult();
    const enemySpd = activeEnemy.combatSpeed || 1;
    // 灵宝「先手符诏」：永远先手，不看攻速
    const firstByTreasure = treasureSpecial('alwaysFirst');
    battleTurn = (firstByTreasure || playerSpd >= enemySpd) ? 'player' : 'enemy';
    if (firstByTreasure && playerSpd < enemySpd) {
      setBattleLog('灵宝「先手符诏」生效：你抢在敌人之前出手！（不看攻速）');
    }
    enemyTurnTimer = battleTurn === 'enemy' ? 0.85 : 0;
    playerIdleTimer = battleTurn === 'player' ? 10 : 0;
    clearDefendState();         // 新战斗：清掉上一场残留的防御姿态 + 护盾特效
    powerCharge = null;         // 新战斗：清掉上一场残留的「强力射击」蓄力
    powerArrowFx = null;
    airSlashFx = null;          // 新战斗：清掉上一场残留的剑气
    activeEnemy.openingAttack = battleTurn === 'enemy';
    ally = null;
    enemyMinionTurns = 0;
    updateEliteButton();

    showBattleUi(true);
    setControls('battle');
    syncMoveButton();
    updateBattleHud();
    if (isStaffEquipped() && !petAlive()) activatePet();
    const petNote = isStaffEquipped() ? ' 法杖宠物已自动现身助战。' : '';
    if (battleTurn === 'player') {
      setBattleLog(
        '你的攻速更快（攻速 ' + Math.round(playerSpd * 100) + '% vs ' +
        Math.round(enemySpd * 100) + '%），获得先手！' + petNote
      );
    } else {
      setBattleLog(
        '敌人攻速更快（攻速 ' + Math.round(enemySpd * 100) + '% vs ' +
        Math.round(playerSpd * 100) + '%），抢先发起攻击！' + petNote
      );
    }
    pushBattleLog('遭遇 ' + (activeEnemy.name || '拦路者') + ' Lv.' + activeEnemy.level, 'system');
    // 新一场战斗：清掉上一场留下的控制 / 点燃状态
    playerStunTurns = 0;
    playerFrozenTurns = 0;
    playerBurnStacks = 0;
    caveAuraClock = 0;
    // 洞府敌人：进场就把它的特殊增幅说清楚，免得玩家以为是 bug
    if (activeEnemy.caveBuff) {
      const b = activeEnemy.caveBuff;
      pushBattleLog('⚠ 洞府增幅：' + (activeEnemy.name || '敌人') + ' 带「' + b.name + '」——' + b.desc, 'enemy');
      addTextPopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 226, b.name, b.color);
    }
    // 先手是玩家时，第一回合也算一个"回合"（图腾圣物同样会出手）
    if (battleTurn === 'player') totemTurnSpell();
  }

  function winBattle() {
    // 世界BOSS：走单独的结算（不发尸体、不刷路上的敌人）
    if (worldBossRun) { finishWorldBossFight(); return; }
    const level = activeEnemy.level;
    const corpseX = activeEnemy.x;
    const wasElite = !!activeEnemy.elite;
    const wasLingLoot = !!activeEnemy.lingLoot;
    const wasLingNode = activeEnemy.lingNode || null;
    const wasLegend = !!activeEnemy.legend;
    const enemyTier = activeEnemy.tier || 'common';
    const enemySprite = activeEnemy.sprite;
    const actKind = activeEnemy.activityKind || null;        // 临时副本：打的是怪还是玩家
    const actId = activeEnemy.activityTargetId || null;
    const wasBoss = !!activeEnemy.boss;                      // 每日副本的关卡BOSS
    // 普通冒险道路：只把 common / magic 的击杀计入下一只敌人的品级保底
    if (!cave && !legendMap) roadKillTick(enemyTier);
    activeEnemy = null;
    battleGroup.length = 0;
    defeatedEnemies++;
    killsTotal++;
    /* 门派任务的击杀计数已挪到 resolveEnemyDown（逐个敌人结算），这里不再重复计数 */
    // 世界BOSS 时装：击杀积累充能（精英 +15% / 普通 +5%）
    addCostumeCharge(wasElite ? COSTUME_CHARGE_PER_ELITE : COSTUME_CHARGE_PER_KILL,
      wasElite ? '精英敌人' : '普通敌人');
    lingRollDrop('你击败了一名敌人');            // 灵脉：击杀敌人有几率拾到灵气编号
    if (wasLingLoot) lingLootOnKill({ lingLoot: true, lingNode: wasLingNode });   // 机缘守卫的奖励
    // 瓶颈任务：击败敌人也算进度（够了就自动冲关）
    checkBottleneckTask();
    gloveOnKill();      // 手套词缀：每击败一名敌人获得 X 生命 / X 灵力
    /* 击杀经验随敌人等级提升：Lv1 敌人 10 点。
     * 武者境（≤ 大圆满，还没练气）的冒险敌人经验下调 30%，放慢前期升级；
     * 练气之后恢复原值。 */
    const xpBase = 10 * Math.max(1, level);
    const xpMult = playerLevel() <= TRIBULATION_STAGE ? WUZHE_XP_MULT : 1;
    // 每日副本"双倍场"：经验 ×2
    const xpDouble = dungeonRewardMul();
    gainXp(Math.max(1, Math.round(xpBase * xpMult * xpDouble)));
    if (xpDouble > 1) pushBattleLog('副本双倍：本场经验 ×2（' + Math.round(xpBase * xpMult) + ' → ' +
      Math.round(xpBase * xpMult * xpDouble) + '）', 'reward');
    pendingVictory = 0;
    damagePopups = [];
    player.hurtT = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 0;
    ally = null;
    enemyMinionTurns = 0;
    clearDefendState();          // 打赢了也把防御姿态 / 护盾特效收掉（本来只在"下一场开始时"才清）

    /* 简易战利品：只恢复少量生命。
     *   灵力（法力）**不再**战斗胜利后自动回满 —— 想补灵力得去灵脉旁打坐修炼，
     *   所以战斗里放技能要算着灵力用。 */
    combat.hp = Math.min(combat.maxHp, combat.hp + 22);
    // 灵宝「聚宝灵纹」：每场战斗胜利必然额外掉 10 金币
    if (treasureSpecial('goldPerWin')) {
      gold += 10;
      updateGold();
      pushBattleLog('灵宝「聚宝灵纹」：本场胜利额外获得 10 金币', 'reward');
    }
    // 血瓶充能：不再"杀一个就 +1"，而是累计【10 个普通敌人 + 1 个精英/传奇敌人】
    addFlaskKill(wasElite || wasLegend);
    // 留下可搜索的尸体（记住类型，尸体用同一套美术）
    // 掉落物在这里就掷好"摆在原地"：有装备就按品级亮光柱，只有金币就是默认样式
    // 洞府内的怪：必掉一件装备（品级仍然按敌人品级封顶）
    const caveGear = !!cave;
    corpse = {
      worldX: corpseX, level: level, tier: enemyTier, sprite: enemySprite, searched: false,
      forceGear: caveGear,
      drops: rollCorpseLoot(level, enemyTier, false, caveGear)
    };
    /* 关卡BOSS：额外掉一份这一天的专属掉落物（深渊入侵 = 深渊战利品） */
    if (wasBoss) {
      const invDef = currentInvasionDef();
      if (invasionHasLoot(invDef)) {
        const extra = makeDungeonLootItem(invDef);
        if (extra) {
          corpse.drops.push(extra);
          pushBattleLog('★ 击败关卡BOSS「' + (enemySpriteByKey(enemySprite) || {}).name + '」：额外掉落「' + extra.name + '」', 'reward');
        }
      }
      /* 表情包：0.1% 极小几率（迷雾副本的僵尸王） */
      const stkDef = rollStickerDrop();
      if (stkDef) {
        const stk = makeStickerItem(stkDef);
        corpse.drops.push(stk);
        pushBattleLog('★★★ 关卡BOSS 掉落了聊天表情包「' + stk.name + '」！回背包点「使用」激活后，聊天表情栏里就能发。', 'reward');
        showToast('★★★ 掉落了聊天表情包「' + stk.name + '」！');
      }
    }

    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    gameState = 'walk';
    if (cave && cave.def.activity) {
      // 临时副本：把被打倒的怪 / 玩家从本层移走，继续在这层搜刮（不刷洞府怪、不结算通关）
      activityAfterFight(actKind, actId);
      updateCaveBar();
      // 注：真人决斗的胜负由服务端判定（act:pvpEnd），这里不需要本地结算
    } else if (cave) {
      // 洞府：清掉这一波后，还有敌人就继续下一波，清空则结算洞府奖励
      if (cave.remaining > 0) {
        setTimeout(function () { if (cave) spawnCaveEnemy(); }, 700);
      } else {
        finishCave();
      }
    } else if (legendMap) {
      if (legendMap.wave >= 10) {
        legendMap = null;
        updateLegendBar();
        spawnNextEnemy();
        setTimeout(function () { showToast('传奇BOSS地图通关！'); }, 1300);
      } else {
        spawnNextLegendWave();
      }
    } else {
      spawnNextEnemy();
    }
    updateEliteButton();
    updateDist();
    updateBattleHud();

    const km = (player.worldX - START_X) / KM_UNIT;
    let msg = '击败 Lv.' + level + ' 拦路者！点击地上的尸体搜索战利品（' + km.toFixed(1) + ' km）';
    if (defeatedEnemies % 10 === 0) {
      eliteCharges += 1;
      msg = '累计击败 10 名敌人，获得 1 次精英挑战机会！';
    }
    if (wasLegend) {
      // 传奇敌人：奖励显著更好
      const rareA = makeEquipment(level, 'rare');
      const rareB = makeEquipment(level, 'rare');
      const stoneA = makeActiveSkillStone();
      const stoneB = makeActiveSkillStone();
      const gem = gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]);
      const bonusGold = randInt(100, 200);
      gold += bonusGold;
      updateGold();
      inventory.push(rareA);
      inventory.push(rareB);
      inventory.push(stoneA);
      inventory.push(stoneB);
      inventory.push(gem);
      if (Math.random() < 0.001) inventory.push(makeLegendStone());
      if (Math.random() < 0.2) inventory.push(makePetSkillBook());
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      pushBattleLog('传奇战利品：黄色装备 ×2、主动技能石 ×2、辅助技能石 ×1、金币 +' + bonusGold, 'reward');
      setTimeout(function () {
        showToast('传奇战利品：黄色装备 ×2、主动技能石 ×2、辅助技能石 ×1、金币 +' + bonusGold);
      }, 1200);
    } else if (wasElite) {
      // 精英：必掉黄色装备 + 主动技能石
      const rareGear = makeEquipment(level, 'rare');
      const stone = makeActiveSkillStone();
      inventory.push(rareGear);
      inventory.push(stone);
      let extra = '';
      // 小概率掉落：传奇Boss召唤石 0.1%
      if (Math.random() < 0.001) {
        inventory.push(makeLegendStone());
        extra += ' + 传奇Boss召唤石 ×1';
      }
      // 宠物技能书：精英 5%
      if (Math.random() < 0.05) {
        inventory.push(makePetSkillBook());
        extra += ' + 宠物技能书 ×1';
      }
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      pushBattleLog('精英战利品：黄色装备「' + rareGear.name + '」+ 主动技能石 ×1' + extra, 'reward');
      setTimeout(function () {
        showToast('精英战利品：黄色装备「' + rareGear.name + '」+ 主动技能石 ×1' + extra);
      }, 1200);
    }
    updateEliteButton();
    pushBattleLog(msg, 'reward');
    showToast(msg);
  }

  /* ---------- 浮动提示（屏幕中央偏上的消息条）----------
   *   以前每条提示都是 absolute + 固定 top，同一时刻来两条就原地重叠、糊成一团。
   *   现在统一挂进 #toastBox（flex 纵列）：新消息追加到列尾，
   *   多条消息自上而下逐条排开，互不遮挡；退场时先收高度再移除，
   *   下面的消息跟着平滑上移，不会突然跳位。 */
  const TOAST_LIFE_MS = 2300;      // 和 CSS 里 toastFade 的时长保持一致
  const TOAST_COLLAPSE_MS = 220;   // 和 CSS 里 #toast 的 transition 时长保持一致

  function toastBox() {
    const host = ui.wrap || ui.hud;
    if (!host) return null;
    let box = document.getElementById('toastBox');
    if (!box) {
      box = document.createElement('div');
      box.id = 'toastBox';
    }
    // 挂在 #wrap 上并抬高 z-index，保证弹窗（例如角色窗口）打开时提示依然可见
    if (box.parentNode !== host) host.appendChild(box);
    placeToastBox(box);
    return box;
  }

  /* 提示列默认贴右上角；战斗中右上角被「战斗信息」面板占着，
   *   这里按面板的**实际底边**把提示列让到它下面 —— 面板高度会随战报文字长短变化，
   *   在 CSS 里写死一个 top 迟早会被血条压住。战斗日志窗口打开时面板被遮罩压住，回到最上面。 */
  function placeToastBox(box) {
    const host = ui.wrap || ui.hud;
    const hud = document.getElementById('battleHud');
    const log = document.getElementById('logPopup');
    if (!host || !box) return;
    const inBattle = hud && !hud.classList.contains('hidden');
    const logOpen = log && !log.classList.contains('hidden');
    let top = 14;
    if (inBattle && !logOpen) {
      const hudBottom = hud.getBoundingClientRect().bottom - host.getBoundingClientRect().top;
      top = Math.max(14, Math.round(hudBottom) + 10);
    }
    box.style.top = top + 'px';
  }

  function showToast(text) {
    const box = toastBox();
    if (!box) return null;
    const el = document.createElement('div');
    el.id = 'toast';
    el.textContent = text;
    box.appendChild(el);                       // 追加到列尾 → 新消息排在最下面
    setTimeout(function () { dismissToast(el); }, TOAST_LIFE_MS);
    return el;
  }

  // 退场：先把当前高度钉住，下一帧再收成 0，让 transition 有起点
  function dismissToast(el) {
    if (!el || !el.parentNode) return;
    el.style.height = el.getBoundingClientRect().height + 'px';
    void el.offsetHeight;
    el.style.height = '0px';
    el.style.marginTop = '0px';
    el.style.paddingTop = '0px';
    el.style.paddingBottom = '0px';
    el.style.overflow = 'hidden';
    el.style.opacity = '0';
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, TOAST_COLLAPSE_MS);
  }

  // ---------- 战斗行动 ----------
  // 玩家行动结束后切换到敌人回合，敌人稍后再反击，避免双方同时出手
  function beginEnemyTurn(message) {
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return;
    battleTurn = 'enemy';
    enemyTurnTimer = 0.9;
    setBattleLog(message || '拦路者正在逼近，准备反击……');
    setControls('battle');
  }

  function resetPlayerIdleTimer() {
    playerIdleTimer = 10;
  }

  // 玩家先手但持续 10 秒不行动：敌人会抢先进攻
  function updatePlayerIdleTimer(dt) {
    if (gameState !== 'battle' || battleTurn !== 'player' || pendingVictory > 0) return;
    if (slashFx) return;
    playerIdleTimer -= dt;
    if (playerIdleTimer <= 0) {
      playerIdleTimer = 10;
      enemyCounterAttack(false, true);
    }
  }

  // 爪：普通攻击命中后吸取 3 点生命
  function clawLeech() {
    let heal = 0;
    if (equipment.weapon && equipment.weapon.weaponType === '爪') heal += 3;
    // 灵宝「血饮魔纹」：每次命中额外回复 5% 最大生命
    if (treasureSpecial('lifesteal10')) heal += Math.round(combat.maxHp * 0.05);
    if (heal <= 0) return;
    if (combat.hp >= combat.maxHp) return;
    heal = Math.min(heal, combat.maxHp - combat.hp);
    combat.hp += heal;
    addDamagePopup(
      player.worldX - cameraX() - 82,
      GROUND_Y - 160,
      heal,
      '#6dff8d',
      true
    );
    updateBattleHud();
  }

  // 宠物的技能来源：法杖灵宠用「召唤宠物」，技能召唤出来的用对应召唤技能
  function petSkillDef(p) {
    const id = (p && p.skillId) || 'summonPet';
    return knownSkills.filter(function (s) { return s.id === id; })[0];
  }

  /* 宠物攻击数值：1 级基础伤害 4~9，每提升 1 级 +15%（等级跟玩家等级走） */
  function petAttackValue(p) {
    if (!p) return 0;
    const range = petDamageRangeOf(p);
    const petSkill = petSkillDef(p);
    const gemMult = 1 + skillGemSum(p.skillId || 'summonPet', ['summonDamage']) / 100;
    // 「处决」这类通用辅助对被辅助的召唤技能同样生效
    const supMult = supportDamageMult(p.skillId || 'summonPet', activeEnemy);
    // 召唤生物辅助（肉盾 / 狂噬 / 专精 / 仆从）
    const mods = minionGemMods(p);
    return Math.max(1, Math.round(
      randInt(range[0], range[1]) * (p.atkMult || 1) * gemMult * supMult *
      skillLevelMult(petSkill) * mods.dmgMult * mods.atkMult
    ));
  }

  // 所有宠物一起出手（列表里每只都打一次），返回 true 表示敌人已被击败
  let lastPetParts = []; // 最近一次宠物出手的逐只明细（供战报逐条列出）
  function petApplyOnHitEffects(p, enemy, dmg) {
    if (!p || !enemy || enemy.hp <= 0) return [];
    const labels = [];
    if (p.sprite === 'scorpio') {
      const before = enemy.burnStacks || 0;
      enemy.burnStacks = Math.min(BURN_STACK_MAX, before + 1);
      if (enemy.burnStacks > before) {
        addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 196, '点燃 +1', '#ff9a3c');
        labels.push('点燃 +1');
      }
    } else if (p.sprite === 'snake') {
      const per = Math.max(1, Math.round(dmg * 0.12));
      const old = enemy.poison && enemy.poison.turns > 0 ? enemy.poison : null;
      enemy.poison = {
        dmg: (old ? old.dmg : 0) + per,
        turns: Math.max(old ? old.turns : 0, GLOVE_DOT_TURNS),
        stacks: (old ? old.stacks || 1 : 0) + 1
      };
      addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 196, '剧毒 +1', '#8fe0a0');
      labels.push('剧毒 +1');
    } else if (p.sprite === 'vulture') {
      const per = Math.max(1, Math.round(dmg * 0.15));
      const old = enemy.bleed && enemy.bleed.turns > 0 ? enemy.bleed : null;
      enemy.bleed = {
        dmg: (old ? old.dmg : 0) + per,
        turns: Math.max(old ? old.turns : 0, GLOVE_DOT_TURNS),
        stacks: (old ? old.stacks || 1 : 0) + 1
      };
      addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 196, '流血 +1', '#ff7b7b');
      labels.push('流血 +1');
    }
    return labels;
  }

  function petFollowUp() {
    if (!petAlive() || gameState !== 'battle' || !activeEnemy) return false;
    const list = alivePets();
    // 每只宠物各自的伤害单独飘字（带名字），方便对比各宠物的输出效率
    const PET_HIT_COLORS = {
      scorpio: '#ffd070', hyena: '#ff9a5a', snake: '#8fe0a0', vulture: '#9fd4ff'
    };
    const parts = [];
    lastPetParts = parts;
    const enemyPopX = activeEnemy.x - cameraX() + 88;
    let total = 0;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      p.attackT = 0.45; // 播放攻击动作
      p.animT = 0;
      petAttackCount += 1;
      // 世界BOSS：境界不够，宠物同样打不动（只显示 MISS）
      if (worldBossRun && !worldBossLevelOk()) {
        addTextPopup(enemyPopX, GROUND_Y - 150 - i * 34, 'MISS', '#9fb6d4');
        parts.push('「' + p.name + '」MISS');
        pushBattleLog('宠物「' + p.name + '」攻击世界BOSS：境界不足，MISS', 'system');
        continue;
      }
      const dmg = petAttackValue(p);
      total += dmg;
      const color = PET_HIT_COLORS[p.sprite] || '#ffe08a';
      activeEnemy.hp -= dmg;
      activeEnemy.hurtT = HURT_TIME;
      // 飘在敌人身上：第 1 只最上，之后依次往下错开，避免重叠
      addTextPopup(enemyPopX, GROUND_Y - 150 - i * 34, p.name + ' ' + dmg, color);
      parts.push('「' + p.name + '」' + dmg);
      pushBattleLog('宠物「' + p.name + '」造成 ' + dmg + ' 点伤害', 'reward');
      const statusFx = petApplyOnHitEffects(p, activeEnemy, dmg);
      if (statusFx.length) parts.push('「' + p.name + '」' + statusFx.join('/'));
      // 鬣狗：35% 几率再攻击一次
      if (p.sprite === 'hyena' && activeEnemy.hp > 0 && Math.random() < HYENA_DOUBLE_PCT) {
        const extra = petAttackValue(p);
        activeEnemy.hp -= extra;
        activeEnemy.hurtT = HURT_TIME;
        total += extra;
        addTextPopup(enemyPopX, GROUND_Y - 150 - i * 34 - 20, '鬣狗追击 ' + extra, '#ffb07a');
        parts.push('「鬣狗」追击 ' + extra);
        pushBattleLog('宠物「鬣狗」35% 追击，再造成 ' + extra + ' 点伤害', 'reward');
      }
      if (activeEnemy.hp <= 0) {
        activeEnemy.hp = 0;
        setBattleLog('「' + p.name + '」扑向敌人，造成 ' + dmg + ' 点伤害并终结战斗！');
        updateBattleHud();
        if (!resolveEnemyDown()) return false;
        pendingVictory = 0.5;
        return true;
      }
    }
    // 战报逐只列出，不再合并成一个总数
    setBattleLog('宠物出手：' + parts.join('，'));
    updateBattleHud();
    return total;
  }

  // 召唤普通敌人（临时召唤物，攻击 3 次）
  function allyFollowUp() {
    if (!ally || ally.turns <= 0 || gameState !== 'battle' || !activeEnemy) return false;
    const allySkill = ally.skillId
      ? knownSkills.filter(function (s) { return s.id === ally.skillId; })[0]
      : null;
    const mult = (1 + skillGemSum(ally.skillId || '', ['summonDamage']) / 100) *
      (allySkill ? skillLevelMult(allySkill) : 1);
    /* 临时召唤物同样按"召唤物基础数值"算：1 级 4~9，每级 +15%（等级跟玩家等级走） */
    const range = petDamageRangeOf(null);
    const dmg = Math.max(1, Math.round(randInt(range[0], range[1]) * mult));
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    addDamagePopup(
      activeEnemy.x - cameraX() + 104,
      GROUND_Y - 196,
      dmg,
      '#b28cff'
    );
      ally.turns -= 1;
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      ally = null;
      setBattleLog('你的召唤物造成 ' + dmg + ' 点伤害并终结了敌人！');
      updateBattleHud();
      if (!resolveEnemyDown()) return false;
      pendingVictory = 0.5;
      return true;
    }
    if (ally.turns <= 0) ally = null;
    updateBattleHud();
    return dmg;
  }

  // 玩家出手后：宠物 → 召唤物 → 敌人回合
  function afterPlayerOffense() {
    const parts = [];
    const petOut = petFollowUp();
    if (petOut === true) return true;
    if (typeof petOut === 'number' && petOut > 0) {
      // 逐只列出宠物伤害（lastPetParts 由 petFollowUp 填好）
      parts.push('宠物出手 ' + (lastPetParts.length
        ? lastPetParts.join('、')
        : (petOut + ' 点伤害')));
    }
    const allyOut = allyFollowUp();
    if (allyOut === true) return true;
    if (typeof allyOut === 'number') parts.push('召唤物造成 ' + allyOut + ' 点伤害');
    beginEnemyTurn(parts.length ? parts.join('，') + '，敌人准备反击……' : '');
    return false;
  }

  function doPlayerAction(type) {
    // 法术按钮：非战斗场景也能打开技能窗口（只看不施法），所以放在战斗判定之前
    if (type === 'spell' && gameState === 'walk') {
      openSpellPanel();
      return;
    }
    /* 真人交锋：普攻 / 法术 / 防御都可用，三者都会结束本回合 */
    if (pvpDuel) {
      if (type === 'attack') return pvpPlayerAttack();
      if (type === 'defend') return pvpGuard();
      if (type === 'spell') {
        openSpellPanel();      // 点技能格里的一招就能出手
        showToast('点技能格里的一招即可出手（施法同样结束本回合）。');
        return true;
      }
      return false;
    }
    if (gameState !== 'battle' || !activeEnemy) return;
    if (pendingVictory > 0) return;
    if (battleTurn !== 'player') return;
    if (slashFx) {
      setBattleLog('血刀还在劈落途中，先等这一击落地……');
      return;
    }

    if (type === 'attack') {
      resetPlayerIdleTimer();
      playPlayerAttack(); // 角色出手动作
      playSfx('attack');
      startAttackFx(activeEnemy.x); // 普通攻击也挥一刀（纯视觉，不影响结算）
      // 世界BOSS：境界不够 → 这一击直接 MISS（只挨打、打不动）
      if (worldBossRun && !worldBossLevelOk()) {
        worldBossMiss('你挥出一击，却从「' + worldBossRun.def.name + '」身上穿了过去——境界不足，MISS');
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      const baseDmg = Math.max(1, Math.round(
        (randInt(23 + derived.flatAtk, 34 + derived.flatAtk) + Math.floor(activeEnemy.level / 3)) *
        gemDamageMult('attack')
      ));
      const hit = rollPlayerDamage(baseDmg);
      const dmg = hit.dmg;
      // 攻击速度：按概率追加一次 60% 伤害的追击
      const extraHit = derived.atkSpeed > 0 && Math.random() * 100 < derived.atkSpeed;
      const extraDmg = extraHit ? Math.max(1, Math.round(dmg * 0.6)) : 0;
      // 长弓基底「攻击时 X% 几率额外射出一支箭矢」：独立一击，伤害单独结算与显示
      const arrowHit = rollExtraArrow();
      bowStat.attacks++; if (arrowHit) bowStat.procs++;
      const arrowDmg = arrowHit ? rollPlayerDamage(Math.max(1, Math.round(
        (randInt(23 + derived.flatAtk, 34 + derived.flatAtk) + Math.floor(activeEnemy.level / 3)) *
        gemDamageMult('attack')
      ))).dmg : 0;
      activeEnemy.hp -= dmg + extraDmg + arrowDmg;
      activeEnemy.hurtT = HURT_TIME;
      gloveOnHit(activeEnemy);   // 手套词缀：命中回血 / 异常状态几率
      caveReflectOnPlayerHit(activeEnemy, dmg + extraDmg + arrowDmg);   // 洞府「反伤」增幅
      addDamagePopup(
        activeEnemy.x - cameraX() + 88,
        GROUND_Y - 148,
        dmg,
        hit.crit ? '#ff4d4d' : '#ff5b40'
      );
      if (arrowDmg) {
        addDamagePopup(activeEnemy.x - cameraX() + 118, GROUND_Y - 178, arrowDmg, '#ffd76a');
      }
      maybeApplyBurn(activeEnemy, 'slash');   // 炎军：普通攻击（共用「劈砍」的宝石）
      if (extraDmg) maybeApplyBurn(activeEnemy, 'slash');   // 攻速追击也算一次伤害
      if (arrowDmg) maybeApplyBurn(activeEnemy, 'slash');   // 额外箭矢也算一次伤害
      setBattleLog(
        '你发起攻击，造成 ' + dmg + ' 点伤害' +
        (hit.crit ? '（暴击）' : '') +
        (extraDmg ? '，攻速追加 ' + extraDmg + ' 点' : '') +
        (arrowDmg ? '，长弓额外箭矢 ' + arrowDmg + ' 点' : '') + '。'
      );
      if (arrowDmg) {
        pushBattleLog('长弓基底触发：额外射出一支箭矢，造成 ' + arrowDmg + ' 点伤害', 'player');
      }
      combat.mp = Math.min(derived.mpUsable, combat.mp + 5);
      clawLeech();
      if (activeEnemy.hp <= 0) {
        activeEnemy.hp = 0;
        setBattleLog('你击败了一个敌人！');
        updateBattleHud();
        if (!resolveEnemyDown()) {
          // 还有同伙：这一回合他们照样会还手（不能白打一轮）
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
    } else if (type === 'spell') {
      // 打开已学习技能窗口，由玩家选择要使用的技能
      openSpellPanel();
      return;
    } else if (type === 'defend') {
      /* 防御：本回合受到的敌人伤害 -35%，另有 5% 几率完全格挡（原「逃跑」已移除）。
       * 练气期及以上：花一点灵力撑起"灵力护盾"——减伤 55%、完全格挡 15%。 */
      resetPlayerIdleTimer();
      const dCost = defendSpiritCostNow();
      defendSpiritPowered = dCost > 0 && paySpirit(dCost);
      defending = true;
      startDefendFx();   // 角色右侧激活护盾特效（pipo-btleffect208）
      addTextPopup(player.worldX - cameraX() - 86, GROUND_Y - 150,
        defendSpiritPowered ? '灵力护盾' : '防御', '#9fd4ff');
      const msg = defendSpiritPowered
        ? ('你以灵力撑起护盾：受到的伤害 -' + Math.round(SPIRIT_DEFEND_REDUCE * 100) +
          '%，并有 ' + SPIRIT_DEFEND_BLOCK_PCT + '% 几率完全格挡（灵力 -' + dCost + '）。')
        : ('你举起盾牌进入防御姿态：受到的伤害 -' + Math.round(DEFEND_REDUCE * 100) +
          '%，并有 ' + DEFEND_BLOCK_PCT + '% 几率完全格挡。' +
          (dCost > 0 ? '（灵力不足，本回合只能普通防御）' : ''));
      setBattleLog(msg);
      pushBattleLog(msg, 'player');
      updateBattleHud();
      afterPlayerOffense();
      return;
    }
    updateBattleHud();
  }

  function openSpellPanel() {
    // 非战斗场景也允许打开（查看技能 / 镶嵌宝石），只是点技能格不会施法
    if (gameState !== 'battle' && gameState !== 'walk') return;
    renderSpellList();
    ui.spellPopup.classList.remove('hidden');
  }

  function closeSpellPanel() {
    hideSkillTip();
    ui.spellPopup.classList.add('hidden');
  }

  // 激活 / 取消激活：自动冒险时优先使用的技能（全局只有一个）
  function toggleAutoSkill(skill) {
    if (!skill) return false;
    if (skill.id === 'summonPet') {
      showToast('「召唤宠物」是法杖带来的被动技能，不需要激活。');
      return false;
    }
    if (autoSkillId === skill.id) {
      autoSkillId = null;
      showToast('已取消「' + skill.name + '」的自动冒险优先。');
    } else {
      autoSkillId = skill.id;
      showToast('已激活「' + skill.name + '」：自动冒险时会优先使用它。');
    }
    saveGame();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return autoSkillId === skill.id;
  }

  // ---------- 尸体搜索 ----------
  function searchCorpse(silent) {
    if (!corpse || corpse.searched) return;
    playSfx('pickup');     // 搜尸拾取音效
    // 掉落物在敌人倒下时就已经摆在原地了，这里把它真正收进背包
    const fromCave = !!corpse.forceGear;   // 洞府内的怪：必掉装备
    const drops = (corpse.drops && corpse.drops.length)
      ? corpse.drops
      : rollCorpseLoot(corpse.level, corpse.tier || 'common', false, fromCave);
    // 临时副本里搜到的战利品同样算"副本内所得"（副本里阵亡会掉回去）
    collectCorpseDrops(drops, !!(cave && cave.def && cave.def.activity));
    corpse = null;
    ui.lootList.textContent = '';
    drops.forEach(function (d) {
      const row = document.createElement('div');
      row.className = 'lootRow';

      const icon = document.createElement('div');
      icon.className = 'lootIcon';
      fillItemIcon(icon, d, d.icon || '?');
      icon.style.borderColor = d.rarityColor || 'rgba(255,255,255,.2)';
      row.appendChild(icon);

      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'lootName';
      name.textContent = d.name || '';
      name.style.color = d.rarityColor || '#ffe9c0';
      const meta = document.createElement('div');
      meta.className = 'lootMeta';
      meta.textContent = d.detail || '';
      info.appendChild(name);
      info.appendChild(meta);
      row.appendChild(info);

      const kind = document.createElement('span');
      kind.className = 'lootMeta';
      kind.textContent = d.type === 'equipment' ? '装备' : d.type === 'potion' ? '道具' : d.type === 'material' ? '材料' : '金币';
      row.appendChild(kind);
      ui.lootList.appendChild(row);
    });
    // （以前这里会在掉落列表最下面塞一条说明文字，每次搜尸体都出现一次，太吵，已经移除。
    //   规则本身没变：洞府里的怪必掉装备，品级由怪物品级决定。）
    if (silent) {
      const summary = drops.map(function (d) {
        return d.kind === 'gold' ? '金币' + d.detail : d.name;
      }).join('、');
      showToast('自动搜索尸体：' + summary);
    } else {
      ui.lootPopup.classList.remove('hidden');
    }
    // 搜索尸体不再清空日志：日志会保留到玩家手动关闭日志窗口为止
  }

  function closeLootPanel() {
    ui.lootPopup.classList.add('hidden');
  }

  // ---------- 装备 / 背包 ----------
  function openGearPanel() {
    renderGearPanel();
    ui.gearPopup.classList.remove('hidden');
  }

  function closeGearPanel() {
    hideItemTip();
    hideBagMenu();
    ui.gearPopup.classList.add('hidden');
  }

  // ---------- 装备回收（背包物品栏右上角的「回收」按钮）----------
  function openRecyclePanel() {
    hideItemTip();
    hideBagMenu();
    renderRecyclePanel();
    ui.recyclePopup.classList.remove('hidden');
  }

  function closeRecyclePanel() {
    ui.recyclePopup.classList.add('hidden');
  }

  function toggleRecycleTier(id) {
    if (!(id in recycleFilter)) return;
    recycleFilter[id] = !recycleFilter[id];
    markSaveDirty();
    renderRecyclePanel();
  }

  function renderRecyclePanel() {
    renderRecycleTiers();
    const preview = ui.recyclePreview;
    if (!preview) return;
    const picked = RECYCLE_TIERS.filter(function (t) { return recycleFilter[t.id]; });
    if (!picked.length) {
      preview.textContent = '还没勾选品级 —— 勾上白 / 蓝 / 金任意一项才能回收（托管也只回收勾选的品级）。';
      preview.classList.remove('ready');
      return;
    }
    const targets = recycleTargets();
    preview.textContent = '将回收：' + picked.map(function (t) { return t.label; }).join(' + ') +
      '　共 ' + targets.length + ' 件，预计 +' + recycleValueOf(targets) + ' 金币' +
      (targets.length ? '' : '（背包里暂时没有这种装备）');
    preview.classList.toggle('ready', targets.length > 0);
  }

  function renderRecycleTiers() {
    const host = ui.recycleTiers;
    if (!host) return;
    host.textContent = '';
    RECYCLE_TIERS.forEach(function (tier) {
      const on = !!recycleFilter[tier.id];
      const owned = inventory.filter(function (it) {
        return it && it.type === 'equipment' && it.rarity === tier.id;
      }).length;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'recycleTier' + (on ? ' on' : '');
      btn.dataset.tier = tier.id;
      btn.style.setProperty('--tierColor', tier.color);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      const tick = document.createElement('span');
      tick.className = 'recycleTick';
      tick.textContent = on ? '✓' : '';
      const name = document.createElement('b');
      name.textContent = tier.label;
      const sub = document.createElement('small');
      sub.textContent = '背包 ' + owned + ' 件';
      btn.appendChild(tick);
      btn.appendChild(name);
      btn.appendChild(sub);
      btn.addEventListener('click', function () { toggleRecycleTier(tier.id); });
      host.appendChild(btn);
    });
  }

  // ---------- 装备洗练 ----------
  let reforgeItem = null;      // 当前在洗练窗口里的装备
  // ============================================================
  // 世界BOSS
  // · 刷新后 3 分钟内可挑战；没被击杀就自己消失，进入冷却后再刷新。
  // · 传送后是一场特殊战斗：BOSS 用敌人美术放大，场上还有一堆"其他玩家"的虚影
  //   （本地机器人）一起打，制造"很多人在打同一个BOSS"的感觉。
  // · 只要玩家对 BOSS 造成过伤害 → 发参与奖励（金币 + 黄色稀有装备 + 世界BOSS宝箱）。
  // · 后期联网：把这里的血量/伤害换成服务端数据 + 排行榜即可。
  // ============================================================
  const WB_WINDOW_SEC = 180;          // 刷新后存在 3 分钟（没被击杀就自己消失）
  // 刷新时刻：东八区（上海）每天 12 次 —— 00:15 / 02:15 / 04:15 … 22:15
  const WB_SCHEDULE_HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
  const WB_SPAWN_MINUTE = 15;
  const WB_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;   // 东八区 UTC+8
  const WB_REWARD_GOLD = 1000;
  const WB_CHEST_TREASURE_CHANCE = 1; // 世界BOSS宝箱开出灵宝的几率（%）
  const WB_ARENA_X = 4000000;         // 世界BOSS战场（远离公路，不会影响路上的敌人）
  const WB_BOSS_SCREEN_X = 880;       // BOSS 在屏幕上的位置（略左移，方便挑战者围在脚下）
  const WB_GHOST_COUNT = 11;          // 场上"其他玩家"虚影数量
  const WB_LOOT_SECONDS = 60;         // 击杀BOSS后，走过去开箱的倒计时（到点自动开）
  const WB_LOOT_CHEST_SCREEN_X = 640; // 宝箱出现的位置：屏幕正中央
  const WB_LOOT_CHEST_DRAW_W = 150;   // 宝箱绘制宽度（原图 895×736）
  const WB_LOOT_REACH = 170;          // 站得多近才能开箱（屏幕像素）
  const WB_GIFT_NOTE = '这只是世界BOSS散落的战利品，正在展示中——谁都拿不走。';
  const WB_GHOST_NAMES = [
    '夜行者', '孤帆远影', '铁匠老王', '白露未晞', '沙漠之鹰', '青衫客',
    '一剑霜寒', '荒野郎中', '逐日者', '月下独行', '半盏清茶', '拾荒人'
  ];

  const wb = {
    phase: 'wait',   // wait = 等刷新；alive = 已刷新可挑战
    nextAt: 0,       // 下一次刷新时间戳
    expiresAt: 0,    // 本次刷新的最后时刻（3 分钟后自己消失）
    spawnedAt: 0,    // 本次刷新的时刻
    maxHp: 0,
    hp: 0,
    hpBossId: '',    // 上面这管血属于哪个 BOSS（换 BOSS 要重新满血）
    killed: 0,
    killedAt: 0,     // 本轮被击杀的刷新时刻（防止同一轮重复刷新）
    claimed: false,  // 本轮（每次刷新）是否已经发过参与奖励
    lastRewardDay: '' // 每日奖励记录（上海日期，每天只能领一次）
  };
  let wbClockSkew = 0;        // 联网校时偏移（毫秒）
  let wbClockSource = 'local'; // 'local' = 本机时钟；'internet' = 已用互联网时间校准
  let wbOverride = null;      // 调试/测试用：直接指定本次刷新窗口
  let wbScheduleClock = 0;    // 面板倒计时刷新节流

  // ---------- 世界BOSS 战场环境效果（每个 BOSS 一套主题场景）----------
  const wbParticles = [];     // 主题粒子：余烬 / 雪 / 孢子 / 尘 / 火星 / 水沫
  let wbDustBands = [];       // 贴地的尘 / 雾带
  let wbClouds = [];          // 云层
  let wbEnvReady = false;
  let wbThemeKey = '';        // 当前已初始化的主题；换 BOSS 就重建环境
  let wbFlash = 0;            // 天光闪强度（1 → 0）
  let wbFlashTimer = 5;
  let wbFlashHold = false;    // 调试用：钉住闪光强度
  let lastBossArtRect = null; // 最近一次绘制的 BOSS 立绘屏幕矩形（调试用）

  /* ============================================================
   * 世界BOSS 战场主题
   *   sky/orb/cloud/far/ground/hill  决定天与地
   *   particle                       决定粒子种类与颜色
   *   ring/flash                     决定脚下法阵与天光闪
   * ============================================================ */
  const WB_THEMES = {
    // 赤沙祭坛（森特欧斯）：原版血沙场景，数值保持不动
    bloodsand: {
      sky: [[0, '#25090c'], [0.45, '#5c1f16'], [0.78, '#9a4a22'], [1, '#c4763a']],
      orb: { x: 300, y: 150, r: 62, core: 'rgba(255,208,170,.92)', glow: 'rgba(255,170,130,.55)' },
      cloud: '96,18,16', cloudA: [0.05, 0.14],
      far: 'rgba(60,20,14,.55)',
      ground: [[0, '#7a3a1c'], [1, '#2b120a']],
      hill: [[0, '#9a5528'], [0.45, '#5f2c13'], [1, '#2a1108']],
      hillEdge: 'rgba(255,196,140,.22)',
      dust: '226,186,132', dustA: [0.05, 0.13],
      particle: { kind: 'ember', max: 70, rate: 28, rise: true, colors: ['255,222,156', '255,142,62', '255,90,40'], size: [1.1, 3.2], speed: [16, 48], life: [1.5, 3.4], sway: 12 },
      ring: ['rgba(255,90,60,A)', 'rgba(255,140,60,.2)', 'rgba(255,130,90,.5)'],
      flash: '255,96,72'
    },
    // 焚心熔窟（熔岩火灵）：焦土 + 熔缝 + 浓密余烬
    lava: {
      sky: [[0, '#1b0604'], [0.4, '#4a1206'], [0.72, '#9c2f08'], [1, '#e0641a']],
      orb: { x: 340, y: 168, r: 74, core: 'rgba(255,236,190,.95)', glow: 'rgba(255,132,50,.6)' },
      cloud: '120,30,10', cloudA: [0.06, 0.17],
      far: 'rgba(38,10,6,.62)',
      ground: [[0, '#5c1f0c'], [1, '#1b0704']],
      hill: [[0, '#7a3210'], [0.45, '#3d1406'], [1, '#170504']],
      hillEdge: 'rgba(255,150,60,.35)',
      dust: '255,150,80', dustA: [0.05, 0.14],
      particle: { kind: 'ember', max: 110, rate: 54, rise: true, colors: ['255,236,180', '255,158,60', '255,90,30'], size: [1.2, 3.8], speed: [26, 78], life: [1.2, 2.8], sway: 16 },
      ring: ['rgba(255,120,40,A)', 'rgba(255,170,60,.24)', 'rgba(255,150,70,.55)'],
      flash: '255,138,60'
    },
    // 蚀月暗窟（暗影巨兽）：紫黑暗窟 + 幽紫浮尘
    shadow: {
      sky: [[0, '#0a0614'], [0.42, '#1d1030'], [0.74, '#3a1c52'], [1, '#5b2c6a']],
      orb: { x: 980, y: 132, r: 58, core: 'rgba(196,168,255,.9)', glow: 'rgba(140,90,220,.5)' },
      cloud: '60,32,92', cloudA: [0.07, 0.2],
      far: 'rgba(18,10,30,.72)',
      ground: [[0, '#2a1a3c'], [1, '#0c0714']],
      hill: [[0, '#3a2450'], [0.45, '#1d1030'], [1, '#0a0614']],
      hillEdge: 'rgba(190,150,255,.28)',
      dust: '150,110,220', dustA: [0.04, 0.12],
      particle: { kind: 'mote', max: 90, rate: 22, rise: true, colors: ['224,200,255', '168,120,255', '110,70,200'], size: [1, 2.6], speed: [8, 26], life: [2.4, 5], sway: 20 },
      ring: ['rgba(180,120,255,A)', 'rgba(140,90,220,.22)', 'rgba(200,160,255,.5)'],
      flash: '170,120,255'
    },
    // 万涛沉船湾（沧涡噬主）：青蓝海雾 + 横掠水沫
    vortex: {
      sky: [[0, '#03111c'], [0.4, '#0a2b3c'], [0.72, '#14506a'], [1, '#2b7f92']],
      orb: { x: 300, y: 140, r: 54, core: 'rgba(200,246,255,.85)', glow: 'rgba(120,220,255,.45)' },
      cloud: '40,90,120', cloudA: [0.06, 0.16],
      far: 'rgba(10,34,46,.6)',
      ground: [[0, '#123a4a'], [1, '#04141d']],
      hill: [[0, '#1d5b6e'], [0.45, '#0d3341'], [1, '#04141d']],
      hillEdge: 'rgba(150,240,255,.3)',
      dust: '180,240,255', dustA: [0.05, 0.15],
      particle: { kind: 'spray', max: 80, rate: 30, rise: false, colors: ['235,255,255', '150,225,255', '90,180,220'], size: [1, 2.8], speed: [60, 150], life: [0.9, 2], sway: 8 },
      ring: ['rgba(90,220,255,A)', 'rgba(120,230,255,.2)', 'rgba(160,240,255,.5)'],
      flash: '150,230,255'
    },
    // 熔渣壁垒（赤熔摧垣）：橙红高温 + 密集火星
    molten: {
      sky: [[0, '#210803'], [0.36, '#5e1606'], [0.68, '#b03f0a'], [1, '#f08326']],
      orb: { x: 660, y: 118, r: 66, core: 'rgba(255,246,214,.96)', glow: 'rgba(255,140,50,.62)' },
      cloud: '150,52,14', cloudA: [0.06, 0.18],
      far: 'rgba(44,12,6,.6)',
      ground: [[0, '#6b2610'], [1, '#1d0805']],
      hill: [[0, '#8a3a12'], [0.45, '#451708'], [1, '#180605']],
      hillEdge: 'rgba(255,170,80,.4)',
      dust: '255,170,90', dustA: [0.05, 0.15],
      particle: { kind: 'ember', max: 120, rate: 60, rise: true, colors: ['255,250,220', '255,180,80', '255,110,40'], size: [1.2, 4], speed: [30, 90], life: [1, 2.6], sway: 18 },
      ring: ['rgba(255,140,50,A)', 'rgba(255,190,80,.24)', 'rgba(255,170,90,.55)'],
      flash: '255,170,90'
    },
    // 古锻工坊（锻枢卫士）：暗铁 + 直冲的锻炉火星
    forge: {
      sky: [[0, '#120c08'], [0.4, '#2c1c0e'], [0.72, '#5a3413'], [1, '#8c5620']],
      orb: { x: 1000, y: 190, r: 92, core: 'rgba(255,214,140,.72)', glow: 'rgba(255,160,60,.5)' },
      cloud: '70,44,20', cloudA: [0.05, 0.14],
      far: 'rgba(26,18,10,.7)',
      ground: [[0, '#3d2a17'], [1, '#130d07']],
      hill: [[0, '#4d3620'], [0.45, '#2a1c0f'], [1, '#120c07']],
      hillEdge: 'rgba(255,196,110,.3)',
      dust: '220,180,120', dustA: [0.04, 0.11],
      particle: { kind: 'spark', max: 90, rate: 46, rise: true, colors: ['255,250,210', '255,200,90', '255,140,50'], size: [0.9, 2.4], speed: [70, 180], life: [0.6, 1.6], sway: 6 },
      ring: ['rgba(255,180,80,A)', 'rgba(255,200,120,.2)', 'rgba(255,200,120,.5)'],
      flash: '255,200,120'
    },
    // 冻牙雪原（寒霜牙裔）：冰蓝 + 飘雪
    frost: {
      sky: [[0, '#0a1a2c'], [0.4, '#1d3f5e'], [0.72, '#5b87a8'], [1, '#a8c8dc']],
      orb: { x: 320, y: 148, r: 60, core: 'rgba(238,250,255,.9)', glow: 'rgba(180,225,255,.5)' },
      cloud: '120,160,190', cloudA: [0.06, 0.16],
      far: 'rgba(28,52,74,.62)',
      ground: [[0, '#8aa8bd'], [1, '#2c4356']],
      hill: [[0, '#b8d2e2'], [0.45, '#6d8ea6'], [1, '#2c4356']],
      hillEdge: 'rgba(255,255,255,.45)',
      dust: '220,240,255', dustA: [0.06, 0.16],
      particle: { kind: 'snow', max: 140, rate: 46, rise: false, colors: ['255,255,255', '226,244,255', '190,222,246'], size: [1.2, 3.4], speed: [22, 62], life: [3, 6], sway: 24 },
      ring: ['rgba(180,230,255,A)', 'rgba(200,240,255,.2)', 'rgba(220,246,255,.55)'],
      flash: '210,240,255'
    },
    // 枯骨荒原（荒原统领）：土黄尘原
    wasteland: {
      sky: [[0, '#402a14'], [0.44, '#77552a'], [0.76, '#b98a4a'], [1, '#d8b077']],
      orb: { x: 880, y: 158, r: 58, core: 'rgba(255,238,196,.8)', glow: 'rgba(255,208,140,.45)' },
      cloud: '150,112,64', cloudA: [0.05, 0.13],
      far: 'rgba(74,52,28,.55)',
      ground: [[0, '#8a6435'], [1, '#3a2713']],
      hill: [[0, '#a67c44'], [0.45, '#6b4c26'], [1, '#30200f']],
      hillEdge: 'rgba(255,224,170,.28)',
      dust: '236,208,160', dustA: [0.06, 0.16],
      particle: { kind: 'dust', max: 80, rate: 26, rise: true, colors: ['246,226,186', '214,180,130', '170,138,96'], size: [1.2, 3], speed: [10, 34], life: [2.4, 5], sway: 26 },
      ring: ['rgba(255,210,140,A)', 'rgba(240,196,130,.2)', 'rgba(255,226,170,.45)'],
      flash: '255,224,170'
    },
    // 残旗古战场（叛旗督军）：昏红 + 飘灰
    banner: {
      sky: [[0, '#1c0a10'], [0.42, '#4a1418'], [0.74, '#7e2a22'], [1, '#a85a34']],
      orb: { x: 960, y: 176, r: 64, core: 'rgba(255,190,140,.85)', glow: 'rgba(230,110,70,.5)' },
      cloud: '110,36,28', cloudA: [0.06, 0.16],
      far: 'rgba(40,16,14,.62)',
      ground: [[0, '#5e3420'], [1, '#22110c']],
      hill: [[0, '#74422a'], [0.45, '#3c2116'], [1, '#1b0d09']],
      hillEdge: 'rgba(255,170,120,.26)',
      dust: '210,168,130', dustA: [0.05, 0.14],
      particle: { kind: 'ash', max: 100, rate: 32, rise: false, colors: ['230,214,196', '180,160,148', '120,104,98'], size: [1, 2.8], speed: [14, 40], life: [3, 6.5], sway: 20 },
      ring: ['rgba(255,120,90,A)', 'rgba(255,150,110,.2)', 'rgba(255,160,130,.45)'],
      flash: '255,140,110'
    },
    // 壤脉地窟（壤脉掘母）：黄土 + 扬尘
    earth: {
      sky: [[0, '#2a1c0e'], [0.42, '#573a1c'], [0.74, '#8f6634'], [1, '#b98d52']],
      orb: { x: 340, y: 172, r: 56, core: 'rgba(255,232,186,.75)', glow: 'rgba(226,182,120,.42)' },
      cloud: '120,90,50', cloudA: [0.05, 0.14],
      far: 'rgba(58,40,20,.6)',
      ground: [[0, '#6d4c26'], [1, '#2a1b0d']],
      hill: [[0, '#8a6335'], [0.45, '#523719'], [1, '#241708']],
      hillEdge: 'rgba(255,214,150,.24)',
      dust: '226,190,140', dustA: [0.07, 0.18],
      particle: { kind: 'dust', max: 95, rate: 34, rise: true, colors: ['240,214,170', '198,158,110', '150,116,74'], size: [1.4, 3.4], speed: [12, 40], life: [2.6, 5.5], sway: 30 },
      ring: ['rgba(230,180,110,A)', 'rgba(200,150,90,.2)', 'rgba(240,200,140,.4)'],
      flash: '240,200,140'
    },
    // 崩岩峡谷（碎脉岩崩巨像）：灰岩 + 落尘
    rockfall: {
      sky: [[0, '#1a1c20'], [0.44, '#3a3f46'], [0.76, '#6b7078'], [1, '#9aa0a8']],
      orb: { x: 300, y: 138, r: 52, core: 'rgba(236,240,246,.8)', glow: 'rgba(190,200,214,.4)' },
      cloud: '90,96,106', cloudA: [0.05, 0.14],
      far: 'rgba(38,42,48,.66)',
      ground: [[0, '#5c6169'], [1, '#23262b']],
      hill: [[0, '#767c86'], [0.45, '#454a52'], [1, '#1d2024']],
      hillEdge: 'rgba(230,236,246,.3)',
      dust: '200,206,216', dustA: [0.06, 0.16],
      particle: { kind: 'dust', max: 90, rate: 30, rise: false, colors: ['236,240,248', '190,196,206', '140,146,156'], size: [1.2, 3.2], speed: [16, 46], life: [2.4, 5], sway: 18 },
      ring: ['rgba(180,190,205,A)', 'rgba(200,210,224,.2)', 'rgba(220,228,240,.45)'],
      flash: '220,228,240'
    },
    // 磐石巢穴（岩壳女王）：灰白岩壳 + 细尘
    rockshell: {
      sky: [[0, '#20242a'], [0.44, '#454c56'], [0.76, '#7b8592'], [1, '#adb8c4']],
      orb: { x: 940, y: 150, r: 56, core: 'rgba(244,248,252,.82)', glow: 'rgba(200,214,228,.42)' },
      cloud: '104,114,126', cloudA: [0.05, 0.13],
      far: 'rgba(44,50,58,.62)',
      ground: [[0, '#6a737f'], [1, '#282e34']],
      hill: [[0, '#8b96a4'], [0.45, '#525b66'], [1, '#20252a']],
      hillEdge: 'rgba(240,246,252,.34)',
      dust: '212,220,230', dustA: [0.05, 0.14],
      particle: { kind: 'dust', max: 80, rate: 26, rise: false, colors: ['246,250,255', '206,214,226', '158,168,180'], size: [1.1, 3], speed: [14, 40], life: [2.6, 5.2], sway: 22 },
      ring: ['rgba(200,214,230,A)', 'rgba(220,230,244,.2)', 'rgba(236,244,252,.45)'],
      flash: '232,240,250'
    },
    // 毒瘴湿地（瘴纹蠕主）：病绿瘴气 + 孢子
    miasma: {
      sky: [[0, '#0a1410'], [0.42, '#16321f'], [0.72, '#3c6432'], [1, '#6d8f42']],
      orb: { x: 350, y: 160, r: 78, core: 'rgba(206,246,160,.55)', glow: 'rgba(140,220,110,.4)' },
      cloud: '56,104,60', cloudA: [0.08, 0.2],
      far: 'rgba(18,36,22,.68)',
      ground: [[0, '#3a4c26'], [1, '#111a0e']],
      hill: [[0, '#4c6630'], [0.45, '#2a3a1b'], [1, '#0f160b']],
      hillEdge: 'rgba(190,240,150,.28)',
      dust: '170,220,130', dustA: [0.08, 0.2],
      particle: { kind: 'spore', max: 100, rate: 34, rise: true, colors: ['224,255,180', '160,226,110', '104,176,70'], size: [1.2, 3.2], speed: [8, 30], life: [3, 6.5], sway: 28 },
      ring: ['rgba(150,230,110,A)', 'rgba(180,240,140,.22)', 'rgba(200,250,160,.5)'],
      flash: '190,246,150'
    }
  };

  function wbTheme() {
    return WB_THEMES[wbThemeKeyOf()] || WB_THEMES.bloodsand;
  }
  function wbThemeKeyOf() {
    const def = worldBossRun && worldBossRun.def;
    return (def && def.theme) || 'bloodsand';
  }

  function initWbEnv() {
    const key = wbThemeKeyOf();
    if (wbEnvReady && wbThemeKey === key) return;   // 同一个 BOSS：不用重建
    wbThemeKey = key;
    wbEnvReady = true;
    const T = wbTheme();
    wbClouds = [];
    for (let i = 0; i < (T.cloud ? 6 : 0); i++) {
      wbClouds.push({
        x: rand(-240, W + 240), y: rand(30, 230), rx: rand(120, 260), ry: rand(26, 60),
        spd: rand(5, 16), a: rand(T.cloudA[0], T.cloudA[1]), seed: rand(0, 10)
      });
    }
    wbDustBands = [];
    for (let i = 0; i < (T.dust ? 8 : 0); i++) {
      wbDustBands.push({
        x: rand(-360, W), y: GROUND_Y + rand(-30, 6), w: rand(180, 460), h: rand(9, 24),
        spd: rand(26, 68), a: rand(T.dustA[0], T.dustA[1])
      });
    }
    wbParticles.length = 0;
    wbFlash = 0;
    wbFlashTimer = rand(4, 9);
  }

  function updateWbEnv(dt) {
    if (!worldBossRun) {
      wbEnvReady = false;
      wbThemeKey = '';
      wbParticles.length = 0;
      wbDustBands = [];
      wbClouds = [];
      wbFlash = 0;
      wbFlashHold = false;
      lastBossArtRect = null;
      return;
    }
    initWbEnv();
    const T = wbTheme();
    // 云层：慢慢横移，飘出画面就从另一侧补进来
    wbClouds.forEach(function (c) {
      c.x -= c.spd * dt;
      if (c.x + c.rx < -60) {
        c.x = W + rand(60, 260);
        c.y = rand(30, 230);
        c.rx = rand(120, 260);
        c.ry = rand(26, 60);
        c.a = rand(T.cloudA[0], T.cloudA[1]);
      }
    });
    // 贴地尘 / 雾带：顺着风向往左掠过地面
    wbDustBands.forEach(function (b) {
      b.x -= b.spd * dt;
      if (b.x + b.w < -80) {
        b.x = W + rand(40, 320);
        b.y = GROUND_Y + rand(-30, 6);
        b.w = rand(180, 460);
        b.h = rand(9, 24);
        b.a = rand(T.dustA[0], T.dustA[1]);
      }
    });
    // 粒子：余烬往上飘 / 雪与灰往下落 / 孢子与浮尘慢慢荡 / 水沫横向掠
    const P = T.particle;
    if (wbParticles.length < P.max && Math.random() < dt * P.rate) {
      const n = randInt(1, 3);
      for (let i = 0; i < n; i++) {
        wbParticles.push({
          x: rand(-20, W + 20),
          y: P.rise ? GROUND_Y - rand(-14, 26) : rand(-40, 190),
          vx: rand(-18, 18),
          vy: P.rise ? -rand(P.speed[0], P.speed[1]) : rand(P.speed[0], P.speed[1]),
          r: rand(P.size[0], P.size[1]),
          t: 0,
          life: rand(P.life[0], P.life[1]),
          sway: rand(0, 10),
          hot: Math.random() < 0.35
        });
      }
    }
    for (let i = wbParticles.length - 1; i >= 0; i--) {
      const e = wbParticles[i];
      e.t += dt;
      e.x += (e.vx + Math.sin((e.t + e.sway) * 2.4) * P.sway) * dt;
      e.y += e.vy * dt;
      if (P.rise) e.vy -= 4 * dt;     // 上升的越飘越慢
      /* 剔除：寿命到了 / 飘出下边界 → 一定删；
       * 「飘太高」只对上升粒子有意义（下落粒子本来就出生在画面上方，
       * 用同一个阈值会把它们刚生成就删掉）。 */
      if (e.t >= e.life || e.y > H + 60 || (P.rise && e.y < GROUND_Y - 460)) wbParticles.splice(i, 1);
    }
    // 天光闪：隔一段时间来一下，照亮整片战场
    wbFlashTimer -= dt;
    if (wbFlashTimer <= 0) {
      wbFlash = 1;
      wbFlashTimer = rand(7, 15);
    }
    if (wbFlash > 0 && !wbFlashHold) wbFlash = Math.max(0, wbFlash - dt * 2.4);
  }

  function drawWbClouds() {
    const T = wbTheme();
    if (!T.cloud) return;
    wbClouds.forEach(function (c) {
      const g = ctx.createRadialGradient(c.x, c.y, 1, c.x, c.y, c.rx);
      g.addColorStop(0, 'rgba(' + T.cloud + ', ' + c.a.toFixed(3) + ')');
      g.addColorStop(1, 'rgba(' + T.cloud + ', 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, c.rx, c.ry, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  function drawWbDustBands() {
    const T = wbTheme();
    if (!T.dust) return;
    wbDustBands.forEach(function (b) {
      const g = ctx.createLinearGradient(b.x, 0, b.x + b.w, 0);
      g.addColorStop(0, 'rgba(' + T.dust + ', 0)');
      g.addColorStop(0.4, 'rgba(' + T.dust + ', ' + b.a.toFixed(3) + ')');
      g.addColorStop(0.75, 'rgba(' + T.dust + ', ' + (b.a * 0.75).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(' + T.dust + ', 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(b.x + b.w / 2, b.y, b.w / 2, b.h, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // 主题粒子：颜色按主题走（余烬暖橙 / 雪白蓝 / 孢子病绿 / 火星亮黄 …）
  function drawWbParticles() {
    if (!wbParticles.length) return;
    const cols = wbTheme().particle.colors;
    ctx.save();
    wbParticles.forEach(function (e) {
      const p = clamp(e.t / e.life, 0, 1);
      const a = (p < 0.15 ? p / 0.15 : 1 - (p - 0.15) / 0.85) * (e.hot ? 0.9 : 0.6);
      const glow = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.r * 4);
      glow.addColorStop(0, 'rgba(' + cols[0] + ', ' + a.toFixed(3) + ')');
      glow.addColorStop(0.4, 'rgba(' + cols[1] + ', ' + (a * 0.6).toFixed(3) + ')');
      glow.addColorStop(1, 'rgba(' + cols[2] + ', 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(' + cols[0] + ', ' + Math.min(1, a * 1.2).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawWbFlash() {
    if (wbFlash <= 0) return;
    ctx.save();
    ctx.fillStyle = 'rgba(' + wbTheme().flash + ', ' + (wbFlash * 0.16).toFixed(3) + ')';
    ctx.fillRect(-40, -40, W + 80, H + 80);
    ctx.restore();
  }

  // ---------- 等级门槛：境界低于世界BOSS → 全部 MISS ----------
  const WB_LEVEL_BOOST = 5;   // 稀有灵宝「破境符诏」：挑战世界BOSS时临时 +5 级
  // 世界BOSS 的门槛按「境界」显示（武者三境 · 炼气境 …），不再显示 Lv.N
  function stageText(level) {
    return stageName(clamp(Math.round(Number(level) || 1), 1, MAX_STAGE));
  }
  function worldBossPlayerLevel() {
    return playerLevel() + (treasureSpecial('levelBoost') ? WB_LEVEL_BOOST : 0);
  }
  function worldBossLevelOk() {
    if (!worldBossRun) return true;
    return worldBossPlayerLevel() >= worldBossRun.boss.level;
  }
  function worldBossMiss(message) {
    if (!worldBossRun) return;
    const boss = worldBossRun.boss;
    addTextPopup(boss.x - cameraX() + 70, GROUND_Y - 190, 'MISS', '#9fb6d4');
    const msg = message || ('你的境界低于「' + worldBossRun.def.name + '」（需要 ' + stageText(boss.level) +
      '，你当前是 ' + stageText(worldBossPlayerLevel()) + '），攻击完全无法破防');
    setBattleLog(msg + '。');
    pushBattleLog(msg, 'system');
    updateBattleHud();
  }

  // ---------- 时间：一律按东八区（上海）算，和本机时区无关 ----------
  function wbNow() { return Date.now() + wbClockSkew; }
  function pad2(n) { return String(n).padStart(2, '0'); }
  function shanghaiParts(ms) {
    const d = new Date(ms + WB_TZ_OFFSET_MS);
    return {
      y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(),
      h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds(), wd: d.getUTCDay()
    };
  }
  function shanghaiDayKey(ms) {
    const p = shanghaiParts(ms);
    return p.y + '-' + pad2(p.mo) + '-' + pad2(p.d);
  }
  function shanghaiClockText(ms) {
    const p = shanghaiParts(ms);
    return pad2(p.h) + ':' + pad2(p.mi) + ':' + pad2(p.s);
  }
  function shanghaiDayStart(ms) {
    return Math.floor((ms + WB_TZ_OFFSET_MS) / 86400000) * 86400000 - WB_TZ_OFFSET_MS;
  }
  function wbSpawnAtInDay(dayStartMs, hour) {
    return dayStartMs + hour * 3600000 + WB_SPAWN_MINUTE * 60000;
  }
  // <= ms 的最近一次刷新时刻
  function wbLastSpawnAt(ms) {
    const dayStart = shanghaiDayStart(ms);
    for (let day = 0; day >= -2; day--) {
      for (let i = WB_SCHEDULE_HOURS.length - 1; i >= 0; i--) {
        const t = wbSpawnAtInDay(dayStart + day * 86400000, WB_SCHEDULE_HOURS[i]);
        if (t <= ms) return t;
      }
    }
    return wbSpawnAtInDay(dayStart - 3 * 86400000, WB_SCHEDULE_HOURS[0]);
  }
  // > ms 的下一次刷新时刻
  function wbNextSpawnAt(ms) {
    const dayStart = shanghaiDayStart(ms);
    for (let day = 0; day <= 2; day++) {
      for (let i = 0; i < WB_SCHEDULE_HOURS.length; i++) {
        const t = wbSpawnAtInDay(dayStart + day * 86400000, WB_SCHEDULE_HOURS[i]);
        if (t > ms) return t;
      }
    }
    return wbSpawnAtInDay(dayStart + 3 * 86400000, WB_SCHEDULE_HOURS[0]);
  }
  // 当天全部刷新时刻（面板展示用）
  function wbTodayTimes(ms) {
    const dayStart = shanghaiDayStart(ms);
    return WB_SCHEDULE_HOURS.map(function (h) { return wbSpawnAtInDay(dayStart, h); });
  }

  /* ---------- 排班：每个刷新时刻只开一个世界BOSS ----------
   *  每日 12 个时刻（00:15 / 02:15 … 22:15）按名单顺序循环排班：
   *    第 n 个刷新时刻 → 名单里第 (n % 13) 个 BOSS
   *  所以每个 BOSS 的刷新时刻都不一样、彼此错开；13 个 BOSS 配 12 个时刻，
   *  每天整体往后挪一位，13 天下来每个 BOSS 都能轮遍所有时刻。
   */
  function wbSlotIndexOf(ms) {
    // ms 必须正好落在某个刷新时刻上，否则返回 -1
    const dayStart = shanghaiDayStart(ms);
    for (let i = 0; i < WB_SCHEDULE_HOURS.length; i++) {
      if (wbSpawnAtInDay(dayStart, WB_SCHEDULE_HOURS[i]) === ms) {
        return Math.round(dayStart / 86400000) * WB_SCHEDULE_HOURS.length + i;
      }
    }
    return -1;
  }
  function wbBossOfSlot(ms) {
    const idx = wbSlotIndexOf(ms);
    if (idx < 0 || !WORLD_BOSSES.length) return null;
    return WORLD_BOSSES[((idx % WORLD_BOSSES.length) + WORLD_BOSSES.length) % WORLD_BOSSES.length];
  }
  // 某个 BOSS 的下一次刷新时刻（往后逐格扫，循环长度 13 格，最多扫两天的时刻表）
  function wbNextSpawnForBoss(def, ms) {
    if (!def) return wbNextSpawnAt(ms);
    let t = ms;
    for (let guard = 0; guard < 40; guard++) {
      t = wbNextSpawnAt(t);
      if (!t) break;
      const boss = wbBossOfSlot(t);
      if (boss && boss.id === def.id) return t;
    }
    return 0;
  }
  // 可选的互联网校时：成功就用网络时间，失败/离线就继续用本机时钟（东八区换算）
  function syncInternetTime() {
    if (skipLoginRequested()) return;          // 自动化测试模式不外联
    if (navigator.onLine === false) return;
    let settled = false;
    const finish = function (utcMs) {
      if (settled || !utcMs || isNaN(utcMs)) return;
      settled = true;
      wbClockSkew = utcMs - Date.now();
      wbClockSource = 'internet';
      if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    };
    const timer = setTimeout(function () { settled = true; }, 5000);
    fetch('https://worldtimeapi.org/api/timezone/Asia/Shanghai', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (d) { clearTimeout(timer); finish(Date.parse(d.utc_datetime || d.datetime)); })
      .catch(function () { clearTimeout(timer); /* 失败就静默用本机时钟 */ });
  }
  let worldBossRun = null;   // 正在打的世界BOSS战场状态
  let wbGhostSeq = 0;
  let wbBossOverrideId = ''; // 调试用：把"当前这一轮的 BOSS"钉死成指定 id（测试用）

  function worldBossDefOf(id) {
    return WORLD_BOSSES.filter(function (d) { return d.id === id; })[0] || null;
  }
  function openWorldBossDef() {
    // 调试用：测试里可以把"当前这一轮"钉死成某个 BOSS，避免用例随真实时间漂
    if (wbBossOverrideId) {
      const forced = worldBossDefOf(wbBossOverrideId);
      if (forced) return forced;
    }
    // 当前刷新窗口轮到哪个 BOSS（按时刻表排班）
    return wbBossOfSlot(wbLastSpawnAt(wbNow())) || null;
  }
  function wbAlive() {
    return wb.phase === 'alive' && wbNow() < wb.expiresAt;
  }
  function fmtClock(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m + ':' + String(s).padStart(2, '0');
  }
  // 长时间用「X 小时 Y 分」，短时间内用 mm:ss
  function fmtDuration(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    if (total < 3600) return fmtClock(ms);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return h + ' 小时 ' + m + ' 分';
  }

  // 刷新 / 消失：完全按东八区（上海）的现实时刻推演
  // 每 2 小时 15 分整点刷新一次 → 刷新后 3 分钟内可以打，没人打死就自己消失。
  function updateWorldBossHud() {
    if (!ui.btnWorldBoss) return;
    const alive = wbAlive();
    if (ui.btnWorldBoss.classList.contains('alive') !== alive) {
      ui.btnWorldBoss.classList.toggle('alive', alive);
    }
    ui.btnWorldBoss.title = alive
      ? '世界BOSS已刷新，快去挑战！'
      : '查看世界BOSS刷新时刻（上海时间 每 2 小时 15 分）';
  }

  function updateWorldBossCycle() {
    const def = openWorldBossDef();
    if (!def) return;
    // 标题 / 加载 / 阵亡界面不推进世界BOSS节奏（免得在标题页弹提示）
    if (gameState !== 'walk' && gameState !== 'battle') return;
    const now = wbNow();
    updateWorldBossHud();

    let spawnedAt;
    let windowEnd;
    if (wbOverride) {
      // 调试/测试：直接指定刷新窗口
      spawnedAt = wbOverride.spawnedAt;
      windowEnd = wbOverride.expiresAt;
    } else {
      spawnedAt = wbLastSpawnAt(now);
      windowEnd = spawnedAt + WB_WINDOW_SEC * 1000;
    }
    wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(now);

    if (now < windowEnd) {
      // 这一轮已经被玩家打死过：本次窗口内不再刷新（等下一个刷新点）
      if (wb.killedAt && wb.killedAt === spawnedAt) {
        wb.phase = 'wait';
        wb.spawnedAt = spawnedAt;
        wb.expiresAt = windowEnd;
        return;
      }
      if (wb.spawnedAt !== spawnedAt || wb.phase !== 'alive') {
        wb.spawnedAt = spawnedAt;
        wb.expiresAt = windowEnd;
        wb.maxHp = def.hp;
        wb.hp = def.hp;
        wb.hpBossId = '';        // 新一轮刷新：血量归属清空，玩家挑哪个 BOSS 就从那个满血开始
        wb.claimed = false;
        wb.killedAt = 0;
        pushBattleLog('世界BOSS「' + def.name + '」已在「' + def.place + '」刷新（上海时间 ' +
          shanghaiClockText(spawnedAt) + '）', 'system');
        showToast('世界BOSS「' + def.name + '」刷新了！3 分钟内没被击杀就会消失。');
        saveGame();
      }
      wb.phase = 'alive';
      wb.expiresAt = windowEnd;
      return;
    }

    // 窗口结束：没被击杀就自己消失
    if (wb.phase === 'alive') {
      wb.phase = 'wait';
      wb.hp = 0;
      pushBattleLog('世界BOSS「' + def.name + '」没被击杀，已经离开了', 'system');
      if (worldBossRun) {
        leaveWorldBoss('世界BOSS消失在风沙里，你被传送回公路。');
      } else {
        showToast('世界BOSS「' + def.name + '」没被击杀，已经消失了。');
      }
    }
    wb.phase = 'wait';
    wb.spawnedAt = spawnedAt;
    wb.expiresAt = windowEnd;
    wb.maxHp = def.hp;
  }

  // ---------- 世界BOSS 列表窗口 ----------
  function openWorldBossPanel() {
    syncInternetTime();     // 打开窗口时顺手校一次时（失败就用本机时钟）
    renderWorldBossPanel();
    toggleWorldBossHelp(false);
    ui.wbPopup.classList.remove('hidden');
  }
  function closeWorldBossPanel() {
    hideWorldBossTip();
    toggleWorldBossHelp(false);
    ui.wbPopup.classList.add('hidden');
  }

  // 右上角「?」帮助：规则说明不再占着窗口主区域
  function toggleWorldBossHelp(show) {
    if (!ui.wbHelp) return;
    const on = show === undefined ? ui.wbHelp.classList.contains('hidden') : !!show;
    ui.wbHelp.classList.toggle('hidden', !on);
    if (ui.wbHelpBtn) ui.wbHelpBtn.classList.toggle('on', on);
  }

  function renderWorldBossPanel() {
    if (!ui.wbList) return;
    // 注意：这里**不能**先 hideWorldBossTip()。
    // 这个窗口每秒重绘一次（刷新倒计时），重建行会把鼠标底下的元素换成新的，
    // 卡片被收起、紧接着 mouseenter 又把它弹回来 —— 看起来就是"每 1~2 秒闪一下"。
    // 现在的做法：重建后如果指针还停在同一行，就地重新贴一次位置（全程不隐藏，不闪）。
    const now = wbNow();
    const open = openWorldBossDef();
    const alive = wbAlive();
    ui.wbNote.textContent = open
      ? (alive
        ? '「' + open.name + '」正在「' + open.place + '」肆虐 · 剩余 ' + fmtClock(wb.expiresAt - now) + '（未被击杀就会消失）'
        : '「' + open.name + '」下次刷新还有 ' + fmtDuration(wb.nextAt - now) +
          '（上海时间 ' + shanghaiClockText(wb.nextAt).slice(0, 5) + '）· 刷新后可挑战 3 分钟')
      : '暂无开放的世界BOSS';
    ui.wbNote.style.color = alive ? '#ffb08a' : '';

    // 上海时间 / 时间源 / 今日奖励
    if (ui.wbClockNow) {
      ui.wbClockNow.textContent = shanghaiClockText(now) +
        '（' + shanghaiDayKey(now) + ' 周' + '日一二三四五六'[shanghaiParts(now).wd] + '）';
    }
    if (ui.wbClockSrc) {
      ui.wbClockSrc.textContent = wbClockSource === 'internet'
        ? '互联网时间（已校准）' : '本机时钟（东八区换算）';
    }
    if (ui.wbRewardState) {
      const canReward = wbRewardAvailable();
      ui.wbRewardState.textContent = canReward ? '未领取（本次可得）' : '今日已领取';
      ui.wbRewardState.className = canReward ? 'ready' : 'done';
    }
    // 当天 12 个刷新时刻
    if (ui.wbSchedule) {
      ui.wbSchedule.textContent = '';
      const times = wbTodayTimes(now);
      const next = wb.nextAt;
      const nextIsToday = shanghaiDayKey(next) === shanghaiDayKey(now);
      const nextClock = shanghaiClockText(next).slice(0, 5);
      times.forEach(function (t) {
        const chip = document.createElement('span');
        const p = shanghaiParts(t);
        const clock = pad2(p.h) + ':' + pad2(p.mi);
        const boss = wbBossOfSlot(t);          // 这个时刻排到哪个 BOSS
        const isTomorrowNext = !nextIsToday && clock === nextClock;
        let cls = 'wbTimeChip';
        if (alive && t === wb.spawnedAt) cls += ' live';
        else if (isTomorrowNext) cls += ' next tomorrow';
        else if (t === next) cls += ' next';
        else if (t < now) cls += ' passed';
        chip.className = cls;
        // 每个时刻下面写上排到的 BOSS，一眼看出"每个 BOSS 什么时候开"
        const ck = document.createElement('b');
        ck.textContent = clock;
        const bn = document.createElement('i');
        bn.textContent = boss ? boss.name : '—';
        chip.appendChild(ck);
        chip.appendChild(bn);
        chip.title = shanghaiDayKey(t) + ' ' + clock + '（上海时间）· ' +
          (boss ? boss.name : '—') + (isTomorrowNext ? ' · 明天' : '');
        ui.wbSchedule.appendChild(chip);
      });
    }

    ui.wbList.textContent = '';
    const rowByDef = {};      // def.id -> 这一行的 DOM（重建后用来把资料图贴回去）
    const curDef = openWorldBossDef();    // 当前这一轮刷新轮到哪个 BOSS
    WORLD_BOSSES.forEach(function (def) {
      const isActive = !!alive && !!curDef && curDef.id === def.id;
      const nextFor = wbNextSpawnForBoss(def, now);   // 这个 BOSS 自己下一次刷新时刻
      const row = document.createElement('div');
      row.className = 'wbRow' + (def.open ? '' : ' locked') + (isActive ? ' activeBoss' : '');

      const lv = document.createElement('span');
      lv.className = 'wbLv';
      lv.textContent = stageText(def.level);

      const info = document.createElement('div');
      info.className = 'wbInfo';
      const name = document.createElement('div');
      name.className = 'wbName';
      name.textContent = def.name;
      const place = document.createElement('span');
      place.className = 'wbPlace';
      const myLv = worldBossPlayerLevel();
      // 刷新节奏在窗口头部和下面 12 个时刻里都写了，行里不再重复，免得每行挤成三行要滚动
      place.textContent = '地点：' + def.place +
        '　·　需要境界 ' + stageText(def.level) +
        (def.open ? '（你是 ' + stageText(myLv) +
          (myLv > playerLevel() ? '，含灵宝 +' + (myLv - playerLevel()) + ' 级' : '') + '）' : '');
      if (def.open && myLv < def.level) place.style.color = '#ff8a6a';
      info.appendChild(name);
      info.appendChild(place);

      const state = document.createElement('div');
      state.className = 'wbState' + (isActive ? ' alive' : '');
      const stateMain = document.createElement('span');
      const count = document.createElement('span');
      count.className = 'wbCount';
      if (!def.open) {
        stateMain.textContent = '未开放';
        count.textContent = '后续版本开放';
      } else if (isActive) {
        stateMain.textContent = '已刷新';
        count.textContent = '剩余 ' + fmtClock(wb.expiresAt - now);
      } else {
        // 每个 BOSS 按时刻表排班：这里显示它自己下一次刷新的时刻
        stateMain.textContent = '下次刷新';
        count.textContent = (nextFor ? shanghaiClockText(nextFor).slice(0, 5) : '--:--') +
          '（还有 ' + fmtDuration(nextFor - now) + '）';
      }
      state.appendChild(stateMain);
      state.appendChild(count);

      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'wbGo';
      go.textContent = '传送';
      const levelOk = myLv >= def.level;      // 境界门槛：达标才允许传送
      // 只有"这一轮轮到的那个 BOSS"才能在刷新窗口里传送
      go.disabled = !def.open || !isActive || !levelOk;
      go.title = !def.open ? '该世界BOSS尚未开放'
        : (!isActive ? '这一轮没轮到它，下次刷新：' +
          (nextFor ? shanghaiDayKey(nextFor) + ' ' + shanghaiClockText(nextFor).slice(0, 5) : '--:--') + '（上海时间）'
          : (!levelOk ? '境界不足：需要 ' + stageText(def.level) + '，你现在是 ' + stageText(myLv)
            : '传送到「' + def.place + '」'));
      go.addEventListener('click', function () { teleportToWorldBoss(def); });

      row.appendChild(lv);
      row.appendChild(info);
      row.appendChild(state);
      row.appendChild(go);
      // 鼠标停在这一行 → 弹出该BOSS的资料图（没配 info 的BOSS不弹）
      if (def.info) {
        row.classList.add('hasInfo');
        row.setAttribute('data-boss-id', def.id);
        row.addEventListener('mouseenter', function () { showWorldBossTip(row, def); });
        row.addEventListener('mouseleave', syncWorldBossTip);
      }
      rowByDef[def.id] = row;
      ui.wbList.appendChild(row);
    });
    // 重绘后校正一次：指针还在同一行就把卡片贴回新行（不隐藏 → 不闪）
    if (wbTipBossId) syncWorldBossTip();
  }

  /* ---------- 世界BOSS 资料图（鼠标悬停在列表行上） ---------- */
  // defs.js 里资料图写的是相对路径（assets/img/boss/stos_info.png），
  // 这里统一补成绝对地址：挂第三方平台 iframe 时也不会去请求平台域名。
  function wbInfoUrl(def) {
    return (def && def.info) ? assetUrl(def.info) : null;
  }

  // 软加载：悬停时才显示，先预取一下避免第一次悬停空白（缺图也不影响游戏）
  function preloadWorldBossInfoImages() {
    WORLD_BOSSES.forEach(function (d) {
      if (!d.info) return;
      const img = new Image();
      img.src = wbInfoUrl(d);
    });
  }

  let wbTipBossId = null;     // 当前资料图对应的 BOSS id
  let wbTipSyncTimer = null;  // 延迟校正用的定时器

  function hideWorldBossTip() {
    wbTipBossId = null;
    if (ui.wbBossTip) ui.wbBossTip.classList.add('hidden');
  }

  // 指针现在停在哪个 BOSS 行上（面板重绘后也能查出来）
  function wbHoveredRow() {
    return document.querySelector('#wbList .wbRow.hasInfo:hover');
  }

  function bossDefById(id) {
    return WORLD_BOSSES.filter(function (d) { return d.id === id; })[0] || null;
  }

  /* 面板每秒重绘一次倒计时：重建时**旧行被移除也会触发 mouseleave**，
     如果直接在 mouseleave 里关卡片，就会出现"每 1~2 秒闪一下"。
     所以这里不立刻关，而是延迟一帧按"指针实际还停在哪一行"来校正：
     仍停在某行上 → 就地贴回去（全程不隐藏，不闪）；确实移开了 → 才关。 */
  function syncWorldBossTip() {
    if (wbTipSyncTimer) clearTimeout(wbTipSyncTimer);
    wbTipSyncTimer = setTimeout(function () {
      wbTipSyncTimer = null;
      if (!ui.wbPopup || ui.wbPopup.classList.contains('hidden')) { hideWorldBossTip(); return; }
      const row = wbHoveredRow();
      if (!row) { hideWorldBossTip(); return; }
      const def = bossDefById(row.getAttribute('data-boss-id'));
      if (def && def.info) showWorldBossTip(row, def);
      else hideWorldBossTip();
    }, 40);
  }

  function showWorldBossTip(row, def) {
    const tip = ui.wbBossTip;
    if (!tip || !def || !def.info) return;
    wbTipBossId = def.id;
    if (ui.wbTipName) ui.wbTipName.textContent = def.name;
    if (ui.wbTipPlace) ui.wbTipPlace.textContent = stageText(def.level) + ' · ' + def.place;
    if (ui.wbTipDetail) {
      ui.wbTipDetail.textContent = def.infoDetail || '';
      ui.wbTipDetail.classList.toggle('hidden', !def.infoDetail);
    }
    const infoUrl = wbInfoUrl(def);
    if (ui.wbTipImg && infoUrl && ui.wbTipImg.getAttribute('src') !== infoUrl) ui.wbTipImg.src = infoUrl;

    /* 卡片位置固定在舞台右上角（占屏幕 1/4、半透明），由 CSS 决定，
     * 这里只负责把它显示出来 —— 不再按鼠标位置算坐标，也就不会挡住列表。 */
    tip.classList.remove('hidden');
    tip.style.left = '';
    tip.style.top = '';
  }

  // 窗口开着的时候每秒刷新倒计时
  let wbPanelClock = 0;
  function updateWorldBossPanelClock(dt) {
    if (!ui.wbPopup || ui.wbPopup.classList.contains('hidden')) return;
    wbPanelClock += dt;
    if (wbPanelClock < 1) return;
    wbPanelClock = 0;
    renderWorldBossPanel();
  }

  // ---------- 传送 / 离开 ----------
  function teleportToWorldBoss(def) {
    if (!def || !def.open) {
      showToast('该世界BOSS尚未开放。');
      return false;
    }
    if (!(arguments.length > 1 && arguments[1] && arguments[1].force)) {
      // 境界不够就不让进（以前能进去但全是 MISS，现在直接拦在门口）
      const myLv = worldBossPlayerLevel();
      if (myLv < def.level) {
        showToast('境界不足：需要 ' + stageText(def.level) + '（你是 ' + stageText(myLv) +
          '），境界达标后才能传送过去。');
        renderWorldBossPanel();
        return false;
      }
    }
    if (gameState !== 'walk') {
      showToast('战斗中（或正在结算）无法传送，先回到路上。');
      return false;
    }
    if (cave) {
      showToast('洞府内无法传送，先离开洞府。');
      return false;
    }
    if (!wbAlive()) {
      showToast('世界BOSS还没刷新（或者已经消失）。');
      renderWorldBossPanel();
      return false;
    }
    if (worldBossRun) return false;

    closeWorldBossPanel();
    if (autoAdventure) stopAutoAdventure('已进入世界BOSS战场，自动冒险暂停。');

    const level = def.level;   // 世界BOSS 境界固定（森特欧斯萨满 = 武者四境·初期，对应 10 级）
    const boss = buildEnemy(level, 'legend', { worldX: 0 });
    boss.sprite = def.sprite;
    boss.kindName = def.name;
    boss.name = def.name;
    boss.tier = 'legend';
    boss.elite = true;
    boss.legend = true;
    boss.skill = null;          // 世界BOSS不玩精英技能（避免治疗干扰血量统计）
    boss.skills = [];
    boss.wbScale = def.scale;   // 体型放大
    boss.wbBossArt = true;      // 用专属立绘绘制
    boss.wbBossArtKey = wbBossArtKey(def);   // 这个 BOSS 自己的立绘
    boss.wbTheme = def.theme || 'bloodsand'; // 战场主题（天空 / 地面 / 粒子）
    /* 每个 BOSS 各自一管血：换 BOSS 就满血，同一个 BOSS 再次进入沿用上一轮血量 */
    if (wb.hpBossId !== def.id || !(wb.hp > 0)) {
      wb.hpBossId = def.id;
      wb.maxHp = def.hp;
      wb.hp = def.hp;
    }
    boss.maxHp = wb.maxHp;      // 全服共享血量：本轮刷新后所有人打的是同一管血
    boss.hp = wb.hp > 0 ? wb.hp : wb.maxHp;

    const returnX = player.worldX;
    player.worldX = WB_ARENA_X;
    player.vx = 0;
    boss.x = WB_ARENA_X + (WB_BOSS_SCREEN_X - START_X);
    boss.baseX = boss.x;
    activeEnemy = boss;
    battleGroup.length = 0;
    battleGroup.push(boss);

    worldBossRun = {
      def: def,
      boss: boss,
      level: level,
      returnX: returnX,
      claimed: false,
      down: false,
      playerDamage: 0,
      ghostDamage: 0,
      tick: 0,
      ghosts: []
    };
    spawnWorldBossGhosts();
    initWorldBossDamageBoard();     // BOSS 出现：本轮伤害榜建档（所有人从 0 开始）

    gameState = 'battle';
    input.moving = false;
    player.vx = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 10;
    activeEnemy.openingAttack = false;
    pendingVictory = 0;
    showBattleUi(true);
    setControls('battle');
    syncMoveButton();
    showWorldBossBar(true);
    updateWorldBossBar();
    setBattleLog('你被传送到「' + def.place + '」，' + def.name + ' 正在肆虐！');
    pushBattleLog('传送至世界BOSS战场：' + def.name + '（' + stageText(level) + '）', 'system');
    if (worldBossPlayerLevel() < level) {
      showToast('境界不足：需要 ' + stageText(level) + '，你当前是 ' + stageText(worldBossPlayerLevel()) +
        '，打上去全是 MISS（找一件带「破境符诏」的灵宝可以临时 +5 级）。');
      pushBattleLog('境界不足（' + stageText(worldBossPlayerLevel()) + ' < ' + stageText(level) +
        '）：对世界BOSS的攻击会全部 MISS', 'system');
    }
    saveGame();
    return true;
  }

  function leaveWorldBoss(message) {
    if (!worldBossRun) return false;
    const run = worldBossRun;
    // 玩家是否对BOSS造成过伤害（虚影打掉的不算）
    const playerDamage = run.boss
      ? Math.max(0, Math.round(run.boss.maxHp - run.ghostDamage - Math.max(0, run.boss.hp)))
      : 0;
    if (run.boss && run.boss.hp > 0) wb.hp = Math.max(0, Math.round(run.boss.hp));
    worldBossRun = null;
    activeEnemy = null;
    battleGroup.length = 0;
    pendingVictory = 0;
    damagePopups = [];
    pets.forEach(function (p) { p.attackT = 0; });
    player.worldX = run.returnX;
    player.vx = 0;
    combat.hp = Math.max(1, combat.hp);   // 被踢出战场不会直接死
    gameState = 'walk';
    showBattleUi(false);
    showWorldBossBar(false);
    setControls('walk');
    syncMoveButton();
    spawnNextEnemy();
    updateDist();
    updateBattleHud();
    saveGame();
    if (message) showToast(message);
    // 「首次挑战奖励」在离开地图时发放：屏幕中央三个宝箱（每天只给一次）
    if (playerDamage > 0) {
      if (run.down) {
        // BOSS 打赢了：奖励本该在地图的宝箱里；万一是没开箱就离开（撤离 / 倒下），这里补发
        if (!run.loot || !run.loot.granted) grantWorldBossRewardChests('离开战场时补发');
      } else {
        // 参与过但没打赢（含中途倒下被传送出场）：直接给三个箱子
        grantWorldBossRewardChests('参与了世界BOSS战斗');
      }
    }
    // 本次挑战结束（击杀 / 撤离 / 到期都走这里）：清空所有人的伤害记录
    clearWorldBossDamageBoard();
    return true;
  }

  // 击杀结算：参与奖励在"第一次造成伤害"时就已经发了，这里只做收尾
  function finishWorldBossFight() {
    const run = worldBossRun;
    if (!run) return;
    const def = run.def;
    const level = run.level || def.level;
    wb.phase = 'wait';
    wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(wbNow());
    wb.hp = 0;
    wb.killed = (wb.killed || 0) + 1;
    wb.killedAt = wb.spawnedAt || Date.now();   // 本轮已被击杀，窗口内不再刷新
    defeatedEnemies++;
    killsTotal++;
    sectKillTick(null);          // 世界BOSS 不计入"同境界"（它不是普通境界怪）
    clearDefendState();     // 世界BOSS击杀后进入开箱阶段：护盾特效也要收掉
    // 世界BOSS 时装：击杀世界BOSS +15%（比精英更高一档）
    addCostumeCharge(COSTUME_CHARGE_PER_BOSS, '世界BOSS「' + def.name + '」');
    gloveOnKill();      // 手套词缀：击败世界BOSS同样触发击杀回复
    gainXp(10 * Math.max(1, level));
    addFlaskKill(true);     // 世界BOSS算作精英级击杀 → 血瓶 +1 充能
    pushBattleLog('世界BOSS「' + def.name + '」被击杀！下次刷新 ' +
      shanghaiClockText(wb.nextAt).slice(0, 5) + '（上海时间，还有 ' + fmtClock(wb.nextAt - wbNow()) + '）', 'reward');
    showToast('世界BOSS「' + def.name + '」被击败！下次刷新还有 ' + fmtClock(wb.nextAt - wbNow()) + '。');
    // 伤害榜第一名：必得 1 个天地灵物（下面这个 0.01% 是"参与奖"，两者独立）
    awardWorldBossTopDamage();
    // 挑战到此结束（伤害榜已经结算完）：清空本轮所有人的伤害记录
    clearWorldBossDamageBoard();
    // 击杀后不立刻送出场：在战场中央放一个宝箱，给玩家 60 秒走过去亲手开启
    startWorldBossLootPhase(run);
  }

  /* ============================================================
   * 击杀后的「战场开箱」阶段
   *   · BOSS 倒下 → 屏幕正中央出现宝箱（美术 wb_chest_big.png）
   *   · 同时在地上散落几件战利品展示（只是给玩家看的，不能拾取）
   *   · 玩家可以走动，走到宝箱前点击开箱；60 秒倒计时结束会自动开启
   *   · 开箱拿的就是「世界BOSS挑战奖励」三个宝箱（每天一次）
   * ============================================================ */
  function worldBossLootActive() {
    return !!(worldBossRun && worldBossRun.loot && !worldBossRun.loot.done);
  }

  function worldBossLootChestX() {
    return WB_ARENA_X + (WB_LOOT_CHEST_SCREEN_X - START_X);
  }

  function startWorldBossLootPhase(run) {
    if (!run) return;
    run.loot = {
      x: worldBossLootChestX(),
      timeLeft: WB_LOOT_SECONDS,
      done: false,        // 箱子已经开过了
      granted: false,     // 奖励已经发出去了
      auto: false         // 是不是倒计时到点自动开的
    };
    // 地上散落的战利品展示（纯装饰，不可拾取）
    run.gifts = [
      // 位置以 BOSS 脚下为参照，整体偏右，避开场地中央那个宝箱
      { key: 'wbGift1', dx: -108, dy: 2, w: 74 },
      { key: 'wbGift2', dx: -34, dy: 10, w: 58 },
      { key: 'wbGift3', dx: 62, dy: -2, w: 72 },
      { key: 'wbGift4', dx: 150, dy: 8, w: 66 }
    ].map(function (g, i) {
      return { key: g.key, x: run.boss.x + g.dx, dy: g.dy, w: g.w, t: i * 0.7 };
    });
    battleTurn = 'player';
    pendingVictory = 0;
    enemyTurnTimer = 0;
    input.moving = false;
    player.vx = 0;
    setControls('walk');       // 允许走动去开箱
    syncMoveButton();
    if (ui.wbBarTime) ui.wbBarTime.textContent = '宝箱 ' + WB_LOOT_SECONDS + 's 后自动开启';
    setBattleLog('「' + run.def.name + '」倒下了！走到场地中央的宝箱前开启战利品（' +
      WB_LOOT_SECONDS + ' 秒后自动开启）。');
    pushBattleLog('世界BOSS已击败：中央出现战利品宝箱，' + WB_LOOT_SECONDS +
      ' 秒内走过去点击开启（倒计时结束会自动开启）', 'system');
    showToast('世界BOSS被击败！走到场地中央的宝箱前开箱（' + WB_LOOT_SECONDS + ' 秒后自动开启）');
  }

  // 每帧推进：倒计时 + 允许在战场里走动
  function updateWorldBossLoot(dt) {
    const run = worldBossRun;
    if (!run || !run.loot) return;
    const loot = run.loot;
    if (!loot.done) {
      loot.timeLeft = Math.max(0, loot.timeLeft - dt);
      if (ui.wbBarTime) {
        ui.wbBarTime.textContent = '宝箱 ' + Math.ceil(loot.timeLeft) + 's 后自动开启';
      }
      // 走动：只在战场这一段范围内活动
      const target = input.moving ? WALK_SPEED * derived.speedMult : 0;
      const ease = input.moving ? 8 : 14;
      player.vx += (target - player.vx) * Math.min(1, dt * ease);
      if (Math.abs(player.vx) < 1) player.vx = 0;
      if (player.vx > 0) {
        player.worldX = Math.min(loot.x + 70, player.worldX + player.vx * dt);
      } else if (player.vx < 0) {
        player.worldX = Math.max(WB_ARENA_X - 420, player.worldX + player.vx * dt);
      }
      player.walkT = player.vx > 0 ? player.walkT + dt * (8 + player.vx / 50) : 0;
      if (loot.timeLeft <= 0) openWorldBossLootChest(true);
    }
    run.gifts.forEach(function (g) { g.t += dt; });
  }

  // 开箱：亲手点开 or 倒计时到点自动开
  function openWorldBossLootChest(auto) {
    const run = worldBossRun;
    if (!run || !run.loot || run.loot.done) return false;
    const loot = run.loot;
    loot.done = true;
    loot.auto = !!auto;
    input.moving = false;
    player.vx = 0;
    syncMoveButton();
    if (ui.wbBarTime) ui.wbBarTime.textContent = '宝箱已开启';
    pushBattleLog(auto ? '倒计时结束：宝箱自动开启' : '你走到宝箱前，亲手开启了战利品宝箱', 'reward');
    // ★ 极低几率的额外彩蛋：只有"击杀BOSS后留在战场开箱"的人才有这个机会
    rollSpiritItemFromLootChest(run);
    if (!wbRewardAvailable()) {
      showToast('今日世界BOSS奖励已经领取过了（每日 0 点重置，一天只能领一次）。');
      pushBattleLog('今日世界BOSS奖励已领取过，这个宝箱里没有新的奖励', 'system');
      return false;
    }
    loot.granted = true;
    showWorldBossRewardChests(true);
    return true;
  }

  // 把奖励三个宝箱发出去（离开战场时的补发也走这里）
  function grantWorldBossRewardChests(reason) {
    if (!wbRewardAvailable()) {
      showToast('今日世界BOSS奖励已经领取过了（每日 0 点重置，一天只能领一次）。');
      pushBattleLog('今日世界BOSS奖励已领取过，本次不再发放宝箱', 'system');
      return false;
    }
    showWorldBossRewardChests(true);
    if (reason) pushBattleLog('世界BOSS奖励已发放（' + reason + '）：三个宝箱在屏幕中央，点开看看', 'reward');
    return true;
  }

  /* ---------- 开箱彩蛋：天地灵物 ----------
   * 只有"击杀BOSS后留在战场、亲手/到点开启中央宝箱"的人才有这个机会，几率 0.01%（万分之一）。
   * 中途被打倒传送出场的人走的是另一条路（grantWorldBossRewardChests），永远拿不到。 */
  const WB_LOOT_SPIRIT_PCT = 0.01;
  function rollSpiritItemFromLootChest(run) {
    if (!run || !(run.playerDamage > 0)) return null;              // 连BOSS都没打到：没有
    if (player.level > TRIBULATION_STAGE || hasSpiritItem()) return null;  // 已经有 / 用不上了
    const forced = spiritRollForced;
    spiritRollForced = false;
    if (!forced && Math.random() * 100 >= WB_LOOT_SPIRIT_PCT) return null;
    const item = makeSpiritItem();
    inventory.push(item);
    showToast('★★★ 宝箱底部还压着一件「天地灵物」！');
    pushBattleLog('★★★ 战场中央的宝箱额外开出「天地灵物」×1（留场开箱 · 0.01%）', 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }

  /* ============================================================
   * 世界BOSS 伤害榜（每次BOSS出现时统计所有人）
   *   · 建档：BOSS 出现（传送进战场）时，本地玩家 + 所有虚影各一条记录
   *   · 统计：虚影每次出手累加自己那份；玩家那份取"BOSS 掉血 - 虚影贡献"
   *   · 结算：BOSS 被击杀时取第一名 → 是玩家自己就必得 1 个天地灵物
   *   · 清空：本次挑战结束（击杀 / 撤离 / 到期）时整体清掉，下一轮从零开始
   * ============================================================ */
  let wbDamageBoard = null;      // { round, startedAt, entries: [{ name, isPlayer, damage, ghost }] }
  let wbDamageRound = 0;

  function initWorldBossDamageBoard() {
    const run = worldBossRun;
    if (!run) { wbDamageBoard = null; return null; }
    wbDamageRound += 1;
    const entries = (run.ghosts || []).map(function (g) {
      return { name: g.name, isPlayer: false, damage: 0, ghost: g };
    });
    entries.push({ name: chatNick(), isPlayer: true, damage: 0, ghost: null });
    wbDamageBoard = { round: wbDamageRound, startedAt: Date.now(), entries: entries };
    return wbDamageBoard;
  }

  function clearWorldBossDamageBoard() {
    if (wbDamageBoard) {
      wbDamageBoard.entries.forEach(function (e) { e.damage = 0; e.ghost = null; });
    }
    wbDamageBoard = null;      // 记录整体丢弃：下一轮重新建档
  }

  // 按当前数据刷新一遍伤害并排序；并列时玩家优先（自己并列第一就算第一）
  function worldBossDamageRanking() {
    const run = worldBossRun;
    const board = wbDamageBoard;
    if (!run || !board) return [];
    board.entries.forEach(function (e) {
      e.damage = e.isPlayer
        ? Math.round(worldBossPlayerDealt(run))
        : Math.round((e.ghost && e.ghost.damage) || 0);
    });
    return board.entries.slice().sort(function (a, b) {
      if (b.damage !== a.damage) return b.damage - a.damage;
      return (b.isPlayer ? 1 : 0) - (a.isPlayer ? 1 : 0);
    });
  }

  // BOSS 被击杀：取伤害第一名，是玩家就必掉 1 个天地灵物
  function awardWorldBossTopDamage() {
    const ranking = worldBossDamageRanking();
    if (!ranking.length) return null;
    const top = ranking[0];
    const topLines = ranking.slice(0, 3).map(function (e, i) {
      return '第' + (i + 1) + '名 ' + e.name + '（' + e.damage + '）';
    }).join('　');
    pushBattleLog('本轮伤害榜：' + topLines + (ranking.length > 3 ? '　…共 ' + ranking.length + ' 人' : ''), 'system');
    if (!top.isPlayer) {
      pushBattleLog('伤害第一是「' + top.name + '」（' + top.damage + '），本轮天地灵物归他。', 'system');
      return null;
    }
    // 已经蜕凡入仙 / 身上已经有一颗：拿了也用不上
    if (player.level > TRIBULATION_STAGE || hasSpiritItem()) {
      pushBattleLog('你以 ' + top.damage + ' 伤害拿下第一，但当前已经不需要「天地灵物」了。', 'system');
      return null;
    }
    const item = makeSpiritItem();
    inventory.push(item);
    showToast('★★★ 伤害榜第一（' + top.damage + '）！获得「天地灵物」×1');
    pushBattleLog('★ 伤害榜第一（' + top.damage + ' 伤害）：获得「天地灵物」×1', 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }

  /* ---------- 天地灵物（世界BOSS限定掉落） ----------
   * 现在的来源只有两个，且都绑定在"世界BOSS这一轮"里：
   *   ① 本轮伤害榜第一名 → 必得 1 个
   *   ② 击杀BOSS后留在战场开箱 → 极低几率（0.01%）额外开出
   * 中途被打倒传送出场的人两条都拿不到（见 grantWorldBossRewardChests 那条路）。
   * 不再通过活力兑换 / 精英 / 传奇敌人掉落获得。 */
  const SPIRIT_ITEM_DROP_PCT = 0.01;   // 与开箱彩蛋同一个数量级（保留给调试/展示用）
  let spiritRollForced = false;    // 测试钩子：强制必出

  // 这一场世界BOSS里玩家自己打掉的伤害（虚影打的不算）
  function worldBossPlayerDealt(run) {
    if (!run) return 0;
    if (run.playerDamage > 0) return run.playerDamage;
    if (!run.boss) return 0;
    // 收尾那一帧 playerDamage 可能还没刷新，这里按"BOSS 掉的血 - 虚影的贡献"补一次
    return Math.max(0, run.boss.maxHp - run.ghostDamage - Math.max(0, run.boss.hp));
  }

  // ---------- 参与奖励 ----------
  function makeWorldBossChest() {
    return {
      id: 'wbchest' + (++itemSeq),
      type: 'worldChest',
      name: '世界BOSS宝箱',
      icon: '匣',
      detail: '打开可获得金币 / 材料 / 装备（极小几率开出灵宝）'
    };
  }

  // 今天还能不能领世界BOSS奖励（每天一次，按上海日期算）
  function wbRewardAvailable() {
    return wb.lastRewardDay !== shanghaiDayKey(wbNow());
  }

  /* ---------- 世界BOSS 固定时装掉落 ----------
   * 每个 BOSS 一件专属时装（见 defs.js 的 COSTUMES），只从它的宝箱里掉，
   * 每次开箱 0.05% 概率。 */
  const WB_COSTUME_PCT = 0.05;
  function rollWorldBossCostume(bossId) {
    if (!bossId) return null;
    // 调试/测试：window.__costumeRollForce 打开时必定掉（用来验证这条分支）
    if (!window.__costumeRollForce && Math.random() * 100 >= WB_COSTUME_PCT) return null;
    const def = COSTUMES.filter(function (c) { return c.bossId === bossId; })[0];
    if (!def) return null;
    const item = makeCostumeItem(def);
    if (item) inventory.push(item);
    return item;
  }

  // ---------- 首次挑战奖励：离开世界BOSS地图时，屏幕中央出三个宝箱 ----------
  // ① 金币箱 ② 当前境界的黄色稀有装备 ③ 世界BOSS宝箱（极低几率灵宝 / 主动技能石 / 被动技能石 / 1 点活力）
  const WB_CHEST_TREASURE_PCT = 0.5;   // 「非常非常低」的灵宝几率
  const WB_CHEST_UNIQUE_PCT = 0.05;    // 世界BOSS宝箱开出独特装备的几率（%）
  let wbRewardOpening = null;          // 正在开的三个宝箱

  // 世界BOSS宝箱：独特装备（暗金）掉落判定，掉到就直接进背包
  function rollUniqueFromChest() {
    if (!window.__uniqueRollForce && Math.random() * 100 >= WB_CHEST_UNIQUE_PCT) return null;
    const it = rollUniqueDrop(playerLevel() + 2);
    if (!it) return null;
    inventory.push(it);
    return it;
  }

  /* 独特装备（暗金）：金色（精英）及以上稀有度的敌人全域掉落，几率 0.03%
   *   "全域"＝野外 / 洞府 / 临时公共副本都走这条（都是在 resolveEnemyDown 里结算）。 */
  const UNIQUE_ENEMY_DROP_PCT = 0.03;
  function enemyIsRarePlus(enemy) {
    if (!enemy) return false;
    return enemy.tier === 'elite' || enemy.tier === 'legend' || !!enemy.elite || !!enemy.legend;
  }
  function rollUniqueFromEnemy(enemy) {
    if (!enemyIsRarePlus(enemy)) return null;
    if (!window.__uniqueRollForce && Math.random() * 100 >= UNIQUE_ENEMY_DROP_PCT) return null;
    const it = rollUniqueDrop(playerLevel() + 2);
    if (!it) return null;
    inventory.push(it);
    showToast('★ 独特装备「' + it.name + '」已收入行囊！');
    pushBattleLog('★ 击败' + (enemy.legend ? '传奇' : '精英') + '敌人，掉落独特装备「' + it.name + '」', 'reward');
    if (ui.gearPopup && !ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return it;
  }

  /* 世界BOSS宝箱掉落（详细版）
   *   返回 { text, name, desc, color, tier, item, icon }
   *   text 给战报/老逻辑用；其余给新的开箱界面用（稀有度配色 + 真实物品图标） */
  function rollWorldBossChestLootDetailed(kind) {
    if (kind === 'gold') {
      gold += WB_REWARD_GOLD;
      updateGold();
      return {
        text: '金币 +' + WB_REWARD_GOLD,
        name: '金币', desc: WB_REWARD_GOLD + ' 枚金币已收入行囊',
        color: '#ffd76a', tier: 'gold', icon: '金',
        item: { type: 'gold', name: '金币' }     // 只是给图标用的占位，不会进背包
      };
    }
    if (kind === 'gear') {
      const gear = makeEquipment(playerLevel(), 'rare');
      inventory.push(gear);
      // 词缀太长不往槽位里塞，只留前两条（完整属性看装备栏）
      const parts = String(gear.detail || '').split('　').filter(Boolean);
      const brief = parts.slice(0, 2).join('　') + (parts.length > 2 ? '　…' : '');
      return {
        text: '黄色稀有「' + gear.name + '」',
        name: gear.name,
        desc: (brief || '黄色稀有装备') + '　·　等级 ' + gear.level,
        color: (RARITY_DEFS[gear.rarity] || RARITY_DEFS.rare).color,
        tier: gear.rarity || 'rare', item: gear, icon: '装'
      };
    }
    // 世界BOSS宝箱：按几率表随机
    const r = Math.random() * 100;
    /* ★ 每个世界BOSS 的固定时装：开箱 0.05% 概率（先于其它随机奖励判定） */
    const costumeItem = rollWorldBossCostume(wbRewardOpening && wbRewardOpening.bossId);
    if (costumeItem) {
      const cdef = costumeDef(costumeItem.costumeId) || {};
      return {
        text: '★ 时装「' + costumeItem.name + '」',
        name: costumeItem.name,
        desc: '时装 · 收藏品（在角色窗口的时装展示槽查看）',
        color: '#ff9df0', tier: 'legend', item: costumeItem, icon: '面'
      };
    }
    if (r < WB_CHEST_TREASURE_PCT) {
      const t = makeTreasureItem(playerLevel() + 2);
      inventory.push(t);
      return {
        text: '★ 灵宝「' + t.name + '」',
        name: t.name,
        desc: '灵宝　·　' + String(t.detail || '附带特殊属性').split('：')[0].split('　')[0],
        color: RARITY_DEFS.legend.color, tier: 'legend', item: t, icon: '灵'
      };
    }
    /* ★ 独特装备（暗金）：开箱 2% 概率 */
    const uniqueItem = rollUniqueFromChest();
    if (uniqueItem) {
      return {
        text: '★ 独特「' + uniqueItem.name + '」',
        name: uniqueItem.name,
        desc: '独特装备　·　固定词缀 + 专属机制　·　等级 ' + uniqueItem.level,
        color: RARITY_DEFS.unique.color, tier: 'unique', item: uniqueItem, icon: uniqueItem.icon
      };
    }
    if (r < 12) {
      const s = makeActiveSkillStone();
      inventory.push(s);
      return {
        text: '主动技能石 ×1（可学 / 升级主动技能）',
        name: '主动技能石',
        desc: '使用后可随机习得一个主动技能，或给已有主动技能升 1 级',
        color: s.rarityColor || '#ffd265', tier: 'rare', item: s, icon: '技'
      };
    }
    if (r < 37) {
      const g = gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]);
      inventory.push(g);
      return {
        text: '被动技能石「' + (g.detail || g.name) + '」',
        name: '被动技能石',
        desc: (g.detail || g.name) + '　·　镶嵌到主动技能后生效',
        color: g.rarityColor || '#7dffa8', tier: 'magic', item: g, icon: '石'
      };
    }
    if (r < 67) {
      vitality += 1;
      updateVitalityUI();
      return {
        text: '活力 +1（当前 ' + vitality + '）',
        name: '活力 +1', desc: '当前活力 ' + vitality + '（每 1 点可自动冒险 10km）',
        color: '#8fffd0', tier: 'magic', icon: '活'
      };
    }
    const g2 = randInt(300, 1200);
    gold += g2;
    updateGold();
    return {
      text: '金币 +' + g2,
      name: '金币 +' + g2, desc: '世界BOSS的战利品，已收入行囊',
      color: '#ffd76a', tier: 'gold', icon: '金'
    };
  }

  // 老接口：只要一行文字（战报 / 自动化测试用）
  function rollWorldBossChestLoot(kind) {
    return rollWorldBossChestLootDetailed(kind).text;
  }

  function renderWorldBossRewardChests() {
    if (!ui.wbRewardChests || !wbRewardOpening) return;
    ui.wbRewardChests.textContent = '';
    wbRewardOpening.chests.forEach(function (c) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'wbChest' + (c.opened ? ' opened' : '') + (c.burst ? ' burst' : '');
      if (c.opened && c.color) card.style.setProperty('--lc', c.color);

      // 掉落光柱：开启后按稀有度升起（越稀有越亮，一眼看出值不值）
      const beam = document.createElement('div');
      beam.className = 'wbChestBeam';

      // 方形槽位：没开时里面是 CSS 画的宝箱，开了就换成战利品图标
      const box = document.createElement('div');
      box.className = 'wbChestBox';
      if (c.opened) {
        const itemEl = document.createElement('div');
        itemEl.className = 'wbChestItem';
        fillItemIcon(itemEl, c.item, c.icon);
        box.appendChild(itemEl);
      } else {
        const icon = document.createElement('div');
        icon.className = 'wbChestIcon';
        // 箱子外观用美术资源（Boss_box.png）
        const img = document.createElement('img');
        img.className = 'wbChestImg';
        img.alt = '';
        img.src = assetUrl('assets/img/boss/wb_chest_box.png');
        icon.appendChild(img);
        box.appendChild(icon);
      }

      const tag = document.createElement('div');
      tag.className = 'wbChestTag';
      tag.textContent = c.opened ? '已开启' : '未开启';

      const name = document.createElement('div');
      name.className = 'wbChestName';
      name.textContent = c.opened ? (c.name || c.label) : '？？？';

      const divider = document.createElement('div');
      divider.className = 'wbChestDivider';

      const loot = document.createElement('div');
      loot.className = 'wbChestLoot';
      loot.textContent = c.opened ? (c.desc || c.text) : '点击开启';

      card.appendChild(beam);
      card.appendChild(tag);
      card.appendChild(box);
      card.appendChild(name);
      card.appendChild(divider);
      card.appendChild(loot);
      card.addEventListener('click', function () { openWorldBossRewardChest(c); });
      ui.wbRewardChests.appendChild(card);
    });
    const left = wbRewardOpening.chests.length - wbRewardOpening.opened;
    ui.wbRewardClose.disabled = left > 0;
    ui.wbRewardClose.textContent = left > 0 ? ('还有 ' + left + ' 个宝箱没打开') : '收下全部奖励';
    if (ui.wbRewardProgressText) {
      ui.wbRewardProgressText.textContent = '已开启 ' + wbRewardOpening.opened + ' / ' + wbRewardOpening.chests.length;
    }
    if (ui.wbRewardDots) {
      ui.wbRewardDots.textContent = '';
      wbRewardOpening.chests.forEach(function (c) {
        const dot = document.createElement('i');
        dot.className = c.opened ? 'on' : '';
        ui.wbRewardDots.appendChild(dot);
      });
    }
    if (ui.wbRewardNote) {
      ui.wbRewardNote.textContent = left > 0
        ? '离开世界BOSS战场后开启战利品 · 三个宝箱都要打开'
        : '全部开启完毕，点下方按钮收下并关闭';
    }
  }

  function openWorldBossRewardChest(c) {
    if (!c || c.opened || !wbRewardOpening) return;
    const reward = rollWorldBossChestLootDetailed(c.kind);
    c.opened = true;
    c.text = reward.text;
    c.name = reward.name;
    c.desc = reward.desc;
    c.color = reward.color;
    c.item = reward.item || null;
    if (reward.icon) c.icon = reward.icon;
    // 只有刚点开的这个箱子弹一次开箱光效，已经开过的不要跟着重放
    wbRewardOpening.chests.forEach(function (o) { o.burst = false; });
    c.burst = true;
    wbRewardOpening.opened += 1;
    pushBattleLog('世界BOSS挑战奖励 · ' + c.label + '：' + c.text, 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    renderWorldBossRewardChests();
  }

  // 屏幕中央弹出三个宝箱（顺序随机摆放）
  function showWorldBossRewardChests(dealtDamage) {
    const chests = [
      { kind: 'gold', label: '金币箱', icon: '金', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false },
      { kind: 'gear', label: '黄色稀有装备箱', icon: '装', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false },
      { kind: 'wbchest', label: '世界BOSS宝箱', icon: '匣', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false }
    ];
    for (let i = chests.length - 1; i > 0; i--) {
      const j = randInt(0, i);
      const t = chests[i]; chests[i] = chests[j]; chests[j] = t;
    }
    wbRewardOpening = {
      chests: chests, opened: 0,
      dealtDamage: dealtDamage !== false,  // 默认视为造成过伤害（圣物掉落的前提）
      // 这一轮打的是哪个 BOSS：决定开箱时能掉哪件专属时装
      bossId: (worldBossRun && worldBossRun.def && worldBossRun.def.id) ||
        wb.hpBossId ||
        ((openWorldBossDef() || {}).id) || ''
    };
    renderWorldBossRewardChests();
    ui.wbRewardPopup.classList.remove('hidden');
    showToast('世界BOSS首次挑战奖励：屏幕中央出现三个宝箱，点开看看！');
  }

  function closeWorldBossRewardChests() {
    if (!wbRewardOpening) return;
    if (wbRewardOpening.opened < wbRewardOpening.chests.length) return;   // 必须全部打开
    const dealtDamage = !!wbRewardOpening.dealtDamage;
    wbRewardOpening = null;
    wb.lastRewardDay = shanghaiDayKey(wbNow());   // 每日一次：记下今天已领
    ui.wbRewardPopup.classList.add('hidden');
    pushBattleLog('世界BOSS首次挑战奖励已领取（今日不再发放，每日 0 点重置）', 'reward');
    showToast('世界BOSS挑战奖励已收下（今日奖励已领取）。');
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    // 森特欧斯独特圣物：前提是"对BOSS造成过伤害"，掉落率 0.001%
    if (dealtDamage) rollSantosRelic();
  }

  // 森特欧斯圣物掉落判定：0.001%（十万分之一）出其中一件，已有的一件不会重复给
  let santosRollForced = false;
  function rollSantosRelic() {
    if (!santosRollForced && Math.random() * 100 >= SANTOS_RELIC_CHANCE) return null;
    santosRollForced = false;
    const pool = ['skull', 'totem'].filter(function (k) { return !hasSantosRelic(k); });
    if (!pool.length) return null;
    const key = pool[randInt(0, pool.length - 1)];
    const item = makeSantosRelic(key);
    inventory.push(item);
    recalcPlayerStats();
    showToast('★★★ 森特欧斯的圣物：「' + item.name + '」！（' + item.detail + '）');
    pushBattleLog('★ 获得森特欧斯圣物「' + item.name + '」· ' + item.detail, 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }
  // 开世界BOSS宝箱：金币 + 材料 + 装备，极小几率开出灵宝
  function openWorldBossChest(item) {
    if (!item) return;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    // 和老版本开出来的「世界BOSS宝箱」共用同一张掉落表
    const text = rollWorldBossChestLoot('wbchest');
    const lines = [text];
    pushBattleLog('开启世界BOSS宝箱：' + text, 'reward');
    showToast(text.indexOf('灵宝') >= 0 ? ('★ 世界BOSS宝箱开出了' + text + '！') : ('世界BOSS宝箱：' + text));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return lines;
  }

  // ---------- 世界BOSS战斗条 ----------
  function showWorldBossBar(show) {
    if (ui.wbBar) ui.wbBar.classList.toggle('hidden', !show);
  }
  function updateWorldBossBar() {
    if (!ui.wbBar || !worldBossRun) return;
    const run = worldBossRun;
    const boss = run.boss;
    const pct = clamp(boss.hp / Math.max(1, boss.maxHp), 0, 1) * 100;
    ui.wbHpFill.style.width = pct.toFixed(1) + '%';
    ui.wbBarName.textContent = run.def.name;
    ui.wbBarLv.textContent = stageText(boss.level) + ' · ' + run.def.place;
    if (ui.wbBarReq) {
      const ok2 = worldBossLevelOk();
      ui.wbBarReq.textContent = ok2
        ? '境界达标（' + stageText(worldBossPlayerLevel()) + '）'
        : '需要 ' + stageText(boss.level) + '（你是 ' + stageText(worldBossPlayerLevel()) + '，攻击 MISS）';
      ui.wbBarReq.className = ok2 ? 'wbReqOk' : 'wbReqBad';
    }
    ui.wbHpText.textContent = Math.max(0, Math.ceil(boss.hp)) + ' / ' + boss.maxHp;
    ui.wbBarTime.textContent = '剩余 ' + fmtClock(run.endsAt - wbNow());
  }

  // ---------- 虚影（模拟其他玩家一起打BOSS）----------
  function spawnWorldBossGhosts() {
    if (!worldBossRun) return;
    const list = [];
    const names = WB_GHOST_NAMES.slice();
    for (let i = 0; i < WB_GHOST_COUNT; i++) {
      const usePlayer = Math.random() < 0.45;
      const sprite = randEnemySprite().key;
      list.push({
        isPlayer: usePlayer,
        sprite: sprite,
        name: names.splice(randInt(0, names.length - 1), 1)[0] || '无名侠客',
        // 挑战者都围在 BOSS 脚下（山丘近侧），不再散得太开
        offset: rand(-360, -60),
        offY: rand(-26, -6),
        phase: rand(0, 10),
        scale: rand(0.82, 1.02),
        attackT: 0,
        damage: 0,          // 本轮该虚影对BOSS造成的伤害（伤害榜用）
        hitTimer: rand(0.4, 2.2),
        interval: rand(1.7, 2.6),
        dmgMin: 24,
        dmgMax: 60
      });
    }
    worldBossRun.ghosts = list;
  }

  function updateWorldBossGhosts(dt) {
    const run = worldBossRun;
    if (!run) return;
    const boss = run.boss;
    run.tick += dt;
    run.ghosts.forEach(function (g) {
      g.phase += dt;
      if (g.attackT > 0) g.attackT = Math.max(0, g.attackT - dt);
      g.hitTimer -= dt;
      if (g.hitTimer > 0) return;
      g.hitTimer = g.interval * rand(0.8, 1.25);
      g.attackT = 0.34;
      if (boss.hp <= 0) return;
      const dmg = randInt(g.dmgMin, g.dmgMax);
      boss.hp = Math.max(0, boss.hp - dmg);
      boss.hurtT = Math.max(boss.hurtT, 0.16);
      run.ghostDamage += dmg;
      g.damage = (g.damage || 0) + dmg;   // 伤害榜：虚影各自记账
      const bx = boss.x - cameraX();
      addDamagePopup(bx + rand(-110, 120), GROUND_Y - 230 + rand(-60, 60), dmg, '#ffe08a');
      if (boss.hp <= 0) worldBossBossDown();
    });
  }

  function worldBossBossDown() {
    const run = worldBossRun;
    if (!run || run.down) return;
    run.down = true;
    setBattleLog('「' + run.def.name + '」倒下了！');
    pushBattleLog('世界BOSS「' + run.def.name + '」被击杀（虚影合计 ' + Math.round(run.ghostDamage) +
      ' 伤害，你贡献 ' + Math.round(run.playerDamage) + '）', 'reward');
    pendingVictory = 0.6;
  }

  // 每帧推进世界BOSS战场：血量回写、倒计时、参与奖励判定
  function updateWorldBossRun(dt) {
    const run = worldBossRun;
    if (!run) return;
    const boss = run.boss;
    wb.hp = Math.max(0, Math.round(boss.hp));   // 全服共享血量（本轮内保留）
    updateWorldBossGhosts(dt);
    updateWorldBossBar();

    // 记录玩家造成的伤害（奖励在"离开世界BOSS地图"时用三个宝箱发，见 leaveWorldBoss）
    if (worldBossLevelOk() && boss.hp < boss.maxHp - run.ghostDamage - 0.5) {
      run.playerDamage = Math.max(1, Math.round(boss.maxHp - run.ghostDamage - boss.hp));
      run.claimed = true;   // 表示"已经打出过有效伤害"
    }

    // 3 分钟到点（或到了下一次刷新时刻）：BOSS 自己消失，玩家被传送回公路
    if (wbNow() >= run.endsAt) {
      wb.phase = 'wait';
      wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(wbNow());
      wb.hp = 0;
      leaveWorldBoss('3 分钟到了，世界BOSS「' + run.def.name + '」消失在风沙里，你被传送回公路。');
      return;
    }
    // 玩家被打倒：不重置进度，直接被踢出战场
    if (combat.hp <= 0) {
      combat.hp = Math.max(1, Math.round(combat.maxHp * 0.3));
      updateBattleHud();
      pushBattleLog('你在「' + run.def.name + '」的战场上倒下了，被传送回公路（参战奖励照发）', 'system');
      leaveWorldBoss('你倒下了，被传送出世界BOSS战场（生命恢复到 30%）。');
    }
  }

  // ---------- 世界BOSS场景绘制 ----------
  function drawWorldBossScene() {
    const run = worldBossRun;
    const cam = cameraX();
    const quake = quakeOffset();
    const T = wbTheme();
    ctx.save();
    if (quake.x || quake.y) ctx.translate(quake.x, quake.y);

    // 天空（按主题渐变）
    const sky = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    T.sky.forEach(function (s) { sky.addColorStop(s[0], s[1]); });
    ctx.fillStyle = sky;
    ctx.fillRect(-40, -40, W + 80, GROUND_Y + 40);

    // 天体：血月 / 熔阳 / 幽月 / 锻炉红光 …
    if (T.orb) {
      const o = T.orb;
      const mg = ctx.createRadialGradient(o.x, o.y, 10, o.x, o.y, o.r * 3.6);
      mg.addColorStop(0, o.glow);
      mg.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = mg;
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r * 3.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = o.core;
      ctx.beginPath();
      ctx.arc(o.x, o.y, o.r, 0, Math.PI * 2);
      ctx.fill();
    }

    // 云层（在日月之上、远山之下慢慢飘动）
    drawWbClouds();

    // 远景剪影（沙丘 / 山脊 / 塔楼…统一用一条起伏线）
    if (T.far) {
      ctx.fillStyle = T.far;
      ctx.beginPath();
      ctx.moveTo(-40, GROUND_Y);
      for (let x = -40; x <= W + 40; x += 40) {
        ctx.lineTo(x, 470 + Math.sin((x + cam * 0.02) / 240) * 46 + Math.sin((x + cam * 0.05) / 90) * 14);
      }
      ctx.lineTo(W + 40, GROUND_Y);
      ctx.closePath();
      ctx.fill();
    }

    // 地面
    const ground = ctx.createLinearGradient(0, GROUND_Y - 30, 0, H);
    T.ground.forEach(function (s) { ground.addColorStop(s[0], s[1]); });
    ctx.fillStyle = ground;
    ctx.fillRect(-40, GROUND_Y, W + 80, H - GROUND_Y + 40);

    // 贴地尘 / 雾带
    drawWbDustBands();

    // 主题粒子（余烬 / 雪 / 孢子 / 尘 / 水沫）——画在角色之前，让角色压在粒子之上
    drawWbParticles();

    // 层次：BOSS（站在山丘后面）→ 前景山丘 → 法阵 / 挑战者 → 玩家
    drawEnemy();
    drawWbForegroundHill(cam);
    drawWbAltarRing(cam);
    drawWorldBossGhosts();
    drawWorldBossLoot(cam);      // 击杀后：地上的战利品展示 + 中央宝箱
    drawBattleGroup();
    drawBloodWavesGround();
    drawShockWavesGround();
    drawDefendFxBehind();   // 防御护盾：先铺一层在角色后面
    drawPlayer();
    drawDirtyOnPlayer();
    drawDefendFxFront();    // 角色前面再罩一层薄的
    drawBloodWavesAura();
    drawBloodBolts();       // 气血反弹：血气冲击波
    drawPet();
      drawAlly();
      drawAirSlashFx();
      drawRockFx();
      drawSlashFx();
      drawYangClawFx();
      drawCageFx();
      drawTotemFx();
      drawDamagePopups();
    drawWbFlash();          // 血雷闪：整体提亮一瞬
    ctx.restore();
  }

  // 前景沙丘的脊线高度（x 越靠右越高，正好把 BOSS 的腿脚挡住）
  let wbHillEnabled = true;   // 调试用：可关掉山丘看 BOSS 全身
  function wbHillLift(x, cam) {
    const t = clamp((x - 240) / 800, 0, 1);
    return GROUND_Y - 18 - Math.sin((x + cam * 0.12) / 300) * 12 - t * 130;
  }

  // 前景沙丘：盖住 BOSS 的下半身，做出"站在山丘后面"的层次
  function drawWbForegroundHill(cam) {
    if (!wbHillEnabled) return;
    const T = wbTheme();
    if (!T.hill) return;
    const lift = function (x) { return wbHillLift(x, cam); };
    const surge = ctx.createLinearGradient(0, GROUND_Y - 130, 0, H + 40);
    T.hill.forEach(function (s) { surge.addColorStop(s[0], s[1]); });
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(-40, H + 40);
    ctx.lineTo(-40, lift(-40));
    for (let x = -40; x <= W + 40; x += 20) ctx.lineTo(x, lift(x));
    ctx.lineTo(W + 40, H + 40);
    ctx.closePath();
    ctx.fillStyle = surge;
    ctx.fill();
    // 脊线高光（背光边缘）
    ctx.beginPath();
    ctx.moveTo(-40, lift(-40));
    for (let x = -40; x <= W + 40; x += 20) ctx.lineTo(x, lift(x));
    ctx.strokeStyle = T.hillEdge || 'rgba(255, 196, 140, .22)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.restore();
  }

  // 祭坛法阵：画在山丘近侧的地面上（BOSS 脚下的光圈）
  function drawWbAltarRing(cam) {
    const run = worldBossRun;
    if (!run) return;
    const R = wbTheme().ring;
    const bossSx = run.boss.x - cam;
    const pulse = 0.5 + Math.sin(run.tick * 1.6) * 0.12;
    ctx.save();
    ctx.translate(bossSx, GROUND_Y + 8);
    ctx.scale(1, 0.3);
    const ring = ctx.createRadialGradient(0, 0, 20, 0, 0, 260);
    ring.addColorStop(0, R[0].replace('A', (0.46 * pulse + 0.2).toFixed(3)));
    ring.addColorStop(0.6, R[1]);
    ring.addColorStop(1, R[1].replace(/,[^,]*\)$/, ', 0)'));
    ctx.fillStyle = ring;
    ctx.beginPath();
    ctx.arc(0, 0, 260, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = R[2];
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, 186 + Math.sin(run.tick * 1.1) * 6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  function drawWorldBossGhosts() {
    const run = worldBossRun;
    if (!run) return;
    const bossSx = run.boss.x - cameraX();
    run.ghosts.forEach(function (g) {
      const lunge = g.attackT > 0 ? Math.sin((1 - g.attackT / 0.34) * Math.PI) * 54 : 0;
      const sx = bossSx + g.offset + lunge;
      const bob = Math.sin(g.phase * 2.8) * 3;
      ctx.save();
      ctx.globalAlpha = 0.46;
      try { ctx.filter = 'saturate(0.25) brightness(1.55)'; } catch (e) { /* 浏览器不支持就算了 */ }
      if (g.isPlayer && images.player_idle_1) {
        drawPlayerFrame(images.player_idle_1, sx, GROUND_Y + g.offY + bob,
          playerFx.scale * g.scale, PLAYER_SPRITE_FLIP, 0);
      } else {
        const img = enemyFrameImage({ sprite: g.sprite, animPhase: g.phase },
          enemyWalkFps(enemySpriteDef({ sprite: g.sprite })));
        drawEnemySprite(img, sx, GROUND_Y + g.offY + bob, g.scale * 0.88, true, 0,
          enemySpriteDef({ sprite: g.sprite }));
      }
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(20, 6, 4, .8)';
      ctx.strokeText(g.name, sx, GROUND_Y - 158 * g.scale + g.offY);
      ctx.fillStyle = '#cfe6ff';
      ctx.fillText(g.name, sx, GROUND_Y - 158 * g.scale + g.offY);
      ctx.restore();
    });
  }

  /* ---------- 击杀后：地上的战利品展示 + 场地中央的宝箱 ---------- */
  function worldBossLootChestRect() {
    const run = worldBossRun;
    if (!run || !run.loot) return null;
    const img = images.wbChestBig;
    const w = WB_LOOT_CHEST_DRAW_W;
    const ratio = (img && img.naturalWidth) ? (img.naturalHeight / img.naturalWidth) : (736 / 895);
    const h = w * ratio;
    const cx = run.loot.x - cameraX();
    const ground = GROUND_Y + 6;
    return { cx: cx, x: cx - w / 2, y: ground - h, w: w, h: h, groundY: ground };
  }

  function worldBossGiftRect(g) {
    const img = images[g.key];
    const w = g.w;
    const h = w * ((img && img.naturalWidth) ? (img.naturalHeight / img.naturalWidth) : 1);
    const cx = g.x - cameraX();
    return { cx: cx, x: cx - w / 2, y: GROUND_Y + 6 - h + g.dy, w: w, h: h };
  }

  function drawWorldBossLoot(cam) {
    const run = worldBossRun;
    if (!run) return;

    // ① 散落在地上的战利品展示：只是给玩家看的，不能拾取
    (run.gifts || []).forEach(function (g) {
      const img = images[g.key];
      if (!img || !img.complete) return;
      const r = worldBossGiftRect(g);
      const bob = Math.sin(enemyAnimClock * 1.6 + g.t) * 3;
      // 一束淡淡的光，让"这堆是战利品"一眼可见
      const beam = ctx.createLinearGradient(0, GROUND_Y - 110, 0, GROUND_Y + 6);
      beam.addColorStop(0, 'rgba(255, 214, 142, 0)');
      beam.addColorStop(1, 'rgba(255, 196, 96, .26)');
      ctx.fillStyle = beam;
      ctx.fillRect(r.cx - r.w * 0.42, GROUND_Y - 110, r.w * 0.84, 116);
      // 地面投影
      ctx.save();
      ctx.globalAlpha = 0.34;
      ctx.fillStyle = 'rgba(40, 12, 4, .75)';
      ctx.beginPath();
      ctx.ellipse(r.cx, GROUND_Y + 6, r.w * 0.46, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      ctx.drawImage(img, r.x, r.y + bob, r.w, r.h);
    });

    // ② 场地中央的宝箱
    const rect = worldBossLootChestRect();
    if (!rect) return;
    const img = images.wbChestBig;
    const opened = !!(run.loot && run.loot.done);
    const pulse = 0.5 + Math.sin(enemyAnimClock * 2.2) * 0.5;
    // 宝箱底下的金色光晕
    const glow = ctx.createRadialGradient(rect.cx, rect.groundY - 10, 6, rect.cx, rect.groundY - 10, rect.w * 0.95);
    glow.addColorStop(0, 'rgba(255, 214, 130, ' + (opened ? 0.18 : (0.34 + pulse * 0.16)).toFixed(3) + ')');
    glow.addColorStop(1, 'rgba(255, 190, 90, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(rect.cx - rect.w * 1.1, rect.groundY - rect.h * 1.25, rect.w * 2.2, rect.h * 1.5);
    if (img && img.complete) ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h);

    if (!opened) {
      // 头顶倒计时
      const secs = Math.max(0, Math.ceil(run.loot.timeLeft));
      const text = '宝箱 ' + secs + 's 后自动开启';
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
      ctx.strokeText(text, rect.cx, rect.y - 16);
      ctx.fillStyle = secs <= 10 ? '#ffd06a' : '#ffe9c0';
      ctx.fillText(text, rect.cx, rect.y - 16);
      ctx.restore();
      // 提示可以走过去点
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(28, 8, 1, .85)';
      ctx.globalAlpha = 0.62 + pulse * 0.38;
      ctx.strokeText('走近后点击开启', rect.cx, rect.groundY + 22);
      ctx.fillStyle = '#ffdfa8';
      ctx.fillText('走近后点击开启', rect.cx, rect.groundY + 22);
      ctx.restore();
    } else {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 15px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .9)';
      ctx.strokeText('宝箱已开启', rect.cx, rect.y - 16);
      ctx.fillStyle = '#b6ffcf';
      ctx.fillText('宝箱已开启', rect.cx, rect.y - 16);
      ctx.restore();
    }
  }

  const REFORGE_UNIT = 100;    // 每级基础费用：100 × 装备等级
  const REFORGE_MAX = 20;      // 每件装备的洗练次数上限

  // 洗练费用 = 100 × 装备等级 ×（1 + 锁定条数）
  // 例：10 级装备洗一次 1000；锁 1 条 → 1000 + 1000 = 2000；锁 2 条 → 3000
  function reforgeCost(item) {
    if (!item) return 0;
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    const locked = (item.mods || []).filter(function (m) { return m.locked; }).length;
    return base * (1 + locked);
  }

  function openReforge(item) {
    if (!item || item.type !== 'equipment') return;
    /* 独特装备：词缀是固定的，洗练没有意义，直接拦下来（镶嵌仍然可用） */
    if (uniqueDefOf(item)) {
      showToast('「' + item.name + '」是独特装备：词缀固定，不能洗练。');
      return;
    }
    reforgeItem = item;
    renderReforge();
    ui.reforgePopup.classList.remove('hidden');
  }

  function closeReforge() {
    ui.reforgePopup.classList.add('hidden');
    reforgeItem = null;
  }

  // 金币或锁定变化后，重算按钮上的费用显示
  function refreshReforgeIfOpen() {
    if (reforgeItem && !ui.reforgePopup.classList.contains('hidden')) renderReforge();
  }

  function renderReforge() {
    const item = reforgeItem;
    if (!item) return;
    // 左侧：装备模型（用装备图标 + 品级配色）
    const color = item.rarityColor || '#ffd9a0';
    fillItemIcon(ui.reforgeIcon, item, item.icon || '装');
    ui.reforgeIcon.style.borderColor = color;
    ui.reforgeIcon.style.color = color;
    ui.reforgeModelName.textContent = item.name;
    ui.reforgeModelName.style.color = color;
    const slotLabel = SLOT_DEFS[item.slot] ? SLOT_DEFS[item.slot].label : '装备';
    ui.reforgeModelMeta.textContent = slotLabel +
      ' · 等级 ' + item.level +
      (item.weaponType ? ' · ' + item.weaponType : '') +
      ' · 已洗练 ' + (item.reforgeCount || 0) + ' / ' + REFORGE_MAX + ' 次';

    // 右侧：当前词缀（逐条来自 item.mods，可单独上锁）
    ensureItemMods(item);
    ui.reforgeList.textContent = '';
    // 基底词缀（盾牌自带 +15% 格挡）：不可洗练、也不参与锁定
    const baseRows = String(item.baseDetail || '').split('　').filter(Boolean);
    baseRows.forEach(function (t) {
      const row = document.createElement('div');
      row.className = 'reforgeAffix base';
      const tag = document.createElement('i');
      tag.textContent = '基底';
      const span = document.createElement('span');
      span.textContent = t + '（固定，不参与洗练）';
      row.appendChild(tag);
      row.appendChild(span);
      ui.reforgeList.appendChild(row);
    });
    const mods = item.mods || [];
    if (!mods.length) {
      const empty = document.createElement('div');
      empty.className = 'reforgeAffix empty';
      empty.textContent = '这件装备没有词缀';
      ui.reforgeList.appendChild(empty);
    } else {
      mods.forEach(function (mod, i) {
        const row = document.createElement('div');
        row.className = 'reforgeAffix' + (mod.locked ? ' locked' : '');
        const tag = document.createElement('i');
        tag.textContent = '词缀 ' + (i + 1);
        const span = document.createElement('span');
        span.textContent = modToText(mod);
        const lock = document.createElement('button');
        lock.type = 'button';
        lock.className = 'lockBtn' + (mod.locked ? ' on' : '');
        lock.textContent = mod.locked ? '🔒' : '🔓';
        lock.title = mod.locked ? '已锁定：洗练时保留这条词缀（点击解锁）' : '点击锁定这条词缀';
        lock.addEventListener('click', function (e) {
          e.stopPropagation();
          mod.locked = !mod.locked;
          renderReforge();
        });
        row.appendChild(tag);
        row.appendChild(span);
        row.appendChild(lock);
        ui.reforgeList.appendChild(row);
      });
      // 灵宝特殊属性：单独一行，洗练不会改动
      if (item.special) {
        const sp = document.createElement('div');
        sp.className = 'reforgeAffix special';
        const tag = document.createElement('i');
        tag.textContent = '灵宝';
        const span = document.createElement('span');
        span.textContent = '【' + item.special.name + '】' + item.special.desc;
        sp.appendChild(tag);
        sp.appendChild(span);
        ui.reforgeList.appendChild(sp);
      }
    }
    // 洗练按钮上的费用提示（随锁定条数变化）
    const cost = reforgeCost(item);
    const reachedCap = (item.reforgeCount || 0) >= REFORGE_MAX;
    if (ui.btnReforge) {
      // 按钮上只留两个字，费用写在下面的说明里
      ui.btnReforge.textContent = reachedCap ? '已达上限' : '洗练';
      ui.btnReforge.disabled = reachedCap || gold < cost;
      ui.btnReforge.title = reachedCap
        ? ('每件装备最多洗练 ' + REFORGE_MAX + ' 次')
        : (gold < cost ? ('金币不足：本次需要 ' + cost + ' 金币') : ('本次洗练花费 ' + cost + ' 金币'));
    }
    // 说明：数量不变 + 锁定的不重掷 + 数值按等级档位
    const bandIdx = Math.min(9, Math.floor((Math.max(1, item.level) - 1) / AFFIX_BAND_SIZE));
    const lockedCount = mods.filter(function (m) { return m.locked; }).length;
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    ui.reforgeHint.innerHTML =
      '洗练只重掷未锁定的词缀，<b>词缀数量不变</b>（当前 ' + mods.length + ' 条' +
      (lockedCount ? '，其中 ' + lockedCount + ' 条已锁定' : '') + '）。' +
      '数值只会落在「' + (bandIdx * AFFIX_BAND_SIZE + 1) + '-' + (bandIdx * AFFIX_BAND_SIZE + AFFIX_BAND_SIZE) +
      ' 级」及以下的档位区间——等级 ' + item.level + ' 的装备洗不出高等级装备的数值。' +
      '<br>本次费用：<b>' + cost + ' 金币</b>（基础 100 × 等级 ' + item.level + ' = ' + base +
      (lockedCount ? '，锁定 ' + lockedCount + ' 条 × ' + base : '') + '）' +
      '<br>洗练次数：<b>' + (item.reforgeCount || 0) + ' / ' + REFORGE_MAX + '</b>' +
      (reachedCap ? '——已达上限，这件装备不能再洗了。' : '');
  }

  function doReforge() {
    const item = reforgeItem;
    if (!item) return;
    // 每件装备最多洗练 REFORGE_MAX 次
    if ((item.reforgeCount || 0) >= REFORGE_MAX) {
      showToast('这件装备已经洗练 ' + REFORGE_MAX + ' 次，达到上限了。');
      renderReforge();
      return;
    }
    // 费用 = 100 × 装备等级 ×（1 + 锁定条数）
    const cost = reforgeCost(item);
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    const lockedNow = (item.mods || []).filter(function (m) { return m.locked; }).length;
    if (gold < cost) {
      showToast('金币不足：本次洗练需要 ' + cost + ' 金币（100 × 等级 ' + item.level + ' = ' + base +
        (lockedNow ? ' + 锁定 ' + lockedNow + ' 条 × ' + base : '') + '），当前只有 ' + gold + '。');
      return;
    }
    gold -= cost;
    updateGold();
    pushBattleLog('洗练「' + item.name + '」花费 ' + cost + ' 金币' +
      (lockedNow ? '（含锁定 ' + lockedNow + ' 条）' : ''), 'system');
    // 核心：数量不变，锁定的词缀整条保留，其余按装备等级对应的档位重掷
    const lockedBefore = (item.mods || []).filter(function (m) { return m.locked; }).length;
    rollItemAffixes(item);
    item.reforgeCount = (item.reforgeCount || 0) + 1;
    recalcPlayerStats();              // 已穿戴，属性立刻生效
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderReforge();
    showToast('洗练完成（重掷 ' + ((item.mods || []).length - lockedBefore) + ' 条' +
      (lockedBefore ? '，保留锁定 ' + lockedBefore + ' 条' : '') + '）：' + (item.detail || ''));
    dailyQuestTick('reforge');   // 每日任务：洗练一次装备
  }

  /* ============================================================
   * 装备镶嵌（入侵材料）
   *   装备窗口里每件装备的「洗练」下面多一个「镶嵌」按钮：
   *   点开弹窗 → 从背包里选一种入侵材料（裂隙 / 灾变 / 迷雾 / 驱灵 /
   *   深渊 / 精华 / 流亡者）→ 点「镶嵌」：
   *     ① 装备名字打上该材料的前缀，例如 迷雾*长弓
   *     ② 额外追加 1 条随机词缀（从这件装备类型对应的词缀库里、
   *        按它的"装备等级"档位抽取——等级 1 的装备抽不出高等级档位）
   *   一件装备同时只挂 1 个镶嵌：想换材料就再镶一次（旧前缀与旧词缀会被换掉）。
   *   镶嵌带来的那条词缀会被标记 inlay=true，洗练时**保留**不被洗掉。
   * ============================================================ */
  const INLAY_MATERIAL_BY_ID = {};
  INVASION_MATERIALS.forEach(function (m) { INLAY_MATERIAL_BY_ID[m.id] = m; });
  const ORE_INLAY_DEF = {
    id: 'oreMaterial', name: '材料矿石', prefix: '灵矿', icon: '矿', color: '#ffd76a',
    detail: '矿石加工产出的高品质材料 · 可镶嵌：为装备加前缀「灵矿」并追加 1 条随机词缀'
  };
  /* 属性矿石也走镶嵌（不是"直接使用"）：镶到装备上，给**那件装备**附加对应三维。
   * 每种属性 × 每个等级各算一种镶嵌材料，id 形如 oreAttr:str:3。 */
  const ORE_ATTR_INLAY_PREFIX = { str: '力矿', agi: '敏矿', int: '慧矿' };
  const ORE_ATTR_INLAY_MODKEY = { str: 'strength', agi: 'agility', int: 'intelligence' };
  function oreAttrInlayId(item) {
    return 'oreAttr:' + (item.oreAttr || 'str') + ':' + clamp(item.oreAttrLevel || 1, 1, 5);
  }
  function oreAttrInlayDef(id) {
    const m = /^oreAttr:(str|agi|int):([1-5])$/.exec(String(id || ''));
    if (!m) return null;
    const attr = m[1], lv = Number(m[2]);
    const ad = ORE_ATTR_DEFS[attr] || ORE_ATTR_DEFS.str;
    const value = [5, 10, 15, 20, 30][lv - 1];
    return {
      id: id, name: ad.name + '矿石·' + lv + '级', prefix: ORE_ATTR_INLAY_PREFIX[attr] || '矿',
      icon: '矿', color: ad.color,
      detail: '属性矿石 · 镶嵌后给这件装备附加 +' + value + ' ' + ad.name,
      fixedMod: { key: ORE_ATTR_INLAY_MODKEY[attr] || 'strength', value: value },
      fixedText: '+' + value + ' ' + ad.name,
      oreAttr: attr, oreAttrLevel: lv
    };
  }
  const INLAY_PREFIX_SEP = '*';
  let inlayTarget = null;      // 当前窗口针对的装备
  let inlayPickId = null;      // 当前选中的材料 id
  let pendingInlayMaterialId = null;   // 从"背包里的材料"那一侧发起镶嵌时预选的材料

  function invasionMaterialDef(id) {
    if (id === ORE_INLAY_DEF.id) return ORE_INLAY_DEF;
    const attrDef = oreAttrInlayDef(id);
    if (attrDef) return attrDef;
    return INLAY_MATERIAL_BY_ID[id] || null;
  }
  function inlayModOf(item) {
    return (item && item.mods || []).filter(function (m) { return m && m.inlay; })[0] || null;
  }
  // 背包里的入侵材料按种类归并（镶嵌窗口的材料列表）
  function inlayMaterialStock() {
    const stock = {};
    inventory.forEach(function (it) {
      if (!it || it.type !== 'material') return;
      const kind = oreMaterialKind(it);
      const materialId = (it.sectMaterial === 'ore' && it.oreFinished && kind === 'affix')
        ? ORE_INLAY_DEF.id
        : (kind === 'attribute' ? oreAttrInlayId(it) : it.materialId);
      if (!materialId) return;
      const def = invasionMaterialDef(materialId);
      if (!def) return;
      if (!stock[materialId]) stock[materialId] = { def: def, count: 0, items: [] };
      stock[materialId].count += 1;
      stock[materialId].items.push(it);
    });
    return stock;
  }
  // 装备的"底名"（去掉品级与可能已有的 前缀*）
  function equipBaseName(item) {
    if (item.baseName) return item.baseName;
    const stripped = String(item.name || '').replace(/^\S+\s+/, '');   // 去掉"稀有 "
    return stripped.replace(/^\S{1,4}\*/, '');                          // 去掉 迷雾*
  }
  // 按"品级 + 前缀*底名"刷新名字
  function refreshItemName(item) {
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    item.baseName = equipBaseName(item);
    const pref = (item.inlay && item.inlay.prefix) ? item.inlay.prefix + INLAY_PREFIX_SEP : '';
    item.name = rar.name + ' ' + pref + item.baseName;
    return item.name;
  }
  // 抽一条"符合这件装备类型与等级"的词缀，作为镶嵌奖励
  function rollInlayMod(item) {
    ensureItemMods(item);
    const pool = affixPoolFor(item.slot, item.weaponType || null);
    const rarity = RARITY_DEFS[item.rarity] ? item.rarity : 'common';
    const mod = rollOneMod(item.slot, item.weaponType || null, Math.max(1, item.level || 1), rarity, pool);
    mod.inlay = true;
    return mod;
  }
  /* 真正的镶嵌：扣材料 → 换前缀 → 换那条镶嵌词缀 → 重算属性 */
  function inlayEquipment(item, materialId) {
    const def = invasionMaterialDef(materialId);
    if (!item || item.type !== 'equipment' || !def) return { ok: false, reason: 'bad_args' };
    const stock = inlayMaterialStock()[materialId];
    if (!stock || !stock.count) return { ok: false, reason: 'no_material' };
    const consumed = stock.items[0];
    inventory = inventory.filter(function (it) { return it.id !== consumed.id; });
    ensureItemMods(item);
    const replaced = !!inlayModOf(item);
    item.mods = (item.mods || []).filter(function (m) { return !m.inlay; });
    /* 属性矿石给的是**固定**加成（+5/+10/… 力量），其它材料仍是随机词缀 */
    const mod = def.fixedMod
      ? { key: def.fixedMod.key, value: def.fixedMod.value, value2: 0, bandIdx: -1, locked: false, inlay: true }
      : rollInlayMod(item);
    item.mods.push(mod);
    item.modCount = item.mods.length;
    item.inlay = { materialId: def.id, prefix: def.prefix, modKey: mod.key };
    refreshItemName(item);
    applyItemMods(item);                       // 重算属性（含新加的这条）
    if (equipment[item.slot] === item) recalcPlayerStats();   // 正穿着：属性立刻生效
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    saveGame();
    const line = '镶嵌「' + def.name + '」→ ' + item.name + '（' + modToText(mod) + '）';
    showToast('镶嵌完成：' + item.name + '　新增词缀：' + modToText(mod));
    pushBattleLog('【镶嵌】' + line + (replaced ? '（已替换原有镶嵌）' : ''), 'reward');
    return { ok: true, name: item.name, prefix: def.prefix, mod: modToText(mod), replaced: replaced };
  }

  function openInlay(item) {
    if (!item || item.type !== 'equipment' || !ui.inlayPopup) return false;
    inlayTarget = item;
    const stock = inlayMaterialStock();
    // 默认选中：优先"当前已镶嵌的那种"，其次从背包材料那侧带过来的预选，最后包里第一种
    inlayPickId = (item.inlay && stock[item.inlay.materialId]) ? item.inlay.materialId
      : (pendingInlayMaterialId && stock[pendingInlayMaterialId] ? pendingInlayMaterialId
        : (Object.keys(stock)[0] || null));
    pendingInlayMaterialId = null;   // 预选只用一次
    renderInlayPanel();
    ui.inlayPopup.classList.remove('hidden');
    return true;
  }
  /* 从「材料 / 矿石」那一侧发起镶嵌：打开装备窗口并预选这颗材料，
   * 玩家在装备窗口点装备的「镶嵌」就会看到它已经选好。 */
  function startInlayFromMaterial(materialId, label) {
    pendingInlayMaterialId = materialId || null;
    if (typeof hideBagMenu === 'function') hideBagMenu();
    openGearPanel();
    showToast('已选中「' + (label || '镶嵌材料') + '」：在装备窗口点一件装备的「镶嵌」按钮即可镶上去。');
    return true;
  }
  function closeInlay() {
    if (ui.inlayPopup) ui.inlayPopup.classList.add('hidden');
    inlayTarget = null;
    inlayPickId = null;
  }
  function renderInlayPanel() {
    const item = inlayTarget;
    if (!item || !ui.inlayList) return;
    const color = item.rarityColor || '#ffd9a0';
    fillItemIcon(ui.inlayIcon, item, item.icon || '装');
    ui.inlayIcon.style.borderColor = color;
    ui.inlayIcon.style.color = color;
    ui.inlayModelName.textContent = item.name;
    ui.inlayModelName.style.color = color;
    const slotLabel = SLOT_DEFS[item.slot] ? SLOT_DEFS[item.slot].label : '装备';
    ui.inlayModelMeta.textContent = slotLabel + ' · 装备等级 ' + item.level +
      (item.weaponType ? ' · ' + item.weaponType : '') +
      (item.inlay ? ' · 已镶嵌「' + item.inlay.prefix + '」' : ' · 未镶嵌');

    // 材料列表（背包里有几种就列几种）
    const stock = inlayMaterialStock();
    ui.inlayList.textContent = '';
    const ids = Object.keys(stock);
    if (!ids.length) {
      const empty = document.createElement('div');
      empty.className = 'inlayEmpty';
      empty.textContent = '背包里还没有镶嵌材料（入侵材料来自临时副本，以后每周副本也会产出）。';
      ui.inlayList.appendChild(empty);
    } else {
      ids.sort(function (a, b) {
        const da = stock[a].def, db = stock[b].def;
        return INVASION_MATERIALS.indexOf(da) - INVASION_MATERIALS.indexOf(db);
      });
      ids.forEach(function (id) {
        const st = stock[id];
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'inlayRow' + (inlayPickId === id ? ' picked' : '');
        row.dataset.material = id;
        const ico = document.createElement('i');
        ico.className = 'inlayIco';
        ico.textContent = st.def.icon;
        ico.style.color = st.def.color;
        ico.style.borderColor = st.def.color;
        const body = document.createElement('div');
        body.className = 'inlayBody';
        const nm = document.createElement('b');
        nm.textContent = st.def.name + ' ×' + st.count;
        nm.style.color = st.def.color;
        const dsc = document.createElement('em');
        dsc.textContent = st.def.detail;
        body.appendChild(nm);
        body.appendChild(dsc);
        const pre = document.createElement('span');
        pre.className = 'inlayPre';
        pre.textContent = '前缀 ' + st.def.prefix + INLAY_PREFIX_SEP;
        row.appendChild(ico);
        row.appendChild(body);
        row.appendChild(pre);
        row.addEventListener('click', function () {
          inlayPickId = id;
          renderInlayPanel();
        });
        ui.inlayList.appendChild(row);
      });
    }

    // 预览 + 按钮
    const def = invasionMaterialDef(inlayPickId);
    if (ui.inlayPreview) {
      ui.inlayPreview.textContent = '';
      if (def) {
        const want = document.createElement('div');
        want.className = 'inlayWant';
        want.textContent = '镶嵌后：' + ((RARITY_DEFS[item.rarity] || RARITY_DEFS.common).name) + ' ' +
          def.prefix + INLAY_PREFIX_SEP + equipBaseName(item);
        const gain = document.createElement('div');
        gain.className = 'inlayGain';
        gain.textContent = def.fixedMod
          ? ('附加属性：' + def.fixedText + '（固定加成，不占随机词缀条数）')
          : ('额外获得：1 条随机词缀（从「' + slotLabel + '」词缀库里按装备等级 ' +
            item.level + ' 的档位抽取）');
        ui.inlayPreview.appendChild(want);
        ui.inlayPreview.appendChild(gain);
      }
    }
    if (ui.btnInlay) {
      ui.btnInlay.disabled = !def;
      ui.btnInlay.textContent = item.inlay ? '替换镶嵌' : '镶嵌';
    }
  }
  function doInlay() {
    const item = inlayTarget;
    if (!item || !inlayPickId) return false;
    const r = inlayEquipment(item, inlayPickId);
    if (!r.ok) {
      showToast(r.reason === 'no_material' ? '这种材料已经用完了。' : '镶嵌失败，请稍后再试。');
      return false;
    }
    // 用当下的库存刷新窗口（材料可能刚好用完）
    const stock = inlayMaterialStock();
    if (!invasionMaterialDef(inlayPickId) || !stock[inlayPickId]) {
      inlayPickId = Object.keys(stock)[0] || null;
    }
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderInlayPanel();
    return true;
  }

  // ---------- 宠物窗口 ----------
  function openPetPanel() {
    renderPetPanel();
    ui.petPopup.classList.remove('hidden');
  }

  function closePetPanel() {
    ui.petPopup.classList.add('hidden');
  }

  function petTierInfo(tier) {
    return tier === 'rare' ? { name: '稀有', color: '#ffd265' }
      : tier === 'magic' ? { name: '魔法', color: '#67b6ff' }
      : { name: '普通', color: '#d8d3c2' };
  }

  // 宠物单次出手伤害的估算（取普攻区间中值，不带随机）
  function petDamageEstimate() {
    return petDamageEstimateOf(leadPet());
  }

  function petDamageEstimateOf(p) {
    if (!p) return 0;
    const range = petDamageRangeOf(p);
    const base = (range[0] + range[1]) / 2;
    const petSkill = petSkillDef(p);
    const gemMult = 1 + skillGemSum(p.skillId || 'summonPet', ['summonDamage']) / 100;
    const mods = minionGemMods(p);
    return Math.max(1, Math.round(
      base * (p.atkMult || 1) * gemMult * skillLevelMult(petSkill) *
      supportDamageMult(p.skillId || 'summonPet', activeEnemy) * mods.dmgMult * mods.atkMult
    ));
  }

  function renderPetPanel() {
    if (!ui.petPopup) return;
    ui.petList.textContent = '';

    if (!petAlive()) {
      ui.petNote.textContent = '当前没有宠物';
      const empty = document.createElement('div');
      empty.className = 'petCard empty';
      empty.textContent = '还没有宠物。装备法杖会出现灵宠；学会「召唤蝎子 / 召唤鬣狗 / 召唤毒蛇 / 召唤猎鹰」等召唤技能，也能召唤宠物跟随。';
      ui.petList.appendChild(empty);
      return;
    }

    const list = alivePets();
    ui.petNote.textContent = '当前出战 ' + list.length + ' / ' + petCap() +
      (hasServantGem() ? '（「仆从」在场：只能拥有一只召唤物）' : '') +
      ' 只宠物 · 按等级从前往后排在玩家身后（等级高的靠前）';
    list.forEach(function (p, idx) {
      renderPetCard(p, idx + 1);
    });
  }

  function renderPetCard(pet, slot) {
    const info = petTierInfo(pet.tier);
    const card = document.createElement('div');
    card.className = 'petCard';
    card.style.borderColor = info.color;

    const head = document.createElement('div');
    head.className = 'petHead';
    const name = document.createElement('b');
    name.textContent = '#' + slot + ' ' + pet.name;
    const tier = document.createElement('span');
    tier.className = 'petTier';
    tier.textContent = info.name + ' · 等级 ' + (pet.level || 1);
    tier.style.color = info.color;
    tier.style.borderColor = info.color;
    head.appendChild(name);
    head.appendChild(tier);
    card.appendChild(head);

    const source = document.createElement('div');
    source.className = 'petSource';
    source.textContent = '来源：' + (pet.source || '未知途径') +
      '　·　站位：玩家身后第 ' + slot + ' 位';
    card.appendChild(source);

    // 生命条
    const hpRow = document.createElement('div');
    hpRow.className = 'petHpRow';
    const hpText = document.createElement('span');
    const petMods = minionGemMods(pet);
    const hpPct = Math.round((petMods.hpMult - 1) * 100);
    hpText.textContent = '生命 ' + Math.ceil(pet.hp) + ' / ' + pet.maxHp +
      '（1 级 75 点，每级 +20% × 生命系数 ' + (pet.hpMult || 1).toFixed(2) +
      (hpPct ? ' × 召唤辅助 ' + (hpPct > 0 ? '+' : '') + hpPct + '%' : '') + '）';
    hpRow.appendChild(hpText);
    card.appendChild(hpRow);

    const barWrap = document.createElement('div');
    barWrap.className = 'petHpBar';
    const bar = document.createElement('i');
    bar.style.width = Math.max(0, Math.min(100, (pet.hp / pet.maxHp) * 100)) + '%';
    barWrap.appendChild(bar);
    card.appendChild(barWrap);

    const stats = document.createElement('div');
    stats.className = 'petStats';
    const lines = [
      ['召唤物等级', 'Lv.' + summonLevelOf(pet) + '（跟玩家等级走，专精再 +1）'],
      ['基础伤害', petDamageRangeOf(pet)[0] + ' ~ ' + petDamageRangeOf(pet)[1] +
        '（1 级 4~9，每级 +15%）× 攻击系数 ' + (pet.atkMult || 1).toFixed(2)],
      ['预计出手伤害', '约 ' + petDamageEstimateOf(pet) + ' 点 / 次'],
      ['召唤伤害宝石', '+' + skillGemSum(pet.skillId || 'summonPet', ['summonDamage']) + '%' +
        (pet.skillId ? '（镶嵌在「' + (petSkillDef(pet) ? petSkillDef(pet).name : '召唤技能') + '」上）' : '')],
      ['召唤辅助宝石', petMods.notes.length
        ? (petMods.notes.join(' · ') + '（伤害 ' + (petMods.dmgMult >= 1 ? '+' : '') +
          Math.round((petMods.dmgMult - 1) * 100) + '%，受伤 ' +
          (petMods.takenMult >= 1 ? '+' : '') + Math.round((petMods.takenMult - 1) * 100) + '%）')
        : '无（把肉盾 / 狂噬 / 专精 / 仆从 / 忠诚镶嵌到它的召唤技能上即可生效）'],
      ['状态', '跟随中 · 战斗内外都会跟随玩家（可被宠物技能书强化）']
    ];
    lines.forEach(function (pair) {
      const row = document.createElement('div');
      row.className = 'petStat';
      const k = document.createElement('span');
      k.textContent = pair[0];
      const v = document.createElement('b');
      v.textContent = pair[1];
      row.appendChild(k);
      row.appendChild(v);
      stats.appendChild(row);
    });
    card.appendChild(stats);
    ui.petList.appendChild(card);
  }

  // ---------- 角色属性窗口（属性点分配 + 时装展示槽）----------
  // ---------- 技能总览窗口 ----------
  const SKILL_TYPE_LABEL = {
    melee: '近战', ranged: '远程', spell: '法术', summon: '召唤', heal: '恢复', toggle: '持续'
  };

  // 技能所属的大境界：武者境（武者十境）/ 炼气境 / 筑基境 …
  function bigRealmOfStage(stage) {
    const info = stageInfo(stage);
    if (info.realmIndex < 10) return { name: '武者境', index: 0 };
    // 炼气 / 筑基 / 结丹 … 统一叫「X境」
    return { name: info.realm + '境', index: info.realmIndex - 9 };
  }

  function playerBigRealm() {
    return bigRealmOfStage(player.level);
  }

  /* ---------- 不同境界的实力压制（战斗减伤） ----------
   *   横跨大境界对战时，**低境界一方打出的伤害**会被削弱：
   *     · 同一大境界（武者一境 ↔ 武者十境）：不变
   *     · 差一个大境界（武者境 ↔ 练气期）：×0.8（减伤 20%）
   *     · 差两个及以上（武者境 ↔ 筑基期）：×0.2（减伤 80%）
   *   规则对称：高境界打低境界照常发挥，低境界打高境界被压。 */
  const REALM_GAP_ONE_MULT = 0.8;   // 差一个大境界：减伤 20%
  const REALM_GAP_TWO_MULT = 0.2;   // 差两个大境界：减伤 80%
  function realmDamageMult(attackerStage, targetStage) {
    const a = bigRealmOfStage(attackerStage || 1).index;
    const t = bigRealmOfStage(targetStage || 1).index;
    const gap = t - a;
    if (gap >= 2) return REALM_GAP_TWO_MULT;
    if (gap === 1) return REALM_GAP_ONE_MULT;
    return 1;
  }
  // 给面板 / 调试看的一句话说明
  function realmGapText(attackerStage, targetStage) {
    const m = realmDamageMult(attackerStage, targetStage);
    if (m === 1) return '同等大境界（无压制）';
    return '越阶压制：伤害 ×' + m + '（减伤 ' + Math.round((1 - m) * 100) + '%）';
  }
  /* 战斗里的"境界压制"提示：每场战斗每一侧只提示一次（避免每一下都刷屏）
   *   side: 'player' = 你的伤害被压；'enemy' = 敌人的伤害被压 */
  let realmNotice = { player: 0, enemy: 0 };
  function noticeRealmGap(side, mult) {
    if (!(mult < 1)) return false;
    if (realmNotice[side] === mult) return false;   // 同一场里同样的压制只提示一次
    realmNotice[side] = mult;
    const pct = Math.round((1 - mult) * 100);
    if (side === 'player') {
      addTextPopup(player.worldX - cameraX() - 92, GROUND_Y - 214, '境界压制 -' + pct + '%', '#ffb0b0');
      pushBattleLog('【境界压制】对方境界高于你，你的伤害被压制：×' + mult + '（-' + pct + '%）', 'system');
    } else {
      pushBattleLog('【境界压制】对方境界低于你，它的伤害同样被压制：×' + mult + '（-' + pct + '%）', 'system');
    }
    return true;
  }

  // 当前境界能看到的全部技能（低境界技能 + 当前境界技能；更高境界完全不可见）
  // 初始技能「劈砍」不在主动技能池里，单独定义一份（改名/改描述后靠它同步老存档）
  const STARTER_SKILL_DEF = {
    id: 'slash', name: '劈砍', type: 'melee', cost: 10, dmg: [34, 48], minStage: 1,
    desc: '挥动武器劈向敌人，造成武器攻击力 75% 的伤害；30% 几率造成双倍伤害。'
  };

  // 技能定义查表（含初始「劈砍」）：用于技能改名 / 调数值后同步老存档
  function skillDefById(id) {
    if (id === STARTER_SKILL_DEF.id) return STARTER_SKILL_DEF;
    return ACTIVE_SKILL_POOL.filter(function (d) { return d.id === id; })[0] || null;
  }

  function visibleSkillDefs() {
    const list = [STARTER_SKILL_DEF];
    ACTIVE_SKILL_POOL.forEach(function (s) {
      if (!list.some(function (k) { return k.id === s.id; })) list.push(s);
    });
    const mine = playerBigRealm().index;
    return list.filter(function (s) {
      return bigRealmOfStage(s.minStage || 1).index <= mine;
    }).map(function (s) {
      const realm = bigRealmOfStage(s.minStage || 1);
      return {
        def: s,
        realmName: realm.name,
        learned: knownSkills.some(function (k) { return k.id === s.id; })
      };
    });
  }

  function openSkillPanel() {
    renderSkillPanel();
    ui.skillPopup.classList.remove('hidden');
  }

  function closeSkillPanel() {
    usingStone = null; // 直接关掉 = 取消使用技能石（技能石留在背包里）
    ui.skillPopup.classList.add('hidden');
  }

  function renderSkillPanel() {
    if (!ui.skillPopup) return;
    ui.skillList.textContent = '';
    const realm = playerBigRealm();
    const all = visibleSkillDefs();
    const learnedCount = all.filter(function (s) { return s.learned; }).length;
    if (usingStone) {
      ui.skillNote.textContent = '★ 正在使用主动技能石（还剩 ' + countSkillStones() +
        ' 颗）：点击灰色技能可【学习】，点击已学技能可【升级 +1】';
      ui.skillNote.style.color = '#ffd970';
    } else {
      ui.skillNote.textContent = '当前境界：' + stageName(player.level) +
        '（' + realm.name + '）· 该境界可学技能 ' + all.length + ' 个，已学 ' + learnedCount + ' 个' +
        '　·　施放法术消耗灵力（' + Math.ceil(combat.mp) + ' / ' + combat.maxMp + '）';
      ui.skillNote.style.color = '';
    }

    all.forEach(function (entry) {
      const def = entry.def;
      const learned = entry.learned;
      const known = knownSkills.filter(function (k) { return k.id === def.id; })[0];

      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'skillTile' + (learned ? ' learned' : ' locked') +
        (usingStone ? ' stoneMode' : '');
      tile.setAttribute('data-skill', def.id);
      tile.addEventListener('click', function () { useStoneOnSkill(def.id, learned); });

      const name = document.createElement('div');
      name.className = 'skillTileName';
      name.textContent = def.name;
      tile.appendChild(name);

      const type = document.createElement('div');
      type.className = 'skillTileType';
      type.textContent = (SKILL_TYPE_LABEL[def.type] || '技能') + ' · ' + entry.realmName;
      tile.appendChild(type);

      const cost = document.createElement('div');
      cost.className = 'skillTileCost';
      // 持续类 / 召唤类技能会占用灵力上限（保留）
      const reserveNote = def.type === 'toggle' ? '占用 35%'
        : def.type === 'summon' ? '占用 50%' : '';
      cost.textContent = (def.cost > 0 ? ('灵力 ' + def.cost) : '被动') +
        (reserveNote ? ' · ' + reserveNote : '');
      tile.appendChild(cost);

      if (learned) {
        if (def.type === 'toggle') {
          const state = document.createElement('div');
          state.className = 'skillTileState';
          const tOn = def.id === 'shieldWall' ? shieldWall : bloodRebound;
          state.textContent = tOn ? '● 已激活' : '○ 未激活';
          state.style.color = tOn ? '#7dffa8' : '#c9b391';
          tile.appendChild(state);
        }
        const info = document.createElement('div');
        info.className = 'skillTileInfo';
        info.textContent = usingStone
          ? ('升级到 Lv.' + ((known ? known.level || 1 : 1) + 1))
          : ((known ? 'Lv.' + (known.level || 1) : '已学习') +
            (known && known.sockets && known.sockets.length
              ? ' · 宝石 ' + known.sockets.length + '/' + socketSlotsOf(known) : ''));
        tile.appendChild(info);
      } else {
        const info = document.createElement('div');
        info.className = 'skillTileInfo';
        info.textContent = usingStone ? '点击学习' : '未学习';
        tile.appendChild(info);
      }

      tile.title = (learned ? '已学习　' : '未学习　') + def.name + '（' +
        (SKILL_TYPE_LABEL[def.type] || '技能') + '，' + entry.realmName + '）\n' + def.desc +
        (usingStone
          ? (learned ? '\n点击消耗 1 颗主动技能石，升 1 级' : '\n点击消耗 1 颗主动技能石，学会这个技能')
          : (learned ? '' : '\n获取方式：主动技能石（精英挑战 / 传奇敌人掉落 / 活力兑换）'));
      ui.skillList.appendChild(tile);
    });
  }

  function updateCharButton() {
    if (!ui.btnChar) return;
    const has = player.attrPoints > 0;
    setHudBtnLabel(ui.btnChar, has ? '角色 ·' + player.attrPoints : '角色');
    ui.btnChar.classList.toggle('hasPoints', has);
    ui.btnChar.title = has
      ? ('还有 ' + player.attrPoints + ' 点属性点可以分配')
      : '查看角色属性';
  }

  function openCharPanel() {
    renderCharPanel();
    ui.charPopup.classList.remove('hidden');
  }

  function closeCharPanel() {
    ui.charPopup.classList.add('hidden');
    toggleArmorHelp(false);   // 关窗口顺手收起护甲说明
  }

  // 分配 1 点属性点：力量 / 敏捷 / 智力
  function allocateAttr(id) {
    if (!player.attrs[id] && player.attrs[id] !== 0) return;
    if (player.attrPoints <= 0) {
      showToast('没有可用属性点，提升境界可以获得。');
      return;
    }
    const label = (ATTR_DEFS.filter(function (a) { return a.id === id; })[0] || {}).label || id;
    const beforeHp = combat.maxHp;
    const beforeMp = combat.maxMp;
    player.attrs[id] += 1;
    player.attrPoints -= 1;
    recalcPlayerStats();
    // 加点带来的生命 / 灵力上限提升直接补满差额，避免出现空血条
    combat.hp = Math.min(combat.maxHp, combat.hp + Math.max(0, combat.maxHp - beforeHp));
    combat.mp = Math.min(derived.mpUsable, combat.mp + Math.max(0, derived.mpUsable - beforeMp));
    updateBattleHud();
    updateCharButton();
    renderCharPanel();
    pushBattleLog(
      '分配属性点：' + label + ' +1（剩余 ' + player.attrPoints + ' 点）',
      'reward'
    );
    showToast(label + ' +1，剩余属性点 ' + player.attrPoints + '。');
    saveGame();
  }

  /* 一行属性。helpId 传了就额外挂一个「?」小按钮（目前只有护甲减伤用） */
  function charStatRow(parent, label, value, helpId) {
    const row = document.createElement('div');
    row.className = 'charStat';
    const name = document.createElement('span');
    name.textContent = label;
    if (helpId) {
      const q = document.createElement('button');
      q.type = 'button';
      q.className = 'statHelpBtn';
      q.textContent = '?';
      q.title = '护甲减伤怎么算';
      q.setAttribute('data-help', helpId);
      q.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        toggleArmorHelp();
      });
      name.appendChild(q);
    }
    const val = document.createElement('b');
    val.textContent = String(value);
    row.appendChild(name);
    row.appendChild(val);
    parent.appendChild(row);
  }

  /* ---------- 护甲减伤说明（角色属性里「防御力（护甲）」旁的「?」） ---------- */
  function renderArmorHelpTable() {
    if (!ui.armorHelpTable || !ui.armorHelpFormula) return;
    const capPct = Math.round(playerReduceCap() * 100);
    ui.armorHelpFormula.textContent =
      '护甲减伤 = 护甲 ÷（护甲 + 120），再叠上元素抗性 / 混沌抗性，这一层合计最多 ' + capPct +
      '%，装备可以抬上限。护甲这一层 50% 以内是正常收益（约 120 点护甲到 50%）；' +
      '超过 50% 之后收益递减 —— 每点护甲只有 1/4 的效果，所以继续堆护甲还是会涨，' +
      '只是涨得很慢（渐近上限约 62.5%）。';
    /* 把"拐点 120"和"之后慢慢涨"都列出来 */
    const list = [0, 100, 120, 240, 600, 1000, 5000];
    const rows = list.map(function (a) {
      const r = armorResReduction(a, 0, 0);
      const pct = Math.round(r * 1000) / 10;
      // 「实际掉血」按减伤后的剩余比例取整，和左边的百分比对得上（100 × (1 − 减伤)）
      const take = Math.max(0, Math.round((1 - r) * 100));
      return '<tr><td>护甲 ' + (a === 120 ? '120（拐点）' : a) + '</td><td>' + pct +
        '%</td><td>' + take + '</td></tr>';
    }).join('');
    ui.armorHelpTable.innerHTML =
      '<tr><th>护甲</th><th>护甲层减伤</th><th>面对 100 伤害实际掉血</th></tr>' + rows;
  }
  function toggleArmorHelp(show) {
    if (!ui.armorHelp) return false;
    const on = show === undefined ? ui.armorHelp.classList.contains('hidden') : !!show;
    ui.armorHelp.classList.toggle('hidden', !on);
    if (on) renderArmorHelpTable();
    return on;
  }

  function renderCharPanel() {
    if (!ui.charPopup) return;
    // 顶部那行"境界 … 每提升一个境界获得 N 点属性点"按需求移除了（点数说明在属性点那一栏）
    ui.charPoints.textContent = String(player.attrPoints);
    // 说明文字直接读常量，避免和实际发点数（每境界 +1）对不上
    if (ui.charPointNote) {
      ui.charPointNote.textContent = '每提升一个境界 +' + ATTR_POINTS_PER_LEVEL + ' 点';
    }

    // 属性点分配区
    ui.charAttrs.textContent = '';
    ATTR_DEFS.forEach(function (def) {
      const box = document.createElement('div');
      box.className = 'charAttr';

      const name = document.createElement('div');
      name.className = 'charAttrName';
      name.textContent = def.label;

      const value = document.createElement('div');
      value.className = 'charAttrValue';
      value.textContent = String(player.attrs[def.id] || 0);

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'charAttrAdd';
      add.textContent = '+1';
      add.disabled = player.attrPoints <= 0;
      add.title = player.attrPoints > 0 ? ('分配 1 点' + def.label) : '没有可用属性点';
      add.addEventListener('click', function () { allocateAttr(def.id); });

      const note = document.createElement('div');
      note.className = 'charAttrNote';
      note.textContent = def.note;

      box.appendChild(name);
      box.appendChild(add);
      box.appendChild(value);
      box.appendChild(note);
      ui.charAttrs.appendChild(box);
    });

    // 时装展示槽：只展示当前穿戴的外观，不可在这里穿脱
    // （保留第一个方格里的角色立绘，只重建 5 个装备方格）
    Array.prototype.slice.call(ui.charSlots.querySelectorAll('.charSlot'))
      .forEach(function (node) { node.remove(); });
    Object.keys(SLOT_DEFS).forEach(function (slot) {
      const def = SLOT_DEFS[slot];
      const gear = equipment[slot];
      // 头部时装"覆盖"在头盔槽上展示（不再单独多开一个格子）
      const wornCost = costumeOnSlot(slot);
      const row = document.createElement('div');
      row.className = 'charSlot' + (gear || wornCost ? '' : ' charSlotEmpty') +
        (wornCost ? ' charSlotCostume' : '');
      row.setAttribute('data-slot', slot); // 供 CSS 摆成“J”字形

      const icon = document.createElement('div');
      icon.className = 'charSlotIcon';
      if (wornCost) {
        const cdef = costumeDef(wornCost.id) || {};
        fillItemIcon(icon, { type: 'costume', costumeId: wornCost.id, art: cdef.art },
          wornCost.icon || '面');
      } else if (gear) {
        fillItemIcon(icon, gear, gear.icon || def.icon || '装');
      } else {
        icon.textContent = def.icon || '空';
      }

      const text = document.createElement('div');
      text.className = 'charSlotText';
      const label = document.createElement('b');
      label.textContent = wornCost ? (def.label + ' · 时装') : def.label;
      const name = document.createElement('span');
      if (wornCost) {
        name.textContent = wornCost.name;
        name.style.color = '#ffcf7a';
      } else if (gear) {
        name.textContent = gear.name;
        name.style.color = gear.rarityColor || '#fff';
      } else {
        name.textContent = '未穿戴';
      }
      text.appendChild(label);
      text.appendChild(name);

      if (wornCost) {
        const badge = document.createElement('span');
        badge.className = 'charSlotBadge';
        badge.textContent = '时装';
        row.appendChild(badge);
        row.title = '时装「' + wornCost.name + '」（收藏品，只在时装展示槽显示）' +
          (gear ? '　当前头盔：' + gear.name : '　未装备头盔');
      } else if (gear) {
        row.title = gear.name;
      }

      row.appendChild(icon);
      row.appendChild(text);
      ui.charSlots.appendChild(row);
    });

    // 详细属性
    ui.charStats.textContent = '';
    const addSection = function (title) {
      const sec = document.createElement('div');
      sec.className = 'charStatSection';
      sec.textContent = title;
      ui.charStats.appendChild(sec);
    };
    addSection('基础属性');
    /* 面板文案：按需求去掉所有括号说明，只留名字和数字 */
    // 生命 / 灵力：值为「当前 / 上限」
    charStatRow(ui.charStats, '生命',
      Math.max(0, Math.round(combat.hp)) + ' / ' + combat.maxHp);
    charStatRow(ui.charStats, '灵力',
      Math.max(0, Math.round(combat.mp)) + ' / ' + combat.maxMp);
    /* 灵力就是法力槽本身：角色栏只报当前 / 上限，规则不在这里解释
     *（境界压制只在战斗里提示，见 noticeRealmGap） */
    // 「灵力可用」不再单独列出（战斗信息面板里有灵力条），改成累计击杀人数
    charStatRow(ui.charStats, '击杀人数', killsTotal);
    // 道统（武者大圆满择道的阵营）：没择过就写"未定"
    (function () {
      const def = factionDef(faction);
      // 按需求：只写阵营名，未择道就写"未定"，不带其它说明
      charStatRow(ui.charStats, '道统', def ? def.name : '未定');
    })();
    // 门派：只标"所属门派 / 无门派"，不带任何说明
    (function () {
      charStatRow(ui.charStats, '所属门派', hasSect() ? sectName() : '无门派');
    })();
    charStatRow(ui.charStats, '攻击力', '+' + derived.flatAtk);
    charStatRow(
      ui.charStats,
      '防御力',
      derived.armor + (derived.armorPct ? ' / +' + derived.armorPct + '%' : ''),
      'armor'
    );
    // 闪避：防具基底（敏捷性质）累计的点数，后面是「闪避提高 X%」
    charStatRow(ui.charStats, '闪避',
      derived.evasion + (derived.evasionPct ? ' / +' + derived.evasionPct + '%' : ''));
    // 移动速度：只作用于野外赶路的实际速度（100% 时 1km ≈ 10 秒）
    charStatRow(ui.charStats, '移动速度', Math.round(derived.speedMult * 100) + '%');
    // 攻击速度：战斗先手判定用；同时仍保留原来的"概率追加一次攻击"
    charStatRow(ui.charStats, '攻击速度', '+' + derived.atkSpeed + '%');
    addSection('三维 / 进阶属性');
    charStatRow(ui.charStats, '力量', derived.strengthTotal);
    charStatRow(ui.charStats, '敏捷', derived.agilityTotal);
    charStatRow(ui.charStats, '智慧', derived.intTotal);
    // 按需求：三维属性区把"力量合计换算生命"换成"免疫流血"（来源见门派武学 / 丹药，框架先留口子）
    charStatRow(ui.charStats, '免疫流血', derived.bleedImmune ? '是' : '否');
    charStatRow(ui.charStats, '免疫中毒', derived.poisonImmune ? '是' : '否');
    charStatRow(ui.charStats, '免疫控制', derived.controlImmune ? '是' : '否');
    charStatRow(ui.charStats, '法术伤害提高', '+' + derived.spellDmgPct + '%');
    /* 灵根资质：显示当前灵根 + 重新随机按钮（练气以上才有灵根这条路） */
    if (player.level > TRIBULATION_STAGE) {
      const rootDef = spiritRootDef();
      charStatRow(ui.charStats, '灵根资质',
        rootDef ? (rootDef.name + ' · ' + rootDef.grade) : '尚未测出灵根');
      if (rootDef) charStatRow(ui.charStats, '灵根效果', rootDef.desc);
      /* 「重测灵根」按钮固定在「修改昵称」旁边，由这里只更新文字 / 显隐 */
      if (ui.rootRerollBtn) {
        ui.rootRerollBtn.classList.remove('hidden');
        ui.rootRerollBtn.textContent = rootDef ? '重测灵根' : '测一测灵根';
        ui.rootRerollBtn.title = rootDef
          ? '重测灵根：打开确认窗看要消耗的材料'
          : '还没有灵根，测一次看看';
      }
    } else if (ui.rootRerollBtn) {
      ui.rootRerollBtn.classList.add('hidden');
    }
    charStatRow(ui.charStats, '暴击率', derived.critChance + '%');
    charStatRow(ui.charStats, '暴击伤害', '+' + derived.critDamage + '%');
    charStatRow(ui.charStats, '全元素抗性', derived.res + '%');
    charStatRow(ui.charStats, '混沌抗性', derived.chaosRes + '%');
    charStatRow(ui.charStats, '格挡几率', derived.blockPct + '%');
    /* 「额外伤害减免」：装备上那两层额外减伤（盾牌「受到的伤害降低」×
     * 盾牌 / 衣服「物理伤害减免」）合起来是多少 —— 它们在护甲减伤之后单独再算一次 */
    (function () {
      const pm = playerPassiveDamageMult();
      const extra = Math.round((1 - (1 - pm.gearReduce) * (1 - pm.physReduce)) * 1000) / 10;
      charStatRow(ui.charStats, '额外伤害减免', extra + '%');
    })();
    charStatRow(ui.charStats, '闪避几率', Math.round(derived.dodgePct) + '%');
    if (derived.dodgeCapPct) {
      charStatRow(ui.charStats, '闪避几率上限', '+' + derived.dodgeCapPct + '% / ' + derived.dodgeCap + '%');
    }
    if (derived.reduceCapPct) {
      charStatRow(ui.charStats, '总减伤上限', '+' + derived.reduceCapPct + '% / ' +
        Math.round(playerReduceCap() * 100) + '%');
    }
    // 新词缀：物理伤害减免 / 两项上限（没有就不显示，免得空占行）
    if (derived.physReducePct) charStatRow(ui.charStats, '物理伤害减免', derived.physReducePct + '%');
    if (derived.resCapPct) {
      charStatRow(ui.charStats, '最大元素抗性上限', '+' + derived.resCapPct + '% / ' + derived.resCap + '%');
    }
    if (derived.blockCapPct) {
      charStatRow(ui.charStats, '格挡几率上限', '+' + derived.blockCapPct + '% / ' + derived.blockCap + '%');
    }
    // 世界BOSS 时装：击杀充能进度（只有穿着带虚影的时装才显示这一行）
    if (hasBossCostume()) {
      charStatRow(ui.charStats, '时装充能',
        Math.round(costumeCharge) + '% / 100%');
    }
    charStatRow(ui.charStats, '荆棘伤害', derived.thorns);
    /* 独特装备（暗金）机制在面板上的可见口径 */
    if (derived.critImmune) charStatRow(ui.charStats, '暴击额外伤害', '免疫');
    if (derived.stunImmune) charStatRow(ui.charStats, '免疫晕眩', '是');
    if (derived.blockHealPct) charStatRow(ui.charStats, '格挡回复生命上限', derived.blockHealPct + '%');
    if (derived.thornsCritPct) charStatRow(ui.charStats, '荆棘暴击几率', '+' + derived.thornsCritPct + '%');
    if (derived.atkDmgPct) charStatRow(ui.charStats, '力量换算攻击伤害', '+' + derived.atkDmgPct + '%');
    if (derived.projDmgPct) charStatRow(ui.charStats, '力量换算投射物伤害', '+' + derived.projDmgPct + '%');
    charStatRow(ui.charStats, '生命每回合再生', derived.lifeRegen + '/回合');
    charStatRow(ui.charStats, '灵力每回合恢复', derived.mpRegen + '/回合');
    // 手套专属词缀（没有就不显示，免得空占一行）
    if (derived.lifePerKill) charStatRow(ui.charStats, '每击败一名敌人获得生命', derived.lifePerKill);
    if (derived.mpPerKill) charStatRow(ui.charStats, '每击败一名敌人获得灵力', derived.mpPerKill);
    if (derived.lifePerHit) charStatRow(ui.charStats, '攻击每击中一名敌人获得生命', derived.lifePerHit);
    if (derived.poisonChance) charStatRow(ui.charStats, '攻击造成中毒几率', derived.poisonChance + '%');
    if (derived.bleedChance) charStatRow(ui.charStats, '攻击造成流血几率', derived.bleedChance + '%');
    if (derived.igniteChance) charStatRow(ui.charStats, '攻击造成点燃几率', derived.igniteChance + '%');
    if (derived.dmgReducePct) charStatRow(ui.charStats, '受到的伤害降低', derived.dmgReducePct + '%');
    if (derived.recoupPct) charStatRow(ui.charStats, '将所受伤害吸纳为生命', derived.recoupPct + '%');
    if (derived.lifeReservePct) charStatRow(ui.charStats, '盾墙生命占用', derived.lifeReservePct + '%');
    charStatRow(ui.charStats, '额外箭矢几率', derived.extraArrowPct + '%');
    charStatRow(ui.charStats, '暴击伤害降低', derived.critReduce + '%');
    charStatRow(
      ui.charStats,
      '世界BOSS挑战境界',
      stageText(worldBossPlayerLevel())
    );
    charStatRow(ui.charStats, '金币', gold);
  }

  function renderGearPanel() {
    ui.equipSlots.textContent = '';
    hideItemTip();
    hideBagMenu();
    Object.keys(SLOT_DEFS).forEach(function (slot) {
      const def = SLOT_DEFS[slot];
      const gear = equipment[slot];
      // 每格外面套一层，方便在右上角放「洗练」小按钮
      const wrap = document.createElement('div');
      wrap.className = 'gearSlotWrap';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gearSlot';
      // 槽位 id 挂在 data-* 上：有装备时槽位名不再显示，靠这个定位（测试 / 后续 UI 都用得上）
      btn.dataset.slot = slot;
      if (gear) btn.dataset.rarity = gear.rarity || 'common';
      if (!gear) {
        btn.disabled = true;
        const empty = document.createElement('span');
        empty.className = 'gearEmpty';
        empty.textContent = '空';
        const label = document.createElement('b');
        label.textContent = def.label;
        btn.appendChild(label);
        btn.appendChild(empty);
        btn.title = def.label + '尚未装备';
      } else {
        // 装备栏也显示美术图标（头盔 / 衣服 / 鞋子 / 手套 / 武器）
        const slotIcon = document.createElement('div');
        slotIcon.className = 'gearSlotIcon';
        fillItemIcon(slotIcon, gear, gear.icon || def.icon || '装');
        const name = document.createElement('span');
        name.className = 'gearName';
        name.textContent = gear.name;
        name.style.color = gear.rarityColor || '#fff';
        /* 有装备的插槽只显示「图标 + 装备名」：
         *   槽位名（武器 / 头盔…）和「卸下」都不再显示 —— 装备名自己就说明这是什么槽，
         *   点一下就是卸下（鼠标悬停有装备属性小窗）。插槽清空后槽位名会重新出现。 */
        btn.appendChild(slotIcon);
        btn.appendChild(name);
        btn.addEventListener('click', function () { unequipSlot(slot); });
        // 鼠标悬停显示这件装备的属性（替代原来顶部那一长串总属性）
        bindItemTip(btn, gear);
        // 洗练按钮：打开洗练窗口（左模型 / 右词缀 / 下洗练）
        const rb = document.createElement('button');
        rb.type = 'button';
        rb.className = 'reforgeBtn';
        rb.textContent = '洗练';
        rb.title = '洗练「' + gear.name + '」的全部词缀';
        rb.addEventListener('click', function (e) {
          e.stopPropagation();
          openReforge(gear);
        });
        wrap.appendChild(rb);
        /* 镶嵌按钮：就在洗练按钮下面（同一个右上角按钮列）
         *   打开镶嵌窗口 → 选入侵材料 → 打前缀 + 追加 1 条随机词缀 */
        const ib = document.createElement('button');
        ib.type = 'button';
        ib.className = 'inlayBtn' + (gear.inlay ? ' done' : '');
        ib.textContent = gear.inlay ? ('已镶·' + gear.inlay.prefix) : '镶嵌';
        ib.title = gear.inlay
          ? ('「' + gear.name + '」已镶嵌「' + gear.inlay.prefix + '」，点这里可替换材料')
          : ('给「' + gear.name + '」镶嵌入侵材料（加前缀 + 追加 1 条随机词缀）');
        ib.addEventListener('click', function (e) {
          e.stopPropagation();
          openInlay(gear);
        });
        wrap.appendChild(ib);
      }
      wrap.appendChild(btn);
      ui.equipSlots.appendChild(wrap);
    });

    ui.bagList.textContent = '';
    // 装备 → 道具 → 材料，按类型显式分组，避免排序歧义
    const bagItems = [];
    ['equipment', 'costume', 'santosRelic', 'gem', 'skillStone', 'legendStone', 'legendTicket', 'petEgg', 'petBook', 'spiritItem', 'potion', 'worldChest', 'sticker', 'material'].forEach(function (type) {
      inventory.forEach(function (it) {
        if (it.type === type) bagItems.push(it);
      });
    });

    // 可叠加物品：技能石 / 回血药瓶 等同类同名道具合成一格，显示 ×N
    const STACKABLE = ['potion', 'skillStone', 'petEgg', 'petBook', 'spiritItem', 'legendTicket', 'legendStone', 'material'];
    const bagEntries = [];
    const stackIndex = {};
    bagItems.forEach(function (it) {
      if (STACKABLE.indexOf(it.type) < 0) {
        bagEntries.push({ item: it, count: 1, items: [it] });
        return;
      }
      // 回血药瓶：按名字叠加（恢复量会随等级/随机浮动，不能因为数值不同就分成两格）
      const key = it.type === 'potion'
        ? it.type + '|' + it.name
        : it.type + '|' + it.name + '|' + (it.detail || '');
      if (stackIndex[key] === undefined) {
        stackIndex[key] = bagEntries.length;
        bagEntries.push({ item: it, count: 1, items: [it] });
      } else {
        const entry = bagEntries[stackIndex[key]];
        entry.count += 1;
        entry.items.push(it);
      }
    });

    if (!bagEntries.length) {
      const empty = document.createElement('div');
      empty.className = 'bagRow';
      empty.textContent = '背包空空如也，搜索尸体试试手气吧。';
      ui.bagList.appendChild(empty);
    }
    // 正方形插槽：一格一件，可叠加的直接角标显示 ×N（尺寸统一，不随内容变）
    bagEntries.forEach(function (entry) {
      const item = entry.item;
      const slotBtn = document.createElement('button');
      slotBtn.type = 'button';
      slotBtn.className = 'bagSlot';
      slotBtn.style.borderColor = item.rarityColor || 'rgba(255, 215, 160, .3)';

      const icon = document.createElement('div');
      icon.className = 'bagSlotIcon';
      fillItemIcon(icon, item, item.icon || '?');
      icon.style.color = item.rarityColor || '#ffe6bb';
      slotBtn.appendChild(icon);

      const tag = document.createElement('span');
      tag.className = 'bagSlotTag';
      tag.textContent = bagTagText(item);
      slotBtn.appendChild(tag);

      if (entry.count > 1) {
        const count = document.createElement('span');
        count.className = 'bagSlotCount';
        count.textContent = '×' + entry.count;
        slotBtn.appendChild(count);
      }

      /* 这里只写 aria-label，**不能写 title**：
       *   鼠标悬停时已经有自定义的黄色属性卡片（bindItemTip → #itemTip），
       *   再挂 title 会同时弹出浏览器原生的白底提示，压着黄色卡片、影响浏览。
       *   （和技能窗口那处同一个处理：属性说明交给自定义卡片） */
      slotBtn.setAttribute('aria-label', item.name + (item.type === 'equipment'
        ? '：' + (item.baseDetail ? '〔基底〕' + item.baseDetail + '　' : '') + (item.detail || '无词缀')
        : (item.detail ? '：' + item.detail : '')));

      if (item.type === 'equipment') bindItemTip(slotBtn, item);
      slotBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        openBagMenu(slotBtn, entry);
      });
      ui.bagList.appendChild(slotBtn);
    });

    // 顶部不再堆一长串总属性：总结属性在「角色」窗口，这里只保留悬停查看单件装备
  }

  /* ---------- 背包正方形插槽：小标签 / 点击菜单 ---------- */
  // 插槽左上角的小标签，一眼看出这是装备还是道具
  function bagTagText(item) {
    if (!item) return '物品';
    if (item.sectMaterial === 'ore') return '矿石';
    if (item.type === 'equipment') {
      if (item.slot === 'weapon') return item.weaponType || '武器';
      const def = SLOT_DEFS[item.slot];
      return def ? def.label : '装备';
    }
    return {
      gem: '技能石', skillStone: '主动石', legendStone: '召唤石', legendTicket: '挑战卷',
      petEgg: '宠物蛋', petBook: '宠物书', spiritItem: '突破材料',
      potion: item.pill ? '丹药' : '药瓶',
      worldChest: '宝箱', santosRelic: '圣物', material: '材料', costume: '时装',
      sticker: '表情包'
    }[item.type] || '物品';
  }

  // 一件物品能做的操作（和以前列表里的按钮完全一致，只是挪到了弹出菜单里）
  function bagActionsFor(item) {
    const acts = [];
    if (item.type === 'equipment') {
      acts.push({ label: equipment[item.slot] ? '替换' : '装备', run: function () { equipItem(item); } });
      // 价格不再写在按钮上（挪到"等级"后面显示），按钮只保留「出售」
      acts.push({ label: '出售', cls: 'sell', title: '卖出价 ' + sellPrice(item) + ' 金币',
        run: function () { sellItem(item); } });
      acts.push({ label: '分享', cls: 'share', title: '把这件装备分享到世界频道',
        run: function () { shareItemToChat(item); } });
      // 背包里的装备也能直接镶嵌（和装备栏那个「镶嵌」按钮同一个窗口）
      acts.push({
        label: item.inlay ? '更换镶嵌' : '镶嵌', cls: 'inlay',
        title: item.inlay ? ('已镶嵌「' + item.inlay.prefix + '」，点这里换材料') : '镶嵌入侵材料：加前缀 + 追加 1 条随机词缀',
        run: function () { openInlay(item); }
      });
    } else if (item.type === 'costume') {
      // 时装：穿脱不花一分钱，也不影响属性
      const def = costumeDef(item.costumeId);
      const worn = def ? equippedCostume(def.slot) : null;
      if (worn && worn.id === item.costumeId) {
        acts.push({ label: '脱下', run: function () { unequipCostume(def.slot); } });
      } else {
        acts.push({
          label: worn ? '替换「' + worn.name + '」' : '穿戴',
          title: def && def.drop ? ('掉落来源：' + def.drop) : '',
          run: function () { equipCostume(item); }
        });
      }
      acts.push({ label: '分享', cls: 'share', title: '把这件时装分享到世界频道',
        run: function () { shareItemToChat(item); } });
    } else if (item.type === 'gem') {
      acts.push({
        label: item.identified ? '镶嵌' : '选择效果',
        run: function () { if (item.identified) openSocketPanel(item); else openGemIdentify(item); }
      });
    } else if (item.type === 'skillStone') {
      acts.push({ label: '使用', run: function () { useActiveSkillStone(item); } });
    } else if (item.type === 'legendStone') {
      acts.push({ label: '召唤', run: function () { useLegendStone(item); } });
    } else if (item.type === 'legendTicket') {
      acts.push({ label: '使用', run: function () { useLegendTicket(item); } });
    } else if (item.type === 'petEgg') {
      acts.push({ label: '孵化', run: function () { usePetEgg(item); } });
    } else if (item.type === 'petBook') {
      acts.push({ label: '使用', run: function () { usePetSkillBook(item); } });
    } else if (item.type === 'spiritItem') {
      if (player.bottleneck) acts.push({ label: '突破', run: breakthrough });
      else acts.push({ label: '突破材料', disabled: true });
    } else if (item.sectMaterial === 'ore') {
      if (oreMaterialKind(item) === 'attribute') {
        /* 属性矿石也走镶嵌（不再直接使用）：附加到装备上，永久给那件装备加三维 */
        acts.push({
          label: '镶嵌', cls: 'inlay',
          title: '镶嵌到装备上：附加 ' + oreAttrValueText(item),
          run: function () { startInlayFromMaterial(oreAttrInlayId(item), item.name); }
        });
        const lv = item.oreAttrLevel || 1;
        const sameCount = inventory.filter(function (it) {
          return it && oreMaterialKind(it) === 'attribute' && it.oreAttr === item.oreAttr && (it.oreAttrLevel || 1) === lv;
        }).length;
        acts.push({
          label: lv >= 5 ? '已满级' : ('合成（' + Math.min(sameCount, 3) + '/3）'),
          cls: 'contribute',
          title: lv >= 5 ? '5级已是最高等级' : '3 颗同属性同等级矿石合成 1 颗高一级矿石',
          disabled: lv >= 5 || sameCount < 3,
          run: function () { combineAttributeOres(item); }
        });
        acts.push({
          label: '出售', cls: 'sell',
          title: '售出后获得 ' + oreValue(item) + ' 金币',
          run: function () { sellOreItem(item); }
        });
        acts.push({
          label: '上缴宗门', cls: 'contribute',
          title: hasSect() ? '上缴九玄山，获得 ' + oreContributionValue(item) + ' 门派贡献' : '加入门派后才能上缴',
          disabled: !hasSect(),
          run: function () { contributeOreItem(item); }
        });
      } else {
        acts.push({
          label: '出售', cls: 'sell',
          title: '售出后获得 ' + oreValue(item) + ' 金币',
          run: function () { sellOreItem(item); }
        });
        acts.push({
          label: '上缴宗门', cls: 'contribute',
          title: hasSect() ? '上缴九玄山，获得门派贡献' : '加入门派后才能上缴',
          disabled: !hasSect(),
          run: function () { contributeOreItem(item); }
        });
        acts.push({
          label: item.oreFinished ? '查看矿石' : '鉴定',
          cls: 'identify',
          title: oreIsRaw(item) ? ('花费 ' + ORE_IDENTIFY_COST + ' 金币鉴定，再进行三步加工') : '打开矿石加工窗口',
          run: function () { identifyOreItem(item); }
        });
      }
    } else if (item.type === 'potion') {
      acts.push({ label: '使用', run: function () { usePotion(item); } });
    } else if (item.type === 'worldChest') {
      acts.push({
        label: '打开', title: '开启世界BOSS宝箱（极小几率开出灵宝）',
        run: function () { openWorldBossChest(item); }
      });
    } else if (item.type === 'santosRelic') {
      acts.push({ label: '携带生效', disabled: true, title: '圣物带在背包里就会生效，不需要装备' });
    } else if (item.type === 'sticker') {
      // 聊天表情包：点「使用」激活，之后聊天表情栏里就能发送
      const s = stickerDef(item.stickerId);
      if (s && chatStickers[s.id]) {
        acts.push({ label: '已激活', disabled: true, title: '这枚表情已经激活过了：聊天窗口的表情栏里可以直接发送' });
      } else {
        acts.push({
          label: '使用', cls: 'use',
          title: '激活这枚聊天表情：之后聊天窗口的表情栏里就能发送它',
          run: function () { useStickerItem(item); }
        });
      }
    } else {
      /* 带 sellGold 的掉落物（深渊战利品这类）：可以直接卖掉换金币 */
      if (item.sellGold) {
        acts.push({
          label: '出售', cls: 'sell',
          title: '售出后获得 ' + item.sellGold + ' 金币',
          run: function () { sellLootItem(item); }
        });
      } else {
        acts.push({ label: '材料', disabled: true });
      }
    }
    // 其它类型的物品也都能分享（装备 / 时装上面已经单独插了，避免重复）
    if (item.type !== 'equipment' && item.type !== 'costume') {
      acts.push({ label: '分享', cls: 'share', title: '把这个物品分享到世界频道',
        run: function () { shareItemToChat(item); } });
    }
    return acts;
  }

  /* 把背包里的一件物品分享到世界频道。
   *   聊天里**只出现物品名字**，属性不再直接贴出来；
   *   其它玩家点这个名字，才会弹出这件物品的详细属性（走 share 负载）。 */
  const SHARE_MAX = 600;        // 负载上限（服务器同样按这个长度截断）
  function itemSharePayload(item) {
    if (!item) return '';
    const p = {
      n: String(item.name || '物品').slice(0, 40),
      t: item.type || 'material',
      r: item.rarity || '',
      s: item.slot || '',
      l: item.level || 0,
      w: item.weaponType || '',
      b: String(item.baseDetail || '').slice(0, 120),
      d: String(item.detail || '').slice(0, 300),
      k: bagTagText(item)                       // 类别文字（武器 / 防具 / 时装 …）
    };
    return JSON.stringify(p).slice(0, SHARE_MAX);
  }
  function parseItemShare(str) {
    if (!str || typeof str !== 'string') return null;
    try {
      const p = JSON.parse(str);
      return (p && typeof p === 'object' && p.n) ? p : null;
    } catch (e) { return null; }
  }
  function shareItemToChat(item) {
    if (!item) return false;
    const payload = itemSharePayload(item);
    sendChatText('【分享】' + item.name, payload);
    showToast('已把「' + item.name + '」分享到世界频道（点名字可看属性）。');
    dailyQuestTick('share');     // 每日任务：分享一次装备
    sectTaskTick('share');       // 门派任务：分享一次装备
    return true;
  }

  function hideBagMenu() {
    if (ui.bagMenu) ui.bagMenu.classList.add('hidden');
  }

  function openBagMenu(anchor, entry) {
    const menu = ui.bagMenu;
    if (!menu || !anchor || !entry) return;
    const item = entry.item;
    menu.textContent = '';

    const head = document.createElement('span');
    head.className = 'bagMenuHead';
    head.textContent = item.name + (entry.count > 1 ? ' ×' + entry.count : '');
    head.style.color = item.rarityColor || '#ffe6bb';
    menu.appendChild(head);

    const meta = document.createElement('span');
    meta.className = 'bagMenuMeta';
    // 装备：等级后面直接标出出售价（按钮上不再带价格）
    meta.textContent = item.type === 'equipment'
      ? (SLOT_DEFS[item.slot] ? SLOT_DEFS[item.slot].label : '装备') + ' · 等级 ' + item.level +
        ' · 售价 ' + sellPrice(item) + ' 金币'
      : bagTagText(item);
    menu.appendChild(meta);

    const actions = document.createElement('div');
    actions.className = 'bagMenuActions';
    bagActionsFor(item).forEach(function (act) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bagAction' + (act.cls ? ' ' + act.cls : '');
      btn.textContent = act.label;
      if (act.title) btn.title = act.title;
      if (act.disabled) btn.disabled = true;
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        hideBagMenu();
        if (!act.disabled) act.run();
      });
      actions.appendChild(btn);
    });
    menu.appendChild(actions);

    // 贴在插槽旁边（右边放不下就换到左边）
    const wrapEl = ui.wrap || document.body;
    menu.classList.remove('hidden');
    menu.style.left = '0px';
    menu.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    let left = aRect.right - wrapRect.left + 8;
    if (left + mw > wrapRect.width - 6) left = aRect.left - wrapRect.left - mw - 8;
    let top = aRect.top - wrapRect.top;
    left = Math.max(6, Math.min(left, wrapRect.width - mw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - mh - 6));
    menu.style.left = Math.round(left) + 'px';
    menu.style.top = Math.round(top) + 'px';
  }

  // ---------- 商店 ----------
  // 商店商品等级跟随玩家当前进度 / 已穿戴装备，品质和词缀随机，价格按属性高低计算
  function getShopItemLevel() {
    // 商店等级跟随玩家境界（以前按击杀数狂涨，才会出现"四境后期刷 60 级装备"）
    return clamp(Math.round(player.level || 1), 1, MAX_STAGE);
  }

  // 商店常驻商品：回血药瓶（固定 100 金币一瓶，不参与随机刷新，也不能锁定）
  const SHOP_POTION_PRICE = 100;
  function makeShopPotion() {
    const pot = makePotion(player.level);
    pot.price = SHOP_POTION_PRICE;
    pot.alwaysAvailable = true;
    pot.rarityName = '常驻道具';
    return pot;
  }
  /* 商店常驻商品②：辅助技能石（500 金币一颗，买了自己挑效果）
   *   点「购买」→ 弹出辅助石列表（多重投射物 / 投射物加速 / 处决 / 炎军 / 召唤系辅助…）
   *   → 挑一颗立即扣 500 金币进背包。 */
  const SHOP_SUPPORT_GEM_PRICE = 500;
  function makeShopSupportGem() {
    return {
      id: 'shopGem' + (++itemSeq), type: 'shopGem', icon: '辅',
      name: '辅助技能石', price: SHOP_SUPPORT_GEM_PRICE, alwaysAvailable: true,
      rarityName: '常驻道具（自选）',
      detail: '购买后从辅助石列表里挑一颗（' + SHOP_SUPPORT_GEM_PRICE + ' 金币）'
    };
  }

  function openShop() {
    const level = getShopItemLevel();
    // 已锁定的商品继续保留，其余位置刷新出新商品
    shopItems = lockedShopItems.slice();
    const count = randInt(4, 5);
    for (let i = shopItems.length; i < count; i++) {
      const gearLevel = Math.max(1, level + randInt(-1, 1));
      shopItems.push(makeEquipment(gearLevel));
    }
    // 商店至少保证有一件武器（玩家主要就是来淘武器的）
    if (shopItems.length && !shopItems.some(function (it) { return it.slot === 'weapon'; })) {
      shopItems[shopItems.length - 1] = makeEquipment(level, null, 'weapon');
    }
    // 固定商品：回血药瓶（每次开店都在，价格固定 100 金币）
    shopItems.push(makeShopPotion());
    // 固定商品：辅助技能石（500 金币，自选一颗）
    shopItems.push(makeShopSupportGem());
    renderShop();
    ui.shopPopup.classList.remove('hidden');
  }

  function closeShop() {
    ui.shopPopup.classList.add('hidden');
  }

  function buyShopItem(item) {
    // 常驻商品（回血药瓶）：买完货架上还留着，背包里每次都是新的道具
    if (item && item.type === 'potion') {
      if (gold < item.price) {
        showToast('金币不足，「回血药瓶」需要 ' + item.price + ' 金币。');
        return;
      }
      gold -= item.price;
      updateGold();
      inventory.push(makePotion(player.level));
      renderShop();
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      showToast('已购买「回血药瓶」×1（-' + item.price + ' 金币），可在背包中使用。');
      return;
    }
    // 常驻商品（辅助技能石）：先看钱够不够，再让玩家挑一颗
    if (item && item.type === 'shopGem') {
      if (gold < SHOP_SUPPORT_GEM_PRICE) {
        showToast('金币不足：辅助技能石需要 ' + SHOP_SUPPORT_GEM_PRICE + ' 金币。');
        return;
      }
      openSupportGemShop();
      return;
    }
    if (gold < item.price) {
      showToast('金币不足，无法购买这件装备。');
      return;
    }
    gold -= item.price;
    updateGold();
    inventory.push(item);
    shopItems = shopItems.filter(function (it) { return it.id !== item.id; });
    lockedShopItems = lockedShopItems.filter(function (it) { return it.id !== item.id; });
    renderShop();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('已购买「' + item.name + '」，可在装备栏中穿戴。');
    dailyQuestTick('shop');      // 每日任务：商店购买过一次装备
  }

  function toggleLockShopItem(item) {
    if (item.locked) {
      item.locked = false;
      lockedShopItems = lockedShopItems.filter(function (it) { return it.id !== item.id; });
      renderShop();
      showToast('已解锁「' + item.name + '」，下次开店会刷新。');
      return;
    }
    const cost = SHOP_LOCK_COST[item.rarity] || 10;
    if (gold < cost) {
      showToast('锁定需要 ' + cost + ' 金币，金币不足。');
      return;
    }
    gold -= cost;
    updateGold();
    item.locked = true;
    lockedShopItems.push(item);
    renderShop();
    showToast('已锁定「' + item.name + '」（花费 ' + cost + ' 金币），下次开店仍会保留。');
  }

  function renderShop() {
    ui.shopGold.textContent = String(gold);
    ui.shopList.textContent = '';
    hideItemTip();       // 重新渲染后旧的悬停卡片会对不上锚点
    hideBagMenu();
    if (!shopItems.length) {
      const empty = document.createElement('div');
      empty.className = 'shopItem';
      empty.textContent = '这一轮商品已售罄，下次到访会补货。';
      ui.shopList.appendChild(empty);
      return;
    }
    shopItems.forEach(function (item) {
      const card = document.createElement('div');
      card.className = 'shopItem';
      // 悬停：显示这件装备的属性，并和当前装备逐条对比（差值高亮）
      if (item.type === 'equipment') {
        card.addEventListener('mouseenter', function () { showItemTip(card, item, { compare: true }); });
        card.addEventListener('mouseleave', hideItemTip);
      }

      const top = document.createElement('div');
      top.style.display = 'flex';
      top.style.alignItems = 'center';
      top.style.gap = '8px';
      const icon = document.createElement('div');
      icon.className = 'shopIcon';
      fillItemIcon(icon, item, item.icon);
      icon.style.color = item.rarityColor;
      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'shopName';
      name.textContent = item.name;
      name.style.color = item.rarityColor;
      const lv = document.createElement('div');
      lv.className = 'shopLv';
      // 装备显示装备等级；常驻道具（回血药瓶）显示品类
      lv.textContent = item.type === 'equipment'
        ? ('装备等级 ' + item.level)
        : (item.rarityName || '道具');
      info.appendChild(name);
      info.appendChild(lv);
      top.appendChild(icon);
      top.appendChild(info);
      card.appendChild(top);

      const mods = document.createElement('div');
      mods.className = 'shopMods';
      mods.textContent = item.detail;
      card.appendChild(mods);

      const actions = document.createElement('div');
      actions.className = 'shopActions';

      // 常驻商品不用锁定（每轮都在货架上）
      if (item.type === 'equipment') {
        const lock = document.createElement('button');
        lock.type = 'button';
        lock.className = 'shopLock' + (item.locked ? ' on' : '');
        lock.textContent = item.locked ? '已锁定' : '锁定 ' + (SHOP_LOCK_COST[item.rarity] || 10);
        lock.addEventListener('click', function () { toggleLockShopItem(item); });
        actions.appendChild(lock);
      }

      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'shopBuy';
      buy.textContent = '购买 · ' + item.price + ' 金币';
      buy.disabled = gold < item.price;
      buy.addEventListener('click', function () { buyShopItem(item); });
      actions.appendChild(buy);
      card.appendChild(actions);
      ui.shopList.appendChild(card);
    });
  }

  // ---------- 活力兑换 ----------
  function openExchange() {
    renderExchange();
    ui.exchangePopup.classList.remove('hidden');
  }

  function closeExchange() {
    ui.exchangePopup.classList.add('hidden');
  }

  function renderExchange() {
    ui.exchangeNote.textContent = '当前活力 ' + vitality + '（签到与自动冒险会消耗/获得活力）';
    ui.exchangeList.textContent = '';
    EXCHANGE_ITEMS.forEach(function (entry) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'socketSkill';
      const name = document.createElement('span');
      name.textContent = entry.label;
      const meta = document.createElement('small');
      meta.textContent = entry.cost + ' 活力 · ' + entry.note +
        (entry.owned ? '　（' + entry.owned() + '）' : '');
      btn.appendChild(name);
      btn.appendChild(meta);
      btn.disabled = vitality < entry.cost;
      btn.addEventListener('click', function () { buyExchange(entry); });
      ui.exchangeList.appendChild(btn);
    });
  }

  function buyExchange(entry) {
    if (vitality < entry.cost) {
      showToast('活力不足，无法兑换「' + entry.label + '」。');
      return;
    }
    vitality -= entry.cost;
    saveGame();
    updateVitalityUI();
    if (entry.grant) {
      // 令牌这类不放进背包的兑换物，直接计数
      entry.grant();
      saveGame();
      renderExchange();
      showToast('消耗 ' + entry.cost + ' 活力，兑换了「' + entry.label + '」' +
        (entry.owned ? '，' + entry.owned() : '') + '。');
      return;
    }
    const item = entry.make();
    inventory.push(item);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderExchange();
    showToast('消耗 ' + entry.cost + ' 活力，兑换了「' + item.name + '」。');
  }

  // ---------- 首次宝箱 ----------
  function openChestPanel() {
    if (!chest) return;
    chest.opened = true;
    renderChestPanel();
    ui.chestPopup.classList.remove('hidden');
  }

  function closeChestPanel() {
    ui.chestPopup.classList.add('hidden');
    if (chest && chest.weaponTaken && chest.gemTaken && chest.stoneTaken) {
      chest.claimed = true;
      showToast('宝箱已经取空，祝你旅途顺利。');
    }
  }

  // 新手宝箱的第三份礼物：主动技能石
  function takeChestStone() {
    if (!chest || chest.stoneTaken) return;
    const stone = makeActiveSkillStone();
    inventory.push(stone);
    chest.stoneItem = stone;
    chest.stoneTaken = true;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得「主动技能石」：在背包中使用可随机习得一个主动技能。');
  }

  function takeChestWeapon(item) {
    if (!chest || chest.weaponTaken) return;
    inventory.push(item);
    chest.weaponTaken = true;
    chest.chosenWeapon = item.id;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得白色武器「' + item.name + '」，已放入背包。');
  }

  function takeChestGem() {
    if (!chest || chest.gemTaken) return;
    const gem = makeEmptyGem();
    inventory.push(gem);
          chest.gemItem = gem;
    chest.gemTaken = true;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得「空白被动技能石」：在背包中右键点击可选择被动效果。');
  }

  function renderChestPanel() {
    if (!chest) return;
    ui.chestWeapons.textContent = '';
    chest.weapons.forEach(function (item) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chestWeapon' + (chest.chosenWeapon === item.id ? ' chosen' : '');
      if (chest.weaponTaken && chest.chosenWeapon !== item.id) btn.disabled = true;
      const icon = document.createElement('b');
      fillItemIcon(icon, item, item.icon);
      const name = document.createElement('span');
      name.textContent = item.weaponType;
      const affix = document.createElement('small');
      affix.textContent = item.detail;
      btn.appendChild(icon);
      btn.appendChild(name);
      btn.appendChild(affix);
      btn.addEventListener('click', function () { takeChestWeapon(item); });
      ui.chestWeapons.appendChild(btn);
    });

    ui.chestGem.textContent = '';
    const box = document.createElement('div');
    box.className = 'chestGemBox';
    const icon = document.createElement('div');
    icon.className = 'gemIcon';
    // 空白被动技能石也用辅助石的美术图标（以前这里只有文字「石」）
    fillItemIcon(icon, { type: 'gem', name: '空白被动技能石' }, '石');
    const info = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'bagName';
    const detail = document.createElement('div');
    detail.className = 'bagMeta';
    if (chest.gemTaken && chest.gemItem) {
      name.textContent = chest.gemItem.name;
      detail.textContent = chest.gemItem.detail;
    } else {
      name.textContent = '空白被动技能石';
      detail.textContent = '右键点击后可挑选属性增幅';
      const take = document.createElement('button');
      take.type = 'button';
      take.className = 'bagAction';
      take.textContent = '领取';
      take.style.marginLeft = 'auto';
      take.addEventListener('click', takeChestGem);
      box.appendChild(take);
    }
    name.style.color = '#bfe1ff';
    info.appendChild(name);
    info.appendChild(detail);
    box.insertBefore(icon, box.firstChild);
    box.insertBefore(info, box.children[1] || null);
    ui.chestGem.appendChild(box);

    // 主动技能石（第三份礼物）
    if (ui.chestStone) {
      ui.chestStone.textContent = '';
      const sbox = document.createElement('div');
      sbox.className = 'chestGemBox';
      const sicon = document.createElement('div');
      sicon.className = 'gemIcon';
      sicon.textContent = '技';
      const sinfo = document.createElement('div');
      const sname = document.createElement('div');
      sname.className = 'bagName';
      const sdetail = document.createElement('div');
      sdetail.className = 'bagMeta';
      if (chest.stoneTaken && chest.stoneItem) {
        sname.textContent = chest.stoneItem.name;
        sdetail.textContent = chest.stoneItem.detail;
      } else {
        sname.textContent = '主动技能石';
        sdetail.textContent = '使用后随机习得一个主动技能';
        const stake = document.createElement('button');
        stake.type = 'button';
        stake.className = 'bagAction';
        stake.textContent = '领取';
        stake.style.marginLeft = 'auto';
        stake.addEventListener('click', takeChestStone);
        sbox.appendChild(stake);
      }
      sname.style.color = '#ffd265';
      sinfo.appendChild(sname);
      sinfo.appendChild(sdetail);
      sbox.insertBefore(sicon, sbox.firstChild);
      sbox.insertBefore(sinfo, sbox.children[1] || null);
      ui.chestStone.appendChild(sbox);
    }
  }

  // ---------- 被动技能石：挑选效果 ----------
  function openGemIdentify(item) {
    if (!item || item.type !== 'gem') return;
    identifyingGem = item;
    shopGemBuying = false;
    ui.gemOptions.textContent = '';
    GEM_OPTIONS.forEach(function (opt) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gemOption';
      btn.textContent = opt.support
        ? opt.name
        : (opt.name + ' +' + opt.min + '~' + opt.max + opt.unit);
      const tip = document.createElement('small');
      tip.textContent = !opt.support
        ? (opt.id === 'summonDamage' ? '作用于召唤出来的宠物' : '镶嵌到主动技能后生效')
        : opt.minionOnly
          ? '召唤辅助：只能镶嵌到召唤类技能（蝎子 / 鬣狗 / 毒蛇 / 猎鹰 / 蝙蝠）'
          : opt.projectileOnly
            ? '投射物辅助：只认「投射物」标签（' + projectileSkillHint() + '）'
            : opt.onlySkill
              ? '专属辅助：只能镶嵌到「' +
                ((knownSkills.filter(function (s) { return s.id === opt.onlySkill; })[0] || {}).name || opt.onlySkill) + '」'
              : '辅助：镶嵌到任意击中敌人的技能后生效';
      btn.appendChild(tip);
      btn.addEventListener('click', function () { chooseGemOption(opt); });
      ui.gemOptions.appendChild(btn);
    });
    ui.gemPopup.classList.remove('hidden');
  }

  /* 商店里买辅助技能石：只列辅助石（support: true），挑一颗立刻结算 500 金币 */
  let shopGemBuying = false;
  function openSupportGemShop() {
    shopGemBuying = true;
    identifyingGem = null;
    ui.gemOptions.textContent = '';
    GEM_OPTIONS.filter(function (o) { return o.support; }).forEach(function (opt) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gemOption';
      btn.textContent = opt.name;
      const tip = document.createElement('small');
      tip.textContent = SHOP_SUPPORT_GEM_PRICE + ' 金币　·　' + (opt.short || '');
      btn.appendChild(tip);
      btn.addEventListener('click', function () { chooseGemOption(opt); });
      ui.gemOptions.appendChild(btn);
    });
    ui.gemPopup.classList.remove('hidden');
  }

  function closeGemPanel() {
    identifyingGem = null;
    shopGemBuying = false;
    ui.gemPopup.classList.add('hidden');
  }

  function chooseGemOption(opt) {
    // 商店模式：扣钱 → 直接给一颗对应的辅助石
    if (shopGemBuying) {
      if (gold < SHOP_SUPPORT_GEM_PRICE) {
        showToast('金币不足：辅助技能石需要 ' + SHOP_SUPPORT_GEM_PRICE + ' 金币。');
        return;
      }
      const bought = gemOptionToItem(opt);
      gold -= SHOP_SUPPORT_GEM_PRICE;
      updateGold();
      inventory.push(bought);
      shopGemBuying = false;
      ui.gemPopup.classList.add('hidden');
      if (!ui.shopPopup.classList.contains('hidden')) renderShop();
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      showToast('已购买辅助技能石「' + bought.name + '」（-' + SHOP_SUPPORT_GEM_PRICE +
        ' 金币）：' + bought.detail);
      return;
    }
    if (!identifyingGem) return;
    const oldId = identifyingGem.id;
    const gem = gemOptionToItem(opt);
    inventory = inventory.filter(function (it) { return it.id !== oldId; });
    inventory.push(gem);
    identifyingGem = null;
    ui.gemPopup.classList.add('hidden');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('生成「' + gem.name + '」：' + gem.detail);
  }

  // ---------- 被动技能石：镶嵌 ----------
  function openSocketPanel(gem) {
    if (!gem || !gem.identified) return;
    socketingGem = gem;
    renderSocketOptions();
    ui.socketPopup.classList.remove('hidden');
  }

  function closeSocketPanel() {
    socketingGem = null;
    ui.socketPopup.classList.add('hidden');
  }

  function renderSocketOptions() {
    ui.socketOptions.textContent = '';
    knownSkills.forEach(function (skill) {
      const sockets = skill.sockets || (skill.sockets = []);
      const slots = socketSlotsOf(skill);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'socketSkill';
      const name = document.createElement('span');
      name.textContent = skill.name;
      const meta = document.createElement('small');
      meta.textContent = '镶孔 ' + sockets.length + ' / ' + slots;
      btn.appendChild(name);
      btn.appendChild(meta);
      // 投射物辅助只能进投射物技能；召唤辅助只能进召唤技能；专属辅助只能进指定技能
      const gem = socketingGem;
      const wrongSkill = !!(gem && ((isProjectileOnlyGem(gem) && !isProjectileSkill(skill.id)) ||
        (isMinionOnlyGem(gem) && skill.type !== 'summon') ||
        (gem.onlySkill && skill.id !== gem.onlySkill)));
      btn.disabled = wrongSkill || sockets.length >= slots;
      if (wrongSkill) {
        btn.title = isMinionOnlyGem(gem)
          ? '「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到召唤类技能（蝎子 / 鬣狗 / 毒蛇 / 猎鹰 / 蝙蝠）'
          : isProjectileOnlyGem(gem)
            ? '「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到带「投射物」标签的技能（' + projectileSkillHint() + '）'
            : '「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到指定技能';
      } else if (btn.disabled) {
        btn.title = slots < SOCKET_SLOTS_MAX
          ? '镶孔已满，可花 ' + SOCKET_SLOT_COST + ' 金币开一个新槽'
          : '镶孔已满（已达上限 ' + SOCKET_SLOTS_MAX + ' 个）';
      }
      btn.addEventListener('click', function () { socketGemInto(skill, socketingGem); });
      ui.socketOptions.appendChild(btn);
      // 镶孔满了但还没到 6 个 → 直接在这里开槽
      if (sockets.length >= slots && slots < SOCKET_SLOTS_MAX) {
        const buy = document.createElement('button');
        buy.type = 'button';
        buy.className = 'socketSkill slotBuy';
        const bName = document.createElement('span');
        bName.textContent = '＋ 给「' + skill.name + '」开一个镶孔';
        const bMeta = document.createElement('small');
        bMeta.textContent = SOCKET_SLOT_COST + ' 金币（' + slots + ' → ' + (slots + 1) + ' / ' + SOCKET_SLOTS_MAX + '）';
        buy.appendChild(bName);
        buy.appendChild(bMeta);
        buy.disabled = gold < SOCKET_SLOT_COST;
        if (buy.disabled) buy.title = '金币不足';
        buy.addEventListener('click', function () { buySocketSlot(skill); });
        ui.socketOptions.appendChild(buy);
      }
    });
  }

  function socketGemInto(skill, gem) {
    if (!skill || !gem) return;
    // 投射物辅助只能镶嵌到带「投射物」标签的技能（剑气 / 箭矢 / 碎石崩裂）
    if (isProjectileOnlyGem(gem) && !isProjectileSkill(skill.id)) {
      showToast('「' + gem.name.replace(/^辅助技能石：/, '') +
        '」只能镶嵌到带「投射物」标签的技能（' + projectileSkillHint() + '）。');
      return;
    }
    // 专属辅助只能镶嵌到指定技能（例：震撼大地 → 阳爪）
    if (gem.onlySkill && skill.id !== gem.onlySkill) {
      const target = knownSkills.filter(function (s) { return s.id === gem.onlySkill; })[0];
      showToast('「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到「' +
        (target ? target.name : gem.onlySkill) + '」上。');
      return;
    }
    // 召唤辅助只能镶嵌到召唤类技能
    if (isMinionOnlyGem(gem) && skill.type !== 'summon') {
      showToast('「' + gem.name.replace(/^辅助技能石：/, '') +
        '」只能镶嵌到召唤类技能（蝎子 / 鬣狗 / 毒蛇 / 猎鹰 / 蝙蝠）。');
      return;
    }
    if (!skill.sockets) skill.sockets = [];
    const slots = socketSlotsOf(skill);
    if (skill.sockets.length >= slots) {
      showToast(slots < SOCKET_SLOTS_MAX
        ? '「' + skill.name + '」的镶孔已满（' + slots + ' 个），可花 ' + SOCKET_SLOT_COST + ' 金币再开一个槽。'
        : '「' + skill.name + '」的镶孔已满（已达上限 ' + SOCKET_SLOTS_MAX + ' 个）。');
      return;
    }
    gem.socketedTo = skill.id;
    skill.sockets.push(gem);
    inventory = inventory.filter(function (it) { return it.id !== gem.id; });
    socketingGem = null;
    ui.socketPopup.classList.add('hidden');
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    showToast('已将「' + gem.name + '」镶嵌到「' + skill.name + '」。');
  }

  function unsocketGem(skill, gem, force) {
    if (!skill || !gem) return;
    /* 战斗中不许卸技能石：
     *   战斗时点技能卡是为了"放招"，很容易顺手点到卡里的辅助石（以前会直接把石头取回背包）。
     *   现在只有非战斗场景（技能窗口安静地打开时）点辅助石才会卸下。 */
    if (!force && gameState === 'battle') {
      showToast('战斗中不能取下技能石：打完再从技能窗口里取。');
      return;
    }
    skill.sockets = (skill.sockets || []).filter(function (g) { return g.id !== gem.id; });
    gem.socketedTo = null;
    inventory.push(gem);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderSpellList();
    showToast('已取下「' + gem.name + '」，放回背包。');
  }

  // ---------- 技能悬停提示：卡片内只放摘要，完整描述与镶嵌技能石随鼠标显示 ----------
  const SKILL_TYPE_LABELS = {
    melee: '近战技能', ranged: '远程技能', spell: '法术', summon: '召唤技能', heal: '恢复技能', toggle: '持续技能'
  };

  function hideSkillTip() {
    if (ui.skillTip) ui.skillTip.classList.add('hidden');
  }

  /* 门派武学悬停详情：复用技能提示卡片，但内容按"门派贡献 / 学习状态"组织，
   *   不和战斗法术窗口的镶孔信息混在一起。 */
  function showSectSkillTip(anchor, sk, owned) {
    if (!ui.skillTip || !anchor || !sk) return;
    const tip = ui.skillTip;
    tip.textContent = '';

    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = '#8fd0ff';
    const name = document.createElement('b');
    name.textContent = sk.name + (owned ? ' Lv.' + (owned.level || 1) : '');
    const type = document.createElement('span');
    type.textContent = SKILL_TYPE_LABELS[sk.type] || '门派武学';
    type.style.color = '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(type);
    tip.appendChild(head);

    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const cost = document.createElement('b');
    cost.textContent = '灵力消耗 ' + (sk.cost || 0);
    mods.appendChild(cost);
    if (sk.dmg) {
      const dmg = document.createElement('b');
      dmg.textContent = '基础伤害 ' + sk.dmg[0] + '~' + sk.dmg[1];
      mods.appendChild(dmg);
    }
    const price = document.createElement('b');
    price.textContent = (owned ? '升级消耗' : '兑换消耗') + ' 门派贡献 ' + (sk.price || 0);
    mods.appendChild(price);
    if (sk.minStage) {
      const stage = document.createElement('b');
      stage.textContent = '需求境界：' + stageName(sk.minStage) + '及以上';
      mods.appendChild(stage);
    }
    const state = document.createElement('b');
    state.textContent = owned
      ? '状态：已学 · 当前等级 Lv.' + (owned.level || 1)
      : '状态：尚未学习（兑换后可在战斗中使用）';
    state.style.color = owned ? '#9adf8c' : '#ffd08a';
    mods.appendChild(state);
    tip.appendChild(mods);

    const desc = document.createElement('div');
    desc.className = 'tipDesc';
    desc.textContent = sk.desc || '门派武学：用门派贡献兑换，学会后可在战斗中施放。';
    tip.appendChild(desc);

    const wrapEl = ui.wrap || document.body;
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = anchorRect.right - wrapRect.left + 12;
    if (left + tw > wrapRect.width - 6) {
      left = anchorRect.left - wrapRect.left - tw - 12;
    }
    let top = anchorRect.top - wrapRect.top + anchorRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  /* 门派法诀悬停详情：显示完整说明、参悟进度、下一层消耗与封极法各部位生效状态 */
  function showSectArtTip(anchor, art, learned, next) {
    if (!ui.skillTip || !anchor || !art) return;
    const tip = ui.skillTip;
    const layers = artLayers(art);
    tip.textContent = '';

    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = '#c9a3ff';
    const name = document.createElement('b');
    name.textContent = art.name;
    const type = document.createElement('span');
    type.textContent = '门派法诀';
    type.style.color = '#d9c0ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(type);
    tip.appendChild(head);

    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const progress = document.createElement('b');
    progress.textContent = '参悟进度：' + learned + ' / ' + layers.length + ' 层';
    mods.appendChild(progress);
    if (next) {
      const nextLine = document.createElement('b');
      nextLine.textContent = '下一层：' + next.name + '　·　消耗门派贡献 ' + (next.price || 0);
      mods.appendChild(nextLine);
      if (next.slot && SLOT_DEFS[next.slot]) {
        const slotLine = document.createElement('b');
        slotLine.textContent = '作用部位：' + SLOT_DEFS[next.slot].label;
        mods.appendChild(slotLine);
      }
      if (next.boostPct) {
        const boostLine = document.createElement('b');
        boostLine.textContent = '达标装备增幅：+' + next.boostPct + '%';
        mods.appendChild(boostLine);
      }
    } else {
      const full = document.createElement('b');
      full.textContent = '状态：已修至圆满';
      full.style.color = '#9adf8c';
      mods.appendChild(full);
    }
    tip.appendChild(mods);

    const desc = document.createElement('div');
    desc.className = 'tipDesc';
    desc.textContent = art.desc || '门派修炼法诀：用门派贡献逐层参悟。';
    tip.appendChild(desc);

    // 封极法已参悟部位的生效情况（和门派窗口里的状态行一致）
    if (art.id === 'fengji' && learned > 0) {
      const box = document.createElement('div');
      box.className = 'tipSockets';
      const title = document.createElement('em');
      title.textContent = '已参悟部位生效情况';
      box.appendChild(title);
      layers.slice(0, learned).forEach(function (L) {
        const g = L.slot ? equipment[L.slot] : null;
        const ok = !!(g && itemAffixUniform(g));
        const line = document.createElement('b');
        line.textContent = '· ' + L.name + '：' + (L.slot
          ? (g ? (ok ? ('生效（词缀统一：' + (itemAffixFamilyName(g) || '') + '，增幅 +' +
              Math.round((fengjiSlotMult(L.slot) - 1) * 100) + '%）') : '未生效（该件装备词缀不统一）')
            : '未生效（该部位没有装备）')
          : '全部位：达标装备增幅倍率翻倍');
        line.style.color = ok ? '#a9d8ff' : '#c9a3a3';
        box.appendChild(line);
      });
      tip.appendChild(box);
    } else if (art.id === 'lianxue' && learned > 0) {
      const box = document.createElement('div');
      box.className = 'tipSockets';
      const line = document.createElement('b');
      line.textContent = '· 当前状态：' + (lianxueActive() ? '生效（近战武器）' : '未生效（需要装备近战武器）');
      line.style.color = lianxueActive() ? '#a9d8ff' : '#c9a3a3';
      box.appendChild(line);
      tip.appendChild(box);
    }

    const wrapEl = ui.wrap || document.body;
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = anchorRect.right - wrapRect.left + 12;
    if (left + tw > wrapRect.width - 6) {
      left = anchorRect.left - wrapRect.left - tw - 12;
    }
    let top = anchorRect.top - wrapRect.top + anchorRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  /* ============================================================
   * 装备悬停提示：鼠标停在装备/背包物品上 → 小窗口显示该装备属性，移开即消失
   * （和技能提示同一套定位方式：贴在目标旁边，放不下就换到另一侧）
   * ============================================================ */
  function hideItemTip() {
    if (ui.itemTip) ui.itemTip.classList.add('hidden');
    if (ui.cmpTip) ui.cmpTip.classList.add('hidden');
  }

  /* 属性卡片的定位：优先放锚点右边，右边放不下换左边。
   * 返回的位置给"商店比对窗"继续往右/往左排用。 */
  function positionItemTip(tip, anchor) {
    const wrapEl = ui.wrap || document.getElementById('wrap');
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = aRect.right - wrapRect.left + 12;
    let placedRight = true;
    if (left + tw > wrapRect.width - 6) {
      left = aRect.left - wrapRect.left - tw - 12;
      placedRight = false;
    }
    let top = aRect.top - wrapRect.top + aRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
    return { left: left, top: top, placedRight: placedRight, wrapRect: wrapRect };
  }

  /* 世界频道里点"分享的物品名"→ 弹出这件物品的详细属性（位置算好的、点空白处收起） */
  function openSharedItemTip(anchor, p) {
    if (!ui.itemTip || !anchor || !p) return false;
    const tip = ui.itemTip;
    const item = {
      type: p.t || 'material',
      name: p.n,
      rarity: p.r || 'common',
      slot: p.s || null,
      level: p.l || 1,
      weaponType: p.w || null,
      baseDetail: p.b || '',
      detail: p.d || ''
    };
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    tip.textContent = '';
    tip.style.borderColor = rar.color || 'rgba(255, 214, 142, .5)';

    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = rar.color;
    const name = document.createElement('b');
    name.textContent = item.name;
    name.style.color = rar.color;
    const tier = document.createElement('span');
    tier.textContent = (rar.name || '') + ' · ' + (p.k || '物品');
    tier.style.color = '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(tier);
    tip.appendChild(head);

    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const lv = document.createElement('em');
    const eq = item.type === 'equipment';
    lv.textContent = (eq
      ? '装备等级 ' + item.level + (item.weaponType ? '　·　武器类型 ' + item.weaponType : '')
      : '来自世界频道的分享') + '　·　其它玩家分享';
    mods.appendChild(lv);
    const lines = eq ? itemTipLines(item) : (item.detail ? item.detail.split('　').filter(Boolean) : []);
    if (!lines.length) {
      const none = document.createElement('b');
      none.textContent = '（这件物品没有词缀）';
      mods.appendChild(none);
    } else {
      lines.forEach(function (t) {
        const line = document.createElement('b');
        line.textContent = '· ' + t;
        mods.appendChild(line);
      });
    }
    const hint = document.createElement('em');
    hint.textContent = '点击别处关闭';
    mods.appendChild(hint);
    tip.appendChild(mods);

    positionItemTip(tip, anchor);
    if (ui.cmpTip) ui.cmpTip.classList.add('hidden');
    return true;
  }

  function itemTipLines(item) {
    const lines = [];
    // 基底词缀（盾牌自带 +15% 格挡）排在最前面，和流放之路的写法一致
    const baseDetail = String(item.baseDetail || '').trim();
    if (baseDetail) {
      baseDetail.split('　').filter(Boolean).forEach(function (t) {
        lines.push('〔基底〕' + t);
      });
    }
    const detail = String(item.detail || '').trim();
    if (detail) detail.split('　').filter(Boolean).forEach(function (t) { lines.push(t); });
    // 可售卖的掉落物：直接把售价写在说明里（背包里点开就能看到）
    if (item.sellGold) lines.push('售价 ' + item.sellGold + ' 金币（背包里可直接出售）');
    return lines;
  }

  /* 商店对比用的属性顺序与中文名（只列数值型属性；
   * item[statKey] 已经是"基底词缀 + 随机词缀"汇总后的结果，直接比就行） */
  const CMP_STAT_ORDER = [
    'hp', 'mp', 'armor', 'armorPct', 'evasion', 'evasionPct', 'atk', 'speedPct', 'thorns', 'blockPct',
    'strength', 'agility', 'intelligence',
    'res', 'chaosRes', 'critReduce', 'lifeRegen', 'mpRegen',
    'atkSpeed', 'critChance', 'critDamage', 'extraArrowPct'
  ];
  const CMP_LABELS = {
    hp: '生命上限', mp: '灵力上限', armor: '护甲', armorPct: '防御力',
    evasion: '闪避', evasionPct: '闪避提高', atk: '攻击伤害',
    speedPct: '移动速度', thorns: '荆棘', blockPct: '格挡',
    strength: '力量', agility: '敏捷', intelligence: '智力',
    res: '元素抗性', chaosRes: '混沌抗性', critReduce: '暴伤降低',
    lifeRegen: '生命恢复', mpRegen: '灵力恢复',
    atkSpeed: '攻击速度', critChance: '暴击率', critDamage: '暴击伤害', extraArrowPct: '额外箭矢'
  };
  const CMP_PCT = {
    speedPct: 1, blockPct: 1, res: 1, chaosRes: 1, critReduce: 1,
    critChance: 1, critDamage: 1, armorPct: 1, evasionPct: 1, extraArrowPct: 1, atkSpeed: 1
  };

  function cmpValue(v) {
    return (v > 0 ? '+' : '') + v;
  }

  /* 「当前装备 vs 货架上这件」的对照表：渲染进独立的比对小窗（不再塞在属性卡片里） */
  function renderCompareTip(tip, item) {
    tip.textContent = '';
    const cur = item.slot ? equipment[item.slot] : null;
    const title = document.createElement('span');
    title.className = 'cmpTitle';
    const slotDef = SLOT_DEFS[item.slot];
    title.textContent = cur
      ? '与当前装备对比（' + (slotDef ? slotDef.label : '装备') + '：' + cur.name + '）'
      : '当前没有装备' + (slotDef ? slotDef.label : '装备') + '，买了就是净提升';
    tip.appendChild(title);

    const head = document.createElement('div');
    head.className = 'cmpRow cmpHead';
    ['属性', '当前', '该件', '差值'].forEach(function (t) {
      const s = document.createElement('span');
      s.textContent = t;
      head.appendChild(s);
    });
    tip.appendChild(head);

    let rows = 0;
    CMP_STAT_ORDER.forEach(function (key) {
      const a = cur ? (cur[key] || 0) : 0;
      const b = item[key] || 0;
      if (!a && !b) return;
      rows++;
      const pct = CMP_PCT[key] ? '%' : '';
      const row = document.createElement('div');
      row.className = 'cmpRow';

      const label = document.createElement('span');
      label.textContent = CMP_LABELS[key] || key;
      row.appendChild(label);

      const now = document.createElement('span');
      now.className = cur ? '' : 'cmpNone';
      now.textContent = cur ? (a + pct) : '—';
      row.appendChild(now);

      const next = document.createElement('span');
      next.textContent = b + pct;
      next.className = (!cur || b > a) ? 'cmpUp' : (b < a ? 'cmpDown' : 'cmpSame');
      row.appendChild(next);

      const diff = document.createElement('span');
      const d = b - a;
      diff.textContent = (!cur || a === 0) ? (b ? '+' + b + pct : '—') : cmpValue(d) + pct;
      diff.className = d > 0 ? 'cmpUp' : (d < 0 ? 'cmpDown' : 'cmpSame');
      row.appendChild(diff);

      tip.appendChild(row);
    });

    if (!rows) {
      const none = document.createElement('div');
      none.className = 'cmpRow cmpSame';
      none.textContent = '（两件都没有可对比的词缀）';
      tip.appendChild(none);
    }
  }

  function showItemTip(anchor, item, opts) {
    if (!ui.itemTip || !anchor || !item || item.type !== 'equipment') return;
    const tip = ui.itemTip;
    const wrapEl = ui.wrap || document.getElementById('wrap');
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    tip.textContent = '';
    tip.style.borderColor = item.rarityColor || rar.color || 'rgba(255, 214, 142, .5)';

    // 标题：品级 + 装备名
    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = item.rarityColor || rar.color;
    const name = document.createElement('b');
    name.textContent = item.name;
    name.style.color = item.rarityColor || '#ffe6bb';
    const tier = document.createElement('span');
    const slotDef = SLOT_DEFS[item.slot];
    tier.textContent = (rar.rarityName || rar.name) + ' · ' + (slotDef ? slotDef.label : '装备');
    tier.style.color = item.rarityColor || '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(tier);
    tip.appendChild(head);

    // 数值区
    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const lvLine = document.createElement('em');
    lvLine.textContent = '装备等级 ' + (item.level || 1) +
      '　·　售价 ' + sellPrice(item) + ' 金币' +
      (item.weaponType ? '　·　武器类型 ' + item.weaponType : '') +
      (item.reforgeCount ? '　·　已洗练 ' + item.reforgeCount + ' 次' : '');
    mods.appendChild(lvLine);

    const lines = itemTipLines(item);
    if (!lines.length) {
      const empty = document.createElement('b');
      empty.textContent = '（这件装备没有词缀）';
      mods.appendChild(empty);
    } else {
      lines.forEach(function (t) {
        const line = document.createElement('b');
        line.textContent = '· ' + t;
        mods.appendChild(line);
      });
    }
    // 已装备的顺带提示怎么卸下 / 洗练
    if (item.slot && equipment[item.slot] === item) {
      const hint = document.createElement('em');
      hint.textContent = '左键卸下　·　右上角按钮洗练';
      mods.appendChild(hint);
    }
    tip.appendChild(mods);

    const pos = positionItemTip(tip, anchor);
    const wrapRect = wrapEl.getBoundingClientRect();
    const left = pos.left;
    const top = pos.top;
    const tw = tip.offsetWidth;
    const placedRight = pos.placedRight;

    /* 商店悬停：比对属性单独放一个小窗，贴在"物品右侧 / 属性卡片外侧"，
     * 不跟属性卡片混在一起，鼠标停在哪件就看得清哪件。 */
    const cmp = ui.cmpTip;
    if (cmp && opts && opts.compare) {
      renderCompareTip(cmp, item);
      cmp.classList.remove('hidden');
      cmp.style.left = '0px';
      cmp.style.top = '0px';
      const cw = cmp.offsetWidth;
      const ch = cmp.offsetHeight;
      // 属性卡片在物品右侧 → 比对窗继续往右；属性卡片被挤到左侧 → 比对窗继续往左
      let cleft = placedRight ? (left + tw + 10) : (left - cw - 10);
      if (cleft < 6 || cleft + cw > wrapRect.width - 6) {
        cleft = placedRight ? (left - cw - 10) : (left + tw + 10);
      }
      cleft = Math.max(6, Math.min(cleft, wrapRect.width - cw - 6));
      const ctop = Math.max(6, Math.min(top, wrapRect.height - ch - 6));
      cmp.style.left = Math.round(cleft) + 'px';
      cmp.style.top = Math.round(ctop) + 'px';
    } else if (cmp) {
      cmp.classList.add('hidden');
    }
  }

  // 给一个元素挂上"悬停显示装备属性"
  function bindItemTip(el, itemGetter) {
    if (!el) return el;
    el.addEventListener('mouseenter', function () {
      const item = typeof itemGetter === 'function' ? itemGetter() : itemGetter;
      if (item && item.type === 'equipment') showItemTip(el, item);
    });
    el.addEventListener('mouseleave', hideItemTip);
    return el;
  }

  function showSkillTip(card, skill) {
    if (!ui.skillTip || !skill || !card) return;
    const tip = ui.skillTip;
    tip.textContent = '';

    // 标题行：技能名 + 等级 + 类型
    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = '#ffd08a';
    const name = document.createElement('b');
    name.textContent = skill.name + (skill.level ? ' Lv.' + skill.level : '');
    const type = document.createElement('span');
    type.textContent = SKILL_TYPE_LABELS[skill.type] || '技能';
    type.style.color = '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(type);
    tip.appendChild(head);

    // 数值行：灵力消耗 / 基础伤害 / 灵力占用
    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const costLine = document.createElement('b');
    costLine.textContent = skill.id === 'summonPet'
      ? '灵力消耗 0（法杖被动）'
      : '灵力消耗 ' + skill.cost;
    mods.appendChild(costLine);
    if (skill.dmg) {
      const dmgLine = document.createElement('b');
      dmgLine.textContent = skill.id === 'slash'
        ? '武器攻击力的 75%（' + SLASH_DOUBLE_PCT + '% 几率双倍伤害）'
        : skill.id === 'yangClaw'
        ? '每次造成攻击力的 ' + (typeof skill.atkPct === 'number' ? skill.atkPct : 15) +
          '%（连续 6 次，逐次独立结算）'
        : '基础伤害 ' + skill.dmg[0] + '~' + skill.dmg[1];
      mods.appendChild(dmgLine);
    }
    if (skill.type === 'summon') {
      const reserveLine = document.createElement('b');
      reserveLine.textContent = '召唤技能：占用 50% 灵力上限';
      mods.appendChild(reserveLine);
    } else if (skill.type === 'toggle') {
      const reserveLine = document.createElement('b');
      reserveLine.textContent = '持续技能：占用 35% 灵力上限';
      mods.appendChild(reserveLine);
    }
    if (skill.level > 1) {
      const lvLine = document.createElement('b');
      lvLine.textContent = '等级加成 +' + levelBonusPct(skill) + '%' +
        (skill.id === 'slash' || skill.id === 'yangClaw' ? '（每级 +10% 基础伤害）' : '');
      mods.appendChild(lvLine);
    }
    tip.appendChild(mods);

    // 完整描述（卡片里被截断的部分在这里看全）
    const desc = document.createElement('div');
    desc.className = 'tipDesc';
    desc.textContent = skill.desc || '';
    tip.appendChild(desc);

    // 已镶嵌的技能石 + 槽位情况
    const socketList = skill.sockets || [];
    const slots = socketSlotsOf(skill);
    const box = document.createElement('div');
    box.className = 'tipSockets';
    const title = document.createElement('em');
    title.textContent = '镶孔 ' + socketList.length + ' / ' + slots +
      (slots < SOCKET_SLOTS_MAX ? '（每个 +500 金币开槽，上限 ' + SOCKET_SLOTS_MAX + '）' : ' · 已开满');
    box.appendChild(title);
    if (socketList.length) {
      socketList.forEach(function (gem) {
        const line = document.createElement('b');
        line.textContent = '· ' + gem.name + '：' + (gem.detail || '');
        box.appendChild(line);
      });
    } else {
      const line = document.createElement('b');
      line.textContent = '· 还没有镶嵌技能石';
      box.appendChild(line);
    }
    tip.appendChild(box);

    // 先显示再量尺寸，位置以 #wrap 为基准（保证不被窗口 / 屏幕裁掉）
    const wrapEl = ui.wrap || document.body;
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    // 法术窗口贴右侧边缘，所以提示优先放在卡片左侧，左侧放不下再放右侧
    let left = cardRect.left - wrapRect.left - tw - 12;
    if (left < 6) left = cardRect.right - wrapRect.left + 12;
    let top = cardRect.top - wrapRect.top + cardRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  function renderSpellList() {
    ui.spellList.textContent = '';
    hideSkillTip();
    // 非战斗状态：提示只能查看，不能施法
    const canCastNow = gameState === 'battle' && !!activeEnemy;
    ui.spellHint.classList.toggle('hidden', canCastNow);
    // 自动冒险的优先技能提示
    if (ui.spellActiveHint) {
      const fav = autoSkillId
        ? knownSkills.filter(function (s) { return s.id === autoSkillId; })[0] : null;
      ui.spellActiveHint.textContent = fav
        ? '★ 已激活「' + fav.name + '」：自动冒险时会优先使用它（用不了时退回劈砍 / 普通攻击）'
        : '点击技能格右上角的「☆ 激活」，自动冒险时会优先使用这个技能（同时只能激活一个）';
      ui.spellActiveHint.classList.toggle('on', !!fav);
    }
    // 固定 2 行 × 3 列槽位，空槽位以虚线圈出，方便看到新技能将出现的位置
    const totalSlots = Math.max(6, knownSkills.length);
    for (let i = 0; i < totalSlots; i++) {
      const skill = knownSkills[i];
      if (!skill) {
        const empty = document.createElement('div');
        empty.className = 'spellCard slot';
        ui.spellList.appendChild(empty);
        continue;
      }
      const isFav = autoSkillId === skill.id;
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'spellCard' + (isFav ? ' favored' : '');

      // 右上角「激活」按钮：自动冒险时优先使用这个技能
      const favBtn = document.createElement('span');
      favBtn.className = 'skillActiveBtn' + (isFav ? ' on' : '');
      favBtn.textContent = isFav ? '★ 已激活' : '☆ 激活';
      favBtn.title = isFav
        ? '已激活：自动冒险会优先使用它（点击取消）'
        : '激活后，自动冒险时会优先使用这个技能';
      favBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleAutoSkill(skill);
      });
      card.appendChild(favBtn);

      /* 右上角第二颗按钮：世界BOSS 时装充能（⚡）
       *   - 充能进度满 100% 时，可以挑一个技能给它充能（伤害 +500%~1000%）
       *   - 已经充能过的技能显示「⚡ ×N%」，下一次施放生效后被消耗 */
      if (hasBossCostume()) {
        const charged = costumeChargeActive && costumeChargeActive.skillId === skill.id;
        const full = costumeCharge >= COSTUME_CHARGE_FULL;
        if (charged || full) {
          const chgBtn = document.createElement('span');
          chgBtn.className = 'skillChargeBtn' + (charged ? ' on' : '') + (full ? ' ready' : '');
          chgBtn.textContent = charged
            ? ('⚡ +' + costumeChargeActive.pct + '%')
            : ('⚡ 充能 ' + Math.round(costumeCharge) + '%');
          chgBtn.title = charged
            ? '已充能：下一次施放「' + skill.name + '」伤害 +' + costumeChargeActive.pct + '%'
            : '时装充能已满：点这里给「' + skill.name + '」充能（伤害 +' +
              COSTUME_CHARGE_MIN_PCT + '%~' + COSTUME_CHARGE_MAX_PCT + '%）';
          chgBtn.addEventListener('click', function (e) {
            e.stopPropagation();
            chargeSkillWithCostume(skill);
          });
          card.appendChild(chgBtn);
        }
      }

      const info = document.createElement('div');
      info.className = 'spellInfo';
      const name = document.createElement('div');
      name.className = 'spellName';
      name.textContent = skill.name + ' Lv.' + (skill.level || 1);
      const desc = document.createElement('div');
      desc.className = 'spellDesc';
      desc.textContent = skill.desc +
        (skill.level > 1 ? '（等级加成 +' + levelBonusPct(skill) + '%）' : '');
      info.appendChild(name);
      info.appendChild(desc);

      const sockets = document.createElement('div');
      sockets.className = 'spellSockets';
      const socketList = skill.sockets || (skill.sockets = []);
      const slots = socketSlotsOf(skill);
      socketList.forEach(function (gem) {
        const chip = document.createElement('span');
        chip.className = 'gemChip';
        chip.textContent = gem.detail;
        const gInBattle = (gameState === 'battle');
        chip.className = 'gemChip' + (gInBattle ? ' locked' : '');
        chip.title = gInBattle
          ? '战斗中不能取下技能石（点这一格＝直接施放这个技能）'
          : '左键点击可将技能石取回背包';
        chip.addEventListener('click', function (e) {
          // 战斗中：不拦事件 → 让它冒泡到技能卡，直接施放（不再误卸辅助石）
          if (gameState === 'battle') return;
          e.stopPropagation();
          unsocketGem(skill, gem);
        });
        sockets.appendChild(chip);
      });
      for (let s = socketList.length; s < slots; s++) {
        const empty = document.createElement('span');
        empty.className = 'gemChip empty';
        empty.textContent = '◇';
        empty.title = '空镶孔（把技能石镶嵌进来即可生效）';
        sockets.appendChild(empty);
      }
      // 还没开满 6 个槽：给一个花 500 金币开槽的小按钮
      if (slots < SOCKET_SLOTS_MAX) {
        const buy = document.createElement('span');
        buy.className = 'gemChip slotBuy';
        buy.textContent = '+500';
        buy.title = '花费 ' + SOCKET_SLOT_COST + ' 金币，给「' + skill.name +
          '」增加 1 个镶嵌槽（当前 ' + slots + ' / ' + SOCKET_SLOTS_MAX + '）';
        buy.addEventListener('click', function (e) {
          e.stopPropagation();
          buySocketSlot(skill);
        });
        sockets.appendChild(buy);
      }

      const meta = document.createElement('div');
      meta.className = 'spellMeta';
      meta.textContent = (SKILL_TYPE_LABELS[skill.type] || '技能').replace('技能', '') +
        ' · 灵力 ' + skill.cost;

      card.appendChild(info);
      card.appendChild(meta);
      card.appendChild(sockets);
      // 持续型技能显示当前开关状态
      if (skill.type === 'toggle') {
        const state = document.createElement('div');
        state.className = 'spellMeta';
        const tOn = skill.id === 'shieldWall' ? shieldWall : bloodRebound;
        state.textContent = tOn ? '● 已激活（持续生效）' : '○ 未激活（点击开启）';
        state.style.color = tOn ? '#7dffa8' : '#c9b391';
        card.appendChild(state);
      }
      card.addEventListener('click', function () {
        hideSkillTip();
        if (gameState !== 'battle' || !activeEnemy) {
          // 非战斗状态：窗口保留，只提示不能施法
          showToast('路上没有敌人，法术只能在战斗中使用。');
          return;
        }
        closeSpellPanel();
        castSkill(skill);
      });
      // 鼠标停在技能格上：显示完整描述与镶嵌的技能石，移开即关闭
      card.addEventListener('mouseenter', function () { showSkillTip(card, skill); });
      card.addEventListener('mouseleave', hideSkillTip);
      ui.spellList.appendChild(card);
    }
  }

  // 战斗中使用已学会的技能
  /* ============================================================
   * 「强力射击」：蓄力 + 完美时机（参考流放之路的 Perfect Timing）
   *   ① 第一次施放：本回合不出手，开始蓄力；期间承受的敌人伤害提高 15%
   *   ② 战斗场景中央出现进度条，标记左右循环滚动
   *      按「空格键」（或点击进度条）让标记停在完美时机区间内 → 触发完美时机
   *   ③ 下一回合自动射出强力一击：150% 伤害
   *      完美时机：箭矢命中时爆炸，对所有敌人额外造成 225% 伤害，并晕眩敌人一回合
   * ============================================================ */
  const POWER_SHOT_MULT = 1.5;           // 强力一击：150%
  const POWER_SHOT_PERFECT_MULT = 2.25;  // 完美时机：额外 225%
  const POWER_SHOT_VULN_PCT = 15;        // 蓄力期间承受的伤害提高 15%
  const POWER_SHOT_STUN_TURNS = 1;       // 完美时机：晕眩敌人 1 回合
  const POWER_TIMING_LIFE = 2.6;         // 进度条存活时间（秒）
  const POWER_TIMING_SPEED = 1.4;        // 标记移动速度（每秒走过的比例，来回算一趟）
  const POWER_TIMING_ZONE = [0.60, 0.80]; // 完美时机区间（占进度条 0~1 的比例）

  let powerCharge = null;   // 蓄力状态：{ perfect, timing: { pos, dir, t, done, result } }
  let powerArrowFx = null;  // 箭矢飞行 + 命中爆炸特效

  function powerShotSkill() { return skillById('powerShot'); }
  function powerCharging() { return !!powerCharge; }
  /* 「强力射击」的武器限制：只有装备弓类武器（长弓）才能放。
   *   其它类型武器 / 空手 → 提示"装备类型错误"，不扣灵力也不占回合。 */
  const POWER_SHOT_WEAPON = '长弓';
  function weaponTypeEquipped() { return (equipment.weapon && equipment.weapon.weaponType) || ''; }
  function powerShotWeaponOk() { return weaponTypeEquipped() === POWER_SHOT_WEAPON; }
  function powerTimingActive() {
    return !!(powerCharge && powerCharge.timing && !powerCharge.timing.done);
  }
  // 蓄力期间承受的伤害倍率（1.15）
  function powerVulnMult() {
    return powerCharging() ? 1 + POWER_SHOT_VULN_PCT / 100 : 1;
  }

  function startPowerCharge() {
    powerCharge = {
      perfect: false,
      timing: { pos: 0, dir: 1, t: 0, done: false, result: '' }
    };
  }

  // 按空格（或点进度条）：判定标记是否落在完美时机区间
  function pressPerfectTiming() {
    const timing = powerCharge && powerCharge.timing;
    if (!timing || timing.done) return null;
    timing.done = true;
    const inZone = timing.pos >= POWER_TIMING_ZONE[0] && timing.pos <= POWER_TIMING_ZONE[1];
    timing.result = inZone ? 'perfect' : 'miss';
    powerCharge.perfect = inZone;
    addTextPopup(W / 2 - 46, 176, inZone ? '完美时机！' : '时机不对', inZone ? '#ffd76a' : '#a9b8c6');
    pushBattleLog(inZone
      ? '完美时机触发：箭矢命中时将爆炸，对所有敌人额外造成 ' +
        Math.round(POWER_SHOT_PERFECT_MULT * 100) + '% 伤害，并晕眩一回合'
      : '完美时机失败（标记没有停在完美区间内）', inZone ? 'reward' : 'system');
    return inZone;
  }

  // 完美时机进度条的位置（战斗场景居中）
  function powerTimingRect() {
    const w = 460;
    const h = 26;
    return { x: (W - w) / 2, y: 232, w: w, h: h };
  }

  function drawPowerTimingBar() {
    if (gameState !== 'battle' || !powerCharge || !powerCharge.timing) return;
    const timing = powerCharge.timing;
    const r = powerTimingRect();
    ctx.save();
    // 背板
    ctx.fillStyle = 'rgba(20,13,8,.8)';
    ctx.strokeStyle = 'rgba(255,214,142,.5)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    roundRect(r.x - 16, r.y - 46, r.w + 32, r.h + 72, 14);
    ctx.fill();
    ctx.stroke();
    // 标题
    ctx.fillStyle = '#ffd76a';
    ctx.font = 'bold 20px "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('完美时机 · 按空格键', W / 2, r.y - 18);
    // 槽底
    ctx.fillStyle = 'rgba(255,255,255,.12)';
    ctx.fillRect(r.x, r.y, r.w, r.h);
    // 完美区间
    const zx = r.x + r.w * POWER_TIMING_ZONE[0];
    const zw = r.w * (POWER_TIMING_ZONE[1] - POWER_TIMING_ZONE[0]);
    const grad = ctx.createLinearGradient(zx, 0, zx + zw, 0);
    grad.addColorStop(0, 'rgba(255,180,70,.55)');
    grad.addColorStop(0.5, 'rgba(255,238,150,.95)');
    grad.addColorStop(1, 'rgba(255,180,70,.55)');
    ctx.fillStyle = grad;
    ctx.fillRect(zx, r.y, zw, r.h);
    ctx.strokeStyle = '#ffeaa8';
    ctx.lineWidth = 2;
    ctx.strokeRect(zx, r.y, zw, r.h);
    // 滑动标记
    const mx = r.x + r.w * clamp(timing.pos, 0, 1);
    ctx.fillStyle = timing.done
      ? (timing.result === 'perfect' ? '#7dff9b' : '#ff6a5a')
      : '#ffffff';
    ctx.beginPath();
    ctx.moveTo(mx, r.y - 5);
    ctx.lineTo(mx - 9, r.y - 20);
    ctx.lineTo(mx + 9, r.y - 20);
    ctx.closePath();
    ctx.fill();
    ctx.fillRect(mx - 1.5, r.y, 3, r.h);
    // 结果文字
    if (timing.done) {
      ctx.fillStyle = timing.result === 'perfect' ? '#7dff9b' : '#ff8f8f';
      ctx.font = 'bold 18px "Segoe UI", sans-serif';
      ctx.fillText(timing.result === 'perfect' ? '完美！' : '失败', W / 2, r.y + r.h + 26);
    }
    ctx.restore();
  }

  /* 「强力射击」法术特效：
   *   飞行形态 = Wispstrike flight_1~18 素材（从角色胸前平射，命中敌人同一 Y 轴）
   *   命中形态 = Wispstrike impact 素材（完美时机命中后播放） */
  function wispFlightReady() {
    for (let i = 1; i <= WISP_FLIGHT_FRAMES; i++) {
      const im = images['wisp_flight_' + i];
      if (!im || !im.complete || im.naturalWidth <= 0) return false;
    }
    return true;
  }
  function wispImpactReady() {
    for (let i = 1; i <= WISP_IMPACT_FRAMES; i++) {
      const im = images['wisp_impact_' + i];
      if (!im || !im.complete || im.naturalWidth <= 0) return false;
    }
    return true;
  }

  function startPowerArrowFx(target, perfect) {
    /* 飞行形态：**平射**——起点是角色胸前，终点是敌人胸前的**同一 Y 轴**
     *   （两个立绘同尺寸同基线，所以玩家胸前 = 敌人胸前；全程水平直线，不带弧线）。
     *   多重投射物（辅助技能石）：箭数 = 基础 1 + 2 支，后一支**尾随**前一支飞出去
     *   （出发间隔 POWER_VOLLEY_GAP），三支都打在胸前那条线上；每支箭单独结算伤害。 */
    const id = 'powerShot';
    const count = projectileCountOf(id, POWER_SHOT_BASE_PROJ);
    const speedMult = projectileSpeedMult(id);
    const gap = Math.max(0, POWER_VOLLEY_GAP / skillSpeedMult(id));
    const chestY = GROUND_Y - POWER_ARROW_LIFT;
    const sx = player.worldX - cameraX() + 52;
    const ex = target.x - cameraX() + POWER_HIT_OFFSET_X;
    /* 铁箍战弓：箭矢穿透所有目标，并且归返 —— 去程一直飞到战场右边界，再原路飞回角色身前 */
    const pierceReturn = !!(derived.unique && derived.unique.pierceReturn);
    const flightEnd = pierceReturn ? (W + 60) : ex;
    const arrows = [];
    for (let i = 0; i < count; i++) {
      arrows.push({
        t: -i * gap,                          // 负数＝还在"尾随"的间隔里，尚未出场
        dur: POWER_FLY_DUR / speedMult,       // 「投射物加速」→ 飞得更快
        sx: sx, sy: chestY,
        ex: flightEnd, ey: chestY,            // 起止同 Y：三支箭全在胸前那条线上
        dmg: 0                                // 命中后填上这一支单独结算的伤害
      });
    }
    const bodyY = enemyBodyCenterY(target);
    powerArrowFx = {
      arrows: arrows,
      ex: ex,
      ey: bodyY,                             // 命中形态的中心（身体中部，保持原样未改）
      perfect: !!perfect,
      pierceReturn: pierceReturn,
      boom: -1
    };
  }

  /* 敌人身体中部（命中点的高度）
   *   实测敌人素材（199×199，脚底基线距底 12px）的可见身体范围约 y=10~190，
   *   正中心在 y≈104 → 换算到画面：帧顶 = GROUND_Y + 4 - 187 = GROUND_Y - 183
   *   于是身体中部 ≈ GROUND_Y - 79（按体型缩放）。
   *   之前固定用 GROUND_Y - 128，正好落在头顶上方，所以看起来"打在头上"。 */
  function enemyBodyCenterY(enemy) {
    const sc = (enemyDrawScale(enemy) || 1) * caveScale();
    return GROUND_Y - 79 * sc;
  }

  function updatePowerArrowFx(dt) {
    const f = powerArrowFx;
    if (!f) return;
    const span = f.pierceReturn ? 2 : 1;      // 穿透+归返 = 去程 + 回程
    let flying = false;
    f.arrows.forEach(function (a) {
      a.t += dt;
      if (a.t < a.dur * span) flying = true;  // 还没飞完（t<0 也算"在场"）
    });
    if (!flying && f.boom < 0) f.boom = 0;
    if (f.boom >= 0) {
      f.boom += dt;
      if (f.boom > (f.perfect ? POWER_IMPACT_DUR : POWER_PUFF_DUR)) powerArrowFx = null;
    }
  }

  /* 箭矢本体的画法：箭杆 + 三角箭头 + 尾部箭羽 + 光晕 + 速度线
   *   dir=1 朝右、-1 朝左（镜像）。big=多重投射物多出来的那几支，稍微小一点做层次。 */
  function drawPowerArrow(x, y, dir, perfect, big) {
    const sc = caveScale() * (big ? 0.86 : 1);
    const L = POWER_ARROW_LEN * sc;
    const d = dir < 0 ? -1 : 1;
    const line = perfect ? '#ffe6a2' : '#eaf8ff';
    const glowRGB = perfect ? '255,205,110' : '150,225,255';
    ctx.save();
    ctx.translate(x, y);
    if (d < 0) ctx.scale(-1, 1);
    // 1) 光晕：压成"顺着箭杆的细长光带"（不能用圆光团，否则又变回飞镖/光球）
    ctx.globalCompositeOperation = 'lighter';
    ctx.save();
    ctx.scale(1.6, 0.42);
    const halo = ctx.createRadialGradient(0, 0, 0, 0, 0, L * 0.5);
    halo.addColorStop(0, 'rgba(' + glowRGB + ',.34)');
    halo.addColorStop(0.5, 'rgba(' + glowRGB + ',.12)');
    halo.addColorStop(1, 'rgba(' + glowRGB + ',0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(0, 0, L * 0.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.restore();
    // 2) 箭杆 / 箭头 / 箭羽
    ctx.save();
    ctx.translate(x, y);
    if (d < 0) ctx.scale(-1, 1);
    const tail = -L * 0.95;
    const tip = L * 0.5;
    const hl = L * 0.26;                     // 箭头长度
    const hw = Math.max(3.2, 5.4 * sc);      // 箭头半宽
    ctx.lineCap = 'round';
    ctx.strokeStyle = line;
    ctx.fillStyle = line;
    ctx.lineWidth = Math.max(2, 3.2 * sc);
    ctx.beginPath();
    ctx.moveTo(tail, 0);
    ctx.lineTo(tip - hl * 0.7, 0);
    ctx.stroke();
    ctx.beginPath();                         // 三角箭头
    ctx.moveTo(tip, 0);
    ctx.lineTo(tip - hl, -hw);
    ctx.lineTo(tip - hl * 0.72, 0);
    ctx.lineTo(tip - hl, hw);
    ctx.closePath();
    ctx.fill();
    ctx.lineWidth = Math.max(1.6, 2.6 * sc);
    ctx.beginPath();                         // 尾部箭羽（上下两片）
    ctx.moveTo(tail, 0);
    ctx.lineTo(tail + L * 0.28, -hw * 1.15);
    ctx.moveTo(tail, 0);
    ctx.lineTo(tail + L * 0.28, hw * 1.15);
    ctx.stroke();
    // 3) 速度线：箭尾向后拖出几条残影，强调"射出去"而不是"扔出去"
    ctx.globalAlpha = 0.45;
    ctx.lineWidth = Math.max(1.2, 2 * sc);
    for (let i = 0; i < 3; i++) {
      const off = (i - 1) * hw * 0.9;
      ctx.beginPath();
      ctx.moveTo(tail - L * (0.16 + i * 0.1), off * 0.6);
      ctx.lineTo(tail - L * (0.5 + i * 0.22), off * 0.6);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawPowerArrowFx() {
    const f = powerArrowFx;
    if (!f) return;
    const src = f.arrows[0];
    /* 飞行形态：Wispstrike flight 素材，沿着"胸前那条线"水平平射（起止同 Y，没有弧线）。
     * 多重投射物时 arrows 里有多支，后面几支只是出发更晚——视觉上排成一串尾随飞行。 */
    f.arrows.forEach(function (a, i) {
      const total = f.pierceReturn ? a.dur * 2 : a.dur;
      if (a.t < 0 || a.t > total) return;        // 还没出场 / 已经命中（或已经飞回手里）
      /* 铁箍战弓：后半程是"归返"——从战场右边界沿原路飞回角色身前 */
      const back = f.pierceReturn && a.t > a.dur;
      const p = clamp((back ? a.t - a.dur : a.t) / a.dur, 0, 1);
      const ease = p * p * (3 - 2 * p);
      const fromX = back ? a.ex : a.sx;
      const toX = back ? a.sx : a.ex;
      const dir = toX < fromX ? -1 : 1;
      const x = fromX + (toX - fromX) * ease;
      const y = a.sy;                            // 全程同一 Y（水平直线）
      // 拖尾：箭尾向后拉出一条淡光带
      const tx = x - (toX - fromX) * 0.16 * dir;
      ctx.save();
      ctx.globalAlpha = 0.38;
      const tg = ctx.createLinearGradient(tx, y, x, y);
      tg.addColorStop(0, f.perfect ? 'rgba(255,214,106,0)' : 'rgba(190,240,255,0)');
      tg.addColorStop(1, f.perfect ? 'rgba(255,214,106,.8)' : 'rgba(190,240,255,.65)');
      ctx.strokeStyle = tg;
      ctx.lineWidth = 5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(tx, y);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.restore();
      // 飞行体：Wispstrike flight（18 帧按飞行进度播完一遍，命中即消失）
      let drew = false;
      if (wispFlightReady()) {
        const idx = Math.min(WISP_FLIGHT_FRAMES - 1, Math.floor(p * WISP_FLIGHT_FRAMES));
        const img = images['wisp_flight_' + (idx + 1)];
        if (img) {
          const size = WISP_BOLT_SIZE * caveScale();
          // 素材亮点核比贴图中心高 12px → 贴图往下压，视觉上的飞行体才压在这条线上
          const cy = y + POWER_FLIGHT_BIAS * caveScale();
          ctx.save();
          ctx.imageSmoothingEnabled = true;      // 法术素材是高清光效，保留平滑
          ctx.globalAlpha = playerFx.alpha;
          if (dir < 0) {                          // 往左飞就镜像
            ctx.translate(x, cy);
            ctx.scale(-1, 1);
            ctx.drawImage(img, -size / 2, -size / 2, size, size);
          } else {
            ctx.drawImage(img, x - size / 2, cy - size / 2, size, size);
          }
          ctx.restore();
          drew = true;
        }
      }
      if (!drew) {                                 // 素材没加载出来才用兜底手绘箭
        ctx.save();
        ctx.globalAlpha = playerFx.alpha;
        drawPowerArrow(x, y, dir, f.perfect, i > 0);
        ctx.restore();
      }
      if (fxTrace.length < 600) {
        const size = WISP_BOLT_SIZE * caveScale();
        const cy = y + POWER_FLIGHT_BIAS * caveScale();
        fxTrace.push({ kind: 'powerFlight', t: Math.round(a.t * 1000), idx: i,
          x: Math.round(x - size / 2), y: Math.round(cy - size / 2), size: Math.round(size),
          centerX: Math.round(x), centerY: Math.round(cy), lineY: Math.round(y) });
      }
    });
    if (f.boom >= 0) {
      if (f.perfect) {
        /* 完美时机：命中后触发新的技能形态（impact 19 帧） */
        const bp = clamp(f.boom / POWER_IMPACT_DUR, 0, 1);
        let drewImpact = false;
        if (wispImpactReady()) {
          const idx = Math.min(WISP_IMPACT_FRAMES - 1, Math.floor(bp * WISP_IMPACT_FRAMES));
          const img = images['wisp_impact_' + (idx + 1)];
          if (img) {
            const size = WISP_IMPACT_SIZE * caveScale();
            ctx.save();
            ctx.imageSmoothingEnabled = true;
            ctx.globalCompositeOperation = 'lighter';
            ctx.globalAlpha = playerFx.alpha * Math.min(1, (1 - bp) * 2.2);
            ctx.drawImage(img, f.ex - size / 2, f.ey - size / 2, size, size);
            ctx.restore();
            drewImpact = true;
            if (fxTrace.length < 600) {
              fxTrace.push({ kind: 'powerImpact', t: Math.round(f.boom * 1000), frame: idx + 1,
                x: Math.round(f.ex - size / 2), y: Math.round(f.ey - size / 2), size: Math.round(size),
                centerX: Math.round(f.ex), centerY: Math.round(f.ey) });
            }
          }
        }
        // 命中形态上再叠一圈扩散光环（素材没加载出来时也有反馈）
        drawBoomFx(f.ex, f.ey, bp, true);
        void drewImpact;
      } else {
        // 没触发完美时机：只有一小圈气浪
        const bp = clamp(f.boom / POWER_PUFF_DUR, 0, 1);
        drawBoomFx(f.ex, f.ey, bp, false);
      }
    }
  }

  // 命中爆炸：一圈扩散的火环 + 中心闪光（完美时机更大更亮）
  function drawBoomFx(x, y, p, big) {
    const maxR = big ? 150 : 76;
    const radius = maxR * (1 - Math.pow(1 - p, 3));
    const alpha = 1 - p;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const grad = ctx.createRadialGradient(x, y, 0, x, y, Math.max(1, radius));
    grad.addColorStop(0, 'rgba(255,255,236,' + (alpha * 0.95) + ')');
    grad.addColorStop(0.45, 'rgba(255,196,92,' + (alpha * 0.55) + ')');
    grad.addColorStop(1, 'rgba(255,110,40,0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1, radius), 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = big ? 'rgba(255,226,150,' + alpha + ')' : 'rgba(255,206,140,' + alpha + ')';
    ctx.lineWidth = big ? 6 : 3;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(1, radius * 0.92), 0, Math.PI * 2);
    ctx.stroke();
    if (big) {
      // 溅射尖刺
      ctx.strokeStyle = 'rgba(255,178,77,' + (alpha * 0.9) + ')';
      ctx.lineWidth = 3;
      for (let i = 0; i < 10; i++) {
        const a = (Math.PI * 2 * i) / 10 + p * 0.4;
        const r1 = radius * 0.7;
        const r2 = radius * (1.02 + 0.16 * Math.sin(i));
        ctx.beginPath();
        ctx.moveTo(x + Math.cos(a) * r1, y + Math.sin(a) * r1 * 0.7);
        ctx.lineTo(x + Math.cos(a) * r2, y + Math.sin(a) * r2 * 0.7);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // 每帧推进：滚动标记 → 回到玩家回合后自动放箭
  function updatePowerShot(dt) {
    if (powerCharge && powerCharge.timing && !powerCharge.timing.done) {
      const t = powerCharge.timing;
      t.t += dt;
      t.pos += t.dir * POWER_TIMING_SPEED * dt;
      if (t.pos >= 1) { t.pos = 1; t.dir = -1; }
      else if (t.pos <= 0) { t.pos = 0; t.dir = 1; }
      if (t.t >= POWER_TIMING_LIFE) {
        t.done = true;
        t.result = 'miss';
        powerCharge.perfect = false;
        pushBattleLog('完美时机窗口结束（没有按空格），这一箭按普通威力射出', 'system');
      }
    }
    if (!powerCharge) return;
    // 战斗已经结束/非战斗：清掉蓄力状态，别让它带到下一场
    if (gameState !== 'battle' || !activeEnemy) { powerCharge = null; return; }
    if (pendingVictory > 0) { powerCharge = null; return; }
    if (battleTurn !== 'player') return;
    if (!powerCharge.timing || !powerCharge.timing.done) return;
    firePowerShot();
  }

  function firePowerShot() {
    const skill = powerShotSkill();
    const perfect = !!(powerCharge && powerCharge.perfect);
    powerCharge = null;
    if (!skill || !activeEnemy || activeEnemy.hp <= 0) return;

    const mult = (1 + skillGemSum(skill.id, ['weaponDamage', 'meleeDamage', 'spellDamage']) / 100) *
      skillDamageLevelMult(skill);
    const main = activeEnemy;

    playPlayerAttack();
    startPowerArrowFx(main, perfect);
    const arrows = powerArrowFx ? powerArrowFx.arrows : [];

    /* 每一支箭**单独结算**：逐支掷伤害、逐支扣血、逐支飘字。
     *   多重投射物时三支箭是"尾随"命中的，所以飘字按出发间隔延迟出现，
     *   视觉上和箭矢抵达的时机对得上（伤害依然在放箭这一帧一次性结算，
     *   不然会打乱回合与胜负判定）。 */
    const parts = [];
    let totalDmg = 0;
    let firstCrit = false;
    /* 铁箍战弓：箭矢穿透所有目标并且归返 —— 去程穿透全场、回程再打一遍 */
    const pierce = !!(derived.unique && derived.unique.pierceReturn);
    const landArrow = function (a, enemy, tag, delay) {
      const base = Math.max(1, Math.round(
        (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor((enemy.level || 1) / 3)) *
        mult * skillDamageMult(skill.id, enemy) * POWER_SHOT_MULT
      ));
      const hit = rollPlayerDamage(base, enemy);
      if (a) a.dmg = hit.dmg;
      totalDmg += hit.dmg;
      if (hit.crit && !firstCrit) firstCrit = true;
      enemy.hp = Math.max(0, enemy.hp - hit.dmg);
      gloveOnHit(enemy, false, tag.indexOf('归返') === 0 ? '归返' : '穿透');
      enemy.hurtT = HURT_TIME;
      caveReflectOnPlayerHit(enemy, hit.dmg);
      addDamagePopup(enemy.x - cameraX() + 88, GROUND_Y - 156, hit.dmg,
        hit.crit ? '#ff4d4d' : '#ffd76a', false, delay || 0);
      maybeApplyBurn(enemy, skill.id);
      parts.push(tag + enemyLabel(enemy, false) + ' −' + hit.dmg + (hit.crit ? '(暴击)' : ''));
    };
    if (pierce) {
      arrows.forEach(function (a, i) {
        battleAlive().forEach(function (e) {
          if (e.hp <= 0) return;
          landArrow(a, e, '第' + (i + 1) + '箭穿透 ', 0.06 * i);
        });
      });
      battleAlive().forEach(function (e) {
        if (e.hp <= 0) return;
        landArrow(null, e, '归返 ', 0.34);
      });
    } else arrows.forEach(function (a, i) {
      const base = Math.max(1, Math.round(
        (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(main.level / 3)) *
        mult * skillDamageMult(skill.id, main) * POWER_SHOT_MULT
      ));
      const hit = rollPlayerDamage(base, main);
      a.dmg = hit.dmg;
      totalDmg += hit.dmg;
      if (i === 0 && hit.crit) firstCrit = true;
      main.hp = Math.max(0, main.hp - hit.dmg);
      gloveOnHit(main, false, '第' + (i + 1) + '箭');   // 手套词缀：每一支箭命中都算一次"击中"
      main.hurtT = HURT_TIME;
      caveReflectOnPlayerHit(main, hit.dmg); // 洞府「反伤」增幅：逐支结算
      // 飘字延迟到"这一支箭抵达敌人"的时刻（a.dur - a.t 就是它还要飞多久）
      addDamagePopup(main.x - cameraX() + 88 + i * 6, GROUND_Y - 148 - i * 26, hit.dmg,
        hit.crit ? '#ff4d4d' : '#ffd76a', false, Math.max(0, a.dur - a.t - 0.05));
      maybeApplyBurn(main, skill.id);        // 炎军：每一支箭都单独判定
      parts.push('第' + (i + 1) + '箭 −' + hit.dmg + (hit.crit ? '(暴击)' : ''));
    });
    if (!pierce) {
      if (arrows.length > 1) parts.unshift(enemyLabel(main, false));
      else parts[0] = enemyLabel(main, false) + ' −' + totalDmg + (firstCrit ? '(暴击)' : '') +
        '（' + Math.round(POWER_SHOT_MULT * 100) + '%）';
    }

    // 长弓基底：额外射出一支箭矢（和这一箭一起结算，伤害单独显示）
    const arrowHit = rollExtraArrow();
    const arrowDmg = arrowHit ? rollPlayerDamage(Math.max(1, Math.round(
      (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(main.level / 3)) *
      mult * skillDamageMult(skill.id, main) * POWER_SHOT_MULT
    )), main).dmg : 0;

    if (arrowDmg) {
      main.hp = Math.max(0, main.hp - arrowDmg);
      gloveOnHit(main, false, '长弓额外箭矢');
      caveReflectOnPlayerHit(main, arrowDmg);
      addDamagePopup(main.x - cameraX() + 118, GROUND_Y - 178, arrowDmg, '#ffd76a');
      maybeApplyBurn(main, skill.id);
      parts.push('长弓额外箭矢 −' + arrowDmg);
    }

    const boomTargets = perfect ? battleAlive() : [];
    boomTargets.forEach(function (e) {
      const boomBase = Math.max(1, Math.round(
        (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(e.level / 3)) *
        mult * skillDamageMult(skill.id, e) * POWER_SHOT_PERFECT_MULT
      ));
      const bHit = rollPlayerDamage(boomBase, e);
      e.hp = Math.max(0, e.hp - bHit.dmg);
      e.hurtT = HURT_TIME;
      e.stunTurns = Math.max(e.stunTurns || 0, POWER_SHOT_STUN_TURNS);
      gloveOnHit(e, false, '完美时机爆炸');   // 手套词缀：爆炸波及到的敌人
      caveReflectOnPlayerHit(e, bHit.dmg);
      addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 178, bHit.dmg, '#ffb24d');
      parts.push('爆炸 ' + enemyLabel(e, false) + ' −' + bHit.dmg + '（晕眩）');
      maybeApplyBurn(e, skill.id);
    });

    // 辅助技能石：箭矢数量 / 飞行速度（数值已经作用在特效与伤害上，这里补一句战报说明）
    const supBits = [];
    if (skillHasGem(skill.id, 'multipleProjectiles')) {
      supBits.push('多重投射物 ×' + projectileCountOf(skill.id, POWER_SHOT_BASE_PROJ) +
        ' 支（伤害总降 25%、速度总降 20%）');
    }
    if (skillHasGem(skill.id, 'projectileSpeed')) supBits.push('投射物加速 +40%');
    const note = (perfect
      ? '（完美时机：爆炸波及 ' + boomTargets.length + ' 个敌人，额外 ' +
        Math.round(POWER_SHOT_PERFECT_MULT * 100) + '% 伤害并全体晕眩 ' + POWER_SHOT_STUN_TURNS + ' 回合'
      : '（没触发完美时机，只有强力一击') + (supBits.length ? '；' + supBits.join('；') : '') + '）';
    setBattleLog('你射出「强力射击」：' + parts.join('，') + note);
    pushBattleLog('「强力射击」命中：' + parts.join('，') + note, 'player');
    updateBattleHud();

    if (activeEnemy && activeEnemy.hp <= 0) {
      if (!resolveEnemyDown()) {
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      pendingVictory = 0.4;
      return;
    }
    afterPlayerOffense();
    updateBattleHud();
  }

  function castSkill(skill) {
    if (pvpDuel) return pvpCastSkill(skill);   // 真人交锋：伤害交给服务端记账
    if (gameState !== 'battle' || !activeEnemy) return;
    if (pendingVictory > 0) return;
    if (battleTurn !== 'player') return;
    if (skill.id === 'summonPet') {
      setBattleLog('「召唤宠物」由法杖被动生效，可用辅助宝石强化宠物。');
      return;
    }
    if (slashFx) {
      setBattleLog('上一招尚未落下，稍等片刻。');
      return;
    }
    // 「强力射击」是弓类武技：武器不是长弓就别放（不扣灵力、不占回合）
    if (skill.id === 'powerShot' && !powerShotWeaponOk()) {
      setBattleLog('装备类型错误：「' + skill.name + '」需要装备' + POWER_SHOT_WEAPON +
        '，当前武器是' + (weaponTypeEquipped() || '空手') + '，放不出这一箭。');
      updateBattleHud();
      return;
    }
    if (combat.mp < skill.cost) {
      setBattleLog('灵力不足，无法施展「' + skill.name + '」。');
      updateBattleHud();
      return;
    }
    combat.mp -= skill.cost;
    resetPlayerIdleTimer();

    /* 「强力射击」：本回合只蓄力、不出伤害，并打开完美时机进度条；
     * 下一回合自动放箭（updatePowerShot 负责推进与触发）。 */
    if (skill.id === 'powerShot') {
      if (powerCharge) {
        combat.mp = Math.min(combat.maxMp, combat.mp + skill.cost);   // 正在蓄力：退回灵力
        setBattleLog('你正在蓄力「强力射击」，这一箭还没射出去。');
        updateBattleHud();
        return;
      }
      startPowerCharge();
      playPlayerAttack();
      const msg = '你拉满弓弦开始蓄力「强力射击」——本回合不出手，期间承受的敌人伤害提高 ' +
        POWER_SHOT_VULN_PCT + '%。用「空格键」让标记停在完美时机区间内，可触发爆炸！';
      setBattleLog(msg);
      pushBattleLog('开始蓄力「强力射击」：完美时机窗口 ' + POWER_TIMING_LIFE + ' 秒，按空格键判定', 'player');
      updateBattleHud();
      beginEnemyTurn(msg);
      return;
    }

    // 持续型开关技能：气血反弹（激活后一直生效，可再次点击关闭）
    if (skill.type === 'toggle') {
      /* 「盾墙」：占用 25% 生命上限 + 常驻 5% 格挡 + 格挡触发碎石崩裂 */
      if (skill.id === 'shieldWall') {
        const wallOn = !shieldWall;
        shieldWall = wallOn;
        recalcPlayerStats();     // 生命上限按比例被"占用"，格挡 +5%
        const wallMsg = wallOn
          ? '「盾墙」已激活：占用 25% 生命上限（当前上限 ' + combat.maxHp +
            '），格挡几率 +5%，每次格挡触发「碎石崩裂」。'
          : '「盾墙」已关闭：生命上限与格挡几率恢复。';
        setBattleLog(wallMsg);
        pushBattleLog((wallOn ? '激活' : '关闭') + '「盾墙」持续状态' +
          (wallOn ? '（生命占用 25%、格挡 +' + SHIELD_WALL_BLOCK_PCT + '%）' : ''),
          wallOn ? 'reward' : 'system');
        updateBattleHud();
        beginEnemyTurn(wallMsg);
        return;
      }
      bloodRebound = !bloodRebound;
      recalcPlayerStats(); // 持续技能开关会改变灵力占用（35%）
      // 激活瞬间来一圈更粗的血色波纹，关闭时让已有波纹自然散尽
      bloodWaveTimer = 0;
      if (bloodRebound) {
        spawnBloodWave(1.8, 0);
        spawnBloodWave(1.2, 0.16);
      }
      const toggleMsg = bloodRebound
        ? '「' + skill.name + '」已激活：每次被敌人攻击都会反噬 ' + bloodReboundValue() + ' 点伤害。'
        : '「' + skill.name + '」已关闭，不再反噬敌人。';
      setBattleLog(toggleMsg);
      pushBattleLog(
        (bloodRebound ? '激活' : '关闭') + '「' + skill.name + '」持续状态',
        bloodRebound ? 'reward' : 'system'
      );
      updateBattleHud();
      // 用同一条提示进入敌人回合，避免被“准备反击”覆盖掉
      beginEnemyTurn(toggleMsg);
      return;
    }

    // 恢复术
    if (skill.type === 'heal') {
      const heal = Math.max(1, Math.round(combat.maxHp * 0.35 * skillLevelMult(skill)));
      combat.hp = Math.min(combat.maxHp, combat.hp + heal);
      addDamagePopup(
        player.worldX - cameraX() - 82,
        GROUND_Y - 160,
        heal,
        '#6dff8d',
        true
      );
      setBattleLog('你施展「恢复术」，恢复 ' + heal + ' 点生命。');
      updateBattleHud();
      beginEnemyTurn();
      return;
    }

    // 召唤宠物：召唤出来的伙伴会一直跟随玩家（战斗结束也不消失）
    if (skill.type === 'summon') {
      const lvMult = skillLevelMult(skill);
      // 召唤物→素材映射：蝎子 / 鬣狗 / 毒蛇 / 猎鹰（蝙蝠沿用猎鹰的飞行素材）
      const profile = skill.id === 'summonScorpion' ? { sprite: 'scorpio', atkMult: 1.1 }
        : skill.id === 'summonHyena' ? { sprite: 'hyena', atkMult: 1.15 }
        : skill.id === 'summonSnake' ? { sprite: 'snake', atkMult: 1.0 }
        : skill.id === 'summonVulture' ? { sprite: 'vulture', atkMult: 1.05 }
        : skill.id === 'summonBats' ? { sprite: 'vulture', atkMult: 1.3 }
        : { sprite: 'scorpio', atkMult: 1.1 };
      profile.name = PET_SPRITES[profile.sprite].name;
      /* 出战上限：「仆从」在场时只能有一只庞然大物，
       * 这时再召唤就"换一只"（旧召唤物消散），而不是直接不让放。 */
      const minionCap = petCap();
      const swapPets = hasServantGem();
      if (alivePets().length >= minionCap && !swapPets) {
        setBattleLog('出战宠物已达上限（' + minionCap + ' 只），无法再召唤。');
        updateBattleHud();
        return;
      }
      const oldPets = swapPets ? alivePets().slice() : [];
      const summoned = {
        name: profile.name,
        tier: 'magic',
        level: petLevelOf('magic'),
        sprite: profile.sprite,
        source: '召唤技能「' + skill.name + '」',
        sourceKind: 'skill',
        atkMult: profile.atkMult * lvMult,
        hpMult: 1,
        hp: 1,
        maxHp: 1,
        hurtT: 0,
        animT: 0,
        attackT: 0,
        walkT: rand(0, 10),
        skillId: skill.id
      };
      addPet(summoned);
      // 先放新的，再收旧的：否则 recalcPlayerStats() 里的法杖灵宠会又冒出来一只
      oldPets.forEach(function (o) {
        const idx = pets.indexOf(o);
        if (idx >= 0) pets.splice(idx, 1);
      });
      refreshPetStats();
      summoned.hp = summoned.maxHp;
      recalcPlayerStats(); // 召唤物在场 → 占用 50% 灵力上限
      if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
      if (oldPets.length) {
        pushBattleLog('「仆从」只能拥有一只召唤物：' + oldPets.length + ' 只旧召唤物消散了。', 'system');
      }
      setBattleLog(
        '你召唤出「' + profile.name + '」，它会一直跟随你作战！' +
        (swapPets ? '（庞然大物体型 · 只能拥有一只召唤物）' : '') +
        '（当前出战 ' + alivePets().length + ' 只宠物）'
      );
      updateBattleHud();
      beginEnemyTurn();
      return;
    }

    // 世界BOSS：境界不足时，所有伤害类技能一律 MISS（治疗 / 召唤等辅助技能不受影响）
    if (worldBossRun && skill.dmg && !worldBossLevelOk()) {
      playPlayerAttack();
      worldBossMiss('你使出「' + skill.name + '」，但境界差距太大，打在「' +
        worldBossRun.def.name + '」身上毫无效果——MISS');
      beginEnemyTurn();
      return;
    }

    // 「森特欧斯的囚笼」：强控敌人 2 秒 + 100% 智力伤害（头骨圣物附带）
    if (skill.id === 'santosCage') {
      playPlayerAttack();
      const intTotal = Math.max(1, derived.intTotal || 1);
      const dmg = Math.max(1, Math.round(intTotal));      // 100% 智力
      activeEnemy.stunT = Math.max(activeEnemy.stunT || 0, WB_CAGE_SECONDS);
      activeEnemy.hp = Math.max(0, activeEnemy.hp - dmg);
      activeEnemy.hurtT = HURT_TIME;
      addDamagePopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 148, dmg, '#c08cff');
      spawnCageFx();
      setBattleLog('你施展「森特欧斯的囚笼」，把' + enemyLabel(activeEnemy, false) +
        '困在原地 ' + WB_CAGE_SECONDS + ' 秒（无法移动与攻击），并造成 ' + dmg + ' 点伤害（100% 智力）。');
      pushBattleLog('「森特欧斯的囚笼」：' + enemyLabel(activeEnemy, false) + ' −' + dmg +
        '，并被困住 ' + WB_CAGE_SECONDS + ' 秒', 'player');
      updateBattleHud();
      if (activeEnemy.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    // “劈砍”走完整演出：刀光出现 -> 劈到敌人身上 -> 结算伤害
    if (skill.id === 'slash') {
      playPlayerAttack(); // 血刀劈落时角色一起挥砍
      const lvMult = skillDamageLevelMult(skill);   // 劈砍：每级 +10% 基础伤害
      slashFx = {
        t: 0,
        dur: SLASH_DUR,
        impactAt: SLASH_IMPACT,
        enemyWx: activeEnemy.x,
        hit: false,
        particles: [],
        dmg: [Math.round(skill.dmg[0] * lvMult), Math.round(skill.dmg[1] * lvMult)]
      };
      setBattleLog('你施展「劈砍」，血刀高高举起，向敌人当头劈落！');
      updateBattleHud();
      return;
    }

    // 其它伤害技能：火球术 / 雷电术 / 闪电箭 / 破空斩
    if (!skill.dmg) return;
    /* 施放令牌：+1 之后，本次施放期间所有伤害结算都算"同一次施放"
     *   —— 时装充能只放大它绑定的那一次（上一次施放没用掉的充能在这里作废） */
    castToken++;
    if (costumeChargeActive && costumeChargeActive.token !== undefined &&
      costumeChargeActive.token !== castToken) {
      costumeChargeActive = null;
      costumeChargeUsed++;
      if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    }
    // 智力提供的「法术伤害提高」只作用于法术类技能，不作用于近战技能
    const attrSpell = skill.type === 'spell' ? (derived.spellDmgPct || 0) : 0;
    const mult = (1 + (skillGemSum(skill.id, ['weaponDamage', 'meleeDamage', 'spellDamage']) + attrSpell) / 100) *
      skillDamageLevelMult(skill);
    // 辅助技能石（处决 / 多重投射物）+ 时装充能 对主目标的伤害倍率
    const supMainMult = skillDamageMult(skill.id, activeEnemy);
    const baseSkillDmg = Math.max(1, Math.round(
      (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(activeEnemy.level / 3)) * mult * supMainMult
    ));

    // 「破空斩」是群攻：一条直线上的所有敌人一起吃剑气（洞府多对一时尤其明显）
    if (skill.id === 'airSlash') {
      // 特效：连续 2 道剑气向前扩散，飞到屏幕最右侧边界消失
      playPlayerAttack();
      startAirSlashFx();
      const targets = battleAlive();
      const parts = [];
      // 投射物数量 = 独立结算次数：镶了多重投射物 → 4 道剑气、每道各自结算（每道伤害总降 25%）
      const waveHits = projectileHitsOf(skill.id, 1);
      targets.forEach(function (e) {
        for (let h = 0; h < waveHits; h++) {
          if (e.hp <= 0) break;
          // 每个敌人单独判定：处决对低血敌人加成（被辅助技能造成的伤害）
          const supT = skillDamageMult(skill.id, e);
          const base = Math.max(1, Math.round(
            (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(e.level / 3)) * mult * supT
          ));
          const hit = rollPlayerDamage(base, e);   // 逐敌结算：各自按境界差折算
          e.hp = Math.max(0, e.hp - hit.dmg);
          e.hurtT = HURT_TIME;
          gloveOnHit(e, false, waveHits > 1 ? '第' + (h + 1) + '道剑气' : '剑气');
          caveReflectOnPlayerHit(e, hit.dmg);   // 洞府「反伤」增幅
          addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 148 - h * 20, hit.dmg, hit.crit ? '#ff4d4d' : '#ffb34d');
          parts.push(enemyLabel(e, false) + ' −' + hit.dmg + (hit.crit ? '(暴击)' : '') +
            (waveHits > 1 ? '(第' + (h + 1) + '道)' : ''));
          maybeApplyBurn(e, skill.id);   // 炎军：剑气命中
        }
      });
      const projCount = projectileCountOf(skill.id, AIRSLASH_FX_WAVES);
      const supNote = (function () {
        const notes = [];
        if (skillHasGem(skill.id, 'multipleProjectiles')) {
          notes.push('多重投射物 ×' + projCount + ' 道 / 独立结算 ' + waveHits +
            ' 次（每次伤害总降 25%、速度总降 20%）');
        }
        if (skillHasGem(skill.id, 'projectileSpeed')) notes.push('投射物加速 +40%');
        if (skillHasGem(skill.id, 'execute')) {
          const bits = [];
          if (playerLowLife()) bits.push('你低血 +30%');
          if (targets.some(enemyLowLife)) bits.push('对低血敌人 +30%');
          if (bits.length) notes.push('处决：' + bits.join('、'));
        }
        return notes.length ? '（' + notes.join('；') + '）' : '';
      })();
      setBattleLog('你使出「' + skill.name + '」（群攻·贯穿一线' + supNote + '）：' + parts.join('，'));
      pushBattleLog('「' + skill.name + '」群攻命中 ' + targets.length + ' 个敌人：' + parts.join('，'), 'player');
      updateBattleHud();
      if (activeEnemy && activeEnemy.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    // 「阳爪」：裂伤挥斩主目标（距离越远伤害越高，最多 +100%），并让被裂伤波及的敌人流血
    if (skill.id === 'yangClaw') {
      playPlayerAttack();  // 出手动作：和劈砍一样带动角色挥砍
      startYangClawFx();   // 特效：2 秒内白色虚影分身突进
      const main = activeEnemy;
      const dist = Math.abs(main.x - player.worldX);
      const distBonus = Math.min(1, dist / 600);        // 0~600px → +0%~+100%
      // 6 次连击：每次造成"攻击力 15%"的基础伤害（逐次独立结算与显示）
      const atkPower = 28.5 + derived.flatAtk;
      const pct = (typeof skill.atkPct === 'number' ? skill.atkPct : YANG_CLAW_ATK_PCT * 100) / 100;
      const hits = [];
      let totalDmg = 0;
      for (let i = 0; i < YANG_CLAW_HITS; i++) {
        const base = Math.max(1, Math.round(
          atkPower * pct * mult * supMainMult * (1 + distBonus)
        ));
        const hit = rollPlayerDamage(base, main);
        main.hp = Math.max(0, main.hp - hit.dmg);
        main.hurtT = HURT_TIME;
        caveReflectOnPlayerHit(main, hit.dmg);   // 洞府「反伤」增幅（每次连击单独结算）
        totalDmg += hit.dmg;
        addDamagePopup(
          main.x - cameraX() + 88 + (i % 2 === 0 ? -30 : 30),
          GROUND_Y - 148 - i * 16,
          hit.dmg,
          hit.crit ? '#ff4d4d' : '#ffb34d'
        );
        hits.push({ n: i + 1, dmg: hit.dmg, crit: hit.crit });
        maybeApplyBurn(main, skill.id);        // 炎军：每一次连击都判定
        /* 独特「武者的刑具」：6 连击 = 6 次独立的"击中时"判定（火伤与熔岩骤雨各判一次） */
        uniqueOnHit(main, '第' + (i + 1) + '击');
        if (main.hp <= 0) break;               // 打死了就不再加后面的段数
      }
      lastSkillCalc = { id: skill.id, level: skill.level || 1, mult: Math.round(mult * 1000) / 1000,
        distBonus: Math.round(distBonus * 1000) / 1000, hits: hits.length, total: totalDmg,
        atkPower: Math.round(atkPower * 10) / 10, atkPct: Math.round(pct * 1000) / 10 };

      // 裂伤：被挥斩波及的敌人（同一战场的敌人）全部陷入流血
      const bleedTurns = 3;
      const bleedDmg = Math.max(1, Math.round(totalDmg * 0.25 / Math.max(1, hits.length)));
      const hitList = battleAlive();
      hitList.forEach(function (e) { e.bleed = { dmg: bleedDmg, turns: bleedTurns }; });
      gloveOnHit(main, true);        // 手套词缀：阳爪（一次出手判定一次；独特装备已在每一击里判过）

      let logText = '你使出「' + skill.name + '」（6 连击·共 ' + totalDmg + ' 点）：' +
        hits.map(function (h) { return '第' + h.n + '击 ' + h.dmg + (h.crit ? '(暴击)' : ''); }).join('、') +
        '（距离加成 +' + Math.round(distBonus * 100) + '%）；裂伤使 ' + hitList.length +
        ' 个敌人流血（每回合 ' + bleedDmg + ' 点，持续 ' + bleedTurns + ' 回合）';

      // 辅助「震撼大地」：脚步震裂大地，冲击波随机 1~5 次（每次独立计算、独立显示）
      const hasQuake = (skill.sockets || []).some(function (g) { return g.effect === 'earthshaker'; });
      if (hasQuake) {
        const waves = randInt(1, 5);
        const targets = battleAlive().slice();
        const waveParts = [];
        let quakeTotal = 0;
        for (let i = 0; i < waves; i++) {
          spawnShockWave(i * 0.1);
          const perParts = [];
          targets.forEach(function (e) {
            if (e.hp <= 0) return;
            // 每一次冲击波单独掷伤害（不再整轮共用同一个数）
            const atkBase = randInt(23 + derived.flatAtk, 34 + derived.flatAtk);
            const quakeDmg = Math.max(1, Math.round(atkBase * 0.5 + derived.strengthTotal));
            const h = rollPlayerDamage(quakeDmg, e);
            e.hp = Math.max(0, e.hp - h.dmg);
            e.hurtT = HURT_TIME;
            caveReflectOnPlayerHit(e, h.dmg);   // 洞府「反伤」增幅
            quakeTotal += h.dmg;
            addDamagePopup(
              e.x - cameraX() + 88 + (i % 2 === 0 ? -26 : 26),
              GROUND_Y - 112 - i * 14,
              h.dmg,
              '#ffd280'
            );
            maybeApplyBurn(e, skill.id);   // 炎军：冲击波也算一次伤害
            /* 震撼大地的每一次击中同样各自判定一次独特装备（火伤 + 熔岩骤雨） */
            uniqueOnHit(e, '震撼大地 第' + (i + 1) + '次');
            perParts.push(enemyLabel(e, false) + ' −' + h.dmg + (h.crit ? '(暴击)' : ''));
          });
          if (perParts.length) waveParts.push('第' + (i + 1) + '次 ' + perParts.join('、'));
        }
        /* 战报里把公式写清楚：每次 = 普攻伤害 × 50% + 力量合计。
         *   （以前只写"共 N 点"，玩家看不出力量到底算没算进去） */
        logText += '；辅助「震撼大地」触发 ' + waves + ' 次冲击波（每次 = 普攻 50% + 力量 ' +
          derived.strengthTotal + '，共 ' + quakeTotal + ' 点）：' + waveParts.join('；');
      }
      setBattleLog(logText + '。');
      pushBattleLog(logText, 'player');
      updateBattleHud();
      if (main.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    const hitInfo = rollPlayerDamage(baseSkillDmg, activeEnemy);
    const dmg = hitInfo.dmg;
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    gloveOnHit(activeEnemy);                    // 手套词缀：命中回血 / 异常状态几率
    caveReflectOnPlayerHit(activeEnemy, dmg);   // 洞府「反伤」增幅
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 148,
      dmg,
      hitInfo.crit ? '#ff4d4d' : '#ffb34d'
    );
    setBattleLog('你使出「' + skill.name + '」，造成 ' + dmg + ' 点伤害' +
      (hitInfo.crit ? '（暴击）' : '') + '。');
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「' + skill.name + '」击败了拦路者！');
      updateBattleHud();
      if (!resolveEnemyDown()) {
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      pendingVictory = 0.4;
      return;
    }
    afterPlayerOffense();
    updateBattleHud();
  }

  // ---------- 森特欧斯的图腾：每回合随机召唤法术 ----------
  const TOTEM_SPELLS = [
    { id: 'meteor', name: '陨石', color: '#ff9a44' },
    { id: 'tree', name: '树木', color: '#7de08a' },
    { id: 'spore', name: '有毒的孢子', color: '#b6e05a' }
  ];
  const totemFx = [];   // 图腾法术的小特效

  // 每回合开始（玩家回合）随机触发一种法术
  function totemTurnSpell() {
    if (!hasSantosRelic('totem')) return false;
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return false;
    const spell = TOTEM_SPELLS[randInt(0, TOTEM_SPELLS.length - 1)];
    const intTotal = Math.max(1, derived.intTotal || 1);
    const targets = battleAlive().length ? battleAlive() : [activeEnemy];
    const parts = [];
    let note = '';
    targets.forEach(function (e) {
      let dmg = 0;
      if (spell.id === 'meteor') {
        dmg = Math.round(intTotal * 1.2 + randInt(10, 26));
        note = '（120% 智力）';
      } else if (spell.id === 'tree') {
        dmg = Math.round(intTotal * 0.8);
        e.rootT = Math.max(e.rootT || 0, 1);
        note = '（80% 智力 + 缠绕 1 回合）';
      } else {
        dmg = Math.round(intTotal * 0.5);
        e.poison = { dmg: Math.max(1, Math.round(intTotal * 0.3)), turns: 3 };
        note = '（50% 智力 + 中毒 3 回合）';
      }
      dmg = Math.max(1, dmg);
      e.hp = Math.max(0, e.hp - dmg);
      e.hurtT = HURT_TIME;
      addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 128, dmg, spell.color);
      parts.push(enemyLabel(e, false) + ' −' + dmg);
      spawnTotemFx(spell.id, e.x);
    });
    const msg = '图腾法术「' + spell.name + '」' + (parts.length ? '：' + parts.join('，') : '');
    setBattleLog(msg);
    pushBattleLog(msg + note, 'player');
    updateBattleHud();
    if (activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) pendingVictory = 0.4;
    }
    return true;
  }

  function spawnTotemFx(kind, wx) {
    totemFx.push({ kind: kind, wx: wx, t: 0, life: kind === 'meteor' ? 0.75 : 0.95 });
  }

  function updateTotemFx(dt) {
    for (let i = totemFx.length - 1; i >= 0; i--) {
      totemFx[i].t += dt;
      if (totemFx[i].t >= totemFx[i].life) totemFx.splice(i, 1);
    }
  }

  function drawTotemFx() {
    if (!totemFx.length) return;
    ctx.save();
    totemFx.forEach(function (f) {
      const sx = f.wx - cameraX();
      const p = clamp(f.t / f.life, 0, 1);
      const a = (p < 0.15 ? p / 0.15 : 1 - (p - 0.15) / 0.85);
      ctx.globalAlpha = Math.max(0, a);
      if (f.kind === 'meteor') {
        // 陨石：从天上砸下来 + 落地冲击环
        const y = GROUND_Y - 420 + p * 420;
        ctx.fillStyle = 'rgba(255, 150, 60, .5)';
        ctx.fillRect(sx - 7, y - 90, 14, 90);
        ctx.beginPath();
        ctx.arc(sx, y, 17, 0, Math.PI * 2);
        ctx.fillStyle = '#ffb055';
        ctx.fill();
        if (p > 0.75) {
          ctx.strokeStyle = 'rgba(255, 160, 80, ' + ((1 - p) * 3).toFixed(3) + ')';
          ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.ellipse(sx, GROUND_Y + 4, 30 + (p - 0.75) * 500, 12 + (p - 0.75) * 90, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (f.kind === 'tree') {
        // 树木：从地面窜出几根树苗把敌人缠住
        ctx.strokeStyle = '#6fbf6a';
        ctx.lineWidth = 6;
        for (let i = -2; i <= 2; i++) {
          const h = (86 + Math.abs(i) * -12) * Math.min(1, p * 2);
          ctx.beginPath();
          ctx.moveTo(sx + i * 22, GROUND_Y + 4);
          ctx.quadraticCurveTo(sx + i * 30, GROUND_Y - h * 0.6, sx + i * 40, GROUND_Y - h);
          ctx.stroke();
        }
      } else {
        // 有毒的孢子：绿色毒雾团
        for (let i = 0; i < 4; i++) {
          const r = 26 + i * 16 + p * 40;
          ctx.fillStyle = 'rgba(150, 210, 80, ' + (0.22 * (1 - p)).toFixed(3) + ')';
          ctx.beginPath();
          ctx.arc(sx + Math.sin(i * 2 + p * 4) * 26, GROUND_Y - 90 + Math.cos(i * 3) * 26, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });
    ctx.restore();
  }

  // 囚笼特效：敌人身上浮现一圈紫色符文牢笼
  const cageFx = [];
  function spawnCageFx() {
    if (!activeEnemy) return;
    cageFx.push({ wx: activeEnemy.x, t: 0, life: WB_CAGE_SECONDS });
  }
  function updateCageFx(dt) {
    for (let i = cageFx.length - 1; i >= 0; i--) {
      cageFx[i].t += dt;
      if (cageFx[i].t >= cageFx[i].life) cageFx.splice(i, 1);
    }
  }
  function drawCageFx() {
    if (!cageFx.length) return;
    ctx.save();
    cageFx.forEach(function (f) {
      const sx = f.wx - cameraX();
      const p = clamp(f.t / f.life, 0, 1);
      const a = (p < 0.1 ? p / 0.1 : 1 - (p - 0.1) / 0.9) * 0.85;
      ctx.globalAlpha = a;
      ctx.strokeStyle = '#c08cff';
      ctx.lineWidth = 3;
      // 竖着的符文柱子
      for (let i = 0; i < 5; i++) {
        const x = sx - 60 + i * 30;
        ctx.beginPath();
        ctx.moveTo(x, GROUND_Y + 4);
        ctx.lineTo(x + (i % 2 ? 4 : -4), GROUND_Y - 168);
        ctx.stroke();
      }
      // 上下两道符文环
      [GROUND_Y - 6, GROUND_Y - 166].forEach(function (y) {
        ctx.beginPath();
        ctx.ellipse(sx, y, 74, 16, 0, 0, Math.PI * 2);
        ctx.stroke();
      });
      ctx.fillStyle = 'rgba(180, 120, 255, .18)';
      ctx.beginPath();
      ctx.ellipse(sx, GROUND_Y - 84, 76, 92, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  // 学习新技能的唯一下发点：击败 BOSS 掉落的技能书
  function learnSkillFromBook(skillDef) {
    if (!knownSkills.some(function (s) { return s.id === skillDef.id; })) {
      knownSkills.push(skillDef);
    }
  }

  function enemyCounterAttack(opening, idleSurprise) {
    if (pvpDuel) return;      // 决斗中：对方何时出手由服务端说了算
    // 玩家出手后的敌方回合：先结算流血 + 燃烧（「炎军」叠加的层数）
    if (applyBleedTick() && activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) { pendingVictory = 0.4; return; }
    }
    if (applyBurnTick() && activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) { pendingVictory = 0.4; return; }
    }
    // 玩家身上的「点燃」（洞府敌人的增幅）：同样每个敌方回合结算一次
    if (applyPlayerBurnTick() > 0 && combat.hp <= 0) return;   // 被烧死就交给阵亡流程
    // 被「森特欧斯的囚笼」/「树木」控住的敌人：这一回合无法移动与攻击
    if (activeEnemy && (activeEnemy.stunT > 0 || activeEnemy.rootT > 0 || activeEnemy.stunTurns > 0)) {
      const label = enemyLabel(activeEnemy, false);
      const why = activeEnemy.stunT > 0 ? '被「森特欧斯的囚笼」困住'
        : activeEnemy.stunTurns > 0 ? '被「强力射击」的爆炸震晕'
        : '被树根缠住';
      if (activeEnemy.rootT > 0) activeEnemy.rootT -= 1;
      if (activeEnemy.stunTurns > 0) activeEnemy.stunTurns -= 1;
      setBattleLog(label + why + '，这一回合无法移动与攻击！');
      pushBattleLog(label + why + '，无法行动', 'player');
      enemyHelpersTurn();
      if (gameState === 'battle' && activeEnemy && pendingVictory <= 0) {
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        applyTurnRegen();
        totemTurnSpell();
        updateBattleHud();
      }
      defending = false;   // 敌人这回合动不了，防御姿态也随之结束
      return;
    }
    // 主目标先出手（内部可能因为格挡 / 恢复 / 被反伤打死而提前结束这次出手）
    enemyCounterAttackCore(opening, idleSurprise);
    // 同一场战斗里的其他敌人不受影响，照样一起围攻玩家（真正的多对一）
    enemyHelpersTurn();
    // 回到玩家回合：结算「生命每回合再生」
    if (gameState === 'battle' && activeEnemy && pendingVictory <= 0) {
      applyTurnRegen();
      totemTurnSpell();   // 「森特欧斯的图腾」：每回合随机召唤一种法术
    }
    defending = false;    // 一轮打完，防御姿态清除
    // 玩家被击晕 / 冰冻：这一回合直接跳过，敌人再动一次（计时器触发）
    if (gameState === 'battle' && activeEnemy && pendingVictory <= 0 && consumePlayerSkipTurn()) return;
  }

  // 围攻：同一场战斗里除当前目标外的敌人都会攻击玩家
  function enemyHelpersTurn() {
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return;
    // 被「强力射击 · 完美时机」震晕的敌人这一回合动不了
    const stunnedHelpers = battleAlive().filter(function (e) {
      return e !== activeEnemy && e.stunTurns > 0;
    });
    stunnedHelpers.forEach(function (e) {
      e.stunTurns -= 1;
      pushBattleLog(enemyLabel(e, false) + '被爆炸震晕，这一回合无法行动', 'player');
    });
    const helpers = battleAlive().filter(function (e) {
      return e !== activeEnemy && stunnedHelpers.indexOf(e) < 0;
    });
    if (helpers.length) {
      // 围攻帮手的伤害同样吃「常驻减伤（合计封顶 90%）」和闪避
      const passive = playerPassiveDamageMult();
      const parts = [];
      // 每个围攻者用不同的颜色，名字相同也能一眼区分
      const HIT_COLORS = ['#ff6a5a', '#ffb24d', '#ff7fd0', '#8fd0ff'];
      // 伤害数字要贴在"被打的玩家"身上，不是贴在敌人身上
      const px = player.worldX - cameraX();
      helpers.forEach(function (e, hi) {
        const label0 = enemyLabel(e);
        const color0 = HIT_COLORS[hi % HIT_COLORS.length];
        if (rollPlayerDodge()) {
          addTextPopup(px - 96, GROUND_Y - 190 - hi * 34 - 30, '闪避', '#a8ffd0');
          pushBattleLog(label0 + '围攻，被你闪避', 'player');
          parts.push(label0 + ' 闪避');
          return;
        }
        // 围攻的帮手同样吃"境界压制"：低境界帮手打高境界的你会被削弱
        const helperRealm = realmDamageMult(e.level || 1, playerLevel());
        if (helperRealm < 1) noticeRealmGap('enemy', helperRealm);
        const helperRaw = e.atk * 0.7 * rand(0.85, 1.15) * powerVulnMult() * helperRealm;
        const raw = Math.max(1, Math.round(helperRaw));
        const dmg = Math.max(1, Math.round(raw * passive.mult));
        combat.hp = Math.max(0, combat.hp - dmg);   // 允许被打到 0（触发阵亡）
        e.hurtT = 0;
        const label = enemyLabel(e);
        const color = HIT_COLORS[hi % HIT_COLORS.length];
        // 多个围攻者同时打中：都飘在玩家身侧，按 34px 一层往上错开，
        // 起点比"主目标那一击"（GROUND_Y-148）高一档，避免两处数字叠在一起
        const stackY = GROUND_Y - 190 - hi * 34;
        addTextPopup(px - 96, stackY - 30, label, color);
        addDamagePopup(px - 96, stackY, dmg, color);
        pushBattleLog(label + '围攻，你损失 ' + dmg + ' 点生命', 'enemy');
        parts.push(label + ' −' + dmg);
      });
      player.hurtT = HURT_TIME;
      // 战报按敌人逐条列出，不再合成一个总数
      setBattleLog('围攻：' + parts.join('，'));
      updateBattleHud();
    }
    // 洞府里被围攻后，附近的敌人还可能继续加入
    if (cave) tryCaveJoinBattle();
  }

  function enemyCounterAttackCore(opening, idleSurprise) {
    const isOpening = !!opening;
    const isIdle = !!idleSurprise;
    let skillNote = '';
    let mult = 1;
    // 传奇BOSS出手：地面震一下
    if (activeEnemy && activeEnemy.legend) enemyQuake = 0.45;

    // 精英自带技能
    const eliteSkill = activeEnemy.skills && activeEnemy.skills.length
      ? activeEnemy.skills[randInt(0, activeEnemy.skills.length - 1)]
      : activeEnemy.skill;
    if (activeEnemy.elite && eliteSkill) {
      const sk = eliteSkill;
      if (sk.id === 'heal' && !activeEnemy.healUsed && activeEnemy.hp <= activeEnemy.maxHp * 0.5) {
        const heal = Math.max(1, Math.round(activeEnemy.maxHp * 0.12));
        activeEnemy.healUsed = true;
        activeEnemy.hp = Math.min(activeEnemy.maxHp, activeEnemy.hp + heal);
        setBattleLog('精英拦路者施展「恢复术」，恢复了 ' + heal + ' 点生命！');
        updateBattleHud();
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        return;
      }
      /* 金色稀有：元素技能（火球 / 雷电）倍率再乘一档 —— 元素伤害提高 */
      const elemBoost = activeEnemy.tier === 'elite' ? ELITE_ELEM_SKILL_MULT : 1;
      if (sk.id === 'fireball') { mult = 1.6 * elemBoost; skillNote = '火球术'; }
      else if (sk.id === 'lightning') { mult = 1.5 * elemBoost; skillNote = '雷电术'; }
      else if (sk.id === 'summonEnemy') {
        skillNote = '召唤普通敌人';
        enemyMinionTurns = 3;
        // 召唤物也随机挑一种敌人美术，面朝玩家对面的敌人。
        // 必须带上 dmg：否则下一回合 allyFollowUp() 读 ally.dmg[0] 会直接抛异常，卡死整回合。
        const minionAtk = Math.max(4, Math.round((activeEnemy.atk || 8) * 0.5));
        ally = {
          turns: 3,
          sprite: randEnemySprite().key,
          dmg: [minionAtk, minionAtk + Math.max(2, Math.round(minionAtk * 0.5))]
        };
      }
    }

    let raw = randInt(activeEnemy.atk, activeEnemy.atk + 5);
    /* 金色稀有敌人：普攻额外附带一段元素伤害（40% 攻击力），在境界压制之前加进去 */
    let elemNote = '';
    if (activeEnemy.tier === 'elite') {
      const elem = Math.max(1, Math.round((activeEnemy.atk || 0) * ELITE_ELEM_ATK_PCT));
      raw += elem;
      elemNote = '（含元素伤害 +' + elem + '）';
    }
    /* 境界压制（对玩家这一侧同样生效）：低境界的敌人打高境界的你，伤害被削弱；
     *   高境界敌人打低境界的你照常发挥（武者境挨筑基期怪一下是要命的）。 */
    {
      const rm = realmDamageMult(activeEnemy.level || 1, playerLevel());
      if (rm !== 1) {
        noticeRealmGap('enemy', rm);   // 战斗内提示（敌人境界低于你 → 它的伤害被压）
        raw = Math.max(1, Math.round(raw * rm));
      }
    }
    if (mult > 1) raw = Math.round(raw * mult);
    if (enemyMinionTurns > 0) {
      raw += Math.round(activeEnemy.atk * 0.6);
      enemyMinionTurns -= 1;
      skillNote = skillNote ? '召唤物协助' : '召唤物';
    }
    // 「强力射击」蓄力中：你承受的敌人伤害提高 15%
    const chargingVuln = powerCharging();
    if (chargingVuln) raw = Math.max(1, Math.round(raw * powerVulnMult()));

    // 出手者写出具体是谁（洞府里可能同时有好几个敌人）
    const attackerName = enemyLabel(activeEnemy);
    const who = skillNote
      ? attackerName + '施展「' + skillNote + '」'
      : (isOpening ? attackerName + '抢先出手'
        : isIdle ? '你迟迟没有攻击，' + attackerName + '抢先进攻'
        : attackerName + '反击');

    // 宠物在场时敌人有 50% 概率先攻击宠物（调试钩子可强制指定打谁）
    if (petAlive() && (debugForcePetHit === true ||
        (debugForcePetHit === null && Math.random() < 0.5))) {
      // 随机挑一只出战宠物挨打
      const targets = alivePets();
      const victim = targets[randInt(0, targets.length - 1)];
      const victimSlot = pets.indexOf(victim);
      // 召唤生物辅助：肉盾（受到伤害总降 35%）/ 狂噬（受到伤害总增 20%）
      const petTaken = Math.max(1, Math.round(raw * minionGemMods(victim).takenMult));
      victim.hp -= petTaken;
      victim.hurtT = HURT_TIME;
      addDamagePopup(
        petScreenX(Math.max(0, victimSlot)),
        GROUND_Y - 86,
        petTaken,
        '#ff8f72'
      );
      if (victim.hp <= 0) {
        victim.hp = 0;
        pets.splice(pets.indexOf(victim), 1);
        recalcPlayerStats(); // 宠物消失 → 释放召唤类占用的灵力
        if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
        setBattleLog(who + '，「' + victim.name + '」被重创倒下了！');
      } else {
        setBattleLog(who + '，「' + victim.name + '」损失 ' + petTaken + ' 点生命。');
      }
    } else {
      // 闪避判定：闪掉则完全不掉血（闪避几率上限 75%）
      if (rollPlayerDodge()) {
        addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 148, '闪避', '#a8ffd0');
        setBattleLog(who + '，你闪身躲开了这次攻击！');
        pushBattleLog(who + '：被你闪避（' + Math.round(derived.dodgePct) + '% 几率）', 'player');
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        updateBattleHud();
        return;
      }
      // 格挡判定：成功则完全免伤（格挡几率上限 75%）
      if (derived.blockPct > 0 && Math.random() * 100 < derived.blockPct) {
        addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 148, '格挡', '#9fd4ff');
        /* 瓦尔塔盾：格挡时回复生命上限的 5% */
        let blockHeal = 0;
        if (derived.blockHealPct > 0 && combat.hp < combat.maxHp) {
          blockHeal = Math.max(1, Math.round(combat.maxHp * derived.blockHealPct / 100));
          combat.hp = Math.min(combat.maxHp, combat.hp + blockHeal);
          addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 130, blockHeal, '#6dff8d', true);
        }
        const rbBlock = applyBloodRebound();
        shieldWallProc();          // 盾墙：每次成功格挡都触发「碎石崩裂」
        setBattleLog(who + '，你成功格挡了这次攻击！' +
          (blockHeal ? '（瓦尔塔盾回复 ' + blockHeal + ' 生命）' : '') +
          (rbBlock ? '（气血反弹反噬 ' + rbBlock + ' 点）' : ''));
        if (rbBlock && (!activeEnemy || activeEnemy.hp <= 0)) return;
        if (shieldWallDealtKill()) return;
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        updateBattleHud();
        return;
      }
      // 常驻减伤三层叠乘（护甲/抗性 → 盾牌减伤 → 物理减免），合计封顶 90%
      const passive = playerPassiveDamageMult();
      // 敌人暴击：普通 5% / 蓝色 8% / 精英 12% / 传奇 15%，暴击伤害 1.5 倍（被"暴击伤害降低"削减）
      const critChance = activeEnemy.legend ? 15
        : activeEnemy.elite ? 12
        : activeEnemy.tier === 'magic' ? 8 : 5;
      const isCrit = Math.random() * 100 < critChance;
      /* 冠军胸甲：不会受到暴击所造成的额外暴击伤害（暴击照样触发，倍数按 1 算） */
      const critNegated = isCrit && !!derived.critImmune;
      const critMult = (isCrit && !critNegated)
        ? Math.max(1, 1.5 - derived.critReduce / 100) : 1;

      // 「防御」姿态：受到的伤害 -35%，另有 5% 几率完全格挡
      if (defending) {
        if (Math.random() * 100 < defendBlockNow()) {
          addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 152, '完全格挡', '#9fd4ff');
          const rbD = applyBloodRebound();
          shieldWallProc();      // 盾墙：完全格挡同样触发「碎石崩裂」
          setBattleLog(who + '，你举盾完全格挡了这次攻击！' +
            (rbD ? '（气血反弹反噬 ' + rbD + ' 点）' : ''));
          pushBattleLog(who + '：被你完全格挡（' + defendBlockNow() + '% 几率' +
            (defendSpiritPowered ? '·灵力护盾' : '') + '）', 'player');
          if (rbD && activeEnemy && activeEnemy.hp <= 0) return;
          if (shieldWallDealtKill()) return;
          battleTurn = 'player';
          resetPlayerIdleTimer();
          setControls('battle');
          updateBattleHud();
          return;
        }
        raw = Math.max(1, Math.round(raw * (1 - defendReduceNow())));
      }
      const dmg = Math.max(1, Math.round(raw * passive.mult * critMult));
      /* 盾牌词缀「将所受伤害的 X% 吸纳为生命」：先记账，之后 4 秒内逐跳回到身上 */
      if (derived.recoupPct > 0) addRecoup(Math.round(dmg * derived.recoupPct / 100));
      /* 「忠诚（辅助）」：对你造成的伤害由被辅助的伙伴的生命承受。
       * 伙伴被打空之后，溢出的那部分才算到你头上（不然一只宠物就是无敌盾）。 */
      let playerDmg = dmg;
      let loyalTaken = 0, loyalDown = false, loyalName = '';
      const loyal = loyaltyPet();
      if (loyal) {
        loyalName = loyal.name;
        loyalTaken = Math.max(1, Math.round(dmg * minionGemMods(loyal).takenMult));
        const overflow = Math.max(0, loyalTaken - Math.max(0, loyal.hp));
        loyal.hp -= loyalTaken;
        loyal.hurtT = HURT_TIME;
        addDamagePopup(
          petScreenX(Math.max(0, pets.indexOf(loyal))),
          GROUND_Y - 86,
          loyalTaken,
          '#ffd7a0'
        );
        if (loyal.hp <= 0) {
          loyal.hp = 0;
          pets.splice(pets.indexOf(loyal), 1);
          recalcPlayerStats();
          if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
          loyalDown = true;
        }
        playerDmg = overflow;
      }
      if (playerDmg > 0) {
        combat.hp = Math.max(0, combat.hp - playerDmg);   // 允许被打到 0（触发阵亡）
        player.hurtT = HURT_TIME;
        addDamagePopup(
          player.worldX - cameraX() - 88,
          GROUND_Y - 148,
          playerDmg,
          isCrit ? '#ff4d4d' : '#ff8f72'
        );
      }
      // 荆棘反伤：被攻击时把荆棘伤害反弹给敌人
      let reflect = 0;
      let thornsCrit = false;
      if (derived.thorns > 0 && activeEnemy) {
        reflect = derived.thorns;
        /* 荆皮护靴：荆棘暴击几率 +50%（触发则 1.5 倍） */
        if (derived.thornsCritPct > 0 && Math.random() * 100 < derived.thornsCritPct) {
          reflect = Math.round(reflect * 1.5);
          thornsCrit = true;
        }
        activeEnemy.hp -= reflect;
        activeEnemy.hurtT = HURT_TIME;
        addDamagePopup(
          activeEnemy.x - cameraX() + 88,
          GROUND_Y - 200,
          reflect,
          thornsCrit ? '#ff5a2a' : '#ffb066'
        );
        if (activeEnemy.hp <= 0) {
          activeEnemy.hp = 0;
          setBattleLog('荆棘反伤 ' + reflect + ' 点' + (thornsCrit ? '（荆棘暴击）' : '') +
            '，敌人被反伤击败！');
          updateBattleHud();
          if (!resolveEnemyDown()) return;
          pendingVictory = 0.5;
          battleTurn = 'player';
          resetPlayerIdleTimer();
          setControls('battle');
          return;
        }
      }
      // 气血反弹（持续生效）：每次被敌人攻击都会反噬
      const rebound = applyBloodRebound();
      if (rebound && activeEnemy && activeEnemy.hp <= 0) return;
      setBattleLog(
        who + (critNegated ? '（暴击·被冠军胸甲免疫额外伤害）' : isCrit ? '（暴击）' : '') +
        elemNote +
        (loyalTaken
          ? '，「' + loyalName + '」替你承受了 ' + loyalTaken + ' 点伤害' +
            (loyalDown ? '（' + loyalName + ' 倒下了！）' : '') +
            (playerDmg > 0 ? '，溢出 ' + playerDmg + ' 点落到你身上。' : '。')
          : '，你损失 ' + playerDmg + ' 点生命。') +
        (defending ? '（防御减伤 ' + Math.round(defendReduceNow() * 100) + '%' +
          (defendSpiritPowered ? '·灵力护盾' : '') + '）' : '') +
        (chargingVuln ? '（蓄力中受伤 +' + POWER_SHOT_VULN_PCT + '%）' : '') +
        (reflect ? '（荆棘反伤 ' + reflect + ' 点' + (thornsCrit ? '·荆棘暴击' : '') + '）' : '') +
        (rebound ? '（气血反弹反噬 ' + rebound + ' 点）' : '')
      );
      // 洞府敌人增幅：命中后的附加效果（震慑 / 点燃 / 寒冰）
      // 「忠诚」把整下伤害都替玩家吃掉了的话，这一击的控制效果也不该落到玩家身上
      if (playerDmg > 0) caveEnemyOnHit(activeEnemy);
    }
    battleTurn = 'player';
    resetPlayerIdleTimer();
    setControls('battle');
    updateBattleHud();

    // 其余敌人的围攻统一在 enemyHelpersTurn() 里处理（由外层 enemyCounterAttack 调用）
  }

  // ---------- “防御”护盾特效 ----------
  // 资源：pipo-btleffect208（192×192，20 帧）
  // 触发点：点击右下角「防御」→ 护盾在角色身上撑开，防御姿态期间一直裹着角色，
  //         敌方回合结束（defending 变 false）后收盾（原淡蓝护盾光弧已移除）
  let defendFx = null; // { t, phase: 'in' | 'hold' | 'out' }
  /* 结束一场战斗时统一收掉"防御姿态 + 护盾特效"：
   *   defending 为 true 时护盾会一直停在 hold 帧（看着像特效卡住），
   *   所以每一处"战斗结束"都必须调它 —— 野外胜利 / 真人交锋结束 / 阵亡 / 离开洞府。 */
  function clearDefendState() {
    defending = false;
    defendFx = null;
    defendSpiritPowered = false;   // 灵力护盾的状态跟着防御姿态一起收
  }

  function defendSpriteReady() {
    const first = images.defend_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  function defendLoadedFrames() {
    let n = 0;
    for (let i = 1; i <= DEFEND_FX_FRAMES; i++) {
      const im = images['defend_' + i];
      if (im && im.complete && im.naturalWidth > 0) n++;
    }
    return n;
  }

  function startDefendFx() {
    defendFx = { t: 0, phase: 'in' };
  }

  function updateDefendFx(dt) {
    if (!defendFx) return;
    defendFx.t += dt;
    if (defendFx.phase === 'in') {
      // 撑开播完：还在防御就转"维持"，防御已经结束就直接收盾
      if (defendFx.t >= DEFEND_FX_IN_DUR) {
        defendFx.phase = defending ? 'hold' : 'out';
        defendFx.t = 0;
      }
    } else if (defendFx.phase === 'hold') {
      // 防御姿态结束（敌方回合打完 / 敌人被控住没出手）→ 收盾
      if (!defending) {
        defendFx.phase = 'out';
        defendFx.t = 0;
      }
    } else if (defendFx.t >= DEFEND_FX_OUT_DUR) {
      defendFx = null;
    }
  }

  // 按阶段取当前该画第几帧（收盾阶段也用于整体淡出）
  function defendFxFrameNo() {
    if (!defendFx) return 0;
    if (defendFx.phase === 'in') {
      const k = Math.max(0, Math.min(1, defendFx.t / DEFEND_FX_IN_DUR));
      return Math.min(DEFEND_FX_IN_TO, DEFEND_FX_IN_FROM + Math.floor(k * (DEFEND_FX_IN_TO - DEFEND_FX_IN_FROM + 1)));
    }
    if (defendFx.phase === 'hold') {
      const span = DEFEND_FX_HOLD_TO - DEFEND_FX_HOLD_FROM + 1;
      return DEFEND_FX_HOLD_FROM + (Math.floor(defendFx.t * DEFEND_FX_HOLD_FPS) % span);
    }
    const k = Math.max(0, Math.min(1, defendFx.t / DEFEND_FX_OUT_DUR));
    return Math.min(DEFEND_FX_OUT_TO, DEFEND_FX_OUT_FROM + Math.floor(k * (DEFEND_FX_OUT_TO - DEFEND_FX_OUT_FROM + 1)));
  }

  // 贴图按"盾面重心对齐"摆放：盾心钉在角色身上，护盾把角色整个裹住。
  // 贴图中心是不透明的实心光团，所以分两层画：角色后面一层完整的（当背景光罩），
  // 角色前面再盖一层半透明的 —— 角色始终看得见，但整体被护盾"包"住。
  const DEFEND_FX_FRONT_ALPHA = 0.34;

  function drawDefendFxLayer(alphaScale, withTrace) {
    if (!defendFx || !defendSpriteReady()) return;
    const idx = defendFxFrameNo();
    const img = images['defend_' + idx];
    if (!img) return;
    const scale = playerFx.scale * caveScale();
    const size = DEFEND_FX_SIZE * scale;
    const sx = player.worldX - cameraX();
    const ax = sx;                                    // 护盾圆心 = 角色身上（不再偏向敌人一侧）
    const ay = GROUND_Y - DEFEND_FX_LIFT * scale;     // 胸腹之间
    const dx = ax - DEFEND_FX_ORIGIN.x * size;
    const dy = ay - DEFEND_FX_ORIGIN.y * size;
    ctx.save();
    ctx.imageSmoothingEnabled = false;                // 像素风：不插值
    // 收盾阶段整体淡出，避免"啪"地消失
    const fade = defendFx.phase === 'out'
      ? Math.max(0, 1 - Math.min(1, defendFx.t / DEFEND_FX_OUT_DUR) * 0.85)
      : 1;
    ctx.globalAlpha = playerFx.alpha * fade * alphaScale;
    if (DEFEND_FX_FLIP) {
      // 水平镜像：护盾从角色前方撑开
      ctx.translate(dx + size, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, size, size);
    } else {
      ctx.drawImage(img, dx, dy, size, size);
    }
    if (withTrace && fxTrace.length < 600) {
      fxTrace.push({
        kind: 'defend', t: Math.round(defendFx.t * 1000), phase: defendFx.phase, frame: idx,
        flip: DEFEND_FX_FLIP,
        x: Math.round(dx), y: Math.round(dy), size: Math.round(size),
        anchorX: Math.round(ax), anchorY: Math.round(ay)
      });
    }
    ctx.restore();
  }

  // 画在角色之后（背景光罩）
  function drawDefendFxBehind() {
    drawDefendFxLayer(1, true);
  }

  // 画在角色之前（薄薄一层，做出"被护盾裹住"的感觉，同时不遮住角色）
  function drawDefendFxFront() {
    drawDefendFxLayer(DEFEND_FX_FRONT_ALPHA, false);
  }

  // ---------- “破空斩”剑气特效 ----------
  // 资源：HolySlash_B（64×64，4 帧红色弧形剑气）
  // 表现：连放 2 道 → 向前扩散 → 飞到屏幕最右侧边界淡出消失
  let airSlashFx = null; // { waves: [{ t, big }] }

  /* ---------- 「盾墙 · 碎石崩裂」碎石特效 ---------- */
  let rockFx = null;     // { rocks: [{ t, yOff, size, spin }] }

  function startRockFx() {
    const id = 'shieldWall';
    const count = projectileCountOf(id, ROCK_FX_COUNT);      // 多重投射物：5 → 7 块
    const speedMult = projectileSpeedMult(id);               // 投射物加速：碎石飞得更快
    const rocks = [];
    for (let i = 0; i < count; i++) {
      rocks.push({
        t: -i * 0.045,                                   // 一块接一块喷出去
        yOff: rand(-26, 14),                             // 上下散开
        size: ROCK_FX_SIZE * rand(0.7, 1.15),
        spin: rand(-0.5, 0.5),                           // 轻微翻滚
        speedMult: speedMult
      });
    }
    rockFx = { rocks: rocks };
  }

  // 一块碎石当前在屏幕上的 x（战斗中镜头不动）
  function rockScreenX(r) {
    return player.worldX + ROCK_FX_START + ROCK_FX_SPEED * (r.speedMult || 1) * r.t - cameraX();
  }

  function updateRockFx(dt) {
    if (!rockFx) return;
    const list = rockFx.rocks;
    for (let i = list.length - 1; i >= 0; i--) {
      const r = list[i];
      r.t += dt;
      if (r.t < 0) continue;
      if (rockScreenX(r) - r.size > W + 40) list.splice(i, 1);
    }
    if (!list.length) rockFx = null;
  }

  function rockSpriteReady() {
    for (let i = 1; i <= ROCK_FX_FRAMES; i++) {
      const im = images['rock_' + i];
      if (!im || !im.complete || im.naturalWidth <= 0) return false;
    }
    return true;
  }

  function drawRockFx() {
    if (!rockFx || !rockSpriteReady()) return;
    const scale = caveScale();
    rockFx.rocks.forEach(function (r) {
      if (r.t < 0) return;
      const idx = Math.floor(r.t * ROCK_FX_FPS) % ROCK_FX_FRAMES;
      const img = images['rock_' + (idx + 1)];
      if (!img) return;
      const size = r.size * scale;
      const sx = rockScreenX(r);
      const cy = GROUND_Y - 96 * scale + r.yOff * scale;
      const fadeIn = Math.min(1, r.t / 0.05);
      const edgeFade = sx > W - 120 ? Math.max(0, 1 - (sx - (W - 120)) / 120) : 1;
      ctx.save();
      ctx.imageSmoothingEnabled = false;
      ctx.globalAlpha = playerFx.alpha * fadeIn * edgeFade;
      ctx.translate(sx, cy);
      ctx.rotate(r.spin * r.t);
      ctx.drawImage(img, -size / 2, -size / 2, size, size);
      ctx.restore();
      if (fxTrace.length < 600) {
        fxTrace.push({
          kind: 'rock', t: Math.round(r.t * 1000), frame: idx + 1,
          x: Math.round(sx - size / 2), y: Math.round(cy - size / 2), size: Math.round(size),
          centerX: Math.round(sx), centerY: Math.round(cy)
        });
      }
    });
  }

  function airSlashSpriteReady() {
    const first = images.holyslash_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  // 出招：第一道立刻出发，后续几道依次跟上（t 为负数表示还没出场）
  // 辅助技能石：「多重投射物」+2 道、出手间隔 +25%（技能速度 -20%）；「投射物加速」飞行速度 ×1.4
  // 多投射物时按"齐射"处理：几道几乎同时出发、上下散开成扇形（不然间隔太大，屏幕上一次只看得到 1~2 道）
  function airSlashWaveGap(count) {
    const n = count || projectileCountOf('airSlash', AIRSLASH_FX_WAVES);
    const volley = n > AIRSLASH_FX_WAVES;
    return (volley ? AIRSLASH_FX_GAP * 0.5 : AIRSLASH_FX_GAP) / skillSpeedMult('airSlash');
  }

  function startAirSlashFx() {
    const id = 'airSlash';
    const count = projectileCountOf(id, AIRSLASH_FX_WAVES);
    const speedMult = projectileSpeedMult(id);
    const gap = airSlashWaveGap(count);
    // 多道剑气时稍微上下散开，看起来才像扇形扩散
    const spread = [-18, 6, -8, 14, -12, 10];
    const waves = [];
    for (let i = 0; i < count; i++) {
      waves.push({
        t: -i * gap,
        big: i > 0,
        speedMult: speedMult,
        yOff: count > AIRSLASH_FX_WAVES ? spread[i % spread.length] : (i > 0 ? 6 : 0)
      });
    }
    airSlashFx = { waves: waves };
  }

  // 剑气当前在屏幕上的 x（世界坐标 → 屏幕坐标，战斗中镜头不动）
  function airSlashWaveScreenX(w) {
    return player.worldX + AIRSLASH_FX_START + AIRSLASH_FX_SPEED * (w.speedMult || 1) * w.t - cameraX();
  }

  function updateAirSlashFx(dt) {
    if (!airSlashFx) return;
    const waves = airSlashFx.waves;
    for (let i = waves.length - 1; i >= 0; i--) {
      const w = waves[i];
      w.t += dt;
      if (w.t < 0) continue;                       // 还在"连续释放"的间隔里
      // 冲到屏幕最右侧边界之外就回收（视觉上是在右边界处淡出消失）
      if (airSlashWaveScreenX(w) - AIRSLASH_FX_SIZE * 0.5 > W + 30) waves.splice(i, 1);
    }
    if (!waves.length) airSlashFx = null;
  }

  function drawAirSlashFx() {
    if (!airSlashFx || !airSlashSpriteReady()) return;
    const scale = caveScale();
    const cy = GROUND_Y - AIRSLASH_FX_LIFT * scale;
    airSlashFx.waves.forEach(function (w) {
      if (w.t < 0) return;
      const size = AIRSLASH_FX_SIZE * scale * (w.big ? 1.12 : 1);
      const sx = airSlashWaveScreenX(w);
      const cyW = cy + (w.yOff || 0) * scale;
      const idx = Math.floor(w.t * AIRSLASH_FX_FPS) % AIRSLASH_FX_FRAMES;
      const img = images['holyslash_' + (idx + 1)];
      if (!img) return;
      // 起手快速淡入 + 抵达右边界前淡出
      const fadeIn = Math.min(1, w.t / 0.06);
      const edgeFade = sx > W - 150 ? Math.max(0, 1 - (sx - (W - 150)) / 150) : 1;
      ctx.save();
      ctx.imageSmoothingEnabled = false;          // 像素风：不插值
      ctx.globalAlpha = playerFx.alpha * fadeIn * edgeFade;
      ctx.drawImage(img, sx - size / 2, cyW - size / 2, size, size);
      ctx.restore();
      if (fxTrace.length < 600) {
        fxTrace.push({
          kind: 'airslash', t: Math.round(w.t * 1000), frame: idx + 1,
          x: Math.round(sx - size / 2), y: Math.round(cyW - size / 2), size: Math.round(size),
          centerX: Math.round(sx), centerY: Math.round(cyW)
        });
      }
    });
  }

  // ---------- “劈砍”特效 ----------
  // 刀光资源：itch.io「Pixel Art Animations - Slashes」的红色 9 帧刀光（128×128）
  const SLASH_FRAMES = 9;
  const SLASH_SPRITE_SIZE = 380; // 屏幕上的绘制尺寸（像素风放大）
  // 「劈砍」刀光贴图中心相对地面的高度：数值越小越靠下。
  // 该贴图内刀光的可视重心约在竖直 48% 处，中心压到 84px 时刀身正好横过敌人胸腹。
  const SLASH_SPRITE_LIFT = 84;
  function slashSpriteReady() {
    const first = images.slash1_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  /* ------------------------------------------------------------
   * 普通攻击的"红色斜向刀光"已按需求移除（2026-10-04）：
   *   以前普通攻击会在敌人身上叠一张 Slash 3 贴图，看起来就是一道红色斜杠。
   *   现在只剩角色自身的出手动作（playPlayerAttack），不再有这层刀光。
   *   · startAttackFx 保留成空实现：两个调用点（普通攻击 / 真人交锋普攻）不用改
   *   · 贴图仍然随资源清单加载，__debugAttackSprite 可以查它有没有加载成功
   *   · attackFx 永远是 null，__debugFxRects().attackFx 也是 null（用例据此校验"已移除"）
   * ------------------------------------------------------------ */
  let attackFx = null;
  const ATTACK_FX_DUR = 0.42;   // 仅保留给调试钩子看尺寸/位置，不再驱动任何绘制
  const ATTACK_SPRITE_SIZE = 200; // 普通攻击刀光（Slash 3，64×64 放大）
  // 刀光贴图中心相对地面的高度：数值越小越靠下。
  // 贴图内刀光的可视重心在竖直方向约 30% 处（偏上），所以中心要压到敌人胸口以下，
  // 可视部分才会正好落在敌人身上（约 52px ≈ 贴图中心落在膝盖~腰部之间，刀身横过胸腹）
  const ATTACK_SPRITE_LIFT = 52; // 普通攻击：刀光直接砍在敌人身上
  function attackSpriteReady() {
    const first = images.slash3_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }
  // 普通攻击刀光：已移除（保留空实现，调用点不用改）
  function startAttackFx() { return null; }

  // 刀光路径（资源缺失时的兜底）：以敌人胸腹为落点，从左上高处一路劈下。
  // 用三次贝塞尔曲线采样，程序化绘制 = 免费自绘特效，无第三方许可负担。
  function slashCurvePoint(u) {
    const inv = 1 - u;
    const x =
      -105 * inv * inv * inv +
      3 * 20 * inv * inv * u +
      3 * 85 * inv * u * u +
      42 * u * u * u;
    const y =
      -265 * inv * inv * inv +
      3 * (-240) * inv * inv * u +
      3 * (-45) * inv * u * u +
      6 * u * u * u;
    return { x: x, y: y };
  }

  function spawnSlashParticles(fx) {
    const baseX = fx.enemyWx - cameraX();
    const baseY = GROUND_Y - 90; // 与刀光的落点一致：火花从敌人胸腹处迸出
    for (let i = 0; i < 18; i++) {
      const angle = rand(0, Math.PI * 2);
      const speed = rand(55, 255);
      fx.particles.push({
        x: baseX + rand(-10, 18),
        y: baseY + rand(-8, 10),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 65,
        life: rand(0.32, 0.68),
        t: 0,
        size: rand(2, 5.5),
        color: ['#ff3b2a', '#ff6a3c', '#ffd6a0'][randInt(0, 2)]
      });
    }
  }

  // 刀光劈落并命中：在这里结算伤害
  function weaponAttackPower() {
    const w = equipment.weapon;
    return Math.max(1, Math.round((w && w.atk) || derived.flatAtk || 1));
  }
  function slashWeaponDamageBase(skill) {
    const lvMult = skillDamageLevelMult(skill);
    return Math.max(1, Math.round(
      weaponAttackPower() * 0.75 * lvMult *
      gemDamageMult('slash') * supportDamageMult('slash', activeEnemy) * spiritSkillMult()
    ));
  }
  function slashImpact() {
    if (!activeEnemy) {
      slashFx = null;
      return;
    }
    const doubleHit = Math.random() * 100 < SLASH_DOUBLE_PCT;
    const baseSlashDmg = slashWeaponDamageBase(skillById('slash')) * (doubleHit ? 2 : 1);
    const slashHit = rollPlayerDamage(baseSlashDmg, activeEnemy);
    const dmg = slashHit.dmg;
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    gloveOnHit(activeEnemy);            // 手套词缀：劈砍命中
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 168,
      dmg,
      slashHit.crit ? '#ff2b2b' : '#ff402a'
    );
    /* 触发双倍伤害时在战斗场景里单独飘一条「双倍效果」（免得只靠数字看不出双倍） */
    if (doubleHit) {
      addTextPopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 206, '双倍效果', '#ffd76a');
    }
    maybeApplyBurn(activeEnemy, 'slash');   // 炎军：劈砍命中
    spawnSlashParticles(slashFx);
    setBattleLog('「劈砍」的血刀劈中身体，造成 ' + dmg + ' 点伤害' +
      (doubleHit ? '（双倍效果）' : '') + (slashHit.crit ? '（暴击）' : '') + '！');
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「劈砍」化作一道血光，击败了拦路者！');
      updateBattleHud();
      if (!resolveEnemyDown()) {
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      pendingVictory = 0.5;
      return;
    }
    afterPlayerOffense();
    updateBattleHud();
  }

  function updateSlashFx(dt) {
    if (!slashFx) return;
    slashFx.t += dt;

    if (!slashFx.hit && slashFx.t >= slashFx.impactAt) {
      slashFx.hit = true;
      slashImpact();
    }

    // 命中火花自行飘散
    for (let i = slashFx.particles.length - 1; i >= 0; i--) {
      const p = slashFx.particles[i];
      p.t += dt;
      p.vy += 420 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.t >= p.life) slashFx.particles.splice(i, 1);
    }

    if (slashFx.t >= slashFx.dur) slashFx = null;
  }

  // 画“从刀柄到刀尖”的当前一段刀光（随劈落进度延伸）
  function strokeSlashPath(ex, hy, progress, width, color, alpha) {
    const N = 26;
    const raw = Math.max(0.005, progress);
    const k = (N - 1) * Math.min(1, raw);
    const i = Math.floor(k);
    const frac = k - i;
    const end = slashCurvePoint((i + Math.min(1, frac + 0.001)) / (N - 1));
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let n = 0; n <= Math.min(i, N - 1); n++) {
      const pt = slashCurvePoint(n / (N - 1));
      const x = ex + pt.x;
      const y = hy + pt.y;
      if (n === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    if (i < N - 1 && i >= 0) {
      ctx.lineTo(ex + end.x, hy + end.y);
    }
    ctx.stroke();
    ctx.restore();
  }

  function drawSlashFx() {
    if (!slashFx) return;
    const ex = slashFx.enemyWx - cameraX();
    const hy = GROUND_Y - 96; // 兜底刀光的锚点：刀尖落在敌人胸腹（贴图资源正常时走贴图分支）
    const p = Math.min(1, slashFx.t / slashFx.dur);
    const impactRatio = slashFx.impactAt / slashFx.dur;
    const cutP = Math.min(1, slashFx.t / slashFx.impactAt);
    const impactAge = slashFx.hit
      ? (slashFx.t - slashFx.impactAt) / (slashFx.dur - slashFx.impactAt)
      : 0;

    // 整体淡入 + 收尾淡出
    const fadeIn = Math.min(1, slashFx.t / 0.08);
    const fadeOut = slashFx.hit
      ? Math.max(0, Math.min(1, 1.25 - impactAge * 1.1)) // 收刀后仍保留一会儿，让刀光看得清
      : 1;
    const trailAlpha = Math.max(0, Math.min(1, fadeIn * fadeOut));
    if (trailAlpha <= 0.01 && slashFx.particles.length === 0) return;

    // ① 刀光本体：优先用 itch 的像素刀光资源（9 帧循环播完一次）
    if (slashSpriteReady()) {
      const idx = Math.min(SLASH_FRAMES - 1, Math.floor(p * SLASH_FRAMES));
      const img = images['slash1_' + (idx + 1)];
      // 每一帧的亮部大小不同，后段略微放大一点，收刀更利落
      const size = SLASH_SPRITE_SIZE * (1 + p * 0.12);
      ctx.save();
      ctx.imageSmoothingEnabled = false; // 像素风：不插值
      ctx.globalAlpha = trailAlpha;
      ctx.drawImage(
        img,
        ex - size * 0.52,
        GROUND_Y - SLASH_SPRITE_LIFT - size * 0.5,
        size,
        size
      );
      if (fxTrace.length < 600) {
        fxTrace.push({ kind: 'slash', t: Math.round(slashFx.t * 1000), frame: idx + 1, x: Math.round(ex - size * 0.52), y: Math.round(GROUND_Y - SLASH_SPRITE_LIFT - size * 0.5), size: Math.round(size), enemyX: Math.round(ex), enemyTop: Math.round(GROUND_Y - 178 * enemyDrawScale(activeEnemy) * caveScale() + 4) });
      }
      ctx.restore();
    } else {
      // 兜底：资源没加载出来时用原来程序化绘制的血刀
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      strokeSlashPath(ex, hy, cutP, 34, 'rgba(255, 45, 20, .26)', trailAlpha);
      strokeSlashPath(ex, hy, cutP, 16, 'rgba(255, 92, 42, .58)', trailAlpha);
      strokeSlashPath(ex, hy, cutP, 4.5, 'rgba(255, 238, 218, .78)', trailAlpha);
      if (cutP < 1) {
        const tip = slashCurvePoint(Math.max(0.04, cutP));
        const next = slashCurvePoint(Math.min(1, cutP + 0.035));
        const dx = next.x - tip.x;
        const dy = next.y - tip.y;
        const len = Math.max(0.001, Math.hypot(dx, dy));
        const g = ctx.createRadialGradient(
          ex + tip.x, hy + tip.y, 0,
          ex + tip.x, hy + tip.y, 26
        );
        g.addColorStop(0, 'rgba(255, 245, 235, .95)');
        g.addColorStop(0.28, 'rgba(255, 128, 72, .75)');
        g.addColorStop(1, 'rgba(255, 45, 20, 0)');
        ctx.globalAlpha = trailAlpha;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(ex + tip.x, hy + tip.y, 20, 12, Math.atan2(dy, dx), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.8 * trailAlpha;
        ctx.strokeStyle = '#fff4e0';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(ex + tip.x - dx / len * 22, hy + tip.y - dy / len * 22);
        ctx.lineTo(ex + tip.x, hy + tip.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // 命中瞬间的红色闪光
    if (slashFx.hit) {
      // 命中闪光收敛一些，避免盖住刀光本体
      const flash = Math.max(0, 1 - impactAge * 4) * 0.62;
      const fx = ex;
      const fy = GROUND_Y - 92; // 命中闪光跟着刀光落在敌人身上
      const radius = 28 + impactAge * 66;
      const fg = ctx.createRadialGradient(fx, fy, 0, fx, fy, radius);
      fg.addColorStop(0, 'rgba(255, 250, 240, ' + (1.1 * flash).toFixed(3) + ')');
      fg.addColorStop(0.22, 'rgba(255, 105, 55, ' + (0.75 * flash).toFixed(3) + ')');
      fg.addColorStop(1, 'rgba(255, 30, 15, 0)');
      ctx.globalAlpha = 1;
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.arc(fx, fy, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // 红色火花（普通叠加，保证醒目且不依赖混合模式）
    for (let i = 0; i < slashFx.particles.length; i++) {
      const sp = slashFx.particles[i];
      const a = Math.max(0, 1 - sp.t / sp.life);
      ctx.save();
      ctx.globalAlpha = a;
      ctx.fillStyle = sp.color;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, sp.size * (0.55 + a * 0.45), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = a * 0.25;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, sp.size * 0.38 * a, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // ---------- 回合顺序 / 受击 / 伤害数字 / 击败演出 ----------
  function updateTurnTimer(dt) {
    /* 真人神识交锋：回合与血量都由服务端记账，本地既不跑敌方 AI、也不会自己判死 */
    if (pvpDuel) {
      combat.hp = Math.max(1, pvpDuel.myHp);
      if (activeEnemy) activeEnemy.hp = Math.max(1, pvpDuel.foeHp);
      return;
    }
    if (gameState !== 'battle' || battleTurn !== 'enemy' || pendingVictory > 0) return;
    if (worldBossLootActive()) return;   // BOSS已倒下、正在开箱阶段：敌人不再出手
    if (!activeEnemy) return;
    enemyTurnTimer -= dt;
    if (enemyTurnTimer <= 0) {
      enemyTurnTimer = 0;
      const opening = activeEnemy.openingAttack === true;
      activeEnemy.openingAttack = false;
      enemyCounterAttack(opening);
    }
  }

  function updateDamageFx(dt) {
    enemyAnimClock += dt; // 敌人帧动画时钟
    // 新加入的敌人小跑到阵位（当前敌人身后）
    battleGroup.forEach(function (e) {
      if (e.moveToX === undefined) return;
      e.x += (e.moveToX - e.x) * Math.min(1, dt * 6);
      if (Math.abs(e.moveToX - e.x) < 2) {
        e.x = e.moveToX;
        delete e.moveToX;
      }
    });
    // 震动与血色划痕倒计时
    if (player.hurtT > 0) player.hurtT = Math.max(0, player.hurtT - dt);
    // 敌人受击：除抖动外还要"被打退一小段再回原位"（同伴敌人也一起走计时）
    tickEnemyKnock(activeEnemy, dt);
    battleGroup.forEach(function (e) { if (e !== activeEnemy) tickEnemyKnock(e, dt); });
    // 「森特欧斯的囚笼」：控制时间是真实秒数，到点自动解除
    if (activeEnemy && activeEnemy.stunT > 0) {
      activeEnemy.stunT = Math.max(0, activeEnemy.stunT - dt);
    }
    updateCageFx(dt);
    updateTotemFx(dt);
    pets.forEach(function (p) {
      if (p.hurtT > 0) p.hurtT = Math.max(0, p.hurtT - dt);
      if (p.hp > 0) {
        p.walkT += dt * 8;
        p.animT = (p.animT || 0) + dt;          // 精灵图播放进度
        if (p.attackT > 0) p.attackT = Math.max(0, p.attackT - dt);
      }
    });

    // 伤害数字向上飘散
    for (let i = damagePopups.length - 1; i >= 0; i--) {
      const p = damagePopups[i];
      if (p.delay > 0) { p.delay -= dt; continue; }   // 还没到出现时间（尾随箭矢的飘字）
      p.t += dt;
      p.y -= 58 * dt;
      if (p.t >= p.life) damagePopups.splice(i, 1);
    }

    // 最后一击先展示 0 血与受击反馈，稍后再关闭战斗
    if (pendingVictory > 0 && gameState === 'battle') {
      pendingVictory -= dt;
      if (pendingVictory <= 0) {
        pendingVictory = 0;
        winBattle();
      }
    }
  }

  // ---------- 更新（走路状态） ----------
  function updateEnemyPatrol(dt) {
    if (!activeEnemy) return;
    activeEnemy.walkT += dt * 9;
    activeEnemy.offset += activeEnemy.dir * activeEnemy.speed * dt;
    if (activeEnemy.offset >= activeEnemy.range) {
      activeEnemy.offset = activeEnemy.range;
      activeEnemy.dir = -1;
    } else if (activeEnemy.offset <= -activeEnemy.range) {
      activeEnemy.offset = -activeEnemy.range;
      activeEnemy.dir = 1;
    }
    activeEnemy.x = activeEnemy.baseX + activeEnemy.offset;
  }

  function update(dt) {
    if (gameState !== 'walk') return;
    if (caveTransition) return; // 进洞过场期间不响应移动输入
    /* 临时副本里用"点屏幕左右"移动，不走这套"移动按钮 + 单向前进"的逻辑；
     * 副本的推进统一在 updateActivityRun() 里做。 */
    if (activityRun) {
      updateActivityRun(dt);
      return;
    }
    // 门派山门：同一条路的走法，只是被限制在山门范围内（不刷怪 / 不刷洞府）
    if (sectScene) {
      updateSectSceneWalk(dt);
      return;
    }

    // 敌人始终在附近左右巡逻
    updateEnemyPatrol(dt);

    // 点击一次移动按钮后自动前进；再次点击或进入战斗即停下（鞋子可提高移速）
    const target = input.moving ? WALK_SPEED * derived.speedMult : 0;
    // 起步稍缓、收步干脆一点：按了「休息」要很快站住，别拖着走半秒
    const ease = input.moving ? 8 : 14;
    player.vx += (target - player.vx) * Math.min(1, dt * ease);
    if (Math.abs(player.vx) < 1) player.vx = 0;

    if (activeEnemy) {
      const limit = activeEnemy.x - ENEMY_GAP_PX;
      if (player.vx > 0 && player.worldX < limit) {
        player.worldX = Math.min(limit, player.worldX + player.vx * dt);
      }
      // 靠近 / 敌人巡逻靠近时开战
      if (player.worldX >= limit) {
        startBattle();
        return;
      }
    } else if (!cave && !worldBossRun) {
      /* 路上没有敌人时（刚打完一场、或"灵脉定位"传送落地后的空档）：
       *   ① 先保证玩家能正常前进 —— 以前这里没有 else 分支，worldX 根本不推进，
       *      表现就是"只能原地跑、走不动"；
       *   ② 顺手把下一个拦路者刷到前方，别让玩家一路空跑。 */
      if (player.vx > 0) player.worldX += player.vx * dt;
      spawnNextEnemy();
    }

    if (player.vx > 0) {
      player.walkT += dt * (8 + player.vx / 50);
    } else {
      player.walkT = 0;
    }

    updateXiuLian(dt);      // 天道·仙人：停下休息时吸纳天地灵力（同时驱动那套环境特效）
    updateLing(dt);         // 灵脉仙人掌：蹭仙人掌吸灵 + 抽出灵气
    hudTimer += dt;
    if (hudTimer >= 0.1) {
      hudTimer = 0;
      updateDist();
      if (legendMap) {
        legendMap.km = Math.max(0, (player.worldX - legendMap.startX) / KM_UNIT);
        updateLegendBar();
      }
    }
  }

  // ---------- 绘制：天空 ----------
  function drawSky() {
    const g = ctx.createLinearGradient(0, 0, 0, 540);
    g.addColorStop(0, '#cfe8f5');
    g.addColorStop(0.42, '#f8e6bd');
    g.addColorStop(0.72, '#f6cd94');
    g.addColorStop(1, '#e9aa70');
    ctx.fillStyle = g;
    ctx.fillRect(-30, -30, W + 60, 640);

    const sx = SUN_X, sy = SUN_Y;
    // 太阳外围的柔和光晕
    const halo = ctx.createRadialGradient(sx, sy, 20, sx, sy, 320);
    halo.addColorStop(0, 'rgba(255, 242, 208, .30)');
    halo.addColorStop(0.45, 'rgba(255, 238, 196, .12)');
    halo.addColorStop(1, 'rgba(255, 238, 196, 0)');
    ctx.fillStyle = halo;
    ctx.fillRect(sx - 330, sy - 330, 660, 660);

    const glow = ctx.createRadialGradient(sx, sy, 10, sx, sy, 175);
    glow.addColorStop(0, 'rgba(255, 246, 216, .95)');
    glow.addColorStop(0.25, 'rgba(255, 232, 172, .55)');
    glow.addColorStop(1, 'rgba(255, 232, 172, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(sx - 190, sy - 190, 380, 380);
    ctx.fillStyle = '#fff6dc';
    ctx.beginPath();
    ctx.arc(sx, sy, 48, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 244, 224, .16)';
    for (let i = 0; i < 4; i++) {
      const cx = 110 + i * 320 + hash01(i) * 130;
      const cy = 80 + hash01(i + 8) * 120;
      ctx.beginPath();
      ctx.ellipse(cx, cy, 135 + hash01(i + 20) * 60, 11 + hash01(i + 4) * 8, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawDuneLayer(color, baseY, amp, wave, phase, cam) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-40, H + 60);
    for (let x = -40; x <= W + 40; x += 22) {
      const y = baseY +
        Math.sin((x + cam) / wave + phase) * amp +
        Math.sin((x + cam) / (wave * 0.45) + phase * 3.1) * amp * 0.26;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(W + 40, H + 60);
    ctx.closePath();
    ctx.fill();
  }

  function drawGround(cam) {
    const groundG = ctx.createLinearGradient(0, 560, 0, H);
    groundG.addColorStop(0, '#dda876');
    groundG.addColorStop(1, '#c88e5a');
    ctx.fillStyle = groundG;
    ctx.fillRect(-30, 560, W + 60, H - 560 + 40);

    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#8a5a33';
    for (let i = 0; i < 70; i++) {
      const span = 900;
      const gx = i * (span / 70) - cam;
      const k = Math.floor(gx / span);
      const x = ((gx - k * span) + span) % span - 20;
      const y = 580 + hash01(i + k * 7.3) * 140;
      const size = 1 + hash01(i * 2.1 + k) * 2.8;
      if (hash01(i + 1.7 * k) < 0.2) continue;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const roadG = ctx.createLinearGradient(0, 642, 0, H);
    roadG.addColorStop(0, '#a9885f');
    roadG.addColorStop(0.2, '#9a7b54');
    roadG.addColorStop(1, '#775a3b');
    ctx.fillStyle = roadG;
    ctx.fillRect(-30, 642, W + 60, H - 642 + 30);
    ctx.fillStyle = 'rgba(255, 226, 177, .14)';
    ctx.fillRect(-30, 638, W + 60, 7);

    ctx.save();
    for (let i = 0; i < 16; i++) {
      const span = 1100;
      const gx = i * (span / 16) - cam;
      const k = Math.floor(gx / span);
      const x = ((gx - k * span) + span) % span - 30;
      const seed = hash01(i * 3.17 + k * 9.1);
      const yy = 664 + hash01(i + k * 5.5) * 48;
      ctx.globalAlpha = 0.08 + seed * 0.15;
      ctx.strokeStyle = '#513b22';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x, yy);
      ctx.lineTo(x + 5 + seed * 8, yy + 3);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  function drawBackground(cam) {
    drawSky();
    drawDuneLayer('rgba(226, 177, 126, .9)', 392, 20, 330, 1.0, cam * 0.055);
    drawDuneLayer('rgba(214, 156, 104, .92)', 452, 27, 270, 2.2, cam * 0.12);
    drawDuneLayer('#cf9a69', 536, 22, 360, 4.0, cam * 0.21);
    drawGround(cam);
  }

  // ---------- 环境：远处沙暴 / 风沙 / 落叶 / 光影 ----------
  function initWeather() {
    sandFar.length = 0;
    sandNear.length = 0;
    leaves.length = 0;
    for (let i = 0; i < 70; i++) {
      sandFar.push({
        x: rand(0, W + 240),
        y: rand(300, 645),
        len: rand(10, 34),
        speed: rand(70, 170),
        alpha: rand(0.04, 0.13),
        w: rand(1, 2)
      });
    }
    for (let i = 0; i < 46; i++) {
      sandNear.push({
        x: rand(0, W + 240),
        y: rand(520, 720),
        len: rand(18, 52),
        speed: rand(220, 430),
        alpha: rand(0.06, 0.19),
        w: rand(1, 2.4)
      });
    }
    for (let i = 0; i < 10; i++) {
      leaves.push({
        x: rand(0, W + 240),
        y: rand(180, 600),
        vx: -rand(30, 90),
        vy: rand(8, 26),
        rot: rand(0, Math.PI * 2),
        vr: rand(-2.4, 2.4),
        size: rand(6, 12),
        tone: randInt(0, 2)
      });
    }
  }

  function updateWeather(dt) {
    weatherTime += dt;
    const span = W + 240;
    for (let i = 0; i < sandFar.length; i++) {
      const p = sandFar[i];
      p.x -= p.speed * dt;
      p.y += Math.sin(weatherTime * 1.6 + i) * 6 * dt;
      if (p.x < -60) p.x += span;
    }
    for (let i = 0; i < sandNear.length; i++) {
      const p = sandNear[i];
      p.x -= p.speed * dt;
      p.y += Math.sin(weatherTime * 2.4 + i * 1.7) * 10 * dt;
      if (p.x < -80) p.x += span;
    }
    for (let i = 0; i < leaves.length; i++) {
      const lf = leaves[i];
      lf.x += lf.vx * dt;
      lf.y += (lf.vy + Math.sin(weatherTime * 3 + i) * 14) * dt;
      lf.rot += lf.vr * dt;
      if (lf.x < -40) {
        lf.x = W + rand(20, 200);
        lf.y = rand(160, 560);
      }
      if (lf.y > 660) lf.y = rand(140, 300);
    }
  }

  // 远处的龙卷风（纯视觉表现，不影响任何玩法数值）
  function drawTornado(cam) {
    const span = W + 520;
    // 位置：太阳（SUN_X=995, SUN_Y=128）左下方的远处沙丘；
    // 缓慢随镜头左移，移出屏幕后从右侧循环出现
    const cx = wrapX(760 + 260 - cam * 0.05, span) - 260;
    const scale = 1.38;                             // 整体尺寸：原版 165 × 1.38 ≈ 228
    const baseY = 472;                              // 落在远处沙丘上
    const height = 165 * scale;
    const t = weatherTime;
    window.__tornado = { x: Math.round(cx), baseY: baseY, height: height }; // 调试用

    ctx.save();
    // 漏斗主体：下细上粗，逐层摆动 + 旋转，形成旋转感
    for (let i = 0; i < 9; i++) {
      const p = i / 8;                              // 0 = 底部，1 = 顶部
      const y = baseY - p * height;
      const x = cx + Math.sin(t * 1.3 + p * 2.4) * (4 + p * 14) * scale;
      const radius = (9 + p * 26) * scale;
      const spin = Math.sin(t * 2.6 + i * 1.1);
      ctx.globalAlpha = 0.36 - p * 0.11;
      const g = ctx.createLinearGradient(x - radius, 0, x + radius, 0);
      g.addColorStop(0, 'rgba(228, 198, 152, 0)');
      g.addColorStop(0.45 + spin * 0.12, 'rgba(248, 228, 192, .98)');
      g.addColorStop(1, 'rgba(228, 198, 152, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(x, y, radius, radius * 0.30, 0, 0, Math.PI * 2);
      ctx.fill();
      // 左侧稍暗，增加立体感
      ctx.globalAlpha = (0.20 - p * 0.06) * 0.7;
      ctx.fillStyle = 'rgba(150, 112, 68, 1)';
      ctx.beginPath();
      ctx.ellipse(x - radius * 0.28, y + radius * 0.04, radius * 0.52, radius * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // 底部扬尘
    ctx.globalAlpha = 0.3;
    for (let i = 0; i < 5; i++) {
      const px = cx + Math.sin(t * 2 + i * 1.7) * (18 + i * 6) * scale;
      const pr = (14 + i * 7) * scale;
      const pg = ctx.createRadialGradient(px, baseY + 4, 2, px, baseY + 4, pr);
      pg.addColorStop(0, 'rgba(234, 206, 164, .8)');
      pg.addColorStop(1, 'rgba(234, 206, 164, 0)');
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.ellipse(px, baseY + 4, pr, pr * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // 顶部扩散的尘土
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#f0dcb4';
    ctx.beginPath();
    ctx.ellipse(
      cx + Math.sin(t * 1.1) * 10 * scale,
      baseY - height - 6,
      46 * scale,
      12 * scale,
      0, 0, Math.PI * 2
    );
    ctx.fill();
    ctx.restore();
  }

  // 远处沙暴：地平线上缓缓推进的沙墙
  function drawSandstorm() {
    const t = weatherTime;
    const haze = ctx.createLinearGradient(0, 250, 0, 500);
    haze.addColorStop(0, 'rgba(232, 196, 146, 0)');
    haze.addColorStop(0.45, 'rgba(226, 184, 132, .22)');
    haze.addColorStop(1, 'rgba(214, 170, 120, 0)');
    ctx.fillStyle = haze;
    ctx.fillRect(-20, 245, W + 40, 250);

    for (let i = 0; i < 8; i++) {
      const w = 260 + hash01(i) * 260;
      const y = 296 + hash01(i + 7) * 92;
      const x = wrapX(i * 260 - t * (16 + i * 5), W + 520) - 260;
      const g = ctx.createRadialGradient(x, y, 8, x, y, w * 0.5);
      g.addColorStop(0, 'rgba(232, 198, 152, .20)');
      g.addColorStop(1, 'rgba(232, 198, 152, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(x, y, w * 0.5, 40 + hash01(i + 3) * 24, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawWeatherBack(cam) {
    const span = W + 240;
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < sandFar.length; i++) {
      const p = sandFar[i];
      const x = wrapX(p.x - cam * 0.12, span) - 120;
      ctx.globalAlpha = p.alpha;
      ctx.strokeStyle = '#e8d0a6';
      ctx.lineWidth = p.w;
      ctx.beginPath();
      ctx.moveTo(x, p.y);
      ctx.lineTo(x - p.len, p.y + 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawWeatherFront(cam) {
    const span = W + 240;
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < sandNear.length; i++) {
      const p = sandNear[i];
      const x = wrapX(p.x - cam * 0.3, span) - 120;
      ctx.globalAlpha = p.alpha;
      ctx.strokeStyle = '#f0dcb4';
      ctx.lineWidth = p.w;
      ctx.beginPath();
      ctx.moveTo(x, p.y);
      ctx.lineTo(x - p.len, p.y + 3);
      ctx.stroke();
    }
    const tones = ['#c08a45', '#a8722f', '#d8b06a'];
    for (let i = 0; i < leaves.length; i++) {
      const lf = leaves[i];
      const x = wrapX(lf.x - cam * 0.34, span) - 120;
      ctx.save();
      ctx.globalAlpha = 0.75;
      ctx.translate(x, lf.y);
      ctx.rotate(lf.rot);
      ctx.fillStyle = tones[lf.tone];
      ctx.beginPath();
      ctx.ellipse(0, 0, lf.size, lf.size * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(90, 52, 18, .5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-lf.size, 0);
      ctx.lineTo(lf.size, 0);
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }

  // 梭梭草：沙漠常见的细枝灌木
  function drawSaxaulBush(x, baseY, sc) {
    ctx.save();
    ctx.fillStyle = 'rgba(60, 38, 14, .16)';
    ctx.beginPath();
    ctx.ellipse(x - 6 * sc, baseY + 3, 30 * sc, 6 * sc, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineCap = 'round';
    for (let i = 0; i < 9; i++) {
      const seed = hash01(x * 0.13 + i * 3.7);
      const lean = (seed - 0.5) * 1.5;
      const h = (30 + seed * 46) * sc;
      const sway = Math.sin(weatherTime * 2.2 + i) * 4 * sc;
      ctx.strokeStyle = i % 3 === 0 ? 'rgba(136, 148, 104, .9)' : 'rgba(108, 120, 80, .82)';
      ctx.lineWidth = (1.6 + seed) * sc;
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.quadraticCurveTo(
        x + lean * 18 * sc + sway,
        baseY - h * 0.62,
        x + lean * 30 * sc + sway,
        baseY - h
      );
      ctx.stroke();
      ctx.lineWidth = 1 * sc;
      ctx.beginPath();
      ctx.moveTo(x + lean * 20 * sc + sway, baseY - h * 0.66);
      ctx.lineTo(x + lean * 38 * sc + sway + 8 * sc, baseY - h * 0.78);
      ctx.stroke();
    }
    ctx.restore();
  }

  // 干草簇
  function drawDryTuft(x, baseY, sc) {
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < 7; i++) {
      const seed = hash01(x * 0.21 + i * 5.1);
      const lean = (seed - 0.5) * 20;
      const h = (10 + seed * 18) * sc;
      ctx.strokeStyle = 'rgba(184, 142, 78, .78)';
      ctx.lineWidth = 1.4 * sc;
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.quadraticCurveTo(x + lean * 0.4, baseY - h * 0.7, x + lean, baseY - h);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawImageFlipped(img, cx, groundY, w, h, flip) {
    ctx.save();
    if (flip) {
      ctx.translate(cx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, groundY - h, w, h);
    } else {
      ctx.drawImage(img, cx - w / 2, groundY - h, w, h);
    }
    ctx.restore();
  }

  // ---------- 绘制：路旁装饰 ----------
  function drawDecorRow(scroll, baseY, span, scaleMax, kinds, rowFactor) {
    const start = Math.floor(scroll / span) - 2;
    const end = start + Math.ceil(W / span) + 4;
    for (let i = start; i <= end; i++) {
      const j = wrapInt(i, 100000);
      const seed = hash01(j);
      const x = i * span - scroll + (seed - 0.5) * span * 0.55;
      const kind = wrapInt(j, kinds);
      const sc = scaleMax * (0.72 + hash01(j + 3) * 0.55);

      if (kind === 0 || kind === 4) {
        if (images.cactus) {
          const h = 135 * sc;
          // 记下这棵仙人掌（worldX 用"屏幕 x + 本排 scroll"还原，之后每帧按视差算回屏幕位置）
          const wxC = x + scroll;
          const deadC = rowFactor !== undefined && lingDead.some(function (d) {
            return d.factor === rowFactor && Math.abs(d.worldX - wxC) < 1;
          });
          if (deadC) continue;                     // 已经枯掉的仙人掌：不再画（虚化）
          if (rowFactor !== undefined && Math.abs(x - player.worldX + cameraX()) < 460) {
            lingCactus.push({ screenX: x, baseY: baseY, sc: sc, worldX: wxC, factor: rowFactor });
          }
          ctx.fillStyle = 'rgba(60, 38, 14, .18)';
          ctx.beginPath();
          ctx.ellipse(x - 8 * sc, baseY + 3, 34 * sc, 7 * sc, 0, 0, Math.PI * 2);
          ctx.fill();
          drawImageFlipped(images.cactus, x, baseY, h, h, false);
        }
      } else if (kind === 1) {
        ctx.strokeStyle = 'rgba(75, 52, 30, .9)';
        ctx.lineWidth = 5 * sc;
        ctx.beginPath();
        ctx.moveTo(x, baseY);
        ctx.lineTo(x, baseY - 135 * sc);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(75, 52, 30, .75)';
        ctx.lineWidth = 2.2 * sc;
        ctx.beginPath();
        ctx.moveTo(x, baseY - 110 * sc);
        ctx.lineTo(x + 15 * sc, baseY - 118 * sc);
        ctx.moveTo(x, baseY - 128 * sc);
        ctx.lineTo(x - 13 * sc, baseY - 138 * sc);
        ctx.stroke();
      } else if (kind === 2 && images.rock) {
        const h = 52 * sc;
        const w = 66 * sc;
        ctx.fillStyle = 'rgba(60, 38, 14, .2)';
        ctx.beginPath();
        ctx.ellipse(x - 5 * sc, baseY + 2, 40 * sc, 7 * sc, 0, 0, Math.PI * 2);
        ctx.fill();
        drawImageFlipped(images.rock, x, baseY, w, h, false);
      } else if (kind === 3 || kind === 5) {
        ctx.fillStyle = 'rgba(176, 112, 53, .72)';
        ctx.beginPath();
        ctx.arc(x, baseY - 12 * sc, 12 * sc, Math.PI, 0);
        ctx.arc(x + 12 * sc, baseY - 7 * sc, 10 * sc, Math.PI, 0);
        ctx.arc(x - 12 * sc, baseY - 8 * sc, 10 * sc, Math.PI, 0);
        ctx.fill();
      } else if (kind === 6 || kind === 7) {
        drawSaxaulBush(x, baseY, sc * 0.95);
      } else if (kind === 8 || kind === 9) {
        drawDryTuft(x, baseY, sc);
      }
    }
  }

  /* 小型漩涡：同心弧 + 中心亮点，转起来像一道门 */
  function drawLingVortex(cx, cy, r, rot, alpha) {
    if (r <= 1 || alpha <= 0.02) return;
    ctx.save();
    ctx.globalAlpha = clamp(alpha, 0, 1);
    const g = ctx.createRadialGradient(cx, cy, r * 0.06, cx, cy, r);
    g.addColorStop(0, 'rgba(226, 255, 246, .95)');
    g.addColorStop(0.35, 'rgba(120, 226, 255, .55)');
    g.addColorStop(0.75, 'rgba(96, 150, 255, .22)');
    g.addColorStop(1, 'rgba(40, 60, 140, 0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(cx, cy, r, r * 0.62, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineWidth = Math.max(1.2, r * 0.06);
    for (let i = 0; i < 3; i++) {
      ctx.strokeStyle = 'rgba(190, 245, 255, ' + (0.5 - i * 0.13) + ')';
      ctx.beginPath();
      const a0 = rot * (1 + i * 0.6) + i * 2.1;
      ctx.ellipse(cx, cy, r * (0.92 - i * 0.2), r * (0.58 - i * 0.13), 0, a0, a0 + Math.PI * 1.35);
      ctx.stroke();
    }
    ctx.fillStyle = 'rgba(255, 255, 255, .9)';
    ctx.beginPath();
    ctx.arc(cx, cy, Math.max(1.5, r * 0.1), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /* 自己的传送演出：头顶虚空漩涡 + 落地漩涡 */
  function drawLingTeleportFx() {
    if (lingTp && lingTp.phase === 'in') {
      const k = clamp(lingTp.t / LING_TP_IN_SEC, 0, 1);
      const sx = player.worldX - cameraX();
      const cy = GROUND_Y - 250 - 40 * k;
      drawLingVortex(sx, cy, 26 + 44 * k, lingTp.t * 9, 0.9);
      // 一根灵气柱：从头顶漩涡连到脚下，说明"门开了"
      ctx.save();
      ctx.globalAlpha = 0.5 * (1 - k * 0.4);
      const g2 = ctx.createLinearGradient(0, cy, 0, GROUND_Y);
      g2.addColorStop(0, 'rgba(190, 245, 255, .75)');
      g2.addColorStop(1, 'rgba(150, 210, 255, 0)');
      ctx.fillStyle = g2;
      ctx.beginPath();
      ctx.moveTo(sx - 20 - 16 * k, cy);
      ctx.lineTo(sx + 20 + 16 * k, cy);
      ctx.lineTo(sx + 30 + 22 * k, GROUND_Y);
      ctx.lineTo(sx - 30 - 22 * k, GROUND_Y);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    // 落地：仙人掌上空还留一枚慢慢收拢的漩涡
    if (lingTp && lingTp.phase === 'mid') {
      const k = clamp(lingTp.t / LING_TP_OUT_SEC, 0, 1);
      const ax = lingAnchorScreenX();
      drawLingVortex(ax, lingNode ? lingNode.baseY - 250 : GROUND_Y - 250, 46 * (1 - k * 0.75), 3 + k * 6, 0.8 * (1 - k));
    }
  }

  /* 灵脉房间里的真人：画在灵脉两侧；刚进来的会从漩涡里掉下来 */
  function drawLingPeers() {
    lingPeerPlates = [];
    /* 战斗中不画灵脉旁的其他真人：
     *   交锋时对手已经作为"敌人"画在战场上了，再画一遍就变成场上有两个人。 */
    if (gameState === 'battle') return;
    if (netRoomKind !== 'ling' || !lingNode) return;
    netMembers.forEach(function (m) {
      const localX = (m.drawX === undefined ? m.localX : m.drawX);
      const bx = lingPeerScreenX(localX);
      if (bx < -200 || bx > W + 200) return;
      const sc = 0.92;
      const w = PLAYER_FRAME.w * sc, h = PLAYER_FRAME.h * sc;
      const baseTop = GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * sc;
      const falling = m.spawnT > 0;
      const k = falling ? clamp(1 - m.spawnT / LING_PEER_DROP_SEC, 0, 1) : 1;
      const top = baseTop - (falling ? (1 - k) * 250 : 0);
      if (falling) {
        drawLingVortex(bx, baseTop - 250, 40 * (0.6 + 0.4 * (1 - k)), 2 + k * 8, 0.85);
      }
      if (images.player_idle_1) {
        const step = Math.floor((player.animT + m.sid.length) * 3) % 4;
        const frame = images['player_idle_' + (step + 1)] || images.player_idle_1;
        ctx.save();
        if (m.facing < 0) {
          ctx.translate(bx, 0); ctx.scale(-1, 1);
          ctx.drawImage(frame, -w / 2, top, w, h);
        } else {
          ctx.drawImage(frame, bx - w / 2, top, w, h);
        }
        ctx.restore();
      }
      drawActorPlate(bx, GROUND_Y, m.name, m.realm || '', {
        bar: true, hp: m.hp, sub: m.action === 'rest' ? '修炼中' : ''
      });
      lingPeerPlates.push({ sid: m.sid, x: Math.round(bx), name: m.name });
      sceneActors.peers += 1;             // 调试/测试：这一帧画了几个真人
    });
    // 灵脉这边被别人打掉的战利品堆
    lingPvpPiles.forEach(function (p) {
      if (p.taken) return;
      const px = p.worldX - cameraX();
      if (px < -160 || px > W + 160) return;
      const t = (p.t || 0) + 0.016;
      p.t = t;
      ctx.save();
      const y = GROUND_Y - 16 + Math.sin(t * 2.2) * 3;
      const g = ctx.createRadialGradient(px, y, 2, px, y, 34);
      g.addColorStop(0, 'rgba(255, 236, 170, .85)');
      g.addColorStop(1, 'rgba(255, 190, 90, 0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(px, y, 34, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'rgba(120, 84, 30, .95)';
      ctx.beginPath(); ctx.ellipse(px, y, 15, 12, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#ffe9b0';
      ctx.font = 'bold 10px "Microsoft YaHei", sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('战利品', px, y - 22);
      ctx.restore();
    });
  }

  /* ---------- 门派山门：同门玩家 + NPC 弟子 ---------- */
  function sectActorScreenX(localX) {
    return sectScene ? (sectScene.baseX + localX - cameraX()) : 0;
  }

  function drawSectActor(localX, name, realm, opts) {
    const o = opts || {};
    const sx = sectActorScreenX(localX);
    if (sx < -200 || sx > W + 200) return false;
    const sc = 0.92;
    const w = PLAYER_FRAME.w * sc, h = PLAYER_FRAME.h * sc;
    const top = GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * sc;
    ctx.save();
    ctx.globalAlpha = o.alpha === undefined ? 1 : o.alpha;
    let frame = null;
    if (o.action === 'mine') {
      const mf = images['player_mine_' + (1 + (Math.floor((o.phase || 0) * PLAYER_MINE_FPS) % PLAYER_MINE_FRAMES))];
      if (mf && mf.complete && mf.naturalWidth > 0) {
        const ms = sc * PLAYER_MINE_DRAW_SCALE;
        const mw = PLAYER_MINE_FRAME.w * ms, mh = PLAYER_MINE_FRAME.h * ms;
        const mtop = GROUND_Y + 4 + PLAYER_MINE_GROUND_DROP -
          (PLAYER_MINE_FRAME.h - PLAYER_MINE_FRAME.basePad) * ms;
        if (o.facing < 0) {
          ctx.translate(sx, 0); ctx.scale(-1, 1);
          ctx.drawImage(mf, -mw / 2, mtop, mw, mh);
        } else {
          ctx.drawImage(mf, sx - mw / 2, mtop, mw, mh);
        }
        ctx.restore();
        drawActorPlate(sx, GROUND_Y, name, realm, { bar: true, hp: o.hp, sub: '挖矿中' });
        return true;
      }
    }
    const step = Math.floor(((o.phase || 0) + (o.seed || 0)) * 3) % 4;
    frame = images['player_idle_' + (step + 1)] || images.player_idle_1;
    if (frame) {
      if (o.facing < 0) {
        ctx.translate(sx, 0); ctx.scale(-1, 1);
        ctx.drawImage(frame, -w / 2, top, w, h);
      } else {
        ctx.drawImage(frame, sx - w / 2, top, w, h);
      }
    }
    ctx.restore();
    drawActorPlate(sx, GROUND_Y, name, realm, {
      bar: true, hp: o.hp,
      sub: o.action === 'walk' ? '' : (o.action === 'mine' ? '挖矿中' : '')
    });
    return true;
  }

  function updateSectNpcs(dt) {
    if (!sectScene || !sectScene.npcs) return;
    sectScene.npcs.forEach(function (n) {
      n.phase += dt;
      const nx = n.localX + n.dir * n.speed * dt;
      if (nx > n.homeX + n.range) n.dir = -1;
      if (nx < n.homeX - n.range) n.dir = 1;
      n.localX = clamp(nx, n.homeX - n.range, n.homeX + n.range);
      n.action = 'walk';
    });
  }

  function drawSectPeers() {
    if (!sectScene || gameState === 'battle') return;
    // 真人：只画同门派玩家（服务端房间也按门派 id 隔离）
    netMembers.forEach(function (m) {
      if (!netSameSect(m)) return;
      const localX = (m.drawX === undefined ? m.localX : m.drawX);
      if (drawSectActor(localX, '道友·' + (m.name || '同门'), m.realm || '', {
        action: m.action, facing: m.facing || 1, phase: player.animT + (m.sid ? m.sid.length : 0),
        seed: m.sid ? m.sid.length : 0, hp: m.hp
      })) sceneActors.peers += 1;
    });
    // NPC 弟子：本地生成，所有玩家都能看到
    (sectScene.npcs || []).forEach(function (n) {
      if (drawSectActor(n.localX, n.name, n.realm, {
        action: n.action, facing: n.dir, phase: n.phase, seed: n.id.length, hp: 1
      })) sceneActors.peers += 1;
    });
  }

  function drawDecor(cam) {
    lingCactus = [];                                  // 本帧的仙人掌位置（点击判定用）
    drawDecorRow(cam * 0.22, 545, 115, 0.72, 10, 0.22);
    drawDecorRow(cam * 0.55, 618, 72, 1.0, 8, 0.55);
    /* 灵脉定位传送落点：程序化装饰里未必正好有仙人掌，这里补画一棵，
     *   并塞进 lingCactus，这样它跟普通仙人掌一样能点、能吸灵。 */
    if (lingForcedCactus) {
      const f = lingForcedCactus;
      const x = f.worldX - cam * f.factor;
      if (x > -220 && x < W + 220) {
        const h = 135 * f.sc;
        ctx.fillStyle = 'rgba(60, 38, 14, .18)';
        ctx.beginPath();
        ctx.ellipse(x - 8 * f.sc, f.baseY + 3, 34 * f.sc, 7 * f.sc, 0, 0, Math.PI * 2);
        ctx.fill();
        drawImageFlipped(images.cactus, x, f.baseY, h, h, false);
        lingCactus.push({ screenX: x, baseY: f.baseY, sc: f.sc, worldX: f.worldX, factor: f.factor });
      }
    }
  }

  function drawMilestones(cam) {
    const n0 = Math.max(1, Math.floor((cam - 300) / KM_UNIT));
    const n1 = Math.ceil((cam + W + 200) / KM_UNIT);
    for (let n = n0; n <= n1; n++) {
      const wx = START_X + n * KM_UNIT;
      const sx = wx - cam;
      if (sx < -160 || sx > W + 160) continue;

      // 直接“印”在马路路面上：压低透视并做旧
      ctx.save();
      ctx.translate(sx, 700);
      ctx.scale(1, 0.56);
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = 'rgba(70, 45, 21, .92)';
      ctx.fillText(n + 'km', 0, 0);
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = '#ffeac2';
      ctx.fillText(n + 'km', -1.5, -1.5);
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = 'rgba(255, 228, 180, .28)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-26, 14);
      ctx.lineTo(26, 14);
      ctx.stroke();
      ctx.restore();
    }
  }

  // ---------- 绘制：玩家 / 敌人 ----------
  // 敌人绘制缩放：传奇 BOSS 体型是原来的 2 倍（1.32 × 2）
  const LEGEND_SCALE = 2.64;
  // 洞府内的整体缩放：玩家与敌人都缩小 25%（空间更挤，观感更像洞窟）
  const CAVE_SCALE = 0.75;
  function caveScale() {
    // 洞府里人和怪都缩小 25%；临时副本是"公共地图"，保持原尺寸
    return (cave && !cave.def.activity) ? CAVE_SCALE : 1;
  }
  function enemyDrawScale(enemy) {
    if (!enemy) return 1;
    if (enemy.wbScale) return enemy.wbScale;   // 世界BOSS：固定放大体型
    const base = enemy.legend ? LEGEND_SCALE
      : enemy.elite ? 1.16
      : enemy.tier === 'magic' ? 1.08 : 1;
    // 关卡BOSS：在精英体型上再放大一截，一眼看出"这只不一样"
    return enemy.boss ? base * (enemy.bossScale || 1.3) : base;
  }

  function updateEnemyQuake(dt) {
    if (enemyQuake > 0) enemyQuake = Math.max(0, enemyQuake - dt);
  }

  // 地面震动偏移：传奇BOSS走动时持续轻微抖动，落步 / 出手时叠加一次更明显的余震
  function quakeOffset() {
    let amp = 0;
    if (activeEnemy && activeEnemy.legend) {
      if (gameState === 'walk') amp = 1.9;
      if (enemyQuake > 0) amp = Math.max(amp, 5.5 * (enemyQuake / 0.45));
    }
    if (amp <= 0) return { x: 0, y: 0 };
    const t = performance.now() / 1000;
    return {
      x: Math.round(Math.sin(t * 46) * amp * 10) / 10,
      y: Math.round(Math.cos(t * 63) * amp * 0.7 * 10) / 10
    };
  }

  function drawCharacterCrop(img, sx, groundY, flip, bobY, scale) {
    if (!img) return;
    const srcX = 42, srcY = 48, srcW = 176, srcH = 208;
    const sc = scale || 1;
    const dw = 148 * sc, dh = 178 * sc;
    const top = groundY - dh + 4 + (bobY || 0);
    ctx.save();
    if (flip) {
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, srcX, srcY, srcW, srcH, -dw / 2, top, dw, dh);
    } else {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, sx - dw / 2, top, dw, dh);
    }
    ctx.restore();
  }

  // 受击震动：值从强到弱地左右/上下抖动
  function shakeOffset(hurtT) {
    if (!hurtT || hurtT <= 0) return { x: 0, y: 0 };
    const elapsed = HURT_TIME - hurtT;
    const k = hurtT / HURT_TIME;
    return {
      x: Math.sin(elapsed * 112) * 7 * k,
      y: Math.cos(elapsed * 88) * 3.5 * k
    };
  }

  /* ============================================================
   * 受击击退：被玩家普攻 / 技能打到时，整个人先"啪"地弹出去一小段，
   *   再自己滑回原地 —— 只在绘制时做位移，不改敌人的世界坐标
   *   （否则尸体会掉在击退后的位置、攻击距离判定也会被带歪）。
   * ============================================================ */
  const KNOCK_DUR = 0.42;        // 击退总时长（秒）
  const KNOCK_PX = 34;          // 峰值位移（屏幕像素）：够看出"被打飞了一下"
  function startEnemyKnock(e, powerPx) {
    if (!e) return;
    const base = powerPx === undefined ? KNOCK_PX : powerPx;
    e.knockDur = KNOCK_DUR;
    e.knockT = KNOCK_DUR;
    e.knockPx = base * (0.86 + Math.random() * 0.3);   // 每次力度略有差别，更像真被打到
    e.knockDir = 1;                                    // 玩家始终在左侧 → 往右（远离玩家）弹
  }
  function enemyKnockOffset(e) {
    if (!e || !(e.knockT > 0)) return 0;
    const dur = e.knockDur || KNOCK_DUR;
    const p = clamp(1 - e.knockT / dur, 0, 1);         // 0 → 1 的进度
    const OUT = 0.22;                                  // 前 22% 时间冲出去
    const shape = p < OUT
      ? 1 - Math.pow(1 - p / OUT, 3)                   // 爆发：瞬间弹开（先快后慢）
      : Math.pow(1 - (p - OUT) / (1 - OUT), 1.7);      // 回位：慢慢滑回去
    return (e.knockDir || 1) * (e.knockPx || 0) * shape;
  }
  /* 每帧：盯住 hurtT 的"突然被刷新"来判断刚刚挨了一下（命中点太多，
   *   统一在这里识别最省事），顺手把击退计时 / 同伴敌人的受击计时往下走。 */
  function tickEnemyKnock(e, dt) {
    if (!e) return;
    const prev = e.__hurtSeen || 0;
    if (e.hurtT > prev + 0.0005) startEnemyKnock(e);
    if (e.hurtT > 0) e.hurtT = Math.max(0, e.hurtT - dt);
    e.__hurtSeen = e.hurtT;
    if (e.knockT > 0) e.knockT = Math.max(0, e.knockT - dt);
  }

  // 被击中时身体上的斜向血色划痕
  function drawHurtSlash(sx, groundY, hurtT) {
    if (!hurtT || hurtT <= 0) return;
    const k = clamp(hurtT / HURT_TIME, 0, 1);
    const clipW = 160;
    const clipH = 184;
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx - clipW / 2, groundY - clipH + 5, clipW, clipH);
    ctx.clip();
    ctx.lineCap = 'round';

    // 外层暗红刀伤
    ctx.strokeStyle = 'rgba(152, 8, 6, ' + (0.85 * k).toFixed(3) + ')';
    ctx.lineWidth = 17;
    ctx.beginPath();
    ctx.moveTo(sx - 66, groundY - 132);
    ctx.lineTo(sx + 58, groundY - 52);
    ctx.stroke();

    // 里层鲜红划痕
    ctx.strokeStyle = 'rgba(255, 52, 28, ' + (0.95 * k).toFixed(3) + ')';
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.moveTo(sx - 62, groundY - 129);
    ctx.lineTo(sx + 54, groundY - 50);
    ctx.stroke();

    // 高光白刃
    ctx.strokeStyle = 'rgba(255, 224, 200, ' + (0.75 * k).toFixed(3) + ')';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(sx - 57, groundY - 125);
    ctx.lineTo(sx + 49, groundY - 47);
    ctx.stroke();
    ctx.restore();
  }

  // 敌人头顶血条与脚下名字（替代原右上角敌方属性 UI）
  function drawEnemyStatus(sx) {
    if (!activeEnemy) return;

    if (gameState === 'battle') {
      const w = 158;
      const h = 22;
      const x = sx - w / 2;
      // 体型越大，血条抬得越高（传奇 BOSS 头顶上方）
      const scale = enemyDrawScale(activeEnemy) * caveScale();
      const y = Math.max(56, GROUND_Y - 238 - Math.round((scale - 1) * 150));
      const pct = clamp(activeEnemy.hp / activeEnemy.maxHp, 0, 1);

      ctx.save();
      ctx.fillStyle = 'rgba(16, 5, 1, .68)';
      roundRect(x - 2, y - 2, w + 4, h + 4, 13);
      ctx.fill();

      ctx.fillStyle = 'rgba(84, 22, 8, .92)';
      roundRect(x, y, w, h, 11);
      ctx.fill();

      if (pct > 0.015) {
        const hpW = Math.max(8, Math.round(w * pct));
        const grad = ctx.createLinearGradient(x, y, x + w, y);
        if (activeEnemy.boss) {
          grad.addColorStop(0, '#b8860b');
          grad.addColorStop(1, '#ffe07a');
        } else if (activeEnemy.legend) {
          grad.addColorStop(0, '#d62a6a');
          grad.addColorStop(1, '#ffb3d6');
        } else if (activeEnemy.elite) {
          grad.addColorStop(0, '#8b3bd6');
          grad.addColorStop(1, '#ffb0ff');
        } else if (activeEnemy.tier === 'magic') {
          grad.addColorStop(0, '#2f7fd6');
          grad.addColorStop(1, '#9fd4ff');
        } else {
          grad.addColorStop(0, pct > 0.5 ? '#43b45f' : '#e26a28');
          grad.addColorStop(1, pct > 0.5 ? '#9ae88a' : '#ff9c52');
        }
        ctx.fillStyle = grad;
        roundRect(x, y, hpW, h, 11);
        ctx.fill();
      }

      ctx.fillStyle = '#fff6e8';
      ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        Math.max(0, Math.ceil(activeEnemy.hp)) + ' / ' + activeEnemy.maxHp,
        sx,
        y + h / 2 + 0.5
      );
      if (activeEnemy.elite && activeEnemy.skill) {
        const skillLabel = activeEnemy.skills && activeEnemy.skills.length > 1
          ? activeEnemy.skills.map(function (s) { return s.name; }).join(' / ')
          : activeEnemy.skill.name;
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(30, 6, 40, .9)';
        ctx.strokeText('技能：' + skillLabel, sx, y - 9);
        ctx.fillStyle = activeEnemy.boss ? '#ffd76a' : (activeEnemy.legend ? '#ffc0e4' : '#e9b6ff');
        ctx.fillText('技能：' + skillLabel, sx, y - 9);
      }
      // 关卡BOSS：血条上方再挂一块金牌子
      if (activeEnemy.boss) {
        const tagW = 78, tagH = 18;
        const tagX = sx - tagW / 2;
        const tagY = y - (activeEnemy.elite && activeEnemy.skill ? 26 : 8) - tagH;
        ctx.fillStyle = 'rgba(24, 12, 2, .82)';
        roundRect(tagX - 2, tagY - 2, tagW + 4, tagH + 4, 9);
        ctx.fill();
        const tg = ctx.createLinearGradient(tagX, tagY, tagX + tagW, tagY + tagH);
        tg.addColorStop(0, '#8a5a12');
        tg.addColorStop(1, '#ffd76a');
        ctx.fillStyle = tg;
        roundRect(tagX, tagY, tagW, tagH, 8);
        ctx.fill();
        ctx.fillStyle = '#2b1704';
        ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('关卡BOSS', sx, tagY + tagH / 2 + 0.5);
      }
      // 流血状态：血条下方标一行，附一滴血
      if (activeEnemy.bleed && activeEnemy.bleed.turns > 0) {
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(40, 0, 0, .92)';
        const bleedLabel = '流血 ' + activeEnemy.bleed.turns + ' 回合（' + Math.round(activeEnemy.bleed.dmg) + '/回合）';
        ctx.strokeText(bleedLabel, sx + 10, y + h + 12);
        ctx.fillStyle = '#ff5b5b';
        ctx.fillText(bleedLabel, sx + 10, y + h + 12);
        ctx.fillStyle = '#e01f2b';
        ctx.beginPath();
        ctx.ellipse(sx - 62, y + h + 9, 5, 7, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // 燃烧层数（「炎军」叠加）：血条左侧标出层数与每回合伤害
      if ((activeEnemy.burnStacks || 0) > 0) {
        const per = Math.max(1, Math.round(combat.maxHp * BURN_PCT));
        const burnLabel = '燃烧 ' + activeEnemy.burnStacks + ' 层（' + (per * activeEnemy.burnStacks) + '/回合）';
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(52, 18, 0, .92)';
        ctx.strokeText(burnLabel, sx + 10, y + h + 30);
        ctx.fillStyle = '#ff9a3c';
        ctx.fillText(burnLabel, sx + 10, y + h + 30);
        // 小火苗
        const fx0 = sx - 70;
        const fy0 = y + h + 27;
        ctx.fillStyle = '#ff6a1f';
        ctx.beginPath();
        ctx.moveTo(fx0, fy0 + 6);
        ctx.quadraticCurveTo(fx0 - 6, fy0 - 2, fx0, fy0 - 9);
        ctx.quadraticCurveTo(fx0 + 6, fy0 - 2, fx0, fy0 + 6);
        ctx.fill();
        ctx.fillStyle = '#ffd26a';
        ctx.beginPath();
        ctx.ellipse(fx0, fy0 + 2, 2.2, 3.4, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // 名字放在敌人脚下
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(28, 8, 1, .9)';
    const label = (activeEnemy.legend ? '★★ ' : activeEnemy.elite ? '★ ' : '') +
      (activeEnemy.name || '拦路者') + ' · Lv.' + activeEnemy.level;
    ctx.strokeText(label, sx, GROUND_Y + 29);
    ctx.fillStyle = activeEnemy.legend
      ? '#ffc0e4'
      : activeEnemy.elite ? '#f0c7ff'
      : activeEnemy.tier === 'magic' ? '#a9d6ff'
      : (gameState === 'battle' ? '#ffd9bd' : '#ffe9c8');
    ctx.fillText(label, sx, GROUND_Y + 29);
    ctx.restore();

    // 洞府增幅标记：名字下面挂一个小色块，让玩家一眼看出这怪带什么效果
    if (gameState === 'battle' && activeEnemy.caveBuff) {
      const b = activeEnemy.caveBuff;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold 12.5px "Microsoft YaHei", sans-serif';
      const text = b.short + ' ' + b.name;
      const tw = ctx.measureText(text).width + 20;
      const bx = sx - tw / 2;
      const by = GROUND_Y + 38;
      ctx.fillStyle = 'rgba(12, 6, 2, .74)';
      roundRect(bx, by, tw, 20, 10);
      ctx.fill();
      ctx.strokeStyle = b.color;
      ctx.lineWidth = 1.4;
      roundRect(bx, by, tw, 20, 10);
      ctx.stroke();
      ctx.fillStyle = b.color;
      ctx.fillText(text, sx, by + 10.5);
      ctx.restore();
    }
  }

  /* ---------- 洞府增幅的特效 ---------- */
  // 一簇小火苗（和血条旁边那朵同款造型）
  function drawFlameShape(x, y, s, outer, inner) {
    ctx.fillStyle = outer;
    ctx.beginPath();
    ctx.moveTo(x, y + 8 * s);
    ctx.quadraticCurveTo(x - 7 * s, y - 2 * s, x, y - 13 * s);
    ctx.quadraticCurveTo(x + 7 * s, y - 2 * s, x, y + 8 * s);
    ctx.fill();
    ctx.fillStyle = inner;
    ctx.beginPath();
    ctx.ellipse(x, y + 2 * s, 2.6 * s, 4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  function caveBuffEnemies() {
    if (gameState !== 'battle') return [];
    const list = battleGroup.length ? battleGroup : (activeEnemy ? [activeEnemy] : []);
    return list.filter(function (e) { return aliveEnemy(e) && e.caveBuff; });
  }

  let caveFxGroundCalls = 0;   // 调试用：确认绘制函数有没有被调用

  // 地面层：脚下光环（烈焰 / 寒冰）——画在敌人之前，光环压在脚下
  function drawCaveBuffGroundFx() {
    caveFxGroundCalls += 1;
    caveBuffEnemies().forEach(function (e) {
      const id = e.caveBuff.id;
      if (id !== 'flameAura' && id !== 'freeze') return;
      const sx = e.x - cameraX();
      if (sx < -200 || sx > W + 200) return;
      const k = caveScale();
      const hot = id === 'flameAura';
      const rgb = hot ? '255, 138, 60' : '150, 215, 255';
      const pulse = 0.5 + Math.sin(enemyAnimClock * (hot ? 3.4 : 2.2)) * 0.5;
      const rx = (hot ? 84 : 74) * k;
      const ry = (hot ? 20 : 17) * k;
      // 底色光晕
      const g = ctx.createRadialGradient(sx, GROUND_Y + 4, 5, sx, GROUND_Y + 4, rx);
      g.addColorStop(0, 'rgba(' + rgb + ',' + (0.30 + pulse * 0.20).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(' + rgb + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(sx, GROUND_Y + 4, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      // 两圈向外荡开的涟漪
      ctx.save();
      ctx.lineWidth = 2.4;
      for (let i = 0; i < 2; i++) {
        const t = (enemyAnimClock * 0.85 + i * 0.5) % 1;
        ctx.globalAlpha = (1 - t) * 0.75;
        ctx.strokeStyle = 'rgba(' + rgb + ',0.95)';
        ctx.beginPath();
        ctx.ellipse(sx, GROUND_Y + 4, rx * (0.42 + t * 0.7), ry * (0.42 + t * 0.7), 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      if (hot) {
        // 烈焰：往上窜的火苗
        for (let i = 0; i < 6; i++) {
          const ph = (enemyAnimClock * 1.5 + i * 0.17) % 1;
          const dx = Math.sin(i * 2.1 + enemyAnimClock * 1.6) * 30 * k;
          ctx.save();
          ctx.globalAlpha = (1 - ph) * 0.9;
          drawFlameShape(sx + dx, GROUND_Y + 4 - ph * 62 * k, (0.72 - ph * 0.2) * k, '#ff5a14', '#ffd26a');
          ctx.restore();
        }
      } else {
        // 寒冰：几根往上飘的冰刺
        ctx.save();
        ctx.strokeStyle = 'rgba(200, 240, 255, .95)';
        ctx.lineWidth = 2 * k;
        for (let i = 0; i < 6; i++) {
          const ph = (enemyAnimClock * 0.7 + i * 0.17) % 1;
          const dx = (i - 2.5) * 18 * k;
          const fy = GROUND_Y + 4 - ph * 40 * k;
          ctx.globalAlpha = (1 - ph) * 0.85;
          ctx.beginPath();
          ctx.moveTo(sx + dx, fy);
          ctx.lineTo(sx + dx + 5 * k, fy - 12 * k);
          ctx.stroke();
        }
        ctx.restore();
      }
    });
  }

  // 覆盖层：手上的火焰（点燃）——画在敌人之后，火苗盖在手上
  function drawCaveBuffOverFx() {
    caveBuffEnemies().forEach(function (e) {
      if (e.caveBuff.id !== 'ignite') return;
      const sx = e.x - cameraX();
      if (sx < -200 || sx > W + 200) return;
      const k = caveScale() * enemyDrawScale(e);
      const flick = 0.85 + Math.sin(enemyAnimClock * 6.2 + (e.animPhase || 0)) * 0.15;
      const hx = sx - 30 * k;
      const hy = GROUND_Y - 74 * k;
      ctx.save();
      ctx.globalAlpha = 0.95;
      drawFlameShape(hx, hy, 1.35 * k * flick, '#ff5a14', '#ffd26a');
      drawFlameShape(hx + 17 * k, hy + 9 * k, 0.9 * k * flick, '#ff7a26', '#ffe08a');
      drawFlameShape(hx - 13 * k, hy + 11 * k, 0.7 * k * flick, '#ff6a1f', '#ffe6a8');
      ctx.restore();
      // 手边一点点暖光
      const g = ctx.createRadialGradient(hx + 6 * k, hy, 2, hx + 6 * k, hy, 58 * k);
      g.addColorStop(0, 'rgba(255, 150, 60, .34)');
      g.addColorStop(1, 'rgba(255, 150, 60, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(hx + 6 * k, hy, 58 * k, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // 伤害数字飘字
  function drawDamagePopups() {
    for (let i = 0; i < damagePopups.length; i++) {
      const p = damagePopups[i];
      if (p.delay > 0) continue;                 // 延迟出现的飘字（尾随箭矢）先不画
      const a = Math.max(0, 1 - p.t / p.life);
      const pop = p.t < 0.12 ? 1 + Math.sin(p.t / 0.12 * Math.PI) * 0.14 : 1;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.textAlign = 'center';
      ctx.font = 'bold 30px Arial, sans-serif';
      ctx.lineWidth = 6;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(38, 5, 0, .92)';
      const label = p.text || ((p.positive ? '+' : '-') + p.dmg);
      ctx.strokeText(label, p.x, p.y);
      ctx.fillStyle = p.color;
      ctx.fillText(label, p.x, p.y);
      ctx.font = 'bold 30px "Microsoft YaHei", sans-serif';
      ctx.restore();
    }
  }

  function cameraX() {
    return Math.max(0, player.worldX - START_X);
  }

  function drawPlayer() {
    const sx = player.worldX - cameraX();
    const shake = shakeOffset(player.hurtT);
    const fx = { scale: playerFx.scale * caveScale(), alpha: playerFx.alpha };
    // 灵脉定位传送：被头顶漩涡吸起来时抬高 + 淡出（playerLiftY / playerTpAlpha）
    const tpA = playerTpAlpha;
    if (fx.alpha * tpA <= 0.01) return; // 进洞过场 / 传送淡出：看不见就不画
    if (debugHidePlayer) return;  // 调试/测试：临时隐藏玩家

    const air = Math.max(0, -playerLiftY / 170);
    ctx.save();
    ctx.globalAlpha = fx.alpha * tpA;
    ctx.fillStyle = 'rgba(60, 32, 8, ' + ((0.38 - air * 0.2) * fx.alpha * tpA).toFixed(3) + ')';
    ctx.beginPath();
    ctx.ellipse(sx - 14, GROUND_Y + 5, 58 * fx.scale * (1 - air * 0.5), 9 * fx.scale * (1 - air * 0.5), -0.06, 0, Math.PI * 2);
    ctx.fill();

    const frame = playerFrameImage();
    const walking = gameState === 'walk' && playerMovingNow() && playerAtkT <= 0;
    // 赶路时上下起伏；休息时只留一点点呼吸感（躺/坐的姿态基本是静的）
    const bob = walking ? Math.sin(player.walkT * 1.6) * 1.5
      : (playerResting ? Math.sin(player.animT * 1.4) * 0.9 : 0);
    const flip = (playFaceDir() < 0) ? !PLAYER_SPRITE_FLIP : PLAYER_SPRITE_FLIP;
    /* 世界BOSS 时装：先画身后的 BOSS 虚影，再画玩家 → 虚影永远在玩家背后 */
    // flip=false → 朝右；flip=true → 朝左。虚影按朝向站到反侧（身后）
    drawBossPhantom(sx + shake.x, GROUND_Y + shake.y, fx.scale, flip ? -1 : 1, fx.alpha);
    // 门派矿山：挖矿动作期间用专门的 5 帧动画覆盖普通待机/移动帧
    const mineFrame = miningPlayerFrame();
    if (mineFrame) {
      drawMiningPlayerFrame(mineFrame, sx + shake.x, GROUND_Y + shake.y, fx.scale, flip);
    } else {
      drawPlayerFrame(frame, sx + shake.x, GROUND_Y + shake.y + playerLiftY, fx.scale, flip, bob);
    }
    drawHurtSlash(sx + shake.x, GROUND_Y + shake.y, player.hurtT);
    ctx.restore();
  }

  // 玩家当前该画哪一帧：出手动作 > 赶路 > 待机
  /* 玩家现在算不算"在走"：
 *   公路上看 vx（单向前进）；临时副本里是点屏幕左右走 / 走向拾取物，没有 input.moving，
 *   所以要单独认这两个状态，否则会一直播"休息"帧。 */
  function playerMovingNow() {
    if (activityRun) return activityRun.moveDir !== 0 || !!activityRun.pick;
    return player.vx !== 0;
  }

  /* 角色此刻朝哪边：1 = 右（素材原方向），-1 = 左（水平镜像）。
   * 临时副本里点屏幕左边就往左走，这时立绘要跟着翻过来，不然会出现"人往左走、脸朝右"。 */
  function playFaceDir() {
    if (debugForceFace) return debugForceFace;   // 调试/测试：钉死朝向
    if (activityRun) {
      if (activityRun.moveDir < 0) return -1;
      if (activityRun.moveDir > 0) return 1;
      if (activityRun.pick) return activityRun.pick.worldX >= player.worldX ? 1 : -1;
    }
    // 门派山门：点屏幕左/右移动时，立绘跟着水平翻转
    if (sectScene) {
      if (sectMoveDir < 0 || player.vx < -1) return -1;
      if (sectMoveDir > 0 || player.vx > 1) return 1;
    }
    return 1;
  }

  // 挖矿动作当前帧（没有素材 / 动画结束就返回 null，自动回落普通角色帧）
  function miningPlayerFrame() {
    // 持续挖矿期间（含矿石飞行 / 两轮间隙）一直播挖矿帧，不回落休息帧
    if (!miningContinuous && !miningFx) return null;
    const idx = Math.floor(miningAnimClock * PLAYER_MINE_FPS) % PLAYER_MINE_FRAMES;
    const key = 'player_mine_' + (idx + 1);
    const img = images[key];
    if (img && img.complete && img.naturalWidth > 0) {
      lastPlayerFrameKey = key;
      lastMineFrameKey = key;
      return img;
    }
    lastMineFrameKey = 'player_mine_1';
    return images.player_mine_1 || null;
  }

  // 挖矿素材画布比普通角色大：单独按脚底基线 + 缩放比例绘制
  function drawMiningPlayerFrame(img, sx, groundY, scale, flip) {
    if (!img) return;
    const s = scale * PLAYER_MINE_DRAW_SCALE;
    const w = PLAYER_MINE_FRAME.w * s;
    const h = PLAYER_MINE_FRAME.h * s;
    const top = groundY + 4 + PLAYER_MINE_GROUND_DROP -
      (PLAYER_MINE_FRAME.h - PLAYER_MINE_FRAME.basePad) * s;
    if (flip) {
      ctx.save();
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, top, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(img, sx - w / 2, top, w, h);
    }
  }

  // 休息帧动画：默认按时间连续播放 4 帧；调试/预览可以强制固定一帧
  function restAnimationIndex() {
    if (restPoseForce >= 0) return restPoseForce % PLAYER_REST_FRAMES;
    return Math.floor(player.animT * PLAYER_REST_FPS) % PLAYER_REST_FRAMES;
  }

  function playerFrameImage() {
    let idx = 0;
    if (playerAtkT > 0) {
      const p = 1 - playerAtkT / PLAYER_ATK_DUR;
      idx = Math.min(PLAYER_ATK_FRAMES - 1, Math.floor(p * PLAYER_ATK_FRAMES));
      const atk = images['player_atk_' + (idx + 1)];
      if (atk) {
        lastPlayerFrameKey = 'player_atk_' + (idx + 1);
        return atk;
      }
    } else if (gameState === 'walk' && playerMovingNow()) {
      // 赶路帧：左边线 / 向右赶路都算（临时副本里点屏幕左右走也要播这段）
      idx = Math.floor(player.walkT * (PLAYER_WALK_FPS / 8.5)) % 4;
    } else if (pvpDuel && playerRestFrame(restPoseIdx)) {
      /* 真人交锋：站着不动就用「休息」姿态（只有出手时才播攻击动作） */
      const ridx = restAnimationIndex();
      const rframe = playerRestFrame(ridx) || playerRestFrame(restPoseIdx);
      restPoseIdx = ridx;
      lastPlayerFrameKey = 'player_rest_' + (ridx + 1);
      return rframe;
    } else if (playerResting && playerRestFrame(restPoseIdx)) {
      // 休息：按 6fps 连续播放 4 张 idle 帧，而不是几秒才换一次姿势
      const ridx = restAnimationIndex();
      const rframe = playerRestFrame(ridx) || playerRestFrame(restPoseIdx);
      restPoseIdx = ridx;
      lastPlayerFrameKey = 'player_rest_' + (ridx + 1);
      return rframe;
    } else {
      idx = Math.floor(player.animT * PLAYER_IDLE_FPS) % 4;
    }
    lastPlayerFrameKey = 'player_idle_' + (idx + 1);
    return images['player_idle_' + (idx + 1)] || images.player_idle_1;
  }

  // 播放一次出手动作（普通攻击 / 劈砍）
  function playPlayerAttack() {
    playerAtkT = PLAYER_ATK_DUR;
  }

  // 休息帧是"软加载"：不进资源清单，缺图也不会卡住加载进度条，只是回落到待机呼吸
  /* 首页（还没点「开始游戏」）也要能看到角色站在路上：
   * 单独把玩家自己的帧先软加载进来（不计入 loadTask 进度，避免影响加载条数字），
   * 点「开始游戏」时 loadAssets() 会按正常流程再走一遍（同 URL 走浏览器缓存，不会重复下载）。 */
  function preloadIntroPlayerFrames() {
    const keys = ['player_idle_1', 'player_idle_2', 'player_idle_3', 'player_idle_4'];
    keys.forEach(function (key) {
      const cur = images[key];
      if (cur && cur.complete && cur.naturalWidth > 0) return;
      const def = ASSETS.filter(function (a) { return a[0] === key; })[0];
      if (!def) return;
      const im = new Image();
      im.src = def[1];
      images[key] = im;
    });
  }

  function preloadPlayerRestFrames() {
    for (let i = 1; i <= PLAYER_REST_FRAMES; i++) {
      const im = new Image();
      // 文件名带 v2：绕过 nginx /assets/ 的 immutable 长缓存，确保新休息动画一定生效
      im.src = assetUrl('assets/img/player/player_rest_v2_' + i + '.png');
      playerRestFrames.push(im);
    }
  }

  function playerRestFrame(idx) {
    const im = playerRestFrames[idx];
    return (im && im.complete && im.naturalWidth > 0) ? im : null;
  }

  function playerRestLoadedCount() {
    let n = 0;
    for (let i = 0; i < playerRestFrames.length; i++) {
      const im = playerRestFrames[i];
      if (im && im.complete && im.naturalWidth > 0) n++;
    }
    return n;
  }

  // 新休息素材全部是站姿 idle 帧：战斗中也可以正常抽用
  const PLAYER_REST_LYING = [];
  // 首页（打开网页、还没登录）固定展示这张休息姿态：
  //   public/assets/img/player/player_rest_v2_3.png（下标从 0 开始，所以是 2）
  const INTRO_REST_POSE = 2;

  // 可用的休息姿态池：新素材全是站姿 idle 帧，任何场合都能用
  function restPosePool() {
    const pool = [];
    for (let i = 0; i < PLAYER_REST_FRAMES; i++) {
      if (gameState === 'battle' && PLAYER_REST_LYING.indexOf(i) >= 0) continue;
      pool.push(i);
    }
    return pool.length ? pool : [0];
  }

  // 随机挑一个休息姿态；再次随机时保证换一张，避免"看着没变"
  function rerollRestPose() {
    const pool = restPosePool();
    if (pool.length < 2) {
      restPoseIdx = pool[0];
    } else {
      let next = restPoseIdx;
      let guard = 0;
      while (next === restPoseIdx && guard++ < 40) next = pool[randInt(0, pool.length - 1)];
      restPoseIdx = next;
    }
    restPoseTimer = rand(PLAYER_REST_REROLL_MIN, PLAYER_REST_REROLL_MAX);
  }

  // 每帧判定：只要"站着不动"就算休息 —— 标题/加载阶段（游戏尚未开始）、
  // 路上停下、战斗中待机，都不该再播走路那一套帧。
  function updateRestPose(dt) {
    // intro = 首页"角色在路上休息，点他登录"的状态
    const standingState = gameState === 'title' || gameState === 'walk' || gameState === 'battle' ||
      gameState === 'intro';
    const resting = standingState && !input.moving && player.vx <= 0 && playerAtkT <= 0;
    if (!resting) {
      playerResting = false;
      restPoseTimer = 0;
      return;
    }
    // 首页固定用第 3 张休息姿态（assets/img/player/player_rest_v2_3.png），不随机换姿势
    if (gameState === 'intro') {
      playerResting = true;
      restPoseIdx = INTRO_REST_POSE;
      restPoseTimer = 0;
      return;
    }
    if (!playerResting) {
      playerResting = true;
      restPoseIdx = restPoseForce >= 0 ? restPoseForce : 0;
      restPoseTimer = 0;
      return;
    }
    if (restPoseForce >= 0) {
      restPoseIdx = restPoseForce;
      restPoseTimer = 0;
      return;
    }
    // 正常休息：帧索引由 restAnimationIndex() 按 6fps 推进
    restPoseTimer = 0;
  }

  // 玩家素材整帧绘制：脚底基线固定在 groundY + 4
  function drawPlayerFrame(img, sx, groundY, scale, flip, bob) {
    if (!img) return;
    const w = PLAYER_FRAME.w * scale;
    const h = PLAYER_FRAME.h * scale;
    const top = groundY + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * scale + (bob || 0) * scale;
    if (flip) {
      ctx.save();
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, top, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(img, sx - w / 2, top, w, h);
    }
  }


  // ---------- 气血反弹：周身血色波纹（中心深、外围淡，一圈圈荡开）----------
  function spawnBloodWave(strength, delay) {
    bloodWaves.push({
      t: -(delay || 0), // 负值表示还没开始荡漾，先不出画面
      life: rand(1.15, 1.5),
      maxR: rand(132, 190),
      strength: strength || 1
    });
    if (bloodWaves.length > 12) bloodWaves.shift();
  }

  // 被攻击触发气血反弹时：从玩家胸口射出一股血气，冲向当前敌人
  function spawnBloodBolt(directionOverride) {
    const dir = typeof directionOverride === 'number'
      ? (directionOverride < 0 ? -1 : 1)
      : ((activeEnemy && activeEnemy.x < player.worldX) ? -1 : 1);
    const startX = player.worldX + dir * 42;
    const hitX = activeEnemy ? (activeEnemy.x - dir * 8) : (startX + dir * 420);
    const hitDist = Math.max(260, Math.abs(hitX - startX));
    // 全程匀速：从身前直线发射，到敌人至少 1 秒，命中后不减速继续飞出屏幕
    const hitT = Math.max(BLOOD_BOLT_MIN_HIT_T, Math.min(1.8, 0.85 + hitDist / 1100));
    const hitSpeed = (hitX - startX) / hitT;
    const postSpeed = (hitSpeed < 0 ? -1 : 1) * Math.max(900, Math.abs(hitSpeed));
    const endX = hitX + postSpeed * BLOOD_BOLT_POST_T;
    const bolt = {
      t: 0,
      life: hitT + BLOOD_BOLT_POST_T,
      startX: startX,
      hitX: hitX,
      endX: endX,
      hitT: hitT,
      postT: BLOOD_BOLT_POST_T,
      hitSpeed: hitSpeed,
      postSpeed: postSpeed,
      laneY: GROUND_Y - 118,
      scale: rand(0.9, 1.02),
      spin: 0,                 // 取消旋转：血气固定朝向直线发射
      flat: 0.78,
      flip: dir < 0,
      strength: 1
    };
    bloodBolts.push(bolt);
    if (bloodBolts.length > 8) bloodBolts.shift();
    bloodBoltSpawnCount++;
    return bolt;
  }

  function updateBloodBolts(dt) {
    for (let i = bloodBolts.length - 1; i >= 0; i--) {
      bloodBolts[i].t += dt;
      if (bloodBolts[i].t >= bloodBolts[i].life) bloodBolts.splice(i, 1);
    }
  }

  function bloodBoltPose(b) {
    if (b.t <= b.hitT) {
      const p = clamp(b.t / b.hitT, 0, 1);
      return {
        x: b.startX + (b.hitX - b.startX) * p,
        p: p, phase: 'fly', speed: b.hitSpeed,
        fade: 1, arc: Math.sin(p * Math.PI) * -6
      };
    }
    const q = clamp((b.t - b.hitT) / b.postT, 0, 1);
    return {
      x: b.hitX + b.postSpeed * (b.t - b.hitT),
      p: q, phase: 'after', speed: b.postSpeed,
      fade: Math.max(0, 1 - Math.pow(q, 3.5)),
      arc: Math.sin(q * Math.PI) * -4
    };
  }

  function updateBloodWaves(dt) {
    // 没学「气血反弹」就不该有波纹（防止读取到旧存档里的过期开关状态）
    if (bloodRebound && !hasBloodReboundSkill()) bloodRebound = false;
    // 盾墙同理：技能没了（换档 / 卖石）就自动关掉，生命占用与格挡随之恢复
    if (shieldWall && !knownSkills.some(function (s) { return s.id === 'shieldWall'; })) {
      shieldWall = false;
      recalcPlayerStats();
    }
    // 激活期间持续从角色脚下荡开
    if (bloodRebound && (gameState === 'walk' || gameState === 'battle')) {
      bloodWaveTimer -= dt;
      if (bloodWaveTimer <= 0) {
        bloodWaveTimer = rand(0.95, 1.25); // 放慢荡漾频率
        spawnBloodWave(1);
      }
    } else {
      bloodWaveTimer = 0;
    }
    for (let i = bloodWaves.length - 1; i >= 0; i--) {
      bloodWaves[i].t += dt;
      if (bloodWaves[i].t >= bloodWaves[i].life) bloodWaves.splice(i, 1);
    }
  }

  function bloodWaveProgress(w) {
    const p = clamp(w.t / w.life, 0, 1);
    // 先快后慢地荡开，接近外圈时明显变淡
    return { p: p, r: w.maxR * (1 - Math.pow(1 - p, 2.2)), fade: (1 - p) * (1 - p) * w.strength };
  }

  // 调试用：查看波纹与玩家屏幕坐标（测试脚本会读取）
  window.__bloodWaves = bloodWaves;
  window.__debugBloodBolts = function () {
    return {
      spawned: bloodBoltSpawnCount,
      drawn: bloodBoltDrawn,
      active: bloodBolts.length,
      imageLoaded: !!(images.bloodReboundFx && images.bloodReboundFx.complete && images.bloodReboundFx.naturalWidth > 0),
      list: bloodBolts.map(function (b) {
        return {
          p: Math.round((b.t / b.life) * 100) / 100,
          t: Math.round(b.t * 1000) / 1000,
          life: Math.round(b.life * 1000) / 1000,
          phase: b.t <= b.hitT ? 'fly' : 'after',
          startX: Math.round(b.startX), hitX: Math.round(b.hitX), endX: Math.round(b.endX),
          hitT: Math.round(b.hitT * 1000) / 1000,
          hitSpeed: Math.round(b.hitSpeed || 0),
          postSpeed: Math.round(b.postSpeed || 0),
          speed: Math.round(b.postSpeed || 0),
          flip: b.flip, spin: Math.round((b.spin || 0) * 100) / 100,
          flat: b.flat
        };
      })
    };
  };
  window.__debugSpawnBloodBolt = function () {
    return !!spawnBloodBolt(1);
  };
  window.__debugSkyFlight = function (force) {
    if (force) {
      skyFlight = null;
      skyFlightTimer = 0;
      updateSkyFlight(0.01);
    }
    return skyFlight ? {
      active: true,
      y: Math.round(skyFlight.y),
      dir: skyFlight.dir,
      art: skyFlight.art || 1,
      t: Math.round(skyFlight.t * 100) / 100,
      dur: Math.round(skyFlight.dur * 100) / 100
    } : { active: false, timer: Math.round(skyFlightTimer * 10) / 10 };
  };
  /* 调试/测试用：强制出一条飞虹并把它拨到指定的行程进度（p=0 刚进屏，1 出屏），
   * 返回它这一帧会画在哪，方便截图和逐像素验收。 */
  window.__debugSkyFlightAt = function (p, art) {
    skyFlight = null;
    skyFlightTimer = 0;
    updateSkyFlight(0.01);
    if (!skyFlight) return null;
    if (art) skyFlight.art = clamp(Math.round(art), 1, SKY_FLIGHT_ART_VARIANTS);
    // 第三个参数：给波形一个额外相位（同一个位置、不同波形，用来验收"波在流动"）
    if (arguments.length > 2) skyFlight.phaseOffset = Number(arguments[2]) || 0;
    const k = clamp(p === undefined ? 0.5 : Number(p), 0, 1);
    skyFlight.t = k * skyFlight.dur;
    const ease = 1 - Math.pow(1 - k, 1.7);
    const depth = clamp((skyFlight.y - 110) / 130, 0, 1);
    return {
      art: skyFlight.art || 1,
      x: Math.round(skyFlight.x0 + (skyFlight.x1 - skyFlight.x0) * ease),
      y: Math.round(skyFlight.y + Math.sin(k * Math.PI * 2) * 12),
      baseY: Math.round(skyFlight.y),
      drawSc: Math.round(skyFlight.scale * (1.18 - 0.5 * depth) * 1000) / 1000,
      phaseOffset: skyFlight.phaseOffset || 0,
      dir: skyFlight.dir,
      scale: Math.round(skyFlight.scale * 100) / 100,
      p: k
    };
  };
  /* 调试/测试用：只改波形相位（不动机位、方向、大小），用来验收"波在沿线流动" */
  window.__debugSkyFlightPhase = function (phase) {
    if (!skyFlight) return null;
    skyFlight.phaseOffset = Number(phase) || 0;
    return { phaseOffset: skyFlight.phaseOffset, x: Math.round(skyFlight.x0 + (skyFlight.x1 - skyFlight.x0) *
      (1 - Math.pow(1 - clamp(skyFlight.t / skyFlight.dur, 0, 1), 1.7))) };
  };
  /* 调试/测试用：只拨"波的时间"（不动机位）——两条线各自的波速不同，
   * 所以拨 waveTime 就能看到它们从分开、追近到几乎并线的过程。 */
  window.__debugSkyFlightWaveTime = function (t) {
    if (!skyFlight) return null;
    skyFlight.waveTime = (t === null || t === undefined) ? null : Number(t) || 0;
    return { waveTime: skyFlight.waveTime };
  };
  /* 调试/测试用：立刻重画一帧（不推进世界时间）。
   * 逐像素验收时这样取"同一瞬间"的两张图，背景（云 / 沙尘）不会动，噪声为 0。 */
  window.__debugDrawNow = function () { draw(); return true; };
  /* 调试/测试用：手动推进一步世界更新（会跑飞虹的"相触"判定） */
  window.__debugSkyFlightStep = function (dt) { updateSkyFlight(Number(dt) || 0.05); return true; };
  /* 调试/测试用：当前两条丝线的贴合情况（贴合处最近距离 / 沿线最远距离） */
  window.__debugSkyTouch = function () {
    const view = skyFlightView();
    if (!view) return null;
    let minGap = null, maxGap = null, at = null;
    for (let i = 0; i <= 30; i++) {
      const d = (i / 30) * SKY_TOUCH_SPAN;
      const y0 = skyThreadOffsetAt(view.threads[0], d, view.sc);
      const y1 = skyThreadOffsetAt(view.threads[1], d, view.sc);
      const gap = Math.abs(y0 - y1);
      if (minGap === null || gap < minGap) { minGap = gap; at = d; }
      if (maxGap === null || gap > maxGap) maxGap = gap;
    }
    return {
      minGap: Math.round(minGap * 100) / 100,
      maxGap: Math.round(maxGap * 100) / 100,
      touchAt: Math.round(at),
      threshold: SKY_TOUCH_GAP,
      sc: Math.round(view.sc * 1000) / 1000
    };
  };
  window.__debugSkyPuffs = function () {
    return {
      count: skyFlightPuffs.length,
      cooldown: Math.round(skyFlightPuffCd * 100) / 100,
      puffs: skyFlightPuffs.map(function (p) {
        return { x: Math.round(p.x), y: Math.round(p.y), t: Math.round(p.t * 100) / 100 };
      })
    };
  };
  window.__debugSkyPuffsClear = function () {
    skyFlightPuffs.length = 0;
    skyFlightPuffCd = 0;
    return true;
  };
  /* 调试/测试用：随机调幅的"振幅轨迹"（每个周期一个倍率，无固定调制） */
  window.__debugSkyAmpTrack = function (threadIdx, count) {
    const w = SKY_THREAD_WAVES[threadIdx ? 1 : 0] || SKY_THREAD_WAVES[0];
    const n = Math.max(1, Math.min(200, Math.floor(count || 24)));
    const seed = ((skyFlight && skyFlight.ampSeed) || 0) + (threadIdx ? 11.3 : 0);
    const out = [];
    for (let i = 0; i < n; i++) out.push(Math.round(skyThreadAmpMul(seed, i) * 1000) / 1000);
    return { min: SKY_AMP_MUL_MIN, max: SKY_AMP_MUL_MAX, seedIsFixed: !w.ampSeed, muls: out };
  };
  /* 调试/测试用：当前这一帧里，沿线每个周期的**实际峰高**（已按基础振幅归一化） */
  window.__debugSkyAmpPeaks = function () {
    const view = skyFlightView();
    if (!view) return null;
    const perThread = [];
    for (let ti = 0; ti < view.threads.length; ti++) {
      const w = view.threads[ti];
      const k = Math.PI * 2 * w.loops;
      const peaks = [];
      for (let m = 0; m < 14; m++) {
        const ph = Math.PI / 2 + m * Math.PI;              // 每个半周期一个峰
        const d = ((ph - w.phase) / k) * 300;
        if (d < 0 || d > 300) continue;
        // 扣掉"随机拉开"的 yBias，只量波形本身的峰高
        const y = skyThreadOffsetAt(w, d, view.sc) - (w.yBias || 0) * view.sc;
        peaks.push(Math.round((Math.abs(y) / (w.amp * view.sc)) * 1000) / 1000);
      }
      perThread.push(peaks);
    }
    return { sc: Math.round(view.sc * 1000) / 1000, peaks: perThread };
  };
  /* 调试/测试用：当前这一帧的"随机拉开"偏移与两条线的间距范围 */
  window.__debugSkySep = function () {
    const view = skyFlightView();
    if (!view) return null;
    let minGap = null, maxGap = null;
    for (let i = 0; i <= 30; i++) {
      const d = (i / 30) * SKY_TOUCH_SPAN;
      const gap = Math.abs(skyThreadOffsetAt(view.threads[0], d, view.sc) -
        skyThreadOffsetAt(view.threads[1], d, view.sc));
      if (minGap === null || gap < minGap) minGap = gap;
      if (maxGap === null || gap > maxGap) maxGap = gap;
    }
    return {
      bias: Math.round((view.threads[1].yBias || 0) * 100) / 100,
      biasA: Math.round((view.threads[0].yBias || 0) * 100) / 100,
      maxSep: SKY_SEP_MAX,
      step: SKY_SEP_STEP,
      sc: Math.round(view.sc * 1000) / 1000,
      minGap: Math.round(minGap * 100) / 100,
      maxGap: Math.round(maxGap * 100) / 100
    };
  };
  /* 调试/测试用：两条丝线各自的波形参数（波速不同 = 会互相追） */
  window.__debugSkyFlightWaves = function () {
    return {
      base: { amp: SKY_WAVE_AMP, loops: SKY_WAVE_LOOPS, speed: SKY_WAVE_SPEED },
      threads: SKY_THREAD_WAVES.map(function (w) {
        return {
          amp: Math.round(SKY_WAVE_AMP * w.amp * 100) / 100,
          loops: Math.round(SKY_WAVE_LOOPS * w.loops * 100) / 100,
          speed: Math.round(SKY_WAVE_SPEED * w.speed * 100) / 100,
          phase: Math.round(w.phase * 100) / 100
        };
      })
    };
  };
  window.__playerScreen = function () {
    return { sx: player.worldX - cameraX(), groundY: GROUND_Y };
  };
  // 调试用：一次拿到测试关心的运行时状态
  // 调试用：记录刀光贴图每一帧真实的绘制矩形（x / y / 尺寸），便于核对落点
  const fxTrace = [];
  window.__debugFxTrace = function () {
    return fxTrace.splice(0, fxTrace.length);
  };

  window.__debugState = function () {
    return {
      gameState: gameState,
      worldX: Math.round(player.worldX),
      distanceKm: Math.round((player.worldX - START_X) / KM_UNIT * 100) / 100,
      level: player.level,
      stageName: stageName(player.level),
      xp: Math.floor(player.xp),
      gold: gold,
      vitality: vitality,
      attrPoints: player.attrPoints,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      mp: Math.round(combat.mp * 10) / 10,
      maxMp: combat.maxMp,
      mpUsable: derived.mpUsable,
      mpReservePct: derived.mpReservePct,
      pet: leadPet() ? {
        name: leadPet().name,
        hp: Math.round(leadPet().hp),
        maxHp: leadPet().maxHp,
        skillId: leadPet().skillId || null
      } : null,
      pets: alivePets().map(function (p) {
        const slot = pets.indexOf(p);
        return {
          name: p.name, level: p.level || 1, sprite: p.sprite || null, slot: slot,
          hp: Math.round(p.hp), maxHp: p.maxHp,
          x: Math.round(petScreenX(slot)), playerX: Math.round(player.worldX - cameraX())
        };
      }),
      petAttacks: petAttackCount,
      ally: ally ? { name: ally.name, turns: ally.turns } : null,
      autoAdventure: autoAdventure,
      logLen: battleLogHistory.length,
      logOpen: !ui.logPopup.classList.contains('hidden'),
      enemy: activeEnemy ? {
        name: activeEnemy.name,
        tier: activeEnemy.tier,
        legend: !!activeEnemy.legend,
        elite: !!activeEnemy.elite,
        scale: enemyDrawScale(activeEnemy)
      } : null,
      quake: quakeOffset()
      ,
      caveImg: (function () {
        const im = images.caveEntrance;
        return im && im.complete && im.naturalWidth > 0 ? im.naturalWidth + 'x' + im.naturalHeight : null;
      })(),
      caveToken: caveToken,
      skillStones: countSkillStones(),
      turn: battleTurn,
      cave: cave ? { id: cave.def.id, name: cave.def.name, total: cave.total, remaining: cave.remaining, killed: cave.killed } : null,
      caveBarVisible: !ui.caveBar.classList.contains('hidden'),
      group: battleAlive().length,
      transition: caveTransition ? { t: Math.round(caveTransition.t * 100) / 100, dur: caveTransition.dur, def: caveTransition.def.id, playerAlpha: Math.round(playerFx.alpha * 100) / 100, playerScale: Math.round(playerFx.scale * 100) / 100 } : null,
      entrances: caveEntrances.map(function (c) {
        return { id: c.def.id, used: c.used, worldX: Math.round(c.worldX), sx: Math.round(c.worldX - cameraX()) };
      })
    };
  };

  // 调试用：直接召唤一只传奇BOSS（省去跑完 10 波地图）
  window.__debugSpawnLegend = function () {
    spawnLegendEnemy();
    return activeEnemy ? activeEnemy.name : null;
  };
  // 调试用：直接发一次精英挑战机会
  window.__debugEliteCharge = function () {
    eliteCharges += 1;
    updateEliteButton();
    return eliteCharges;
  };
  // 调试/测试：直接生成 1~3 个系统精英挑战敌人
  window.__debugSpawnEliteGroup = function (count) {
    const group = spawnEliteEnemy(count);
    return {
      count: group.length,
      list: group.map(function (e) {
        return { name: e.name, level: e.level, tier: e.tier, hp: e.maxHp, atk: e.atk, skill: e.skill ? e.skill.name : null };
      })
    };
  };
  // 调试/测试：当前属性口径下的敌人属性表
  window.__debugEnemyStatTable = function (levels) {
    const list = (levels || [1, 10, 20, 30, 40]).map(function (level) {
      const base = enemyBaseStats(level);
      return {
        level: level,
        boost: enemyLevelStatBoost(level),
        common: enemyScaledStats(level, 'common'),
        magic: enemyScaledStats(level, 'magic'),
        elite: enemyScaledStats(level, 'elite'),
        base: base
      };
    });
    return { list: list, multipliers: ENEMY_TIER_STAT_MULT };
  };
  // 调试用：玩家角色素材与当前动作（待机 / 赶路 / 出手）
  window.__debugPlayerAnim = function () {
    return {
      key: lastPlayerFrameKey,
      atkT: Math.round(playerAtkT * 1000) / 1000,
      atkDur: PLAYER_ATK_DUR,
      animT: Math.round(player.animT * 100) / 100,
      walkT: Math.round(player.walkT * 100) / 100,
      vx: Math.round(player.vx * 100) / 100,
      moving: !!input.moving,
      resting: playerResting,
      restPose: restPoseIdx,
      restPoseTimer: Math.round(restPoseTimer * 10) / 10,
      restLoaded: playerRestLoadedCount(),
      restTotal: PLAYER_REST_FRAMES,
      flip: PLAYER_SPRITE_FLIP,
      face: playFaceDir(),                 // 1 = 朝右（素材原方向），-1 = 朝左（镜像绘制）
      drawnFlip: (playFaceDir() < 0) ? !PLAYER_SPRITE_FLIP : PLAYER_SPRITE_FLIP,
      sx: Math.round(player.worldX - cameraX()),
      groundY: GROUND_Y,
      drawTop: Math.round(GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * playerFx.scale * caveScale()),
      frame: { w: PLAYER_FRAME.w, h: PLAYER_FRAME.h, basePad: PLAYER_FRAME.basePad },
      loaded: [1, 2, 3, 4].every(function (i) {
        const im = images['player_idle_' + i];
        return !!(im && im.complete && im.naturalWidth > 0);
      }) && [1, 2, 3, 4, 5].every(function (i) {
        const im = images['player_atk_' + i];
        return !!(im && im.complete && im.naturalWidth > 0);
      }),
      size: (function () {
        const im = images.player_idle_1;
        return im ? im.naturalWidth + 'x' + im.naturalHeight : null;
      })()
    };
  };
  // 调试用：手动播一次出手动作
  window.__debugPlayerAttack = function () {
    playPlayerAttack();
    return playerAtkT;
  };
  // 调试用：临时隐藏玩家角色（量屏幕位置用）
  window.__debugHidePlayer = function (on) {
    debugHidePlayer = !!on;
    return debugHidePlayer;
  };
  // 调试用：隐藏敌人 / 查敌人美术信息 / 指定类型刷一个敌人（截图用）
  window.__debugHideEnemies = function (on) {
    debugHideEnemies = !!on;
    return debugHideEnemies;
  };
  // 调试用：当前敌人的等级 + 它"本该是多少级"（验证临时副本按对应境界定级）
  window.__debugEnemyLevel = function () {
    if (!activeEnemy) return null;
    const def = cave ? cave.def : null;
    return {
      name: activeEnemy.name, level: activeEnemy.level, tier: activeEnemy.tier,
      cave: def ? def.id : null, activity: !!(def && def.activity),
      expect: def ? caveEnemyLevel(def) : null,
      playerLevel: playerLevel()
    };
  };
  window.__debugEnemyInfo = function () {
    const def = enemySpriteDef(activeEnemy);
    const im = images[def.key + '_1'];
    return {
      name: activeEnemy ? activeEnemy.name : null,
      sprite: activeEnemy ? activeEnemy.sprite : null,
      tier: activeEnemy ? activeEnemy.tier : null,
      level: activeEnemy ? activeEnemy.level : null,
      boss: !!(activeEnemy && activeEnemy.boss),
      elite: !!(activeEnemy && activeEnemy.elite),
      hp: activeEnemy ? Math.round(activeEnemy.hp) : null,
      maxHp: activeEnemy ? activeEnemy.maxHp : null,
      atk: activeEnemy ? activeEnemy.atk : null,
      scale: activeEnemy ? Math.round(enemyDrawScale(activeEnemy) * 100) / 100 : null,
      frames: def.frames,
      frameSize: im && im.complete && im.naturalWidth > 0 ? im.naturalWidth + 'x' + im.naturalHeight : null,
      loadedFrames: (function () {
        let n = 0;
        for (let i = 1; i <= def.frames; i++) {
          const f = images[def.key + '_' + i];
          if (f && f.complete && f.naturalWidth > 0) n++;
        }
        return n;
      })(),
      allTypes: ENEMY_SPRITES.map(function (s) { return s.key; }),
      loadedTypes: ENEMY_SPRITES.filter(function (s) {
        const f = images[s.key + '_1'];
        return f && f.complete && f.naturalWidth > 0;
      }).length,
      frame: { w: ENEMY_FRAME.w, h: ENEMY_FRAME.h, basePad: ENEMY_FRAME.basePad, anchorX: ENEMY_FRAME.anchorX },
      drawTop: Math.round(GROUND_Y + 4 - (ENEMY_FRAME.h - ENEMY_FRAME.basePad) * (activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : 1)),
      baselineY: Math.round(GROUND_Y + 4),
      sx: activeEnemy ? Math.round(activeEnemy.x - cameraX()) : null
    };
  };
  /* 调试/测试用：敌人模型池（all = 全部；road = 会被随机抽到的那些）
   *   —— 用来确认"副本专属敌人"不会跑到马路 / 洞府 / 其它副本里。 */
  window.__debugEnemyPool = function () {
    return {
      all: ENEMY_SPRITES.map(function (s) { return s.key; }),
      road: enemySpritePool().map(function (s) { return s.key; }),
      dungeonOnly: ENEMY_SPRITES.filter(function (s) { return !!s.dungeonOnly; })
        .map(function (s) { return s.key; })
    };
  };
  /* 调试用：敌人当前播到第几帧（验证新的跑步帧动画真的在推进）
   *   walk = 赶路（一轮 ENEMY_WALK_CYCLE 秒）；其它 = 战斗站定（慢速循环） */
  window.__debugEnemyFrame = function () {
    if (!activeEnemy) return null;
    const def = enemySpriteDef(activeEnemy);
    const walking = gameState === 'walk';
    const fps = walking ? enemyWalkFps(def) : enemyStandFps(def);
    const t = enemyAnimClock + (activeEnemy.animPhase ? activeEnemy.animPhase : 0);
    return {
      key: def.key, name: def.name, frames: def.frames,
      fps: Math.round(fps * 1000) / 1000,
      idx: Math.abs(Math.floor(t * fps)) % def.frames + 1,
      state: gameState,
      cycleSec: walking ? ENEMY_WALK_CYCLE : ENEMY_STAND_CYCLE,
      frameSize: (function () {
        const im = images[def.key + '_1'];
        return im && im.complete && im.naturalWidth > 0 ? im.naturalWidth + 'x' + im.naturalHeight : null;
      })()
    };
  };
  window.__debugSpawnEnemyType = function (key) {
    const def = ENEMY_SPRITES.filter(function (s) { return s.key === key; })[0];
    if (!def) return null;
    spawnNextEnemy();
    if (!activeEnemy) return null;
    activeEnemy.sprite = def.key;
    activeEnemy.kindName = def.name;
    activeEnemy.name = def.name;
    activeEnemy.baseX = player.worldX + 420;
    activeEnemy.x = activeEnemy.baseX;
    return { name: activeEnemy.name, sx: Math.round(activeEnemy.x - cameraX()) };
  };
  // 调试用：重新刷一个敌人（每次都会随机一种类型），用来验证"随机模型"
  window.__debugRespawnEnemy = function () {
    spawnNextEnemy();
    return activeEnemy ? { sprite: activeEnemy.sprite, name: activeEnemy.name } : null;
  };
  /* 调试 / 测试用：冒险道路品级保底
   *   普通→蓝色：3 个；蓝色→金色稀有（精英）：3 个 */
  window.__debugRoadPity = function () {
    return {
      commonKills: roadCommonKills,
      magicKills: roadMagicKills,
      nextTier: roadNextEnemyTier(),
      needBlue: ROAD_BLUE_AFTER,
      needGold: ROAD_GOLD_AFTER
    };
  };
  window.__debugRoadReset = function () {
    roadCommonKills = 0;
    roadMagicKills = 0;
    return window.__debugRoadPity();
  };
  window.__debugRoadKill = function (tier, n) {
    const times = Math.max(1, Math.floor(Number(n) || 1));
    for (let i = 0; i < times; i++) roadKillTick(tier || 'common');
    return window.__debugRoadPity();
  };
  window.__debugRoadSpawn = function () {
    spawnNextEnemy();
    if (!activeEnemy) return null;
    return {
      tier: activeEnemy.tier, name: activeEnemy.name,
      elite: !!activeEnemy.elite, hp: activeEnemy.maxHp, atk: activeEnemy.atk,
      skill: activeEnemy.skill ? activeEnemy.skill.name : null
    };
  };
  // 调试用：直接给一件装备并穿上（测洗练 / 截图用）
  window.__debugGiveGear = function (level, rarity, slot, weaponKind) {
    const item = makeEquipment(level || 1, rarity || 'rare', slot, weaponKind);
    equipItem(item);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: item.id, name: item.name, slot: item.slot, level: item.level, rarity: item.rarity, detail: item.detail, icon: item.icon, iconImg: item.iconImg || 0, weaponType: item.weaponType };
  };
  // 调试用：造一件装备（不穿上），用于检查图标分配
  window.__debugMakeEquipment = function (level, rarity, slot, weaponKind) {
    const item = makeEquipment(level || 10, rarity || 'rare', slot, weaponKind);
    return {
      id: item.id, name: item.name, slot: item.slot, level: item.level, rarity: item.rarity,
      icon: item.icon, iconSet: item.iconSet || null, iconImg: item.iconImg || 0,
      weaponType: item.weaponType || null, detail: item.detail,
      modCount: (item.mods || []).length        // 词缀条数（验收「金色 3/4/5/6 条」的分布）
    };
  };
  /* 调试/测试用：造一件防具，只给指定的三维词缀，看基底防御定成了哪种性质、数值是多少 */
  window.__debugBaseDef = function (level, slot, attrKey) {
    const it = makeEquipment(level || 1, 'common', slot || 'helmet', null);
    it.mods = attrKey ? [{ key: attrKey, value: 5, value2: 0, bandIdx: -1, locked: false }] : [];
    it.modCount = it.mods.length;
    applyItemMods(it);
    return {
      slot: it.slot, level: it.level,
      kind: it.baseDefKind || null, value: it.baseDefValue || 0,
      armor: it.armor || 0, evasion: it.evasion || 0, mp: it.mp || 0,
      baseDetail: it.baseDetail || ''
    };
  };
  window.__debugBaseDefBands = function () {
    return BASE_DEFENSE_BANDS.map(function (b) { return { minLevel: b.minLevel, min: b.min, max: b.max }; });
  };
  /* 调试/测试用：读某件装备的词缀；
   *   传第二个参数就是"写死一组词缀再按当前规则重算"（验证武器不该有的词缀会被清掉） */
  window.__debugItemMods = function (id, mods) {
    const all = inventory.filter(function (x) { return x.type === 'equipment'; })
      .concat(Object.keys(equipment).map(function (k) { return equipment[k]; }).filter(Boolean));
    const it = (id ? all.filter(function (x) { return x.id === id; })[0] : all[0]) || null;
    if (!it) return null;
    if (Array.isArray(mods)) {
      it.mods = mods.map(function (m) {
        return { key: m.key, value: m.value || 1, value2: m.value2 || 0, bandIdx: -1, locked: !!m.locked };
      });
      it.modCount = it.mods.length;
      delete it.weaponModsCleaned;
      ensureItemMods(it);
      recalcPlayerStats();
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    }
    return {
      id: it.id, name: it.name, slot: it.slot, weaponType: it.weaponType || null,
      mods: (it.mods || []).map(function (m) { return m.key; }),
      baseMods: (it.baseMods || []).map(function (m) { return m.key; }),
      detail: it.detail || '', baseDetail: it.baseDetail || '',
      res: it.res || 0, speedPct: it.speedPct || 0, thorns: it.thorns || 0, atk: it.atk || 0,
      armor: it.armor || 0, evasion: it.evasion || 0, mp: it.mp || 0,
      baseDefKind: it.baseDefKind || null, baseDefValue: it.baseDefValue || 0,
      cleaned: !!it.weaponModsCleaned
    };
  };
  // 调试用：直接改金币（测洗练费用）
  window.__debugGold = function (value) {
    if (typeof value === 'number') {
      gold = Math.max(0, Math.round(value));
      updateGold();
      refreshReforgeIfOpen();
    }
    return gold;
  };
  // 调试用：当前洗练窗口的费用（含锁定加成）
  window.__debugReforgeCost = function () {
    if (!reforgeItem) return null;
    return {
      cost: reforgeCost(reforgeItem),
      level: reforgeItem.level,
      locked: (reforgeItem.mods || []).filter(function (m) { return m.locked; }).length,
      count: reforgeItem.reforgeCount || 0,
      max: REFORGE_MAX
    };
  };
  // 调试用：给当前装备加/减锁定条数（批量验证费用）
  window.__debugSetLocks = function (n) {
    if (!reforgeItem) return null;
    (reforgeItem.mods || []).forEach(function (m, i) { m.locked = i < n; });
    refreshReforgeIfOpen();
    return (reforgeItem.mods || []).filter(function (m) { return m.locked; }).length;
  };
  // 调试用：把当前洗练窗口里的装备次数清零（测洗练逻辑时反复用）
  window.__debugResetReforgeCount = function () {
    if (!reforgeItem) return null;
    reforgeItem.reforgeCount = 0;
    refreshReforgeIfOpen();
    return 0;
  };
  // 调试用：当前洗练窗口里那件装备的词缀明细（含锁定状态）
  window.__debugReforgeMods = function () {
    if (!reforgeItem) return null;
    return {
      name: reforgeItem.name,
      baseDetail: reforgeItem.baseDetail || '',
      mods: (reforgeItem.mods || []).map(function (m) {
        return { key: m.key, value: m.value, value2: m.value2 || 0, locked: !!m.locked };
      })
    };
  };
  // 调试用：返回某类装备的词缀池（含各档位区间），测试用它核对"洗练不越级"
  window.__debugAffixPool = function (slot, weaponType) {
    const pool = affixPoolFor(slot, weaponType);
    return pool.map(function (m) {
      return { key: m.key, bands: m.bands || null, bands2: m.bands2 || null };
    });
  };
  // 调试用：直接对当前穿着的装备洗练一次（不经过界面，批量验证用）
  window.__debugReforgeOnce = function (slotKey) {
    const key = slotKey || Object.keys(equipment).filter(function (k) { return equipment[k]; })[0];
    const item = equipment[key];
    if (!item) return null;
    rollItemAffixes(item);
    return item.detail;
  };
  /* ---------- 调试用：装备镶嵌 ---------- */
  // 取某件装备的完整快照（背包 + 已装备都能取；含镶嵌信息与逐条词缀）
  window.__debugItemById = function (id) {
    const all = inventory.concat(Object.keys(equipment).map(function (k) { return equipment[k]; })
      .filter(Boolean));
    const it = (id ? all.filter(function (x) { return x.id === id; })[0] : null) ||
      all.filter(function (x) { return x.type === 'equipment'; })[0];
    if (!it) return null;
    return {
      id: it.id, name: it.name, slot: it.slot, level: it.level, rarity: it.rarity,
      weaponType: it.weaponType || null, baseName: it.baseName || null,
      inlay: it.inlay || null, detail: it.detail || '', baseDetail: it.baseDetail || '',
      mods: (it.mods || []).map(function (m) {
        return { key: m.key, value: m.value, value2: m.value2 || 0, bandIdx: m.bandIdx,
          locked: !!m.locked, inlay: !!m.inlay };
      })
    };
  };
  // 发几颗入侵材料（不给 id 就随机一种）
  window.__debugGiveInvasionMaterial = function (id, n) {
    const count = Math.max(1, Math.floor(n || 1));
    const def = id ? invasionMaterialDef(id) : INVASION_MATERIALS[randInt(0, INVASION_MATERIALS.length - 1)];
    if (!def) return null;
    const out = [];
    for (let i = 0; i < count; i++) {
      const it = makeInvasionMaterial(def);
      inventory.push(it);
      out.push(it.id);
    }
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { materialId: def.id, name: def.name, ids: out };
  };
  // 打开某件装备（按 id，背包或已装备都行）的镶嵌窗口
  window.__debugOpenInlayById = function (id) {
    const all = inventory.concat(Object.keys(equipment).map(function (k) { return equipment[k]; })
      .filter(Boolean));
    const item = (id ? all.filter(function (x) { return x.id === id; })[0] : null) ||
      all.filter(function (x) { return x.type === 'equipment'; })[0];
    if (!item) return null;
    openInlay(item);
    return window.__debugInlayState();
  };
  // 镶嵌窗口的即时状态（供测试核对）
  window.__debugInlayState = function () {
    const stock = inlayMaterialStock();
    return {
      open: !!(ui.inlayPopup && !ui.inlayPopup.classList.contains('hidden')),
      target: inlayTarget ? {
        id: inlayTarget.id, name: inlayTarget.name, slot: inlayTarget.slot,
        level: inlayTarget.level, detail: inlayTarget.detail,
        inlay: inlayTarget.inlay || null
      } : null,
      picked: inlayPickId,
      stock: Object.keys(stock).map(function (k) {
        return { materialId: k, name: stock[k].def.name, prefix: stock[k].def.prefix, count: stock[k].count };
      }),
      preview: ui.inlayPreview ? ui.inlayPreview.textContent.replace(/\s+/g, ' ').trim() : '',
      buttonText: ui.btnInlay ? ui.btnInlay.textContent : null
    };
  };
  // 直接镶嵌（等价于点「镶嵌」按钮）
  window.__debugInlayNow = function (itemId, materialId) {
    const all = inventory.concat(Object.keys(equipment).map(function (k) { return equipment[k]; })
      .filter(Boolean));
    const item = all.filter(function (x) { return x.id === itemId; })[0] ||
      (inlayTarget && inlayTarget.id === itemId ? inlayTarget : null);
    if (!item) return { ok: false, reason: 'no_item' };
    return inlayEquipment(item, materialId);
  };
  // 调试用：最近一次词缀随机的明细（每条词缀用了哪一档、抽到多少）
  window.__debugLastRoll = function () {
    return lastAffixRolls.map(function (r) { return { key: r.key, level: r.level, bandIdx: r.bandIdx, value: r.value, value2: r.value2 }; });
  };
  // 调试用：武器图标 —— 造一批武器看随机分配 / 已加载数量 / 每个物品的图标
  window.__debugWeaponIcons = function (n) {
    const items = [];
    const total = n || 12;
    for (let i = 0; i < total; i++) {
      const it = makeEquipment(1 + i, null, 'weapon');
      items.push({ name: it.name, weaponType: it.weaponType, iconImg: it.iconImg });
    }
    let loaded = 0;
    for (let i = 1; i <= WEAPON_ICON_COUNT; i++) {
      const im = images['wicon_' + i];
      if (im && im.complete && im.naturalWidth > 0) loaded++;
    }
    return { total: WEAPON_ICON_COUNT, loaded: loaded, items: items };
  };
  // 调试用：给背包塞 N 件随机武器（看图标列表）
  window.__debugFillBagWithWeapons = function (n) {
    for (let i = 0; i < (n || 6); i++) inventory.push(makeEquipment(1 + i * 2, null, 'weapon'));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.filter(function (it) { return it.type === 'equipment'; }).length;
  };
  // 调试用：账号 / 存档
  // 角色结算后的进阶属性（图鉴 / 装备加成是否真的进了面板，用这个断言）
  window.__debugDerived = function () {
    return {
      maxHp: combat.maxHp,
      maxMp: combat.maxMp,
      hp: Math.round(combat.hp),
      armor: Math.round(derived.armor || 0),
      blockPct: derived.blockPct || 0,
      // 防具基底：闪避点数 / 闪避提高% / 由点数换算出的闪避几率
      evasion: Math.round(derived.evasion || 0),
      evasionPct: derived.evasionPct || 0,
      dodgeFromGear: derived.dodgeFromGear || 0,
      dodgeFromAgi: derived.dodgeFromAgi || 0,
      // 新增词缀：物理伤害减免 + 两项上限（生效值 / 上限 / 抗性原始值）
      physReducePct: derived.physReducePct || 0,
      resCapPct: derived.resCapPct || 0,
      blockCapPct: derived.blockCapPct || 0,
      resCap: derived.resCap || 0,
      blockCap: derived.blockCap || 0,
      // 新增：闪避 / 总减伤上限（默认 75% / 90%，只有「上限 +X%」词缀能抬）
      dodgePct: derived.dodgePct || 0,
      dodgeCap: derived.dodgeCap || 0,
      dodgeCapPct: derived.dodgeCapPct || 0,
      reduceCap: derived.reduceCap || 0,
      reduceCapPct: derived.reduceCapPct || 0,
      resRaw: derived.resRaw || 0,
      thorns: Math.round(derived.thorns || 0),
      res: derived.res || 0,
      chaosRes: derived.chaosRes || 0,
      strength: derived.strength || 0,
      // 力量合计（装备 + 加点）—— 「震撼大地」的冲击波就是按它加成的，方便核对
      strengthTotal: derived.strengthTotal || 0,
      attrStr: derived.attrStr || 0,
      agility: derived.agility || 0,
      intelligence: derived.intelligence || 0,
      lifeRegen: derived.lifeRegen || 0,
      mpRegen: derived.mpRegen || 0,
      extraArrowPct: derived.extraArrowPct || 0,
      critChance: derived.critChance || 0,
      armorPct: derived.armorPct || 0,
      speedPct: derived.speedPct || 0,
      speedMult: Math.round((derived.speedMult || 1) * 1000) / 1000,   // 野外移动倍率
      flatAtk: derived.flatAtk || 0,          // 攻击力加成（装备/等级给的）
      atkMin: 23 + (derived.flatAtk || 0),    // 普攻区间
      atkMax: 34 + (derived.flatAtk || 0),
      spellDmgPct: derived.spellDmgPct || 0,
      // 手套专属词缀（生效后的合计值）
      lifePerKill: derived.lifePerKill || 0,
      mpPerKill: derived.mpPerKill || 0,
      lifePerHit: derived.lifePerHit || 0,
      poisonChance: derived.poisonChance || 0,
      bleedChance: derived.bleedChance || 0,
      igniteChance: derived.igniteChance || 0,
      atkSpeed: derived.atkSpeed || 0,
      // 盾牌专属词缀
      dmgReducePct: derived.dmgReducePct || 0,
      recoupPct: derived.recoupPct || 0,
      lifeReservePct: derived.lifeReservePct || 0
    };
  };
  /* 调试/测试用：盾墙状态 + 碎石崩裂的伤害预览 */
  window.__debugShieldWall = function () {
    const learned = knownSkills.some(function (s) { return s.id === 'shieldWall'; });
    const blockPct = derived.blockPct || 0;
    const atkPower = 28.5 + (derived.flatAtk || 0);
    return {
      learned: learned,
      active: !!shieldWall,
      lifeReservePct: derived.lifeReservePct || 0,
      maxHp: combat.maxHp,
      blockPct: blockPct,
      atkPart: Math.max(1, Math.round(atkPower * blockPct / 100)),
      lifePart: Math.max(1, Math.round((derived.maxHp || 100) * SHIELD_WALL_PROC_LIFE_PCT / 100)),
      /* 碎石 = 投射物：多重投射物 / 投射物加速 对它的实际影响（测试脚本直接读这里） */
      multiProjectiles: skillHasGem('shieldWall', 'multipleProjectiles'),
      projectileSpeed: skillHasGem('shieldWall', 'projectileSpeed'),
      shards: projectileCountOf('shieldWall', ROCK_FX_COUNT),
      hits: projectileHitsOf('shieldWall', 1),
      projSpeedMult: projectileSpeedMult('shieldWall'),
      stonesReady: rockSpriteReady(),
      fxRunning: !!rockFx,
      recoupPool: Math.round(recoupPool)
    };
  };
  // 调试/测试用：手动触发一次「碎石崩裂」（验证伤害与特效，不用等格挡）
  window.__debugShieldWallProc = function () {
    const dmg = shieldWallProc();
    return { dmg: dmg, state: window.__debugShieldWall() };
  };
  /* 调试/测试用：投射物标签体系一览（多重投射物 / 投射物加速 到底能增幅谁）
   *   谁带 tags: ['projectile']、镶了宝石后投射物几个、独立结算几次、伤害/速度乘多少 —— 全在这里。 */
  window.__debugProjectiles = function () {
    const out = {
      hint: projectileSkillHint(),
      extra: MULTI_PROJ_EXTRA, dmgPct: MULTI_PROJ_DMG_PCT,
      speedPct: MULTI_PROJ_SPEED_PCT, projSpeedPct: PROJ_SPEED_PCT,
      skills: {}
    };
    ACTIVE_SKILL_POOL.concat(SKILL_POOL).forEach(function (d) {
      const id = d.id;
      const proj = isProjectileSkill(id);
      let visual = 1;
      if (id === 'airSlash') visual = AIRSLASH_FX_WAVES;
      if (id === 'powerShot') visual = POWER_SHOT_BASE_PROJ;
      if (id === 'shieldWall') visual = ROCK_FX_COUNT;
      const sk = skillById(id);
      out.skills[id] = {
        name: d.name,
        tags: skillTagsOf(id),
        projectile: proj,
        projectileNote: d.projectileNote || null,
        learned: !!sk,
        sockets: sk ? (sk.sockets || []).map(function (g) { return g.effect; }) : [],
        hasMulti: skillHasGem(id, 'multipleProjectiles'),
        hasProjSpeed: skillHasGem(id, 'projectileSpeed'),
        projectileCount: projectileCountOf(id, visual),
        hits: projectileHitsOf(id, 1),
        dmgMult: Math.round((1 + supportDamagePct(id, null) / 100) * 100) / 100,
        speedMult: Math.round(skillSpeedMult(id) * 100) / 100,
        projSpeedMult: Math.round(projectileSpeedMult(id) * 100) / 100
      };
    });
    return out;
  };
  // 调试/测试用：给定一次"敌人原始伤害"，算出实际会掉多少血（含盾牌减伤）
  window.__debugDamagePreview = function (raw, critMult) {
    return playerDamageAfterReduction(Number(raw) || 0, critMult);
  };
  /* 调试/测试用：一次看全「防御上限」相关数值（护甲减伤 / 总减伤上限 / 闪避 / 格挡） */
  window.__debugDefense = function () {
    const passive = playerPassiveDamageMult();
    return {
      armor: Math.round(derived.armor), res: derived.res, resCap: derived.resCap,
      chaosRes: derived.chaosRes,
      reduction: passive.reduction, reductionPct: Math.round(passive.reduction * 1000) / 10,
      gearReduce: passive.gearReduce, physReduce: passive.physReduce,
      // 主减伤栈（护甲/抗性 + 盾牌减伤，封顶 90%）与叠加额外物理减免之后的总减伤
      mainReducePct: passive.mainReducePct,
      mainMult: passive.mainMult,
      passiveMult: passive.mult, totalReducePct: Math.round((1 - passive.mult) * 1000) / 10,
      cap: passive.cap, reduceCapPct: derived.reduceCapPct || 0,
      dodgePct: derived.dodgePct, dodgeCap: derived.dodgeCap, dodgeCapPct: derived.dodgeCapPct || 0,
      dodgeFromAgi: derived.dodgeFromAgi || 0, dodgeRaw: derived.dodgeRaw || 0,
      // 防具基底：闪避点数 / 「闪避提高 X%」 / 点数换算来的闪避几率
      evasion: derived.evasion || 0,
      evasionPct: derived.evasionPct || 0,
      dodgeFromGear: derived.dodgeFromGear || 0,
      blockPct: derived.blockPct, blockCap: derived.blockCap, blockCapPct: derived.blockCapPct || 0
    };
  };
  /* 调试/测试用：按「护甲 / 抗性」直接算这一层的减伤（不改角色数据） */
  window.__debugArmorReduction = function (armor, res, chaosRes) {
    const r = armorResReduction(Number(armor) || 0, Number(res) || 0, Number(chaosRes) || 0);
    return { armor: Number(armor) || 0, reduction: r, pct: Math.round(r * 1000) / 10 };
  };
  /* 调试/测试用：清掉玩家身上的控制 / 点燃状态
   * （洞府敌人的增幅是随机分配的，长流程测试脚本会被"寒冰/震慑"卡住整回合） */
  window.__debugClearPlayerDebuff = function () {
    playerStunTurns = 0;
    playerFrozenTurns = 0;
    playerBurnStacks = 0;
    return { stun: playerStunTurns, frozen: playerFrozenTurns, burn: playerBurnStacks };
  };
  // 调试/测试用：当前敌人的持续伤害状态（流血 / 中毒 / 燃烧层数）
  window.__debugEnemyStatus = function () {
    if (!activeEnemy) return null;
    return {
      name: activeEnemy.name, hp: Math.round(activeEnemy.hp), maxHp: Math.round(activeEnemy.maxHp),
      bleed: activeEnemy.bleed ? { dmg: Math.round(activeEnemy.bleed.dmg), turns: activeEnemy.bleed.turns } : null,
      poison: activeEnemy.poison ? { dmg: Math.round(activeEnemy.poison.dmg), turns: activeEnemy.poison.turns } : null,
      burnStacks: activeEnemy.burnStacks || 0
    };
  };
  window.__debugGacha = function () {
    const last = gachaResultList && gachaResultList.length ? gachaResultList[gachaResultList.length - 1] : null;
    return {
      gold: gold,
      pity: gachaPity,
      pityMax: GACHA_PITY_MAX,
      count: gachaCount,
      single: GACHA_SINGLE_COST,
      ten: GACHA_TEN_COST,
      lastTier: last ? last.tier : null,
      lastTierName: last ? last.tierName : null,
      lastPityFlag: last ? !!last.pity : false
    };
  };
  window.__debugDoGacha = function (n) { doGacha(n || 1); return window.__debugGacha(); };
  window.__debugSetPity = function (n) { gachaPity = Math.max(0, Math.floor(n || 0)); renderGacha(); return gachaPity; };

  /* 调试用：抽卡动画状态（播放中 / 已经揭晓了几张卡） */
  window.__debugGachaAnim = function () {
    return {
      playing: gachaAnimPlaying(),
      stageHidden: !ui.gachaStage || ui.gachaStage.classList.contains('hidden'),
      backCount: ui.gachaStageCards ? ui.gachaStageCards.children.length : 0,
      cardCount: ui.gachaResults ? ui.gachaResults.querySelectorAll('.gachaCard').length : 0,
      results: (gachaResultList || []).length,
      oneDisabled: !!(ui.gachaOne && ui.gachaOne.disabled),
      tenDisabled: !!(ui.gachaTen && ui.gachaTen.disabled)
    };
  };
  // 调试用：跳过动画直接揭晓
  window.__debugGachaSkipAnim = function () {
    if (gachaAnimPlaying()) finishGachaAnimation(function () { renderGachaResults(false); });
    return window.__debugGachaAnim();
  };
  // 调试用：属性点 / 在线奖励 / 装备价格
  window.__debugGainXp = function (n) { gainXp(n || 0); return { level: player.level, xp: Math.floor(player.xp), attrPoints: player.attrPoints }; };
  // 调试用：尸体掉落（光柱判定用）
  window.__debugClearCorpse = function () { corpse = null; return true; };
  // 调试用：直接搜索当前尸体（等同点击地上的尸体）
  window.__debugSearchCorpse = function () { searchCorpse(false); return true; };
  // 调试用：给全身普通装备槽塞上装备（测洞府阵亡掉落）
  window.__debugEquipFullSet = function () {
    ['helmet', 'weapon', 'clothes', 'shoes', 'gloves', 'jewelry'].forEach(function (slot) {
      const g = makeEquipment(player.level, 'common', slot);   // 第三个参数要传字符串槽位
      g.iconSet = slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[slot] || null);
      ensureItemIcon(g);
      equipment[slot] = g;
    });
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return Object.keys(equipment).filter(function (s) { return !!equipment[s]; })
      .map(function (s) {
        const g = equipment[s];
        return s + ':' + g.name + '(槽=' + g.slot + ' 类型=' + (g.weaponType || '-') + ')';
      });
  };
  // 调试用：在洞府里直接召一只指定品级的怪（测洞府掉落用）
  window.__debugSpawnCaveEnemyTier = function (tier) {
    if (!cave) return false;
    const def = cave.def;
    const enemy = buildEnemy(caveEnemyLevel(def),
      tier || def.tiers[0], { worldX: player.worldX + 560, cave: true });
    battleGroup.length = 0;
    battleGroup.push(enemy);
    activeEnemy = enemy;
    battleTurn = 'player';
    resetPlayerIdleTimer();
    enemyTurnTimer = 0;
    gameState = 'walk';
    return { tier: enemy.tier, name: enemy.name };
  };

  /* ---------- 洞府敌人增幅 调试钩子 ---------- */
  window.__debugCaveBuffList = function () {
    return CAVE_BUFFS.map(function (b) { return { id: b.id, name: b.name, desc: b.desc, color: b.color }; });
  };
  window.__debugCaveBuffState = function () {
    return {
      // 场上带增幅的敌人（野外敌人应为空）
      buffs: (battleGroup.length ? battleGroup : (activeEnemy ? [activeEnemy] : []))
        .filter(function (e) { return e && e.caveBuff; })
        .map(function (e) { return { name: e.name, id: e.caveBuff.id, name2: e.caveBuff.name, stunUsed: !!e.stunUsed }; }),
      stunTurns: playerStunTurns,
      frozenTurns: playerFrozenTurns,
      burnStacks: playerBurnStacks,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      turn: battleTurn,
      inBattle: gameState === 'battle'
    };
  };
  // 给当前敌人强制指定一种增幅（不传就随机）
  window.__debugSetCaveBuff = function (id) {
    if (!activeEnemy) return null;
    if (!id) { activeEnemy.caveBuff = CAVE_BUFFS[randInt(0, CAVE_BUFFS.length - 1)]; }
    else {
      activeEnemy.caveBuff = CAVE_BUFFS.filter(function (b) { return b.id === id; })[0] || null;
    }
    activeEnemy.stunUsed = false;
    return window.__debugCaveBuffState();
  };
  /* 调试/测试用：清掉本场战斗里所有洞府敌人的增幅。
   * 洞府增幅（烈焰光环 / 反伤 / 寒冰…）会让长流程用例变得不稳定 —— 例如
   * 「一发完美时机箭同时打 3 个反伤怪」会在一帧里把测试角色弹死，战斗提前结束。 */
  window.__debugClearCaveBuffs = function () {
    let n = 0;
    battleAlive().forEach(function (e) { if (e.caveBuff) { e.caveBuff = null; n++; } });
    if (activeEnemy && activeEnemy.caveBuff) { activeEnemy.caveBuff = null; n++; }
    return n;
  };
  // 立刻结算一次敌方回合（含增幅的命中效果）
  window.__debugEnemyTurnNow = function () {
    if (gameState !== 'battle' || !activeEnemy) return null;
    battleTurn = 'enemy';
    enemyCounterAttack(false);
    const snap = window.__debugCaveBuffState();
    // 测试用：把回合安顿回玩家，免得后台计时器又补一次敌方行动，干扰断言
    if (battleTurn === 'enemy') {
      battleTurn = 'player';
      enemyTurnTimer = 0;
      resetPlayerIdleTimer();
      setControls('battle');
    }
    return snap;
  };
  // 直接把玩家血量设成指定值（测反伤 / 光环时用，避免被打死）
  window.__debugSetHpExact = function (v) {
    combat.hp = clamp(Number(v) || 0, 0, combat.maxHp);
    updateBattleHud();
    return Math.round(combat.hp);
  };
  // 调试用：把当前战斗里所有敌人的血量拉到指定值（做"打完还活着"的测试用）
  window.__debugEnemyHp = function (v) {
    const list = battleAlive();
    list.forEach(function (e) {
      const hp = Math.max(1, Math.round(Number(v) || 0));
      e.maxHp = Math.max(e.maxHp || 0, hp);
      e.hp = hp;
    });
    return window.__debugBleed();
  };
  // 调试用：洞府增幅特效的自查（画了几个敌人、在屏幕哪个位置）
  window.__debugCaveFx = function () {
    const list = caveBuffEnemies();
    return {
      gameState: gameState,
      battleGroup: battleGroup.length,
      count: list.length,
      list: list.map(function (e) {
        return { id: e.caveBuff.id, sx: Math.round(e.x - cameraX()), hp: Math.round(e.hp), scale: Math.round(caveScale() * 100) / 100 };
      }),
      groundY: GROUND_Y
      , groundCalls: caveFxGroundCalls
    };
  };
  // 调试用：一击放倒当前敌人（走正常出手流程，会正常留下可搜索的尸体）
  window.__debugKillEnemy = function () {
    if (gameState !== 'battle' || !activeEnemy || battleTurn !== 'player' || pendingVictory > 0) return false;
    activeEnemy.hp = 1;
    doPlayerAction('attack');
    return true;
  };
  /* 调试/测试用：受击击退的实时状态（位移只在绘制上，敌人世界坐标不变） */
  window.__debugKnockState = function () {
    const e = activeEnemy;
    const r3 = function (v) { return Math.round(v * 1000) / 1000; };
    return {
      inBattle: gameState === 'battle',
      knockT: e ? r3(e.knockT || 0) : null,
      knockDur: e ? (e.knockDur || 0) : null,
      knockPx: e ? Math.round((e.knockPx || 0) * 10) / 10 : null,
      offset: e ? Math.round(enemyKnockOffset(e) * 10) / 10 : null,
      hurtT: e ? r3(e.hurtT || 0) : null,
      enemyX: e ? Math.round(e.x) : null,
      group: battleGroup.filter(function (g) { return g !== e; }).map(function (g) {
        return { offset: Math.round(enemyKnockOffset(g) * 10) / 10, knockT: r3(g.knockT || 0) };
      })
    };
  };
  window.__debugKnock = function (px) {
    if (!activeEnemy) return false;
    startEnemyKnock(activeEnemy, px);
    return window.__debugKnockState();
  };
  // 调试用：量尸体上方那束光柱（只有掉装备时才会亮起来）。
  // 沙漠背景本身很亮，所以用"和没有尸体时的底图做差分"挑出光柱，而不是看绝对亮度。
  window.__debugSamplePillar = function (mode) {
    const cv = document.getElementById('game');
    const g = cv.getContext('2d');
    const probe = (typeof window.__pillarProbeX === 'number') ? window.__pillarProbeX : 510;
    const x0 = Math.max(0, probe - 90);
    const w = Math.min(180, W - x0);
    const y0 = Math.max(0, GROUND_Y - 320);
    const h = Math.min(320, GROUND_Y - y0);
    const cur = g.getImageData(x0, y0, w, h).data;
    if (mode === 'base') {
      window.__pillarProbeX = probe;
      window.__pillarBase = { x0: x0, y0: y0, w: w, h: h, d: cur };
      return { stored: true, x0: x0, y0: y0, w: w, h: h };
    }
    const base = window.__pillarBase;
    if (!base || base.x0 !== x0) return null;
    let n = 0, r = 0, gg = 0, bb = 0, minY = 1e9, maxY = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const dr = cur[i] - base.d[i];
      const dg = cur[i + 1] - base.d[i + 1];
      const db = cur[i + 2] - base.d[i + 2];
      // 光柱是加法混合：三个通道一起被提亮
      if (dr > 14 && dg > 14 && db > 14) {
        n++; r += dr; gg += dg; bb += db;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
    return {
      brightened: n, corpseSx: corpse ? Math.round(corpse.worldX - cameraX()) : null,
      r: n ? Math.round(r / n) : 0, g: n ? Math.round(gg / n) : 0, b: n ? Math.round(bb / n) : 0,
      topY: n ? (y0 + minY) : null, bottomY: n ? (y0 + maxY) : null
    };
  };
  window.__debugCorpseState = function () {
    const drops = (corpse && corpse.drops) ? corpse.drops.map(function (d) {
      return {
        kind: d.kind || d.type, name: d.name || '',
        rarity: d.rarity || null, rarityColor: d.rarityColor || null, coins: d.coins || 0
      };
    }) : null;
    return {
      hasCorpse: !!corpse, searched: corpse ? !!corpse.searched : null,
      worldX: corpse ? Math.round(corpse.worldX) : null,
      forceGear: corpse ? !!corpse.forceGear : null,
      drops: drops, topRarity: corpseTopRarity(),
      gearRarities: (corpse && corpse.drops)
        ? corpse.drops.filter(function (d) { return d.type === 'equipment'; }).map(function (d) { return d.rarity; })
        : []
    };
  };
  // 调试用：抽 N 次装备掉落，看概率分布（force=洞府必掉）
  window.__debugGearDropSample = function (tier, n, force) {
    const out = { tier: tier || 'common', force: force !== false, n: Math.max(1, n || 300), counts: {}, perKill: {}, has: {} };
    const order = ['common', 'magic', 'rare', 'legend'];
    for (let i = 0; i < out.n; i++) {
      const list = rollGearRarities(out.tier, out.force);
      out.perKill[list.length] = (out.perKill[list.length] || 0) + 1;
      list.forEach(function (r) { out.counts[r] = (out.counts[r] || 0) + 1; });
      order.forEach(function (r) { if (list.indexOf(r) >= 0) out.has[r] = (out.has[r] || 0) + 1; });
    }
    out.rate = {};       // 每件装备的品级占比
    out.hasRate = {};    // "这只怪掉出了该品级"的比例
    order.forEach(function (k) {
      if (out.counts[k]) out.rate[k] = Math.round(out.counts[k] / out.n * 1000) / 10;
      if (out.has[k]) out.hasRate[k] = Math.round(out.has[k] / out.n * 1000) / 10;
    });
    out.none = out.perKill[0] || 0;                     // 空手的次数
    out.avgPieces = Math.round(
      Object.keys(out.perKill).reduce(function (s, k) { return s + Number(k) * out.perKill[k]; }, 0) / out.n * 100
    ) / 100;
    out.maxRarity = order.filter(function (k) { return out.counts[k]; }).pop() || null;
    return out;
  };
  // 调试用：光柱的"柔和度"剖面（中心轴纵向衰减 + 最亮那行横向羽化）
  window.__debugPillarProfile = function () {
    if (!corpse || !window.__pillarBase) return null;
    const cv = document.getElementById('game');
    const g = cv.getContext('2d');
    const base = window.__pillarBase;
    const cur = g.getImageData(base.x0, base.y0, base.w, base.h).data;
    const csx = Math.round(corpse.worldX - cameraX()) - base.x0;
    if (csx < 0 || csx >= base.w) return null;
    const delta = function (x, y) {
      const i = (y * base.w + x) * 4;
      return Math.max(0, (cur[i] - base.d[i]) + (cur[i + 1] - base.d[i + 1]) + (cur[i + 2] - base.d[i + 2]));
    };
    const axis = [];
    // 只量"尸体上方"那一段：尸体的美术和「点击搜索」文字本身也会让像素变化，
    // 混进来会把纵向曲线搞出一堆假跳变（那不是光柱的锅）
    const axisEnd = Math.max(4, Math.min(base.h, GROUND_Y - 80 - base.y0));
    for (let y = 0; y < axisEnd; y += 4) axis.push(delta(csx, y));
    let bestRow = 0, bestVal = -1;
    axis.forEach(function (v, k) { if (v > bestVal) { bestVal = v; bestRow = k; } });
    const row = [];
    for (let x = 0; x < base.w; x++) row.push(delta(x, bestRow * 4));
    return {
      axis: axis, row: row, peak: bestVal, peakY: base.y0 + bestRow * 4,
      cx: base.x0 + csx, rowWidth: base.w, rowHeight: base.h
    };
  };
  // 调试用：直接在路边摆一具带指定品级装备的尸体（验证光柱特效）
  window.__debugForceCorpse = function (rarity) {
    if (!corpse || corpse.searched) {
      const level = Math.max(1, player.level);
      const drops = [{ kind: 'gold', name: '金币', icon: '金', detail: '+10', coins: 10, rarityColor: '#ffd970' }];
      if (rarity) drops.push(makeEquipment(level, rarity));
      corpse = {
        worldX: player.worldX + 150, level: level, tier: 'common',
        sprite: (ENEMY_SPRITES[0] && ENEMY_SPRITES[0].key) || 'bingfei', searched: false, drops: drops
      };
    }
    return window.__debugCorpseState();
  };
  // 调试用：血瓶充能进度 / 商店等级
  window.__debugFlask = function (charges, common, elite) {
    if (typeof charges === 'number') flaskCharges = clamp(Math.round(charges), 0, 5);
    if (typeof common === 'number') flaskKillCommon = Math.max(0, Math.round(common));
    if (typeof elite === 'number') flaskKillElite = Math.max(0, Math.round(elite));
    updateFlaskUI();
    return { charges: flaskCharges, common: flaskKillCommon, elite: flaskKillElite,
      needCommon: FLASK_COMMON_NEED, needElite: FLASK_ELITE_NEED,
      title: ui.btnFlask ? ui.btnFlask.title : null };
  };
  window.__debugAddKill = function (isElite) { addFlaskKill(!!isElite, true); return window.__debugFlask(); };
  window.__debugShopLevel = function () {
    return { playerLevel: player.level, defeated: defeatedEnemies, shopLevel: getShopItemLevel() };
  };
  // 调试用：清空本机所有档案（验证"新建档案从起点开始"）
  window.__debugWipeAll = function () { resetAllSaves(); resetProgressState(); return true; };
  // 调试用：赶路扬尘（验证跑步扬尘）
  window.__debugRunDust = function () {
    return {
      running: gameState === 'walk' && !deathActive && player.vx > 0 && playerAtkT <= 0,
      vx: Math.round(player.vx),
      playerX: Math.round(player.worldX - cameraX()),
      count: runDust.length,
      drawn: dustDrawn,   // 累计绘制过的尘粒数（>0 说明渲染路径确实跑到了）
      particles: runDust.slice(0, 8).map(function (d) {
        return {
          x: Math.round(d.x), y: Math.round(d.y),
          r: Math.round(d.r * 10) / 10,
          progress: Math.round(d.t / d.life * 100) / 100,
          behind: d.x < (player.worldX - cameraX())
        };
      })
    };
  };
  // 调试用：当前存档里的里程（验证移动距离存档）
  window.__debugSavedDistance = function () {
    try {
      const d = JSON.parse(window.localStorage.getItem(activeSaveKey()) || '{}');
      return { distance: d.distance, defeatedEnemies: d.defeatedEnemies };
    } catch (e) { return null; }
  };
  /* ---------- 调试钩子：独特装备 ---------- */
  window.__debugUniqueList = function () {
    return UNIQUE_DEFS.map(function (d) {
      return { id: d.id, name: d.name, slot: d.slot, weaponType: d.weaponType || null, lines: d.lines.slice() };
    });
  };
  window.__debugGiveUnique = function (id) {
    const def = id ? UNIQUE_BY_ID[id] : UNIQUE_DEFS[randInt(0, UNIQUE_DEFS.length - 1)];
    if (!def) return null;
    const it = makeUniqueItem(def.id, playerLevel() + 2);
    inventory.push(it);
    if (ui.gearPopup && !ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return uniqueSnapshot(it);
  };
  // 直接把独特装备穿到对应部位（测机制用）
  window.__debugEquipUnique = function (id) {
    const def = UNIQUE_BY_ID[id];
    if (!def) return null;
    const it = makeUniqueItem(def.id, playerLevel() + 2);
    equipItem(it);
    return Object.assign(uniqueSnapshot(it), { derived: uniqueDerivedSnapshot() });
  };
  window.__debugUniqueDerived = function () { return uniqueDerivedSnapshot(); };
  window.__debugFireArrowReady = function () { return fireArrowFramesReady(); };
  // 熔岩骤雨的触发几率开关（暂时固定 100%，测完改回 null）
  window.__debugLavaRainOverride = function () { return LAVA_RAIN_OVERRIDE_PCT; };
  // 世界BOSS宝箱的独特装备判定（window.__uniqueRollForce 可强制必出）
  window.__debugUniqueChestRoll = function () {
    const it = rollUniqueFromChest();
    return it ? uniqueSnapshot(it) : null;
  };
  // 金色精英及以上敌人的独特装备判定（window.__uniqueRollForce 可强制必出）
  window.__debugUniqueEnemyRoll = function (tier) {
    const it = rollUniqueFromEnemy({ tier: tier || 'elite', level: playerLevel() });
    return it ? uniqueSnapshot(it) : null;
  };
  /* 测试用赠送：临时把账号设成指定名字跑一次赠送判定（不改变真实登录账号） */
  window.__debugTestGrant = function (accountName) {
    const prev = account.name;
    account.name = accountName || 'AAA';
    const it = maybeGrantTestUnique();
    account.name = prev;
    return it ? uniqueSnapshot(it) : null;
  };
  window.__debugGrantState = function () {
    const bag = inventory.filter(function (it) { return it && it.unique; })
      .map(function (it) { return it.name; });
    const worn = Object.keys(equipment).filter(function (k) { return equipment[k] && equipment[k].unique; })
      .map(function (k) { return k + ':' + equipment[k].name; });
    return { account: account.name, bagUniques: bag, wornUniques: worn };
  };

  function uniqueSnapshot(it) {
    if (!it) return null;
    return {
      id: it.unique, name: it.name, slot: it.slot, weaponType: it.weaponType,
      rarity: it.rarity, level: it.level,
      stats: Object.assign({}, it.uniqueStats),
      mech: Object.assign({}, it.mech),
      detail: it.detail,
      baseDetail: it.baseDetail,
      sell: sellPrice(it)
    };
  }
  function uniqueDerivedSnapshot() {
    const d = derived || {};
    return {
      blockPct: d.blockPct, thorns: d.thorns, critImmune: !!d.critImmune,
      stunImmune: !!d.stunImmune, blockHealPct: d.blockHealPct || 0,
      thornsCritPct: d.thornsCritPct || 0, atkDmgPct: d.atkDmgPct || 0,
      projDmgPct: d.projDmgPct || 0, flatAtk: d.flatAtk || 0,
      resCapPct: d.resCapPct || 0, resCap: d.resCap || 0,
      strengthTotal: d.strengthTotal || 0,
      intTotal: d.intTotal || 0, mpPer20AgiPct: d.mpPer20AgiPct || 0,
      agilityTotal: d.agilityTotal || 0,
      maxMp: d.maxMp || 0,
      mech: Object.assign({}, d.unique || {})
    };
  }

  // 调试用：灵宝（金色洞府终点掉落）
  window.__debugMakeTreasure = function (level, forceKey) {
    const it = makeEquipment(level || 10, 'legend', 'treasure');
    if (forceKey) {
      const sp = TREASURE_SPECIALS.filter(function (s) { return s.key === forceKey; })[0] || TREASURE_SPECIALS[0];
      attachTreasureSpecial(it, sp);
    } else {
      attachTreasureSpecial(it, TREASURE_SPECIALS[randInt(0, TREASURE_SPECIALS.length - 1)]);
    }
    inventory.push(it);
    return { name: it.name, level: it.level, rarity: it.rarity, special: it.special, detail: it.detail };
  };
  window.__debugTreasureState = function () {
    const it = equipment.treasure;
    return {
      equipped: it ? { name: it.name, special: it.special } : null,
      maxHp: derived.maxHp,
      flatAtk: derived.flatAtk,
      strengthTotal: derived.strengthTotal,
      strengthHp: derived.strengthHp,
      strHpPer: treasureSpecial('strToHpDouble') ? 10 : 5,
      alwaysFirst: treasureSpecial('alwaysFirst'),
      goldPerWin: treasureSpecial('goldPerWin'),
      hpFlat1000: treasureSpecial('hpFlat1000'),
      lifesteal: treasureSpecial('lifesteal10'),
      skills: knownSkills.map(function (s) { return s.name; })
    };
  };
  window.__debugClearTreasure = function () {
    if (equipment.treasure) inventory.push(equipment.treasure);
    equipment.treasure = null;
    recalcPlayerStats();
    return true;
  };
  // 调试用：直接学会某个技能（按 id）
  window.__debugLearnSkill = function (id) {
    const s = learnSkillById(id);
    return s ? s.name : null;
  };
  // 调试用：自动冒险优先技能（激活 / 取消 / 查询）
  window.__debugAutoSkill = function () {
    const fav = autoSkillId ? knownSkills.filter(function (s) { return s.id === autoSkillId; })[0] : null;
    return { id: autoSkillId, name: fav ? fav.name : null };
  };
  window.__debugToggleAutoSkill = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return { id: autoSkillId };
    toggleAutoSkill(skill);
    return window.__debugAutoSkill();
  };
  // 调试用：技能的镶嵌槽信息 / 花钱开槽 / 指定镶嵌槽数量
  window.__debugSocketInfo = function (skillId) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    return {
      id: skill.id, name: skill.name,
      slots: socketSlotsOf(skill),
      sockets: (skill.sockets || []).length,
      max: SOCKET_SLOTS_MAX, cost: SOCKET_SLOT_COST, gold: gold
    };
  };
  window.__debugBuySlot = function (skillId) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    buySocketSlot(skill);
    return window.__debugSocketInfo(skillId);
  };
  window.__debugSetSocketSlots = function (skillId, n) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    skill.socketSlots = clamp(Math.floor(n), SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return window.__debugSocketInfo(skillId);
  };
  // 调试用：休息回血（把休息计时快进 seconds 秒，返回这次恢复的生命）
  window.__debugRestHeal = function (seconds) {
    const before = combat.hp;
    restHealTimer += Math.max(0, Number(seconds) || 0);
    updateRestHeal(0);
    return {
      timer: Math.round(restHealTimer * 100) / 100,
      hpBefore: Math.round(before), hpAfter: Math.round(combat.hp),
      healed: Math.round(combat.hp - before), maxHp: combat.maxHp
    };
  };
  window.__debugSetHp = function (v) {
    combat.hp = clamp(Number(v) || 0, 1, combat.maxHp);
    updateBattleHud();
    return Math.round(combat.hp);
  };
  /* 调试/测试用：把灵力（法力槽）设成**绝对值**。
   *   注意：老的 __debugSetMp(pct) 收的是"百分比"，而且传 0 会被当成 100%（`Number(0)||100`），
   *   所以这里单独给一个精确版钩子。 */
  window.__debugSetMpExact = function (v) {
    combat.mp = clamp(Number(v) || 0, 0, derived.mpUsable);
    updateBattleHud();
    return Math.round(combat.mp);
  };
  window.__debugMp = function () {
    return { mp: Math.round(combat.mp), usable: Math.round(derived.mpUsable), max: Math.round(combat.maxMp) };
  };
  // 调试用：直接施放某个技能，返回命中明细（验证群攻）
  // 调试用：给某个技能镶嵌一个指定效果的技能石（验证专属辅助）
  window.__debugSocketGem = function (effect, skillId, value) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
    if (!opt) return null;
    const gem = gemOptionToItem(opt);
    if (typeof value === 'number' && !opt.support) {
      gem.value = value;
      gem.detail = opt.name + ' +' + value + opt.unit;
    }
    inventory.push(gem);
    socketGemInto(skill, gem);
    return {
      skill: skill.name,
      effect: gem.effect,
      sockets: (skill.sockets || []).map(function (g) { return g.name; }),
      refused: (skill.sockets || []).indexOf(gem) < 0
    };
  };
  // 调试用：流血 / 冲击波状态
  // 调试用：阳爪特效状态（分身数量 / 进度 / 当前位置）
  window.__debugYangClawFx = function () {
    if (!yangClawFx) return null;
    const fromX = yangClawFx.fromX - cameraX();
    const toX = yangClawFx.toX - cameraX();
    return {
      t: Math.round(yangClawFx.t * 100) / 100,
      dur: yangClawFx.dur,
      clones: yangClawFx.clones.map(function (c) {
        const local = yangClawFx.t - c.delay;
        const p = local < 0 ? 0 : clamp(local / c.dur, 0, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        return { delay: Math.round(c.delay * 100) / 100, p: Math.round(p * 100) / 100,
          x: Math.round(fromX + (toX - fromX) * ease), active: local >= 0 && local <= c.dur };
      })
    };
  };
  // 调试用：立刻进入敌方回合（结算流血等）
  window.__debugEndTurn = function () {
    afterPlayerOffense();
    return battleTurn;
  };
  // 调试用：核对「震撼大地」冲击波的伤害公式（普通攻击 50% + 力量）
  // 调试用：新手宝箱 / 阵亡相关
  // 调试用：已学技能快照（含镶嵌的技能石）
  window.__debugSkillState = function () {
    return {
      skills: knownSkills.map(function (s) {
        return {
          id: s.id, name: s.name, level: s.level || 1, type: s.type,
          sockets: (s.sockets || []).map(function (g) { return g.name + '(' + (g.detail || '') + ')'; })
        };
      })
    };
  };
  // 调试用：「强力射击」蓄力 / 完美时机状态
  window.__debugPowerState = function () {
    const target = (battleAlive()[0]) || activeEnemy || null;
    return {
      charging: !!powerCharge,
      perfect: !!(powerCharge && powerCharge.perfect),
      timing: powerCharge && powerCharge.timing
        ? {
          pos: Math.round(powerCharge.timing.pos * 1000) / 1000,
          dir: powerCharge.timing.dir,
          t: Math.round(powerCharge.timing.t * 100) / 100,
          done: powerCharge.timing.done,
          result: powerCharge.timing.result
        }
        : null,
      zone: POWER_TIMING_ZONE.slice(),
      vulnPct: POWER_SHOT_VULN_PCT,
      mult: POWER_SHOT_MULT,
      perfectMult: POWER_SHOT_PERFECT_MULT,
      stunTurns: target ? (target.stunTurns || 0) : 0,
      enemyCount: battleAlive().length,
      turn: battleTurn,
      playerStun: playerStunTurns,
      playerFrozen: playerFrozenTurns,
      playerHp: Math.round(combat.hp)
    };
  };
  // 调试用：把标记推到指定位置再按空格（传 0.7 = 落在完美区间内）
  window.__debugPressTiming = function (pos) {
    if (powerCharge && powerCharge.timing && typeof pos === 'number') {
      powerCharge.timing.pos = Math.max(0, Math.min(1, pos));
    }
    return pressPerfectTiming();
  };
  // 调试用：「强力射击」箭矢特效的实时状态（箭数 / 每支箭的飞行 Y / 命中中心）
  window.__debugPowerArrowFx = function () {
    if (!powerArrowFx) return null;
    const t0 = powerArrowFx.arrows[0] ? powerArrowFx.arrows[0].t : 0;
    return {
      count: powerArrowFx.arrows.length,
      arrows: powerArrowFx.arrows.map(function (a) {
        return {
          t: Math.round(a.t * 1000) / 1000,
          rel: Math.round((a.t - t0) * 1000) / 1000,   // 相对第一支箭的出发延迟（负数＝更晚出发）
          dur: Math.round(a.dur * 1000) / 1000,
          sy: Math.round(a.sy), ey: Math.round(a.ey), sx: Math.round(a.sx),
          ex: Math.round(a.ex),                        // 飞行终点（铁箍战弓会越过画面右边界）
          dmg: a.dmg || 0,                             // 这一支单独结算的伤害
          big: !!a.big
        };
      }),
      ex: Math.round(powerArrowFx.ex),
      ey: Math.round(powerArrowFx.ey),
      perfect: powerArrowFx.perfect,
      pierceReturn: !!powerArrowFx.pierceReturn,
      boom: powerArrowFx.boom
    };
  };
  // 调试用：看某件装备的基底词缀（盾牌 +15% 格挡）
  window.__debugBaseMods = function (item) {
    const it = (item && item.slot) ? item : equipment.weapon;
    if (!it) return null;
    return {
      weaponType: it.weaponType,
      baseDetail: it.baseDetail || '',
      blockPct: it.blockPct || 0,
      extraArrowPct: it.extraArrowPct || 0,
      randomMods: (it.mods || []).length
    };
  };
  /* 调试/平衡用：列出手上这把武器 / 指定等级的武器能滚出来的「攻击伤害」区间
   *   ① 不传参数：返回当前装备武器的实际攻击伤害
   *   ② 传 level：返回该等级四种武器 × 白/蓝/金的区间（公式与 rollOneMod 同一套）
   *   ③ 不传参数但传 true：返回「每个境界」的整张表（境界起点最低档 ~ 境界终点最高档） */
  /* 调试/测试用：看某个部位能滚出哪些词缀（只要 key 列表与条数）
   *   注意：上面已有一个同名的 __debugAffixPool（返回整份词缀定义，洗练用例在用），
   *   这里换个名字，别把它覆盖了。 */
  window.__debugAffixPoolKeys = function (slot, weaponType) {
    const pool = affixPoolFor(slot || 'helmet', weaponType || null);
    return { slot: slot, weaponType: weaponType || null,
      keys: pool.map(function (m) { return m.key; }), count: pool.length };
  };
  window.__debugWeaponAtkTable = function (arg) {
    if (!arg) {
      const w = equipment.weapon;
      return w ? { name: w.name, weaponType: w.weaponType, level: w.level, atk: w.atk || 0 } : null;
    }
    const KINDS = ['长弓', '短剑', '爪', '法杖'];
    const bounds = function (kind, level) {
      return { white: weaponAtkBounds(kind, level, 1), blue: weaponAtkBounds(kind, level, 2),
        rare: weaponAtkBounds(kind, level, 3) };
    };
    if (typeof arg === 'number') {
      const lv = Math.max(1, Math.round(arg));
      const out = { level: lv, name: stageName(lv), kinds: {} };
      KINDS.forEach(function (k) { out.kinds[k] = bounds(k, lv); });
      return out;
    }
    // true → 每个境界一整行
    const rows = [];
    let i = 1;
    while (i <= MAX_STAGE) {
      const realm = stageInfo(i).realm;
      let last = i;
      while (last + 1 <= MAX_STAGE && stageInfo(last + 1).realm === realm) last++;
      const row = {
        realm: realm, levelFrom: i, levelTo: last,
        nameFrom: stageName(i), nameTo: stageName(last), kinds: {}
      };
      KINDS.forEach(function (k) {
        // 区间 = 境界起点（白装）的最低值 ~ 境界终点（金装）的最高值
        row.kinds[k] = {
          white: weaponAtkBounds(k, i, 1),
          rare: weaponAtkBounds(k, last, 3),
          span: [weaponAtkBounds(k, i, 1).min, weaponAtkBounds(k, last, 3).max]
        };
      });
      rows.push(row);
      i = last + 1;
    }
    return { band: AFFIX_BAND_SIZE, maxStage: MAX_STAGE, kinds: KINDS, rows: rows };
  };
  // 调试用：统计长弓基底「额外箭矢」的触发率（纯函数，不打架）
  window.__debugBowStats = function () {
    return {
      extraArrowPct: derived.extraArrowPct, attacks: bowStat.attacks, procs: bowStat.procs,
      ratio: bowStat.attacks ? Math.round(bowStat.procs / bowStat.attacks * 1000) / 1000 : 0
    };
  };
  window.__debugResetBowStats = function () { bowStat.attacks = 0; bowStat.procs = 0; return true; };
  window.__debugRollBow = function (n) {
    const total = Math.max(1, Math.floor(n || 1000));
    let hit = 0;
    for (let i = 0; i < total; i++) if (rollExtraArrow()) hit++;
    return { extraArrowPct: derived.extraArrowPct, n: total, hit: hit, ratio: Math.round(hit / total * 1000) / 1000 };
  };
  // 调试用：首饰基底词缀的分布（造 n 件首饰，看抽到哪一条）
  // 调试用：反复生成首饰直到抽到指定的基底词缀，然后放进背包（不伪造数据，只是筛）
  window.__debugGiveJewelryWithBase = function (label) {
    for (let i = 0; i < 4000; i++) {
      const it = makeEquipment(10, 'rare', 'jewelry');
      if (it.baseDetail === label) {
        inventory.push(it);
        saveGame();
        if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
        return { id: it.id, name: it.name, baseDetail: it.baseDetail, mpRegen: it.mpRegen || 0, lifeRegen: it.lifeRegen || 0 };
      }
    }
    return null;
  };
  // 调试用：造一件首饰并把完整数据返回（检查"全能力 +5"这类效果）
  window.__debugMakeJewelry = function (rarity) {
    const it = makeEquipment(10, rarity || 'rare', 'jewelry');
    return {
      name: it.name, baseDetail: it.baseDetail || '', detail: it.detail || '',
      strength: it.strength || 0, agility: it.agility || 0, intelligence: it.intelligence || 0,
      lifeRegen: it.lifeRegen || 0, mpRegen: it.mpRegen || 0, armorPct: it.armorPct || 0,
      speedPct: it.speedPct || 0, critChance: it.critChance || 0,
      mods: (it.mods || []).map(function (m) { return modToText(m); })
    };
  };
  // 调试用：往背包塞一个回血药瓶（测叠加角标）
  window.__debugAddPotion = function (n) {
    const times = Math.max(1, Math.floor(n || 1));
    for (let i = 0; i < times; i++) {
      const pot = {
        id: 'pot' + (++itemSeq), type: 'potion', name: '回血药瓶', icon: '药',
        detail: '恢复 30% 生命', heal: 0.3, price: 100
      };
      inventory.push(pot);
    }
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.filter(function (it) { return it.type === 'potion'; }).length;
  };
  window.__debugRollJewelryBase = function (n) {
    const total = Math.max(1, Math.floor(n || 30));
    const counts = {};
    const samples = [];
    for (let i = 0; i < total; i++) {
      const it = makeEquipment(10, 'rare', 'jewelry');
      const label = it.baseDetail || '(无)';
      counts[label] = (counts[label] || 0) + 1;
      if (i < 3) {
        samples.push({
          baseDetail: label,
          strength: it.strength || 0, agility: it.agility || 0, intelligence: it.intelligence || 0,
          lifeRegen: it.lifeRegen || 0, mpRegen: it.mpRegen || 0,
          armorPct: it.armorPct || 0, speedPct: it.speedPct || 0, critChance: it.critChance || 0,
          randomMods: (it.mods || []).length
        });
      }
    }
    return { n: total, counts: counts, samples: samples };
  };
  // 调试用：直接设定某个技能的等级（验证"每级 +10% 基础伤害"）
  window.__debugSetSkillLevel = function (id, lv) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    skill.level = Math.max(1, Math.floor(lv || 1));
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return window.__debugSkillInfo(id);
  };
  window.__debugSkillInfo = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    return {
      id: skill.id, name: skill.name, level: skill.level || 1,
      dmg: skill.dmg ? [skill.dmg[0], skill.dmg[1]] : null,
      levelMult: Math.round(skillLevelMult(skill) * 1000) / 1000,
      dmgLevelMult: Math.round(skillDamageLevelMult(skill) * 1000) / 1000,
      // 实际结算时的基础伤害区间（劈砍走刀光演出，单独看 slashFx）
      effDmg: skill.dmg
        ? [Math.round(skill.dmg[0] * skillDamageLevelMult(skill)),
           Math.round(skill.dmg[1] * skillDamageLevelMult(skill))]
        : null
    };
  };
  window.__debugSlashDamage = function () {
    const base = slashWeaponDamageBase(skillById('slash'));
    return {
      weaponAtk: weaponAttackPower(),
      weaponPct: 75,
      base: base,
      doubleChance: SLASH_DOUBLE_PCT,
      max: base * 2
    };
  };
  window.__debugRollSlashDamage = function () {
    const base = slashWeaponDamageBase(skillById('slash'));
    const doubleHit = Math.random() * 100 < SLASH_DOUBLE_PCT;
    return { base: base, doubleHit: doubleHit, damage: base * (doubleHit ? 2 : 1) };
  };
  // 调试用：已移除的技能不该还能学到
  window.__debugLearnRemoved = function (id) {
    return isRemovedSkillId(id) ? 'REMOVED' : (learnSkillById(id) ? 'LEARNED' : 'NO_SUCH_SKILL');
  };
  // 调试用：最近一次技能伤害计算（等级加成 / 距离加成 / 最终基础伤害）
  window.__debugLastSkillCalc = function () { return lastSkillCalc; };
  // 调试用：主循环 / 后台继续运行的状态
  window.__debugLoopState = function () {
    return {
      hidden: !!document.hidden,
      bgTicker: !!bgTicker,
      bgTicks: worldClock.bgTicks,
      catchupSeconds: Math.round(worldClock.catchupSeconds * 10) / 10,
      lastTime: Math.round(lastTime),
      gameState: gameState,
      worldX: Math.round(player.worldX),
      distanceKm: Math.round((player.worldX - START_X) / KM_UNIT * 100) / 100,
      vitality: vitality,
      onlineSeconds: Math.round(onlineSeconds),
      autoAdventure: autoAdventure
    };
  };
  // 调试用：手动推进世界（等价于"网页在后台待了 N 秒"）
  window.__debugAdvance = function (seconds) {
    const before = window.__debugLoopState();
    advanceWorld(Number(seconds) || 0);
    lastTime = performance.now();
    const after = window.__debugLoopState();
    return { before: before, after: after };
  };
  // 调试用：世界BOSS 状态 / 强制刷新 / 传送 / 虚影信息
  window.__debugWorldBoss = function () {
    const def = openWorldBossDef();
    const run = worldBossRun;
    return {
      defs: WORLD_BOSSES.map(function (d) {
        return { id: d.id, level: d.level, name: d.name, place: d.place, open: !!d.open, theme: d.theme };
      }),
      hpBossId: wb.hpBossId,
      openName: def ? def.name : null,
      phase: wb.phase,
      alive: wbAlive(),
      hp: Math.round(wb.hp),
      maxHp: wb.maxHp,
      killed: wb.killed,
      claimed: wb.claimed,
      lastRewardDay: wb.lastRewardDay,
      rewardAvailable: wbRewardAvailable(),
      nextInSec: Math.round((wb.nextAt - wbNow()) / 1000),
      windowLeftSec: wb.phase === 'alive' ? Math.round((wb.expiresAt - wbNow()) / 1000) : 0,
      inFight: !!run,
      fight: run ? {
        name: run.def.name,
        level: run.level,
        hp: Math.round(run.boss.hp),
        maxHp: run.boss.maxHp,
        ghosts: run.ghosts.length,
        ghostDamage: Math.round(run.ghostDamage),
        playerDamage: Math.round(run.playerDamage),
        claimed: run.claimed,
        scale: run.boss.wbScale
      } : null
    };
  };
  window.__debugWbRefresh = function () {
    const def = openWorldBossDef();
    if (!def) return null;
    wbOverride = { spawnedAt: wbNow(), expiresAt: wbNow() + WB_WINDOW_SEC * 1000 };
    wb.phase = 'alive';
    wb.maxHp = def.hp;
    wb.hp = def.hp;
    wb.spawnedAt = wbOverride.spawnedAt;
    wb.expiresAt = wbOverride.expiresAt;
    wb.nextAt = wbOverride.expiresAt;
    wb.claimed = false;
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();   // 面板立刻跟着刷新
    return window.__debugWorldBoss();
  };
  // 调试用：把当前这一轮钉死成某个 BOSS（'' = 恢复按时刻表排班）
  window.__debugWbForceBoss = function (id) {
    wbBossOverrideId = id || '';
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    const d = openWorldBossDef();
    return { forced: wbBossOverrideId || null, current: d ? d.name : null };
  };
  // 调试用：时装状态（穿没穿、背包里有没有、头部框量出来是多少）
  window.__debugCostume = function () {
    const worn = equippedCostume('head');
    const img = worn ? images['costume_' + worn.id] : null;
    return {
      head: worn ? { id: worn.id, name: worn.name } : null,
      inBag: inventory.filter(function (it) { return it.type === 'costume'; })
        .map(function (it) { return it.costumeId; }),
      // 时装不再画在角色身上（走路 / 战斗 / 立绘都没有），只在角色窗口的时装展示槽里显示
      renderedOnCharacter: false,
      artLoaded: !!(img && img.complete && img.naturalWidth > 0),
      defs: COSTUMES.map(function (c) { return { id: c.id, name: c.name, bossId: c.bossId, slot: c.slot }; })
    };
  };
  // 调试用：直接给一件时装 / 直接穿上
  window.__debugGiveCostume = function (id) {
    const def = costumeDef(id || 'shamanMask');
    if (!def) return null;
    const it = makeCostumeItem(def);
    inventory.push(it);
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: it.id, name: it.name };
  };
  /* 开发期拿一件「世界BOSS 时装」来试（面具已下架，只能用这个钩子发）。
   *   只在本地 / 内网生效；线上调用直接返回 null。 */
  window.__debugGiveTestCostume = function (id) {
    if (!isTestEnvironment()) return null;
    const def = costumeDef(id || (COSTUMES[0] && COSTUMES[0].id) || 'shamanMask');
    if (!def) return null;
    const it = makeCostumeItem(def);
    if (!it) return null;
    inventory = inventory.filter(function (x) {
      return !(x.type === 'costume' && x.costumeId === def.id);
    });
    inventory.push(it);
    equipCostume(it);
    pushBattleLog('测试环境：已发放并穿上时装「' + def.name + '」', 'system');
    return { id: it.id, name: it.name, slot: def.slot, phantom: def.phantom || null };
  };
  /* 调试/测试用：世界BOSS 时装充能（进度 / 已充能的技能 / 击杀一次 / 直接充满 / 手动给技能充能） */
  /* 调试/测试用：灵脉仙人掌（炼气期点仙人掌吸灵）
   *   state   —— 当前灵脉 / 屏幕上的仙人掌 / 是否在吸 / 灵气微粒数
   *   pick(i) —— 直接把第 i 棵屏幕上的仙人掌认成灵脉（测试不用真的去点画布）
   *   drop()  —— 手动触发一次"拾到灵气编号"的判定
   *   prompt(n) —— 手动弹出「异常灵气聚集地」提示（测试按钮） */
  window.__debugLing = function () {
    return {
      factionOk: xiuFactionOk(),
      node: lingNode ? { number: lingNode.number, registered: lingNode.registered,
        screenX: Math.round(lingNodeScreenX()), baseY: lingNode.baseY,
        pool: Math.round(lingNode.pool), poolMax: lingNode.poolMax, key: lingNode.key || null } : null,
      cactusMemo: Object.keys(lingCactusSeen).length,   // 已经"点过就定死"的仙人掌数量
      boomVisible: !!(ui.lingBoom && !ui.lingBoom.classList.contains('hidden')),
      deadCactus: lingDead.length,
      bottleneck: !!player.bottleneck,
      active: lingNodeActive(),
      absorbing: canAbsorbQi(),
      cactusOnScreen: lingCactus.length,
      motes: lingMotes.length,
      promptOpen: !!(ui.lingPrompt && !ui.lingPrompt.classList.contains('hidden')),
      dropPct: LING_DROP_PCT, min: LING_MIN, max: LING_MAX,
      localNodes: lingLocalNodes.length
    };
  };
  window.__debugLingDrain = function () { lingExhausted(); return window.__debugLing(); };
  /* 调试/测试：看仙人掌备忘（每棵的编号 + 灵力上限） */
  window.__debugLingMemo = function () {
    const out = {};
    Object.keys(lingCactusSeen).forEach(function (k) {
      const v = lingCactusSeen[k];
      out[k] = { number: v.number, pool: v.pool, poolMax: v.poolMax };
    });
    return out;
  };
  /* 调试/测试：按屏幕位置直接点一棵仙人掌（和真实点击同一条路径） */
  window.__debugLingClickAt = function (screenX, screenY) {
    const hit = lingCactusAt(Number(screenX) || 0, Number(screenY) || 0);
    if (!hit) return null;
    lingActivateCactus(hit);
    return window.__debugLing();
  };
  /* 【测试功能·稍后移除】灵脉定位的调试 / 自动化验收钩子 */
  window.__debugLingLocator = function () {
    return {
      testEnv: isTestEnvironment(),
      // 按钮与窗口已下架（测试完毕）：这里保留传送/房间的运行时诊断
      teleporting: !!lingTp,
      tpPhase: lingTp ? lingTp.phase : null,
      liftY: Math.round(playerLiftY),
      tpAlpha: Math.round(playerTpAlpha * 100) / 100,
      vein: lingNode ? { number: lingNode.number, owner: lingNode.owner || '', located: !!lingNode.located,
        screenX: Math.round(lingNodeScreenX()), pool: Math.round(lingNode.pool) } : null,
      forcedCactus: !!lingForcedCactus,
      room: netActRoom, roomKind: netRoomKind, number: netLingNumber,
      peers: Array.from(netMembers.values()).map(function (m) {
        return { sid: m.sid, name: m.name, localX: Math.round(m.localX),
          drawX: Math.round(m.drawX === undefined ? m.localX : m.drawX),
          spawnT: Math.round((m.spawnT || 0) * 100) / 100, action: m.action };
      }),
      peerPlates: lingPeerPlates.slice(),
      piles: lingPvpPiles.filter(function (p) { return !p.taken; }).length
    };
  };
  window.__debugLingLocate = function (n, owner) { return lingLocateTo(n, owner); };
  /* 调试/验收：我这边"灵脉在场"的完整状态 —— 排查"传送过去看不到人"用 */
  window.__debugLingPresence = function () {
    return {
      chat: net.chatState || null,
      chatReady: netChatReady(),
      room: netActRoom, roomKind: netRoomKind,
      myNumber: netLingNumber, joined: netLingJoined,
      sid: netActSid, count: netActCount,
      vein: lingNode ? { number: lingNode.number, active: lingNodeActive() } : null,
      members: Array.from(netMembers.values()).map(function (m) {
        return { sid: m.sid, name: m.name, x: Math.round(m.localX), action: m.action,
          ageMs: Date.now() - m.ts };
      })
    };
  };
  window.__debugLingPeerHit = function (sx, sy) {
    const m = lingHitTestPeer(sx, sy);
    return m ? { sid: m.sid, name: m.name } : null;
  };
  /* 调试/测试用：仙人掌流程太慢（默认 10 点/分，一棵 1~1000 的仙人掌要吸几十分钟）
   *   · __debugSetXiuPerMin(n)  —— 临时改吸纳速度（例：600 → 10 点/秒，80 秒吸干一棵满池仙人掌）
   *   · __debugLingSetPool(n)   —— 直接把这棵仙人掌的灵力改成 n（例：20 → 2 秒见枯竭）
   *   · __debugLingRefill()     —— 恢复默认速度（10 点/分） */
  window.__debugSetXiuPerMin = function (n) {
    const v = Number(n);
    if (isFinite(v) && v > 0) XIU_PER_MIN = v;
    return { perMin: XIU_PER_MIN, perSec: Math.round(XIU_PER_MIN / 60 * 1000) / 1000 };
  };
  window.__debugLingSetPool = function (n) {
    if (!lingNode) return null;
    const v = Math.max(0, Math.round(Number(n) || 0));
    lingNode.pool = v;
    if (v > lingNode.poolMax) lingNode.poolMax = v;
    if (v <= 0) lingExhausted();          // 归零 = 吸干：直接走枯竭 → 爆炸 + 救命
    return window.__debugLing();
  };
  /* 调试/测试用：瓶颈 · 破境任务 */
  window.__debugBottleneck = function () {
    return {
      bottleneck: !!player.bottleneck,
      level: player.level, xp: Math.floor(player.xp), need: xpNeeded(player.level),
      tasks: bottleneckTasks().map(function (t) { return { id: t.id, label: t.label, have: t.have, need: t.need }; }),
      panelOpen: !!(ui.bottleneckPopup && !ui.bottleneckPopup.classList.contains('hidden')),
      kills: killsTotal, pvpKills: pvpKillTotal, caveClears: caveClearTotal,
      needs: bottleneckNeeds(player.level), allDone: bottleneckTasks().every(function (t) { return t.have >= t.need; })
    };
  };
  window.__debugBottleneckPanel = function () { showBottleneckPanel(); return window.__debugBottleneck(); };
  window.__debugPvpKill = function () { pvpKillTotal += 1; checkBottleneckTask(); return window.__debugBottleneck(); };
  window.__debugCaveClear = function () { caveClearTotal += 1; checkBottleneckTask(); return window.__debugBottleneck(); };
  window.__debugAddKills = function (n) {
    killsTotal += Math.max(0, Math.round(Number(n) || 0));
    checkBottleneckTask();
    return window.__debugBottleneck();
  };
  /* 调试/测试用：灵根 */
  window.__debugSpiritRoot = function () {
    const cur = spiritRootDef();
    const b = spiritRootBonus();
    return {
      root: cur ? { id: cur.id, name: cur.name, grade: cur.grade, desc: cur.desc } : null,
      offer: rootOffer ? { id: rootOffer.id, name: rootOffer.name, grade: rootOffer.grade } : null,
      bonus: b,
      canReroll: canRerollSpiritRoot(),
      relics: inventory.filter(function (it) { return it.type === 'spiritItem'; }).length,
      derived: { flatAtk: derived.flatAtk || 0, hp: combat.maxHp, res: derived.res || 0,
        critChance: derived.critChance || 0, spellDmgPct: derived.spellDmgPct || 0,
        speedPct: derived.speedPct || 0, blockPct: derived.blockPct || 0 },
      cost: SPIRIT_ROOT_REROLL_COST, relicCost: SPIRIT_ROOT_REROLL_RELIC, gold: gold,
      panelOpen: !!(ui.rootPopup && !ui.rootPopup.classList.contains('hidden')),
      rerollPopupOpen: !!(ui.rootRerollPopup && !ui.rootRerollPopup.classList.contains('hidden')),
      table: SPIRIT_ROOTS.map(function (r) { return { id: r.id, name: r.name, weight: r.weight }; })
    };
  };
  window.__debugRollRoot = function (free) { return openSpiritRootPanel(free === true); };
  /* 调试/美术验收用：直接把"备选灵根"设成指定那条（不扣钱、不改当前） */
  window.__debugSetRootOffer = function (id) {
    const def = SPIRIT_ROOTS.filter(function (r) { return r.id === id; })[0];
    if (!def) return null;
    rootOffer = def;
    renderSpiritRootPanel();
    if (ui.rootPopup) ui.rootPopup.classList.remove('hidden');
    spiritRootVoices(def);        // 美术验收：换一条灵根就放一波对应的"声音"
    return window.__debugSpiritRoot();
  };
  /* 调试/测试：看这一波"周围的声音"（数量 / 是哪一档 / 都喊了什么 / 排版随机性） */
  window.__debugRootVoices = function () {
    const box = document.getElementById('rootVoices');
    const els = box ? Array.prototype.slice.call(box.querySelectorAll('.rootVoice')) : [];
    return {
      count: els.length,
      weak: !!(box && box.classList.contains('weak')),
      texts: els.map(function (n) { return n.textContent; }),
      sample: els.slice(0, 4).map(function (n) {
        const cs = getComputedStyle(n);
        return {
          left: n.style.left, top: n.style.top,
          size: cs.fontSize, delay: cs.animationDelay, dur: cs.animationDuration,
          rot: n.style.getPropertyValue('--rot'), dy: n.style.getPropertyValue('--dy')
        };
      })
    };
  };
  window.__debugRootVoicesFire = function (id) {
    const def = SPIRIT_ROOTS.filter(function (r) { return r.id === id; })[0];
    if (!def) return null;
    return spiritRootVoices(def);
  };
  /* 调试/测试用：给几枚天地灵物 + 抽 N 次灵根看分布（不改任何状态） */
  window.__debugGiveSpiritItem = function (n) {
    const times = Math.max(1, Math.round(Number(n) || 1));
    for (let i = 0; i < times; i++) inventory.push(makeSpiritItem());
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { relics: inventory.filter(function (it) { return it.type === 'spiritItem'; }).length };
  };
  window.__debugRollRootSample = function (n) {
    const times = Math.max(1, Math.round(Number(n) || 1000));
    const count = {};
    for (let i = 0; i < times; i++) {
      const r = rollSpiritRoot();
      count[r.id] = (count[r.id] || 0) + 1;
    }
    const out = {};
    SPIRIT_ROOTS.forEach(function (r) {
      out[r.name] = Math.round((count[r.id] || 0) / times * 1000) / 10;   // 百分比，一位小数
    });
    return { n: times, pct: out };
  };
  /* 调试/测试用：直接走一次「服下天地灵物 → 蜕凡入炼气」（会带出灵根弹窗） */
  window.__debugGiveBreakthrough = function (factionId) {
    const item = makeSpiritItem();
    if (factionId) faction = factionId;
    applyBreakthrough(item, factionId || undefined);
    return { level: player.level, faction: faction, root: window.__debugSpiritRoot() };
  };
  window.__debugAcceptRoot = function () { return acceptSpiritRoot(); };
  window.__debugSetRoot = function (id) {
    const def = SPIRIT_ROOTS.filter(function (r) { return r.id === id; })[0];
    if (!def) return null;
    player.spiritRoot = { id: def.id, name: def.name, grade: def.grade, rerolls: 1 };
    recalcPlayerStats();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return window.__debugSpiritRoot();
  };
  window.__debugLingPick = function (i) {
    // 默认点"离玩家最近的那棵"（i 按由近到远排序，测试挑第 i 近的）
    const px = player.worldX - cameraX();
    const sorted = lingCactus.slice().sort(function (a, b) {
      return Math.abs(a.screenX - px) - Math.abs(b.screenX - px);
    });
    if (!sorted.length) return null;
    const hit = sorted[Math.max(0, Math.min(sorted.length - 1, Math.round(i || 0)))];
    return hit ? (lingActivateCactus(hit) ? window.__debugLing() : null) : null;
  };
  window.__debugLingDrop = function () { lingRollDrop('调试：拾取灵气编号'); return window.__debugLing(); };
  window.__debugLingPrompt = function (n) {
    lingShowPrompt(Math.round(Number(n) || 137), { owner: '调试修士' });
    return window.__debugLing();
  };
  window.__debugLingSetFaction = function (f) { faction = f; return faction; };
  /* 开发/测试用：临时注册一件世界BOSS 时装。
   *   正式上架时把定义写进 defs.js 的 COSTUMES 就行；这个钩子只是让"还没上架的时装"
   *   也能先跑一遍「虚影 + 击杀充能」流程（线上不生效）。 */
  window.__debugDefineTestCostume = function (def) {
    if (!isTestEnvironment()) return null;
    const d = Object.assign({
      id: 'testCostume', slot: 'head', bossId: 'stos', name: '测试时装', icon: '面',
      art: 'assets/img/costume/shaman_mask_v3.png', phantom: 'stos',
      desc: '测试用世界BOSS 时装', drop: '（测试）'
    }, def || {});
    const i = COSTUMES.findIndex(function (c) { return c.id === d.id; });
    if (i >= 0) COSTUMES[i] = d; else COSTUMES.push(d);
    return { id: d.id, name: d.name, phantom: d.phantom || null };
  };
  window.__debugCostumeCharge = function () {
    const a = costumeChargeActive;
    return {
      bossCostume: hasBossCostume(),
      charge: Math.round(costumeCharge),
      full: costumeCharge >= COSTUME_CHARGE_FULL,
      active: a ? { skillId: a.skillId, name: (skillById(a.skillId) || {}).name || a.skillId,
        pct: a.pct, started: !!a.t0 } : null,
      used: costumeChargeUsed,
      perKill: COSTUME_CHARGE_PER_KILL, perElite: COSTUME_CHARGE_PER_ELITE,
      perBoss: COSTUME_CHARGE_PER_BOSS,
      minPct: COSTUME_CHARGE_MIN_PCT, maxPct: COSTUME_CHARGE_MAX_PCT
    };
  };
  window.__debugAddCostumeCharge = function (pct) {
    const okAdd = addCostumeCharge(Number(pct) || COSTUME_CHARGE_PER_KILL, '调试加充能');
    return { added: okAdd, state: window.__debugCostumeCharge() };
  };
  window.__debugChargeSkill = function (id) {
    const sk = skillById(id);
    return sk ? chargeSkillWithCostume(sk) : null;
  };
  window.__debugEquipCostume = function (id) {
    const it = inventory.filter(function (x) {
      return x.type === 'costume' && x.costumeId === (id || 'shamanMask');
    })[0];
    return it ? equipCostume(it) : false;
  };
  window.__debugUnequipCostume = function (slotId) {
    unequipCostume(slotId || 'head');
    return window.__debugCostume();
  };
  // 调试用：临时把角色朝向钉死（验证身后虚影会跟着换边）
  window.__debugFaceLeft = function (on) {
    debugForceFace = on === false ? 0 : -1;
    return debugForceFace;
  };
  /* 调试用：世界BOSS 时装的身后虚影状态
   *   lastRect 是"这一帧真的画在哪"（每帧刷新），测试据此核对位置与尺寸 */
  window.__debugBossPhantom = function () {
    const worn = equippedCostume('head');
    const art = wornBossPhantom();
    const img = art ? images[art.key] : null;
    return {
      worn: worn ? { id: worn.id, name: worn.name } : null,
      bossId: art ? art.bossId : null,
      key: art ? art.key : null,
      artLoaded: !!(img && img.complete && img.naturalWidth > 0),
      targetHeight: WB_GHOST.h,
      targetWidth: Math.round(WB_GHOST.h * (WB_BOSS_ART.w / WB_BOSS_ART.h)),
      behind: WB_GHOST.behind,
      alpha: WB_GHOST.alpha,
      lastRect: lastPhantomRect
    };
  };
  // 调试用：直接发一个天地灵物（验证新图标）
  window.__debugGiveSpiritItem = function () {
    const it = makeSpiritItem();
    inventory.push(it);
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: it.id, name: it.name, icon: itemIconUrl(it) };
  };
  /* 调试用：阵营 / 命运分叉点
   *   不传参数 = 读状态；传 'xian' / 'nixian' = 直接走一遍择道（等价于点那张卡） */
  window.__debugFaction = function (id) {
    if (id) chooseFaction(id);
    const def = factionDef(faction);
    return {
      faction: faction,
      name: def ? def.name : null,
      level: player.level,
      bottleneck: !!player.bottleneck,
      pending: !!factionPendingItem,
      popupOpen: !!(ui.factionPopup && !ui.factionPopup.classList.contains('hidden')),
      spiritItems: inventory.filter(function (it) { return it.type === 'spiritItem'; }).length,
      options: FACTIONS.map(function (f) {
        return { id: f.id, name: f.name, title: f.title, desc: f.desc };
      })
    };
  };
  // 调试用：把角色摆到"武者大圆满瓶颈"上（验证择道剧情）
  window.__debugSetBottleneck = function (on) {
    player.level = TRIBULATION_STAGE;
    player.xp = 0;
    player.bottleneck = on !== false;
    updateLevelUI();
    updateCharButton();
    return { level: player.level, bottleneck: !!player.bottleneck };
  };
  // 调试用：直接设境界（验证"仙人静修吸纳天地灵力"这类按境界分档的玩法）
  window.__debugSetStage = function (stage) {
    player.level = clamp(Math.floor(stage || 1), 1, MAX_STAGE);
    player.xp = 0;
    player.bottleneck = false;
    recalcPlayerStats();
    updateLevelUI();
    updateCharButton();
    return { level: player.level, name: stageName(player.level) };
  };
  // 调试用：直接指定阵营（不走择道弹窗），验证各阵营的玩法差异
  window.__debugSetFaction = function (id) {
    faction = factionDef(id) ? id : null;
    updateLevelUI();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return { faction: faction };
  };
  /* ---------- 调试用：开场 CG ---------- */
  window.__debugCgState = function () {
    return {
      active: cg.active,
      seen: cgSeen,
      index: cg.i,
      total: CG_SLIDES.length,
      t: Math.round(cg.t * 100) / 100,
      dur: Math.round(cg.dur * 10) / 10,
      slideId: CG_SLIDES[cg.i] ? CG_SLIDES[cg.i].id : null,
      title: CG_SLIDES[cg.i] ? CG_SLIDES[cg.i].title : null,
      subtitle: CG_SLIDES[cg.i] ? CG_SLIDES[cg.i].subtitle : null,
      narration: CG_SLIDES[cg.i] ? CG_SLIDES[cg.i].narration : null,
      shownLines: cg.shownLines,
      /* 旁白与字幕已按需求从画面上移除，只留标题；文案数据仍在 CG_SLIDES 里 */
      narrationVisible: false,
      subtitleVisible: false,
      skipVisible: !!(ui.cgScreen && !ui.cgScreen.classList.contains('hidden')),
      artLoaded: Object.keys(cg.images).length,
      slides: CG_SLIDES.map(function (s) {
        return { id: s.id, title: s.title, subtitle: s.subtitle, art: s.art, lines: (s.narration || []).length };
      })
    };
  };
  // 立刻重播开场 CG（不看"已看过"标记）；onDone 之前会先进场景
  window.__debugPlayCg = function () {
    cgSeen = false;
    if (gameState === 'title' || gameState === 'intro') { playCgIntro(startScene); return true; }
    return playCgIntro(null);
  };
  window.__debugSkipCg = function () { return cgSkip(); };
  // 跳到第 i 张（预览 / 测试用：直接给到"旁白已浮现"的时刻）
  window.__debugCgGoto = function (i) {
    if (!cg.active) playCgIntro(null);
    cg.i = Math.max(0, Math.min(CG_SLIDES.length - 1, Math.floor(i || 0)));
    cg.dur = cgSlideDuration(CG_SLIDES[cg.i]);
    cg.t = 2.2;
    cgRenderText();
    cgRevealLines(cg.t);
    return window.__debugCgState();
  };
  /* 调试用：分镜里真实立绘的加载 / 裁切情况（排查"素材没进来"用） */
  window.__debugCgArt = function () {
    const out = { pre: !!cg.pre, used: CG_ART_USED.length, ok: 0, missing: [], boxes: {}, slides: {} };
    CG_ART_USED.forEach(function (art) {
      const im = cgArtImg(art);
      const loaded = !!(im.complete && im.naturalWidth);
      if (loaded) out.ok++; else out.missing.push(art);
      const box = loaded ? cgArtBox(art) : null;
      if (box) out.boxes[art] = box.w + 'x' + box.h;
    });
    Object.keys(CG_SLIDE_ART).forEach(function (id) {
      out.slides[id] = { need: CG_SLIDE_ART[id].length,
        ready: cgArtReady(CG_SLIDE_ART[id]), painted: !!cgBitmaps[id] };
    });
    return out;
  };
  // 把"看过开场 CG"的标记清掉（下次点开始游戏就会重播）
  window.__debugResetCgSeen = function () {
    cgSeen = false;
    markSaveDirty();
    return { seen: cgSeen };
  };
  /* 调试用：静修吸纳的状态（强度 / 速率 / 微粒与涟漪数量 / 累计修为）
   *   传 seconds 就先把游戏时间推进这么多（用假时钟的测试可以直接看进度） */
  window.__debugXiuLian = function () {
    return {
      faction: faction,
      active: xiuActive(),
      canAbsorb: canAbsorbQi(),
      amp: Math.round(xiu.amp * 1000) / 1000,
      rate: Math.round(xiuRate() * 10) / 10,
      seconds: Math.round(xiu.seconds * 10) / 10,
      gained: Math.round(xiu.gained),
      motes: xiuMotes.length,
      rings: xiuRings.length,
      pulse: Math.round(xiu.pulse * 100) / 100,
      level: player.level,
      xp: Math.floor(player.xp),
      need: xpNeeded(player.level),
      bottleneck: !!player.bottleneck,
      resting: !!playerResting,
      moving: !!input.moving
    };
  };
  // 调试用：当前灵气微粒的 {id, 到角色距离}（可对同一颗粒子前后采样，验证"被吸向角色"）
  window.__debugXiuMotes = function () {
    return xiuMotes.map(function (m) { return { id: m.id, r: Math.round(m.r) }; });
  };
  /* 调试/测试用：直接给一笔修为（走正常结算链）。
   *   —— 吸纳速度改成 10 点/分之后，测试没法靠"等"来攒够破境所需，用这个垫一下末尾那段。 */
  window.__debugAddXiu = function (n) {
    applyXp(Math.max(0, Math.round(Number(n) || 0)), { quiet: true });
    return window.__debugXiuLian();
  };
  // 调试用：把开箱随机数钉死，验证 0.05% 时装掉落这条分支
  window.__debugForceCostumeRoll = function (on) {
    window.__costumeRollForce = on !== false;
    return window.__costumeRollForce;
  };
  window.__debugWbExpire = function () {
    // 把 3 分钟窗口立刻走完（验证"没击杀就自己消失"）
    if (!wbOverride) wbOverride = { spawnedAt: wb.spawnedAt || wbNow(), expiresAt: wbNow() - 1 };
    wbOverride.expiresAt = wbNow() - 1;
    wb.expiresAt = wbOverride.expiresAt;
    return window.__debugWorldBoss();
  };
  window.__debugWbClearOverride = function () { wbOverride = null; return window.__debugWorldBoss(); };
  // 调试用：世界BOSS 战场环境效果（余烬 / 沙尘带 / 血云 / 血雷闪）
  // 调试用：森特欧斯圣物（发放 / 掉落判定 / 状态）
  window.__debugGrantSantosRelic = function (key) {
    const item = makeSantosRelic(key || 'skull');
    inventory.push(item);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { name: item.name, relic: item.relic, detail: item.detail, state: window.__debugSantosState() };
  };
  window.__debugSantosRoll = function (force) {
    if (force) santosRollForced = true;
    const got = rollSantosRelic();
    return { got: got ? got.name : null, state: window.__debugSantosState() };
  };

  /* ---------- 天地灵物（世界BOSS限定掉落）调试钩子 ---------- */
  window.__debugSpiritState = function () {
    return {
      hasItem: hasSpiritItem(),
      dropPct: SPIRIT_ITEM_DROP_PCT,
      exchangeHasSpirit: EXCHANGE_ITEMS.some(function (e) { return e.label === '天地灵物'; }),
      bottleneck: !!player.bottleneck,
      level: player.level,
      stage: stageName(player.level)
    };
  };
  // force=true 必出；不传 force 走真实 0.01% 判定。第二个参数传 false 表示"没参与战斗"。
  // 走的还是"留场开箱彩蛋"那一条判定（现已不存在于BOSS死亡结算里）。
  window.__debugSpiritRoll = function (force, participated) {
    spiritRollForced = !!force;
    const run = (participated === false)
      ? { playerDamage: 0, ghostDamage: 0, boss: null }     // 没参与：不该掉
      : { playerDamage: 1, ghostDamage: 0, boss: null };
    const got = rollSpiritItemFromLootChest(run);
    spiritRollForced = false;
    return { got: got ? got.name : null, state: window.__debugSpiritState() };
  };

  /* ---------- 第一个任务 / 首页引路人对白 调试钩子 ---------- */
  window.__debugQuest = function () {
    return {
      accepted: !!quests.first.accepted,
      done: !!quests.first.done,
      targetStage: QUEST_FIRST_STAGE,
      targetName: stageName(QUEST_FIRST_STAGE),
      nowStage: stageName(player.level),
      popupOpen: !!(ui.questPopup && !ui.questPopup.classList.contains('hidden')),
      popupTitle: ui.questPopup ? (ui.questPopup.querySelector('h2') || {}).textContent : null
    };
  };
  window.__debugQuestAccept = function () { closeQuestPopup(); return window.__debugQuest(); };
  window.__debugShowIntroDialog = function () {
    if (gameState !== 'intro') return null;
    ui.introDialog.classList.remove('hidden');
    return window.__debugIntro();
  };
  window.__debugSantosState = function () {
    return {
      chance: SANTOS_RELIC_CHANCE,
      skull: hasSantosRelic('skull'),
      totem: hasSantosRelic('totem'),
      relicInt: derived.relicInt || 0,
      intTotal: derived.intTotal || 0,
      maxMp: combat.maxMp,
      poisonImmune: !!derived.poisonImmune,
      controlImmune: !!derived.controlImmune,
      hasCageSkill: knownSkills.some(function (s) { return s.id === 'santosCage'; }),
      cageSeconds: WB_CAGE_SECONDS,
      enemy: activeEnemy ? {
        name: activeEnemy.name, hp: Math.round(activeEnemy.hp),
        stunT: Math.round((activeEnemy.stunT || 0) * 100) / 100,
        rootT: activeEnemy.rootT || 0,
        poison: activeEnemy.poison ? { dmg: Math.round(activeEnemy.poison.dmg), turns: activeEnemy.poison.turns } : null
      } : null
    };
  };
  // 调试用：手动触发一次图腾法术 / 囚笼
  window.__debugTotemTurn = function () { return totemTurnSpell(); };
  // 调试用：防御姿态（是否处于防御 / 减伤与格挡参数）
  /* ---------- 调试钩子：灵力 / 境界压制 ---------- */
  window.__debugSpirit = function () {
    return {
      stage: playerLevel(), stageText: stageName(playerLevel()),
      hasSpirit: true,                       // 灵力 = 法力槽，人人都有
      base: derived.spiritBase,              // 境界基准（升级所需修为 × 2）
      max: derived.maxMp,                    // 上限（含装备 / 宝石 / 智力加成）
      current: combat.mp | 0,
      defendCost: defendSpiritCostNow(),
      boosted: derived.spiritBoosted,
      dmgBoost: SPIRIT_DMG_BOOST,
      defendReduce: SPIRIT_DEFEND_REDUCE, defendBlockPct: SPIRIT_DEFEND_BLOCK_PCT,
      defending: defending, spiritPowered: defendSpiritPowered,
      reduceNow: defendReduceNow(), blockPctNow: defendBlockNow()
    };
  };
  // 直接设灵力（测试用；灵力就是 combat.mp）
  window.__debugSpiritSet = function (n) {
    combat.mp = clamp(Math.floor(Number(n) || 0), 0, combat.maxMp);
    updateBattleHud();
    return combat.mp | 0;
  };
  // 直接设灵力（测试用：长流程用例里很容易把灵力打空）
  window.__debugSetMp = function (n) {
    combat.mp = clamp(Math.floor(Number(n) || 0), 0, combat.maxMp);
    updateBattleHud();
    return combat.mp | 0;
  };
  // 境界压制倍数：给两个境界阶数，返回伤害倍率
  window.__debugRealmGap = function (attackerStage, targetStage) {
    const a = Number(attackerStage) || 1, t = Number(targetStage) || 1;
    return {
      attacker: a, attackerRealm: bigRealmOfStage(a).name,
      target: t, targetRealm: bigRealmOfStage(t).name,
      mult: realmDamageMult(a, t), text: realmGapText(a, t)
    };
  };
  // 灵力催动倍率（练气期及以上 1.4 = +40%；武者境 1）
  window.__debugSpiritCastMult = function () {
    return spiritSkillMult();
  };
  // 改当前敌人的境界阶数（测"境界压制"用）
  window.__debugSetEnemyLevel = function (n) {
    if (!activeEnemy) return null;
    activeEnemy.level = Math.max(1, Math.floor(Number(n) || 1));
    return { level: activeEnemy.level, realm: bigRealmOfStage(activeEnemy.level).name };
  };
  window.__debugDefendState = function () {
    return { defending: defending, reduce: DEFEND_REDUCE, blockPct: DEFEND_BLOCK_PCT,
      spiritPowered: defendSpiritPowered,
      reduceNow: defendReduceNow(), blockPctNow: defendBlockNow(),
      buttonLabel: ui.btnDefend ? (ui.btnDefend.getAttribute('title') || '') : null,
      buttonImg: ui.btnDefend && ui.btnDefend.querySelector('img')
        ? ui.btnDefend.querySelector('img').getAttribute('src') : null,
      disabled: ui.btnDefend ? ui.btnDefend.disabled : null,
      // 护盾特效：是不是在播、第几帧、画在哪（旧的淡蓝光弧已移除）
      fx: (function () {
        if (!defendFx) return null;
        return {
          t: Math.round(defendFx.t * 1000) / 1000,
          phase: defendFx.phase,
          frame: defendFxFrameNo(),
          frames: DEFEND_FX_FRAMES
        };
      })()
    };
  };
  // 调试用：护盾特效资源与落点（角色右侧 = 屏幕 x 更大的一侧）
  window.__debugDefendFx = function () {
    const first = images.defend_1;
    const scale = playerFx.scale * caveScale();
    const sx = player.worldX - cameraX();
    const size = DEFEND_FX_SIZE * scale;
    const ax = sx;
    const ay = GROUND_Y - DEFEND_FX_LIFT * scale;
    const idx = defendFxFrameNo();
    // 盾面（贴图里可见内容的范围：源图 x 20~160、y 11~181）换算到屏幕，核对是否罩住角色
    const ox = function (srcX) { return ax + (srcX / 192 - DEFEND_FX_ORIGIN.x) * size; };
    const oy = function (srcY) { return ay + (srcY / 192 - DEFEND_FX_ORIGIN.y) * size; };
    const mir = function (srcX) { return DEFEND_FX_FLIP ? 192 - srcX : srcX; };
    const box = {
      left: Math.round(ox(mir(161))), right: Math.round(ox(mir(20))),
      top: Math.round(oy(11)), bottom: Math.round(oy(182))
    };
    return {
      ready: defendSpriteReady(),
      frames: DEFEND_FX_FRAMES,
      loadedFrames: defendLoadedFrames(),
      size: first ? first.naturalWidth + 'x' + first.naturalHeight : null,
      playing: !!defendFx,
      defending: !!defending,         // 本回合是否处于防御姿态（交锋里下一回合要清掉）
      phase: defendFx ? defendFx.phase : null,
      frame: idx,
      flip: DEFEND_FX_FLIP,
      drawSize: Math.round(size),
      // 护盾圆心：应压在角色身上（和角色中心 x 对齐）
      anchorX: Math.round(ax),
      anchorY: Math.round(ay),
      playerSx: Math.round(sx),
      groundY: GROUND_Y,
      centeredOnPlayer: Math.abs(Math.round(ax) - Math.round(sx)) <= 2,
      // 盾面覆盖范围（应把角色整个包进去，而不是铺到敌人脚下）
      shieldBox: box,
      coversPlayer: box.left <= sx + 6 && box.right >= sx - 6,
      draw: defendFx ? {
        x: Math.round(ax - DEFEND_FX_ORIGIN.x * size),
        y: Math.round(ay - DEFEND_FX_ORIGIN.y * size),
        w: Math.round(size)
      } : null
    };
  };
  // 调试用：「破空斩」剑气（连续 2 道 → 向前扩散 → 到最右侧边界消失）
  window.__debugAirSlashFx = function () {
    const loaded = (function () {
      let n = 0;
      for (let i = 1; i <= AIRSLASH_FX_FRAMES; i++) {
        const im = images['holyslash_' + i];
        if (im && im.complete && im.naturalWidth > 0) n++;
      }
      return n;
    })();
    const waves = airSlashFx ? airSlashFx.waves.map(function (w) {
      const sx = airSlashWaveScreenX(w);
      return {
        t: Math.round(w.t * 1000) / 1000,
        big: !!w.big,
        yOff: Math.round(w.yOff || 0),
        speedMult: w.speedMult || 1,
        visible: w.t >= 0,
        screenX: Math.round(sx),
        frame: w.t < 0 ? 0 : (Math.floor(w.t * AIRSLASH_FX_FPS) % AIRSLASH_FX_FRAMES) + 1,
        atRightEdge: sx >= W - 150,
        gone: sx - AIRSLASH_FX_SIZE * 0.5 > W
      };
    }) : [];
    return {
      ready: airSlashSpriteReady(),
      frames: AIRSLASH_FX_FRAMES,
      loadedFrames: loaded,
      active: !!airSlashFx,
      waveCount: waves.length,
      waves: waves,
      size: AIRSLASH_FX_SIZE,
      lift: AIRSLASH_FX_LIFT,
      speed: AIRSLASH_FX_SPEED,
      screenW: W,
      groundY: GROUND_Y,
      playerSx: Math.round(player.worldX - cameraX())
    };
  };
  // 调试用：辅助技能石（多重投射物 / 投射物加速 / 处决）当前生效情况
  window.__debugSupportState = function (skillId) {
    const id = skillId || 'airSlash';
    const skill = skillById(id);
    const sockets = skill ? (skill.sockets || []).map(function (g) {
      return { effect: g.effect, name: g.name, projectileOnly: isProjectileOnlyGem(g) };
    }) : [];
    return {
      skill: skill ? skill.name : null,
      sockets: sockets,
      isProjectileSkill: isProjectileSkill(id),
      projectileCount: projectileCountOf(id, AIRSLASH_FX_WAVES),
      projectileSpeedMult: Math.round(projectileSpeedMult(id) * 100) / 100,
      skillSpeedMult: Math.round(skillSpeedMult(id) * 100) / 100,
      waveGap: Math.round(airSlashWaveGap(projectileCountOf(id, AIRSLASH_FX_WAVES)) * 1000) / 1000,
      damagePct: supportDamagePct(id, activeEnemy),
      playerLowLife: playerLowLife(),
      playerHpPct: combat.maxHp > 0 ? Math.round(combat.hp / combat.maxHp * 1000) / 10 : null,
      enemyLowLife: enemyLowLife(activeEnemy),
      enemyHpPct: activeEnemy && activeEnemy.maxHp > 0
        ? Math.round(activeEnemy.hp / activeEnemy.maxHp * 1000) / 10 : null,
      lowLifePct: LOW_LIFE_PCT,
      gemPool: GEM_OPTIONS.filter(function (o) { return o.support; }).map(function (o) {
        return { id: o.id, name: o.name, projectileOnly: !!o.projectileOnly,
          minionOnly: !!o.minionOnly, onlySkill: o.onlySkill || null };
      })
    };
  };
  /* 调试/测试用：召唤生物（宠物）+ 召唤辅助技能石的当前状态 */
  window.__debugMinionState = function () {
    return {
      cap: petCap(),
      servant: hasServantGem(),
      loyaltyPet: (function () { const p = loyaltyPet(); return p ? p.name : null; })(),
      /* 旧的"每 1 点灵力 +1 宠物伤害"已废弃（改成固定基础伤害 4~9），这里恒为 0 只为兼容老用例 */
      mpBonus: 0,
      pets: alivePets().map(function (p) {
        const mods = minionGemMods(p);
        return {
          name: p.name, sprite: p.sprite, level: summonLevelOf(p), petTierLevel: p.level,
          hp: Math.ceil(p.hp), maxHp: p.maxHp,
          skillId: p.skillId || null, sourceKind: p.sourceKind || null,
          source: p.source || '',
          notes: mods.notes,
          dmgMult: Math.round(mods.dmgMult * 1000) / 1000,
          takenMult: Math.round(mods.takenMult * 1000) / 1000,
          hpMult: Math.round(mods.hpMult * 1000) / 1000,
          atkMult: Math.round(mods.atkMult * 1000) / 1000,
          scale: mods.scale,
          estimate: petDamageEstimateOf(p)
        };
      }),
      gemPool: GEM_OPTIONS.filter(function (o) { return o.minionOnly; }).map(function (o) {
        return { id: o.id, name: o.name, short: o.short || '', desc: o.desc || '' };
      })
    };
  };
  // 调试用：造一颗指定的技能石放进背包（测试辅助石用）
  window.__debugGrantGem = function (effect) {
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
    if (!opt) return null;
    const gem = gemOptionToItem(opt);
    inventory.push(gem);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: gem.id, name: gem.name, effect: gem.effect, detail: gem.detail };
  };
  // 调试用：把已镶嵌的某颗技能石取下来（换宝石做对比测试用）
  window.__debugUnsocketGem = function (effect, skillId) {
    const target = skillId ? skillById(skillId) : null;
    const list = target ? [target] : knownSkills;
    const skill = list.filter(function (s) {
      return (s.sockets || []).some(function (g) { return g.effect === effect; });
    })[0];
    if (!skill) return null;
    const gem = (skill.sockets || []).filter(function (g) { return g.effect === effect; })[0];
    unsocketGem(skill, gem, true);      // 调试钩子：绕过"战斗中不许卸"的限制
    return { skill: skill.name, effect: effect };
  };
  // 调试用：把背包里某个效果的技能石直接镶进指定技能（绕过 UI）
  window.__debugSocketGem = function (effect, skillId, value) {
    let gem = inventory.filter(function (it) { return it.type === 'gem' && it.effect === effect; })[0];
    // 调试便利：背包里没有这颗石头就现场造一颗（测试脚本不用先 grant）
    if (!gem) {
      const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
      if (opt) { gem = gemOptionToItem(opt); inventory.push(gem); }
    }
    // 指定数值（测试用）：被动石想验证"数值保留"就传第三个参数
    if (gem && typeof value === 'number' && !gem.support) {
      gem.value = Math.round(value);
      const opt2 = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
      if (opt2) gem.detail = opt2.name + ' +' + gem.value + opt2.unit;
    }
    const skill = skillById(skillId);
    if (!gem || !skill) return { ok: false, reason: !gem ? '背包里没有这颗技能石' : '没有这个技能' };
    const before = (skill.sockets || []).length;
    socketGemInto(skill, gem);
    const after = (skill.sockets || []).length;
    return {
      ok: after > before,
      socketed: after > before,
      sockets: (skill.sockets || []).map(function (g) { return g.effect; }),
      // 可能同时挂着多条提示，全部取回来（判定用"任意一条"）
      toast: Array.prototype.map.call(document.querySelectorAll('#toast'), function (el) {
        return el.textContent;
      }).join(' | ')
    };
  };
  // 调试用：连续模拟 N 次敌人攻击，返回每次受到的伤害（用于核对 35% 减伤 / 5% 格挡）
  window.__debugSampleEnemyHits = function (defendingOn, n) {
    if (!activeEnemy) return null;
    const keepTurn = battleTurn;
    const out = [];
    const total = Math.max(1, Math.floor(n || 20));
    for (let i = 0; i < total; i++) {
      combat.hp = combat.maxHp;
      const before = combat.hp;
      defending = !!defendingOn;
      enemyCounterAttackCore(false, false);
      out.push(Math.round(before - combat.hp));
    }
    defending = false;
    battleTurn = keepTurn;
    return out;
  };
  window.__debugCage = function () { return castSkill(knownSkills.filter(function (s) { return s.id === 'santosCage'; })[0]); };

  window.__debugWbEnv = function () {
    const artKey = activeEnemy && activeEnemy.wbBossArtKey;
    const art = artKey ? images[artKey] : null;
    return {
      ready: wbEnvReady,
      theme: wbThemeKeyOf(),
      particles: wbParticles.length,
      dustBands: wbDustBands.length,
      clouds: wbClouds.length,
      flash: Math.round(wbFlash * 100) / 100,
      nextFlashInSec: Math.round(wbFlashTimer * 10) / 10,
      bossArt: !!(activeEnemy && activeEnemy.wbBossArt),
      bossArtKey: artKey || null,
      bossArtLoaded: !!(art && art.complete && art.naturalWidth > 0),
      bossArtSize: art ? (art.naturalWidth + 'x' + art.naturalHeight) : null
      ,
      bossArtRect: lastBossArtRect,
      flashHold: wbFlashHold
    };
  };
  // 调试用：战场层次信息（BOSS 立绘 / 前景山丘 / 挑战者位置）
  window.__debugWbScene = function () {
    const cam = cameraX();
    const run = worldBossRun;
    const bossSx = run ? run.boss.x - cam : null;
    return {
      hillEnabled: wbHillEnabled,
      bossArtRect: lastBossArtRect,
      hillTopAtBoss: bossSx === null ? null : Math.round(wbHillLift(bossSx, cam)),
      bossCoveredPx: (lastBossArtRect && bossSx !== null)
        ? Math.round(lastBossArtRect.bottom - wbHillLift(bossSx, cam)) : null,
      ghosts: run ? run.ghosts.map(function (g) {
        return { x: Math.round(bossSx + g.offset), sprite: g.isPlayer ? 'player' : g.sprite };
      }) : [],
      playerSx: Math.round(player.worldX - cam)
    };
  };
  // 调试用：13 个世界BOSS 的名字 / 境界 / 主题 / 立绘与资料图是否加载成功
  window.__debugWbRoster = function () {
    return WORLD_BOSSES.map(function (d) {
      const artKey = wbBossArtKey(d);
      const art = images[artKey];
      return {
        id: d.id, name: d.name, place: d.place, level: d.level,
        stage: stageText(d.level), theme: d.theme,
        artKey: artKey,
        artUrl: d.art, infoUrl: d.info || null,
        artLoaded: !!(art && art.complete && art.naturalWidth > 0),
        artSize: art ? art.naturalWidth + 'x' + art.naturalHeight : null
      };
    });
  };
  // 调试用：传送到指定 BOSS（按 id），用来验证"每个 BOSS 一套场景"
  window.__debugWbTeleportTo = function (id) {
    const def = WORLD_BOSSES.filter(function (d) { return d.id === id; })[0];
    if (!def) return false;
    return teleportToWorldBoss(def, { force: true });
  };
  // 调试用：当前战场主题 + 立绘 key（验收"换 BOSS 换场景"）
  window.__debugWbTheme = function () {
    return {
      theme: wbThemeKeyOf(),
      bossArtKey: activeEnemy && activeEnemy.wbBossArtKey ? activeEnemy.wbBossArtKey : null,
      env: window.__debugWbEnv()
    };
  };
  window.__debugWbHill = function (on) {
    wbHillEnabled = on !== false;
    return window.__debugWbScene();
  };
  // 调试用：立刻触发一次血雷闪（验证闪光效果）
  // 传数字 = 钉住闪光强度（方便截帧对比）；传 'release' = 恢复自动衰减；不传 = 触发一次
  window.__debugWbFlash = function (v) {
    if (typeof v === 'number') { wbFlash = v; wbFlashHold = true; }
    else if (v === 'release') { wbFlashHold = false; }
    else { wbFlash = 1; wbFlashHold = false; }
    return window.__debugWbEnv();
  };
  // 调试用：直接抽一次世界BOSS宝箱的掉落（验证几率表）
  window.__debugRollWbChest = function () { return rollWorldBossChestLoot('wbchest'); };
  // 调试用：直接开关战斗日志窗口（战斗日志按钮现在只在战斗中显示）
  window.__debugOpenLog = function () { openBattleLog(); return true; };
  // 调试/测试用：按 id 打开某件装备的洗练窗口（看它的完整词缀）
  window.__debugOpenReforgeById = function (id) {
    const it = inventory.filter(function (x) { return x.id === id; })[0] ||
      Object.keys(equipment).map(function (k) { return equipment[k]; })
        .filter(Boolean).filter(function (x) { return x.id === id; })[0];
    if (!it || it.type !== 'equipment') return null;
    openReforge(it);
    return window.__debugReforgeMods();
  };
  // 调试用：把"今日已领奖励"的日期改成指定值（验证每天一次的规则）
  window.__debugWbSetRewardDay = function (day) {
    wb.lastRewardDay = typeof day === 'string' ? day : shanghaiDayKey(wbNow());
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    return { lastRewardDay: wb.lastRewardDay, rewardAvailable: wbRewardAvailable() };
  };
  // 调试用：模拟互联网校时（设定时钟偏移）
  window.__debugWbClock = function (skewMs, source) {
    wbClockSkew = Number(skewMs) || 0;
    wbClockSource = source || (wbClockSkew ? 'internet' : 'local');
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    return window.__debugWbSchedule();
  };
  // 调试用：世界BOSS 时间表（上海时间）+ 时间源 + 今日奖励状态
  window.__debugWbSchedule = function () {
    const now = wbNow();
    return {
      clockSource: wbClockSource,
      skewMs: Math.round(wbClockSkew),
      nowUtc: new Date(now).toISOString(),
      nowShanghai: shanghaiDayKey(now) + ' ' + shanghaiClockText(now),
      todayTimes: wbTodayTimes(now).map(function (t) {
        const p = shanghaiParts(t);
        return pad2(p.h) + ':' + pad2(p.mi);
      }),
      nextAt: shanghaiDayKey(wbNextSpawnAt(now)) + ' ' + shanghaiClockText(wbNextSpawnAt(now)),
      nextInSec: Math.round((wbNextSpawnAt(now) - now) / 1000),
      lastAt: shanghaiDayKey(wbLastSpawnAt(now)) + ' ' + shanghaiClockText(wbLastSpawnAt(now)),
      alive: wbAlive(),
      phase: wb.phase,
      rewardDay: wb.lastRewardDay,
      rewardAvailable: wbRewardAvailable(),
      killed: wb.killed
    };
  };
  window.__debugWbTeleport = function () {
    // 默认 force=true（老测试直接进战场）；传 __debugWbTeleport(false) 走"真实按钮"那条路，含境界门槛
    return teleportToWorldBoss(openWorldBossDef(), { force: arguments.length ? arguments[0] !== false : true });
  };
  window.__debugWbGhosts = function () {
    if (!worldBossRun) return null;
    return worldBossRun.ghosts.map(function (g) {
      return { name: g.name, sprite: g.isPlayer ? 'player' : g.sprite, playerModel: g.isPlayer };
    });
  };
  // 调试用：世界BOSS 等级门槛（玩家按多少级计算 / 是否达标）
  window.__debugWbLevel = function () {
    const run = worldBossRun;
    return {
      playerLevel: playerLevel(),
      effectiveLevel: worldBossPlayerLevel(),
      boost: worldBossPlayerLevel() - playerLevel(),
      playerStage: stageText(playerLevel()),
      effectiveStage: stageText(worldBossPlayerLevel()),
      bossLevel: run ? run.boss.level : (openWorldBossDef() ? openWorldBossDef().level : null),
      bossStage: stageText(run ? run.boss.level : (openWorldBossDef() ? openWorldBossDef().level : 1)),
      canDamage: run ? worldBossLevelOk() : null,
      hasLevelBoostTreasure: treasureSpecial('levelBoost')
    };
  };
  // 调试用：统计灵宝特殊属性的抽取概率（验证「破境符诏」很稀有）
  window.__debugTreasureRates = function (n) {
    const count = {};
    const total = Math.max(1, Math.floor(n || 2000));
    for (let i = 0; i < total; i++) {
      const sp = pickTreasureSpecial();
      count[sp.key] = (count[sp.key] || 0) + 1;
    }
    return { total: total, count: count };
  };
  window.__debugWbHurt = function (dmg) {
    // 模拟玩家对BOSS造成伤害（验证参与奖励）
    if (!worldBossRun) return null;
    worldBossRun.boss.hp = Math.max(0, worldBossRun.boss.hp - Math.max(1, Math.round(dmg || 100)));
    worldBossRun.playerDamage += Math.max(1, Math.round(dmg || 100));
    if (worldBossRun.boss.hp <= 0) worldBossBossDown();
    return window.__debugWorldBoss();
  };

  /* ---------- 世界BOSS 伤害榜调试钩子 ---------- */
  /* 调试用：击杀后的战场开箱阶段状态 */
  window.__debugWbLoot = function () {
    const run = worldBossRun;
    const chest = worldBossLootChestRect();
    const playerSx = player.worldX - cameraX();
    return {
      active: worldBossLootActive(),
      timeLeft: run && run.loot ? Math.round(run.loot.timeLeft * 10) / 10 : null,
      opened: !!(run && run.loot && run.loot.done),
      granted: !!(run && run.loot && run.loot.granted),
      auto: !!(run && run.loot && run.loot.auto),
      chestOnScreen: chest ? { cx: Math.round(chest.cx), w: Math.round(chest.w), h: Math.round(chest.h),
        x: Math.round(chest.x), y: Math.round(chest.y), groundY: Math.round(chest.groundY) } : null,
      playerSx: Math.round(playerSx),
      distance: chest ? Math.round(Math.abs(playerSx - chest.cx)) : null,
      gifts: run && run.gifts ? run.gifts.map(function (g) {
        const r = worldBossGiftRect(g);
        return { key: g.key, cx: Math.round(r.cx), w: Math.round(r.w) };
      }) : [],
      rewardPopupOpen: !!(ui.wbRewardPopup && !ui.wbRewardPopup.classList.contains('hidden')),
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      inArena: !!worldBossRun
    };
  };
  // 调试用：把玩家瞬移到宝箱旁边 / 直接开箱 / 让倒计时归零
  window.__debugWbWalkToChest = function () {
    if (!worldBossRun || !worldBossRun.loot) return null;
    player.worldX = worldBossRun.loot.x - 40;
    player.vx = 0;
    return window.__debugWbLoot();
  };
  window.__debugWbOpenLootChest = function (auto) {
    openWorldBossLootChest(!!auto);
    return window.__debugWbLoot();
  };
  // 调试用：强制触发"开箱彩蛋"（天地灵物）
  window.__debugWbLootSpirit = function (force) {
    if (force) spiritRollForced = true;
    const got = rollSpiritItemFromLootChest(worldBossRun);
    spiritRollForced = false;
    return { got: got ? got.name : null, state: window.__debugSpiritState() };
  };
  window.__debugWbLootTimer = function (sec) {
    if (!worldBossRun || !worldBossRun.loot) return null;
    worldBossRun.loot.timeLeft = Math.max(0, Number(sec) || 0);
    return window.__debugWbLoot();
  };
  // 调试用：在BOSS战场里把玩家打死（走"倒下被传送出场"那一条分支）
  window.__debugWbKillPlayer = function () {
    combat.hp = 0;
    updateBattleHud();
    return true;
  };
  // 只把 BOSS 打死、不给玩家加伤害（用来测"第一名是虚影"的情况）
  window.__debugWbKillBoss = function () {
    if (!worldBossRun) return null;
    worldBossRun.boss.hp = 0;
    worldBossBossDown();
    return window.__debugWbDamage();
  };
  window.__debugWbDamage = function () {
    return {
      round: wbDamageBoard ? wbDamageBoard.round : wbDamageRound,
      tracking: !!wbDamageBoard,
      entries: wbDamageBoard ? wbDamageBoard.entries.length : 0,
      ranking: window.__debugWbDamageRanking()
    };
  };
  window.__debugWbDamageRanking = function () {
    return worldBossDamageRanking().map(function (e) {
      return { name: e.name, isPlayer: e.isPlayer, damage: e.damage };
    });
  };
  // 造场景：把某个虚影的伤害钉到指定值
  window.__debugWbSetGhostDamage = function (i, dmg) {
    if (!worldBossRun || !worldBossRun.ghosts) return null;
    const g = worldBossRun.ghosts[Math.max(0, Math.min(i | 0, worldBossRun.ghosts.length - 1))];
    if (!g) return null;
    g.damage = Math.max(0, Math.round(dmg || 0));
    worldBossRun.ghostDamage = worldBossRun.ghosts.reduce(function (s, x) { return s + (x.damage || 0); }, 0);
    return window.__debugWbDamageRanking();
  };
  // 造场景：把所有虚影压到很低，让玩家稳拿第一
  window.__debugWbForceTop = function (gap) {
    if (!worldBossRun) return null;
    worldBossRun.ghosts.forEach(function (g) { g.damage = 10; });
    worldBossRun.ghostDamage = worldBossRun.ghosts.length * 10;
    worldBossRun.playerDamage = worldBossRun.ghostDamage + Math.max(1, Math.round(gap || 100));
    return window.__debugWbDamageRanking();
  };
  // 造场景：让虚影稳拿第一（玩家排名靠后）
  window.__debugWbForceGhostTop = function () {
    if (!worldBossRun) return null;
    worldBossRun.ghosts.forEach(function (g, i) { g.damage = 900 - i * 10; });
    worldBossRun.ghostDamage = worldBossRun.ghosts.reduce(function (s, x) { return s + x.damage; }, 0);
    worldBossRun.playerDamage = 5;
    return window.__debugWbDamageRanking();
  };
  window.__debugInventory = function () {
    return inventory.map(function (it) {
      return { id: it.id, type: it.type, name: it.name, rarity: it.rarity || null,
        detail: it.detail || '', fromActivity: !!it.fromActivity,
        // 掉落物（深渊战利品这类）：带上美术 / 售价，测试要核对
        giftId: it.giftId || null, lootId: it.lootId || null, materialId: it.materialId || null,
        lootSet: it.lootSet || null, stickerId: it.stickerId || null,
        sellGold: it.sellGold || 0, iconArt: it.iconArt || null };
    });
  };
  // 调试/测试用：副本阵亡会掉的东西（副本内所得 + 身上金币）
  window.__debugActivityLootState = function () {
    return {
      inActivity: !!activityRun,
      activityItems: inventory.filter(function (it) { return it && it.fromActivity; })
        .map(function (it) { return it.name; }),
      otherItems: inventory.filter(function (it) { return !(it && it.fromActivity); })
        .map(function (it) { return it.name; }),
      gold: gold
    };
  };
  window.__debugGiveWorldBossChest = function () {
    const chest = makeWorldBossChest();
    inventory.push(chest);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: chest.id, name: chest.name };
  };
  // 调试用：直接弹出"世界BOSS挑战奖励"三个宝箱（看美术 / 测开启流程）
  window.__debugWbRewardPopup = function () {
    showWorldBossRewardChests(true);
    return window.__debugWbChests();
  };
  window.__debugWbChests = function () {
    const box = ui.wbRewardChests;
    if (!box) return null;
    const cards = Array.prototype.slice.call(box.querySelectorAll('.wbChest'));
    return {
      open: !ui.wbRewardPopup.classList.contains('hidden'),
      count: cards.length,
      // 每格结构是不是齐全：掉落光柱 + 方形槽位（槽里要么是箱子图，要么是战利品）
      partsOk: cards.every(function (c) {
        return ['.wbChestBeam', '.wbChestBox'].every(function (sel) { return !!c.querySelector(sel); });
      }),
      // 未开启时用的箱子美术
      chestImgs: cards.map(function (c) {
        const img = c.querySelector('.wbChestImg');
        return img ? img.getAttribute('src') : null;
      }),
      tags: cards.map(function (c) { return (c.querySelector('.wbChestTag') || {}).textContent; }),
      loot: cards.map(function (c) { return (c.querySelector('.wbChestLoot') || {}).textContent; }),
      names: cards.map(function (c) { return (c.querySelector('.wbChestName') || {}).textContent; }),
      lidTransforms: cards.map(function (c) {
        const lid = c.querySelector('.wbChestLid');
        return lid ? getComputedStyle(lid).transform : null;
      }),
      // 新界面：稀有度主色 / 掉落光柱高度 / 槽位里是箱子还是战利品
      colors: cards.map(function (c) { return c.style.getPropertyValue('--lc') || null; }),
      beamHeights: cards.map(function (c) { return Math.round(c.querySelector('.wbChestBeam').getBoundingClientRect().height); }),
      slotItems: cards.map(function (c) {
        const it = c.querySelector('.wbChestItem');
        if (!it) return null;
        const img = it.querySelector('img');
        return img ? ('img:' + img.getAttribute('src')) : ('glyph:' + it.textContent);
      }),
      slotBox: (function () {
        const b = box.querySelector('.wbChestBox');
        if (!b) return null;
        const r = b.getBoundingClientRect();
        return { w: Math.round(r.width), h: Math.round(r.height), square: Math.abs(r.width - r.height) <= 1 };
      })(),
      progress: ui.wbRewardProgressText ? ui.wbRewardProgressText.textContent : null,
      columns: getComputedStyle(box).gridTemplateColumns
    };
  };
  window.__debugWbOpenChest = function (i) {
    if (!wbRewardOpening) return null;
    const c = wbRewardOpening.chests[Math.max(0, Math.min(i || 0, wbRewardOpening.chests.length - 1))];
    openWorldBossRewardChest(c);
    return window.__debugWbChests();
  };
  window.__debugOpenWorldBossChest = function () {
    const chest = inventory.filter(function (it) { return it.type === 'worldChest'; })[0];
    if (!chest) return null;
    return openWorldBossChest(chest);
  };
  window.__debugChest = function () {
    return chest ? {
      worldX: Math.round(chest.worldX),
      opened: !!chest.opened,
      weaponTaken: !!chest.weaponTaken,
      gemTaken: !!chest.gemTaken,
      stoneTaken: !!chest.stoneTaken
    } : null;
  };
  window.__debugOpenChest = function () { openChestPanel(); return !!chest; };
  window.__debugOpenSpell = function () { openSpellPanel(); return true; };
  window.__debugOpenGear = function () { openGearPanel(); return true; };
  window.__debugOpenShop = function () { openShop(); renderShop(); return true; };
  window.__debugOpenChar = function () { openCharPanel(); return true; };
  // 调试用：首页（点角色登录）/ 赞助窗口
  window.__debugIntro = function () {
    const sx = player.worldX - cameraX();
    return {
      gameState: gameState,
      loginHidden: ui.loginScreen.classList.contains('hidden'),
      titleHidden: ui.title.classList.contains('hidden'),
      resting: playerResting,
      restPose: restPoseIdx,
      frameKey: lastPlayerFrameKey,        // 当前实际绘制的帧（首页应为 player_rest_3）
      frameFile: (function () {
        if (!lastPlayerFrameKey) return null;
        if (/^player_rest_/.test(lastPlayerFrameKey)) {
          return assetUrl('assets/img/player/player_rest_v2_' + lastPlayerFrameKey.slice(12) + '.png');
        }
        if (/^player_mine_/.test(lastPlayerFrameKey)) {
          return assetUrl('assets/img/player/mining_v2_' + lastPlayerFrameKey.slice(12) + '.png');
        }
        return assetUrl('assets/img/player/' + lastPlayerFrameKey + '.png');
      })(),
      playerSx: Math.round(sx),
      groundY: GROUND_Y,
      playerTopY: Math.round(GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * playerFx.scale * caveScale()),
      dialogVisible: introDialogVisible(),
      donateVisible: ui.btnDonate ? !ui.btnDonate.classList.contains('hidden') : null
    };
  };
  window.__debugClickPlayer = function () {
    const sx = player.worldX - cameraX();
    const hit = hitPlayerAt(sx, GROUND_Y - 90);
    if (hit) openLoginFromIntro();
    return {
      hit: hit,
      loginHidden: ui.loginScreen.classList.contains('hidden'),
      // 附带诊断信息，便于排查"首页看不到角色"之类的问题
      diag: {
        idLoaded: !!(images.player_idle_1 && images.player_idle_1.complete),
        alpha: playerFx.alpha,
        hide: debugHidePlayer,
        lastFrame: lastPlayerFrameKey,
        resting: playerResting,
        restIdx: restPoseIdx,
        restLoaded: playerRestLoadedCount()
      }
    };
  };
  window.__debugClickAt = function (sx, sy) {
    return hitPlayerAt(sx, sy);
  };
  window.__debugDonate = function () {
    return {
      visible: ui.btnDonate ? !ui.btnDonate.classList.contains('hidden') : null,
      popupOpen: ui.donatePopup ? !ui.donatePopup.classList.contains('hidden') : null,
      alipayLoaded: !!(ui.donateAlipay && ui.donateAlipay.naturalWidth > 0),
      wechatLoaded: !!(ui.donateWechat && ui.donateWechat.naturalWidth > 0),
      title: ui.donatePopup ? (ui.donatePopup.querySelector('h2') || {}).textContent : null
    };
  };
  window.__debugOpenDonate = function () { openDonate(); return window.__debugDonate(); };
  window.__debugCloseDonate = function () { closeDonate(); return window.__debugDonate(); };
  window.__debugMusic = function () {
    return {
      visible: ui.musicControl ? !ui.musicControl.classList.contains('hidden') : null,
      open: !!(ui.musicControl && ui.musicControl.classList.contains('open')),
      inChat: !!(ui.musicControl && ui.musicControl.classList.contains('in-chat')),
      parent: ui.musicControl && ui.musicControl.parentElement ? ui.musicControl.parentElement.className : null,
      volume: Math.round(musicVolume * 100),
      audioVolume: musicAudio ? Math.round(musicAudio.volume * 100) : null,
      muted: musicMuted,
      playing: !!(musicAudio && !musicAudio.paused),
      loop: !!(musicAudio && musicAudio.loop),
      src: musicAudio ? musicAudio.src : null,
      err: musicAudio ? musicAudio.error && musicAudio.error.code : null
    };
  };
  window.__debugSfx = function () {
    return { globalVolume: Math.round(sfxVolume * 100), list: Object.keys(SFX_DEFS).map(function (k) {
      const a = sfxPool[k];
      const voices = sfxVoices[k] || (a ? [a] : []);
      return {
        key: k, src: SFX_DEFS[k].src,
        loaded: !!a,
        // 多声部音效（拾取）：任一 voice 在响就算"正在播放"
        playing: voices.some(function (v) { return !v.paused; }),
        voiceCount: voices.length,
        volume: Math.round(SFX_DEFS[k].volume * 100) / 100,
        effectiveVolume: Math.round(SFX_DEFS[k].volume * sfxVolume * 100) / 100
      };
    }) };
  };
  window.__debugPlaySfx = function (key) { return playSfx(key); };
  window.__debugStopSfx = function (key) {
    const voices = sfxVoices[key] || (sfxPool[key] ? [sfxPool[key]] : null);
    if (!voices) return false;
    voices.forEach(function (a) {
      a.pause();
      try { a.currentTime = 0; } catch (e) { /* 忽略 */ }
    });
    return true;
  };
  window.__debugRunSfx = function () {
    return {
      playing: runLoopPlaying,
      loop: !!(runLoopAudio && runLoopAudio.loop),
      src: runLoopAudio ? runLoopAudio.src : null,
      volume: runLoopAudio ? Math.round(runLoopAudio.volume * 100) / 100 : null
    };
  };
  window.__debugCloseChar = function () { closeCharPanel(); return true; };
  window.__debugNewbieChestFlag = function () { return newbieChestUsed; };
  window.__debugKillPlayer = function () {
    combat.hp = 0;
    if (!deathActive) triggerDeath();   // 走完整阵亡流程（含洞府掉落惩罚）
    return {
      hp: combat.hp,
      death: deathActive,
      deathOpen: !ui.deathPopup.classList.contains('hidden'),
      lostRows: ui.deathLoss ? ui.deathLoss.querySelectorAll('.lootRow').length : 0
    };
  };
  window.__debugDeathState = function () {
    return {
      deathActive: deathActive,
      gameState: gameState,
      popup: ui.deathPopup ? !ui.deathPopup.classList.contains('hidden') : null,
      distanceKm: Math.round((Math.max(0, player.worldX - START_X) / KM_UNIT) * 10) / 10,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      defeated: defeatedEnemies
    };
  };
  window.__debugQuakeDamage = function () {
    const atkMin = 23 + derived.flatAtk;
    const atkMax = 34 + derived.flatAtk;
    return {
      atkMin: atkMin, atkMax: atkMax, strength: derived.strengthTotal, flatAtk: derived.flatAtk,
      quake: Math.max(1, Math.round(randInt(atkMin, atkMax) * 0.5 + derived.strengthTotal))
    };
  };
  window.__debugBleed = function () {
    return {
      waves: shockWaves.length,
      enemies: battleAlive().map(function (e) {
        return { name: enemyLabel(e, false), hp: Math.round(e.hp),
          bleed: e.bleed ? { dmg: Math.round(e.bleed.dmg), turns: e.bleed.turns } : null };
      })
    };
  };
  window.__debugCastSkill = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    const before = battleAlive().map(function (e) {
      return { name: enemyLabel(e, false), hp: e.hp, level: e.level };
    });
    castSkill(skill);
    const after = battleAlive().map(function (e) { return { name: enemyLabel(e, false), hp: e.hp }; });
    const hits = before.map(function (b) {
      const a = after.filter(function (x) { return x.name === b.name; })[0];
      return { name: b.name, dmg: a ? (b.hp - a.hp) : b.hp };
    });
    return { skill: skill.name, hits: hits, log: ui.battleLog ? ui.battleLog.textContent : '' };
  };
  // 调试用：把背包里最新的灵宝穿上
  // 调试用：造一件只有"力量 +N"的手套并穿上（核对力量→生命的换算）
  window.__debugMakeStrengthGloves = function (n) {
    const it = makeEquipment(1, 'common', 'gloves');
    it.mods = [{ key: 'strength', value: Math.max(1, Math.round(n || 6)), value2: 0, bandIdx: 0, locked: false }];
    applyItemMods(it);
    equipItem(it);
    return { name: it.name, detail: it.detail, strength: it.strength || 0, slot: it.slot };
  };
  // 调试用：造一双带指定三维词缀的鞋子并穿上（验收"装备敏捷/智力真的生效"）
  window.__debugMakeStatShoes = function (mods) {
    const it = makeEquipment(10, 'rare', 'shoes');
    const list = Array.isArray(mods) ? mods : [{ key: mods || 'agility', value: 20 }];
    it.mods = list.map(function (m) {
      return { key: m.key, value: Math.max(1, Math.round(m.value || 20)), value2: 0, bandIdx: 0, locked: false };
    });
    applyItemMods(it);
    equipItem(it);
    recalcPlayerStats();
    return {
      name: it.name, detail: it.detail, slot: it.slot,
      strength: it.strength || 0, agility: it.agility || 0, intelligence: it.intelligence || 0
    };
  };
  window.__debugEquipTreasure = function () {
    let idx = -1;
    inventory.forEach(function (it, i) { if (it.slot === 'treasure') idx = i; });
    if (idx < 0) return null;
    equipItem(inventory[idx]);
    return equipment.treasure
      ? { name: equipment.treasure.name, special: equipment.treasure.special }
      : null;
  };
  window.__debugUnequipTreasure = function () {
    if (!equipment.treasure) return null;
    unequipSlot('treasure');
    return { equipped: !!equipment.treasure, effLevel: worldBossPlayerLevel() };
  };
  // 调试用：直接结算一个洞府（验证金色洞府掉灵宝）
  window.__debugFinishCave = function (id) {
    const def = CAVE_DEFS.filter(function (d) { return d.id === (id || 'gold'); })[0] || CAVE_DEFS[2];
    const beforeCount = inventory.length;
    const goldBefore = gold;
    cave = { def: def, remaining: 0, total: def.enemies, killed: def.enemies, entrance: null };
    finishCave();
    const added = inventory.slice(beforeCount);
    const treasure = added.filter(function (it) { return !!it.special; })[0] || null;
    return {
      cave: def.id,
      goldGain: gold - goldBefore,
      added: added.map(function (it) { return it.name; }),
      treasure: treasure
        ? { name: treasure.name, special: treasure.special, detail: treasure.detail }
        : null
    };
  };
  /* 调试用：造一个指定"战斗速度"的敌人开战，看谁先手
   *   （先手现在比的是**攻击速度**：玩家 100% + 攻速加成 vs 敌人 combatSpeed） */
  window.__debugTestFirstStrike = function (enemySpeed) {
    spawnNextEnemy();
    if (!activeEnemy) return null;
    activeEnemy.combatSpeed = typeof enemySpeed === 'number' ? enemySpeed : 9.9;
    player.worldX = activeEnemy.x - ENEMY_GAP_PX;
    startBattle();
    const turn = battleTurn;
    const myAtk = attackSpeedMult();
    const foeAtk = activeEnemy.combatSpeed;
    // 收尾：把这场假战斗清干净
    pendingVictory = 0;
    activeEnemy = null;
    battleGroup.length = 0;
    gameState = 'walk';
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    return {
      turn: turn,
      playerAtk: Math.round(myAtk * 1000) / 1000,
      enemySpd: Math.round(foeAtk * 1000) / 1000,
      playerMoveSpeed: Math.round((derived.speedMult || 1) * 1000) / 1000,
      atkSpeedPct: derived.atkSpeed || 0
    };
  };
  // 调试用：直接赢一场，看金币变化（验证「聚宝灵纹」）
  window.__debugTestBattleGold = function () {
    spawnNextEnemy();
    if (!activeEnemy) return null;
    const before = gold;
    activeEnemy.hp = 1;
    winBattle();
    return { before: before, after: gold, delta: gold - before };
  };
  // 调试用：存档快照 / 手动存档（验证读档恢复金币与装备）
  window.__debugSave = function () { saveGame(); return activeSaveKey(); };
  // 调试用：联网状态 / 登录 / 存档同步 / 聊天
  window.__debugNetState = function () {
    return {
      serverUrl: net.serverUrl,
      accountOnline: net.accountOnline,
      chatState: net.chatState,
      onlineCount: net.onlineCount,
      hasToken: !!net.token,
      pushCount: net.pushCount,
      lastPushAt: net.lastPushAt,
      pendingSave: !!net.pendingSave,
      lastError: net.lastError,
      accountName: account.name,
      saveKey: activeSaveKey(),
      localSavedAt: (function () {
        try {
          const raw = window.localStorage.getItem(activeSaveKey());
          return raw ? (JSON.parse(raw).savedAt || 0) : 0;
        } catch (e) { return 0; }
      })()
    };
  };
  // 调试用：直接走一遍"服务器登录"（等同在登录页点登录）
  window.__debugNetLogin = async function (user, pwd, server) {
    if (ui.loginUser) ui.loginUser.value = user || '';
    if (ui.loginPwd) ui.loginPwd.value = pwd || '';
    if (server) window.__debugSetServer(server);
    await doLogin();
    return window.__debugNetState();
  };
  /* 调试/自动化：指定后端地址（登录框里的服务器 IP 已按需求移除）。
   *   传空字符串 = 回到"当前页面同源"。 */
  window.__debugSetServer = function (url) {
    const raw = String(url == null ? '' : url).trim();
    net.serverUrl = normalizeServerUrl(raw);
    try { window.localStorage.setItem(NET_SERVER_KEY, net.serverUrl); } catch (e) { /* 忽略 */ }
    if (ui.loginIp) ui.loginIp.value = raw;
    return net.serverUrl;
  };
  // 调试/自动化：呼出"删除本机所有档案"的确认弹窗（按钮已按需求从登录框移除）
  window.__debugAskWipe = function () { askWipeSaves(); return true; };
  // 调试用：把当前存档立刻推给服务器
  window.__debugNetFlush = async function () {
    net.pendingSave = buildSavePayload();
    const r = await flushRemoteSaveNow();
    return { pushed: !!r, state: window.__debugNetState() };
  };
  // 调试用：从服务器重新拉一次存档（返回服务器上那份的等级 / 金币）
  window.__debugNetFetchSave = async function () {
    if (!net.api || !net.token) return null;
    const r = await net.api.fetchSave(net.token);
    return {
      version: r.version, savedAt: r.savedAt,
      level: r.save ? r.save.level : null,
      gold: r.save ? r.save.gold : null,
      vitality: r.save ? r.save.vitality : null,
      distance: r.save ? r.save.distance : null,
      codexCount: r.save && r.save.codex ? r.save.codex.length : 0,
      equipmentCount: r.save && r.save.equipment ? Object.keys(r.save.equipment).length : 0
    };
  };
  // 调试用：发一条聊天（走联网通道）
  window.__debugChatSend = function (text) {
    if (!net.chat) return false;
    net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text });
    return true;
  };
  // 调试用：模拟"服务器推来一条聊天"（含别人的物品分享负载），验证收消息链路
  window.__debugChatRecv = function (msg) {
    if (!msg || typeof msg !== 'object') return false;
    onChatServerMessage(msg);
    return true;
  };
  // 调试用：把背包里某件物品分享到世界频道（走和「分享」按钮完全一样的逻辑）
  window.__debugShareItem = function (id) {
    const it = inventory.filter(function (x) { return x.id === id; })[0];
    if (!it) return false;
    return shareItemToChat(it);
  };
  // 调试用：「强力射击」的武器限制 / 当前武器类型
  window.__debugPowerShotWeapon = function () {
    return { required: POWER_SHOT_WEAPON, current: weaponTypeEquipped(), ok: powerShotWeaponOk() };
  };
  // 调试用：战斗回合的即时状态（灵力 / 战报 / 是否在蓄力）——武器限制这类改动要核对
  window.__debugBattle = function () {
    return {
      turn: battleTurn,
      mp: Math.round(combat.mp), maxMp: Math.round(combat.maxMp),
      charging: !!powerCharge,
      weapon: weaponTypeEquipped(),
      log: ui.battleLog ? ui.battleLog.textContent : ''
    };
  };
  // 调试用：自动冒险时这个技能能不能用（武器不对的强力射击必须返回 false）
  window.__debugAutoSkillUsable = function (id) {
    const s = knownSkills.filter(function (x) { return x.id === id; })[0];
    return s ? !!autoSkillUsable(s) : null;
  };
  /* 调试用：每日任务状态
   *   传 id 就是"手动完成这一项"（验证加分 / 满 100 送活力 / 跨天重置） */
  window.__debugDailyQuest = function (id) {
    if (id && typeof id === 'string') {
      const existed = !!dailyQuests.done[id];
      dailyQuestTick(id);
      return Object.assign(window.__debugDailyQuest(),
        { ticked: id, alreadyDone: existed });
    }
    dailyQuestSyncDay();
    return {
      day: dailyQuests.day,
      done: Object.keys(dailyQuests.done).filter(function (k) { return dailyQuests.done[k]; }),
      points: dailyQuestPoints(),
      goal: DAILY_QUEST_GOAL,
      reward: DAILY_QUEST_REWARD,
      rewarded: !!dailyQuests.rewarded,
      vitality: vitality,
      quests: DAILY_QUESTS.map(function (q) {
        return { id: q.id, name: q.name, points: q.points, done: !!dailyQuests.done[q.id] };
      })
    };
  };
  // 调试用：把每日任务按"昨天"存档（验证跨天自动清零）
  window.__debugDailyQuestSetDay = function (day) {
    dailyQuests.day = String(day || '');
    // 故意不在这里 sync：先把"昨天"的状态原样回给测试，再由测试触发一次跨天检查
    return {
      day: dailyQuests.day,
      done: Object.keys(dailyQuests.done).filter(function (k) { return dailyQuests.done[k]; }),
      points: dailyQuests.points,
      rewarded: !!dailyQuests.rewarded
    };
  };
  // 调试用：实名绑定状态 / 打开绑定窗口 / 直接绑定
  window.__debugIdentity = function () {
    return {
      bound: net.identityBound,
      profile: net.profile,
      popupOpen: ui.identityPopup ? !ui.identityPopup.classList.contains('hidden') : null,
      chatStatus: ui.chatStatus ? ui.chatStatus.textContent : null
    };
  };
  window.__debugOpenIdentity = function () { openIdentityPanel(''); return true; };
  window.__debugBindIdentity = async function (realName, idCard) {
    if (ui.idRealName) ui.idRealName.value = realName || '';
    if (ui.idCardNo) ui.idCardNo.value = idCard || '';
    if (ui.identityPopup) ui.identityPopup.classList.remove('hidden');
    const okDone = await submitIdentity();
    return { ok: okDone, state: window.__debugIdentity() };
  };
  // 调试用：金币等数值是否已经落盘（本地 / 云端）
  window.__debugNetToken = function () { return net.token; };
  // 调试用：燃烧层数 / 昵称 / 多段伤害
  window.__debugBurnState = function () {
    const list = battleAlive().map(function (e) {
      return { name: enemyLabel(e, false), stacks: e.burnStacks || 0, hp: Math.round(e.hp) };
    });
    return {
      stacks: activeEnemy ? (activeEnemy.burnStacks || 0) : 0,
      enemies: list,
      perStack: Math.max(1, Math.round(combat.maxHp * BURN_PCT)),
      maxHp: combat.maxHp,
      chance: BURN_CHANCE,
      hasGem: skillHasGem('yangClaw', 'flameLegion')
    };
  };
  window.__debugSetBurn = function (n) {
    if (!activeEnemy) return null;
    activeEnemy.burnStacks = Math.max(0, Math.min(BURN_STACK_MAX, Math.round(n || 0)));
    return activeEnemy.burnStacks;
  };
  window.__debugBurnTick = function () {
    const before = activeEnemy ? Math.round(activeEnemy.hp) : null;
    applyBurnTick();
    return { hpBefore: before, hpAfter: activeEnemy ? Math.round(activeEnemy.hp) : null };
  };
  window.__debugNick = function () {
    return {
      accountName: account.name,
      nickname: account.nickname,
      chatNick: chatNick(),
      popupOpen: ui.nickPopup ? !ui.nickPopup.classList.contains('hidden') : null,
      localSaved: loadLocalNickname(),
      battleWho: (function () {
        const el = document.querySelector('#battleHud .who');
        return el ? el.textContent : null;
      })()
    };
  };
  window.__debugSetNickname = async function (nick) {
    openNickPanel();                                   // 先开面板（会用当前昵称填充输入框）
    if (ui.nickInput) ui.nickInput.value = nick || '';  // 再写入要测的值
    const done = await submitNickname();
    return { ok: done, state: window.__debugNick() };
  };
  window.__debugSaveState = function () {
    let local = null;
    try {
      const raw = window.localStorage.getItem(activeSaveKey());
      local = raw ? JSON.parse(raw) : null;
    } catch (e) { local = null; }
    return {
      memoryGold: gold,
      memoryVitality: vitality,
      memoryLevel: player.level,
      localGold: local ? local.gold : null,
      localSavedAt: local ? local.savedAt : null,
      dirty: saveDirty,
      pendingUpload: !!net.pendingSave,
      accountOnline: net.accountOnline,
      pushCount: net.pushCount
    };
  };
  window.__debugChatRows = function () {
    if (!ui.chatList) return [];
    return Array.prototype.map.call(ui.chatList.children, function (row) {
      return row.textContent.replace(/\s+/g, ' ').trim();
    });
  };
  /* 调试/测试用：聊天表情面板状态 */
  window.__debugChatEmoji = function () {
    const panel = ui.chatEmojiPanel;
    buildChatEmojiPanel();
    return {
      hasButton: !!ui.chatEmoji,
      open: !!(panel && !panel.classList.contains('hidden')),
      items: panel ? panel.querySelectorAll('.emojiItem').length : 0,
      groups: panel ? Array.prototype.map.call(panel.querySelectorAll('.emojiGroup'), function (g) { return g.textContent; }) : [],
      input: ui.chatInput ? ui.chatInput.value : null,
      // 表情包（僵尸表情）在这块面板里的状态
      stickers: panel ? Array.prototype.map.call(panel.querySelectorAll('.emojiItem.sticker'), function (b) {
        return {
          label: b.getAttribute('aria-label'),
          locked: b.classList.contains('locked'),
          disabled: !!b.disabled,
          hasImg: !!b.querySelector('img.emojiImg'),
          src: (b.querySelector('img.emojiImg') || {}).getAttribute
            ? b.querySelector('img.emojiImg').getAttribute('src') : null
        };
      }) : [],
      unlocked: Object.keys(chatStickers)
    };
  };
  /* 调试/测试用：表情包状态 / 发一枚到背包 / 临时改掉落率（验完记得改回 0.1） */
  window.__debugStickerState = function () {
    return {
      all: CHAT_STICKERS.map(function (s) { return { id: s.id, name: s.name, token: s.token, art: s.art }; }),
      unlocked: Object.keys(chatStickers),
      dropPct: stickerDropPct(),
      inBag: inventory.filter(function (it) { return it && it.type === 'sticker'; })
        .map(function (it) { return { id: it.id, stickerId: it.stickerId, name: it.name, art: it.iconArt }; })
    };
  };
  window.__debugGiveSticker = function (id) {
    const s = id ? stickerDef(id) : null;
    const def = s || CHAT_STICKERS[randInt(0, CHAT_STICKERS.length - 1)];
    const it = makeStickerItem(def);
    inventory.push(it);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: it.id, stickerId: it.stickerId, name: it.name };
  };
  window.__debugStickerSetPct = function (pct) {
    stickerDropPctOverride = (typeof pct === 'number') ? Math.max(0, pct) : null;
    return stickerDropPct();
  };
  window.__debugChatEmojiInsert = function (ch) { return insertChatEmoji(ch || '🙂'); };
  /* 调试/测试用：聊天小红点状态 */
  window.__debugChatDot = function () {
    return {
      visible: !!(ui.chatDot && !ui.chatDot.classList.contains('hidden')),
      collapsed: chatCollapsed(),
      statusText: ui.chatStatus ? ui.chatStatus.textContent : null
    };
  };
  /* 调试/测试用：直接切聊天连接状态（验证状态栏文案 / 红点） */
  window.__debugSetChatState = function (st) {
    net.chatState = st || 'offline';
    updateChatStatus();
    return window.__debugChatDot();
  };
  /* 调试/测试用：模拟"账号在线、聊天长连接断了"（发消息走排队 + 只提示一次重连） */
  window.__debugFakeChatOffline = function () {
    net.accountOnline = true;
    net.chat = { send: function () { return false; }, close: function () {} };
    net.chatState = 'connecting';       // 已经是"连接中"→ 不再触发真的重连（测试里没有 ws）
    updateChatStatus();
    return { accountOnline: net.accountOnline, chatState: net.chatState, noticed: chatOfflineNoticed };
  };
  /* 调试/测试用：走一遍真实的"连接状态变化"（含连上时的提示 / 复位） */
  window.__debugChatStatus = function (st) {
    chatStatusChanged(st || 'offline');
    return { chatState: net.chatState, noticed: chatOfflineNoticed };
  };
  /* 调试/测试用：走一遍真实的"发消息"（断线时走排队 + 提示） */
  window.__debugChatText = function (text) {
    sendChatText(text == null ? '' : String(text), '');
    return window.__debugChatRows();
  };
  window.__debugChatNick = function () { return chatNick(); };
  window.__debugSaveSnapshot = function () {
    return {
      key: activeSaveKey(),
      gold: gold,
      level: player.level,
      vitality: vitality,
      flask: flaskCharges,
      equipment: Object.keys(equipment).filter(function (k) { return equipment[k]; })
        .map(function (k) { return { slot: k, name: equipment[k].name, level: equipment[k].level, detail: equipment[k].detail }; }),
      bag: inventory.map(function (it) { return { type: it.type, name: it.name }; })
    };
  };
  window.__debugShopItems = function (n) {
    const out = [];
    for (let i = 0; i < (n || 12); i++) {
      const lv = getShopItemLevel();
      out.push({ level: Math.max(1, lv + randInt(-1, 1)), name: makeEquipment(lv).name });
    }
    return { shopLevel: getShopItemLevel(), playerLevel: player.level, levels: out.map(function (o) { return o.level; }) };
  };

  /* ---------- 调试/测试：装备回收 ---------- */
  // 连续弹 n 条提示（用来验证多条消息是"自上而下逐条排开"而不是原地重叠）
  window.__debugToast = function (n, prefix) {
    const times = Math.max(1, Math.round(Number(n) || 1));
    for (let i = 1; i <= times; i++) showToast((prefix || '测试提示') + ' ' + i);
    return document.querySelectorAll('#toast').length;
  };
  // 直接弹一条指定文案的提示（出验收图 / 复现多消息并排用）
  window.__debugToastText = function (text) {
    showToast(String(text == null ? '' : text));
    return document.querySelectorAll('#toast').length;
  };
  // 往背包里塞 n 件指定品级的装备（不指定品级就白/蓝/金各来一件的循环）
  window.__debugGiveBagGear = function (n, rarity, slot) {
    const times = Math.max(1, Math.round(Number(n) || 1));
    const pool = ['common', 'magic', 'rare'];
    for (let i = 0; i < times; i++) {
      inventory.push(makeEquipment(player.level, rarity || pool[i % pool.length], slot || null));
    }
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.filter(function (it) { return it.type === 'equipment'; }).length;
  };

  /* 宠物蛋：活力兑换里已经下架（按需求移除），测试 / 展示需要时用这个直接塞一颗进背包 */
  window.__debugGivePetEgg = function (n) {
    const times = Math.max(1, Math.round(Number(n) || 1));
    for (let i = 0; i < times; i++) inventory.push(makePetEgg());
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.filter(function (it) { return it.type === 'petEgg'; }).length;
  };
  window.__debugRecycle = function () {
    const byTier = {};
    inventory.forEach(function (it) {
      if (it && it.type === 'equipment') byTier[it.rarity] = (byTier[it.rarity] || 0) + 1;
    });
    return {
      filter: { common: !!recycleFilter.common, magic: !!recycleFilter.magic, rare: !!recycleFilter.rare },
      byTier: byTier,
      targets: recycleTargets().length,
      targetValue: recycleValueOf(recycleTargets()),
      gold: gold,
      auto: !!autoAdventure,
      materialChance: RECYCLE_MATERIAL_CHANCE
    };
  };
  window.__debugSetRecycleFilter = function (obj) {
    if (obj && typeof obj === 'object') {
      Object.keys(recycleFilter).forEach(function (k) { recycleFilter[k] = !!obj[k]; });
    }
    if (!ui.recyclePopup.classList.contains('hidden')) renderRecyclePanel();
    return window.__debugRecycle();
  };
  window.__debugRecycleNow = function () { return recycleRunNow(); };
  window.__debugAutoRecycle = function () { autoRecycleSweep(); return window.__debugRecycle(); };
  // 包里有多少份回收材料（按名字统计）
  window.__debugRecycleMaterials = function () {
    const out = {};
    inventory.forEach(function (it) {
      if (it && it.type === 'material' && it.recycleRarity) {
        out[it.name] = (out[it.name] || 0) + 1;
      }
    });
    return out;
  };
  window.__debugAttrPoints = function () {
    const spent = ATTR_DEFS.reduce(function (sum, d) { return sum + (player.attrs[d.id] || 0); }, 0);
    return {
      level: player.level,
      attrs: { str: player.attrs.str, agi: player.attrs.agi, int: player.attrs.int },
      attrPoints: player.attrPoints,
      spent: spent,
      maxAvailable: Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent)
    };
  };
  /* 调试/测试用：直接加点（走正常的 allocateAttr：扣点 → 重算属性 → 刷 UI） */
  window.__debugAllocAttr = function (id, n) {
    const times = Math.max(1, Math.round(Number(n) || 1));
    for (let i = 0; i < times; i++) allocateAttr(id || 'str');
    return window.__debugAttrPoints();
  };
  /* 调试/测试用：直接写属性点（不经 allocateAttr，用来构造特定敏捷 / 智慧），会自动重算属性 */
  window.__debugSetAttrs = function (attrs) {
    if (!attrs || typeof attrs !== 'object') return window.__debugAttrPoints();
    ['str', 'agi', 'int'].forEach(function (k) {
      if (typeof attrs[k] === 'number') player.attrs[k] = Math.max(0, Math.round(attrs[k]));
    });
    recalcPlayerStats();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    return window.__debugAttrPoints();
  };
  window.__debugOnline = function (addSeconds) {
    if (typeof addSeconds === 'number' && addSeconds > 0) {
      onlineSeconds += addSeconds;
      const before = onlinePending;
      let lastStep = 0;
      while (onlineSeconds >= onlineNextStep()) {
        lastStep = onlineNextStep();
        onlineSeconds -= lastStep;
        onlineRewardIndex += 1;
        onlinePending += 1;
      }
      updateOnlineUI();
      // 注意：这里只累计"可领取"，活力要玩家自己点领取（对齐线上行为）
      return { pending: onlinePending, pendingGained: onlinePending - before, vitality: vitality,
        remainder: Math.round(onlineSeconds), lastStep: lastStep, nextStep: onlineNextStep(),
        index: onlineRewardIndex, claimable: onlinePending > 0 };
    }
    return { onlineSeconds: Math.round(onlineSeconds), nextStep: onlineNextStep(), vitality: vitality,
      index: onlineRewardIndex, pending: onlinePending, steps: ONLINE_REWARD_STEPS.slice(),
      text: ui.onlineTimer ? ui.onlineTimer.textContent : null,
      claimVisible: !!(ui.btnOnlineClaim && !ui.btnOnlineClaim.classList.contains('hidden')) };
  };
  // 调试用：手动领取在线奖励（等同点击 HUD 上的「领取」按钮）
  window.__debugOnlineClaim = function () {
    const before = vitality;
    claimOnlineReward();
    return { claimed: vitality - before, vitality: vitality, pending: onlinePending };
  };
  // 调试用：把在线奖励阶梯清零（重新从 3 分钟开始）
  window.__debugOnlineReset = function () {
    onlineSeconds = 0;
    onlineRewardIndex = 0;
    onlinePending = 0;
    updateOnlineUI();
    return onlineNextStep();
  };
  window.__debugPrices = function (n) {
    const out = { common: [], magic: [], rare: [] };
    ['common', 'magic', 'rare'].forEach(function (r) {
      for (let i = 0; i < (n || 12); i++) {
        out[r].push(makeEquipment(10, r, ['weapon', 'helmet', 'jewelry', 'clothes'][i % 4]).price);
      }
    });
    return out;
  };
  // 调试用：批量造鞋子，检查词缀库与数值上限
  window.__debugShoes = function (n, level) {
    const count = n || 60;
    const lv = level || 28;
    let hasAtk = 0, hasThorns = 0, hasStr = 0, hasAgi = 0, hasInt = 0;
    const max = { thornsMax: 0, hpMax: 0, armorMax: 0, speedMax: 0, resMax: 0, strengthMax: 0, agilityMax: 0, intelligenceMax: 0, chaosResMax: 0, regenMax: 0 };
    // 单条词缀的最大值（同一件装备可能有重复词缀叠加，所以单独统计"每条"的上限）
    const modMax = { strength: 0, agility: 0, intelligence: 0, hp: 0, armor: 0, thorns: 0, speedPct: 0, res: 0, mp: 0 };
    for (let i = 0; i < count; i++) {
      const it = makeEquipment(lv, ['common', 'magic', 'rare'][i % 3], 'shoes');
      (it.mods || []).forEach(function (m) {
        if (m.key === 'atk') hasAtk++;
        if (m.key === 'thorns') hasThorns++;
        if (m.key === 'strength') hasStr++;
        if (m.key === 'agility') hasAgi++;
        if (m.key === 'intelligence') hasInt++;
        if (modMax[m.key] !== undefined) modMax[m.key] = Math.max(modMax[m.key], m.value || 0);
      });
      max.thornsMax = Math.max(max.thornsMax, it.thorns || 0);
      max.hpMax = Math.max(max.hpMax, it.hp || 0);
      max.armorMax = Math.max(max.armorMax, it.armor || 0);
      max.speedMax = Math.max(max.speedMax, it.speedPct || 0);
      max.resMax = Math.max(max.resMax, it.res || 0);
      max.strengthMax = Math.max(max.strengthMax, it.strength || 0);
      max.agilityMax = Math.max(max.agilityMax, it.agility || 0);
      max.intelligenceMax = Math.max(max.intelligenceMax, it.intelligence || 0);
      max.chaosResMax = Math.max(max.chaosResMax, it.chaosRes || 0);
      max.regenMax = Math.max(max.regenMax, it.lifeRegen || 0);
    }
    return {
      count: count, level: lv,
      hasAtk: hasAtk, hasThorns: hasThorns,
      hasStr: hasStr, hasAgi: hasAgi, hasInt: hasInt,
      range: max, modMax: modMax
    };
  };
  // 调试用：造一双指定档位的鞋子并返回文本（预览/核对用）
  window.__debugShoeSample = function (i) {
    const rarities = ['common', 'magic', 'rare', 'rare', 'magic'];
    const it = makeEquipment(28, rarities[(i || 0) % rarities.length], 'shoes');
    return { name: it.name, detail: it.detail, level: it.level };
  };
  // 调试用：把几双 28 级鞋子塞进背包
  window.__debugShowBagShoes = function () {
    for (let i = 0; i < 6; i++) inventory.push(makeEquipment(28, ['common', 'magic', 'rare'][i % 3], 'shoes'));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.length;
  };
  window.__debugAccounts = function () {
    const store = readAccounts();
    return {
      current: account.hash ? { name: account.name, hash: account.hash, isNew: account.isNew } : null,
      accounts: store.accounts.map(function (a) {
        const raw = window.localStorage.getItem(saveKeyFor(a.hash));
        let lv = null;
        try { lv = raw ? (JSON.parse(raw).level || 1) : null; } catch (e) { lv = null; }
        return { name: a.name, hash: a.hash, savedLevel: lv, hasSave: !!raw };
      }),
      legacySaveExists: !!window.localStorage.getItem(LEGACY_SAVE_KEY),
      saveKeys: Object.keys(window.localStorage).filter(function (k) { return k.indexOf('homeward.save.') === 0; })
    };
  };
  window.__debugAccountHash = function (name, pwd) { return accountHash(name, pwd); };
  window.__debugWriteSaveLevel = function (level) {   // 造一个"已存在的档案"用来测续档
    player.level = Math.max(1, level || 3);
    player.xp = 0;
    saveGame();
    return activeSaveKey();
  };
  window.__debugResetSaves = function () {            // 一键抹掉本机所有档案（含旧版单存档）
    resetAllSaves();
    return true;
  };
  // 调试用：当前穿戴的装备快照（洗练前后对比用）
  window.__debugEquipped = function () {
    const out = {};
    Object.keys(equipment).forEach(function (k) {
      const it = equipment[k];
      if (!it) return;
      out[k] = { id: it.id, name: it.name, level: it.level, rarity: it.rarity, detail: it.detail,
        slot: it.slot, iconSet: it.iconSet || null, iconImg: it.iconImg || 0,
        baseDefKind: it.baseDefKind || null, baseDefValue: it.baseDefValue || 0,
        baseDetail: it.baseDetail || '',
        inlay: it.inlay || null,
        uniqueStats: it.uniqueStats || null, mech: it.mech || null,
        stats: GEAR_STAT_KEYS.reduce(function (acc, key) { if (it[key]) acc[key] = it[key]; return acc; }, {}) };
    });
    return out;
  };
  // 调试/测试用：按 id 把背包里的一件装备穿到身上
  window.__debugEquipById = function (id) {
    const it = inventory.filter(function (x) { return x.id === id; })[0];
    if (!it || it.type !== 'equipment') return null;
    equipItem(it);
    return { id: it.id, name: it.name, slot: it.slot };
  };
  // 调试用：休息姿态——传秒数可缩短"随机换姿态"的间隔；传对象可强制指定姿态
  window.__debugRestPose = function (arg) {
    if (typeof arg === 'number') {
      restPoseForce = -1;
      return { force: restPoseForce };
    }
    if (arg && typeof arg.force === 'number') {
      restPoseForce = arg.force < 0 ? -1 : ((arg.force % PLAYER_REST_FRAMES) + PLAYER_REST_FRAMES) % PLAYER_REST_FRAMES;
      restPoseIdx = restPoseForce >= 0 ? restPoseForce : restPoseIdx;
      playerResting = true;
    }
    return { resting: playerResting, pose: restPoseIdx, force: restPoseForce, loaded: playerRestLoadedCount() };
  };
  // 调试用：查看/修改精英按钮的"中央等待时长"（秒）与当前停靠状态
  window.__debugEliteDock = function (sec) {
    if (typeof sec === 'number') {
      eliteDockWait = sec;
      return eliteDockWait;
    }
    return {
      docked: eliteDocked,
      wait: eliteDockWait,
      shift: ui.btnElite ? (ui.btnElite.style.getPropertyValue('--eliteShift') || null) : null,
      className: ui.btnElite ? ui.btnElite.className : null,
      rect: ui.btnElite ? (function () {
        const r = ui.btnElite.getBoundingClientRect();
        return { left: Math.round(r.left), right: Math.round(r.right), cy: Math.round((r.top + r.bottom) / 2) };
      })() : null,
      wrap: ui.wrap ? (function () {
        const r = ui.wrap.getBoundingClientRect();
        return { left: Math.round(r.left), right: Math.round(r.right), cy: Math.round((r.top + r.bottom) / 2) };
      })() : null
    };
  };
  // 调试用：在玩家前方摆好三种洞府入口（测试用，正常游戏按路程随机生成）
  window.__debugPlaceCaves = function () {
    caveEntrances.length = 0;
    ['green', 'blue', 'gold'].forEach(function (id, i) {
      const def = CAVE_DEFS.filter(function (d) { return d.id === id; })[0];
      caveEntrances.push({ def: def, worldX: player.worldX + 520 + i * 420, used: false });
    });
    return caveEntrances.map(function (c) { return c.def.id + '@' + Math.round(c.worldX); });
  };
  // 调试用：直接进入指定洞府（相当于点击入口）
  window.__debugClickCave = function (id) {
    const entrance = caveEntrances.filter(function (c) {
      return c.def.id === id && !c.used;
    })[0];
    if (!entrance) return 'no entrance';
    clickCaveEntrance(entrance);
    return cave ? cave.def.name : (caveTransition ? 'entering' : 'blocked');
  };
  // 调试用：把角色瞬移到指定公里数（测试洞府生成）
  window.__debugTeleport = function (km) {
    player.worldX = START_X + km * KM_UNIT;
    defeatedEnemies = Math.max(defeatedEnemies, Math.floor(km / (ENEMY_GAP / KM_UNIT)));
    spawnNextEnemy();
    updateDist();
    return km;
  };
  // 调试用：把当前路上的洞府入口标记为已用过（模拟“路过不进”）
  window.__debugSkipEntrances = function () {
    caveEntrances.forEach(function (c) { c.used = true; });
    return true;
  };
  // 调试用：查看玩家 / 敌人的实际绘制缩放与战斗阵型
  window.__debugLogs = function () {
    return battleLogHistory.map(function (e) { return e.text; });
  };
  // 调试用：当前屏幕上的飘字（每个敌人的伤害应该是独立一条）
  window.__debugPopups = function () {
    return damagePopups.map(function (p) {
      return p.text ? p.text : String(p.dmg);
    });
  };
  // 调试用：飘字的位置（用来核对"伤害数字是不是飘在被攻击单位身边"）
  window.__debugPopupList = function () {
    return damagePopups.map(function (p) {
      return {
        text: p.text || ((p.positive ? '+' : '-') + p.dmg),
        x: Math.round(p.x),
        y: Math.round(p.y),
        color: p.color
      };
    });
  };
  // 调试用：清空当前飘字（测试时用来隔离"敌方出手"这一批）
  window.__debugClearPopups = function () {
    const n = damagePopups.length;
    damagePopups.length = 0;
    return n;
  };
  // 调试用：刀光资源与当前播放帧
  window.__debugSlashSprite = function () {
    return slashSpriteReady();
  };
  window.__debugSlashFx = function () {
    if (!slashFx) return null;
    const p = Math.min(1, slashFx.t / slashFx.dur);
    return {
      t: Math.round(slashFx.t * 1000) / 1000,
      dur: slashFx.dur,
      frame: Math.min(SLASH_FRAMES - 1, Math.floor(p * SLASH_FRAMES)) + 1,
      hit: slashFx.hit
    };
  };
  // 调试用：普通攻击的刀光（Slash 3 素材）
  window.__debugAttackSprite = function () {
    const first = images.slash3_1;
    return {
      ready: attackSpriteReady(),
      size: first ? first.naturalWidth + 'x' + first.naturalHeight : null,
      frame: attackFx ? Math.min(SLASH_FRAMES, Math.floor(attackFx.t / attackFx.dur * SLASH_FRAMES) + 1) : 0
    };
  };
  // 调试用：当前宠物的精灵图状态
  // 调试用：敌人身体与刀光在屏幕上的位置（核对特效是否落在敌人身上）
  window.__debugFxRects = function () {
    const sc = activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : 1;
    const ex = activeEnemy ? Math.round(activeEnemy.x - cameraX()) : null;
    const dh = 178 * sc;
    return {
      groundY: GROUND_Y,
      enemyX: ex,
      enemyBody: ex === null ? null : {
        left: Math.round(ex - 148 * sc / 2),
        top: Math.round(GROUND_Y - dh + 4),
        bottom: Math.round(GROUND_Y + 4),
        centerY: Math.round(GROUND_Y - dh / 2 + 4)
      },
      attackFx: attackFx ? {
        spriteCenterY: Math.round(GROUND_Y - ATTACK_SPRITE_LIFT),
        size: ATTACK_SPRITE_SIZE
      } : null,
      slashFx: slashFx ? {
        spriteCenterY: Math.round(GROUND_Y - SLASH_SPRITE_LIFT),
        anchorY: Math.round(GROUND_Y - 96)
      } : null
    };
  };
  window.__debugPetFlip = function () {
    return PET_SPRITE_FLIP;
  };
  // 调试用：立刻生成一个"其他玩家"虚影（可选指定模式）
  window.__debugSpawnMirage = function (mode) {
    spawnMirage(mode);
    return mirage ? mirage.mode : null;
  };
  window.__debugMirage = function () {
    return mirage ? Object.assign({ drawn: sceneActors.mirage }, {
      mode: mirage.mode, t: Math.round(mirage.t * 100) / 100,
      life: Math.round(mirage.life * 100) / 100,
      worldX: Math.round(mirage.worldX),
      sx: Math.round(mirage.worldX - cameraX()),
      playerSx: Math.round(player.worldX - cameraX())
    }) : null;
  };
  /* 调试/测试：这一帧实际画出来的"非战斗角色"（战斗场景应当全是 0/false） */
  window.__debugSceneActors = function () {
    return { state: gameState, mirage: sceneActors.mirage, corpse: sceneActors.corpse, peers: sceneActors.peers };
  };
  window.__debugMirageRaw = function () {
    if (!mirage) return null;
    return {
      mode: mirage.mode,
      t: Math.round(mirage.t * 100) / 100,
      life: Math.round(mirage.life * 100) / 100,
      worldX: Math.round(mirage.worldX),
      sx: Math.round(mirage.worldX - cameraX()),
      playerSx: Math.round(player.worldX - cameraX())
    };
  };
  window.__debugPetSprite = function () {
    if (!petAlive()) return null;
    const p = leadPet();
    const key = p.sprite || 'snake';
    const info = PET_SPRITES[key] || {};
    const anims = ['idle', 'walk', 'attack', 'hurt'].map(function (a) {
      const img = images['pet_' + key + '_' + a];
      return a + ':' + (img && img.naturalWidth ? (img.naturalWidth / PET_FRAME_SIZE) + '帧' : '缺失');
    });
    return {
      pet: p.name, key: key, ok: !!info.name, sheet: 'pet_' + key, anims: anims.join(' '),
      count: alivePets().length,
      slots: pets.map(function (x) { return x.name + '(等级' + (x.level || 1) + ')'; })
    };
  };
  // 调试用：把当前目标打成残血，测试"击杀后其余敌人照样还手"
  window.__debugBoostEnemyHp = function (mult) {
    if (!activeEnemy) return 0;
    const m = Math.max(1, mult || 5);
    activeEnemy.maxHp = Math.round(activeEnemy.maxHp * m);
    activeEnemy.hp = activeEnemy.maxHp;
    updateBattleHud();
    return activeEnemy.maxHp;
  };
  window.__debugHurtPrimary = function (hp) {
    if (!activeEnemy) return false;
    activeEnemy.hp = Math.max(1, Math.round(hp || 1));
    updateBattleHud();
    return activeEnemy.hp;
  };
  // 调试用：把玩家生命设成最大生命的百分之多少（测「处决」的低血判定）
  window.__debugSetPlayerHp = function (pct) {
    const p = clamp(Number(pct) || 100, 1, 100);
    combat.hp = Math.max(1, Math.round(combat.maxHp * p / 100));
    updateBattleHud();
    return {
      hp: combat.hp, maxHp: combat.maxHp,
      pct: Math.round(combat.hp / combat.maxHp * 100),
      lowLife: playerLowLife()
    };
  };
  // 调试用：把玩家灵力补到最大值的百分之多少（连放技能采样时用）
  window.__debugSetMp = function (pct) {
    const max = derived.mpUsable || combat.maxMp || 0;
    combat.mp = Math.max(0, Math.round(max * clamp(Number(pct) || 100, 0, 100) / 100));
    updateBattleHud();
    return { mp: Math.round(combat.mp), max: Math.round(max) };
  };
  window.__debugScales = function () {
    return {
      cave: cave ? cave.def.id : null,
      player: playerFx.scale * caveScale(),
      enemyMode: caveScale(),
      enemyTier: activeEnemy ? enemyDrawScale(activeEnemy) : null,
      enemy: activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : null
    };
  };
  window.__debugGroupInfo = function () {
    return {
      primary: activeEnemy ? { x: activeEnemy.x, tier: activeEnemy.tier } : null,
      helpers: battleGroup.filter(function (e) { return e !== activeEnemy; }).map(function (e) {
        return { x: e.x, moveToX: e.moveToX, tier: e.tier };
      })
    };
  };
  // 调试用：强制把附近敌人拉进当前战斗
  window.__debugForceJoin = function () {
    if (!cave || !activeEnemy) return false;
    const def = cave.def;
    const tier = def.tiers[randInt(0, def.tiers.length - 1)];
    addCaveHelper(tier, def);
    setBattleLog('附近的敌人加入了战斗！（多对一）');
    updateBattleHud();
    return true;
  };

  // 地面上的波纹（画在角色下方，像从脚边漾开）
  function drawBloodWavesGround() {
    if (!bloodWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const gy = GROUND_Y + 6;
    bloodWaves.forEach(function (w) {
      if (w.t < 0) return;
      const s = bloodWaveProgress(w);
      const r = Math.max(1, s.r);
      const a = s.fade;
      if (a <= 0.01) return;

      ctx.save();
      ctx.translate(cx, gy);
      ctx.scale(1, 0.34); // 压扁成地面上的涟漪

      // 中心深、外围淡的血色径向渐变
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      g.addColorStop(0, 'rgba(138, 4, 10, ' + (0.52 * a).toFixed(3) + ')');
      g.addColorStop(0.4, 'rgba(186, 18, 24, ' + (0.3 * a).toFixed(3) + ')');
      g.addColorStop(0.74, 'rgba(238, 52, 58, ' + (0.14 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 96, 96, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();

      // 两道波纹前沿，增强“荡漾”的层次
      ctx.strokeStyle = 'rgba(255, 86, 86, ' + (0.38 * a).toFixed(3) + ')';
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.97, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255, 152, 152, ' + (0.2 * a).toFixed(3) + ')';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
  }

  // 身周的血色光晕（画在角色上方，让血气裹住身体）
  function drawBloodWavesAura() {
    if (!bloodWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const hy = GROUND_Y - 56;
    bloodWaves.forEach(function (w) {
      if (w.t < 0) return;
      const s = bloodWaveProgress(w);
      const a = s.fade;
      if (a <= 0.01) return;
      const r = 30 + s.r * 0.34;
      const g = ctx.createRadialGradient(cx, hy, 3, cx, hy, r);
      g.addColorStop(0, 'rgba(112, 0, 6, ' + (0.4 * a).toFixed(3) + ')');
      g.addColorStop(0.5, 'rgba(178, 16, 22, ' + (0.16 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 80, 80, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, hy, r, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  /* 气血反弹：不规则椭圆血气冲击波飞向敌人。
   *   血气虚影本身已带左侧拖尾；这里再叠两层残影，强调"冲出去"的速度感。 */
  function drawBloodBolts() {
    if (!bloodBolts.length) return;
    const img = images.bloodReboundFx;
    if (!img || !img.complete || !img.naturalWidth) return;
    bloodBolts.forEach(function (b) {
      const s = bloodBoltPose(b);
      if (s.fade <= 0.01) return;
      const x = s.x - cameraX();
      const y = b.laneY + s.arc;
      const dir = b.flip ? -1 : 1;
      const w = 190 * b.scale * (0.84 + Math.sin(s.p * Math.PI) * 0.16);
      const h = w * (img.naturalHeight / img.naturalWidth) * (b.flat || 0.78);
      const rot = (b.spin || 0) * b.t;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      // 尾部残影
      for (let k = 2; k >= 1; k--) {
        ctx.globalAlpha = s.fade * (k === 2 ? 0.12 : 0.2);
        drawBloodBoltImage(img, x - dir * 26 * k, y, w * (k === 2 ? 0.7 : 0.84), h,
          b.flip, rot - (b.spin || 0) * 0.055 * k);
      }
      // 本体
      ctx.globalAlpha = s.fade;
      drawBloodBoltImage(img, x, y, w, h, b.flip, rot);
      ctx.restore();
      bloodBoltDrawn++;
    });
  }

  function drawBloodBoltImage(img, cx, cy, w, h, flip, rot) {
    ctx.save();
    ctx.translate(cx, cy);
    if (flip) ctx.scale(-1, 1);
    if (rot) ctx.rotate(rot);
    ctx.drawImage(img, -w / 2, -h / 2, w, h);
    ctx.restore();
  }

  // 宠物：跟随玩家的简易小动物（Canvas 自绘，免费无版权问题）
  function drawPet() {
    if (!petAlive()) return;
    // 按列表顺序（等级高的在前）排在玩家身后
    alivePets().forEach(function (p, i) {
      drawOnePet(p, i);
    });
  }

  function drawOnePet(p, slot) {
    const sx = petScreenX(slot);
    const shake = shakeOffset(p.hurtT);
    const bob = Math.sin(p.walkT * 6) * 2;
    const py = GROUND_Y + shake.y;
    // 「仆从（辅助）」：召唤生物变庞然大物 → 立绘放大
    const petScale = minionGemMods(p).scale;

    // 影子
    ctx.fillStyle = 'rgba(55, 28, 8, .32)';
    ctx.beginPath();
    ctx.ellipse(sx + shake.x, py + 4, PET_DRAW_SIZE * 0.42 * petScale, 6 * petScale, 0, 0, Math.PI * 2);
    ctx.fill();

    // 有素材就用 itch 的沙漠生物精灵图（蝎子 / 鬣狗 / 毒蛇 / 猎鹰）
    if (drawPetSprite(p, sx + shake.x, py, petScale)) {
      drawPetHpBar(p, sx, shake, petScale);
      return;
    }

    // 兜底：素材没加载出来时用手绘小动物
    ctx.save();
    ctx.translate(sx + shake.x, py + bob);
    ctx.scale(petScale, petScale);

    // 尾巴
    ctx.strokeStyle = '#a8743f';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-18, -14);
    ctx.quadraticCurveTo(-34, -20, -30, -32);
    ctx.stroke();

    // 身体
    ctx.fillStyle = '#c98f54';
    ctx.beginPath();
    ctx.ellipse(0, -20, 25, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f2c58c';
    ctx.beginPath();
    ctx.ellipse(-2, -26, 16, 12, 0, 0, Math.PI * 2);
    ctx.fill();

    // 头与耳朵
    ctx.fillStyle = '#c98f54';
    ctx.beginPath();
    ctx.moveTo(14, -60);
    ctx.lineTo(24, -74);
    ctx.lineTo(31, -56);
    ctx.closePath();
    ctx.moveTo(30, -62);
    ctx.lineTo(42, -68);
    ctx.lineTo(43, -51);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#e8b078';
    ctx.beginPath();
    ctx.arc(28, -48, 15, 0, Math.PI * 2);
    ctx.fill();

    // 眼睛
    ctx.fillStyle = '#2a1406';
    ctx.beginPath();
    ctx.arc(32, -51, 2.4, 0, Math.PI * 2);
    ctx.arc(37, -49, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 宠物血条（保留 35% 生命的反馈）
    drawPetHpBar(p, sx, shake);
  }

  // 宠物精灵图：受伤 → 攻击 → 行走 → 待机
  function petAnimKey(p) {
    const key = p.sprite || 'snake';
    const info = PET_SPRITES[key] || PET_SPRITES.snake;
    const sheetOf = function (anim) {
      const img = images['pet_' + key + '_' + anim];
      return img && img.complete && img.naturalWidth > 0 ? { img: img, frames: info[anim] || 4 } : null;
    };
    if (p.hurtT > 0.12) {
      const s = sheetOf('hurt');
      if (s) return s;
    }
    if (p.attackT > 0) {
      const s = sheetOf('attack');
      if (s) return s;
    }
    if (gameState === 'walk' && input.moving) {
      const s = sheetOf('walk');
      if (s) return s;
    }
    return sheetOf('idle') || sheetOf('walk') || sheetOf('attack');
  }

  function drawPetSprite(p, drawX, groundY, scale) {
    const sheet = petAnimKey(p);
    if (!sheet) return false;
    const frames = sheet.frames;
    const fps = 8;
    const idx = Math.floor((p.animT || 0) * fps) % frames;
    const size = PET_DRAW_SIZE * (scale || 1);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (PET_SPRITE_FLIP) {
      ctx.translate(drawX, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(
        sheet.img,
        idx * PET_FRAME_SIZE, 0, PET_FRAME_SIZE, PET_FRAME_SIZE,
        -size / 2, groundY - size + 6, size, size
      );
    } else {
      ctx.drawImage(
        sheet.img,
        idx * PET_FRAME_SIZE, 0, PET_FRAME_SIZE, PET_FRAME_SIZE,
        drawX - size / 2, groundY - size + 6, size, size
      );
    }
    ctx.restore();
    return true;
  }

  function drawPetHpBar(p, sx, shake, scale) {
    if (gameState === 'battle' || p.hp < p.maxHp) {
      const bw = 48 * (scale || 1);
      const bx = sx + shake.x - bw / 2;
      const by = GROUND_Y - 86;
      const pct = clamp(p.hp / p.maxHp, 0, 1);
      ctx.fillStyle = 'rgba(15, 6, 2, .72)';
      roundRect(bx - 1, by - 1, bw + 2, 8, 4);
      ctx.fill();
      ctx.fillStyle = pct > 0.4 ? '#9adf8c' : '#e27a4a';
      if (pct > 0.01) {
        roundRect(bx, by, Math.max(4, Math.round(bw * pct)), 6, 3);
        ctx.fill();
      }
    }
  }

  // “召唤普通敌人”的临时召唤物
  function drawAlly() {
    if (!ally || ally.turns <= 0) return;
    const sx = player.worldX - cameraX() - 112;
    const aura = ctx.createRadialGradient(sx, GROUND_Y - 46, 6, sx, GROUND_Y - 46, 66);
    aura.addColorStop(0, 'rgba(120, 255, 170, .32)');
    aura.addColorStop(1, 'rgba(120, 255, 170, 0)');
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(sx, GROUND_Y - 46, 66, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(50, 28, 8, .3)';
    ctx.beginPath();
    ctx.ellipse(sx - 6, GROUND_Y + 4, 34, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    // 召唤物用敌人美术（面朝右，和玩家一起打左边的敌人）
    const allyImg = images[enemySpriteDef({ sprite: ally.sprite }).key + '_1'];
    if (!drawEnemySprite(allyImg && allyImg.complete ? allyImg : null, sx, GROUND_Y, 0.62, true, 0,
      enemySpriteDef({ sprite: ally.sprite }))) {
      drawCharacterCrop(images.enemyIdle, sx, GROUND_Y, false, 0, 0.62);
    }
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 4;
    ctx.strokeStyle = 'rgba(20, 40, 20, .9)';
    const label = (ally.name || '召唤物') + ' x' + ally.turns;
    ctx.strokeText(label, sx, GROUND_Y - 96);
    ctx.fillStyle = '#b6ffcf';
    ctx.fillText(label, sx, GROUND_Y - 96);
    ctx.restore();
  }

  function drawEnemy() {
    if (!activeEnemy) return;
    if (debugHideEnemies) return;
    // 受击击退：只挪绘制位置，世界坐标不动（尸体 / 判定都还在原地）
    const sx = activeEnemy.x - cameraX() + enemyKnockOffset(activeEnemy);
    if (sx < -180 || sx > W + 180) return;
    const shake = shakeOffset(activeEnemy.hurtT);

    // 精英光环
    if (activeEnemy.elite) {
      const rr = (activeEnemy.legend ? 240 : 120) * caveScale();
      const ag = ctx.createRadialGradient(sx, GROUND_Y - 70, 10, sx, GROUND_Y - 70, rr);
      ag.addColorStop(0, activeEnemy.legend ? 'rgba(255, 120, 220, .5)' : 'rgba(190, 110, 255, .38)');
      ag.addColorStop(1, activeEnemy.legend ? 'rgba(255, 120, 220, 0)' : 'rgba(190, 110, 255, 0)');
      ctx.fillStyle = ag;
      ctx.beginPath();
      ctx.arc(sx, GROUND_Y - 70, rr, 0, Math.PI * 2);
      ctx.fill();
    } else if (activeEnemy.tier === 'magic') {
      const rrM = 92 * caveScale();
      const mg = ctx.createRadialGradient(sx, GROUND_Y - 70, 6, sx, GROUND_Y - 70, rrM);
      mg.addColorStop(0, 'rgba(90, 170, 255, .32)');
      mg.addColorStop(1, 'rgba(90, 170, 255, 0)');
      ctx.fillStyle = mg;
      ctx.beginPath();
      ctx.arc(sx, GROUND_Y - 70, rrM, 0, Math.PI * 2);
      ctx.fill();
    }

    // 走路状态敌人左右巡逻；战斗时停下并面朝玩家（玩家在左侧）
    const walkBob = gameState === 'walk' ? Math.sin(activeEnemy.walkT * 4.5) * 1.5 : 0;
    const scale = enemyDrawScale(activeEnemy) * caveScale();
    ctx.fillStyle = 'rgba(58, 30, 8, .34)';
    ctx.beginPath();
    ctx.ellipse(sx - 12 * scale, GROUND_Y + 5, 56 * scale, 9 * scale, -0.06, 0, Math.PI * 2);
    ctx.fill();

  // 传奇 BOSS 走动时脚下扬尘
    if (activeEnemy.legend && gameState === 'walk') {
      for (let i = 0; i < 3; i++) {
        const dp = (activeEnemy.walkT * 0.5 + i * 0.33) % 1;
        const dx = sx - 70 * scale + dp * 150 * scale;
        const r = 14 + dp * 40;
        ctx.fillStyle = 'rgba(198, 158, 112, ' + (0.3 * (1 - dp)).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(dx, GROUND_Y + 6, r * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 世界BOSS：直接用专属立绘（单张图，靠呼吸起伏 / 缓慢摇摆显得"活"）
    const bossArt = activeEnemy.wbBossArt ? images[activeEnemy.wbBossArtKey] : null;
    if (bossArt && bossArt.complete && bossArt.naturalWidth > 0) {
      drawWorldBossArt(bossArt, sx + shake.x, GROUND_Y + shake.y, scale);
    } else {
      /* 临时副本里"其他玩家"用玩家立绘画（是实体，不是半透明虚影）；
       * 其它敌人照旧用敌人素材。 */
      if (activeEnemy.playerModel && images.player_idle_1) {
        let pFrame;
        if (activeEnemy.pvpFoe) {
          /* 交锋中的对手：平时是"休息"姿态，只有出手（攻击 / 法术 / 防御）时才播攻击动作 */
          if (pvpFoeAtkT > 0) {
            const p2 = 1 - pvpFoeAtkT / PLAYER_ATK_DUR;
            const idxA = Math.min(PLAYER_ATK_FRAMES - 1, Math.floor(p2 * PLAYER_ATK_FRAMES));
            pFrame = images['player_atk_' + (idxA + 1)] || images.player_idle_1;
            activeEnemy.curFrameKey = 'player_atk_' + (idxA + 1);
          } else {
            pFrame = playerRestFrame(activeEnemy.pvpRestPose) || images.player_idle_1;
            activeEnemy.curFrameKey = 'player_rest_' + ((activeEnemy.pvpRestPose || 0) + 1);
          }
        } else {
          const step = Math.floor(activeEnemy.walkT * 2.4) % 4;
          pFrame = images['player_idle_' + (step + 1)] || images.player_idle_1;
        }
        const pw = PLAYER_FRAME.w * scale, ph = PLAYER_FRAME.h * scale;
        const ptop = GROUND_Y + shake.y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * scale + walkBob * scale;
        const faceLeft = gameState === 'walk' ? activeEnemy.dir < 0 : true;   // 战斗中面朝左侧的玩家
        ctx.save();
        if (faceLeft) {
          ctx.translate(sx + shake.x, 0);
          ctx.scale(-1, 1);
          ctx.drawImage(pFrame, -pw / 2, ptop, pw, ph);
        } else {
          ctx.drawImage(pFrame, sx + shake.x - pw / 2, ptop, pw, ph);
        }
        ctx.restore();
      } else {
        // 新美术：按类型取帧（只有移动帧 → 赶路跑一轮 0.72s、战斗站定慢慢循环）
        const eDef = enemySpriteDef(activeEnemy);
        const spriteImg = enemyFrameImage(activeEnemy,
          gameState === 'walk' ? enemyWalkFps(eDef) : enemyStandFps(eDef));
        const faceRight = gameState === 'walk' ? activeEnemy.dir > 0 : false;
        if (!drawEnemySprite(spriteImg, sx + shake.x, GROUND_Y + shake.y, scale, faceRight, walkBob,
          enemySpriteDef(activeEnemy))) {
          // 兜底：新美术没加载出来时用旧的 Kenney 敌人，避免敌人"消失"
          const oldFrame = gameState === 'walk'
            ? (Math.floor(activeEnemy.walkT) % 2 === 0 ? images.enemyWalkA : images.enemyWalkB)
            : images.enemyIdle;
          drawCharacterCrop(oldFrame, sx + shake.x, GROUND_Y + shake.y, true, walkBob, scale);
        }
      }
    }
    drawHurtSlash(sx + shake.x, GROUND_Y + shake.y, activeEnemy.hurtT);
    drawEnemyStatus(sx);

    if (gameState === 'walk') {
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ff3f2c';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText('!', sx, GROUND_Y - 256 - Math.round((scale - 1) * 150));
    }
  }

  // 多对一：画出同场战斗里其余的敌人（带小血条）
  function drawBattleGroup() {
    if (battleGroup.length < 2) return;
    battleGroup.forEach(function (e) {
      if (e === activeEnemy || !aliveEnemy(e)) return;
      const sx = e.x - cameraX() + enemyKnockOffset(e);
      if (sx < -180 || sx > W + 180) return;
      const scale = enemyDrawScale(e) * 0.85 * caveScale();

      ctx.fillStyle = 'rgba(58, 30, 8, .3)';
      ctx.beginPath();
      ctx.ellipse(sx - 10 * scale, GROUND_Y + 5, 50 * scale, 8 * scale, 0, 0, Math.PI * 2);
      ctx.fill();

      // 同伴敌人也用它自己那套美术（面朝左 → 面对玩家/主目标）
      const helperImg = enemyFrameImage(e, enemyStandFps(enemySpriteDef(e)));
      if (!drawEnemySprite(helperImg, sx, GROUND_Y, scale, false, 0, enemySpriteDef(e))) {
        drawCharacterCrop(images.enemyIdle, sx, GROUND_Y, true, 0, scale);
      }

      // 小血条
      const w = 86;
      const h = 8;
      const bx = sx - w / 2;
      const by = GROUND_Y - 162 * scale - 26;
      const pct = clamp(e.hp / e.maxHp, 0, 1);
      ctx.fillStyle = 'rgba(16, 5, 1, .7)';
      roundRect(bx - 1, by - 1, w + 2, h + 2, 5);
      ctx.fill();
      ctx.fillStyle = 'rgba(84, 22, 8, .92)';
      roundRect(bx, by, w, h, 4);
      ctx.fill();
      if (pct > 0.02) {
        ctx.fillStyle = e.legend ? '#ff7fb8' : e.elite ? '#b479ff' : e.tier === 'magic' ? '#5fa8ff' : '#6fd08a';
        roundRect(bx, by, Math.max(4, Math.round(w * pct)), h, 4);
        ctx.fill();
      }
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(24, 6, 2, .9)';
      ctx.strokeText(enemyLabel(e, false) + ' · Lv.' + e.level, sx, by - 5);
      ctx.fillStyle = '#ffd9bd';
      ctx.fillText(enemyLabel(e, false) + ' · Lv.' + e.level, sx, by - 5);
    });

    // 围攻提示
    ctx.textAlign = 'center';
    ctx.font = 'bold 15px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(40, 4, 4, .92)';
    const label = '多对一：被 ' + battleAlive().length + ' 个敌人围攻';
    ctx.strokeText(label, W / 2, 96);
    ctx.fillStyle = '#ff9a8a';
    ctx.fillText(label, W / 2, 96);
  }

  // ---------- 掉落光柱：装备按品级发光（普通/魔法/稀有/传奇 → 白/蓝/金/粉）----------
  const DROP_PILLAR_TIER = { common: 1, magic: 2, rare: 3, legend: 4 };

  function hexToRgba(hex, alpha) {
    let h = String(hex || '').replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const n = parseInt(h || 'ffffff', 16);
    if (isNaN(n)) return 'rgba(255,255,255,' + alpha + ')';
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
  }

  // 这堆掉落里最好的那件装备（决定光柱颜色 / 高度）
  function corpseTopRarity() {
    if (!corpse || !corpse.drops) return null;
    let best = null;
    corpse.drops.forEach(function (d) {
      if (!d || d.type !== 'equipment') return;
      const tier = DROP_PILLAR_TIER[d.rarity] || 1;
      if (!best || tier > best.tier) {
        best = { tier: tier, color: d.rarityColor || '#d8d3c2', name: d.rarityName || '装备' };
      }
    });
    return best;
  }

  // 颜色混合（往白里靠，做亮芯）
  function mixHex(hex, target, k) {
    let h1 = String(hex || '').replace('#', '');
    let h2 = String(target || '').replace('#', '');
    if (h1.length === 3) h1 = h1[0] + h1[0] + h1[1] + h1[1] + h1[2] + h1[2];
    if (h2.length === 3) h2 = h2[0] + h2[0] + h2[1] + h2[1] + h2[2] + h2[2];
    const n1 = parseInt(h1, 16), n2 = parseInt(h2, 16);
    if (isNaN(n1) || isNaN(n2)) return hex;
    const mix = function (a, b) { return Math.round(a + (b - a) * k); };
    const r = mix((n1 >> 16) & 255, (n2 >> 16) & 255);
    const g2 = mix((n1 >> 8) & 255, (n2 >> 8) & 255);
    const b = mix(n1 & 255, n2 & 255);
    return '#' + ((1 << 24) + (r << 16) + (g2 << 8) + b).toString(16).slice(1);
  }

  // 光柱贴图：一次性预渲染（三层柱体 + 地面光池都带模糊），运行期只 drawImage。
  // 硬边梯形看着生硬，模糊过的体积光才像暗黑 4 那种"一束柔光"。
  const PILLAR_SPRITE = { w: 210, h: 440, ground: 410 };
  const pillarSpriteCache = {};
  const pillarDotCache = {};

  function pillarBeamHeight(tier) { return 150 + tier * 62; }

  function pillarSprite(color, tier) {
    const key = color + '|' + tier;
    if (pillarSpriteCache[key]) return pillarSpriteCache[key];
    const cv = document.createElement('canvas');
    cv.width = PILLAR_SPRITE.w;
    cv.height = PILLAR_SPRITE.h;
    const g = cv.getContext('2d');
    const cx = PILLAR_SPRITE.w / 2;
    const gy = PILLAR_SPRITE.ground;
    const t = clamp(tier || 1, 1, 4);
    const h = pillarBeamHeight(t);
    const half = 13 + t * 4.5;                 // 柱脚半宽
    const bright = mixHex(color, '#ffffff', 0.55);

    // ① 地面光池（柔和的椭圆光晕）
    const pool = g.createRadialGradient(cx, gy, 1, cx, gy, half * 3.2);
    pool.addColorStop(0, hexToRgba(color, 0.42));
    pool.addColorStop(0.45, hexToRgba(color, 0.14));
    pool.addColorStop(1, hexToRgba(color, 0));
    g.save();
    g.globalCompositeOperation = 'lighter';
    g.filter = 'blur(6px)';
    g.fillStyle = pool;
    g.beginPath();
    g.ellipse(cx, gy, half * 3.2, half * 1.15, 0, 0, Math.PI * 2);
    g.fill();
    g.restore();

    // ② 三层柱体：外柔光 → 中层 → 近白亮芯（越往里越窄越亮）
    const layers = [
      { blur: 18, widen: 1.95, alpha: 0.30, color: color, topCut: 0 },
      { blur: 8, widen: 1.15, alpha: 0.46, color: color, topCut: 0.07 },
      { blur: 3.5, widen: 0.5, alpha: 0.78, color: bright, topCut: 0.24 }
    ];
    layers.forEach(function (L) {
      const topY = gy - h * (1 - L.topCut);
      const grad = g.createLinearGradient(0, topY, 0, gy + 4);
      grad.addColorStop(0, hexToRgba(L.color, 0));
      grad.addColorStop(0.3, hexToRgba(L.color, L.alpha * 0.22));
      grad.addColorStop(0.7, hexToRgba(L.color, L.alpha * 0.62));
      grad.addColorStop(1, hexToRgba(L.color, L.alpha));
      const halfTop = Math.max(1.6, half * 0.16 * L.widen);
      const halfBot = half * L.widen;
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.filter = 'blur(' + L.blur + 'px)';
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(cx - halfTop, topY);
      g.bezierCurveTo(cx - halfTop * 1.7, topY + h * 0.34,
        cx - halfBot * 0.86, gy - h * 0.32, cx - halfBot, gy + 3);
      g.lineTo(cx + halfBot, gy + 3);
      g.bezierCurveTo(cx + halfBot * 0.86, gy - h * 0.32,
        cx + halfTop * 1.7, topY + h * 0.34, cx + halfTop, topY);
      g.closePath();
      g.fill();
      g.restore();
    });
    pillarSpriteCache[key] = cv;
    return cv;
  }

  // 上飘的光点：柔和小圆点（径向渐变，不是硬边圆）
  function pillarDot(color) {
    if (pillarDotCache[color]) return pillarDotCache[color];
    const s = 36;
    const cv = document.createElement('canvas');
    cv.width = s; cv.height = s;
    const g = cv.getContext('2d');
    const gr = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    gr.addColorStop(0, hexToRgba('#ffffff', 0.9));
    gr.addColorStop(0.32, hexToRgba(color, 0.6));
    gr.addColorStop(1, hexToRgba(color, 0));
    g.fillStyle = gr;
    g.fillRect(0, 0, s, s);
    pillarDotCache[color] = cv;
    return cv;
  }

  function drawDropPillar(sx, best) {
    const now = player.animT;
    const spr = pillarSprite(best.color, best.tier);
    const dot = pillarDot(best.color);
    // 呼吸要轻，别一闪一闪（暗黑 4 的光柱几乎是稳定的，只微微起伏）
    const pulse = 0.92 + Math.sin(now * 1.35) * 0.06 + Math.sin(now * 3.1) * 0.015;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = pulse;
    ctx.drawImage(spr, sx - PILLAR_SPRITE.w / 2, GROUND_Y + 12 - PILLAR_SPRITE.ground,
      PILLAR_SPRITE.w, PILLAR_SPRITE.h);
    // 上飘的光点：中段最亮，两端淡出
    const beamH = pillarBeamHeight(best.tier);
    for (let i = 0; i < 6; i++) {
      const t = (now * 0.3 + i / 6) % 1;
      const px = sx + Math.sin(now * 0.7 + i * 2.2) * (8 + best.tier * 3);
      const py = GROUND_Y + 2 - t * beamH;
      const size = 9 + (1 - t) * 9;
      ctx.globalAlpha = Math.sin(Math.PI * t) * 0.5;
      ctx.drawImage(dot, px - size / 2, py - size / 2, size, size);
    }
    ctx.restore();
  }

  function drawCorpse() {
    /* 战斗中不画上一场留下的尸体：它也是"人形"，和交战双方混在一起会显得场上有好几号人。
     *   （战斗里本来也点不到尸体，打完自然会重新出现。） */
    if (gameState === 'battle') return;
    if (!corpse) return;
    const sx = corpse.worldX - cameraX();
    if (sx < -180 || sx > W + 180) return;
    sceneActors.corpse = true;          // 调试/测试：这一帧确实画了旧尸体

    // 掉落物里有装备 → 按装备品级在尸体上打一根光柱（只有金币 / 材料就不打，走默认样式）
    const bestDrop = corpseTopRarity();
    if (bestDrop) drawDropPillar(sx, bestDrop);

    // 尸体影子
    ctx.fillStyle = 'rgba(34, 17, 6, .5)';
    ctx.beginPath();
    ctx.ellipse(sx + 2, GROUND_Y + 9, 76, 13, 0, 0, Math.PI * 2);
    ctx.fill();

    // 压扁成“倒地”的尸体轮廓（沿用敌人自己的那套美术）
    const corpseDef = enemySpriteDef({ sprite: corpse.sprite });
    const img = (function () {
      const im = images[corpseDef.key + '_1'];
      return im && im.complete && im.naturalWidth > 0 ? im : images.enemyIdle;
    })();
    ctx.save();
    ctx.translate(sx, GROUND_Y);
    ctx.scale(1.02, 0.34);
    ctx.globalAlpha = 0.88;
    if (img === images.enemyIdle) {
      ctx.drawImage(img, 42, 48, 176, 208, -74, -170, 148, 178);
    } else {
      // 新素材：整帧贴，锚点对齐（躺下时以锚点为中心稍微铺开）
      ctx.drawImage(img, -ENEMY_FRAME.w / 2, -(ENEMY_FRAME.h - ENEMY_FRAME.basePad) - 6,
        ENEMY_FRAME.w, ENEMY_FRAME.h);
    }
    ctx.restore();

    // 暗色血迹与尘土
    ctx.fillStyle = 'rgba(92, 12, 8, .24)';
    ctx.beginPath();
    ctx.ellipse(sx - 4, GROUND_Y + 2, 62, 16, 0, 0, Math.PI * 2);
    ctx.fill();

    if (!corpse.searched) {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
      ctx.strokeText('尸体 · 点击搜索', sx, GROUND_Y - 24);
      ctx.fillStyle = '#ffe9c0';
      ctx.fillText('尸体 · 点击搜索', sx, GROUND_Y - 24);
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillStyle = '#9fe2ff';
      ctx.fillText('?', sx - 126, GROUND_Y - 42);
      ctx.restore();
    }
  }

  // ---------- 遗迹洞府：路面入口 + 洞内环境 ----------
  // 入口美术：用户提供 Build1.png（已裁掉透明边、缩到 560×300，见 tools/make_cave_asset.cjs）
  const CAVE_IMG_RATIO = 560 / 388; // 兜底宽高比（素材没加载完时用）
  const CAVE_IMG_H = 240;           // 画到场景里的高度（宽度按素材实际比例算）

  // 宽高比直接读素材，换图不用再改代码
  function caveImgRatio() {
    const im = images.caveEntrance;
    if (im && im.complete && im.naturalWidth > 0) return im.naturalWidth / im.naturalHeight;
    return CAVE_IMG_RATIO;
  }

  function drawCaveEntrances() {
    if (cave) return;
    caveEntrances.forEach(function (c) {
      if (c.used) return;
      const sx = c.worldX - cameraX();
      const dh = CAVE_IMG_H;
      const dw = dh * caveImgRatio();
      if (sx < -dw || sx > W + dw) return;
      const def = c.def;
      const baseY = GROUND_Y + 10;   // 底座稍微压在路面下沿，像是"长"在路上
      const topY = baseY - dh;
      const near = Math.abs(sx - (player.worldX - cameraX())) < 320;
      const img = images.caveEntrance;

      ctx.save();

      // ① 地面投影
      ctx.fillStyle = 'rgba(30, 22, 16, .5)';
      ctx.beginPath();
      ctx.ellipse(sx - 6, baseY + 2, dw * 0.42, 16, 0, 0, Math.PI * 2);
      ctx.fill();

      // ② 阶级光晕（画在建筑后面）
      const glowY = baseY - dh * 0.46;
      const glowR = dw * 0.62;
      const glow = ctx.createRadialGradient(sx, glowY, 8, sx, glowY, glowR);
      glow.addColorStop(0, def.glow);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(sx, glowY, glowR, 0, Math.PI * 2);
      ctx.fill();

      if (img && img.complete && img.naturalWidth > 0) {
        // ③ 洞府建筑本体
        ctx.drawImage(img, sx - dw / 2, topY, dw, dh);
        // ④ 阶级色光：从洞口往外的一层淡色，绿/蓝/金三种洞府还是能一眼分开
        const tint = ctx.createRadialGradient(sx, baseY - dh * 0.4, 6, sx, baseY - dh * 0.4, dw * 0.4);
        tint.addColorStop(0, def.color);
        tint.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.26;
        ctx.fillStyle = tint;
        ctx.beginPath();
        ctx.arc(sx, baseY - dh * 0.4, dw * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
      } else {
        // 兜底：素材没加载出来时还是画原来的石洞口
        ctx.fillStyle = '#4a3a2c';
        ctx.beginPath();
        ctx.moveTo(sx - 120, baseY);
        ctx.quadraticCurveTo(sx - 110, baseY - 210, sx, baseY - 235);
        ctx.quadraticCurveTo(sx + 110, baseY - 210, sx + 120, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#120c08';
        ctx.beginPath();
        ctx.moveTo(sx - 78, baseY);
        ctx.quadraticCurveTo(sx - 70, baseY - 160, sx, baseY - 182);
        ctx.quadraticCurveTo(sx + 70, baseY - 160, sx + 78, baseY);
        ctx.closePath();
        ctx.fill();
      }

      // ⑤ 名牌
      ctx.textAlign = 'center';
      ctx.font = 'bold 16px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(20, 10, 2, .92)';
      const label = def.name + (def.token ? '（需令牌）' : '');
      ctx.strokeText(label, sx, topY - 26);
      ctx.fillStyle = def.color;
      ctx.fillText(label, sx, topY - 26);

      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      const sub = def.token
        ? (caveToken > 0 ? '点击进入 · 令牌 ' + caveToken + ' 枚' : '需要金色洞府令牌')
        : '点击进入 · 随时可离开';
      ctx.strokeText(sub, sx, topY - 8);
      ctx.fillStyle = 'rgba(255, 240, 214, .88)';
      ctx.fillText(sub, sx, topY - 8);

      if (near) {
        ctx.font = 'bold 30px Arial, sans-serif';
        ctx.fillStyle = def.color;
        ctx.fillText('▼', sx, topY - 44 + Math.sin(performance.now() / 260) * 4);
      }
      ctx.restore();
    });
  }

  /* ============================================================
   * 每日副本的"环境场景"（工业做法：**预渲染视差图层 + 程序化地面 + 多层流动雾**）
   *   目前实现的是迷雾副本（cave.def.scene === 'fog'）：
   *     · 整片空间弥漫厚重、流动的灰白大雾（多条雾带视差漂移 + 地平线浓雾 + 轻微脉动）
   *     · 远景树影分 3 层，越远越淡越模糊（预渲染到离屏画布，每帧只做平移 → 省性能）
   *     · 地面冷灰，散落杂草 / 残木 / 碎石（按世界坐标哈希 → 滚动时不闪、不重复）
   *     · 微弱冷环境光、无强光源：整体去饱和 + 暗角，神秘压抑、能见度低
   *   以后给别的副本做环境：在这里加一个 scene key，并在 defs.js 的副本条目里写 scene: '<key>'
   * ============================================================ */
  const FOG_TILE_W = 1600;          // 每个视差图层的贴片宽度（横向无缝平铺）
  const FOG_TREE_LAYERS = [         // 远 → 近：越远越淡、越模糊、视差越小
    { parallax: 0.22, alpha: 0.30, blur: 7, tone: '#8d9aa3', hMin: 0.42, hMax: 0.62, trees: 16 },
    { parallax: 0.45, alpha: 0.46, blur: 4, tone: '#5d6a74', hMin: 0.52, hMax: 0.78, trees: 13 },
    { parallax: 0.72, alpha: 0.62, blur: 1.6, tone: '#39434c', hMin: 0.62, hMax: 0.92, trees: 10 }
  ];
  const fogScene = { built: false, layers: [], bands: [], W: 0, H: 0 };
  // 确定性伪随机：同一个 n 永远同一个值（地面细节滚动时不闪）
  function fogHash(n) {
    let h = (Math.imul((n | 0) ^ 0x9e3779b9, 2654435761) >>> 0);
    h ^= h >>> 15; h = Math.imul(h, 2246822519) >>> 0;
    h ^= h >>> 13; h = Math.imul(h, 3266489917) >>> 0;
    h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  }
  function fogSceneActive() {
    return !!(cave && cave.def.invasion && cave.def.scene === 'fog');
  }
  /* 预渲染 3 层树影（离屏画布，横向可平铺）：主干 + 分叉 + 树冠轮廓，整体按层做模糊 */
  function fogBuildLayers() {
    fogScene.layers = FOG_TREE_LAYERS.map(function (cfg, li) {
      const cv = document.createElement('canvas');
      cv.width = FOG_TILE_W; cv.height = H;
      const g = cv.getContext('2d');
      if ('filter' in g) g.filter = 'blur(' + cfg.blur + 'px)';   // 描边柔化 = 雾里虚化
      g.fillStyle = cfg.tone;
      g.strokeStyle = cfg.tone;
      for (let i = 0; i < cfg.trees; i++) {
        const seed = (li + 1) * 7919 + i * 131;
        const x = (i + 0.15 + fogHash(seed) * 0.7) * (FOG_TILE_W / cfg.trees);
        const hh = (cfg.hMin + (cfg.hMax - cfg.hMin) * fogHash(seed + 1)) * (GROUND_Y - 60);
        const baseY = GROUND_Y - 4 + fogHash(seed + 2) * 10;
        const topY = baseY - hh;
        const w = 5 + fogHash(seed + 3) * 7;
        const lean = (fogHash(seed + 4) - 0.5) * 0.12;
        g.beginPath();
        g.moveTo(x - w / 2, baseY);
        g.lineTo(x + w / 2, baseY);
        g.lineTo(x + lean * hh + w * 0.22, topY);
        g.lineTo(x + lean * hh - w * 0.22, topY);
        g.closePath();
        g.fill();
        const br = 3 + Math.floor(fogHash(seed + 5) * 3);
        for (let b2 = 0; b2 < br; b2++) {
          const t = 0.42 + 0.5 * ((b2 + fogHash(seed + 6 + b2)) / br);
          const by = baseY - hh * t;
          const dir = (b2 % 2 ? 1 : -1);
          const len = hh * (0.13 + 0.16 * fogHash(seed + 11 + b2));
          g.lineWidth = Math.max(1.6, w * 0.32);
          g.beginPath();
          g.moveTo(x + lean * hh * t, by);
          g.lineTo(x + lean * hh * t + dir * len, by - len * 0.55);
          g.stroke();
        }
        const blobs = 3 + Math.floor(fogHash(seed + 17) * 3);
        for (let c2 = 0; c2 < blobs; c2++) {
          const cs = seed + 23 + c2;
          const cw = hh * (0.20 + 0.22 * fogHash(cs));
          const cx2 = x + lean * hh + (fogHash(cs + 1) - 0.5) * cw * 1.5;
          const cy2 = topY + hh * (0.06 + 0.30 * fogHash(cs + 2));
          g.beginPath();
          g.ellipse(cx2, cy2, cw * 0.55, cw * 0.42, fogHash(cs + 3) * Math.PI, 0, Math.PI * 2);
          g.fill();
        }
      }
      if ('filter' in g) g.filter = 'none';
      return { cfg: cfg, cv: cv };
    });
    // 流动雾带：每条的速度 / 视差 / 高度 / 大小都不同
    fogScene.bands = [0, 1, 2, 3, 4, 5].map(function (i) {
      return {
        y: GROUND_Y - 360 + i * 60 + fogHash(9 + i) * 26,
        r: 230 + fogHash(31 + i) * 280,
        speed: 10 + fogHash(51 + i) * 26,
        parallax: 0.08 + fogHash(71 + i) * 0.30,
        alpha: 0.10 + fogHash(91 + i) * 0.15,
        phase: fogHash(111 + i) * 1000
      };
    });
    fogScene.built = true;
  }
  // 流动雾带（back：地平线附近；front：挡在角色前面的那一层）
  function drawFogBands(alphaMul, speedMul) {
    fogScene.bands.forEach(function (b) {
      const t = weatherTime * b.speed * speedMul + cameraX() * b.parallax;
      const span = W + b.r * 2;
      const base = ((b.phase * 137 + t) % span) - b.r;
      const y = b.y + Math.sin(weatherTime * 0.16 + b.phase) * 15;
      [base, base - span, base + span].forEach(function (cx) {
        if (cx < -b.r * 1.3 || cx > W + b.r * 1.3) return;
        const g2 = ctx.createRadialGradient(cx, y, 0, cx, y, b.r);
        const a = Math.min(0.5, b.alpha * alphaMul);
        g2.addColorStop(0, 'rgba(236,241,244,' + a.toFixed(3) + ')');
        g2.addColorStop(0.55, 'rgba(228,234,238,' + (a * 0.5).toFixed(3) + ')');
        g2.addColorStop(1, 'rgba(228,234,238,0)');
        ctx.fillStyle = g2;
        ctx.beginPath();
        ctx.ellipse(cx, y, b.r, b.r * 0.24, 0, 0, Math.PI * 2);
        ctx.fill();
      });
    });
  }
  /* 地面：冷灰土 + 杂草 / 残木 / 碎石（世界坐标哈希，滚动稳定） */
  function drawFogGround(cam) {
    const g = ctx.createLinearGradient(0, GROUND_Y - 8, 0, H);
    g.addColorStop(0, '#5f676c');
    g.addColorStop(1, '#394046');
    ctx.fillStyle = g;
    ctx.fillRect(0, GROUND_Y - 2, W, H - GROUND_Y + 10);
    ctx.fillStyle = 'rgba(18,24,28,.35)';
    ctx.fillRect(0, GROUND_Y - 3, W, 4);

    const STEP = 46;
    const from = Math.floor((cam - 140) / STEP) * STEP;
    for (let wx = from; wx <= cam + W + 140; wx += STEP) {
      const sx = wx - cam;
      const h1 = fogHash(wx);
      if (h1 < 0.62) {                       // 杂草
        ctx.strokeStyle = 'rgba(118,130,116,' + (0.32 + fogHash(wx + 2) * 0.3).toFixed(2) + ')';
        ctx.lineWidth = 1.4;
        const n = 3 + Math.floor(fogHash(wx + 1) * 4);
        for (let i = 0; i < n; i++) {
          const bx = sx + (fogHash(wx + 3 + i) - 0.5) * 28;
          const bh = 8 + fogHash(wx + 9 + i) * 15;
          const bend = (fogHash(wx + 15 + i) - 0.5) * 11;
          ctx.beginPath();
          ctx.moveTo(bx, GROUND_Y + 3);
          ctx.quadraticCurveTo(bx + bend * 0.4, GROUND_Y + 3 - bh * 0.6, bx + bend, GROUND_Y + 3 - bh);
          ctx.stroke();
        }
      }
      if (h1 > 0.86) {                       // 残木
        const len = 34 + fogHash(wx + 21) * 48;
        ctx.save();
        ctx.translate(sx, GROUND_Y + 7 + fogHash(wx + 22) * 9);
        ctx.rotate((fogHash(wx + 23) - 0.5) * 0.5);
        ctx.fillStyle = 'rgba(58,48,42,.85)';
        roundRect(-len / 2, -4, len, 7, 3);
        ctx.fill();
        ctx.fillStyle = 'rgba(36,29,25,.85)';
        roundRect(-len / 2, -1, len, 3, 2);
        ctx.fill();
        if (fogHash(wx + 24) > 0.5) {
          ctx.fillStyle = 'rgba(52,44,38,.85)';
          ctx.beginPath();
          ctx.moveTo(len / 2 - 6, -4);
          ctx.lineTo(len / 2 + 8, -1);
          ctx.lineTo(len / 2 - 6, 3);
          ctx.closePath();
          ctx.fill();
        }
        ctx.restore();
      }
      if (fogHash(wx + 31) > 0.55) {          // 碎石
        const n = 1 + Math.floor(fogHash(wx + 32) * 3);
        for (let i = 0; i < n; i++) {
          const rx = sx + (fogHash(wx + 33 + i) - 0.5) * 36;
          const ry = GROUND_Y + 9 + fogHash(wx + 39 + i) * 10;
          const rr = 2 + fogHash(wx + 45 + i) * 3.4;
          ctx.fillStyle = 'rgba(96,104,108,.75)';
          ctx.beginPath();
          ctx.ellipse(rx, ry, rr, rr * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }
  /* 背景层：冷灰天幕 → 三层树影（视差）→ 地平线浓雾 → 地面 → 地面雾 */
  function drawFogSceneBack(cam) {
    if (!fogScene.built || fogScene.W !== W || fogScene.H !== H) {
      fogScene.W = W; fogScene.H = H;
      fogBuildLayers();
    }
    const sky = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    sky.addColorStop(0, '#39434d');
    sky.addColorStop(0.45, '#69747d');
    sky.addColorStop(1, '#9ba5ac');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, GROUND_Y + 6);

    fogScene.layers.forEach(function (L) {
      const p = L.cfg.parallax;
      const off = -((cam * p + weatherTime * (7 * p)) % FOG_TILE_W);
      ctx.save();
      ctx.globalAlpha = L.cfg.alpha;
      for (let x = off - FOG_TILE_W; x < W + FOG_TILE_W; x += FOG_TILE_W) {
        ctx.drawImage(L.cv, Math.round(x), 0);
      }
      ctx.restore();
    });

    drawFogGround(cam);

    // 地平线浓雾：把远景"吃掉"一半 —— 能见度不高
    const hz = ctx.createLinearGradient(0, GROUND_Y - 300, 0, GROUND_Y + 12);
    hz.addColorStop(0, 'rgba(226,232,236,0)');
    hz.addColorStop(0.5, 'rgba(228,234,238,.5)');
    hz.addColorStop(1, 'rgba(234,240,243,.8)');
    ctx.fillStyle = hz;
    ctx.fillRect(0, GROUND_Y - 300, W, 316);
    drawFogBands(1, 1);
  }
  /* 前景层：挡在角色前面的雾带 + 整体薄霾 + 暗角 + 冷色环境光 */
  function drawFogSceneFront() {
    if (!fogScene.built) return;
    drawFogBands(1.1, 1.35);
    ctx.fillStyle = 'rgba(224,231,236,.08)';
    ctx.fillRect(0, 0, W, H);
    const vg = ctx.createRadialGradient(W * 0.5, GROUND_Y - 150, H * 0.26, W * 0.5, GROUND_Y - 150, H * 0.92);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(8,14,20,.5)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(146,174,196,.06)';   // 冷环境光（没有强光源）
    ctx.fillRect(0, 0, W, H);
  }
  function drawInvasionSceneBack(cam) {
    if (fogSceneActive()) { drawFogSceneBack(cam); return; }
    if (abyssSceneActive()) { drawAbyssSceneBack(cam); return; }
  }
  function drawInvasionSceneFront() {
    if (fogSceneActive()) { drawFogSceneFront(); return; }
    if (abyssSceneActive()) { drawAbyssSceneFront(); return; }
  }

  /* ============================================================
   * 深渊副本的环境场景（同一套"预渲染视差 + 程序化细节"的工业做法）
   *   · 深邃漆黑的地下虚空：整体近黑，只有零星蓝色荧光水晶自发光
   *   · 大量悬空破碎岩石（3 层视差：越远越小越模糊越淡）+ 顶部钟乳岩
   *   · 底部是"虚无"：没有实地面，往下直接化进黑暗
   *   · 冷蓝微光只从水晶渗出，照亮身边一圈，其余全黑 → 压抑幽深
   * ============================================================ */
  const ABYSS_TILE_W = 1600;
  const ABYSS_CRYSTAL = { line: '120,205,255', body: '96,186,240', core: '232,250,255' };
  const ABYSS_ROCK_LAYERS = [   // 远 → 近
    { parallax: 0.18, alpha: 0.24, blur: 8, tone: '#141a22', count: 9, size: [0.09, 0.20], yFrom: 0.34, yTo: 0.66 },
    { parallax: 0.42, alpha: 0.46, blur: 4, tone: '#0d1117', count: 8, size: [0.13, 0.28], yFrom: 0.20, yTo: 0.72 },
    { parallax: 0.70, alpha: 0.78, blur: 1.6, tone: '#05070b', count: 7, size: [0.17, 0.36], yFrom: 0.12, yTo: 0.78 }
  ];
  const abyssScene = { built: false, layers: [], crystals: [], motes: [], W: 0, H: 0 };
  function abyssSceneActive() {
    return !!(cave && cave.def.invasion && cave.def.scene === 'abyss');
  }
  // 一块"碎岩"的不规则轮廓：6~9 个顶点的多边形 + 少量棱面
  function abyssRockPath(g, cx, cy, r, seed) {
    const n = 6 + Math.floor(fogHash(seed) * 4);
    g.beginPath();
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (0.62 + fogHash(seed + i + 1) * 0.55);
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr * 0.78;
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.closePath();
  }
  /* 预渲染 3 层岩石（悬空碎岩 + 顶部钟乳岩），横向可无缝平铺 */
  function abyssBuildLayers() {
    abyssScene.layers = ABYSS_ROCK_LAYERS.map(function (cfg, li) {
      const cv = document.createElement('canvas');
      cv.width = ABYSS_TILE_W; cv.height = H;
      const g = cv.getContext('2d');
      if ('filter' in g) g.filter = 'blur(' + cfg.blur + 'px)';
      // ① 悬空碎岩
      for (let i = 0; i < cfg.count; i++) {
        const seed = (li + 1) * 6151 + i * 173;
        const x = (i + 0.15 + fogHash(seed) * 0.7) * (ABYSS_TILE_W / cfg.count);
        const y = (cfg.yFrom + (cfg.yTo - cfg.yFrom) * fogHash(seed + 1)) * GROUND_Y;
        const r = (cfg.size[0] + (cfg.size[1] - cfg.size[0]) * fogHash(seed + 2)) * (GROUND_Y - 120);
        g.fillStyle = cfg.tone;
        abyssRockPath(g, x, y, r, seed + 3);
        g.fill();
        // 棱面：从光来的方向（水晶在下）蹭一点冷灰，让碎岩有体积
        g.save();
        abyssRockPath(g, x, y, r * 0.99, seed + 3);
        g.clip();
        g.fillStyle = 'rgba(120,170,205,' + (0.05 + fogHash(seed + 9) * 0.06).toFixed(3) + ')';
        g.beginPath();
        g.ellipse(x - r * 0.15, y + r * 0.42, r * 0.7, r * 0.3, 0.25, 0, Math.PI * 2);
        g.fill();
        g.restore();
      }
      // ② 顶部钟乳岩（倒挂的石钟乳）：近层更长更密
      const stN = Math.round(cfg.count * 1.6) + li * 3;
      g.fillStyle = cfg.tone;
      for (let i = 0; i < stN; i++) {
        const seed = (li + 5) * 3499 + i * 211;
        const x = (i + 0.1 + fogHash(seed) * 0.85) * (ABYSS_TILE_W / stN);
        const w = 12 + fogHash(seed + 1) * 26 + li * 6;
        const L = 60 + fogHash(seed + 2) * 150 + li * 40;
        const tipX = x + (fogHash(seed + 3) - 0.5) * 26;
        g.beginPath();
        g.moveTo(x - w / 2, -6);
        g.lineTo(x + w / 2, -6);
        g.lineTo(tipX + w * 0.12, L * 0.62);
        g.lineTo(tipX, L);
        g.lineTo(tipX - w * 0.14, L * 0.6);
        g.closePath();
        g.fill();
      }
      if ('filter' in g) g.filter = 'none';
      return { cfg: cfg, cv: cv };
    });
    /* ③ 水晶簇：贴地分布（世界坐标 → 平铺），每簇 2~4 根晶柱 */
    abyssScene.crystals = [];
    for (let i = 0; i < 9; i++) {
      const seed = 5100 + i * 97;
      const n = 2 + Math.floor(fogHash(seed + 2) * 3);
      const shards = [];
      for (let k = 0; k < n; k++) {
        const s2 = seed + 10 + k * 7;
        shards.push({
          dx: (fogHash(s2) - 0.5) * 30,
          h: 16 + fogHash(s2 + 1) * 36,
          w: 5 + fogHash(s2 + 2) * 9,
          tilt: (fogHash(s2 + 3) - 0.5) * 0.5
        });
      }
      abyssScene.crystals.push({
        x: (i + 0.2 + fogHash(seed) * 0.6) * (ABYSS_TILE_W / 9),
        y: GROUND_Y - 8 - fogHash(seed + 1) * 26,
        shards: shards,
        phase: fogHash(seed + 4) * 6.28,
        pulse: 0.55 + fogHash(seed + 5) * 0.7,
        parallax: 0.42
      });
    }
    /* ④ 悬浮微粒（很淡的冷光尘埃，慢慢往上飘） */
    abyssScene.motes = [];
    for (let i = 0; i < 48; i++) {
      const seed = 7300 + i * 53;
      abyssScene.motes.push({
        x: fogHash(seed) * ABYSS_TILE_W,
        y: GROUND_Y - 40 - fogHash(seed + 1) * 430,
        r: 0.8 + fogHash(seed + 2) * 1.7,
        vy: 3 + fogHash(seed + 3) * 9,
        parallax: 0.22 + fogHash(seed + 4) * 0.55,
        phase: fogHash(seed + 5) * 6.28,
        alpha: 0.10 + fogHash(seed + 6) * 0.22
      });
    }
    abyssScene.built = true;
  }
  /* 水晶自发光（画在角色之后的地面附近）：脉动光晕 + 晶柱 + 冷色地面反光 */
  function drawAbyssCrystals(cam) {
    const now = weatherTime;
    abyssScene.crystals.forEach(function (c) {
      const span = ABYSS_TILE_W;
      const base = -((cam * c.parallax) % span);
      for (let rep = -1; rep <= 1; rep++) {
        const sx = c.x + base + rep * span;
        if (sx < -220 || sx > W + 220) continue;
        const pulse = 0.72 + 0.28 * Math.sin(now * c.pulse + c.phase);
        const R = 158 * pulse;
        const g1 = ctx.createRadialGradient(sx, c.y, 0, sx, c.y, R);
        g1.addColorStop(0, 'rgba(' + ABYSS_CRYSTAL.line + ',' + (0.34 * pulse).toFixed(3) + ')');
        g1.addColorStop(0.45, 'rgba(' + ABYSS_CRYSTAL.line + ',' + (0.11 * pulse).toFixed(3) + ')');
        g1.addColorStop(1, 'rgba(' + ABYSS_CRYSTAL.line + ',0)');
        ctx.fillStyle = g1;
        ctx.beginPath();
        ctx.ellipse(sx, c.y - 10, R, R * 0.42, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.save();
        ctx.globalCompositeOperation = 'lighter';
        c.shards.forEach(function (s) {
          ctx.save();
          ctx.translate(sx + s.dx, c.y);
          ctx.rotate(s.tilt);
          ctx.fillStyle = 'rgba(' + ABYSS_CRYSTAL.body + ',' + (0.5 * pulse).toFixed(3) + ')';
          ctx.beginPath();
          ctx.moveTo(0, 1);
          ctx.lineTo(s.w / 2, -s.h * 0.82);
          ctx.lineTo(0, -s.h * 1.14);
          ctx.lineTo(-s.w / 2, -s.h * 0.82);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = 'rgba(' + ABYSS_CRYSTAL.core + ',' + (0.45 * pulse).toFixed(3) + ')';
          ctx.fillRect(-0.9, -s.h * 0.86, 1.8, s.h * 0.74);
          ctx.restore();
        });
        ctx.restore();
      }
    });
  }
  function drawAbyssMotes(cam) {
    const now = weatherTime;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    abyssScene.motes.forEach(function (m) {
      const span = ABYSS_TILE_W;
      const x0 = ((m.x - cam * m.parallax) % span + span) % span;
      for (let rep = -1; rep <= 1; rep++) {
        const sx = x0 + rep * span;
        if (sx < -20 || sx > W + 20) continue;
        const dy = ((now * m.vy + m.phase * 40) % 460);
        const sy = m.y - dy;
        if (sy < -10 || sy > GROUND_Y + 10) continue;
        const a = m.alpha * (0.55 + 0.45 * Math.sin(now * 0.9 + m.phase));
        ctx.fillStyle = 'rgba(' + ABYSS_CRYSTAL.line + ',' + a.toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(sx, sy, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.restore();
  }
  /* 背景层：虚空底 → 冷雾 → 3 层岩石/钟乳岩 → 虚无的地面 → 水晶 → 微粒 */
  function drawAbyssSceneBack(cam) {
    if (!abyssScene.built || abyssScene.W !== W || abyssScene.H !== H) {
      abyssScene.W = W; abyssScene.H = H;
      abyssBuildLayers();
    }
    // ① 虚空：几乎全黑，只有地平线一层很淡的冷调
    const void0 = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    void0.addColorStop(0, '#04060a');
    void0.addColorStop(0.6, '#080c12');
    void0.addColorStop(1, '#10161e');
    ctx.fillStyle = void0;
    ctx.fillRect(0, 0, W, GROUND_Y + 6);
    const hz = ctx.createLinearGradient(0, GROUND_Y - 260, 0, GROUND_Y + 8);
    hz.addColorStop(0, 'rgba(60,110,150,0)');
    hz.addColorStop(0.7, 'rgba(58,104,146,.10)');
    hz.addColorStop(1, 'rgba(70,120,165,.16)');
    ctx.fillStyle = hz;
    ctx.fillRect(0, GROUND_Y - 260, W, 270);
    // ② 三层悬空岩石 / 钟乳岩（视差）
    abyssScene.layers.forEach(function (L) {
      const p = L.cfg.parallax;
      const off = -((cam * p + weatherTime * (3.5 * p)) % ABYSS_TILE_W);
      ctx.save();
      ctx.globalAlpha = L.cfg.alpha;
      for (let x = off - ABYSS_TILE_W; x < W + ABYSS_TILE_W; x += ABYSS_TILE_W) {
        ctx.drawImage(L.cv, Math.round(x), 0);
      }
      ctx.restore();
    });
    // ③ 地面：没有实地面 —— 往下化进黑（虚无的深渊底部）
    const g2 = ctx.createLinearGradient(0, GROUND_Y - 8, 0, H);
    g2.addColorStop(0, 'rgba(18,26,34,1)');
    g2.addColorStop(0.35, 'rgba(7,11,16,1)');
    g2.addColorStop(1, 'rgba(0,1,4,1)');
    ctx.fillStyle = g2;
    ctx.fillRect(0, GROUND_Y - 4, W, H - GROUND_Y + 8);
    ctx.fillStyle = 'rgba(96,168,215,.10)';   // 地平线一丝冷光（晶石的余光，不是光源）
    ctx.fillRect(0, GROUND_Y - 5, W, 3);
    // ④ 水晶自发光 + 悬浮微粒
    drawAbyssCrystals(cam);
    drawAbyssMotes(cam);
  }
  /* 前景层：漂到镜头前的碎岩（只放两侧，别挡角色）+ 重暗角 + 冷调 */
  function drawAbyssSceneFront() {
    if (!abyssScene.built) return;
    const now = weatherTime;
    for (let i = 0; i < 3; i++) {
      const seed = 9100 + i * 131;
      const side = i % 2 === 0 ? -1 : 1;      // 左 / 右
      const baseX = side < 0 ? W * (0.06 + fogHash(seed) * 0.12) : W * (0.82 + fogHash(seed) * 0.12);
      const x = baseX + Math.sin(now * 0.22 + fogHash(seed + 1) * 6) * 12;
      const y = (0.30 + fogHash(seed + 2) * 0.5) * GROUND_Y + Math.sin(now * 0.16 + fogHash(seed + 3) * 6) * 10;
      const r = 34 + fogHash(seed + 4) * 40;
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.fillStyle = '#03050a';
      abyssRockPath(ctx, x, y, r, seed + 5);
      ctx.fill();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = 'rgba(' + ABYSS_CRYSTAL.line + ',1)';
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.5, r * 0.6, r * 0.16, 0.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    const vg = ctx.createRadialGradient(W * 0.5, GROUND_Y - 170, H * 0.20, W * 0.5, GROUND_Y - 170, H * 1.02);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,2,.82)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = 'rgba(70,120,170,.07)';   // 冷环境光（全靠晶石反光）
    ctx.fillRect(0, 0, W, H);
  }

  // 洞府内：压暗画面 + 岩壁剪影，营造洞窟氛围（每日副本只保留顶层岩壁）
  function drawCaveInterior() {
    if (!cave) return;
    const def = cave.def;
    /* 每日轮换副本（入侵图）：**不压暗**（背景保持和冒险道路一致），
     *   但顶层的岩层剪影照旧保留；洞府那层颜色光带先省掉 ——
     *   以后给每种副本单独做环境时，在这里按 def.id 分支加自己的场景层即可。 */
    const invasion = !!def.invasion;
    ctx.save();
    if (!invasion) {
      // ① 整屏暗色遮罩（只有洞府用）
      ctx.fillStyle = 'rgba(8, 6, 10, .62)';
      ctx.fillRect(-20, -20, W + 40, H + 40);
    }

    // ② 顶层岩层剪影（副本 / 洞府都保留）
    ctx.fillStyle = 'rgba(10, 8, 14, .85)';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(W, 0);
    ctx.lineTo(W, 150);
    for (let x = W; x >= 0; x -= 60) {
      ctx.lineTo(x, 110 + Math.sin(x / 90) * 42 + Math.sin(x / 31) * 12);
    }
    ctx.closePath();
    ctx.fill();

    if (!invasion) {
      // ③ 洞府颜色光带（只有洞府用）
      const g = ctx.createLinearGradient(0, GROUND_Y, 0, GROUND_Y - 420);
      g.addColorStop(0, def.glow);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.globalAlpha = 0.25;
      ctx.fillStyle = g;
      ctx.fillRect(0, GROUND_Y - 420, W, 420);
    }
    ctx.restore();
  }

  // 首次游戏的宝箱
  function drawChest() {
    if (!chest) return;
    const sx = chest.worldX - cameraX();
    if (sx < -200 || sx > W + 200) return;
    const baseY = GROUND_Y + 6;
    const hasLoot = !chest.weaponTaken || !chest.gemTaken;

    ctx.fillStyle = 'rgba(40, 20, 6, .42)';
    ctx.beginPath();
    ctx.ellipse(sx - 8, baseY + 4, 74, 12, 0, 0, Math.PI * 2);
    ctx.fill();

    // 箱体
    ctx.save();
    ctx.fillStyle = '#8a5626';
    roundRect(sx - 58, baseY - 52, 116, 54, 8);
    ctx.fill();
    ctx.fillStyle = '#6d3f19';
    roundRect(sx - 58, baseY - 22, 116, 24, 7);
    ctx.fill();

    // 金属包边
    ctx.fillStyle = '#e0b25e';
    ctx.fillRect(sx - 44, baseY - 52, 9, 54);
    ctx.fillRect(sx - 4, baseY - 52, 9, 54);
    ctx.fillRect(sx + 35, baseY - 52, 9, 54);
    ctx.fillStyle = '#b98a3e';
    ctx.fillRect(sx - 58, baseY - 6, 116, 7);

    // 箱盖（打开时向后翻起）
    ctx.translate(sx, baseY - 52);
    if (chest.opened) ctx.rotate(-0.62);
    ctx.fillStyle = '#9a6330';
    roundRect(-58, -26, 116, 30, 8);
    ctx.fill();
    ctx.fillStyle = '#e0b25e';
    ctx.fillRect(-44, -26, 9, 30);
    ctx.fillRect(-4, -26, 9, 30);
    ctx.fillRect(35, -26, 9, 30);
    ctx.restore();

    // 锁扣
    ctx.fillStyle = '#ffd98a';
    roundRect(sx - 9, baseY - 30, 18, 22, 5);
    ctx.fill();

    // 未取完时闪烁的光点
    if (hasLoot) {
      for (let i = 0; i < 4; i++) {
        const a = 0.35 + 0.4 * Math.sin(weatherTime * 3 + i * 1.7);
        const gx = sx + Math.sin(weatherTime * 1.4 + i * 2.1) * 44;
        const gy = baseY - 40 - i * 16 + Math.cos(weatherTime * 2 + i) * 6;
        ctx.globalAlpha = Math.max(0.1, a);
        ctx.fillStyle = '#fff0b8';
        ctx.beginPath();
        ctx.arc(gx, gy, 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
    ctx.strokeText(hasLoot ? '宝箱 · 点击打开' : '空宝箱', sx, baseY - 86);
    ctx.fillStyle = hasLoot ? '#ffe9c0' : '#c9b391';
    ctx.fillText(hasLoot ? '宝箱 · 点击打开' : '空宝箱', sx, baseY - 86);
    ctx.restore();
  }

  // ---------- 主循环 ----------
  function draw() {
    ctx.clearRect(0, 0, W, H);
    // 世界BOSS战场：整套换成血沙场景（BOSS + 其他玩家虚影）
    if (worldBossRun) {
      drawWorldBossScene();
      return;
    }
    // intro：首页也在路上（镜头跟着角色），这样角色是"站在路边休息"而不是被推到屏幕外
    const cam = (gameState === 'walk' || gameState === 'battle' || gameState === 'dead' ||
      gameState === 'intro') ? cameraX() : 0;
    // 传奇BOSS在场时地面轻微震动（走动持续、落步/出手时更明显）
    const quake = quakeOffset();
    if (quake.x || quake.y) {
      ctx.save();
      ctx.translate(quake.x, quake.y);
    }
    drawBackground(cam);
    drawSkyFlight();          // 练气期后：远处天空偶尔划过的飞虹
    drawSkyPuffs();           // 两条丝线相触时炸开的烟尘
    sceneActors.mirage = false; sceneActors.corpse = false; sceneActors.peers = 0;   // 本帧重置
    /* 每日副本有自己的环境（迷雾 / 深渊）：沙漠的沙暴 / 龙卷 / 沙尘就不画了 */
    const dungeonSceneOn = fogSceneActive() || abyssSceneActive();
    if (!dungeonSceneOn) {
      drawSandstorm();
      drawTornado(cam);
    }
    drawDecor(cam);
    if (!dungeonSceneOn) {
      drawMilestones(cam);
      drawWeatherBack(cam);
    }
    drawCans();
    drawChest();
    drawCaveEntrances();
    drawSectScene();           // 门派山门：路边的建筑（先用洞府美术）
    drawInvasionSceneBack(cam);   // 每日副本环境：整屏场景层（铺在角色之后）
    if (images.player_idle_1) {
      drawCorpse();
      drawEnemy();
      drawBattleGroup();
      drawBloodWavesGround();
      drawShockWavesGround();
      drawDefendFxBehind();   // 防御护盾：铺在角色后面（护盾裹住角色）
      drawXiuLianGround();    // 天道·静修：环境压暗 / 法阵 / 向内收的涟漪（画在角色之后层）
      drawSectPeers();        // 门派山门：NPC 弟子 + 同门派玩家
      drawPlayer();
      drawRunDust();          // 赶路扬尘：跟在脚后（画在角色之后，免得被脚下的影子盖住）
      drawXiuLianFront();     // 天道·静修：光柱 / 被吸纳的灵气微粒 / 吸收脉冲（画在角色之前层）
      drawLingFx();           // 灵脉仙人掌：明暗闪烁 + 头顶编号 + 被抽向角色的灵气
      drawSkyOmen();          // 破境天地异象（压暗天光 + 雷光 + 灵气旋涡 + 横幅）
      drawDirtyOnPlayer();
      drawMiningFx();         // 门派矿山：被抛起的矿石图标
      drawDefendFxFront();    // 角色前面再罩一层薄的：看起来被包住，又不遮住角色
      drawBloodWavesAura();
      drawBloodBolts();       // 气血反弹：血气冲击波
      drawPet();
      drawAlly();
      drawMirage();
      drawLingPeers();        // 灵脉房间里守着的真人（含"从漩涡里掉下来"的出场）
      drawLingTeleportFx();   // 灵脉定位：头顶漩涡 / 落地漩涡
    }
    if (!dungeonSceneOn) drawWeatherFront(cam);
    drawInvasionSceneFront();      // 每日副本环境：挡在角色前面的雾 / 霾 / 暗角
    if (!(cave && cave.def.activity)) drawCaveInterior();   // 临时副本有自己的场地
    drawActivityScene(cam);         // 临时公共副本：本层地面 + 物品 + 其他玩家 + 怪 + 传送门
    drawCaveEnterFx();
    // 洞府增幅特效：要画在「洞府内部暗色滤镜」之后，否则会被那层滤镜压得看不见
    drawCaveBuffGroundFx();   // 脚下的火焰 / 寒冰光环
    drawCaveBuffOverFx();     // 敌人手上的火焰
      drawDirtyDrops();
      drawAirSlashFx();
      drawRockFx();
      drawSlashFx();
    drawYangClawFx();
    drawPowerArrowFx();        // 强力射击：箭矢飞行 + 命中爆炸
    drawLavaRainFx();          // 独特「武者的刑具」：熔岩骤雨
    drawPowerTimingBar();      // 强力射击：完美时机进度条（战斗场景居中）
    drawDamagePopups();
    if (quake.x || quake.y) ctx.restore();
  }

  // ---------- 主循环：把 dt 秒的世界推进走一遍（不含绘制）----------
  // 单独抽出来是为了"网页在后台也要继续跑"：前台由 requestAnimationFrame 驱动，
  // 后台 rAF 会被浏览器停掉，改由定时器 + 真实时间差继续推进同一套逻辑。
  function updateWorld(dt) {
    if (gameState === 'walk' || gameState === 'battle') battleLogClock += dt;
    updateCg(dt);                // 开场 CG：幻灯片推进（在进入场景之前）
    // 玩家角色动作计时：待机呼吸一直在走，出手动作只在点击后播一次
    player.animT += dt;
    if (playerAtkT > 0) playerAtkT = Math.max(0, playerAtkT - dt);
    updateRestPose(dt);
    if (gameState === 'walk') update(dt);
    updateWeather(dt);
    updateSkyFlight(dt);        // 练气期后：远处天空偶尔划过的高阶修仙者
    updateLoading(dt);
    updateSlashFx(dt);
    updateDefendFx(dt);
    updateAirSlashFx(dt);
    updateRockFx(dt);
    updateCostumeCharge();      // 世界BOSS 时装充能：清掉过期没用掉的充能
    updateRecoup(dt);
    updatePowerArrowFx(dt);
    updatePowerShot(dt);
    updateLavaRainFx(dt);       // 独特「武者的刑具」：火雨表现层
    updateTurnTimer(dt);
    updatePlayerIdleTimer(dt);
    updateAutoAdventure(dt);
    updateCans(dt);
    updateCaveSpawn();
    updateCaveTransition(dt);
    updateMirage(dt);
    updateDirty(dt);
    updateRunDust(dt);
    updateMiningFx(dt);       // 门派矿山：挖矿 + 矿石抛物线入包
    updateOreProcess(dt);     // 矿石鉴定窗口：三步加工动画推进
    updateOnline(dt);
    updateBloodWaves(dt);
    updateBloodBolts(dt);     // 气血反弹：血气冲击波飞向敌人
    updateShockWaves(dt);
    updateYangClawFx(dt);
    updateEnemyQuake(dt);
    applyLifeRegen(dt);
    updateRestHeal(dt);
    updateDamageFx(dt);
    updatePeriodicSave(dt);   // 定期兜底存档（金币等随时变动的数值也一定会落盘）
    // 世界BOSS：刷新 / 消失循环 + 战场推进（虚影、倒计时、参与奖励）
    updateWorldBossCycle();
    updateWorldBossRun(dt);
    updateWorldBossLoot(dt);    // 击杀后：战场中央宝箱的倒计时 + 走过去开箱
    updateCaveEnemyBuffs(dt);   // 洞府敌人「烈焰光环」：每秒灼烧 + 其它增幅状态
    updateWbEnv(dt);
    updateWorldBossPanelClock(dt);
    updateDungeonClock(dt);
    updateActivityClock(dt);        // 活动窗口里的倒计时
    updateActivityRunClock();       // 临时副本到点自动传出来
    updateActivityNet(dt);          // 临时副本里"真人"的位置同步（10Hz 上报 + 插值）
    updateSectNet(dt);              // 门派山门：同门玩家位置同步（10Hz 上报 + 插值）
    updateLingTeleport(dt);         // 灵脉定位：传送演出
    updateLingPresence();           // 站在自己灵脉旁修炼 → 进/退灵脉房间
    updateLingNet(dt);              // 灵脉房间的真人位置同步
    updatePvpClocks(dt);            // 真人交锋：锁定倒计时 / 回合倒计时
    // 战斗中定时校正「战斗日志」按钮位置（战斗信息面板会随文案变高变矮 + 入场动画）
    if (gameState === 'battle' && ui.btnLog && !ui.btnLog.classList.contains('hidden')) {
      logBtnClock += dt;
      if (logBtnClock >= 0.3) {
        logBtnClock = 0;
        layoutBattleLogBtn();
      }
    }
    // HUD 布局定期校正（进游戏后按钮才可见，尺寸变化也要跟着重排）
    hudLayoutClock += dt;
    if (hudLayoutClock >= 1) {
      hudLayoutClock = 0;
      layoutHud();
    }
    // 生命归零 → 阵亡（回到 0km 重新出发）
    // 世界BOSS战场例外：那里在 updateWorldBossRun 里就把玩家踢出战场了（见上），不走普通阵亡
    if (!deathActive && combat.hp <= 0 && gameState === 'battle' && !worldBossRun) triggerDeath();
  }

  const MAX_WORLD_STEP = 0.1;    // 单步最多推进 0.1 秒（保证物理/动画不跳帧）
  const MAX_WORLD_CATCHUP = 120; // 一次最多补 120 秒（防止断电式长时间挂后台后卡死）
  let worldClock = { last: performance.now(), catchupSeconds: 0, bgTicks: 0 };

  // 按真实秒数推进世界：自动拆成若干小步，后台补帧也走这里
  function advanceWorld(seconds) {
    let remain = Math.min(Math.max(0, seconds), MAX_WORLD_CATCHUP);
    if (seconds > MAX_WORLD_CATCHUP) worldClock.catchupSeconds += seconds - MAX_WORLD_CATCHUP;
    while (remain > 0.0001) {
      const step = Math.min(MAX_WORLD_STEP, remain);
      updateWorld(step);
      remain -= step;
    }
  }

  function frame(now) {
    requestAnimationFrame(frame);
    const dt = Math.max(0, (now - lastTime) / 1000);
    lastTime = now;
    advanceWorld(dt);
    draw();
    drawCgFrame();      // 开场 CG 有自己的画布，画在主画布之上（不参与游戏绘制）
  }

  // ---------- 后台继续运行 ----------
  // 切到别的标签页 / 最小化窗口时，浏览器会把 requestAnimationFrame 停掉（甚至会节流定时器）。
  // 这里用定时器按"真实时间差"继续推进世界，所以回到前台时进度不会凭空丢失。
  let bgTicker = null;
  function backgroundTick() {
    const now = performance.now();
    const dt = Math.max(0, (now - lastTime) / 1000);
    lastTime = now;
    worldClock.bgTicks += 1;
    if (dt > 0) advanceWorld(dt);
  }

  function syncPageVisibility() {
    if (document.hidden) {
      if (!bgTicker) bgTicker = setInterval(backgroundTick, 250);
    } else {
      if (bgTicker) {
        clearInterval(bgTicker);
        bgTicker = null;
      }
      // 回到前台：先把后台落下的时间补上，避免"切回来才发现停了几分钟"
      backgroundTick();
      lastTime = performance.now();
    }
  }
  document.addEventListener('visibilitychange', syncPageVisibility);
  window.addEventListener('pagehide', syncPageVisibility);
  window.addEventListener('pageshow', syncPageVisibility);
  window.addEventListener('focus', syncPageVisibility);

  // ---------- 按钮事件 ----------
  ui.btnLogin.addEventListener('click', doLogin);
  // 删档要走确认弹窗
  if (ui.btnWipe) ui.btnWipe.addEventListener('click', askWipeSaves);
  if (ui.wipeConfirm) ui.wipeConfirm.addEventListener('click', doWipeSaves);
  if (ui.wipeCancel) ui.wipeCancel.addEventListener('click', closeWipeConfirm);
  // 首页引路人对白：点它（或点「上前对话」）就打开账号窗口
  if (ui.introDialog) {
    ui.introDialog.addEventListener('click', function (e) {
      openLoginFromIntro();
      // 点开登录窗口的就是这一下，别让它冒泡到外层"点空白收起登录窗口"的监听
      e.stopPropagation();
    });
  }
  // 第一个任务：接受后关闭
  if (ui.questAccept) ui.questAccept.addEventListener('click', closeQuestPopup);
  if (ui.questPopup) {
    ui.questPopup.addEventListener('click', function (e) {
      if (e.target === ui.questPopup) closeQuestPopup();
    });
  }
  if (ui.wipePopup) {
    ui.wipePopup.addEventListener('click', function (e) {
      if (e.target === ui.wipePopup) closeWipeConfirm();   // 点空白处 = 取消
    });
  }
  [ui.loginUser, ui.loginPwd, ui.loginIp].filter(Boolean).forEach(function (el) {
    // 输入时不要触发游戏快捷键
    el.addEventListener('keydown', function (e) {
      e.stopPropagation();
      if (e.code === 'Enter') {
        e.preventDefault();
        doLogin();
      }
    });
    // 改动输入内容 → 取消"确认新建档案"状态
    el.addEventListener('input', resetLoginConfirm);
  });
  ui.start.addEventListener('click', beginStartGame);
  if (ui.loginHelpBtn) {
    ui.loginHelpBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (ui.loginHelp) ui.loginHelp.classList.toggle('hidden');
    });
  }

  // ---------- HUD 左侧功能列（养成 / 交易）----------
  // 「养成 / 交易」现在是分组标签，下面的功能按钮全部直接可见（带图标 + 文字），
  // 所以这里不需要展开 / 收起逻辑，只需要在窗口尺寸变化时重新排版（见 layoutHud）。
  ui.btnMove.addEventListener('click', function () {
    // 世界BOSS击杀后的开箱阶段也允许走动（去中央宝箱那边）
    if ((gameState !== 'walk' && !worldBossLootActive()) || ui.btnMove.disabled) return;
    // 挖矿期间不能移动：必须先点「停止挖矿」
    if (miningContinuous || miningFx) {
      showToast('正在挖矿，点击「停止挖矿」后才能移动。');
      return;
    }
    if (sectScene) {
      showToast('门派内请点击屏幕左/右移动，长按可以持续移动。');
      return;
    }
    // 临时副本内用"点屏幕左右"移动，别让移动按钮把人带着跑
    if (activityRun) {
      showToast('副本内请点击屏幕左半边 / 右半边来走动。');
      return;
    }
    input.moving = !input.moving;
    // 开始赶路 = 顺手把「第一个任务」的任务卡收掉（不挡着看路）
    if (input.moving && ui.questPopup && !ui.questPopup.classList.contains('hidden')) closeQuestPopup();
    syncMoveButton();
  });
  ui.btnFlask.addEventListener('click', useFlask);

  ui.btnAttack.addEventListener('click', function () { doPlayerAction('attack'); });
  if (ui.duelAccept) ui.duelAccept.addEventListener('click', function () { replyPvpDuel(true); });
  if (ui.duelFlee) ui.duelFlee.addEventListener('click', function () { replyPvpDuel(false); });
  ui.btnSpell.addEventListener('click', function () { doPlayerAction('spell'); });
  ui.btnDefend.addEventListener('click', function () { doPlayerAction('defend'); });
  ui.spellClose.addEventListener('click', closeSpellPanel);
  ui.btnWorldBoss.addEventListener('click', function () { openWorldBossPanel(); });
  ui.wbClose.addEventListener('click', closeWorldBossPanel);
  ui.wbHelpBtn.addEventListener('click', function () { toggleWorldBossHelp(); });
  ui.wbHelpClose.addEventListener('click', function () { toggleWorldBossHelp(false); });
  ui.wbPopup.addEventListener('click', function (e) {
    if (e.target === ui.wbPopup) closeWorldBossPanel();
  });
  ui.wbLeave.addEventListener('click', function () { leaveWorldBoss('你撤离了世界BOSS战场。'); });
  ui.wbRewardClose.addEventListener('click', closeWorldBossRewardChests);
  // 技能列表滚动时卡片位置会变，先收起悬停提示，避免提示停在旧位置
  ui.spellList.addEventListener('scroll', hideSkillTip);
  ui.spellPopup.addEventListener('click', function (e) {
    if (e.target === ui.spellPopup) closeSpellPanel();
  });
  ui.lootClose.addEventListener('click', closeLootPanel);
  ui.lootPopup.addEventListener('click', function (e) {
    if (e.target === ui.lootPopup) closeLootPanel();
  });
  ui.btnGear.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openGearPanel();
  });
  ui.gearClose.addEventListener('click', closeGearPanel);
  ui.gearPopup.addEventListener('click', function (e) {
    if (e.target === ui.gearPopup) closeGearPanel();
  });
  // 装备回收（背包物品栏右上角）
  ui.btnRecycle.addEventListener('click', function () { openRecyclePanel(); });
  ui.recycleRun.addEventListener('click', recycleRunNow);
  ui.recycleClose.addEventListener('click', closeRecyclePanel);
  ui.recyclePopup.addEventListener('click', function (e) {
    if (e.target === ui.recyclePopup) closeRecyclePanel();
  });
  // 装备洗练
  ui.btnReforge.addEventListener('click', doReforge);
  ui.reforgeClose.addEventListener('click', closeReforge);
  ui.reforgePopup.addEventListener('click', function (e) {
    if (e.target === ui.reforgePopup) closeReforge();
  });
  // 装备镶嵌
  if (ui.btnInlay) ui.btnInlay.addEventListener('click', doInlay);
  if (ui.inlayClose) ui.inlayClose.addEventListener('click', closeInlay);
  if (ui.oreProcessClose) ui.oreProcessClose.addEventListener('click', closeOreProcessPanel);
  if (ui.oreProcessPopup) {
    ui.oreProcessPopup.addEventListener('click', function (e) {
      if (e.target === ui.oreProcessPopup) closeOreProcessPanel();
    });
  }
  if (ui.inlayPopup) {
    ui.inlayPopup.addEventListener('click', function (e) {
      if (e.target === ui.inlayPopup) closeInlay();   // 点空白关闭
    });
  }
  ui.btnShop.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openShop();
  });
  ui.btnSign.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) signIn();
  });
  ui.btnAuto.addEventListener('click', function () {
    // 临时副本内要手动搜刮 / 应战，不允许托管
    if (activityRun) { showToast('副本内需要手动探索，不能开启托管。'); return; }
    if (ui.title.classList.contains('hidden')) toggleAutoAdventure();
  });
  ui.btnElite.addEventListener('click', tryEliteChallenge);
  // 开场 CG：跳过按钮（Esc / 空格 / 回车也能跳）
  if (ui.cgSkip) ui.cgSkip.addEventListener('click', cgSkip);
  // 每日任务窗口
  if (ui.btnQuest) ui.btnQuest.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openQuestPanel();
  });
  if (ui.dailyClose) ui.dailyClose.addEventListener('click', closeQuestPanel);
  if (ui.dailyPopup) {
    ui.dailyPopup.addEventListener('click', function (e) {
      if (e.target === ui.dailyPopup) closeQuestPanel();   // 点空白关闭
    });
  }
  // 门派窗口
  if (ui.btnSect) ui.btnSect.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openSectPanel();
  });
  if (ui.sectClose) ui.sectClose.addEventListener('click', closeSectPanel);
  if (ui.sectPopup) {
    ui.sectPopup.addEventListener('click', function (e) {
      if (e.target === ui.sectPopup) closeSectPanel();   // 点空白关闭
    });
  }
  // 门派建筑内部：返回山门 = 关掉内部面板，继续在山门这条路上走
  if (ui.sectRoomBack) ui.sectRoomBack.addEventListener('click', function () { closeSectRoom(); });
  if (ui.sectRoomClose) ui.sectRoomClose.addEventListener('click', function () { closeSectRoom(); });
  // 持续挖矿：点停止按钮结束循环（当前这一轮会安全播完）
  if (ui.btnMineStop) {
    ui.btnMineStop.addEventListener('click', function (e) {
      e.stopPropagation();
      stopSectMining();
    });
  }
  if (ui.sectRoomOverlay) {
    ui.sectRoomOverlay.addEventListener('click', function (e) {
      if (e.target === ui.sectRoomOverlay) closeSectRoom();
    });
  }
  // 阵营抉择：必须二选一（不想现在定就点"暂且收起灵物"）
  if (ui.factionLater) ui.factionLater.addEventListener('click', function () {
    closeFactionPanel();
    showToast('你把「天地灵物」收了起来 —— 这道岔路，迟早要走。');
  });
  if (ui.factionPopup) {
    ui.factionPopup.addEventListener('click', function (e) {
      if (e.target === ui.factionPopup) closeFactionPanel();   // 点空白 = 暂时收起
    });
  }
  /* 点空白处收起"世界频道里点开的物品属性卡片"
   *   （聊天里的物品链接用点击展开，和背包那种"悬停即显"不一样） */
  document.addEventListener('click', function (e) {
    if (!ui.itemTip || ui.itemTip.classList.contains('hidden')) return;
    const t = e.target;
    if (ui.itemTip.contains(t)) return;
    if (t && t.closest && t.closest('.chatShareLink')) return;
    hideItemTip();
  });
  if (ui.caveLeave) ui.caveLeave.addEventListener('click', leaveCave);
  ui.btnBreak.addEventListener('click', breakthrough);
  // 赞助 / 自愿捐赠
  if (ui.btnDonate) ui.btnDonate.addEventListener('click', openDonate);
  if (ui.btnMusic) {
    ui.btnMusic.addEventListener('click', function (e) {
      e.stopPropagation();
      musicOpen = !musicOpen;
      updateMusicUI();
      if (musicOpen) tryPlayBgm();
    });
  }
  if (ui.musicVolume) {
    ui.musicVolume.addEventListener('input', function () {
      musicVolume = clamp((Number(ui.musicVolume.value) || 0) / 100, 0, 1);
      musicMuted = musicVolume <= 0;
      if (musicAudio) musicAudio.volume = musicMuted ? 0 : musicVolume;
      saveMusicSettings();
      updateMusicUI();
      if (!musicMuted) tryPlayBgm();
    });
  }
  if (ui.sfxVolume) {
    ui.sfxVolume.addEventListener('input', function () {
      sfxVolume = clamp((Number(ui.sfxVolume.value) || 0) / 100, 0, 1);
      if (runLoopAudio) runLoopAudio.volume = clamp(SFX_DEFS.run.volume * sfxVolume, 0, 1);
      if (sfxVolume <= 0) updateRunSfx(false);
      saveMusicSettings();
      updateMusicUI();
    });
  }
  window.addEventListener('pointerdown', function () { tryPlayBgm(); }, { once: true });
  window.addEventListener('keydown', function () { tryPlayBgm(); }, { once: true });

  /* 任意按钮：点击 / 悬停音效（事件委托，覆盖弹窗、HUD、装背包插槽等） */
  const SFX_CONTROL_SELECTOR =
    'button, .cmd, .hudSubBtn, .bagSlot, .spellCard, .activityEnter, ' +
    '.sectTaskBtn, .sectEnterBtn, .charNickBtn, input[type="range"]';
  document.addEventListener('pointerdown', function (e) {
    const t = e.target && e.target.closest ? e.target.closest(SFX_CONTROL_SELECTOR) : null;
    if (!t) return;
    // 右下角战斗操作区不播全局点击音：攻击/法术/移动/防御/托管/血瓶各自走战斗音效
    if (t.closest('#controls') || t.closest('#btnElite')) return;
    playSfx('click');
  });
  // 悬停音效只作用于左侧功能列：角色 / 装备 / 技能 / 宠物 / 图鉴 / … / 门派
  document.addEventListener('pointerover', function (e) {
    const t = e.target && e.target.closest ? e.target.closest('.hudSubBtn') : null;
    if (!t) { lastHoverSfxEl = null; return; }
    if (t === lastHoverSfxEl) return;
    const now = Date.now();
    if (now - lastHoverSfxAt < 90) return;
    lastHoverSfxEl = t;
    lastHoverSfxAt = now;
    playSfx('hover');
  });
  if (ui.donateClose) ui.donateClose.addEventListener('click', closeDonate);
  if (ui.donatePopup) {
    ui.donatePopup.addEventListener('click', function (e) {
      if (e.target === ui.donatePopup) closeDonate();   // 点空白关闭
    });
  }
  if (ui.btnOnlineClaim) ui.btnOnlineClaim.addEventListener('click', function () { claimOnlineReward(); });
  // 实名绑定窗口
  if (ui.identitySubmit) ui.identitySubmit.addEventListener('click', function () { submitIdentity(); });
  if (ui.identityCancel) ui.identityCancel.addEventListener('click', closeIdentityPanel);
  // 修改昵称
  if (ui.btnEditNick) ui.btnEditNick.addEventListener('click', function (e) { e.stopPropagation(); openNickPanel(); });
  // 重测灵根（与「修改昵称」并排）：已有灵根先弹确认窗；没有灵根时第一次免费直接测
  if (ui.rootRerollBtn) {
    ui.rootRerollBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (spiritRootDef()) openRootRerollPopup();
      else rerollSpiritRoot();
    });
  }
  if (ui.nickSubmit) ui.nickSubmit.addEventListener('click', function () { submitNickname(); });
  if (ui.nickCancel) ui.nickCancel.addEventListener('click', closeNickPanel);
  if (ui.nickInput) {
    ui.nickInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitNickname(); }
    });
  }
  [ui.idRealName, ui.idCardNo].forEach(function (el) {
    if (!el) return;
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitIdentity(); }
    });
  });
  ui.btnExchange.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openExchange();
  });
  ui.exchangeClose.addEventListener('click', closeExchange);
  ui.exchangePopup.addEventListener('click', function (e) {
    if (e.target === ui.exchangePopup) closeExchange();
  });
  ui.btnLog.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openBattleLog();
  });
  ui.logClose.addEventListener('click', closeBattleLog);
  // 点聊天窗右上角状态：手动重连世界频道
  if (ui.chatStatus) {
    ui.chatStatus.style.cursor = 'pointer';
    ui.chatStatus.addEventListener('click', reconnectChat);
  }

  // 聊天窗口（本地模式）
  ui.chatForm.addEventListener('submit', function (e) {
    e.preventDefault();
    sendChatMessage();
  });
  ui.chatToggle.addEventListener('click', toggleChatCollapse);
  /* 背包右上角：一键出售所有未鉴定矿石（5 金币/块） */
  if (ui.btnSellOre) {
    ui.btnSellOre.addEventListener('click', function (e) {
      e.stopPropagation();
      sellAllRawOres();
    });
  }
  ui.chatInput.addEventListener('keydown', function (e) {
    e.stopPropagation(); // 输入时不要触发游戏快捷键（例如标题页回车）
  });
  // 表情面板：点笑脸展开 / 收起；点面板外、按 Esc、发出消息都会收起来
  if (ui.chatEmoji) {
    ui.chatEmoji.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      toggleChatEmojiPanel();
    });
  }
  if (ui.chatEmojiPanel) {
    ui.chatEmojiPanel.addEventListener('click', function (e) { e.stopPropagation(); });
  }
  document.addEventListener('click', function (e) {
    if (!ui.chatEmojiPanel || ui.chatEmojiPanel.classList.contains('hidden')) return;
    if (ui.chatEmoji && ui.chatEmoji.contains(e.target)) return;
    if (ui.chatEmojiPanel.contains(e.target)) return;
    toggleChatEmojiPanel(false);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') toggleChatEmojiPanel(false);
  });

  // 图鉴 / 抽卡
  ui.btnCodex.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openCodex();
  });
  ui.btnGacha.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openGacha();
  });

  // 世界BOSS 立绘：每个 BOSS 一张（defs.js 的 art 字段；换 BOSS 就换图）
  WORLD_BOSSES.forEach(function (d) {
    if (d.art) ASSETS.push([wbBossArtKey(d), d.art]);
  });

  // 时装美术（每个 BOSS 一件，见 defs.js 的 COSTUMES）
  COSTUMES.forEach(function (c) {
    ASSETS.push(['costume_' + c.id, c.art]);
  });

  // 物品图标（天地灵物 / 金币袋子）：用户提供的素材，见 tools/import_item_icons.cjs
  const ICON_ART = {
    spirit: 'assets/img/ui/spirit_item.png',
    gold: 'assets/img/ui/gold_bag.png',
    flask: 'assets/img/ui/flask.png',              // 回血药瓶（和右下角血瓶同一个素材）
    stoneActive: 'assets/img/ui/stone_active.png',  // 主动技能石（森特欧斯 gift2）
    stoneSupport: 'assets/img/ui/stone_support.png' // 辅助技能石（森特欧斯 gift2 去底）
  };
  Object.keys(ICON_ART).forEach(function (k) {
    ASSETS.push(['icon_' + k, ICON_ART[k]]);
  });
  // 副本按钮：排在「抽卡」下面
  ui.btnDungeon.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openDungeonPanel();
  });
  ui.btnActivity.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openActivityPanel();
  });
  ui.activityClose.addEventListener('click', closeActivityPanel);
  if (ui.activityHelpBtn) ui.activityHelpBtn.addEventListener('click', function () { toggleActivityHelp(); });
  if (ui.activityHelpClose) ui.activityHelpClose.addEventListener('click', function () { toggleActivityHelp(false); });
  ui.activityPopup.addEventListener('click', function (e) {
    if (e.target === ui.activityPopup) closeActivityPanel();
  });
  ui.dungeonClose.addEventListener('click', closeDungeonPanel);
  ui.dungeonPopup.addEventListener('click', function (e) {
    if (e.target === ui.dungeonPopup) closeDungeonPanel();
  });
  ui.gachaOne.addEventListener('click', function () { doGacha(1); });
  ui.gachaTen.addEventListener('click', function () { doGacha(10); });
  ui.codexClose.addEventListener('click', closeCodex);
  ui.codexPopup.addEventListener('click', function (e) {
    if (e.target === ui.codexPopup) closeCodex();
  });
  // 鼠标滚轮翻藏品时格子会移动，提示跟着关掉，避免“粘”在旧位置
  ui.codexGrid.addEventListener('scroll', hideCodexTip);
  ui.gachaClose.addEventListener('click', closeGacha);
  ui.gachaPopup.addEventListener('click', function (e) {
    if (e.target === ui.gachaPopup) closeGacha();
  });
  ui.btnChar.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openCharPanel();
  });
  ui.btnSkill.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openSkillPanel();
  });
  ui.btnPet.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openPetPanel();
  });
  ui.petClose.addEventListener('click', closePetPanel);
  ui.petPopup.addEventListener('click', function (e) {
    if (e.target === ui.petPopup) closePetPanel();
  });
  ui.skillClose.addEventListener('click', closeSkillPanel);
  ui.skillPopup.addEventListener('click', function (e) {
    if (e.target === ui.skillPopup) closeSkillPanel();
  });
  ui.charClose.addEventListener('click', closeCharPanel);
  if (ui.armorHelpClose) ui.armorHelpClose.addEventListener('click', function () { toggleArmorHelp(false); });
  ui.charPopup.addEventListener('click', function (e) {
    // 和其余窗口保持一致：点窗口外的空白遮罩直接关掉
    if (e.target === ui.charPopup) closeCharPanel();
  });
  ui.shopClose.addEventListener('click', closeShop);
  ui.shopPopup.addEventListener('click', function (e) {
    if (e.target === ui.shopPopup) closeShop();
  });
  ui.chestClose.addEventListener('click', closeChestPanel);
  // 灵脉：「发现异常灵气聚集地」→ 前去一探 / 跳过
  if (ui.lingPromptGo) ui.lingPromptGo.addEventListener('click', lingGoInvestigate);
  if (ui.lingPromptSkip) ui.lingPromptSkip.addEventListener('click', lingClosePrompt);
  if (ui.bottleneckClose) ui.bottleneckClose.addEventListener('click', hideBottleneckPanel);
  // 灵根弹窗：接受 / 重新随机 / 关闭
  if (ui.rootAccept) ui.rootAccept.addEventListener('click', acceptSpiritRoot);
  if (ui.rootReroll) ui.rootReroll.addEventListener('click', function () { rerollSpiritRoot(); });
  if (ui.rootClose) ui.rootClose.addEventListener('click', closeSpiritRootPanel);
  // 角色窗口的「重测灵根」→ 确认窗（材料消耗说明）
  if (ui.rootRerollConfirm) ui.rootRerollConfirm.addEventListener('click', confirmRootReroll);
  if (ui.rootRerollCancel) ui.rootRerollCancel.addEventListener('click', closeRootRerollPopup);
  if (ui.rootRerollPopup) {
    ui.rootRerollPopup.addEventListener('click', function (e) {
      if (e.target === ui.rootRerollPopup) closeRootRerollPopup();
    });
  }
  /* 灵脉定位（测试用）：按钮与窗口已下架（见 index.html 里的注释），
   *   这里不再有任何玩家可见的入口；传送核心只通过 __debugLingLocate 供自动化用例调用。 */
  {   // 仙人掌灵力耗尽时那张「救命」艺术字
    const boomImg = document.getElementById('lingBoomImg');
    if (boomImg) boomImg.src = assetUrl('assets/img/ui/jiu_ming.png');
  }
  ui.reviveBtn.addEventListener('click', reviveAtStart);
  ui.chestPopup.addEventListener('click', function (e) {
    if (e.target === ui.chestPopup) closeChestPanel();
  });
  ui.gemClose.addEventListener('click', closeGemPanel);
  ui.gemPopup.addEventListener('click', function (e) {
    if (e.target === ui.gemPopup) closeGemPanel();
  });
  ui.socketClose.addEventListener('click', closeSocketPanel);
  ui.socketPopup.addEventListener('click', function (e) {
    if (e.target === ui.socketPopup) closeSocketPanel();
  });
  // 点击画布上的尸体进行搜索
  canvas.addEventListener('click', function (e) {
    // 「强力射击」完美时机：点进度条等价于按空格键（方便触屏 / 手机玩家）
    if (powerTimingActive()) {
      const rectP = canvas.getBoundingClientRect();
      const px = (e.clientX - rectP.left) * (canvas.width / rectP.width);
      const py = (e.clientY - rectP.top) * (canvas.height / rectP.height);
      const r = powerTimingRect();
      if (px >= r.x - 22 && px <= r.x + r.w + 22 && py >= r.y - 52 && py <= r.y + r.h + 24) {
        pressPerfectTiming();
        return;
      }
    }
    /* 首页：点画面任意位置都弹出登录窗口
     *   （以前只认"点在角色身上"，玩家经常点了没反应，以为页面卡住了） */
    if (gameState === 'intro') {
      openLoginFromIntro();
      e.stopPropagation();   // 顺手点开的那一下，别让外层"点空白关闭"的监听又把它收掉
      return;
    }
    /* 山门：点路边建筑 → 进内部；点空地 → 照旧走路（下面的通用分支会处理） */
    if (sectScene && gameState === 'walk') {
      const rectS = canvas.getBoundingClientRect();
      const sxS = (e.clientX - rectS.left) * (canvas.width / rectS.width);
      const syS = (e.clientY - rectS.top) * (canvas.height / rectS.height);
      const hitB = sectSceneBuildingAt(sxS, syS);
      if (hitB) { openSectRoom(hitB.id); return; }
    }
    /* 临时公共副本：点击优先判定"物品 / 其他玩家 / 怪 / 传送门"，
     * 点空地则按左右半边决定往哪走。 */
    if (activityRun && gameState === 'walk') {
      window.__debugActivityClicks = (window.__debugActivityClicks || 0) + 1;
      const rectA = canvas.getBoundingClientRect();
      const sxA = (e.clientX - rectA.left) * (canvas.width / rectA.width);
      const syA = (e.clientY - rectA.top) * (canvas.height / rectA.height);
      if (activityHitTest(sxA, syA)) return;
      const run = activityRun;
      /* 这次按下已经由 pointerdown / pointerup 处理过了（真机上按下就开始走），
       * 浏览器跟在后面的 click 不要再补一步，否则点一下会走两步。 */
      if (run.pressTs && Date.now() - run.pressTs < 800) return;
      // ⑥ 点空地：以角色自身为界 —— 点他左边往左走、右边往右走
      //    （角色固定画在屏幕左侧约 28% 处，所以"屏幕左半边"实际大部分是"角色右侧"）
      activityTapMove(sxA);
      return;
    }
    // 世界BOSS击杀后的开箱阶段：点中央宝箱开箱；地上的战利品展示点了只给提示
    if (worldBossRun) {
      const rect0 = canvas.getBoundingClientRect();
      const cx = (e.clientX - rect0.left) * (canvas.width / rect0.width);
      const cy = (e.clientY - rect0.top) * (canvas.height / rect0.height);
      const chest = worldBossLootChestRect();
      if (chest && worldBossLootActive() &&
          cx >= chest.x - 14 && cx <= chest.x + chest.w + 14 &&
          cy >= chest.y - 14 && cy <= chest.groundY + 30) {
        const playerSx = player.worldX - cameraX();
        if (Math.abs(playerSx - chest.cx) > WB_LOOT_REACH) {
          showToast('先走近中央的宝箱，再点它开启。');
        } else {
          openWorldBossLootChest(false);
        }
        return;
      }
      const hitGift = (worldBossRun.gifts || []).some(function (g) {
        const r = worldBossGiftRect(g);
        return cx >= r.x - 6 && cx <= r.x + r.w + 6 && cy >= r.y - 6 && cy <= r.y + r.h + 10;
      });
      if (hitGift) { showToast(WB_GIFT_NOTE); return; }
      if (worldBossLootActive()) return;   // 开箱阶段不做别的
    }
    if (gameState !== 'walk') return;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    // 优先检查易拉罐：点击可以踢飞
    for (let i = 0; i < cans.length; i++) {
      const c = cans[i];
      if (c.kicked) continue;
      const cxs = c.worldX - cameraX();
      if (Math.abs(sx - cxs) < 52 && Math.abs(sy - c.y) < 48) {
        kickCan(c);
        return;
      }
    }
    // 灵脉房间里被别人打掉的战利品堆
    const lingPile = lingPvpPiles.filter(function (p) {
      return !p.taken && Math.abs(sx - (p.worldX - cameraX())) < 115;
    })[0];
    if (lingPile) { takeActivityPile(lingPile); return; }
    // 灵脉房间里的真人：点他 = 神识锁定、发起交锋
    const lingPeer = lingHitTestPeer(sx, sy);
    if (lingPeer) { requestPvpDuel(lingPeer); return; }
    // 灵脉仙人掌：炼气期修士点一棵仙人掌把它认成自己的灵脉（编号 1~1000）
    const lingHit = lingCactusAt(sx, sy);
    if (chest && (!chest.weaponTaken || !chest.gemTaken)) {
      const cxs = chest.worldX - cameraX();
      if (Math.abs(sx - cxs) < 95 && sy > GROUND_Y - 160 && sy < H) {
        openChestPanel();
        return;
      }
    }
    // 尸体（精确目标，有才判定；没有尸体不能直接 return，否则会挡掉后面的洞府点击）
    if (corpse && !corpse.searched) {
      const csx = corpse.worldX - cameraX();
      if (Math.abs(sx - csx) < 115 && sy > GROUND_Y - 300 && sy < H) {
        searchCorpse();
        return;
      }
    }
    // 最后才是面积最大的洞府入口（避免挡住尸体 / 易拉罐的点击）
    const caveHit = caveEntranceAt(sx, sy);
    if (caveHit && !cave) clickCaveEntrance(caveHit);
    /* 仙人掌放在**最后**判定：掉落物（尸体 / 宝箱 / 罐子）跟仙人掌重叠时，
     *   点击优先给掉落物 —— 以前仙人掌夹在中间，会把落在它身上的战利品点掉。 */
    if (lingHit) lingActivateCactus(lingHit);
  });

  /* 临时副本的"按下即走、松手就停"：
   *   点一下就松手 → 只走一小段（tapLeft 走完自动停）
   *   一直按着不放   → 一直走，松手立刻停
   * 点到的不是空地（物品 / 尸体 / 道友 / 怪 / 传送门）就交给 activityHitTest 处理。 */
  canvas.addEventListener('pointerdown', function (e) {
    if (!activityRun || gameState !== 'walk') return;
    if (typeof e.button === 'number' && e.button !== 0) return;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    const run = activityRun;
    run.pressTs = Date.now();          // 让随后的 click 别再补一步
    if (activityHitTest(sx, sy)) return;
    const playerSx = player.worldX - cameraX();
    run.moveDir = sx < playerSx ? -1 : 1;
    run.tapLeft = 0;                   // 长按：不限步数
    run.holding = true;
    run.pressDist = 0;
    activityCancelPick();
  });
  function activityEndPress() {
    const run = activityRun;
    if (!run || !run.holding) return;
    run.holding = false;
    if (run.pressDist < ACT_TAP_STEP) {
      run.tapLeft = ACT_TAP_STEP - run.pressDist;   // 短按：把这一小步走完
    } else {
      activityStopMove();                           // 长按：松手就停
    }
  }
  window.addEventListener('pointerup', activityEndPress);
  window.addEventListener('pointercancel', activityEndPress);

  /* 门派山门：点屏幕左/右自由移动；长按持续走，点一下只走一小段。
   * 点到建筑不移动，交给 click 处理进殿。 */
  canvas.addEventListener('pointerdown', function (e) {
    if (!sectScene || gameState !== 'walk') return;
    if (typeof e.button === 'number' && e.button !== 0) return;
    if (miningContinuous || miningFx) {
      showToast('正在挖矿，点击「停止挖矿」后才能移动。');
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    if (sectSceneBuildingAt(sx, sy)) return;
    const playerSx = player.worldX - cameraX();
    sectMoveDir = sx < playerSx ? -1 : 1;
    sectHolding = true;
    sectTapLeft = 0;
    sectPressDist = 0;
  });
  function sectEndPress() {
    if (!sectScene || !sectHolding) return;
    sectHolding = false;
    if (sectPressDist < SECT_TAP_STEP) {
      sectTapLeft = SECT_TAP_STEP - sectPressDist;
    } else {
      sectMoveDir = 0;
      player.vx = 0;
    }
  }
  window.addEventListener('pointerup', sectEndPress);
  window.addEventListener('pointercancel', sectEndPress);

  /* 副本"真人"心跳：不依赖主循环的节流，网页切到后台（浏览器把 rAF 停掉、
   * 定时器压到一分钟一次）也照样按真实时间把位置发上去，不会被服务端当成掉线。 */
  setInterval(function () {
    if (!netActRoom || !netChatReady()) return;
    // 副本里报"层内坐标"
    if (activityRun) {
      const p = activitySelfPayload();
      net.chat.send({ type: 'act:pos', x: p.x, hp: p.hp, action: p.action, level: p.level, realm: p.realm });
      return;
    }
    /* 灵脉房间同样要有独立心跳：主循环被浏览器降频时（切后台 / 省电模式），
     *   只靠主循环上报位置会被服务端当成掉线清出去 —— 那样别人传送到灵脉旁就"看不到人"了。 */
    if (netRoomKind === 'ling' && netLingNumber) {
      const p = lingSelfPayload();
      net.chat.send({ type: 'act:pos', x: p.x, hp: p.hp, action: p.action,
        level: p.level, realm: p.realm, speed: p.speed, atkSpd: p.atkSpd });
    }
  }, NET_HEARTBEAT_MS);

  // 登录窗口：点窗口之外的空白处（画面里的暗底、画面外的黑色留白）都收起窗口。
  // 面板内部的输入框 / 按钮 / 关闭按钮照常操作，不受影响。
  (function bindLoginBackdropClose() {
    const stageEl = document.getElementById('stage');
    if (!stageEl) return;
    stageEl.addEventListener('click', function (e) {
      if (!ui.loginScreen || ui.loginScreen.classList.contains('hidden')) return;
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('#loginScreen .panel')) return;                     // 登录面板内部
      if (t.closest('.popupOverlay')) return;                           // 任何弹窗内部（删档确认、商店…）
      if (ui.introDialog && ui.introDialog.contains(t)) return;         // 首页引路人对白
      if (ui.btnDonate && ui.btnDonate.contains(t)) return;             // 赞助按钮
      if (ui.beianBar && ui.beianBar.contains(t)) return;               // 底部备案链接
      closeLoginFromIntro();
    });
  })();

  /* 角色窗口：除了点面板外的遮罩，点「游戏画面外的黑色留白」（#wrap 之外）也要能关掉。
   *   #wrap 是 16:9 居中显示的，非 16:9 屏幕上上下会留黑边；
   *   遮罩只铺在 #wrap 里，所以在黑边里点以前是没反应的 —— 玩家会觉得"点了外面没关"。 */
  (function bindCharBackdropClose() {
    const stageEl = document.getElementById('stage');
    if (!stageEl) return;
    stageEl.addEventListener('click', function (e) {
      if (!ui.charPopup || ui.charPopup.classList.contains('hidden')) return;
      /* 用"点击坐标"判断在不在画面里，不能看 e.target：
       *   角色窗口里点「+1」加点时，按钮自己的处理函数会立刻重排面板，
       *   等事件冒泡到这里，e.target 已经从文档里摘掉了 —— closest('#wrap') 返回 null，
       *   会被误判成"点在画面外"，一加点就把窗口关掉。 */
      const wrapEl = ui.wrap || document.getElementById('wrap');
      if (!wrapEl) return;
      const r = wrapEl.getBoundingClientRect();
      const inGame = e.clientX >= r.left && e.clientX <= r.right &&
                     e.clientY >= r.top && e.clientY <= r.bottom;
      if (inGame) return;   // 画面内的点击交给遮罩那层监听
      closeCharPanel();
    });
  })();

  // 开始画面也允许用回车进入
  // 点空白处收起背包小菜单（菜单自己内部的点击不关）
  document.addEventListener('click', function (e) {
    if (ui.bagMenu && !ui.bagMenu.classList.contains('hidden') && !ui.bagMenu.contains(e.target)) {
      hideBagMenu();
    }
  });

  window.addEventListener('keydown', function (e) {
    // 「强力射击」完美时机：空格键判定（在完美区间内按下才算成功）
    if (e.code === 'Space' || e.key === ' ') {
      if (powerTimingActive()) {
        e.preventDefault();
        pressPerfectTiming();
        return;
      }
    }
    if (e.code === 'Enter') {
      if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) { doLogin(); return; }
      if (gameState === 'title') beginStartGame();
    }
    // 角色窗口：Esc 等价于点「关闭」
    if (e.code === 'Escape' && !ui.charPopup.classList.contains('hidden')) closeCharPanel();
    // 副本窗口：Esc 关闭
    if (e.code === 'Escape' && ui.dungeonPopup && !ui.dungeonPopup.classList.contains('hidden')) closeDungeonPanel();
    // 活动窗口：Esc 关闭
    if (e.code === 'Escape' && ui.activityPopup && !ui.activityPopup.classList.contains('hidden')) closeActivityPanel();
    // 开场 CG：Esc / 空格 / 回车 都能跳过
    if (cg.active && (e.code === 'Escape' || e.code === 'Space' || e.code === 'Enter')) {
      e.preventDefault();
      cgSkip();
    }
  });

  // ---------- 启动 ----------
  initWeather();
  loadSave();
  updateCharButton();
  preloadPlayerRestFrames(); // 休息姿态：软加载，缺图也不影响进游戏
  preloadWorldBossInfoImages(); // 世界BOSS 资料图：悬停时才显示，先预取
  // 先显示登录页；登录成功后再露出标题页，点「开始游戏」才开始加载资源
  /* 服务器地址：登录框里的输入框已移除 —— 这里只把"上次用过的地址"记进 net.serverUrl，
   *   没记过就走当前页面同源（自有域名 → nginx 反代；第三方平台 → 正式域名）。 */
  try {
    const remembered = window.localStorage.getItem(NET_SERVER_KEY);
    net.serverUrl = remembered || defaultServerInput();
  } catch (e) {
    net.serverUrl = defaultServerInput();
  }
  if (skipLoginRequested()) {
    account.name = '测试账号';
    account.ip = net.serverUrl;
    account.hash = '';     // 测试直连：继续用本地默认存档（不影响账号体系）
    account.isNew = false;
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.remove('hidden');
    if (ui.titleUser) ui.titleUser.textContent = '当前账号：' + account.name + '　·　服务器：' + account.ip + '　·　测试直连（本地默认档案）';
    gameState = 'title';
  } else {
    // 首页：先看到角色在路上休息，点角色才弹出登录窗口
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.add('hidden');
    gameState = 'intro';
    enterIntroIdle();
  }
  updateDonateButton();
  syncPageVisibility();   // 页面一开始就处于后台标签页时，直接启用后台推进
  layoutHud();            // 首次进入就按当前窗口尺寸排好 HUD

  // 关页面 / 切到后台：把最新状态立刻推给服务器（避免丢进度、丢金币）
  window.addEventListener('pagehide', flushOnPageHide);
  window.addEventListener('beforeunload', flushOnPageHide);
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) flushOnPageHide();
  });

  requestAnimationFrame(frame);
})();



